import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Users, Settings, User, Share2, Trash2, Edit2, Check, X, Mic, Square, Play, PlayCircle, Pause, Loader, RefreshCw, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, AlertCircle, Video, Plus, Download, ThumbsUp, ThumbsDown, Scissors, Upload, CheckCircle, Search, Copy, Star, Box, Film, MoreVertical, Calendar, Camera, Type, RotateCcw, Image as ImageIcon, Edit3 } from 'react-feather';
import { useWorkspace } from './WorkspaceContext';
import { useDarkMode } from './DarkModeContext';
import { useWorkspaceTab } from './WorkspaceTabContext';
import TextareaAutosize from 'react-textarea-autosize';
import api from './api';
import { trackEvent } from './utils/amplitude';
import { getCurrencySymbol } from './utils/currency';
import { formatThumbnailTitleForRender, normalizeThumbnailTitleText } from './utils/thumbnailTitleText';
import './WorkspaceManagement.css';
import FastSpinner from './components/FastSpinner';
import TimelineCutStrip from './components/TimelineCutStrip';
import NewNleEditor from './components/NewNleEditor';
import OnboardingOverlay from './components/OnboardingOverlay';
import EditTimelineIcon from './components/EditTimelineIcon';
import { timelineInsertIndexAtPlayhead, timelinePlayheadJustPastMidCut, timelineCutUsageCounts } from './utils/timelineStrip';
import { readNleMode, writeNleMode, NLE_MODE_NEW, NLE_MODE_LEGACY } from './utils/newNle';
import { filterStudioFonts } from './utils/studioFonts';
import { preloadHlsEngine } from './components/NleHlsPreview';
import { prefetchVoiceover, prefetchCutPreviewAssets, warmNleHlsPlaylist } from './utils/nleWarmup';
import { autoplayVoiceoverUrl } from './utils/autoplayVoiceover';
import VideoGenerationSteps from './components/VideoGenerationSteps';
import BatchVideoSettingsOverride from './components/BatchVideoSettingsOverride';
import {
    emptyBatchVideoSettingsFrom,
    mergeBatchVideoSettingsWithExport,
    resolveBatchVideoSettings,
    openingLineFontFields,
    rekeyByScriptId,
} from './utils/batchVideoSettings';
import WorkspaceHomeTab from './WorkspaceHomeTab';
import { useNavigate } from 'react-router-dom';
import CarouselHerokuLogsOverlay from './components/CarouselHerokuLogsOverlay';
import StudioCarousel from './components/StudioCarousel';
import { celebrateVideoDownload } from './utils/downloadCelebration';
import { isRawVideoTooLong, readVideoFileDurationSec, RAW_VIDEO_TOO_LONG_MESSAGE } from './utils/rawVideoLimits';

const SYSTEM_UI_FONT = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const VOICE_CLONE_LIMIT_MESSAGE = "You've reached the limit of voice-cloning for free accounts. Please upgrade your subscription.";

function isVoiceCloneLimitError(error) {
    const data = error?.response?.data;
    if (data?.code === 'voice_clone_limit_reached') return true;
    const detail = data?.details?.detail || data?.detail;
    return detail?.status === 'voice_add_edit_limit_reached';
}

/** 4-point sparkle: pointy outer tips, rounded inner corners. */
function roundedSparklePath(cx, cy, tipLength, armWidth, cornerRadius) {
    const L = tipLength;
    const w = armWidth;
    const r = cornerRadius;
    return [
        `M${cx} ${cy - L}`,
        `L${cx + w} ${cy - w - r}`,
        `A${r} ${r} 0 0 0 ${cx + w + r} ${cy - w}`,
        `L${cx + L} ${cy}`,
        `L${cx + w + r} ${cy + w}`,
        `A${r} ${r} 0 0 0 ${cx + w} ${cy + w + r}`,
        `L${cx} ${cy + L}`,
        `L${cx - w} ${cy + w + r}`,
        `A${r} ${r} 0 0 0 ${cx - w - r} ${cy + w}`,
        `L${cx - L} ${cy}`,
        `L${cx - w - r} ${cy - w}`,
        `A${r} ${r} 0 0 0 ${cx - w} ${cy - w - r}`,
        'Z',
    ].join(' ');
}

function SparklesIcon({ size = 16, style }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0, ...(style || {}) }}>
            <path d={roundedSparklePath(10.2, 9, 7.6, 2.0, 1.55)} />
            <path d={roundedSparklePath(18.4, 17.8, 3.6, 0.95, 0.8)} />
        </svg>
    );
}

function getRecordingIdsKey(recordings) {
    return (Array.isArray(recordings) ? recordings : [])
        .map((r) => String(r._id || r.id || ''))
        .filter(Boolean)
        .sort()
        .join('|');
}

function getColorForString(str) {
    if (!str || typeof str !== 'string') return null;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash) + str.charCodeAt(i);
        hash |= 0;
    }
    hash = (hash ^ (hash >>> 16)) * 0x85ebca6b;
    hash = (hash ^ (hash >>> 13)) * 0xc2b2ae35;
    hash = (hash >>> 0) ^ (hash >>> 16);
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 62%, 42%)`;
}

/** Effective on-timeline seconds per cut (encoding preview: fast pacing trims 2s assets to 1s). */
function timelineCutEffectiveSeconds(cut, fastPacing) {
    const d = (typeof cut?.duration === 'number' && cut.duration >= 2) ? 2 : 1;
    return fastPacing && d === 2 ? 1 : d;
}

/** Raw seconds from API usedCutDurations (1 or 2 per cut); unchanged when fast pacing toggles. */
function sumUsedCutDurationsRaw(usedDurations) {
    if (!Array.isArray(usedDurations) || usedDurations.length === 0) return 0;
    return usedDurations.reduce((acc, d) => {
        const raw = (typeof d === 'number' && d >= 2) ? 2 : 1;
        return acc + raw;
    }, 0);
}

/** Stable key for timeline add-cut picker tag filters (server applies same tags before pagination). */
function timelinePickerTagsKey(includeTags, excludeTags) {
    const norm = (arr) => [...(arr || [])].map((t) => String(t).trim().toLowerCase()).filter(Boolean).sort();
    return JSON.stringify({ inc: norm(includeTags), exc: norm(excludeTags) });
}

function timelinePickerCutsQuery(includeTags, excludeTags) {
    return {
        includeTags: includeTags.length > 0 ? includeTags.join(',') : undefined,
        excludeTags: excludeTags.length > 0 ? excludeTags.join(',') : undefined,
    };
}

function parseWorkspaceCutsResponse(data) {
    if (Array.isArray(data)) return data;
    return data?.items || data?.cuts || [];
}

function cutSourceVideoId(cut) {
    if (cut?.videoId != null) return String(cut.videoId);
    if (cut?.video?._id != null) return String(cut.video._id);
    return null;
}

function shuffleSliceCuts(cuts, max) {
    const shuffled = [...(cuts || [])].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, Math.min(max, shuffled.length));
}

/** Fixed thumbnail width for a 2-second cut in the timeline strip. */
const TIMELINE_CUT_BASE_WIDTH_PX = 70;
const TIMELINE_CUT_THUMB_HEIGHT_PX = Math.round(TIMELINE_CUT_BASE_WIDTH_PX * (16 / 9));
const TIMELINE_PX_PER_EFFECTIVE_SECOND = TIMELINE_CUT_BASE_WIDTH_PX / 2;
const TIMELINE_STRIP_PADDING_X_PX = 2;
const TIMELINE_CUT_BORDER_PX = 1;
const TIMELINE_CUT_OVERLAP_MARGIN_PX = 1;

function timelineCutThumbWidthPx(cut, fastPacing) {
    return timelineCutEffectiveSeconds(cut, fastPacing) * TIMELINE_PX_PER_EFFECTIVE_SECOND;
}

/** Outer card width: thumb + left/right border. */
function timelineCutCardWidthPx(cut, fastPacing) {
    return timelineCutThumbWidthPx(cut, fastPacing) + TIMELINE_CUT_BORDER_PX * 2;
}

/** Width of the cuts track only (variable card widths + overlap margins, no strip padding). */
function timelineCutsTrackWidthPx(cuts, fastPacing) {
    if (!cuts?.length) return 0;
    return cuts.reduce(
        (acc, cut, index) => acc + timelineCutCardWidthPx(cut, fastPacing) + (index > 0 ? TIMELINE_CUT_OVERLAP_MARGIN_PX : 0),
        0
    );
}

/** Active cut index from voiceover currentTime (same pacing rules as labels and strip widths). */
function timelineActiveCutIndexFromTime(cuts, timeSeconds, fastPacing) {
    const n = (cuts || []).length;
    if (n === 0) return null;
    let acc = 0;
    let idx = n - 1;
    for (let i = 0; i < n; i++) {
        const end = acc + timelineCutEffectiveSeconds(cuts[i], fastPacing);
        if (timeSeconds < end) {
            idx = i;
            break;
        }
        acc = end;
    }
    return idx;
}

/** Playhead X (px from strip left) mapped to voiceover time across variable-width cuts. */
function timelinePlayheadLeftPx(cuts, timeSeconds, fastPacing) {
    if (!cuts?.length) return TIMELINE_STRIP_PADDING_X_PX;
    let accTime = 0;
    let accPx = TIMELINE_STRIP_PADDING_X_PX;
    for (let i = 0; i < cuts.length; i++) {
        const cutSec = timelineCutEffectiveSeconds(cuts[i], fastPacing);
        const cardW = timelineCutCardWidthPx(cuts[i], fastPacing);
        const thumbW = timelineCutThumbWidthPx(cuts[i], fastPacing);
        const margin = i > 0 ? TIMELINE_CUT_OVERLAP_MARGIN_PX : 0;
        const cardStartPx = accPx + margin;
        if (timeSeconds <= accTime + cutSec) {
            const progressInCut = cutSec > 0
                ? Math.min(1, Math.max(0, (timeSeconds - accTime) / cutSec))
                : 0;
            return cardStartPx + TIMELINE_CUT_BORDER_PX + progressInCut * thumbW;
        }
        accTime += cutSec;
        accPx = cardStartPx + cardW;
    }
    return accPx;
}

/** Second markers + 0.2s tick marks under the timeline cut strip. */
function TimelineSecondsRuler({ totalSeconds, trackWidthPx, darkMode = false }) {
    const secCount = Math.max(1, Math.ceil(totalSeconds));
    const secWidthPx = trackWidthPx > 0 ? trackWidthPx / secCount : TIMELINE_CUT_BASE_WIDTH_PX;
    return (
        <div
            style={{
                paddingLeft: TIMELINE_STRIP_PADDING_X_PX,
                paddingRight: TIMELINE_STRIP_PADDING_X_PX,
                borderTop: '1px solid var(--input-border)',
                backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.18)' : '#D9D9DC',
                flexShrink: 0,
                boxSizing: 'border-box',
            }}
            aria-hidden
        >
            <div
                style={{
                    display: 'flex',
                    width: trackWidthPx,
                    height: 22,
                }}
            >
            {Array.from({ length: secCount }, (_, sec) => (
                <div
                    key={`timeline-ruler-sec-${sec}`}
                    style={{
                        width: secWidthPx,
                        flexShrink: 0,
                        position: 'relative',
                        height: '100%',
                    }}
                >
                    <span
                        style={{
                            position: 'absolute',
                            left: 3,
                            bottom: 4,
                            fontSize: '10px',
                            lineHeight: 1,
                            color: 'var(--text-secondary)',
                            fontVariantNumeric: 'tabular-nums',
                            userSelect: 'none',
                        }}
                    >
                        {sec}s
                    </span>
                    {[1, 2, 3, 4].map((tick) => (
                        <div
                            key={tick}
                            style={{
                                position: 'absolute',
                                left: `${(tick / 5) * 100}%`,
                                bottom: 2,
                                width: 1,
                                height: 5,
                                backgroundColor: 'var(--text-secondary)',
                                opacity: 0.45,
                                transform: 'translateX(-50%)',
                            }}
                        />
                    ))}
                </div>
            ))}
            </div>
        </div>
    );
}

/** Drop duplicate cut ids (pagination overlap or API repeats). */
function dedupeCutsById(cuts) {
    const seen = new Set();
    return (cuts || []).filter((c) => {
        const id = String(c?._id || c?.id || '');
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
    });
}

/** Hover preview for cut thumbnails (parent must contain a single <video>). */
function handleCutThumbMouseEnter(e) {
    const v = e.currentTarget.querySelector('video');
    if (!v) return;
    v.muted = true;
    v.loop = true;
    v.play().catch(() => {});
}
function handleCutThumbMouseLeave(e) {
    const v = e.currentTarget.querySelector('video');
    if (!v) return;
    v.pause();
    try {
        v.currentTime = 0;
    } catch (_) {}
}

function timelineCutPickerDurationLabel(cut, fastPacing) {
    if (fastPacing && typeof cut.duration === 'number' && cut.duration >= 2) return '1 sec';
    if (typeof cut.duration === 'number' && cut.duration >= 2) return '2 sec';
    return '1 sec';
}

/** Memoized so adding one cut does not remount every picker thumbnail. */
const TimelineAddCutThumb = React.memo(function TimelineAddCutThumb({ cut, fastPacing, usageCount, onAdd }) {
    return (
        <button
            type="button"
            onClick={() => onAdd(cut)}
            style={{
                padding: 0,
                border: '2px solid var(--input-border)',
                borderRadius: '6px',
                overflow: 'hidden',
                cursor: 'pointer',
                background: 'none',
                transition: 'border-color 0.15s, transform 0.15s',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--button-bg)';
                e.currentTarget.style.transform = 'scale(1.02)';
                handleCutThumbMouseEnter(e);
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--input-border)';
                e.currentTarget.style.transform = 'scale(1)';
                handleCutThumbMouseLeave(e);
            }}
        >
            <div style={{ position: 'relative' }}>
                <video src={cut.url} style={{ width: '100%', aspectRatio: '9/16', objectFit: 'cover', display: 'block', pointerEvents: 'none' }} muted playsInline preload="metadata" />
                {usageCount > 0 && (
                    <span style={{
                        position: 'absolute',
                        right: 4,
                        bottom: 4,
                        minWidth: 18,
                        height: 18,
                        padding: '0 4px',
                        borderRadius: 9,
                        backgroundColor: 'rgba(0, 0, 0, 0.72)',
                        color: '#fff',
                        fontSize: 10,
                        fontWeight: 700,
                        lineHeight: '18px',
                        textAlign: 'center',
                        pointerEvents: 'none',
                    }}>
                        {usageCount}
                    </span>
                )}
            </div>
            <span style={{ display: 'block', fontSize: '10px', color: 'var(--text-secondary)', marginTop: '2px', textAlign: 'center' }}>{timelineCutPickerDurationLabel(cut, fastPacing)}</span>
        </button>
    );
});

/** Fast-cut (flipbook) intro duration — matches backend/lambda (15 × 0.12s). */
const FLIPBOOK_INTRO_DURATION_SEC = 1.8;

/** Total timeline seconds required: voiceover-based slots, or sum of ordered cuts when no duration. */
function computeTimelineRequiredTotalSeconds({ voiceoverSeconds, fastPacing, orderedCuts }) {
    const slot = fastPacing ? 1 : 2;
    if (voiceoverSeconds > 0) {
        return Math.ceil(voiceoverSeconds / slot) * slot;
    }
    if (orderedCuts && orderedCuts.length > 0) {
        return orderedCuts.reduce((acc, c) => acc + timelineCutEffectiveSeconds(c, fastPacing), 0);
    }
    return null;
}

function getVoiceoverSecondsFromTimestamps(timestamps) {
    const arr = timestamps?.character_end_times_seconds;
    if (!Array.isArray(arr) || arr.length === 0) return 0;
    return Math.max(...arr);
}

/** Resolve required main-timeline seconds (excludes auto-prepended flipbook cuts). */
function resolveTimelineRequiredSeconds({
    voiceoverSeconds,
    fastPacing,
    orderedCuts,
    usedDurations,
    flipbookIntro,
    isNoScriptMode,
    noScriptVideoLength,
}) {
    if (isNoScriptMode) {
        return computeTimelineRequiredTotalSeconds({
            voiceoverSeconds: noScriptVideoLength,
            fastPacing,
            orderedCuts: null,
        });
    }

    const flipbookSec = flipbookIntro ? FLIPBOOK_INTRO_DURATION_SEC : 0;
    const fromUsed = sumUsedCutDurationsRaw(usedDurations);
    const voSec = voiceoverSeconds > 0 ? voiceoverSeconds : 0;
    const fromVoiceover = voSec > 0
        ? computeTimelineRequiredTotalSeconds({
            voiceoverSeconds: Math.max(0, voSec - flipbookSec),
            fastPacing,
            orderedCuts: null,
        })
        : 0;

    // Voiceover-based target matches backend cut selection when flipbook intro is on.
    if (flipbookIntro && fromVoiceover > 0) {
        return fromVoiceover;
    }

    if (fromUsed > 0) {
        if (flipbookIntro && fromVoiceover === 0) {
            return Math.max(0, fromUsed - flipbookSec);
        }
        return fromUsed;
    }

    if (fromVoiceover > 0) {
        return fromVoiceover;
    }

    return computeTimelineRequiredTotalSeconds({
        voiceoverSeconds: 0,
        fastPacing,
        orderedCuts,
    });
}

function sanitizeVideoFilename(name) {
    const base = (name || '').replace(/[/\\?%*:|"<>]/g, '').replace(/\s+/g, ' ').trim().slice(0, 88);
    return base || 'merra-video';
}

/** merra-[first-words-of-script-content]-[timestamp].mp4 — prefers content over thumbnail title. */
function buildMerraVideoDownloadFilename(scriptContent, { wordCount = 6, maxSlugLen = 56 } = {}) {
    const words = String(scriptContent || '')
        .replace(/[\r\n]+/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, wordCount)
        .join(' ');
    const slug = words
        .replace(/[/\\?%*:|"<>]/g, '')
        .replace(/[^a-zA-Z0-9\s_-]/g, '')
        .trim()
        .replace(/[\s_]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .toLowerCase()
        .slice(0, maxSlugLen)
        .replace(/-$/, '');
    return `merra-${slug || 'video'}-${Date.now()}.mp4`;
}

/** Hand off to the browser download manager (no in-memory blob fetch). */
function triggerDirectVideoDownload(url, filename) {
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    if (filename) link.download = filename;
    link.rel = 'noopener';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

async function resolveStudioVideoDownloadUrl(workspaceId, scriptId, filename, fallbackUrl) {
    let directUrl = fallbackUrl;
    try {
        const downloadResp = await api.getWorkspaceStudioVideoDownloadUrl(workspaceId, scriptId, {
            params: { filename },
        });
        directUrl =
            downloadResp?.data?.downloadUrl
            || downloadResp?.data?.url
            || downloadResp?.data
            || fallbackUrl;
    } catch (_) {
        // Fallback to script.videoUrl
    }
    return directUrl;
}

function getOrbGradientForString(str) {
    if (!str || typeof str !== 'string') return null;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash) + str.charCodeAt(i);
        hash |= 0;
    }
    hash = (hash ^ (hash >>> 16)) * 0x85ebca6b;
    hash = (hash ^ (hash >>> 13)) * 0xc2b2ae35;
    hash = (hash >>> 0) ^ (hash >>> 16);
    const hue = Math.abs(hash) % 360;
    const magenta = `hsl(${hue}, 75%, 45%)`;
    const red = `hsl(${(hue + 25) % 360}, 80%, 50%)`;
    const orange = `hsl(${(hue + 50) % 360}, 85%, 55%)`;
    const yellow = `hsl(${(hue + 70) % 360}, 90%, 65%)`;
    return {
        background: `radial-gradient(circle at 75% 25%, rgba(255,255,255,0.25) 0%, transparent 30%), radial-gradient(ellipse 120% 100% at 20% 60%, ${magenta} 0%, ${red} 25%, ${orange} 50%, ${yellow} 85%, transparent 100%)`,
        boxShadow: 'inset -5px 6px 20px rgba(0,0,0,0.3)'
    };
}

// SmoothScaleCarousel component
const SmoothScaleCarousel = ({ items = [], compact = false }) => {
  const N = items.length;
  
  // Detect mobile device
  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 768px)').matches;
  });

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleChange = (e) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  
  // Create infinite loop by repeating items multiple times
  // Reduce copies on mobile for better performance
  const copies = isMobile || compact ? 1 : 2;
  const extendedItems = React.useMemo(() => {
    const result = [];
    for (let i = 0; i < copies; i++) {
      result.push(...items);
    }
    return result;
  }, [items, copies]);
  
  const [index, setIndex] = React.useState(Math.floor(N / 2)); // Start near middle of first copy
  const [resting, setResting] = React.useState(true);
  const dwell = compact ? 1000 : 1200;     // pause when centered
  const duration = compact ? 700 : 900;   // movement duration
  const videoRefs = React.useRef({});

  React.useEffect(() => {
    if (!resting) return;
    const id = setTimeout(() => {
      setResting(false);
      setIndex((i) => {
        return i + 1;
      });
    }, dwell);
    return () => clearTimeout(id);
  }, [resting, dwell]);

  // Control video playback - only play videos near center (disabled on mobile)
  React.useEffect(() => {
    if (isMobile || compact) return; // Don't autoplay on mobile / compact preview strip
    
    const PLAY_RANGE = 2; // Play center video and 2 on each side
    
    Object.keys(videoRefs.current).forEach((key) => {
      const video = videoRefs.current[key];
      if (!video) return;
      
      const videoIndex = parseInt(key.split('-')[0]);
      const distanceFromCenter = Math.abs(videoIndex - index);
      
      try {
        if (distanceFromCenter <= PLAY_RANGE) {
          // Play videos near center
          if (video.paused) {
            video.play().catch(() => {}); // Ignore play errors
          }
        } else {
          // Pause videos far from center
          if (!video.paused) {
            video.pause();
          }
        }
      } catch (error) {
        // Ignore playback errors
      }
    });
  }, [index, isMobile, compact]);

  const onTransitionEnd = () => {
    setResting(true);
    // When we reach the end, snap back to start
    if (copies === 1) {
      // For single copy, loop back to start
      if (index >= N) {
        setIndex(0);
      }
    } else {
      // When we reach the end of the 2nd copy, snap back to start of 2nd copy (which is index N)
      if (index >= N * 2) {
        setIndex(N); // Reset to start of second copy
      }
    }
  };

  const VISIBLE = 3;
  const STEP = 100 / VISIBLE;
  const translate = STEP - index * STEP;
  const trackWidth = (extendedItems.length / VISIBLE) * 100;
  const cardRadius = compact ? '14px' : '16px';

  // Early return after all hooks
  if (N === 0) return null;

  return (
    <div style={{ overflowX: "hidden", width: compact ? '100%' : undefined, height: compact ? '100%' : undefined }}>
    <style>{`
      @keyframes carousel-green-scan {
        0% { top: -48px; }
        28.57% { top: 100%; }
        57.14% { top: -48px; }
        85.71% { top: 100%; }
        100% { top: 100%; }
      }
      .carousel-scan-overlay {
        position: absolute;
        inset: 0;
        border-radius: inherit;
        overflow: hidden;
        pointer-events: none;
        z-index: 3;
      }
      .carousel-scan-band {
        position: absolute;
        left: 0;
        right: 0;
        height: 48px;
        background: linear-gradient(
          to bottom,
          rgba(0, 255, 136, 0),
          rgba(0, 255, 160, 0.35),
          rgba(100, 255, 180, 0.25),
          rgba(0, 255, 136, 0)
        );
        animation: carousel-green-scan 3.5s ease-in-out infinite;
      }
    `}</style>
    <div
      style={{
        overflowY: "visible",
        maxWidth: compact ? '100%' : 800,
        margin: "0 auto",
        position: "relative",
        width: compact ? '100%' : undefined,
        height: compact ? '100%' : 440,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginTop: compact ? 0 : 30,
      }}
    >
      <div
        onTransitionEnd={onTransitionEnd}
        style={{
          display: "flex",
          transition: `transform ${duration}ms cubic-bezier(0.4,0,0.2,1)`,
          transform: `translateX(${translate}%)`,
          width: `${trackWidth}%`,
          height: compact ? '100%' : undefined,
          alignItems: "center",
        }}
      >
        {extendedItems.map((cut, i) => {
          const isCenter = i === index;
          const scale = isCenter ? (compact ? 1 : 1.15) : (compact ? 0.72 : 0.85);
          const opacity = isCenter ? 1 : (compact ? 0.5 : 0.7);
          const distanceFromCenter = Math.abs(i - index);
          const shouldPlay = !isMobile && !compact && distanceFromCenter <= 2;
          
          return (
            <div
              key={`${i}-${cut._id}`}
              style={{
                flex: `0 0 ${STEP}%`,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: compact ? '100%' : undefined,
                transition: "transform 600ms ease, opacity 600ms ease",
                transform: `scale(${scale})`,
                opacity,
              }}
            >
              <div
                style={{
                  position: 'relative',
                  ...(compact
                    ? { height: '94%', width: 'auto' }
                    : { width: '55%' }),
                  aspectRatio: '9 / 16',
                  borderRadius: cardRadius,
                  overflow: 'hidden',
                  backgroundColor: 'var(--background-primary)',
                  boxShadow: isCenter
                    ? (compact ? "0 8px 20px rgba(0,0,0,0.32)" : "0 20px 40px rgba(0,0,0,0.35)")
                    : (compact ? "0 3px 8px rgba(0,0,0,0.18)" : "0 10px 20px rgba(0,0,0,0.2)"),
                }}
              >
                <video
                  ref={(el) => {
                    if (el) videoRefs.current[`${i}-${cut._id}`] = el;
                  }}
                  src={cut.url}
                  playsInline
                  muted
                  autoPlay={shouldPlay}
                  loop={shouldPlay}
                  preload={shouldPlay ? "auto" : "metadata"}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                  onLoadedMetadata={(e) => {
                    try {
                      e.currentTarget.currentTime = e.currentTarget.duration / 2;
                    } catch {}
                  }}
                />
                <div className="carousel-scan-overlay" aria-hidden="true">
                  <div className="carousel-scan-band" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
    </div>
  );
};

// Studio generation examples
const studioExamples = [
    'analyzing your hook structure',
    'crafting personalized content',
    'generating authentic storytelling',
    'optimizing for engagement',
    'creating conversational flow',
    'building emotional connection',
    'developing unique angles',
    'refining your message',
    'enhancing authenticity',
    'perfecting the narrative'
];

// Studio animation styles
const studioFadeTransitionStyle = `
    @keyframes studioFadeInOut {
        0% {
            opacity: 0;
            transform: translateY(10px);
        }
        20%, 80% {
            opacity: 1;
            transform: translateY(0);
        }
        100% {
            opacity: 0;
            transform: translateY(-10px);
        }
    }

    @keyframes studioFadeIn {
        0% {
            opacity: 0;
            transform: translateY(10px);
        }
        100% {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .studio-example-text {
        animation: studioFadeInOut 3s;
        position: absolute;
        left: 0;
        right: 0;
    }

    .studio-example-text-final {
        animation: studioFadeIn 1s;
        animation-fill-mode: forwards;
        position: absolute;
        left: 0;
        right: 0;
    }

    @keyframes titleCardDemoFadeUp {
        0%, 8% { opacity: 0; transform: translateY(8px); }
        18%, 100% { opacity: 1; transform: translateY(0); }
    }

    @keyframes titleCardDemoReveal {
        0%, 5% { opacity: 0; }
        12%, 100% { opacity: 1; }
    }

    @keyframes titleCardDemoFadeUp {
        0%, 8% { opacity: 0; transform: translateY(8px); }
        18%, 100% { opacity: 1; transform: translateY(0); }
    }

    @keyframes titleCardDemoReveal {
        0%, 5% { opacity: 0; }
        12%, 100% { opacity: 1; }
    }

    .title-card-demo-style1-line {
        animation: titleCardDemoFadeUp 3.6s ease-out infinite;
    }

    .title-card-demo-style2-word {
        animation: titleCardDemoReveal 3.2s ease-out infinite;
        display: inline;
    }

    .title-card-demo-style3-line {
        animation: titleCardDemoFadeUp 3.2s ease-out infinite;
    }

    @keyframes fastCutDemoFrame {
        0%, 11% { opacity: 1; }
        12%, 100% { opacity: 0; }
    }

    .fast-cut-demo-frame {
        position: absolute;
        inset: 0;
        opacity: 0;
        animation: fastCutDemoFrame 1.6s steps(1, end) infinite;
        background-size: cover;
        background-position: center;
    }
`;

// Default voices for Studio with preview URLs
const DEFAULT_VOICES = [
    {
        id: 'nPczCjzI2devNBz1zQrb',
        name: 'Brian',
    },
    // { 
    //     id: 'st7NwhTPEzqo2riw7qWC', 
    //     name: 'Blondie Radio Host',
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_blondie+-+radio+host.mp3'
    // },
    // { 
    //     id: '6OzrBCQf8cjERkYgzSg8', 
    //     name: 'Young Jamal',
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_young+jamal.mp3'
    // },
    // { 
    //     id: 'f5HLTX707KIM4SzJYzSz', 
    //     name: "Hey it's Brad",
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_hey+its+brad+-+happy+to+help.mp3'
    // },
    // { 
    //     id: 'kPzsL2i3teMYv0FxEYQ6', 
    //     name: 'Brittney Youthful',
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_brittney+-+social+media+voice+-+fun%2C+youthful+%26+informative.mp3'
    // },
    // { 
    //     id: 'RXtWW6etvimS8QJ5nhVk', 
    //     name: 'Fiona Chill',
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_fiona+-+chill%2C+natural+%26+real.mp3'
    // },
    // { 
    //     id: '6u6JbqKdaQy89ENzLSju', 
    //     name: 'Brielle Podcast',
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_brielle+-+podcast+girl+extremely+natural.mp3'
    // },
    // { 
    //     id: '19STyYD15bswVz51nqLf', 
    //     name: 'Samara Elegant Relaxed',
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_samara+x.mp3'
    // },
    // { 
    //     id: 'BZgkqPqms7Kj9ulSkVzn', 
    //     name: 'Eve Energy',
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_eve.mp3'
    // },
    // { 
    //     id: '6aDn1KB0hjpdcocrUkmq', 
    //     name: 'Tiffany Natural',
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_tiffany+-+realistic+conversations.mp3'
    // },
    // { 
    //     id: 'edRtkKm7qEwZ8pH9ggtf', 
    //     name: 'Antoine Confident',
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_antoine+.mp3'
    // },
    // { 
    //     id: 'kmSVBPu7loj4ayNinwWM', 
    //     name: 'Archie British',
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_archie+-+english+teen+youth.mp3'
    // },
    // { 
    //     id: 'nXIYu9FT5meibkBbZFT7', 
    //     name: 'James Sales Pro',
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_james+-+sales+professional+.mp3'
    // },
    // { 
    //     id: 'ASpz5ro8ZOxcwQMVjP7I', 
    //     name: 'Serious Longmire',
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_spc.+longmire.mp3'
    // },
    // { 
    //     id: 'tQbs4WJdeIOdank6mubQ', 
    //     name: 'Joshua Calm',
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_joshua+-+calm+casual+confident+explainer.mp3'
    // },
    // { 
    //     id: 'y4PCE6siR4nMByEcTk5o', 
    //     name: 'Chad California',
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_chad+-+southern+california+.mp3'
    // },
    // { 
    //     id: 'UkO7OCLgMp3WYf4UPjE5', 
    //     name: 'Tiago Reassuring (PT)',
    //     previewUrl: 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_tiago+-+serious+and+reassuring.mp3'
    // }
];

// Recording Examples
const RECORDING_EXAMPLES = [
    // Introduction & Context
    "Hi, I noticed your impressive work at [Company] and wanted to reach out.",
    "I've been following [Company]'s growth in the [industry] space.",
    "Your recent post about [topic] really resonated with me.",
    "Your background in [field] caught my attention.",
    "I saw your team is expanding the [department/initiative] and thought I'd reach out.",

    // Value Proposition
    "We help companies like yours improve [metric] by [percentage].",
    "Our solution has helped similar businesses save [X] hours per week.",
    "We've helped other [job title]s increase their team's productivity by [percentage].",
    "Our platform typically reduces [pain point] by [metric].",
    "Companies similar to yours have seen [specific result] within [timeframe].",

    // Industry-Specific
    "The [industry] landscape is rapidly changing, especially regarding [trend].",
    "Many [job title]s we work with struggle with [common challenge].",
    "In today's [industry] environment, [pain point] is more crucial than ever.",
    "With recent changes in [regulation/technology], many companies are looking to [goal].",
    "We've noticed a growing trend in [industry] where [observation].",

    // Problem Recognition
    "Many teams face challenges with [common pain point].",
    "I understand how challenging [specific task] can be.",
    "One common issue we see is [problem description].",
    "Teams often struggle to balance [competing priority A] with [priority B].",
    "The complexity of [process] often leads to [negative outcome].",

    // Solution Overview
    "Our approach simplifies [complex process] through [method].",
    "We've developed a unique solution for [specific challenge].",
    "Our platform automates [manual process] while maintaining [important aspect].",
    "We combine [technology A] with [technology B] to achieve [outcome].",
    "Our solution integrates seamlessly with [common tools] to improve [process].",

    // Results & Benefits
    "Clients typically see ROI within [timeframe].",
    "Our average customer saves [amount] per month.",
    "Teams report [percentage] improvement in [metric] after implementation.",
    "We've helped companies reduce [cost/time] by up to [percentage].",
    "Users experience [benefit] while maintaining [important factor].",

    // Social Proof
    "Companies like [known brand] have achieved [result] using our solution.",
    "We recently helped a [industry] company overcome [challenge].",
    "One of our clients in [industry] reported [specific outcome].",
    "A similar [company type] saw [improvement] within [timeframe].",
    "We've worked with several teams in [industry] to improve [metric].",

    // Use Cases
    "This works particularly well for teams that need to [specific use case].",
    "It's especially useful when you're trying to [achieve goal].",
    "Many teams use this to streamline their [process].",
    "This solution is perfect for organizations looking to [objective].",
    "We've seen great results with teams focusing on [specific goal].",

    // Implementation & Support
    "The implementation process typically takes [timeframe].",
    "Our support team provides [type of support] throughout the process.",
    "We offer comprehensive training for [feature/process].",
    "The onboarding process is designed to minimize [common concern].",
    "Our team handles all aspects of [technical process].",

    // Future-Focused
    "We're constantly developing new features for [emerging need].",
    "Our roadmap includes exciting developments in [area].",
    "We're preparing for [industry trend] with [feature/capability].",
    "Our solution adapts to [changing circumstance] automatically.",
    "We're investing heavily in [future technology/capability]."
];

const ONBOARDING_REFERRAL_OPTIONS = [
    'TikTok', 'Instagram', 'X (Twitter)', 'Reddit', 'Linkedin', 'Youtube',
    'Discord', 'Google', 'Product Hunt', 'AI', 'Friend or Colleague', 'Other'
];

/** CSS approximation of THUMBNAIL_COLOR_GRADE_VF in lambda (preview only). */
const THUMBNAIL_COLOR_GRADE_CSS_FILTER = 'contrast(1.25) saturate(1.3) brightness(0.98) sepia(0.05) hue-rotate(-4deg)';

const WorkspaceManagement = ({ disableCuts, onboardingCompleted = true, referralSource = '', onReferralSourceSaved, onOnboardingComplete, onSetupProgress, onSetupStep3Complete }) => {
    const { darkMode } = useDarkMode();
    const navigate = useNavigate();
    const [onboardingReferralSource, setOnboardingReferralSource] = useState('');
    const currency = getCurrencySymbol();
    
    // Instant glow feedback when a generation completes
    const [completionGlowActive, setCompletionGlowActive] = useState(false);
    const completionGlowTimeoutRef = useRef(null);
    const COMPLETION_GLOW_MS = 1450;
    const triggerCompletionGlow = (colors) => {
        try {
            const palette = colors?.length === 2 ? colors : ['#22c55e', '#16a34a'];
            if (completionGlowTimeoutRef.current) {
                clearTimeout(completionGlowTimeoutRef.current);
            }
            // Restart animation even if it was already active
            setCompletionGlowActive(false);
            document.body.classList.remove('wm-completion-glow');
            requestAnimationFrame(() => {
                setCompletionGlowActive(true);
                try {
                    // Used by both the full-screen overlay and the card glow
                    document.body.style.setProperty('--wm-glow-1', palette[0]);
                    document.body.style.setProperty('--wm-glow-2', palette[1]);
                } catch (e) {
                    // ignore
                }
                document.body.classList.add('wm-completion-glow');
                completionGlowTimeoutRef.current = setTimeout(() => {
                    setCompletionGlowActive(false);
                    document.body.classList.remove('wm-completion-glow');
                 }, COMPLETION_GLOW_MS);
            });
        } catch (e) {
            // Non-critical UI effect; ignore failures
        }
    };
    useEffect(() => {
        return () => {
            if (completionGlowTimeoutRef.current) {
                clearTimeout(completionGlowTimeoutRef.current);
            }
            try {
                document.body.classList.remove('wm-completion-glow');
                document.body.style.removeProperty('--wm-glow-1');
                document.body.style.removeProperty('--wm-glow-2');
            } catch (e) {
                // ignore
            }
        };
    }, []);

    // Helper function to play completion sounds
    const playCompletionSound = (soundName) => {
        try {
            const audio = new Audio(`/audio/${soundName}.mp3`);
            audio.volume = 0.3; // Set volume to 30%
            audio.play().catch(err => {
                console.warn(`Failed to play ${soundName} sound:`, err);
            });
        } catch (error) {
            console.warn(`Error playing ${soundName} sound:`, error);
        }
    };

    const BATCH_GLOW_MS = 1450;
    const triggerBatchScriptGlow = (scriptId, type) => {
        if (batchScriptGlowTimeoutRef.current) clearTimeout(batchScriptGlowTimeoutRef.current);
        const colors = type === 'script' ? ['#22c55e', '#16a34a'] : type === 'voiceover' ? ['#667eea', '#764ba2'] : ['#f093fb', '#f5576c'];
        try {
            document.body.style.setProperty('--wm-glow-1', colors[0]);
            document.body.style.setProperty('--wm-glow-2', colors[1]);
        } catch (_) {}
        setBatchScriptGlow({ scriptId, type });
        batchScriptGlowTimeoutRef.current = setTimeout(() => {
            setBatchScriptGlow(null);
            batchScriptGlowTimeoutRef.current = null;
        }, BATCH_GLOW_MS);
    };
    const {
        currentWorkspace,
        updateWorkspace,
        deleteWorkspace,
        generateInviteLink,
        updateMemberRole,
        removeMember,
        leaveWorkspace,
        hasPermission,
        getUserRole,
        setWorkspaceCounts
    } = useWorkspace();

    const tabContext = useWorkspaceTab();
    const [localActiveTab, setLocalActiveTab] = useState('profile');
    const activeTab = tabContext?.activeTab ?? localActiveTab;
    const setActiveTab = tabContext?.setActiveTab ?? setLocalActiveTab;
    const [hasVisitedProfile, setHasVisitedProfile] = useState(false);
    const [hasVisitedCutsTab, setHasVisitedCutsTab] = useState(false);
    const [hasVisitedVoiceTab, setHasVisitedVoiceTab] = useState(false);

    // Restore persisted tab-visit flags from workspace
    useEffect(() => {
        const progress = currentWorkspace?.setupStepsProgress || {};
        setHasVisitedCutsTab(!!progress.visitedCutsTab);
        setHasVisitedProfile(!!progress.visitedProfileTab);
        setHasVisitedVoiceTab(!!progress.visitedVoiceTab);
    }, [currentWorkspace?._id, currentWorkspace?.setupStepsProgress]);

    const persistSetupStep = useCallback(async (updates) => {
        if (!currentWorkspace?._id) return;
        const current = currentWorkspace.setupStepsProgress || {};
        const filtered = Object.fromEntries(
            Object.entries(updates).filter(([key, val]) => val && !current[key])
        );
        if (Object.keys(filtered).length === 0) return;
        try {
            await updateWorkspace(currentWorkspace._id, {
                setupStepsProgress: { ...current, ...filtered }
            }, { skipLoading: true });
        } catch (err) {
            console.warn('Failed to persist setup step progress:', err);
        }
    }, [currentWorkspace?._id, currentWorkspace?.setupStepsProgress, updateWorkspace]);
    const tabNavScrollRef = useRef(null);
    const [tabCanScrollLeft, setTabCanScrollLeft] = useState(false);
    const [tabCanScrollRight, setTabCanScrollRight] = useState(false);
    
    // Video management state
    const [videos, setVideos] = useState([]);
    const [generatedVideos, setGeneratedVideos] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [hasMore, setHasMore] = useState(true);
    const [allWorkspaceTags, setAllWorkspaceTags] = useState([]); // All tags from backend
    const [isLoadingPage, setIsLoadingPage] = useState(false);
    const loadMoreRef = useRef(null);
    const [uploadProgress, setUploadProgress] = useState({});
    const [isDragging, setIsDragging] = useState(false);
    const [editingVideo, setEditingVideo] = useState(null);
    const [tagInputs, setTagInputs] = useState({});
    const [newDescription, setNewDescription] = useState('');
    const [errors, setErrors] = useState([]);
    const [batchUploadTags, setBatchUploadTags] = useState([]); // Changed to array for chips
    const [batchUploadTagInput, setBatchUploadTagInput] = useState(''); // Input value for batch tags
    const [videoSearchQuery, setVideoSearchQuery] = useState('');
    const [showBatchTagSuggestions, setShowBatchTagSuggestions] = useState(false);
    const [showSearchTagSuggestions, setShowSearchTagSuggestions] = useState(false);
    const batchTagInputRef = useRef(null);
    const searchInputRef = useRef(null);
    const [importingInstagram, setImportingInstagram] = useState(false);
    
    // Tag suggestion dropdowns state
    const [tagSuggestionDropdowns, setTagSuggestionDropdowns] = useState({});
    const tagInputRefs = useRef({});
    
    // Studio generation tag settings
    const [includeTags, setIncludeTags] = useState([]);
    const [excludeTags, setExcludeTags] = useState([]);
    const [includeTagInput, setIncludeTagInput] = useState('');
    const [excludeTagInput, setExcludeTagInput] = useState('');
    const [showIncludeTagSuggestions, setShowIncludeTagSuggestions] = useState(false);
    const [showExcludeTagSuggestions, setShowExcludeTagSuggestions] = useState(false);
    const [matchingCutsCount, setMatchingCutsCount] = useState(null);
    const includeTagInputRef = useRef(null);
    const excludeTagInputRef = useRef(null);
    const fontMixingSecondColorInputRef = useRef(null);
    const thumbnailFontColorInputRef = useRef(null);
    const studioVlogFontColorInputRef = useRef(null);
    const studioVlogOutlineColorInputRef = useRef(null);
    const studioTextFontColorInputRef = useRef(null);
    const studioTextOutlineColorInputRef = useRef(null);
    const [slowMotion, setSlowMotion] = useState(false);
    const [cutSilenceMode, setCutSilenceMode] = useState(() => {
        const saved = localStorage.getItem('workspace_cut_silence_mode');
        return saved === 'true';
    });
    const [cutMoreSilenceMode, setCutMoreSilenceMode] = useState(() => {
        const saved = localStorage.getItem('workspace_cut_more_silence_mode');
        return saved === 'true';
    });
    const [aiSoundEffects, setAiSoundEffects] = useState(() => {
        const saved = localStorage.getItem('workspace_ai_sound_effects');
        return saved === 'true';
    });
    const [speedUp, setSpeedUp] = useState(() => {
        const saved = localStorage.getItem('workspace_speed_up');
        return saved === 'true';
    });
    const [fastPacing, setFastPacing] = useState(() => {
        const saved = localStorage.getItem('workspace_fast_pacing');
        return saved === 'true';
    });
    const [createThumbnail, setCreateThumbnail] = useState(false);
    const [thumbnailCut, setThumbnailCut] = useState(null);
    const [thumbnailTitle, setThumbnailTitle] = useState('');
    const [thumbnailBigText, setThumbnailBigText] = useState(true);
    const [thumbnailFontId, setThumbnailFontId] = useState('');
    const [thumbnailFontColor, setThumbnailFontColor] = useState('#ffffff');
    const [showThumbnailFontDropdown, setShowThumbnailFontDropdown] = useState(false);
    const [showThumbnailModal, setShowThumbnailModal] = useState(false);
    const [thumbnailAvailableCuts, setThumbnailAvailableCuts] = useState([]);
    const [thumbnailCutsLoading, setThumbnailCutsLoading] = useState(false);
    const thumbnailPreviewVideoRef = useRef(null);
    // Per-script thumbnail config for batch tab: { [scriptId]: { title, cut, bigText } }
    const [batchThumbnailByScriptId, setBatchThumbnailByScriptId] = useState({});
    const [batchThumbnailModalScriptId, setBatchThumbnailModalScriptId] = useState(null);
    // Text-based batch cards: selected cut per script (for video generation); modal to change cut
    const [batchTextSelectedCutByScriptId, setBatchTextSelectedCutByScriptId] = useState({});
    const [batchTextCutModalScriptId, setBatchTextCutModalScriptId] = useState(null);
    const [batchTextAvailableCuts, setBatchTextAvailableCuts] = useState([]);
    const [batchTextCutsLoading, setBatchTextCutsLoading] = useState(false);
    useEffect(() => {
        if (!showThumbnailModal || !thumbnailCut) return;
        const el = thumbnailPreviewVideoRef.current;
        if (el) {
            el.currentTime = 0;
        } else {
            const t = setTimeout(() => {
                const e = thumbnailPreviewVideoRef.current;
                if (e) e.currentTime = 0;
            }, 100);
            return () => clearTimeout(t);
        }
    }, [showThumbnailModal, thumbnailCut]);
    const [showStudioSettingsDropdown, setShowStudioSettingsDropdown] = useState(false);
    const [showSkipSettingsDropdown, setShowSkipSettingsDropdown] = useState(false);
    const studioSettingsDropdownRef = useRef(null);
    const skipSettingsDropdownRef = useRef(null);
    const [downloadingVideos, setDownloadingVideos] = useState(new Set());
    const [editingGeneratedVideoId, setEditingGeneratedVideoId] = useState(null);
    const [editingGeneratedVideoTitle, setEditingGeneratedVideoTitle] = useState('');
    const [generatingCuts, setGeneratingCuts] = useState(new Set());
    const [generatingShorts, setGeneratingShorts] = useState(new Set());
    const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
    const [copiedDescriptions, setCopiedDescriptions] = useState(new Set());
    const fileInputRef = useRef(null);
    const audioImportRef = useRef(null);
    const [showMobilePickerNotice, setShowMobilePickerNotice] = useState(false);
    const [mobilePickerNoticeDismissed, setMobilePickerNoticeDismissed] = useState(() => {
        try {
            return localStorage.getItem('workspace_mobile_picker_notice_dismissed') === '1';
        } catch {
            return false;
        }
    });
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [newCut, setNewCut] = useState({ name: '', url: '' });
    const [isLoading, setIsLoading] = useState(true);
    const [rawTotal, setRawTotal] = useState(0);
    const [generatedTotal, setGeneratedTotal] = useState(0);
    const [generatedPage, setGeneratedPage] = useState(1);
    const [generatedHasMore, setGeneratedHasMore] = useState(true);
    const [isLoadingGeneratedPage, setIsLoadingGeneratedPage] = useState(false);
    const generatedVideosLoadMoreRef = useRef(null);
    
    // Cuts state
    const [workspaceCuts, setWorkspaceCuts] = useState([]);
    const [cutsTagFilter, setCutsTagFilter] = useState('');
    const [cutsLoading, setCutsLoading] = useState(false);
    const [cutsTotal, setCutsTotal] = useState(0);
    const [cutsPage, setCutsPage] = useState(1);
    const [cutsPageSize] = useState(40);
    const [cutsHasMore, setCutsHasMore] = useState(false);
    const [carouselCuts, setCarouselCuts] = useState([]); // matching cuts for generation carousel
    const carouselCutsRequestIdRef = useRef(0);
    const hiddenCutsRef = useRef(new Set()); // Track locally hidden cuts to handle pagination shifts
    const cutsLoadMoreRef = useRef(null);
    const cutsPollingIntervalRef = useRef(null); // Single global polling interval
    
    // Upload tracking state
    const [uploadStats, setUploadStats] = useState({
        totalFiles: 0,
        currentFileIndex: 0,
        completedFiles: 0,
        failedFiles: 0,
        currentFileName: ''
    });
    const [activeUploads, setActiveUploads] = useState(new Map()); // Track active XMLHttpRequests

    // Studio state
    const [studioHook, setStudioHook] = useState('');
    const [studioTopic, setStudioTopic] = useState('');
    const [showTopicInput, setShowTopicInput] = useState(true);
    const [availableHooks, setAvailableHooks] = useState([]);
    const [hookStats, setHookStats] = useState({ totalHooks: 0, availableHooks: 0, usedHooks: 0 });
    const [studioGenerating, setStudioGenerating] = useState(false);
    const [studioAutoCreateMode, setStudioAutoCreateMode] = useState(false);
    const [selectedVoiceId, setSelectedVoiceId] = useState('');
    const [availableVoices, setAvailableVoices] = useState([]);
    const [availableMusic, setAvailableMusic] = useState([]);
    const [selectedMusicId, setSelectedMusicId] = useState('');
    // Track music selection state for regeneration options
    const [musicSelectionChanged, setMusicSelectionChanged] = useState(false);
    const [lastUsedMusicId, setLastUsedMusicId] = useState(null);
    const [lastUsedMusicName, setLastUsedMusicName] = useState(null);
    const [musicRegenerationMode, setMusicRegenerationMode] = useState('keep'); // 'keep' or 'random'
    // Background music toggle - saved in localStorage
    const [useMusic, setUseMusic] = useState(() => {
        const saved = localStorage.getItem('workspace_use_music');
        return saved !== null ? saved === 'true' : true; // default to true
    });

    const openWorkspaceFilePicker = (e) => {
        // Keep it as a direct user gesture; show a mobile notice first on small screens.
        if (e?.preventDefault) e.preventDefault();
        if (screenWidth <= 768 && !mobilePickerNoticeDismissed) {
            setShowMobilePickerNotice(true);
            return;
        }
        fileInputRef.current?.click();
    };
    const [userFonts, setUserFonts] = useState([]);
    const [selectedFontId, setSelectedFontId] = useState('');
    // Video settings toggles - saved in localStorage
    const [bigText, setBigText] = useState(() => {
        const saved = localStorage.getItem('workspace_big_text');
        return saved === 'true'; // default to false
    });
    const [zoomWhoosh, setZoomWhoosh] = useState(() => {
        const saved = localStorage.getItem('workspace_zoom_whoosh');
        return saved === 'true'; // default to false
    });
    const [flipbookIntro, setFlipbookIntro] = useState(() => {
        const saved = localStorage.getItem('workspace_flipbook_intro');
        return saved === 'true'; // default to false
    });
    const [openingLineInCenter, setOpeningLineInCenter] = useState(() => {
        const saved = localStorage.getItem('workspace_opening_line_in_center');
        return saved === 'true'; // default to false
    });
    // Opening line text size - saved in localStorage
    const [openingLineTextSize, setOpeningLineTextSize] = useState(() => {
        const saved = localStorage.getItem('workspace_opening_line_text_size');
        return saved || 'big'; // default to 'big'
    });
    const [openingLineFontId, setOpeningLineFontId] = useState(() => {
        const saved = localStorage.getItem('workspace_opening_line_font_id');
        return saved || '';
    });
    const [openingLineFontColor, setOpeningLineFontColor] = useState(() => {
        const saved = localStorage.getItem('workspace_opening_line_font_color');
        return saved || '#ffffff';
    });
    const [showOpeningLineFontDropdown, setShowOpeningLineFontDropdown] = useState(false);
    const [subtitlePosition, setSubtitlePosition] = useState(() => {
        const saved = localStorage.getItem('workspace_subtitle_position');
        return saved || 'bottom'; // default to 'bottom'
    });
    const [enableSubtitles, setEnableSubtitles] = useState(() => {
        const saved = localStorage.getItem('workspace_enable_subtitles');
        return saved !== null ? saved === 'true' : true; // default to true
    });
    const [oneWordPerSubtitle, setOneWordPerSubtitle] = useState(() => {
        const saved = localStorage.getItem('workspace_one_word_per_subtitle');
        return saved === 'true'; // default to false
    });
    const BOUKAR_USER_ID = '673247877e4ac0a824cbac0d';
    const [studioSubTab, setStudioSubTab] = useState(() => {
        try {
            const saved = localStorage.getItem('workspace_studio_sub_tab');
            if (saved === 'vlog' || saved === 'text-on-screen' || saved === 'carousel' || saved === 'visual-only') return saved;
        } catch (_) {}
        return 'vlog';
    });
    // Text-on-screen studio (type: text scripts — no voiceover)
    const [textStudioScriptId, setTextStudioScriptId] = useState(null);
    const [textStudioContent, setTextStudioContent] = useState('');
    const [textStudioDescription, setTextStudioDescription] = useState('');
    const [textStudioSelectedCut, setTextStudioSelectedCut] = useState(null);
    const [textStudioGeneratingOverlay, setTextStudioGeneratingOverlay] = useState(false);
    const [textStudioGeneratingDescription, setTextStudioGeneratingDescription] = useState(false);
    const [textStudioGeneratingVideo, setTextStudioGeneratingVideo] = useState(false);
    const [textStudioVideoProgress, setTextStudioVideoProgress] = useState(0);
    const [textStudioVideoUrl, setTextStudioVideoUrl] = useState('');
    const [textStudioShowCutModal, setTextStudioShowCutModal] = useState(false);
    const [textStudioCuts, setTextStudioCuts] = useState([]);
    const [textStudioCutsLoading, setTextStudioCutsLoading] = useState(false);
    const [textStudioHook, setTextStudioHook] = useState('');
    const [textStudioTopic, setTextStudioTopic] = useState('');
    const [textStudioShowHookInput, setTextStudioShowHookInput] = useState(false);
    const [textStudioShowTopicInput, setTextStudioShowTopicInput] = useState(true);
    const [textStudioContentUnlocked, setTextStudioContentUnlocked] = useState(false);
    const [textStudioPublishingVideo, setTextStudioPublishingVideo] = useState(false);
    const [textStudioVideoSaved, setTextStudioVideoSaved] = useState(false);
    const [fontMixing, setFontMixing] = useState(() => {
        const saved = localStorage.getItem('workspace_font_mixing');
        return saved === 'true'; // default to false
    });
    const [centerStackOpening, setCenterStackOpening] = useState(() => {
        const saved = localStorage.getItem('workspace_center_stack_opening');
        return saved === 'true'; // default to false
    });
    const [centerStackLineByLine, setCenterStackLineByLine] = useState(() => {
        const saved = localStorage.getItem('workspace_center_stack_line_by_line');
        return saved === 'true'; // default to false (Style 3)
    });
    const [boukisIntro, setBoukisIntro] = useState(() => {
        const saved = localStorage.getItem('workspace_boukis_intro');
        return saved === 'true';
    });
    const [boukisIntroPitch, setBoukisIntroPitch] = useState(() => {
        const saved = localStorage.getItem('workspace_boukis_intro_pitch');
        return (saved === 'low' || saved === 'high') ? saved : 'high';
    });
    const [glowText, setGlowText] = useState(() => {
        const saved = localStorage.getItem('workspace_glow_text');
        return saved === 'true'; // default to false
    });
    const [glowTextColor, setGlowTextColor] = useState(() => {
        const saved = localStorage.getItem('workspace_glow_text_color');
        return saved || 'white'; // default to 'white'
    });
    const [showVoiceDropdown, setShowVoiceDropdown] = useState(false);
    const [playingVoiceId, setPlayingVoiceId] = useState(null);
    const [currentAudio, setCurrentAudio] = useState(null);
    const [playingMusicId, setPlayingMusicId] = useState(null);
    const [currentMusicAudio, setCurrentMusicAudio] = useState(null);
    const [showMusicDropdown, setShowMusicDropdown] = useState(false);
    const [showFontDropdown, setShowFontDropdown] = useState(false);
    const [studioRegeneratingScript, setStudioRegeneratingScript] = useState(false);
    const [studioScripts, setStudioScripts] = useState([]);
    const [studioSelectedScriptId, setStudioSelectedScriptId] = useState(null);
    const [isNoScriptMode, setIsNoScriptMode] = useState(() => {
        try {
            return localStorage.getItem('workspace_studio_sub_tab') === 'visual-only';
        } catch (_) {
            return false;
        }
    });
    const [noScriptScriptId, setNoScriptScriptId] = useState(null);
    const [noScriptVideoLength, setNoScriptVideoLength] = useState(15); // 15 or 30 seconds
    const [studioProgress, setStudioProgress] = useState({ current: 0, total: 1, percent: 0, currentStep: '' });
    
    // Language picker state
    const [generationLanguage, setGenerationLanguage] = useState('en');
    const [translationLanguage, setTranslationLanguage] = useState('es');
    const [isTranslating, setIsTranslating] = useState(false);
    const [showTranslateControls, setShowTranslateControls] = useState(false);
    const [showGenerationLanguageDropdown, setShowGenerationLanguageDropdown] = useState(false);
    const [showTranslationLanguageDropdown, setShowTranslationLanguageDropdown] = useState(false);
    
    // Tag info tooltips state
    const [showIncludeTagsInfo, setShowIncludeTagsInfo] = useState(false);
    const [showExcludeTagsInfo, setShowExcludeTagsInfo] = useState(false);
    const [showTitleCardStyleInfo, setShowTitleCardStyleInfo] = useState(null); // null | 'style1' | 'style2' | 'style3'
    const [showFastCutIntroInfo, setShowFastCutIntroInfo] = useState(false);
    
    // Voice generation mode state (stable/expressive) - saved in localStorage
    const [voiceGenerationMode, setVoiceGenerationMode] = useState(() => {
        const saved = localStorage.getItem('voiceover_voice_generation_mode');
        return saved || 'stable'; // default to stable
    });
    const [showVoiceGenerationModeInfo, setShowVoiceGenerationModeInfo] = useState(false);
    
    // Advanced details visibility state - saved in localStorage
    const [showAdvancedDetails, setShowAdvancedDetails] = useState(() => {
        const saved = localStorage.getItem('workspace_show_advanced_details');
        return saved === 'true'; // default to false
    });

    // Studio generation settings section nav highlight: subtitle | video | audio | tags
    const [studioSettingsTab, setStudioSettingsTab] = useState(() => {
        try {
            const saved = localStorage.getItem('workspace_studio_settings_tab');
            if (saved === 'subtitle' || saved === 'tags' || saved === 'video' || saved === 'audio') return saved;
        } catch (_) {}
        return 'subtitle';
    });
    const studioSettingsListRef = useRef(null);
    const studioSettingsSectionJumpRef = useRef(null); // section id while programmatic jump is in progress
    const studioSettingsJumpUnlockTimerRef = useRef(null);
    const [studioSettingsListEl, setStudioSettingsListEl] = useState(null);
    const [studioSettingsNavHoverId, setStudioSettingsNavHoverId] = useState(null);

    const scrollToStudioSettingsSection = (id) => {
        const root = studioSettingsListRef.current;
        if (!root) return;
        const el = root.querySelector(`[data-studio-section="${id}"]`);
        if (!el) return;
        if (studioSettingsJumpUnlockTimerRef.current) {
            window.clearTimeout(studioSettingsJumpUnlockTimerRef.current);
            studioSettingsJumpUnlockTimerRef.current = null;
        }
        studioSettingsSectionJumpRef.current = id;
        setStudioSettingsTab(id);
        try { localStorage.setItem('workspace_studio_settings_tab', id); } catch (_) {}
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        studioSettingsJumpUnlockTimerRef.current = window.setTimeout(() => {
            studioSettingsSectionJumpRef.current = null;
            studioSettingsJumpUnlockTimerRef.current = null;
        }, 900);
    };
    
    // Screen width state for responsive design
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    
    // Cuts refresh state
    const [isRefreshingCuts, setIsRefreshingCuts] = useState(false);
    const [cutsRefreshInterval, setCutsRefreshInterval] = useState(null);
    
    // Batch creation state
    const [batchScripts, setBatchScripts] = useState([]);
    const batchScriptsRef = useRef([]);
    batchScriptsRef.current = batchScripts;
    const [batchGenerating, setBatchGenerating] = useState(false);
    const [batchProgress, setBatchProgress] = useState({ current: 0, total: 1, percent: 0, currentStep: '' });
    const [batchVideoProgress, setBatchVideoProgress] = useState({ current: 0, total: 0, percent: 0 });
    const [batchVideoProgressByScript, setBatchVideoProgressByScript] = useState({}); // { scriptId: 0-100 }
    const [batchGeneratingVoiceovers, setBatchGeneratingVoiceovers] = useState(new Set());
    const [batchGeneratingVideos, setBatchGeneratingVideos] = useState(new Set());
    const [batchGeneratingTextOverlay, setBatchGeneratingTextOverlay] = useState(new Set());
    const [batchGeneratingDescription, setBatchGeneratingDescription] = useState(new Set());
    const batchPollRef = useRef(null);
    const batchGenerationStartTime = useRef(null);
    const [batchProductionSettingsExpanded, setBatchProductionSettingsExpanded] = useState(() => {
        const saved = localStorage.getItem('batch_production_settings_expanded');
        return saved === 'true'; // default to false (collapsed)
    });
    const [batchUseMusic, setBatchUseMusic] = useState(() => {
        const saved = localStorage.getItem('batch_use_music');
        return saved !== null ? saved === 'true' : true; // default to true
    });
    const [batchScriptMusicSelections, setBatchScriptMusicSelections] = useState({}); // { scriptId: { musicId, musicName, isRandom } }
    const [batchShowMusicDropdowns, setBatchShowMusicDropdowns] = useState({}); // { scriptId: boolean }
    // Per-video override of workspace/device video settings. Missing key = use the live workspace settings.
    const [batchVideoSettingsOverrideByScriptId, setBatchVideoSettingsOverrideByScriptId] = useState({});
    const batchVideoSettingsOverrideRef = useRef({});
    batchVideoSettingsOverrideRef.current = batchVideoSettingsOverrideByScriptId;
    const batchVideoSettingsGlobalsRef = useRef({});
    const [batchAutopilot, setBatchAutopilot] = useState(() => {
        try { return localStorage.getItem('batch_autopilot') === 'true'; } catch (_) { return false; }
    });
    const [batchTotalVideos, setBatchTotalVideos] = useState(() => {
        try {
            const v = parseInt(localStorage.getItem('batch_total_videos'), 10);
            return Number.isFinite(v) && v >= 1 && v <= 10 ? v : 10;
        } catch (_) { return 10; }
    }); // 1–10: total videos to create
    const [batchIncludeTextBased, setBatchIncludeTextBased] = useState(() => {
        try { return localStorage.getItem('batch_include_text_based') === 'true'; } catch (_) { return false; }
    });
    const [batchTextVideos, setBatchTextVideos] = useState(() => {
        try {
            const v = parseInt(localStorage.getItem('batch_text_videos'), 10);
            return Number.isFinite(v) && v >= 0 && v <= 10 ? v : 0;
        } catch (_) { return 0; }
    }); // 0–10: text-based count (Boukar-only; bounded by batchTotalVideos)
    const [batchAutopilotActive, setBatchAutopilotActive] = useState(false); // true while autopilot pipeline is running
    /** Batch tab: optional topic + script length (seconds) sent to generate-scripts-async */
    const [batchTopic, setBatchTopic] = useState(() => {
        try { return localStorage.getItem('batch_topic') ?? ''; } catch (_) { return ''; }
    });
    const [batchScriptDuration, setBatchScriptDuration] = useState(() => {
        try {
            const v = parseInt(localStorage.getItem('batch_script_duration_sec'), 10);
            return [15, 30, 60].includes(v) ? v : 30;
        } catch (_) { return 30; }
    });
    const [batchAutopilotVoiceoverTarget, setBatchAutopilotVoiceoverTarget] = useState(0);
    const batchAutopilotStartedAtRef = useRef(null);
    const [batchAutopilotEtaTick, setBatchAutopilotEtaTick] = useState(0);
    const [batchAutopilotThoughtSeconds, setBatchAutopilotThoughtSeconds] = useState(null);
    /** True from startBatchAutopilot until scripts + voiceovers + videos (+ text overlay queue) are done */
    const [batchAutopilotSessionActive, setBatchAutopilotSessionActive] = useState(false);
    const prevAutopilotPipelineBusyRef = useRef(false);
    const [batchScriptGlow, setBatchScriptGlow] = useState(null); // { scriptId, type: 'script'|'voiceover'|'video' } | null
    const batchScriptGlowTimeoutRef = useRef(null);
    // One moving cursor per card when autopilot is on (script/voiceover phase); 10 colors for up to 10 cards. { [scriptId]: { x, y, targetX, targetY, color, label } }
    const BATCH_CURSOR_COLORS = ['#0066cc', '#ff6b6b', '#4ecdc4', '#ffc107', '#9b59b6', '#e74c3c', '#2ecc71', '#e67e22', '#3498db', '#1abc9c'];
    const BATCH_CURSOR_LABELS = ['@blue', '@coral', '@teal', '@amber', '@purple', '@red', '@green', '@orange', '@sky', '@mint'];
    const STUDIO_OVERLAY_CURSOR_WAYPOINTS = [
        [{ left: 18, top: 18 }, { left: 38, top: 30 }, { left: 28, top: 52 }],
        [{ left: 82, top: 22 }, { left: 64, top: 34 }, { left: 74, top: 58 }],
        [{ left: 50, top: 80 }, { left: 58, top: 64 }, { left: 42, top: 66 }],
    ];
    const STUDIO_OVERLAY_PAUSE_MIN_MS = 500;
    const STUDIO_OVERLAY_PAUSE_MAX_MS = 2000;
    const STUDIO_OVERLAY_MOVE_MIN_MS = 500;
    const STUDIO_OVERLAY_MOVE_MAX_MS = 2000;
    const STUDIO_OVERLAY_CURSOR_ROTATIONS = [-4, 6, -8];
    const [studioOverlayCursors, setStudioOverlayCursors] = useState(() =>
        STUDIO_OVERLAY_CURSOR_WAYPOINTS.map((wps, i) => ({
            left: wps[0].left,
            top: wps[0].top,
            rotate: STUDIO_OVERLAY_CURSOR_ROTATIONS[i],
        }))
    );
    const studioOverlayCursorsEngineRef = useRef([]);
    const studioOverlayCursorTimeoutsRef = useRef([]);
    const [batchCardMousePositions, setBatchCardMousePositions] = useState({});
    const batchCardMouseBounds = { w: 280, h: 220 }; // area for cursor movement inside card

    useEffect(() => {
        const pipelineBusy = batchAutopilot && batchAutopilotSessionActive && (
            batchAutopilotActive ||
            batchGenerating ||
            batchGeneratingVoiceovers.size > 0 ||
            batchGeneratingVideos.size > 0 ||
            batchGeneratingTextOverlay.size > 0
        );
        if (!pipelineBusy) return undefined;
        const id = setInterval(() => setBatchAutopilotEtaTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, [
        batchAutopilot,
        batchAutopilotSessionActive,
        batchAutopilotActive,
        batchGenerating,
        batchGeneratingVoiceovers,
        batchGeneratingVideos,
        batchGeneratingTextOverlay
    ]);

    useEffect(() => {
        const pipelineBusy = batchAutopilot && batchAutopilotSessionActive && (
            batchAutopilotActive ||
            batchGenerating ||
            batchGeneratingVoiceovers.size > 0 ||
            batchGeneratingVideos.size > 0 ||
            batchGeneratingTextOverlay.size > 0
        );
        if (prevAutopilotPipelineBusyRef.current && !pipelineBusy && batchAutopilotSessionActive) {
            const t0 = batchAutopilotStartedAtRef.current;
            if (t0) {
                setBatchAutopilotThoughtSeconds(Math.max(0, Math.floor((Date.now() - t0) / 1000)));
            }
            setBatchAutopilotSessionActive(false);
            batchAutopilotStartedAtRef.current = null;
            setBatchAutopilotVoiceoverTarget(0);
        }
        prevAutopilotPipelineBusyRef.current = pipelineBusy;
    }, [
        batchAutopilot,
        batchAutopilotSessionActive,
        batchAutopilotActive,
        batchGenerating,
        batchGeneratingVoiceovers,
        batchGeneratingVideos,
        batchGeneratingTextOverlay
    ]);

    useEffect(() => {
        if (batchAutopilotActive || batchGenerating) return;
        if (!batchAutopilotSessionActive) {
            batchAutopilotStartedAtRef.current = null;
        }
    }, [batchAutopilotActive, batchGenerating, batchAutopilotSessionActive]);

    useEffect(() => {
        if (!batchAutopilot) {
            setBatchAutopilotThoughtSeconds(null);
            setBatchAutopilotSessionActive(false);
            batchAutopilotStartedAtRef.current = null;
            prevAutopilotPipelineBusyRef.current = false;
        }
    }, [batchAutopilot]);

    // Animate one cursor per batch card when autopilot is on through script, voiceover, text overlay, and video encode
    useEffect(() => {
        const showCursors = batchAutopilot && (
            batchAutopilotActive ||
            batchGenerating ||
            batchGeneratingVoiceovers.size > 0 ||
            batchGeneratingVideos.size > 0 ||
            batchGeneratingTextOverlay.size > 0 ||
            batchScripts.some(s => !s.voiceoverUrl)
        );
        if (!showCursors) {
            setBatchCardMousePositions({});
            return;
        }
        const { w, h } = batchCardMouseBounds;
        const activeScriptIds = batchScripts
            .filter(s => s._id && (
                batchGenerating ||
                batchGeneratingVoiceovers.has(s._id) ||
                batchGeneratingVideos.has(s._id) ||
                batchGeneratingTextOverlay.has(s._id) ||
                !s.voiceoverUrl
            ))
            .map(s => s._id);
        if (activeScriptIds.length === 0) return;

        // Initialize one cursor per active card; color by card index (max 10 colors)
        setBatchCardMousePositions(prev => {
            let next = { ...prev };
            activeScriptIds.forEach((scriptId, index) => {
                if (next[scriptId]) return;
                const color = BATCH_CURSOR_COLORS[index % BATCH_CURSOR_COLORS.length];
                const label = BATCH_CURSOR_LABELS[index % BATCH_CURSOR_LABELS.length];
                const x = Math.random() * (w - 40) + 10;
                const y = Math.random() * (h - 40) + 10;
                next[scriptId] = { x, y, targetX: x, targetY: y, color, label };
            });
            return next;
        });

        const intervalIds = [];
        const timeoutIds = [];
        activeScriptIds.forEach((scriptId, index) => {
            const initialDelay = index * 400;
            const baseInterval = 2000 + index * 400;
            const timeoutId = setTimeout(() => {
                const setNewTarget = () => {
                    setBatchCardMousePositions(prev => {
                        const mouse = prev[scriptId];
                        if (!mouse) return prev;
                        return {
                            ...prev,
                            [scriptId]: {
                                ...mouse,
                                targetX: Math.random() * (w - 40) + 10,
                                targetY: Math.random() * (h - 40) + 10
                            }
                        };
                    });
                };
                setNewTarget();
                const intervalId = setInterval(setNewTarget, baseInterval + Math.random() * 1500);
                intervalIds.push(intervalId);
            }, initialDelay);
            timeoutIds.push(timeoutId);
        });

        const animate = () => {
            setBatchCardMousePositions(prev => {
                const next = { ...prev };
                const speed = 0.06;
                activeScriptIds.forEach(scriptId => {
                    const mouse = next[scriptId];
                    if (!mouse) return;
                    const dx = mouse.targetX - mouse.x;
                    const dy = mouse.targetY - mouse.y;
                    next[scriptId] = {
                        ...mouse,
                        x: mouse.x + dx * speed,
                        y: mouse.y + dy * speed
                    };
                });
                return next;
            });
        };
        const animationId = setInterval(animate, 16);

        return () => {
            timeoutIds.forEach(id => clearTimeout(id));
            intervalIds.forEach(id => clearInterval(id));
            clearInterval(animationId);
        };
    }, [batchAutopilot, batchAutopilotActive, batchGenerating, batchGeneratingVoiceovers, batchGeneratingVideos, batchGeneratingTextOverlay, batchScripts]);

    // Load workspace cuts when we have text-based batch cards (so we can assign random cut)
    useEffect(() => {
        const textScripts = batchScripts.filter(s => s.type === 'text');
        if (textScripts.length === 0 || !currentWorkspace?._id || batchTextAvailableCuts.length > 0) return;
        let cancelled = false;
        setBatchTextCutsLoading(true);
        loadWorkspaceCutsForPicker()
            .then((all) => {
                if (!cancelled) setBatchTextAvailableCuts(all);
            })
            .catch(() => { if (!cancelled) setBatchTextAvailableCuts([]); })
            .finally(() => { if (!cancelled) setBatchTextCutsLoading(false); });
        return () => { cancelled = true; };
    }, [currentWorkspace?._id, batchScripts.length, batchTextAvailableCuts.length]);
    const batchTextAssignedCutRef = useRef(new Set());
    useEffect(() => {
        const textScripts = batchScripts.filter(s => s.type === 'text');
        if (textScripts.length === 0 || batchTextAvailableCuts.length === 0) return;
        const toAssign = textScripts.filter(s => !batchTextAssignedCutRef.current.has(s._id));
        if (toAssign.length === 0) return;
        toAssign.forEach(s => batchTextAssignedCutRef.current.add(s._id));
        setBatchTextSelectedCutByScriptId(prev => {
            const next = { ...prev };
            toAssign.forEach(s => {
                next[s._id] = batchTextAvailableCuts[Math.floor(Math.random() * batchTextAvailableCuts.length)];
            });
            return next;
        });
    }, [batchScripts, batchTextAvailableCuts]);

    // Timeline editing (Boukar only): modal + ordered cuts for regenerate
    const [showNoScriptConfirmModal, setShowNoScriptConfirmModal] = useState(false);
    const [showTimelineModal, setShowTimelineModal] = useState(false);
    const [showNewNleEditor, setShowNewNleEditor] = useState(false);
    /** null | { type: 'studio' } | { type: 'batch', scriptId } — which script the open NLE is editing */
    const [nleEditorSource, setNleEditorSource] = useState(null);
    const useNewNle = true;
    const [newNleCuts, setNewNleCuts] = useState([]);
    const [newNleAvailableCuts, setNewNleAvailableCuts] = useState([]);
    const [newNleCutsLoading, setNewNleCutsLoading] = useState(false);
    const [newNleHasMoreCuts, setNewNleHasMoreCuts] = useState(false);
    const [newNleCutsPage, setNewNleCutsPage] = useState(1);
    const newNlePickerTagKeyRef = useRef(null);
    const [newNleLoadingMore, setNewNleLoadingMore] = useState(false);
    const [newNlePlanningSfx, setNewNlePlanningSfx] = useState(false);
    const newNleExportExtrasRef = useRef(null);
    const nleWarmKeyRef = useRef('');
    const [timelineCuts, setTimelineCuts] = useState([]);
    const [timelineAvailableCuts, setTimelineAvailableCuts] = useState([]); // Fetched when opening modal (paginated)
    const [timelineCutsLoading, setTimelineCutsLoading] = useState(false);
    const [timelineCutsPage, setTimelineCutsPage] = useState(1);
    const [timelineHasMoreCuts, setTimelineHasMoreCuts] = useState(false);
    const [timelineCutsLoadingMore, setTimelineCutsLoadingMore] = useState(false);
    const [showTimelineAddCutPicker, setShowTimelineAddCutPicker] = useState(false);
    const [timelineDraggingIndex, setTimelineDraggingIndex] = useState(null);
    const [timelineDropTargetIndex, setTimelineDropTargetIndex] = useState(null);
    const [timelineActiveCutIndex, setTimelineActiveCutIndex] = useState(null); // Cut highlighted during audio playback
    const [timelinePlayheadTime, setTimelinePlayheadTime] = useState(0);
    const [timelineAudioPlaying, setTimelineAudioPlaying] = useState(false);
    const [timelineRequiredCutCount, setTimelineRequiredCutCount] = useState(null); // Raw sec from usedCutDurations (script) or no-script length / computed fallback
    const timelineAudioRef = useRef(null);
    const studioVoiceoverAudioRef = useRef(null);
    const shouldAutoplayStudioVoiceoverRef = useRef(false);
    const timelineAudioPlayingRef = useRef(false);
    const timelineWasPlayingRef = useRef(false);
    /** Strip time at the playhead (may extend past voiceover); used for cut insert position. */
    const timelineStripTimeRef = useRef(0);
    const timelineCutStripRef = useRef(null);
    const pendingTimelineScrollRef = useRef(null);
    const timelineUsedCutIdsRef = useRef(null);
    const timelineUsedCutDurationsRef = useRef(null);
    /** Sync with timelinePickerTagsKey after picker fetch so we only refetch when tags actually change. */
    const timelinePickerTagKeyRef = useRef(null);

    // Language options
    const languageOptions = [
        { code: 'en', name: 'English' },
        { code: 'es', name: 'Spanish' },
        { code: 'fr', name: 'French' },
        { code: 'de', name: 'German' },
        { code: 'it', name: 'Italian' },
        { code: 'pt', name: 'Portuguese' },
        { code: 'ru', name: 'Russian' },
        { code: 'ja', name: 'Japanese' },
        { code: 'ko', name: 'Korean' },
        { code: 'zh', name: 'Chinese' },
        { code: 'ar', name: 'Arabic' },
        { code: 'hi', name: 'Hindi' },
        { code: 'nl', name: 'Dutch' },
        { code: 'sv', name: 'Swedish' },
        { code: 'no', name: 'Norwegian' },
        { code: 'da', name: 'Danish' },
        { code: 'fi', name: 'Finnish' },
        { code: 'pl', name: 'Polish' },
        { code: 'tr', name: 'Turkish' },
        { code: 'th', name: 'Thai' }
    ];
    const studioPollRef = useRef(null);
    const voiceoverSectionRef = useRef(null);
    const videoSectionRef = useRef(null);
    const studioGenerationSettingsRef = useRef(null);
    const [studioGenerationSettingsWidth, setStudioGenerationSettingsWidth] = useState(null);
    const studioSettingsNarrow = (studioGenerationSettingsWidth ?? screenWidth) <= 600;

    useEffect(() => {
        const root = studioSettingsListEl;
        if (!root) return undefined;

        const updateActiveSection = () => {
            if (studioSettingsSectionJumpRef.current) return;
            const sections = Array.from(root.querySelectorAll('[data-studio-section]'));
            if (sections.length === 0) return;

            const rootRect = root.getBoundingClientRect();
            const rootScrollable = root.scrollHeight > root.clientHeight + 1;
            const markerY = rootScrollable
                ? rootRect.top + Math.min(48, rootRect.height * 0.2)
                : Math.min(Math.max(rootRect.top + 24, 96), window.innerHeight * 0.35);

            let activeId = sections[0].getAttribute('data-studio-section');
            for (let i = 0; i < sections.length; i += 1) {
                const section = sections[i];
                if (section.getBoundingClientRect().top <= markerY) {
                    activeId = section.getAttribute('data-studio-section');
                } else {
                    break;
                }
            }

            if (!activeId) return;
            setStudioSettingsTab((prev) => (prev === activeId ? prev : activeId));
        };

        root.addEventListener('scroll', updateActiveSection, { passive: true });
        window.addEventListener('scroll', updateActiveSection, { passive: true });
        window.addEventListener('resize', updateActiveSection);
        updateActiveSection();
        return () => {
            root.removeEventListener('scroll', updateActiveSection);
            window.removeEventListener('scroll', updateActiveSection);
            window.removeEventListener('resize', updateActiveSection);
        };
    }, [studioSettingsListEl]);
    const studioVideoPollRef = useRef(null);
    const studioVoiceoverCancelledRef = useRef(false);
    const textStudioOverlayPollRef = useRef(null);
    const textStudioVideoPollRef = useRef(null);
    const textStudioOverlayCancelledRef = useRef(false);
    const textStudioVideoCancelledRef = useRef(false);
    const lastVoiceoverTimestampsRef = useRef(null); // Keep last timestamps so subtitles persist on regenerate
    const lastVoiceoverUrlForTimestampsRef = useRef(null); // URL that the timestamps ref belongs to (for matching)
    const studioVideoHasPlayedRef = useRef(false);
    const studioAutoCreateModeRef = useRef(false);
    const autoGenerateVideoAfterVoiceoverRef = useRef(false);
    const lastStudioVideoCompletionUrlRef = useRef(null); // Prevent duplicate "video completed" side effects
    const customScriptTextareaRef = useRef(null);
    const prevStudioSelectedScriptIdRef = useRef(null);
    const [lastProgressUpdate, setLastProgressUpdate] = useState(0);
    const [simulatedProgressTimers, setSimulatedProgressTimers] = useState([]);
    const [lastSimulatedStep, setLastSimulatedStep] = useState('');
    const [studioVoiceId, setStudioVoiceId] = useState(null);
    const [studioVoiceoverUrl, setStudioVoiceoverUrl] = useState('');
    const [studioVoiceoverScriptContent, setStudioVoiceoverScriptContent] = useState(null); // Track script content used for current voiceover
    const [scriptChangedSinceVoiceover, setScriptChangedSinceVoiceover] = useState(false); // Only true when user edits script or clicks Regenerate script
    const [studioGeneratingVoice, setStudioGeneratingVoice] = useState(false);
    const [studioManualVoiceoverRequested, setStudioManualVoiceoverRequested] = useState(false);
    const [studioVoiceoverOverlayProgress, setStudioVoiceoverOverlayProgress] = useState({
        percent: 10,
        step: 'Preparing voiceover...',
    });
    const [studioGeneratingVideo, setStudioGeneratingVideo] = useState(false);
    const [studioVideoProgress, setStudioVideoProgress] = useState(0);
    // Whether the running Studio generation reports SFX steps (backend plans SFX, or the export already carries sfxCues).
    const [studioSfxStepsActive, setStudioSfxStepsActive] = useState(false);
    const [studioVideoUrl, setStudioVideoUrl] = useState('');
    const [studioVideoUrlTimestamp, setStudioVideoUrlTimestamp] = useState(0); // Track when video URL changes to force reload
    /** Same idea as mobile Studio: limit cuts pool to all workspace videos, a subset, or jump to Raw tab to import. */
    const [studioVideoSourceMode, setStudioVideoSourceMode] = useState('all'); // 'all' | 'select' | 'import'
    const [studioSelectedRawVideoIds, setStudioSelectedRawVideoIds] = useState([]);
    const [showStudioRawVideosPicker, setShowStudioRawVideosPicker] = useState(false);
    const [studioRawPickerVideos, setStudioRawPickerVideos] = useState([]);
    const [studioRawPickerLoading, setStudioRawPickerLoading] = useState(false);
    const [studioRawPickerLoadingMore, setStudioRawPickerLoadingMore] = useState(false);
    const [studioRawPickerPage, setStudioRawPickerPage] = useState(1);
    const [studioRawPickerHasMore, setStudioRawPickerHasMore] = useState(false);
    const [voiceoverDuration, setVoiceoverDuration] = useState(0);

    // Tracks manual voiceover runs so we can distinguish them from autopilot voice in UI (e.g. Auto button label).
    useEffect(() => {
        if (!studioGeneratingVoice) setStudioManualVoiceoverRequested(false);
    }, [studioGeneratingVoice]);

    // Autoplay regenerated voiceover through the visible player so pause/seek controls work.
    useEffect(() => {
        if (!studioVoiceoverUrl || !shouldAutoplayStudioVoiceoverRef.current) return;
        shouldAutoplayStudioVoiceoverRef.current = false;
        const t = setTimeout(() => {
            autoplayVoiceoverUrl(studioVoiceoverUrl, studioVoiceoverAudioRef.current);
        }, 50);
        return () => clearTimeout(t);
    }, [studioVoiceoverUrl]);

    // Simulated voiceover progress for the studio process overlay
    useEffect(() => {
        if (!studioGeneratingVoice) {
            setStudioVoiceoverOverlayProgress({ percent: 10, step: 'Preparing voiceover...' });
            return;
        }
        const steps = [
            { percent: 22, step: 'Reading script...', delay: 700 },
            { percent: 48, step: 'Synthesizing voice...', delay: 2200 },
            { percent: 72, step: 'Aligning timing...', delay: 4500 },
            { percent: 90, step: 'Finalizing audio...', delay: 7000 },
        ];
        const timers = steps.map((s) =>
            setTimeout(() => setStudioVoiceoverOverlayProgress({ percent: s.percent, step: s.step }), s.delay)
        );
        return () => timers.forEach(clearTimeout);
    }, [studioGeneratingVoice]);

    // Studio overlay cursors: random 0.5–2s pauses and move durations between waypoints
    useEffect(() => {
        const overlayActive =
            (studioAutoCreateMode || studioAutoCreateModeRef.current || autoGenerateVideoAfterVoiceoverRef.current) &&
            (studioGenerating || studioGeneratingVoice);

        const clearCursorTimers = () => {
            studioOverlayCursorTimeoutsRef.current.forEach(clearTimeout);
            studioOverlayCursorTimeoutsRef.current = [];
        };

        if (!overlayActive) {
            clearCursorTimers();
            return;
        }

        const randomPauseMs = () =>
            STUDIO_OVERLAY_PAUSE_MIN_MS + Math.random() * (STUDIO_OVERLAY_PAUSE_MAX_MS - STUDIO_OVERLAY_PAUSE_MIN_MS);

        const randomMoveMs = () =>
            STUDIO_OVERLAY_MOVE_MIN_MS + Math.random() * (STUDIO_OVERLAY_MOVE_MAX_MS - STUDIO_OVERLAY_MOVE_MIN_MS);

        const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

        const pickWaypointIndex = (cursorIdx, avoidIdx) => {
            const wps = STUDIO_OVERLAY_CURSOR_WAYPOINTS[cursorIdx];
            if (wps.length <= 1) return 0;
            let idx = Math.floor(Math.random() * wps.length);
            while (idx === avoidIdx) idx = Math.floor(Math.random() * wps.length);
            return idx;
        };

        const engines = STUDIO_OVERLAY_CURSOR_WAYPOINTS.map((wps, i) => ({
            left: wps[0].left,
            top: wps[0].top,
            targetLeft: wps[0].left,
            targetTop: wps[0].top,
            moveStartLeft: wps[0].left,
            moveStartTop: wps[0].top,
            moveStartTime: 0,
            moveDurationMs: 0,
            waypointIndex: 0,
            moving: false,
            rotate: STUDIO_OVERLAY_CURSOR_ROTATIONS[i],
        }));
        studioOverlayCursorsEngineRef.current = engines;
        setStudioOverlayCursors(engines.map(({ left, top, rotate }) => ({ left, top, rotate })));

        const schedulePause = (cursorIdx) => {
            const delay = randomPauseMs();
            const tid = setTimeout(() => {
                const cur = studioOverlayCursorsEngineRef.current[cursorIdx];
                if (!cur || cur.moving) return;
                const nextIdx = pickWaypointIndex(cursorIdx, cur.waypointIndex);
                const pt = STUDIO_OVERLAY_CURSOR_WAYPOINTS[cursorIdx][nextIdx];
                studioOverlayCursorsEngineRef.current[cursorIdx] = {
                    ...cur,
                    moveStartLeft: cur.left,
                    moveStartTop: cur.top,
                    targetLeft: pt.left,
                    targetTop: pt.top,
                    waypointIndex: nextIdx,
                    moving: true,
                    moveStartTime: Date.now(),
                    moveDurationMs: randomMoveMs(),
                    rotate: Math.random() * 14 - 7,
                };
            }, delay);
            studioOverlayCursorTimeoutsRef.current.push(tid);
        };

        engines.forEach((_, i) => {
            const tid = setTimeout(() => schedulePause(i), i * 200 + randomPauseMs() * 0.35);
            studioOverlayCursorTimeoutsRef.current.push(tid);
        });

        let rafId;
        const tick = () => {
            const now = Date.now();
            studioOverlayCursorsEngineRef.current = studioOverlayCursorsEngineRef.current.map((c, idx) => {
                if (!c.moving) return c;
                const elapsed = now - c.moveStartTime;
                const progress = Math.min(1, elapsed / c.moveDurationMs);
                const eased = easeInOut(progress);
                if (progress >= 1) {
                    schedulePause(idx);
                    return {
                        ...c,
                        left: c.targetLeft,
                        top: c.targetTop,
                        moving: false,
                    };
                }
                return {
                    ...c,
                    left: c.moveStartLeft + (c.targetLeft - c.moveStartLeft) * eased,
                    top: c.moveStartTop + (c.targetTop - c.moveStartTop) * eased,
                };
            });
            setStudioOverlayCursors(
                studioOverlayCursorsEngineRef.current.map(({ left, top, rotate }) => ({ left, top, rotate }))
            );
            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);

        return () => {
            clearCursorTimers();
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [studioAutoCreateMode, studioGenerating, studioGeneratingVoice]);

    const exitStudioAutopilotMode = () => {
        setStudioAutoCreateMode(false);
        studioAutoCreateModeRef.current = false;
        autoGenerateVideoAfterVoiceoverRef.current = false;
    };

    useEffect(() => {
        if (!showTimelineModal || timelineCutsLoading) return;
        setTimelineRequiredCutCount(resolveTimelineRequiredSeconds({
            voiceoverSeconds: voiceoverDuration,
            fastPacing,
            orderedCuts: timelineCuts,
            usedDurations: timelineUsedCutDurationsRef.current,
            flipbookIntro,
            isNoScriptMode,
            noScriptVideoLength,
        }));
    }, [showTimelineModal, timelineCutsLoading, fastPacing, flipbookIntro, isNoScriptMode, noScriptVideoLength, timelineCuts, voiceoverDuration]);

    useEffect(() => {
        setStudioVideoSourceMode('all');
        setStudioSelectedRawVideoIds([]);
        setShowStudioRawVideosPicker(false);
        setStudioRawPickerVideos([]);
        setStudioRawPickerPage(1);
        setStudioRawPickerHasMore(false);
    }, [currentWorkspace?._id]);

    const [studioPublishingVideo, setStudioPublishingVideo] = useState(false);
    const [studioVideoSaved, setStudioVideoSaved] = useState(false);
    const [editingScriptContent, setEditingScriptContent] = useState({});
    const editDebounceRef = useRef({});
    const [currentStudioExample, setCurrentStudioExample] = useState('');
    const [isLastStudioExample, setIsLastStudioExample] = useState(false);
    const [studioAnimationActive, setStudioAnimationActive] = useState(false);
    const [simulatedProgressActive, setSimulatedProgressActive] = useState(false);
    const [currentUserId, setCurrentUserId] = useState('');
    const [boukarTimeRoundedTo10s, setBoukarTimeRoundedTo10s] = useState('');
    
    // GetLate integration state
    const [getlateConnecting, setGetlateConnecting] = useState({ instagram: false, tiktok: false });
    const [getlateConnectedAccounts, setGetlateConnectedAccounts] = useState({ instagram: false, tiktok: false });
    const [getlatePublishing, setGetlatePublishing] = useState(new Set());
    const [showPublishModal, setShowPublishModal] = useState({ videoId: null });
    const [publishScheduleDate, setPublishScheduleDate] = useState('');
    const [publishScheduleTime, setPublishScheduleTime] = useState('');
    const [publishCaption, setPublishCaption] = useState('');
    const [publishMode, setPublishMode] = useState('now'); // 'now' or 'schedule'
    const [publishPlatformInstagram, setPublishPlatformInstagram] = useState(true);
    const [publishPlatformTiktok, setPublishPlatformTiktok] = useState(true);
    const [publishScheduleAfterLast, setPublishScheduleAfterLast] = useState(false);
    const [publishAfterLastValue, setPublishAfterLastValue] = useState(1);
    const [publishAfterLastUnit, setPublishAfterLastUnit] = useState('days'); // 'hours' | 'days'
    const [workspaceScheduledPosts, setWorkspaceScheduledPosts] = useState([]);
    
    // Modal states
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [modalErrorMessage, setModalErrorMessage] = useState('');
    const [showUpgradePaywall, setShowUpgradePaywall] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [modalSuccessMessage, setModalSuccessMessage] = useState('');

    // Boukar-only: show a clock with tenths of a second (HH:MM:SS.t)
    useEffect(() => {
        if (!['673247877e4ac0a824cbac0d' /* Boukar */].includes(currentUserId)) return;

        const tick = () => {
            const d = new Date();
            const hh = String(d.getHours()).padStart(2, '0');
            const mm = String(d.getMinutes()).padStart(2, '0');
            const ss = String(d.getSeconds()).padStart(2, '0');
            const tenth = Math.floor(d.getMilliseconds() / 100); // 0..9
            setBoukarTimeRoundedTo10s(`${hh}:${mm}:${ss}.${tenth}`);
        };

        tick();
        const id = setInterval(tick, 100);
        return () => clearInterval(id);
    }, [currentUserId]);

    // Non-Boukar users shouldn't stay on the Home tab
    useEffect(() => {
        if (activeTab !== 'home') return;
        if (['673247877e4ac0a824cbac0d' /* Boukar */].includes(currentUserId)) return;
        if (currentUserId) setActiveTab('profile');
    }, [activeTab, currentUserId, setActiveTab]);
    
    // Confirmation modal states
    const [showDeleteWorkspaceModal, setShowDeleteWorkspaceModal] = useState(false);
    const [showRemoveMemberModal, setShowRemoveMemberModal] = useState(false);
    const [showLeaveWorkspaceModal, setShowLeaveWorkspaceModal] = useState(false);
    const [showDeleteVideoModal, setShowDeleteVideoModal] = useState(false);
    const [showDeleteRecordingModal, setShowDeleteRecordingModal] = useState(false);
    const [showInsufficientCutsModal, setShowInsufficientCutsModal] = useState(false);
    const [showBatchConfirmModal, setShowBatchConfirmModal] = useState(false);
    const [batchConfirmModal, setBatchConfirmModal] = useState({ title: '', message: '', confirmLabel: 'Continue' });
    const batchConfirmActionRef = useRef(null);
    const [memberToRemove, setMemberToRemove] = useState(null);
    const [videoToDelete, setVideoToDelete] = useState(null);
    const [bulkSelectedVideoIds, setBulkSelectedVideoIds] = useState([]);
    const [bulkActionMenu, setBulkActionMenu] = useState(null); // null | 'tags' | 'rename' | 'delete'
    const [bulkTagsInput, setBulkTagsInput] = useState('');
    const [bulkRenameInput, setBulkRenameInput] = useState('');
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [recordingToDelete, setRecordingToDelete] = useState(null);
    const [recordingDeleteInFlight, setRecordingDeleteInFlight] = useState(false);
    
    // Font loading state
    const [loadedFonts, setLoadedFonts] = useState(new Set());
    const DEFAULT_SUBTITLE_FONT_NAME = 'TikTok medium';
    const defaultFontFamilyCss = `"${DEFAULT_SUBTITLE_FONT_NAME}", Arial, sans-serif`;
    const fontFamilyCssForName = (fontName) =>
        fontName ? `"${fontName}", Arial, sans-serif` : defaultFontFamilyCss;
    const sortedUserFonts = useMemo(
        () => [...userFonts].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })),
        [userFonts]
    );
    
    // Function to add font CSS to the page
    const addFontCSS = (fontName, fontUrl) => {
        if (!fontName || !fontUrl || loadedFonts.has(fontName)) return;
        
        const fontId = `font-${fontName.replace(/\s+/g, '-').toLowerCase()}`;
        
        // Check if font CSS already exists
        if (document.getElementById(fontId)) return;
        
        const style = document.createElement('style');
        style.id = fontId;
        style.textContent = `
            @font-face {
                font-family: "${fontName}";
                src: url("${fontUrl}") format("truetype");
                font-display: swap;
            }
        `;
        document.head.appendChild(style);
        setLoadedFonts(prev => new Set([...prev, fontName]));
    };
    
    // Load font faces as soon as fonts are available (closed + open dropdowns need them)
    useEffect(() => {
        if (userFonts.length === 0) return;
        userFonts.forEach(font => {
            if (font?.name && font?.url) {
                addFontCSS(font.name, font.url);
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per fonts list; addFontCSS also guards via DOM id
    }, [userFonts]);
    
    // Refs for fresh values in intervals
    const simulatedProgressActiveRef = useRef(false);
    const lastSimulatedStepRef = useRef('');
    const progressLockUntilRef = useRef(0);
    const latestBackendProgressRef = useRef({ percent: 0, step: '', timestamp: 0 });
    
    // Step order for monotonic progress
    const stepOrder = {
        'Coming up with a big idea...': 1,
        'Generating a first version': 2,
        'Refining the content...': 3,
        'Generating an alternative version': 4,
        'Finalizing details...': 5,
        'Completed': 6
    };

    // Check profile completeness for AI script generation
    const checkProfileCompleteness = () => {
        try {
            if (!currentWorkspace?.prospectingProfile) {
                return { isComplete: true, missingFields: [], completenessPercentage: 100 };
            }
            
            const prospectingProfile = currentWorkspace.prospectingProfile;
            const requiredFields = [
                { key: 'companyPurpose', label: 'Company Purpose' },
                { key: 'targetAudience', label: 'Target Audience' },
                // { key: 'howToBePerceived', label: 'How you want to be perceived' },
                // { key: 'currentMarketingStrategy', label: 'Current Marketing Strategy' },
                // { key: 'contentIdeas', label: 'Content Ideas' },
                { key: 'targetChallenge', label: 'Target Challenges' }
            ];
            
            const missingFields = requiredFields.filter(field => 
                !prospectingProfile[field.key] || prospectingProfile[field.key].trim() === ''
            );

            console.log('[PROFILE COMPLETENESS] Missing fields:', missingFields);
            console.log('[PROFILE COMPLETENESS] Required fields:', requiredFields);
            console.log('[PROFILE COMPLETENESS] ProspectingProfile:', prospectingProfile);
            
            return {
                isComplete: missingFields.length === 0,
                missingFields: missingFields.map(f => f.label),
                completenessPercentage: Math.round(((requiredFields.length - missingFields.length) / requiredFields.length) * 100)
            };
        } catch (error) {
            console.error('[PROFILE COMPLETENESS] Error:', error);
            return { isComplete: true, missingFields: [], completenessPercentage: 100 };
        }
    };

    const [profileCompleteness, setProfileCompleteness] = useState({ isComplete: true, missingFields: [], completenessPercentage: 100 });

    // Reset video played ref when a new video URL is set
    useEffect(() => {
        if (studioVideoUrl) {
            studioVideoHasPlayedRef.current = false;
        }
    }, [studioVideoUrl]);

    // If a no-script generation is still running after a refresh/tab switch, restore progress only (not prior drafts)
    useEffect(() => {
        if (activeTab !== 'studio' || !currentWorkspace?._id || !isNoScriptMode || studioGeneratingVideo) return;
        let cancelled = false;
        (async () => {
            try {
                const statusResp = await api.getNoScriptVideoStatus(currentWorkspace._id);
                if (cancelled) return;
                const d = statusResp.data || {};
                const { status, progress } = d;
                if (status === 'processing' && typeof progress === 'number') {
                    setStudioVideoProgress(progress);
                }
            } catch (e) {
                console.warn('[NO-SCRIPT] Failed to restore generation progress:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [activeTab, currentWorkspace?._id, isNoScriptMode, studioGeneratingVideo]);

    // Focus custom script textarea only when user selected/created a custom script, not when scripts were refetched (e.g. after video generation)
    useEffect(() => {
        const hasCustomScript = studioScripts.some(s => s.isCustom && s._id === studioSelectedScriptId);
        const selectedIdChanged = prevStudioSelectedScriptIdRef.current !== studioSelectedScriptId;
        prevStudioSelectedScriptIdRef.current = studioSelectedScriptId;
        if (hasCustomScript && customScriptTextareaRef.current && selectedIdChanged) {
            setTimeout(() => {
                if (customScriptTextareaRef.current) {
                    customScriptTextareaRef.current.focus();
                }
            }, 100);
        }
    }, [studioScripts, studioSelectedScriptId]);

    // When opening publish modal, populate caption from video description, default platform checkboxes, and refresh scheduled posts
    useEffect(() => {
        if (showPublishModal.videoId && generatedVideos.length > 0) {
            const video = generatedVideos.find(v => v._id === showPublishModal.videoId);
            if (video) {
                setPublishCaption(video.description || '');
                setPublishPlatformInstagram(getlateConnectedAccounts.instagram);
                setPublishPlatformTiktok(getlateConnectedAccounts.tiktok);
            }
        }
        if (showPublishModal.videoId && currentWorkspace?._id) {
            api.getScheduledPosts(currentWorkspace._id)
                .then((data) => setWorkspaceScheduledPosts(Array.isArray(data) ? data : []))
                .catch(() => {});
        }
    }, [showPublishModal.videoId]); // eslint-disable-line react-hooks/exhaustive-deps -- only run when modal opens; generatedVideos/getlateConnectedAccounts/currentWorkspace are stable enough

    // Fetch scheduled posts for this workspace (for View calendar + autoschedule after last)
    useEffect(() => {
        if (!currentWorkspace?._id) return;
        api.getScheduledPosts(currentWorkspace._id)
            .then((data) => setWorkspaceScheduledPosts(Array.isArray(data) ? data : []))
            .catch(() => setWorkspaceScheduledPosts([]));
    }, [currentWorkspace?._id]);

    // Update profile completeness when workspace data changes
    useEffect(() => {
        if (activeTab === 'studio' && currentWorkspace) {
            setProfileCompleteness(checkProfileCompleteness());
        } else {
            setProfileCompleteness({ isComplete: true, missingFields: [], completenessPercentage: 100 });
        }
    }, [currentWorkspace, activeTab]);

    // Add Studio animation styles to document
    useEffect(() => {
        const styleSheet = document.createElement("style");
        styleSheet.innerText = studioFadeTransitionStyle;
        document.head.appendChild(styleSheet);
        return () => styleSheet.remove();
    }, [studioFadeTransitionStyle]);

    // Listen for window resize to update responsive design
    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Measure Generation Settings container width (stack panel above preview when ≤600px)
    useEffect(() => {
        if (!studioSelectedScriptId || !studioVoiceoverUrl) {
            setStudioGenerationSettingsWidth(null);
            return;
        }
        if (typeof ResizeObserver === 'undefined') return;

        let observer = null;
        let cancelled = false;

        const attach = () => {
            if (cancelled) return;
            const el = studioGenerationSettingsRef.current;
            if (!el) {
                requestAnimationFrame(attach);
                return;
            }

            const updateWidth = (width) => {
                if (typeof width === 'number' && !Number.isNaN(width)) {
                    setStudioGenerationSettingsWidth(width);
                }
            };

            updateWidth(el.getBoundingClientRect().width);
            observer = new ResizeObserver((entries) => {
                updateWidth(entries[0]?.contentRect?.width);
            });
            observer.observe(el);
        };

        attach();
        return () => {
            cancelled = true;
            if (observer) observer.disconnect();
        };
    }, [studioSelectedScriptId, studioVoiceoverUrl]);

    // Cleanup cuts polling interval on unmount
    useEffect(() => {
        return () => {
            if (cutsRefreshInterval) {
                clearInterval(cutsRefreshInterval);
            }
        };
    }, [cutsRefreshInterval]);

    const handleResetOnboarding = async () => {
        try {
            await api.resetOnboarding();
            // Navigate to onboarding page
            // navigate('/onboarding');
        } catch (error) {
            console.error('Error resetting onboarding:', error);
        }
    };

    // Studio animation effect
    useEffect(() => {
        if (!studioRegeneratingScript) {
            // Reset when not regenerating
            console.log('[ANIMATION] Resetting animation');
            setStudioAnimationActive(false);
            setCurrentStudioExample('');
            setIsLastStudioExample(false);
            return;
        }

        console.log('[ANIMATION] Starting animation');
        setStudioAnimationActive(true);
        let currentIndex = 0;

        // Show first example immediately
        const firstExample = studioExamples[0];
        console.log(`[ANIMATION] Showing example ${currentIndex + 1}/${studioExamples.length}: "${firstExample}"`);
        setCurrentStudioExample(firstExample);
        currentIndex++;

        const interval = setInterval(() => {
            if (currentIndex < studioExamples.length) {
                const nextExample = studioExamples[currentIndex];
                console.log(`[ANIMATION] Showing example ${currentIndex + 1}/${studioExamples.length}: "${nextExample}"`);
                setCurrentStudioExample(nextExample);
                currentIndex++;
                if (currentIndex === studioExamples.length) {
                    console.log('[ANIMATION] Reached final example');
                    setIsLastStudioExample(true);
                }
            } else {
                console.log('[ANIMATION] Animation complete, clearing interval');
                clearInterval(interval);
                setStudioAnimationActive(false);
            }
        }, 3000);

        return () => {
            console.log('[ANIMATION] Cleaning up interval');
            clearInterval(interval);
            setStudioAnimationActive(false);
        };
    }, [studioRegeneratingScript]);

    const [isEditingName, setIsEditingName] = useState(false);
    const [workspaceProfilePictureUploading, setWorkspaceProfilePictureUploading] = useState(false);
    const [workspaceProfilePictureError, setWorkspaceProfilePictureError] = useState('');
    const workspaceProfilePictureInputRef = useRef(null);
    const [newName, setNewName] = useState('');
    const isUpdatingNameRef = useRef(false);
    const [inviteLink, setInviteLink] = useState('');
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    const [message, setMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // Workspace profile state
    const [workspaceProfile, setWorkspaceProfile] = useState({
        currentSituation: '',
        shortTermGoals: '',
        longTermGoals: '',
        websiteUrl: '',
        prospectingProfile: {
            currentRole: '',
            companyPurpose: '',
            targetAudience: '',
            howToBePerceived: '',
            currentMarketingStrategy: '',
            contentIdeas: '',
            instagramUrl: '',
            inspiringBrands: '',
            competitors: '',
            targetChallenge: ''
        },
        socialMedia: {
            mainPlatform: 'instagram',
            instagram: { handle: '', currentFollowers: 0, targetFollowers: 0 },
            tiktok: { handle: '', currentFollowers: 0, targetFollowers: 0 },
            youtube: { handle: '', currentFollowers: 0, targetFollowers: 0 }
        },
        videoProduction: {
            currentVideosPerMonth: 0,
            targetVideosPerMonth: 0
        },
        isSubscribed: false,
        subscriptionPlan: 'none'
    });

    // Voice cloning state variables
    const [isRecording, setIsRecording] = useState(false);
    const [recordings, setRecordings] = useState([]);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [mediaStream, setMediaStream] = useState(null);
    const [isGeneratingVoice, setIsGeneratingVoice] = useState(false);
    const [isTestingVoice, setIsTestingVoice] = useState(false);
    const [audioUrl, setAudioUrl] = useState('');
    const [isPlayingVoice, setIsPlayingVoice] = useState(false);
    const audioRef = useRef(null);
    const [recordingDuration, setRecordingDuration] = useState(0);
    const [timerInterval, setTimerInterval] = useState(null);
    const [isSavingRecording, setIsSavingRecording] = useState(false);
    const [isImportingAudio, setIsImportingAudio] = useState(false);
    const [isVoiceImportDragging, setIsVoiceImportDragging] = useState(false);
    const [glowingRecordingIds, setGlowingRecordingIds] = useState([]);
    const recordingGlowTimeoutRef = useRef(null);
    const [recordingLength, setRecordingLength] = useState({});
    const [showRecordingSection, setShowRecordingSection] = useState(false);
    const [showExamples, setShowExamples] = useState(false);
    const [currentExample, setCurrentExample] = useState(0);
    const [displayedExamples, setDisplayedExamples] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState('');
    const [isAutoSaving, setIsAutoSaving] = useState(false);
    const [workspaceVoiceId, setWorkspaceVoiceId] = useState(null);
    const [recloneButtonShake, setRecloneButtonShake] = useState(false);
    const prevRecordingIdsKeyRef = useRef(null);
    const [testAudioUrl, setTestAudioUrl] = useState('');
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [hasSavedToBackend, setHasSavedToBackend] = useState(false);
    const [savedFormData, setSavedFormData] = useState(null);

    // Subtitle color settings
    const [subtitleFontColor, setSubtitleFontColor] = useState('#ffffff');
    const [subtitleOutlineColor, setSubtitleOutlineColor] = useState('#000000');
    const [fontMixingSecondColor, setFontMixingSecondColor] = useState('#F5D66D');
    const [subtitleBackgroundColor, setSubtitleBackgroundColor] = useState('transparent');
    const [subtitleOutlineEnabled, setSubtitleOutlineEnabled] = useState(false);

    batchVideoSettingsGlobalsRef.current = {
        fastPacing,
        speedUp,
        flipbookIntro,
        openingLineInCenter,
        openingLineTextSize,
        openingLineFontId,
        openingLineFontColor,
        fontMixing,
        centerStackOpening,
        centerStackLineByLine,
        fontMixingSecondColor,
        boukisIntro,
        boukisIntroPitch,
        enableSubtitles,
        selectedFontId,
        subtitleFontColor,
        subtitleOutlineEnabled,
        subtitleOutlineColor,
        bigText,
        subtitlePosition,
        oneWordPerSubtitle,
        glowText,
        glowTextColor,
        cutSilenceMode,
        cutMoreSilenceMode,
        aiSoundEffects,
        useMusic: batchUseMusic,
    };

    // Individual field loading states
    const [fieldLoadingStates, setFieldLoadingStates] = useState({
        currentSituation: false,
        shortTermGoals: false,
        longTermGoals: false,
        websiteUrl: false,
        prospectingProfile: false,
        socialMedia: false,
        videoProduction: false,
        subtitleColors: false,
        voiceCloning: false,
        workspaceName: false
    });

    // Individual field save states (for showing checkmarks)
    const [fieldSaveStates, setFieldSaveStates] = useState({
        currentSituation: '',
        shortTermGoals: '',
        longTermGoals: '',
        websiteUrl: '',
        prospectingProfile: '',
        socialMedia: '',
        videoProduction: '',
        subtitleColors: '',
        voiceCloning: '',
        workspaceName: ''
    });

    // Form data state
    const [formData, setFormData] = useState({
        currentSituation: '',
        shortTermGoals: '',
        longTermGoals: '',
        websiteUrl: '',
        prospectingProfile: {
            currentRole: '',
            companyPurpose: '',
            targetAudience: '',
            howToBePerceived: '',
            currentMarketingStrategy: '',
            contentIdeas: '',
            instagramUrl: '',
            inspiringBrands: '',
            competitors: '',
            targetChallenge: ''
        },
        socialMedia: {
            mainPlatform: 'instagram',
            instagram: { handle: '', currentFollowers: 0, targetFollowers: 0 },
            tiktok: { handle: '', currentFollowers: 0, targetFollowers: 0 },
            youtube: { handle: '', currentFollowers: 0, targetFollowers: 0 }
        },
        videoProduction: {
            currentVideosPerMonth: 0,
            targetVideosPerMonth: 0
        }
    });

    // Helper functions for field loading states
    const setFieldLoading = (fieldName, isLoading) => {
        setFieldLoadingStates(prev => ({
            ...prev,
            [fieldName]: isLoading
        }));
    };

    const setFieldSaved = (fieldName, isSaved = true) => {
        setFieldSaveStates(prev => ({
            ...prev,
            [fieldName]: isSaved ? 'saved' : ''
        }));
        
        // Clear the saved state after 3 seconds
        if (isSaved) {
            setTimeout(() => {
                setFieldSaveStates(prev => ({
                    ...prev,
                    [fieldName]: ''
                }));
            }, 3000);
        }
    };

    useEffect(() => {
        if (currentWorkspace) {
            setNewName(currentWorkspace.name);
            
            // Check if this workspace has been visited before
            // If it's a first-time visit (no profile data filled), show profile tab
            const hasProfileData = currentWorkspace.profile?.currentSituation || 
                                 currentWorkspace.prospectingProfile?.companyPurpose ||
                                 currentWorkspace.socialMedia?.instagram?.handle ||
                                 currentWorkspace.videoProduction?.currentVideosPerMonth > 0;
            
            // if (!hasProfileData && !hasVisitedProfile) {
            //     setActiveTab('profile');
            //     setHasVisitedProfile(true);
            // } else if (activeTab === 'profile' && hasProfileData && !isEditingName && !isUpdatingNameRef.current) {
            //     // If profile tab is active but workspace has data, switch to raw videos
            //     // But don't switch if user is currently editing the name or updating it
            //     setActiveTab('raw');
            // }
            
            // Initialize workspace profile data
            setWorkspaceProfile({
                currentSituation: currentWorkspace.profile?.currentSituation || '',
                shortTermGoals: currentWorkspace.profile?.shortTermGoals || '',
                longTermGoals: currentWorkspace.profile?.longTermGoals || '',
                websiteUrl: currentWorkspace.profile?.websiteUrl || '',
                prospectingProfile: {
                    currentRole: currentWorkspace.prospectingProfile?.currentRole || '',
                    companyPurpose: currentWorkspace.prospectingProfile?.companyPurpose || '',
                    targetAudience: currentWorkspace.prospectingProfile?.targetAudience || '',
                    howToBePerceived: currentWorkspace.prospectingProfile?.howToBePerceived || '',
                    currentMarketingStrategy: currentWorkspace.prospectingProfile?.currentMarketingStrategy || '',
                    contentIdeas: currentWorkspace.prospectingProfile?.contentIdeas || '',
                    instagramUrl: currentWorkspace.prospectingProfile?.instagramUrl || '',
                    inspiringBrands: currentWorkspace.prospectingProfile?.inspiringBrands || '',
                    competitors: currentWorkspace.prospectingProfile?.competitors || '',
                    targetChallenge: currentWorkspace.prospectingProfile?.targetChallenge || ''
                },
                socialMedia: {
                    mainPlatform: currentWorkspace.socialMedia?.mainPlatform || 'instagram',
                    instagram: {
                        handle: currentWorkspace.socialMedia?.instagram?.handle || '',
                        currentFollowers: currentWorkspace.socialMedia?.instagram?.currentFollowers || 0,
                        targetFollowers: currentWorkspace.socialMedia?.instagram?.targetFollowers || 0
                    },
                    tiktok: {
                        handle: currentWorkspace.socialMedia?.tiktok?.handle || '',
                        currentFollowers: currentWorkspace.socialMedia?.tiktok?.currentFollowers || 0,
                        targetFollowers: currentWorkspace.socialMedia?.tiktok?.targetFollowers || 0
                    },
                    youtube: {
                        handle: currentWorkspace.socialMedia?.youtube?.handle || '',
                        currentFollowers: currentWorkspace.socialMedia?.youtube?.currentFollowers || 0,
                        targetFollowers: currentWorkspace.socialMedia?.youtube?.targetFollowers || 0
                    }
                },
                videoProduction: {
                    currentVideosPerMonth: currentWorkspace.videoProduction?.currentVideosPerMonth || 0,
                    targetVideosPerMonth: currentWorkspace.videoProduction?.targetVideosPerMonth || 0
                },
                isSubscribed: currentWorkspace.isSubscribed || false,
                subscriptionPlan: currentWorkspace.subscriptionPlan || 'none'
            });

            // Initialize form data with workspace data
            setFormData({
                currentSituation: currentWorkspace.profile?.currentSituation || '',
                shortTermGoals: currentWorkspace.profile?.shortTermGoals || '',
                longTermGoals: currentWorkspace.profile?.longTermGoals || '',
                websiteUrl: currentWorkspace.profile?.websiteUrl || '',
                prospectingProfile: {
                    currentRole: currentWorkspace.prospectingProfile?.currentRole || '',
                    companyPurpose: currentWorkspace.prospectingProfile?.companyPurpose || '',
                    targetAudience: currentWorkspace.prospectingProfile?.targetAudience || '',
                    howToBePerceived: currentWorkspace.prospectingProfile?.howToBePerceived || '',
                    currentMarketingStrategy: currentWorkspace.prospectingProfile?.currentMarketingStrategy || '',
                    contentIdeas: currentWorkspace.prospectingProfile?.contentIdeas || '',
                    instagramUrl: currentWorkspace.prospectingProfile?.instagramUrl || '',
                    inspiringBrands: currentWorkspace.prospectingProfile?.inspiringBrands || '',
                    competitors: currentWorkspace.prospectingProfile?.competitors || '',
                    targetChallenge: currentWorkspace.prospectingProfile?.targetChallenge || ''
                },
                socialMedia: {
                    mainPlatform: currentWorkspace.socialMedia?.mainPlatform || 'instagram',
                    instagram: {
                        handle: currentWorkspace.socialMedia?.instagram?.handle || '',
                        currentFollowers: currentWorkspace.socialMedia?.instagram?.currentFollowers || 0,
                        targetFollowers: currentWorkspace.socialMedia?.instagram?.targetFollowers || 0
                    },
                    tiktok: {
                        handle: currentWorkspace.socialMedia?.tiktok?.handle || '',
                        currentFollowers: currentWorkspace.socialMedia?.tiktok?.currentFollowers || 0,
                        targetFollowers: currentWorkspace.socialMedia?.tiktok?.targetFollowers || 0
                    },
                    youtube: {
                        handle: currentWorkspace.socialMedia?.youtube?.handle || '',
                        currentFollowers: currentWorkspace.socialMedia?.youtube?.currentFollowers || 0,
                        targetFollowers: currentWorkspace.socialMedia?.youtube?.targetFollowers || 0
                    }
                },
                videoProduction: {
                    currentVideosPerMonth: currentWorkspace.videoProduction?.currentVideosPerMonth || 0,
                    targetVideosPerMonth: currentWorkspace.videoProduction?.targetVideosPerMonth || 0
                }
            });

            // Initialize subtitle colors from workspace
            setSubtitleFontColor(currentWorkspace.profile?.subtitleFontColor || '#ffffff');
            setSubtitleOutlineColor(currentWorkspace.profile?.subtitleOutlineColor || '#000000');
            setFontMixingSecondColor(currentWorkspace.profile?.fontMixingSecondColor || '#F5D66D');
            setSubtitleBackgroundColor(currentWorkspace.profile?.subtitleBackgroundColor || 'transparent');
            setSubtitleOutlineEnabled(currentWorkspace.profile?.subtitleOutlineEnabled !== undefined ? currentWorkspace.profile.subtitleOutlineEnabled : false);

            if (currentWorkspace.profile?.customFontId != null && currentWorkspace.profile.customFontId !== '') {
                setSelectedFontId(String(currentWorkspace.profile.customFontId));
            }

            if (currentWorkspace.profile?.thumbnailFontId != null && currentWorkspace.profile.thumbnailFontId !== '') {
                setThumbnailFontId(String(currentWorkspace.profile.thumbnailFontId));
            } else {
                setThumbnailFontId('');
            }
            setThumbnailFontColor(currentWorkspace.profile?.thumbnailFontColor || '#ffffff');

            // Initialize voice data from workspace
            if (currentWorkspace.voiceId && currentWorkspace.voiceId.trim() !== '') {
                setWorkspaceVoiceId(currentWorkspace.voiceId);
                setShowRecordingSection(false);
        } else {
                setWorkspaceVoiceId(null); // Set to null for empty voiceId
                setShowRecordingSection(true);
            }

            if (currentWorkspace.recordings) {
                setRecordings(currentWorkspace.recordings);
            }

            if (currentWorkspace.testAudio) {
                setTestAudioUrl(currentWorkspace.testAudio);
            } else {
                setTestAudioUrl(''); // Clear test audio when workspace has none
            }
            
            // Reset voice playing state when switching workspaces
            setIsPlayingVoice(false);
        }
    }, [currentWorkspace, isEditingName]);

    useEffect(() => {
        const idsKey = getRecordingIdsKey(recordings);
        const prevKey = prevRecordingIdsKeyRef.current;
        prevRecordingIdsKeyRef.current = idsKey;
        if (prevKey === null) return;

        const hasClonedVoice = !!(workspaceVoiceId && String(workspaceVoiceId).trim() !== '');
        if (!hasClonedVoice) return;

        const prevIds = new Set(prevKey.split('|').filter(Boolean));
        const addedNew = recordings.some((r) => {
            const id = String(r._id || r.id || '');
            return id && !prevIds.has(id);
        });

        if (addedNew) {
            setRecloneButtonShake(true);
            const t = setTimeout(() => setRecloneButtonShake(false), 500);
            return () => clearTimeout(t);
        }
    }, [recordings, workspaceVoiceId]);

    // Auto-save functionality
    useEffect(() => {
        if (!hasUnsavedChanges || !currentWorkspace) {
            return;
        }

        // Auto-save after 2 seconds of inactivity
        const timer = setTimeout(async () => {
            try {
                setIsAutoSaving(true);
                await saveWorkspaceProfile();
                console.log('Auto-saved workspace profile');
            } catch (error) {
                console.error('Auto-save failed:', error);
            } finally {
                setIsAutoSaving(false);
            }
        }, 2000);

        return () => clearTimeout(timer);
    }, [formData, subtitleFontColor, subtitleOutlineColor, subtitleBackgroundColor, subtitleOutlineEnabled, hasUnsavedChanges, currentWorkspace]);

    // Check for unsaved changes
    const checkForChanges = () => {
        if (!currentWorkspace) return false;
        
        const hasChanges = 
            formData.currentSituation !== (currentWorkspace.profile?.currentSituation || '') ||
            formData.shortTermGoals !== (currentWorkspace.profile?.shortTermGoals || '') ||
            formData.longTermGoals !== (currentWorkspace.profile?.longTermGoals || '') ||
            formData.websiteUrl !== (currentWorkspace.profile?.websiteUrl || '') ||
            subtitleFontColor !== (currentWorkspace.profile?.subtitleFontColor || '#ffffff') ||
            subtitleOutlineColor !== (currentWorkspace.profile?.subtitleOutlineColor || '#000000') ||
            fontMixingSecondColor !== (currentWorkspace.profile?.fontMixingSecondColor || '#F5D66D') ||
            subtitleBackgroundColor !== (currentWorkspace.profile?.subtitleBackgroundColor || 'transparent') ||
            subtitleOutlineEnabled !== (currentWorkspace.profile?.subtitleOutlineEnabled !== undefined ? currentWorkspace.profile.subtitleOutlineEnabled : false) ||
            formData.prospectingProfile.currentRole !== (currentWorkspace.prospectingProfile?.currentRole || '') ||
            formData.prospectingProfile.companyPurpose !== (currentWorkspace.prospectingProfile?.companyPurpose || '') ||
            formData.prospectingProfile.targetAudience !== (currentWorkspace.prospectingProfile?.targetAudience || '') ||
            formData.prospectingProfile.howToBePerceived !== (currentWorkspace.prospectingProfile?.howToBePerceived || '') ||
            formData.prospectingProfile.currentMarketingStrategy !== (currentWorkspace.prospectingProfile?.currentMarketingStrategy || '') ||
            formData.prospectingProfile.contentIdeas !== (currentWorkspace.prospectingProfile?.contentIdeas || '') ||
            formData.prospectingProfile.instagramUrl !== (currentWorkspace.prospectingProfile?.instagramUrl || '') ||
            formData.prospectingProfile.inspiringBrands !== (currentWorkspace.prospectingProfile?.inspiringBrands || '') ||
            formData.prospectingProfile.competitors !== (currentWorkspace.prospectingProfile?.competitors || '') ||
            formData.prospectingProfile.targetChallenge !== (currentWorkspace.prospectingProfile?.targetChallenge || '') ||
            formData.socialMedia.mainPlatform !== (currentWorkspace.socialMedia?.mainPlatform || 'instagram') ||
            formData.socialMedia.instagram.handle !== (currentWorkspace.socialMedia?.instagram?.handle || '') ||
            formData.socialMedia.instagram.currentFollowers !== (currentWorkspace.socialMedia?.instagram?.currentFollowers || 0) ||
            formData.socialMedia.instagram.targetFollowers !== (currentWorkspace.socialMedia?.instagram?.targetFollowers || 0) ||
            formData.socialMedia.tiktok.handle !== (currentWorkspace.socialMedia?.tiktok?.handle || '') ||
            formData.socialMedia.tiktok.currentFollowers !== (currentWorkspace.socialMedia?.tiktok?.currentFollowers || 0) ||
            formData.socialMedia.tiktok.targetFollowers !== (currentWorkspace.socialMedia?.tiktok?.targetFollowers || 0) ||
            formData.socialMedia.youtube.handle !== (currentWorkspace.socialMedia?.youtube?.handle || '') ||
            formData.socialMedia.youtube.currentFollowers !== (currentWorkspace.socialMedia?.youtube?.currentFollowers || 0) ||
            formData.socialMedia.youtube.targetFollowers !== (currentWorkspace.socialMedia?.youtube?.targetFollowers || 0) ||
            formData.videoProduction.currentVideosPerMonth !== (currentWorkspace.videoProduction?.currentVideosPerMonth || 0) ||
            formData.videoProduction.targetVideosPerMonth !== (currentWorkspace.videoProduction?.targetVideosPerMonth || 0);
        
        setHasUnsavedChanges(hasChanges);
        return hasChanges;
    };

    // Update hasUnsavedChanges whenever formData or subtitle colors change
    useEffect(() => {
        checkForChanges();
    }, [formData, subtitleFontColor, subtitleOutlineColor, fontMixingSecondColor, subtitleBackgroundColor, subtitleOutlineEnabled, currentWorkspace]);

    // Function to reload videos (can be called from anywhere)
    const reloadVideos = async () => {
        if (!currentWorkspace) return;
        
        setIsLoading(true);
        setIsLoadingPage(true);
        try {
            const response = await api.getWorkspaceVideos(currentWorkspace._id, { page: 1, limit: pageSize });
            const payload = response.data?.items ? response.data : { items: response.data, total: response.data?.length || 0, page: 1, pageSize };
            trackEvent('videos_fetched', {
                count: payload.items.length,
                tab: activeTab,
                page: 1,
                workspaceId: currentWorkspace._id
            });
            const mapped = (payload.items || []).map(video => ({
                _id: video._id,
                name: video.title,
                url: video.url,
                thumbnailUrl: video.thumbnailUrl || '',
                status: video.status,
                tags: Array.isArray(video.tags) ? video.tags : [],
                description: video.description,
                cuts: video.cuts,
                createdAt: video.createdAt
            }));
            setVideos(mapped);
            setPage(1);
            setHasMore(payload.items.length > 0 && (payload.items.length === pageSize || (payload.total && mapped.length < payload.total)));
            setRawTotal(payload.total || mapped.length);
        } catch (error) {
            trackEvent('videos_fetch_error', { error: error.message, tab: activeTab, workspaceId: currentWorkspace._id });
            console.error('Error fetching videos:', error);
            setErrors(prev => [...prev, 'Failed to fetch videos: ' + (error.response?.data?.error || error.message)]);
        } finally {
            setIsLoading(false);
            setIsLoadingPage(false);
        }
    };

    const fetchGeneratedVideosPage = useCallback(async (pageNum = 1, append = false) => {
        if (!currentWorkspace?._id) return;
        try {
            if (append) setIsLoadingGeneratedPage(true);
            const response = await api.getWorkspaceGeneratedVideos(currentWorkspace._id, {
                page: pageNum,
                pageSize,
            });
            const items = (response.data.items || []).map((v) => ({
                ...v,
                tags: Array.isArray(v.tags) ? v.tags : [],
            }));
            const total = response.data.total ?? 0;
            const hasMore = response.data.hasMore ?? false;

            if (append) {
                setGeneratedVideos(prev => {
                    const ids = new Set(prev.map(v => v._id));
                    const next = [...prev];
                    for (const v of items) {
                        if (!ids.has(v._id)) next.push(v);
                    }
                    return next;
                });
            } else {
                setGeneratedVideos(items);
            }
            setGeneratedTotal(total);
            setGeneratedPage(pageNum);
            setGeneratedHasMore(hasMore);

            if (pageNum === 1) {
                trackEvent('generated_videos_fetched', {
                    count: items.length,
                    workspaceId: currentWorkspace._id,
                });
            }
        } catch (error) {
            if (pageNum === 1) {
                trackEvent('generated_videos_fetch_error', {
                    error: error.message,
                    workspaceId: currentWorkspace._id,
                });
                console.error('Error fetching generated videos:', error);
                setErrors(prev => [...prev, 'Failed to fetch generated videos: ' + (error.response?.data?.error || error.message)]);
            } else {
                console.error('Error loading more generated videos:', error);
                setErrors(prev => [...prev, 'Failed to load more generated videos: ' + (error.response?.data?.error || error.message)]);
            }
        } finally {
            setIsLoadingGeneratedPage(false);
        }
    }, [currentWorkspace?._id, pageSize]);

    const loadMoreGeneratedVideos = useCallback(async () => {
        if (!currentWorkspace?._id || isLoadingGeneratedPage || !generatedHasMore) return;
        await fetchGeneratedVideosPage(generatedPage + 1, true);
    }, [currentWorkspace?._id, isLoadingGeneratedPage, generatedHasMore, generatedPage, fetchGeneratedVideosPage]);

    // Fetch videos and generated videos when workspace changes
    useEffect(() => {
        if (!currentWorkspace) return;

        const fetchFirstPage = async () => {
            await reloadVideos();
        };

        fetchFirstPage();
        fetchGeneratedVideosPage(1, false);
    }, [currentWorkspace, fetchGeneratedVideosPage]);

    // Function to fetch workspace cuts (paginated for cuts tab)
    const fetchWorkspaceCuts = async (page = 1, append = false) => {
        if (!currentWorkspace) return;

        try {
            setCutsLoading(true);
            
            // Clear hidden cuts ref when fetching page 1 (fresh start)
            if (page === 1 && !append) {
                hiddenCutsRef.current.clear();
            }
            
            const response = await api.getWorkspaceCuts(currentWorkspace._id, { 
                page, 
                pageSize: cutsPageSize 
            });
            
            if (response.data.items) {
                // Paginated response
                if (append) {
                    // When appending, filter out any duplicates and locally hidden cuts
                    setWorkspaceCuts(prev => {
                        const existingIds = new Set(prev.map(cut => cut._id));
                        const newCuts = response.data.items.filter(cut => 
                            !existingIds.has(cut._id) && !hiddenCutsRef.current.has(cut._id)
                        );
                        return [...prev, ...newCuts];
                    });
                } else {
                    // When fetching a new page, filter out locally hidden cuts
                    const filteredItems = response.data.items.filter(cut => !hiddenCutsRef.current.has(cut._id));
                    setWorkspaceCuts(filteredItems);
                }
                setCutsTotal(response.data.total || 0);
                setCutsHasMore(response.data.hasMore || false);
                setCutsPage(page);
            } else {
                // Backward compatibility: non-paginated response
                const visibleCuts = response.data.filter(cut => !cut.hidden);
                setWorkspaceCuts(visibleCuts);
                setCutsTotal(visibleCuts.length);
                setCutsHasMore(false);
            }
        } catch (error) {
            console.error('Error fetching workspace cuts:', error);
            setErrors(prev => [...prev, 'Failed to fetch cuts: ' + (error.response?.data?.error || error.message)]);
        } finally {
            setCutsLoading(false);
        }
    };

    // Function to load more cuts (for infinite scroll)
    const loadMoreCuts = async () => {
        if (!cutsHasMore || cutsLoading) return;
        await fetchWorkspaceCuts(cutsPage + 1, true);
    };

    // Function to refresh cuts data
    const refreshCutsData = async () => {
        if (!currentWorkspace) return;
        
        try {
            const response = await api.getWorkspaceCuts(currentWorkspace._id);
            const visibleCuts = response.data.filter(cut => !cut.hidden);
            const previousCount = workspaceCuts.length;
            
            setWorkspaceCuts(visibleCuts);
            setCutsTotal(visibleCuts.length);
            
            // If cuts count increased, we found new cuts
            if (visibleCuts.length > previousCount) {
                console.log(`Found ${visibleCuts.length - previousCount} new cuts!`);
                return true; // Indicates new cuts were found
            }
            return false; // No new cuts
        } catch (error) {
            console.error('Error refreshing cuts:', error);
            return false;
        }
    };

    // Function to start polling for cuts after upload
    const startCutsPolling = () => {
        if (cutsRefreshInterval) {
            clearInterval(cutsRefreshInterval);
        }
        
        setIsRefreshingCuts(true);
        let pollCount = 0;
        const maxPolls = 30; // Poll for up to 5 minutes (30 * 10 seconds)
        
        const interval = setInterval(async () => {
            pollCount++;
            const foundNewCuts = await refreshCutsData();
            
            if (foundNewCuts || pollCount >= maxPolls) {
                clearInterval(interval);
                setCutsRefreshInterval(null);
                setIsRefreshingCuts(false);
                console.log('Stopped polling for cuts');
            }
        }, 10000); // Poll every 10 seconds
        
        setCutsRefreshInterval(interval);
    };

    // Fetch cuts total when workspace changes (for tab button count)
    useEffect(() => {
        if (currentWorkspace) {
            // Clear hidden cuts ref when workspace changes (fresh start)
            hiddenCutsRef.current.clear();
            
            // Fetch cuts total immediately for tab button
            api.getWorkspaceCuts(currentWorkspace._id, { totalOnly: true })
                .then(response => {
                    setCutsTotal(response.data.total || 0);
                })
                .catch(error => {
                    console.error('Error fetching cuts total:', error);
                });
            
            // Fetch all workspace tags
            api.getWorkspaceTags(currentWorkspace._id)
                .then(response => {
                    setAllWorkspaceTags(response.data.tags || []);
                })
                .catch(error => {
                    console.error('Error fetching workspace tags:', error);
                    setAllWorkspaceTags([]);
                });
            
            // Also fetch first page if on cuts tab
            if (activeTab === 'cuts') {
                fetchWorkspaceCuts(1, false);
            }
        }
    }, [currentWorkspace]);

    // Refresh cuts when switching to cuts tab
    useEffect(() => {
        if (activeTab === 'cuts') {
            // Fetch first page of cuts for display
            fetchWorkspaceCuts(1, false);
        }
    }, [activeTab]);

    const prepareCarouselCuts = useCallback(async () => {
        if (!currentWorkspace?._id) return;
        const selectedVideoIds = (
            (studioVideoSourceMode === 'select' || studioVideoSourceMode === 'import') &&
            Array.isArray(studioSelectedRawVideoIds) &&
            studioSelectedRawVideoIds.length > 0
        )
            ? studioSelectedRawVideoIds
            : null;
        const hasTagFilters = includeTags.length > 0 || excludeTags.length > 0;
        const requestId = ++carouselCutsRequestIdRef.current;
        try {
            let items = [];
            if (selectedVideoIds && !hasTagFilters) {
                const response = await api.getWorkspaceCuts(currentWorkspace._id, {
                    videoIds: selectedVideoIds.join(','),
                });
                items = parseWorkspaceCutsResponse(response.data);
            } else {
                const response = await api.getWorkspaceCuts(currentWorkspace._id, timelinePickerCutsQuery(includeTags, excludeTags));
                items = parseWorkspaceCutsResponse(response.data);
            }
            if (selectedVideoIds) {
                const idSet = new Set(selectedVideoIds.map(String));
                items = items.filter((cut) => idSet.has(cutSourceVideoId(cut)));
            }
            if (requestId !== carouselCutsRequestIdRef.current) return;
            const visible = items.filter((cut) => !cut.hidden);
            setCarouselCuts(shuffleSliceCuts(visible, 15));
        } catch (error) {
            if (requestId !== carouselCutsRequestIdRef.current) return;
            console.error('Error fetching cuts for carousel:', error);
            setCarouselCuts([]);
        }
    }, [currentWorkspace?._id, includeTags, excludeTags, studioVideoSourceMode, studioSelectedRawVideoIds]);

    // Preload carousel cuts when opening studio, and refresh when tags / selected videos change
    useEffect(() => {
        if (activeTab === 'studio' && currentWorkspace && cutsTotal > 0) {
            prepareCarouselCuts();
        }
    }, [activeTab, currentWorkspace, cutsTotal, prepareCarouselCuts]);
    
    // Fetch matching cuts count when include/exclude tags change (for tag filter display)
    useEffect(() => {
        if (!currentWorkspace?._id) {
            setMatchingCutsCount(null);
            return;
        }
        const hasTagFilters = includeTags.length > 0 || excludeTags.length > 0;
        if (!hasTagFilters) {
            setMatchingCutsCount(null);
            return;
        }
        const timeoutId = setTimeout(async () => {
            try {
                const response = await api.getWorkspaceCuts(currentWorkspace._id, {
                    totalOnly: true,
                    includeTags: includeTags.length > 0 ? includeTags.join(',') : undefined,
                    excludeTags: excludeTags.length > 0 ? excludeTags.join(',') : undefined
                });
                setMatchingCutsCount(response.data.total ?? 0);
            } catch (err) {
                console.warn('Failed to fetch matching cuts count:', err);
                setMatchingCutsCount(null);
            }
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [currentWorkspace?._id, includeTags, excludeTags]);
    
    // Global polling for all videos generating cuts (parallel checks)
    useEffect(() => {
        if (!currentWorkspace || generatingCuts.size === 0) {
            // Clear interval if no videos are generating
            if (cutsPollingIntervalRef.current) {
                clearInterval(cutsPollingIntervalRef.current);
                cutsPollingIntervalRef.current = null;
            }
            return;
        }

        // Poll all videos in parallel
        const pollAllVideos = async () => {
            try {
                // Check all videos in parallel instead of sequentially
                const statusPromises = Array.from(generatingCuts).map(videoId => 
                    api.getVideoCutsGenerationStatus(currentWorkspace._id, videoId)
                        .then(resp => ({ videoId, status: resp?.data?.status, error: resp?.data?.error, success: true }))
                        .catch(error => {
                            console.error(`Error checking status for video ${videoId}:`, error);
                            return { videoId, status: null, success: false };
                        })
                );

                const statusResults = await Promise.all(statusPromises);
                
                // Update video statuses and handle completed/errored videos
                const completedVideos = [];
                const erroredVideos = [];
                const errorMessages = [];
                
                for (const result of statusResults) {
                    if (!result.success) {
                        continue; // Skip failed checks
                    }
                    
                    const { videoId, status } = result;
                    
                    // Update video status in local state
                    setVideos(prev => prev.map(v => 
                        v._id === videoId 
                            ? { ...v, cutsGenerationStatus: status }
                            : v
                    ));
                    
                    if (status === 'completed' || status === 'error') {
                        console.log(`[CUTS POLL] Generation ${status} for video ${videoId}`);
                        if (status === 'completed') {
                            completedVideos.push(videoId);
                        } else {
                            erroredVideos.push(videoId);
                            if (result.error) errorMessages.push(result.error);
                        }
                    }
                }
                
                // Remove completed/errored videos from generatingCuts
                if (completedVideos.length > 0 || erroredVideos.length > 0) {
                    setGeneratingCuts(prev => {
                        const newSet = new Set(prev);
                        completedVideos.forEach(id => newSet.delete(id));
                        erroredVideos.forEach(id => newSet.delete(id));
                        return newSet;
                    });
                    
                    // Refresh cuts count for completed videos
                    if (completedVideos.length > 0) {
                        try {
                            const cutsResponse = await api.getWorkspaceCuts(currentWorkspace._id, { totalOnly: true });
                            const newTotal = cutsResponse.data.total || 0;
                            console.log(`[CUTS POLL] Updating cuts total from ${cutsTotal} to ${newTotal}`);
                            setCutsTotal(newTotal);
                        } catch (error) {
                            console.error('Error refreshing cuts total:', error);
                        }

                        // Keep the raw list in place — only status was updated above.
                        // Avoid reloadVideos() which resets pagination/scroll on the raw tab.
                        
                        // If on cuts tab, refresh the cuts list
                        if (activeTab === 'cuts') {
                            fetchWorkspaceCuts(1, false);
                        }
                    }
                    
                    // Show error messages for failed videos
                    if (erroredVideos.length > 0) {
                        const unique = [...new Set(errorMessages)];
                        setErrors(prev => [...prev, ...(unique.length ? unique : ['Cut generation failed for some videos. Please try again.'])]);
                    }
                }
            } catch (error) {
                console.error('Error in parallel cuts polling:', error);
            }
        };

        // Start polling immediately, then every 5 seconds
        pollAllVideos();
        cutsPollingIntervalRef.current = setInterval(pollAllVideos, 5000);

        // Cleanup on unmount or when dependencies change
        return () => {
            if (cutsPollingIntervalRef.current) {
                clearInterval(cutsPollingIntervalRef.current);
                cutsPollingIntervalRef.current = null;
            }
        };
    }, [currentWorkspace?._id, generatingCuts, activeTab, fetchWorkspaceCuts]);

    // Infinite scroll observer for cuts
    useEffect(() => {
        if (!cutsHasMore || cutsLoading) return;
        const el = cutsLoadMoreRef.current;
        if (!el) return;
        const observer = new IntersectionObserver((entries) => {
            const first = entries[0];
            if (first.isIntersecting) {
                loadMoreCuts();
            }
        }, { root: null, rootMargin: '200px', threshold: 0 });
        observer.observe(el);
        return () => observer.disconnect();
    }, [cutsHasMore, cutsLoading, cutsPage]);

    // Infinite scroll observer
    useEffect(() => {
        if (activeTab !== 'raw') return;
        if (!hasMore || isLoading || isLoadingPage) return;
        const el = loadMoreRef.current;
        if (!el) return;
        let cancelled = false;
        const tryLoadMore = () => {
            if (cancelled || isLoadingPage || !hasMore) return;
            loadMore();
        };
        const observer = new IntersectionObserver((entries) => {
            const first = entries[0];
            if (first.isIntersecting) {
                tryLoadMore();
            }
        }, { root: null, rootMargin: '200px', threshold: 0 });
        observer.observe(el);
        // If the sentinel is already in view (short lists / tall screens), intersection
        // may not re-fire after a page load — check once after mount/update.
        requestAnimationFrame(() => {
            if (cancelled) return;
            const rect = el.getBoundingClientRect();
            if (rect.top < window.innerHeight + 200) tryLoadMore();
        });
        return () => {
            cancelled = true;
            observer.disconnect();
        };
    }, [activeTab, hasMore, isLoading, isLoadingPage, page]);

    // Infinite scroll for workspace generated videos tab
    useEffect(() => {
        if (activeTab !== 'generated') return;
        if (!generatedHasMore || isLoadingGeneratedPage) return;
        const el = generatedVideosLoadMoreRef.current;
        if (!el) return;
        const observer = new IntersectionObserver((entries) => {
            const first = entries[0];
            if (first.isIntersecting) {
                loadMoreGeneratedVideos();
            }
        }, { root: null, rootMargin: '200px', threshold: 0 });
        observer.observe(el);
        return () => observer.disconnect();
    }, [activeTab, generatedHasMore, isLoadingGeneratedPage, generatedVideosLoadMoreRef, loadMoreGeneratedVideos, generatedPage]);

    // Voice cloning useEffect hooks
    // Initialize random examples when showing them
    useEffect(() => {
        if (showExamples) {
            // Shuffle array and take first 10 examples
            const shuffled = [...RECORDING_EXAMPLES]
                .sort(() => Math.random() - 0.5)
                .slice(0, 10);
            setDisplayedExamples(shuffled);
            setCurrentExample(0);
        }
    }, [showExamples]);

    // Rotate through examples
    useEffect(() => {
        if (showExamples && displayedExamples.length > 0) {
            const interval = setInterval(() => {
                setCurrentExample(prev => (prev + 1) % displayedExamples.length);
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [showExamples, displayedExamples]);

    // Fetch Studio data when workspace changes
    useEffect(() => {
        if (!currentWorkspace) return;

        const fetchStudioData = async () => {
            try {
                // Extract currentUserId from JWT token
                const token = localStorage.getItem('token');
                let userId = null;
                if (token) {
                    try {
                        const payload = JSON.parse(atob(token.split('.')[1]));
                        userId = payload.id || payload.userId || payload.sub;
                        console.log('Extracted userId from token:', userId);
                    } catch (tokenError) {
                        console.error('Error parsing token:', tokenError);
                    }
                }
                setCurrentUserId(userId || '');
                
                // Fetch hooks - always use workspace hooks for workspaces
                const hooksResponse = await api.getWorkspaceHooks(currentWorkspace._id);
                const hooks = hooksResponse.data.hooks || [];
                setAvailableHooks(hooks);
                setHookStats(hooksResponse.data);
                
                // Automatically select a random hook if hooks are available
                if (hooks.length > 0) {
                    const randomIndex = Math.floor(Math.random() * hooks.length);
                    setStudioHook(hooks[randomIndex]);
                }

                // Fetch voices
                const voicesResponse = await api.getWorkspaceVoices(currentWorkspace._id);
                setAvailableVoices(voicesResponse.data.voices || []);
                setSelectedVoiceId(voicesResponse.data.workspaceVoiceId || voicesResponse.data.voices?.[0]?.id || '');

                // Fetch music
                const musicResponse = await api.getWorkspaceMusic(currentWorkspace._id);
                setAvailableMusic(musicResponse.data.music || []);

                // Fetch fonts
                const fontsResponse = await api.getWorkspaceFonts(currentWorkspace._id);
                setUserFonts(filterStudioFonts(fontsResponse.data.fonts || []));
                const workspaceFontId = fontsResponse.data.workspaceFontId;
                setSelectedFontId(
                    workspaceFontId != null && workspaceFontId !== ''
                        ? String(workspaceFontId)
                        : ''
                );

                // Fetch existing scripts
                // const scriptsResponse = await api.getWorkspaceScripts(currentWorkspace._id);
                // setStudioScripts(scriptsResponse.data || []);
                // We don't need to fetch scripts here anymore, they are fetched in the startStudioGenerate function
            } catch (error) {
                console.error('Error fetching Studio data:', error);
            }
        };

        fetchStudioData();
    }, [currentWorkspace]);

    // Load language preferences from localStorage
    useEffect(() => {
        if (currentWorkspace) {
            const savedGenerationLanguage = localStorage.getItem(`workspace_${currentWorkspace._id}_generation_language`);
            const savedTranslationLanguage = localStorage.getItem(`workspace_${currentWorkspace._id}_translation_language`);
            
            if (savedGenerationLanguage) {
                setGenerationLanguage(savedGenerationLanguage);
            }
            if (savedTranslationLanguage) {
                setTranslationLanguage(savedTranslationLanguage);
            }
        }
    }, [currentWorkspace]);

    // Save language preferences to localStorage
    useEffect(() => {
        if (currentWorkspace && generationLanguage) {
            localStorage.setItem(`workspace_${currentWorkspace._id}_generation_language`, generationLanguage);
        }
    }, [generationLanguage, currentWorkspace]);

    useEffect(() => {
        if (currentWorkspace && translationLanguage) {
            localStorage.setItem(`workspace_${currentWorkspace._id}_translation_language`, translationLanguage);
        }
    }, [translationLanguage, currentWorkspace]);

    // Clean up audio when component unmounts
    useEffect(() => {
        return () => {
            if (currentAudio) {
                currentAudio.pause();
                currentAudio.currentTime = 0;
                currentAudio.onended = null;
                currentAudio.onerror = null;
                currentAudio.src = '';
                currentAudio.load();
            }
            if (currentMusicAudio) {
                currentMusicAudio.pause();
                currentMusicAudio.currentTime = 0;
                currentMusicAudio.onended = null;
                currentMusicAudio.onerror = null;
                currentMusicAudio.src = '';
                currentMusicAudio.load();
            }
        };
    }, [currentAudio, currentMusicAudio]);

    // Stop audio when voice dropdown closes
    useEffect(() => {
        if (!showVoiceDropdown && currentAudio) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
            // Remove event listeners to prevent memory leaks
            currentAudio.onended = null;
            currentAudio.onerror = null;
            // Properly dispose of audio object
            currentAudio.src = '';
            currentAudio.load();
            setCurrentAudio(null);
            setPlayingVoiceId(null);
        }
    }, [showVoiceDropdown, currentAudio]);

    // Stop audio when music dropdown closes
    useEffect(() => {
        if (!showMusicDropdown && currentMusicAudio) {
            currentMusicAudio.pause();
            currentMusicAudio.currentTime = 0;
            // Remove event listeners to prevent memory leaks
            currentMusicAudio.onended = null;
            currentMusicAudio.onerror = null;
            // Properly dispose of audio object
            currentMusicAudio.src = '';
            currentMusicAudio.load();
            setCurrentMusicAudio(null);
            setPlayingMusicId(null);
        }
    }, [showMusicDropdown, currentMusicAudio]);

    // Close language dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is outside generation language dropdown
            if (showGenerationLanguageDropdown && !event.target.closest('[data-generation-language-dropdown]')) {
                setShowGenerationLanguageDropdown(false);
            }
            
            // Check if click is outside translation language dropdown
            if (showTranslationLanguageDropdown && !event.target.closest('[data-translation-language-dropdown]')) {
                setShowTranslationLanguageDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showGenerationLanguageDropdown, showTranslationLanguageDropdown]);

    // Handle closing dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is outside voice dropdown
            if (showVoiceDropdown && !event.target.closest('[data-voice-dropdown]')) {
                setShowVoiceDropdown(false);
            }
            // Check if click is outside music dropdown
            if (showMusicDropdown && !event.target.closest('[data-music-dropdown]')) {
                setShowMusicDropdown(false);
            }
            
            // Check if click is outside any batch music dropdown
            const clickedBatchMusicDropdown = event.target.closest('[data-music-dropdown]');
            if (!clickedBatchMusicDropdown) {
                setBatchShowMusicDropdowns({});
            }
            // Check if click is outside font dropdown
            if (showFontDropdown && !event.target.closest('[data-font-dropdown]')) {
                setShowFontDropdown(false);
            }
            if (showThumbnailFontDropdown && !event.target.closest('[data-thumbnail-font-dropdown]')) {
                setShowThumbnailFontDropdown(false);
            }
            if (showOpeningLineFontDropdown && !event.target.closest('[data-opening-line-font-dropdown]')) {
                setShowOpeningLineFontDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showVoiceDropdown, showMusicDropdown, showFontDropdown, showThumbnailFontDropdown, showOpeningLineFontDropdown, batchShowMusicDropdowns]);

    // Handle closing studio settings dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showStudioSettingsDropdown && studioSettingsDropdownRef.current && !studioSettingsDropdownRef.current.contains(event.target)) {
                setShowStudioSettingsDropdown(false);
            }
            if (showSkipSettingsDropdown && skipSettingsDropdownRef.current && !skipSettingsDropdownRef.current.contains(event.target)) {
                setShowSkipSettingsDropdown(false);
            }
        };

        if (showStudioSettingsDropdown || showSkipSettingsDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showStudioSettingsDropdown, showSkipSettingsDropdown]);

    const renderStudioSubtitleColorPickers = (scope) => {
        const fontColorRef = scope === 'vlog' ? studioVlogFontColorInputRef : studioTextFontColorInputRef;
        const outlineColorRef = scope === 'vlog' ? studioVlogOutlineColorInputRef : studioTextOutlineColorInputRef;
        const outlineCheckboxId = `studio-${scope}-subtitle-outline-enabled`;

        return (
            <div style={{ marginBottom: '12px' }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: screenWidth <= 630 ? '1fr' : '1fr 1fr',
                    gap: '12px',
                }}>
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Font color</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
                            {/* <input
                                type="text"
                                value={subtitleFontColor}
                                onChange={(e) => setSubtitleFontColor(e.target.value)}
                                onBlur={(e) => handleSubtitleFontColorChange(e.target.value)}
                                placeholder="#ffffff"
                                style={{
                                    width: '70px',
                                    padding: '8px',
                                    borderRadius: '5px',
                                    border: '1px solid var(--input-border)',
                                    backgroundColor: 'var(--input-bg)',
                                    color: 'var(--input-text)',
                                    fontSize: '13px',
                                }}
                            /> */}
                            <input
                                ref={fontColorRef}
                                type="color"
                                value={subtitleFontColor}
                                onChange={(e) => handleSubtitleFontColorChange(e.target.value)}
                                style={{ width: '36px', height: '36px', border: 'none', borderRadius: '3px', cursor: 'pointer', padding: 0 }}
                            />
                            <button
                                type="button"
                                onClick={() => fontColorRef.current?.click()}
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex' }}
                                aria-label="Open font color picker"
                            >
                                <img src="/color-picker.png" alt="" style={{ width: '28px', height: '28px' }} />
                            </button>
                        </div>
                    </div>
                    <div>
                        <label
                            htmlFor={outlineCheckboxId}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '5px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: 'var(--text-color)',
                            }}
                        >
                            <input
                                type="checkbox"
                                id={outlineCheckboxId}
                                checked={subtitleOutlineEnabled}
                                onChange={(e) => handleSubtitleOutlineEnabledChange(e.target.checked)}
                                style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                            />
                            <span>Outline color</span>
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative', opacity: subtitleOutlineEnabled ? 1 : 0.55 }}>
                            {/* <input
                                type="text"
                                value={subtitleOutlineColor}
                                onChange={(e) => setSubtitleOutlineColor(e.target.value)}
                                onBlur={(e) => handleSubtitleOutlineColorChange(e.target.value)}
                                placeholder="#000000"
                                disabled={!subtitleOutlineEnabled}
                                style={{
                                    width: '70px',
                                    padding: '8px',
                                    borderRadius: '5px',
                                    border: '1px solid var(--input-border)',
                                    backgroundColor: 'var(--input-bg)',
                                    color: 'var(--input-text)',
                                    fontSize: '13px',
                                }}
                            /> */}
                            <input
                                ref={outlineColorRef}
                                type="color"
                                value={subtitleOutlineColor}
                                onChange={(e) => handleSubtitleOutlineColorChange(e.target.value)}
                                disabled={!subtitleOutlineEnabled}
                                style={{ width: '36px', height: '36px', border: 'none', borderRadius: '3px', cursor: subtitleOutlineEnabled ? 'pointer' : 'not-allowed', padding: 0 }}
                            />
                            <button
                                type="button"
                                onClick={() => outlineColorRef.current?.click()}
                                disabled={!subtitleOutlineEnabled}
                                style={{ background: 'none', border: 'none', padding: 0, cursor: subtitleOutlineEnabled ? 'pointer' : 'not-allowed', display: 'flex' }}
                                aria-label="Open outline color picker"
                            >
                                <img src="/color-picker.png" alt="" style={{ width: '28px', height: '28px' }} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderSubtitleSizeRadios = (radioName) => (
        enableSubtitles ? (
            <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '14px', marginBottom: '8px', display: 'block', color: 'var(--text-color)' }}>
                    Subtitle size:
                </label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                        <input
                            type="radio"
                            name={radioName}
                            value="small"
                            checked={!bigText}
                            onChange={() => {
                                setBigText(false);
                                localStorage.setItem('workspace_big_text', 'false');
                            }}
                            style={{
                                cursor: 'pointer',
                                accentColor: 'var(--button-bg)',
                            }}
                        />
                        <span>Small</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                        <input
                            type="radio"
                            name={radioName}
                            value="large"
                            checked={bigText}
                            onChange={() => {
                                setBigText(true);
                                localStorage.setItem('workspace_big_text', 'true');
                            }}
                            style={{
                                cursor: 'pointer',
                                accentColor: 'var(--button-bg)',
                            }}
                        />
                        <span>Large</span>
                    </label>
                </div>
            </div>
        ) : null
    );

    const titleCardStyle = fontMixing
        ? 'style1'
        : (centerStackOpening ? 'style2' : (centerStackLineByLine ? 'style3' : 'standard'));
    const setTitleCardStyle = (style) => {
        const nextFontMixing = style === 'style1';
        const nextCenterStack = style === 'style2';
        const nextLineByLine = style === 'style3';
        setFontMixing(nextFontMixing);
        setCenterStackOpening(nextCenterStack);
        setCenterStackLineByLine(nextLineByLine);
        localStorage.setItem('workspace_font_mixing', nextFontMixing.toString());
        localStorage.setItem('workspace_center_stack_opening', nextCenterStack.toString());
        localStorage.setItem('workspace_center_stack_line_by_line', nextLineByLine.toString());
    };

    const renderTitleCardStyleRadios = (radioName) => {
        const infoButton = (styleId, ariaLabel) => (
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowTitleCardStyleInfo((prev) => (prev === styleId ? null : styleId));
                }}
                style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: 'var(--background-tertiary)',
                    color: 'var(--text-secondary)',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    flexShrink: 0,
                }}
                aria-label={ariaLabel}
            >
                i
            </button>
        );

        const accent = fontMixingSecondColor || '#F5D66D';
        const primary = openingLineFontColor || '#ffffff';

        const style1Demo = (
            <div style={{
                width: '160px',
                height: '220px',
                borderRadius: '14px',
                background: '#111',
                border: '1px solid rgba(255,255,255,0.12)',
                overflow: 'hidden',
                position: 'relative',
                padding: '28px 10px 16px',
                boxSizing: 'border-box',
            }}>
                {[
                    { text: 'THIS IS', color: primary, font: 'Georgia, serif', align: 'center', delay: '0s' },
                    { text: 'YOUR', color: accent, font: 'Arial Black, Arial, sans-serif', align: 'flex-start', delay: '0.35s' },
                    { text: 'TITLE', color: accent, font: 'Georgia, serif', align: 'flex-end', delay: '0.7s' },
                    { text: 'CARD', color: primary, font: 'Arial Black, Arial, sans-serif', align: 'center', delay: '1.05s' },
                ].map((line) => (
                    <div
                        key={line.text}
                        className="title-card-demo-style1-line"
                        style={{
                            display: 'flex',
                            justifyContent: line.align === 'center' ? 'center' : (line.align === 'flex-start' ? 'flex-start' : 'flex-end'),
                            color: line.color,
                            fontFamily: line.font,
                            fontSize: '15px',
                            fontWeight: 700,
                            lineHeight: 1.55,
                            letterSpacing: '0.02em',
                            animationDelay: line.delay,
                            textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                            padding: '0 4px',
                        }}
                    >
                        {line.text}
                    </div>
                ))}
            </div>
        );

        const style2Words = ['THIS', 'IS', 'YOUR', 'TITLE', 'CARD'];
        const style2Demo = (
            <div style={{
                width: '160px',
                height: '220px',
                borderRadius: '14px',
                background: '#111',
                border: '1px solid rgba(255,255,255,0.12)',
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                boxSizing: 'border-box',
                padding: '16px',
            }}>
                {[0, 1, 2].map((row) => {
                    const words = style2Words.slice(row * 2, row * 2 + 2);
                    if (words.length === 0) return null;
                    return (
                        <div
                            key={`row-${row}`}
                            style={{
                                display: 'flex',
                                gap: '8px',
                                justifyContent: 'center',
                                minHeight: '22px',
                            }}
                        >
                            {words.map((word, wi) => (
                                <span
                                    key={`${row}-${word}`}
                                    className="title-card-demo-style2-word"
                                    style={{
                                        color: primary,
                                        fontFamily: 'Arial Black, Arial, sans-serif',
                                        fontSize: '15px',
                                        fontWeight: 700,
                                        letterSpacing: '0.02em',
                                        animationDelay: `${(row * 2 + wi) * 0.35}s`,
                                        textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                                    }}
                                >
                                    {word}
                                </span>
                            ))}
                        </div>
                    );
                })}
            </div>
        );

        const style3Lines = ['THIS IS', 'YOUR TITLE', 'CARD'];
        const style3Demo = (
            <div style={{
                width: '160px',
                height: '220px',
                borderRadius: '14px',
                background: '#111',
                border: '1px solid rgba(255,255,255,0.12)',
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '10px',
                boxSizing: 'border-box',
                padding: '48px 16px 16px',
            }}>
                {style3Lines.map((line, li) => (
                    <div
                        key={`s3-${line}`}
                        className="title-card-demo-style3-line"
                        style={{
                            color: primary,
                            fontFamily: 'Arial Black, Arial, sans-serif',
                            fontSize: '15px',
                            fontWeight: 700,
                            letterSpacing: '0.02em',
                            textAlign: 'center',
                            animationDelay: `${li * 0.7}s`,
                            textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                            minHeight: '22px',
                        }}
                    >
                        {line}
                    </div>
                ))}
            </div>
        );

        const styleInfoMeta = {
            style1: {
                title: 'Style 1 example',
                demo: style1Demo,
                hint: 'Mixed fonts and alternating colors as lines appear.',
            },
            style2: {
                title: 'Style 2 example',
                demo: style2Demo,
                hint: 'One font and color; words reveal one-by-one in the center.',
            },
            style3: {
                title: 'Style 3 example',
                demo: style3Demo,
                hint: 'Same as Style 2, but lines appear whole; top line stays fixed.',
            },
        };
        const activeStyleInfo = styleInfoMeta[showTitleCardStyleInfo] || null;

        const popup = activeStyleInfo && (
            <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '8px',
                zIndex: 1200,
                backgroundColor: 'var(--input-bg)',
                border: '1px solid var(--input-border)',
                borderRadius: '10px',
                padding: '12px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
                width: 'max-content',
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    marginBottom: '10px',
                }}>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-color)' }}>
                        {activeStyleInfo.title}
                    </span>
                    <button
                        type="button"
                        onClick={() => setShowTitleCardStyleInfo(null)}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '16px',
                            color: 'var(--text-secondary)',
                            padding: '0 2px',
                            lineHeight: 1,
                        }}
                        aria-label="Close style example"
                    >
                        ×
                    </button>
                </div>
                {activeStyleInfo.demo}
                <div style={{
                    marginTop: '8px',
                    fontSize: '11px',
                    color: 'var(--text-secondary)',
                    lineHeight: 1.35,
                    maxWidth: '160px',
                }}>
                    {activeStyleInfo.hint}
                </div>
            </div>
        );

        return (
            <div style={{ marginBottom: '12px', position: 'relative' }}>
                <label style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', display: 'block' }}>Style:</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                        <input
                            type="radio"
                            name={radioName}
                            value="standard"
                            checked={titleCardStyle === 'standard'}
                            onChange={() => setTitleCardStyle('standard')}
                            style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                        />
                        <span>Standard</span>
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', position: 'relative' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                            <input
                                type="radio"
                                name={radioName}
                                value="style1"
                                checked={titleCardStyle === 'style1'}
                                onChange={() => setTitleCardStyle('style1')}
                                style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                            />
                            <span>Style 1</span>
                        </label>
                        {infoButton('style1', 'Show Style 1 animation example')}
                        {showTitleCardStyleInfo === 'style1' && popup}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', position: 'relative' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                            <input
                                type="radio"
                                name={radioName}
                                value="style2"
                                checked={titleCardStyle === 'style2'}
                                onChange={() => setTitleCardStyle('style2')}
                                style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                            />
                            <span>Style 2</span>
                        </label>
                        {infoButton('style2', 'Show Style 2 animation example')}
                        {showTitleCardStyleInfo === 'style2' && popup}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', position: 'relative' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                            <input
                                type="radio"
                                name={radioName}
                                value="style3"
                                checked={titleCardStyle === 'style3'}
                                onChange={() => setTitleCardStyle('style3')}
                                style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                            />
                            <span>Style 3</span>
                        </label>
                        {infoButton('style3', 'Show Style 3 animation example')}
                        {showTitleCardStyleInfo === 'style3' && popup}
                    </div>
                </div>
                {fontMixing && (
                    <div style={{ marginTop: '8px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>Second color</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }} data-font-mixing-second-color-picker>
                            <input
                                type="color"
                                value={fontMixingSecondColor}
                                onChange={(e) => setFontMixingSecondColor(e.target.value)}
                                style={{
                                    width: '30px',
                                    height: '30px',
                                    border: 'none',
                                    borderRadius: '3px',
                                    cursor: 'pointer',
                                }}
                            />
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="color"
                                    ref={fontMixingSecondColorInputRef}
                                    value={fontMixingSecondColor}
                                    onChange={(e) => setFontMixingSecondColor(e.target.value)}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '30px',
                                        height: '30px',
                                        border: 'none',
                                        borderRadius: '3px',
                                        cursor: 'pointer',
                                        opacity: 0,
                                        zIndex: 1,
                                    }}
                                />
                                <img
                                    src="/color-picker.png"
                                    alt="Color Picker"
                                    style={{
                                        width: '25px',
                                        height: '25px',
                                        cursor: 'pointer',
                                        position: 'relative',
                                        zIndex: 0,
                                        top: 2,
                                    }}
                                    onClick={() => {
                                        if (fontMixingSecondColorInputRef.current) {
                                            fontMixingSecondColorInputRef.current.click();
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderFastCutIntroToggle = () => {
        const frames = [
            'linear-gradient(145deg, #2a2a2a 0%, #6b6b6b 45%, #1a1a1a 100%)',
            'linear-gradient(160deg, #3d2b1f 0%, #c4a574 50%, #1f1510 100%)',
            'linear-gradient(180deg, #1e2a38 0%, #7aa0c4 48%, #0f1620 100%)',
            'linear-gradient(135deg, #2b1f33 0%, #b07ac4 50%, #140f18 100%)',
            'linear-gradient(150deg, #1f3326 0%, #7ec4a0 48%, #0f1814 100%)',
            'linear-gradient(170deg, #33221f 0%, #c48a7a 50%, #18100f 100%)',
            'linear-gradient(140deg, #222222 0%, #999999 40%, #111111 100%)',
            'linear-gradient(155deg, #2a2438 0%, #8a7ac4 50%, #12101a 100%)',
        ];

        return (
            <div style={{ marginBottom: '12px', position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={flipbookIntro}
                            onChange={(e) => {
                                const newValue = e.target.checked;
                                setFlipbookIntro(newValue);
                                localStorage.setItem('workspace_flipbook_intro', newValue.toString());
                            }}
                            style={{
                                cursor: 'pointer',
                                accentColor: 'var(--button-bg)',
                            }}
                        />
                        <span>Fast-cut Intro</span>
                    </label>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowFastCutIntroInfo((prev) => !prev);
                        }}
                        style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            border: 'none',
                            backgroundColor: 'var(--background-tertiary)',
                            color: 'var(--text-secondary)',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                            flexShrink: 0,
                        }}
                        aria-label="Show Fast-cut Intro animation example"
                    >
                        i
                    </button>
                </div>
                {showFastCutIntroInfo && (
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: '8px',
                        zIndex: 1200,
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '10px',
                        padding: '12px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
                        width: 'max-content',
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '12px',
                            marginBottom: '10px',
                        }}>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-color)' }}>
                                Fast-cut Intro example
                            </span>
                            <button
                                type="button"
                                onClick={() => setShowFastCutIntroInfo(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    color: 'var(--text-secondary)',
                                    padding: '0 2px',
                                    lineHeight: 1,
                                }}
                                aria-label="Close Fast-cut Intro example"
                            >
                                ×
                            </button>
                        </div>
                        <div style={{
                            width: '160px',
                            height: '220px',
                            borderRadius: '14px',
                            background: '#111',
                            border: '1px solid rgba(255,255,255,0.12)',
                            overflow: 'hidden',
                            position: 'relative',
                        }}>
                            {frames.map((bg, i) => (
                                <div
                                    key={i}
                                    className="fast-cut-demo-frame"
                                    style={{
                                        backgroundImage: bg,
                                        animationDelay: `${(i * 0.2).toFixed(2)}s`,
                                        filter: 'brightness(0.7)',
                                    }}
                                />
                            ))}
                        </div>
                        <div style={{
                            marginTop: '8px',
                            fontSize: '11px',
                            color: 'var(--text-secondary)',
                            lineHeight: 1.35,
                            maxWidth: '160px',
                        }}>
                            Rapid flashes of short clips at the start of the video.
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderTitleCardFontAndColor = ({ narrow = false } = {}) => (
        <div style={{
            marginTop: '4px',
            display: 'grid',
            gridTemplateColumns: narrow ? '1fr' : 'minmax(0, 1fr) auto',
            gap: '12px',
            alignItems: 'end',
        }}>
            <div style={{ minWidth: 0 }}>
                <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Title card font</label>
                <div style={{ position: 'relative' }} data-opening-line-font-dropdown>
                    <button
                        type="button"
                        onClick={() => setShowOpeningLineFontDropdown(!showOpeningLineFontDropdown)}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid var(--input-border)',
                            background: 'var(--input-bg)',
                            color: 'var(--input-text)',
                            fontSize: '13px',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            textAlign: 'left',
                        }}
                    >
                        <span style={{
                            fontFamily: openingLineFontId
                                ? fontFamilyCssForName(userFonts.find(f => f._id === openingLineFontId)?.name)
                                : defaultFontFamilyCss,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>
                            {openingLineFontId
                                ? userFonts.find(f => f._id === openingLineFontId)?.name || 'Select font...'
                                : 'Default (TikTok medium)'}
                        </span>
                        <span style={{ fontSize: '12px', opacity: 0.7, flexShrink: 0 }}>{showOpeningLineFontDropdown ? '▲' : '▼'}</span>
                    </button>
                    {showOpeningLineFontDropdown && (
                        <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '6px',
                            marginTop: '2px',
                            zIndex: 1000,
                            maxHeight: '200px',
                            overflowY: 'auto',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.5)',
                        }}>
                            <div
                                style={{ padding: '8px 12px', cursor: 'pointer', backgroundColor: openingLineFontId === '' ? 'var(--button-bg)' : 'transparent', color: openingLineFontId === '' ? 'var(--button-text)' : 'var(--text-color)', fontFamily: defaultFontFamilyCss }}
                                onClick={() => { handleOpeningLineFontChange(''); setShowOpeningLineFontDropdown(false); }}
                            >
                                Default (TikTok medium)
                            </div>
                            {sortedUserFonts.map(font => (
                                <div
                                    key={font._id}
                                    style={{ padding: '8px 12px', cursor: 'pointer', backgroundColor: openingLineFontId === font._id ? 'var(--button-bg)' : 'transparent', color: openingLineFontId === font._id ? 'var(--button-text)' : 'var(--text-color)', fontFamily: fontFamilyCssForName(font.name) }}
                                    onClick={() => { handleOpeningLineFontChange(font._id); setShowOpeningLineFontDropdown(false); }}
                                >
                                    {font.name}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <div>
                <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Title card color</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                        type="color"
                        value={openingLineFontColor}
                        onChange={(e) => handleOpeningLineColorChange(e.target.value)}
                        style={{ width: '30px', height: '30px', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                    />
                </div>
            </div>
        </div>
    );

    // Helper function to render generation settings dropdown content
    const renderGenerationSettingsDropdown = (placement = 'below') => (
        <div style={{
            position: 'absolute',
            ...(placement === 'above'
                ? { bottom: '100%', marginBottom: '8px', top: 'auto' }
                : { top: '100%', marginTop: '8px', bottom: 'auto' }),
            right: 0,
            backgroundColor: 'var(--input-bg)',
            border: '1px solid var(--input-border)',
            borderRadius: '8px',
            padding: '12px',
            paddingBottom: '4px',
            minWidth: '280px',
            maxWidth: '320px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 10002,
            maxHeight: '70vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '150px'
        }}>
            <div style={{ marginBottom: '12px', fontSize: '15px', fontWeight: '600', color: 'var(--text-color)', flex: '0 0 auto' }}>
                Generation Settings
            </div>
            <div style={{ position: 'relative', flex: '1 1 auto', minHeight: 0 }}>
            <div style={{ overflowY: 'auto', height: '100%', paddingBottom: '20px' }}>
            {/* Zoom + Whoosh Toggle */}
            {/* <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={zoomWhoosh}
                        onChange={(e) => {
                            const newValue = e.target.checked;
                            setZoomWhoosh(newValue);
                            localStorage.setItem('workspace_zoom_whoosh', newValue.toString());
                        }}
                        style={{
                            cursor: 'pointer',
                            accentColor: 'var(--button-bg)'
                        }}
                    />
                    <span>Zoom + Whoosh</span>
                </label>
            </div> */}

            {/* Flipbook Intro Toggle */}
            {renderFastCutIntroToggle()}

            {/* Slow motion toggle */}
            {/* <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontSize: '14px',
                    color: 'var(--text-color)'
                }}>
                    <input
                        type="checkbox"
                        checked={slowMotion}
                        onChange={(e) => setSlowMotion(e.target.checked)}
                        style={{
                            cursor: 'pointer',
                            accentColor: 'var(--button-bg)'
                        }}
                    />
                    <span style={{marginLeft:'8px'}}>Slow motion (0.75x speed)</span>
                </label>
            </div> */}

            {/* Cut length: 1s or 2s per cut */}
            <div style={{ marginBottom: '12px' }}>
                <span style={{ display: 'block', fontSize: '14px', color: 'var(--text-color)', marginBottom: '8px' }}>Cut length</span>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                        <input
                            type="radio"
                            name="dropdown-cut-length"
                            checked={!!fastPacing}
                            onChange={() => {
                                setFastPacing(true);
                                localStorage.setItem('workspace_fast_pacing', 'true');
                            }}
                            style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                        />
                        <span>1 sec</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                        <input
                            type="radio"
                            name="dropdown-cut-length"
                            checked={!fastPacing}
                            onChange={() => {
                                setFastPacing(false);
                                localStorage.setItem('workspace_fast_pacing', 'false');
                            }}
                            style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                        />
                        <span>2 sec</span>
                    </label>
                </div>
            </div>

            {currentUserId === BOUKAR_USER_ID && (
                <div style={{ margin: '14px 0 10px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
                    Boukar only
                </div>
            )}

            {currentUserId === BOUKAR_USER_ID && (
            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontSize: '14px',
                    color: 'var(--text-color)'
                }}>
                    <input
                        type="checkbox"
                        checked={cutSilenceMode}
                        onChange={(e) => {
                            const newValue = e.target.checked;
                            setCutSilenceMode(newValue);
                            localStorage.setItem('workspace_cut_silence_mode', newValue.toString());
                        }}
                        style={{
                            cursor: 'pointer',
                            accentColor: 'var(--button-bg)'
                        }}
                    />
                    <span style={{marginLeft:'8px'}}>Cut silence in voiceover</span>
                </label>
            </div>
            )}

            {currentUserId === BOUKAR_USER_ID && (
            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontSize: '14px',
                    color: 'var(--text-color)'
                }}>
                    <input
                        type="checkbox"
                        checked={speedUp}
                        onChange={(e) => {
                            const newValue = e.target.checked;
                            setSpeedUp(newValue);
                            localStorage.setItem('workspace_speed_up', newValue.toString());
                        }}
                        style={{
                            cursor: 'pointer',
                            accentColor: 'var(--button-bg)'
                        }}
                    />
                    <span style={{marginLeft:'8px'}}>Speed up (1.15x)</span>
                </label>
            </div>
            )}

            {currentUserId === BOUKAR_USER_ID && (
            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontSize: '14px',
                    color: 'var(--text-color)'
                }}>
                    <input
                        type="checkbox"
                        checked={cutMoreSilenceMode}
                        onChange={(e) => {
                            const newValue = e.target.checked;
                            setCutMoreSilenceMode(newValue);
                            localStorage.setItem('workspace_cut_more_silence_mode', newValue.toString());
                        }}
                        style={{
                            cursor: 'pointer',
                            accentColor: 'var(--button-bg)'
                        }}
                    />
                    <span style={{marginLeft:'8px'}}>Cut more silence</span>
                </label>
            </div>
            )}

            {currentUserId === BOUKAR_USER_ID && (
            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontSize: '14px',
                    color: 'var(--text-color)'
                }}>
                    <input
                        type="checkbox"
                        checked={aiSoundEffects}
                        onChange={(e) => {
                            const newValue = e.target.checked;
                            setAiSoundEffects(newValue);
                            localStorage.setItem('workspace_ai_sound_effects', newValue.toString());
                        }}
                        style={{
                            cursor: 'pointer',
                            accentColor: 'var(--button-bg)'
                        }}
                    />
                    <span style={{marginLeft:'8px'}}>AI sound effects</span>
                </label>
            </div>
            )}

            {/* Enable Subtitles Toggle */}
            <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={enableSubtitles}
                        onChange={(e) => {
                            setEnableSubtitles(e.target.checked);
                            localStorage.setItem('workspace_enable_subtitles', e.target.checked.toString());
                        }}
                        style={{
                            cursor: 'pointer',
                            accentColor: 'var(--button-bg)'
                        }}
                    />
                    <span>Enable subtitles</span>
                </label>
            </div>

            {/* Subtitle Position */}
            {enableSubtitles && (
                <div style={{ marginBottom: '12px' }}>
                    <label style={{ fontSize: '14px', marginBottom: '8px', display: 'block', color: 'var(--text-color)' }}>Subtitle position:</label>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                            <input
                                type="radio"
                                name="subtitlePosition"
                                value="center"
                                checked={subtitlePosition === 'center'}
                                onChange={(e) => {
                                    setSubtitlePosition('center');
                                    localStorage.setItem('workspace_subtitle_position', 'center');
                                }}
                                style={{
                                    cursor: 'pointer',
                                    accentColor: 'var(--button-bg)'
                                }}
                            />
                            <span>Center</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                            <input
                                type="radio"
                                name="subtitlePosition"
                                value="bottom"
                                checked={subtitlePosition === 'bottom'}
                                onChange={(e) => {
                                    setSubtitlePosition('bottom');
                                    localStorage.setItem('workspace_subtitle_position', 'bottom');
                                }}
                                style={{
                                    cursor: 'pointer',
                                    accentColor: 'var(--button-bg)'
                                }}
                            />
                            <span>Bottom</span>
                        </label>
                    </div>
                </div>
            )}

            {/* Subtitle size */}
            {renderSubtitleSizeRadios('dropdown-subtitle-size')}

            {/* Opening Line in Center Toggle — Boukar only */}
            {currentUserId === BOUKAR_USER_ID && enableSubtitles && (
                <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={openingLineInCenter}
                            onChange={(e) => {
                                const newValue = e.target.checked;
                                setOpeningLineInCenter(newValue);
                                localStorage.setItem('workspace_opening_line_in_center', newValue.toString());
                            }}
                            style={{
                                cursor: 'pointer',
                                accentColor: 'var(--button-bg)',
                                marginTop: '2px',
                            }}
                        />
                        <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span>Title card</span>
                            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 'normal' }}>
                                (First sentence becomes the title)
                            </span>
                        </span>
                    </label>
                </div>
            )}

            {/* One Word Per Subtitle Toggle */}
            {enableSubtitles && (
                <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={oneWordPerSubtitle}
                            onChange={(e) => {
                                const newValue = e.target.checked;
                                setOneWordPerSubtitle(newValue);
                                localStorage.setItem('workspace_one_word_per_subtitle', newValue.toString());
                            }}
                            style={{
                                cursor: 'pointer',
                                accentColor: 'var(--button-bg)'
                            }}
                        />
                        <span>1 word per subtitle</span>
                    </label>
                </div>
            )}

            </div>
            <div
                aria-hidden="true"
                style={{
                    pointerEvents: 'none',
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: 40,
                    background: 'linear-gradient(to bottom, transparent, var(--input-bg))',
                    zIndex: 2,
                }}
            />
            </div>
        </div>
    );

    // Save individual field to backend
    const saveField = async (fieldName, fieldData) => {
        try {
            setFieldLoading(fieldName, true);
            setFieldSaved(fieldName, false);
            
            await api.updateWorkspace(currentWorkspace._id, fieldData);
            
            // Update local workspace data to reflect the saved changes
            if (currentWorkspace) {
                if (fieldData.profile) {
                    currentWorkspace.profile = { ...currentWorkspace.profile, ...fieldData.profile };
                }
                if (fieldData.prospectingProfile) {
                    currentWorkspace.prospectingProfile = { ...currentWorkspace.prospectingProfile, ...fieldData.prospectingProfile };
                }
                if (fieldData.socialMedia) {
                    currentWorkspace.socialMedia = { ...currentWorkspace.socialMedia, ...fieldData.socialMedia };
                }
                if (fieldData.videoProduction) {
                    currentWorkspace.videoProduction = { ...currentWorkspace.videoProduction, ...fieldData.videoProduction };
                }
                if (fieldData.elevenLabsModel !== undefined) {
                    currentWorkspace.elevenLabsModel = fieldData.elevenLabsModel;
                }
            }
            
            setFieldLoading(fieldName, false);
            setFieldSaved(fieldName, true);
            
            // Refresh profile completeness check if we're in studio tab
            if (activeTab === 'studio') {
                setProfileCompleteness(checkProfileCompleteness());
            }
            
            return true;
        } catch (error) {
            console.error(`Error saving ${fieldName}:`, error);
            setFieldLoading(fieldName, false);
            setFieldSaved(fieldName, false);
            throw error;
        }
    };

    // Save workspace profile to backend
    const saveWorkspaceProfile = async () => {
        console.log('🔄 saveWorkspaceProfile called');
        try {
            setIsSaving(true);
            setSaveStatus('saving');
            
            const workspaceData = {
                profile: {
                    currentSituation: formData.currentSituation,
                    shortTermGoals: formData.shortTermGoals,
                    longTermGoals: formData.longTermGoals,
                    websiteUrl: formData.websiteUrl,
                    subtitleFontColor: subtitleFontColor,
                    subtitleOutlineColor: subtitleOutlineColor,
                    fontMixingSecondColor: fontMixingSecondColor,
                    subtitleBackgroundColor: subtitleBackgroundColor,
                    subtitleOutlineEnabled: subtitleOutlineEnabled
                },
                prospectingProfile: {
                    currentRole: formData.prospectingProfile.currentRole,
                    companyPurpose: formData.prospectingProfile.companyPurpose,
                    targetAudience: formData.prospectingProfile.targetAudience,
                    howToBePerceived: formData.prospectingProfile.howToBePerceived,
                    currentMarketingStrategy: formData.prospectingProfile.currentMarketingStrategy,
                    contentIdeas: formData.prospectingProfile.contentIdeas,
                    instagramUrl: formData.prospectingProfile.instagramUrl,
                    inspiringBrands: formData.prospectingProfile.inspiringBrands,
                    competitors: formData.prospectingProfile.competitors,
                    targetChallenge: formData.prospectingProfile.targetChallenge
                },
                socialMedia: formData.socialMedia,
                videoProduction: formData.videoProduction
            };
            
            // Save to backend directly without updating context state (prevents re-renders)
            console.log('📤 Sending workspace data to API:', workspaceData);
            await api.updateWorkspace(currentWorkspace._id, workspaceData);
            console.log('✅ Workspace data saved successfully');

            // Update local workspace data to reflect the saved changes
            if (currentWorkspace) {
                currentWorkspace.profile = { ...currentWorkspace.profile, ...workspaceData.profile };
                currentWorkspace.prospectingProfile = { ...currentWorkspace.prospectingProfile, ...workspaceData.prospectingProfile };
                currentWorkspace.socialMedia = { ...currentWorkspace.socialMedia, ...workspaceData.socialMedia };
                currentWorkspace.videoProduction = { ...currentWorkspace.videoProduction, ...workspaceData.videoProduction };
            }

            // Clear unsaved changes flag and mark as saved to backend
            console.log('🔄 Updating local state flags');
            setHasUnsavedChanges(false);
            
            // Refresh profile completeness check if we're in studio tab
            if (activeTab === 'studio') {
                setProfileCompleteness(checkProfileCompleteness());
            }
            setHasSavedToBackend(true);
            
            // Update the saved baseline to current form data
            setSavedFormData({
                currentSituation: formData.currentSituation,
                shortTermGoals: formData.shortTermGoals,
                longTermGoals: formData.longTermGoals,
                websiteUrl: formData.websiteUrl,
                subtitleFontColor: subtitleFontColor,
                subtitleOutlineColor: subtitleOutlineColor,
                fontMixingSecondColor: fontMixingSecondColor,
                subtitleBackgroundColor: subtitleBackgroundColor,
                subtitleOutlineEnabled: subtitleOutlineEnabled,
                prospectingProfile: {
                    currentRole: formData.prospectingProfile.currentRole,
                    companyPurpose: formData.prospectingProfile.companyPurpose,
                    targetAudience: formData.prospectingProfile.targetAudience,
                    howToBePerceived: formData.prospectingProfile.howToBePerceived,
                    currentMarketingStrategy: formData.prospectingProfile.currentMarketingStrategy,
                    contentIdeas: formData.prospectingProfile.contentIdeas,
                    instagramUrl: formData.prospectingProfile.instagramUrl,
                    inspiringBrands: formData.prospectingProfile.inspiringBrands,
                    competitors: formData.prospectingProfile.competitors,
                    targetChallenge: formData.prospectingProfile.targetChallenge
                },
                socialMedia: formData.socialMedia,
                videoProduction: formData.videoProduction
            });
            
            // Show success status
            setSaveStatus('saved');
            setIsSaving(false);
            
            // Reset status after 3 seconds
            setTimeout(() => {
                setSaveStatus('');
            }, 3000);
            
            return true; // Indicate success
            
            } catch (error) {
            console.error('Error saving workspace profile:', error);
            setSaveStatus('error');
            setIsSaving(false);
            
            // Reset error status after 5 seconds
            setTimeout(() => {
                setSaveStatus('');
            }, 5000);
            
            throw error; // Re-throw to be caught by caller
        }
    };

    // Voice cloning utility functions
    const formatTime = (seconds) => {
        if (typeof seconds === 'string') {
            return seconds;
        }
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Initialize voice recorder
    const initializeRecorder = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            setMediaStream(stream);

            const recorder = new MediaRecorder(stream, {
                mimeType: 'audio/webm'
            });
    
            const chunks = [];
            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunks.push(e.data);
                }
            };

            recorder.onstop = async () => {
                const audioBlob = new Blob(chunks, { type: 'audio/webm' });
                const finalDuration = recordingDuration;
                console.log('Final recording duration:', recordingDuration);

                try {
                    const formData = new FormData();
                    formData.append('audio', audioBlob, 'recording.webm');
                    formData.append('duration', finalDuration);
                    formData.append('workspaceId', currentWorkspace._id);

                    const response = await api.saveWorkspaceRecording(currentWorkspace._id, formData);
                    console.log('Recording saved:', response.data);
                    if (response.data.success) {
                        setRecordings(prev => [...prev, response.data.recording]);
                        setRecordingLength(prev => ({
                            ...prev,
                            [response.data.recording._id]: finalDuration
                        }));
                    }
                } catch (error) {
                    console.error('Error saving recording:', error);
                } finally {
                    setIsSavingRecording(false);
                    setIsRecording(false);
                }
                chunks.length = 0;
            };

            setMediaRecorder(recorder);
            return recorder;
        } catch (error) {
            console.error('Error accessing microphone:', error);
            return null;
        }
    };

    // Start recording
    const startRecording = async () => {
        if (recordings.length >= 5) {
            setModalErrorMessage('Maximum number of recordings reached');
            setShowErrorModal(true);
            return;
        }

        try {
            setIsRecording(true);
            setIsSavingRecording(false);
            setRecordingDuration(0);

            const recorder = await initializeRecorder();
            if (!recorder) {
                setIsRecording(false);
            return;
        }

            recorder.start();
            setMediaRecorder(recorder);

            // Start timer
            const interval = setInterval(() => {
                setRecordingDuration(prev => prev + 1);
            }, 1000);
            setTimerInterval(interval);

            } catch (error) {
            console.error('Error starting recording:', error);
            setIsRecording(false);
        }
    };

    // Stop recording
    const stopRecording = () => {
        if (mediaRecorder && mediaRecorder.state === 'recording') {
            setIsSavingRecording(true);
            mediaRecorder.stop();
            
            // Stop timer
            if (timerInterval) {
                clearInterval(timerInterval);
                setTimerInterval(null);
            }

            // Stop all tracks
            if (mediaStream) {
                mediaStream.getTracks().forEach(track => track.stop());
            }
        }
    };

    // Handle voice cloning
    const handleVoiceCloning = async () => {
        // Track voice cloning start
        trackEvent('voice_cloning_started', {
            recordingCount: recordings.length,
            workspaceId: currentWorkspace?._id,
            totalRecordingDuration: recordings.reduce((sum, rec) => sum + (recordingLength[rec._id] || 0), 0)
        });
        
        try {
            setIsGeneratingVoice(true);
            
            console.log('Recordings to send:', recordings);
            
            if (!recordings || recordings.length === 0) {
                throw new Error('No recordings found. Please record some samples first.');
            }
    
            // Create FormData
            const formData = new FormData();
            
            // Append each recording URL as a separate field
            recordings.forEach((recording, index) => {
                formData.append(`recording${index}`, recording.url);
            });
    
            // Add the total number of recordings and workspace ID
            formData.append('recordingCount', recordings.length);
            formData.append('workspaceId', currentWorkspace._id);
    
            // Clone the voice using ElevenLabs API
            const cloneResponse = await api.cloneWorkspaceVoice(currentWorkspace._id, formData);
    
            console.log('Voice cloning response:', cloneResponse.data);
            
            if (cloneResponse.data && cloneResponse.data.voice_id) {
                setWorkspaceVoiceId(cloneResponse.data.voice_id);
                await api.updateWorkspace(currentWorkspace._id, { voiceId: cloneResponse.data.voice_id });
                
                // Update the current workspace object to reflect the new voiceId
                if (currentWorkspace) {
                    currentWorkspace.voiceId = cloneResponse.data.voice_id;
                }
                
                // Refresh the voices list to include the new voice
                await refreshVoices();
                
                setModalSuccessMessage('Voice cloned successfully!');
                setShowSuccessModal(true);
            }
            } catch (error) {
            console.error('Error cloning voice:', error);
            setModalErrorMessage(
                isVoiceCloneLimitError(error)
                    ? VOICE_CLONE_LIMIT_MESSAGE
                    : (error.response?.data?.error || 'Failed to clone voice. Please try again.')
            );
            setShowErrorModal(true);
        } finally {
            setIsGeneratingVoice(false);
        }
    };

    // Test cloned voice
    const testClonedVoice = async () => {
        if (!workspaceVoiceId || workspaceVoiceId.trim() === '') {
            setModalErrorMessage('Please clone your voice first');
            setShowErrorModal(true);
            return;
        }
    
        try {
            setIsTestingVoice(true);
            const response = await api.testWorkspaceClonedVoice(
                currentWorkspace._id,
                "hey, this is me, the cloned voice, do I sound like you ?",
                workspaceVoiceId
            );
            
            if (response.data && response.data.audioUrl) {
                setTestAudioUrl(response.data.audioUrl);
                }
            } catch (error) {
            console.error('Error testing cloned voice:', error);
            
            // Check if it's a voice expiration error (404 from ElevenLabs)
            if (error.response?.status === 500 && error.response?.data?.error?.includes('voice_not_found')) {
                setModalErrorMessage('Your voice has expired and needs to be regenerated. Please go to the Profile tab to clone your voice again.');
                setShowErrorModal(true);
                return;
            }
            
            setModalErrorMessage('Failed to test voice. Please try again.');
            setShowErrorModal(true);
        } finally {
            setIsTestingVoice(false);
        }
    };

    // Handle delete recording
    const handleDeleteRecording = async (recordingId) => {
        // Check if this is the last recording and workspace has a cloned voice
        const isLastRecording = recordings.length === 1;
        const hasClonedVoice = workspaceVoiceId && workspaceVoiceId.trim() !== '';
        
        if (isLastRecording && hasClonedVoice) {
            // Show confirmation modal
            setRecordingToDelete(recordingId);
            setShowDeleteRecordingModal(true);
            return;
        }
        
        // Otherwise, proceed with deletion directly (pass id — state from setRecordingToDelete is stale this tick)
        await confirmDeleteRecording(recordingId);
    };
    
    // Confirm delete recording (called after confirmation or if no warning needed).
    // recordingIdOverride: pass when deleting from handleDeleteRecording so optimistic UI runs immediately (React state is async).
    const confirmDeleteRecording = async (recordingIdOverride) => {
        const idToDelete = recordingIdOverride != null ? recordingIdOverride : recordingToDelete;
        if (!idToDelete || !currentWorkspace?._id) return;
        if (recordingDeleteInFlight) return;
        setRecordingDeleteInFlight(true);
        
        // Store previous state for optimistic update revert
        const previousRecordings = [...recordings];
        const previousRecordingLength = { ...recordingLength };
        const previousWorkspaceVoiceId = workspaceVoiceId;
        const previousSelectedVoiceId = selectedVoiceId;
        const previousAvailableVoices = [...(availableVoices || [])];
        const previousCurrentWorkspaceVoiceId = currentWorkspace?.voiceId;
        
        // Optimistically update UI — list + modal dismiss before the network round-trip
        setRecordings(prev => prev.filter(recording => recording._id !== idToDelete));
        setRecordingLength(prev => {
            const newState = { ...prev };
            delete newState[idToDelete];
            return newState;
        });
        setShowDeleteRecordingModal(false);
        setRecordingToDelete(null);
        
        try {
            const response = await api.deleteWorkspaceRecording(currentWorkspace._id, idToDelete);
            
            // If voice was deleted, clear it from state
            if (response.data?.voiceDeleted) {
                setWorkspaceVoiceId('');
                // Also update currentWorkspace.voiceId to keep it in sync
                if (currentWorkspace) {
                    currentWorkspace.voiceId = '';
                }
                // Clear selectedVoiceId if it was the deleted voice
                if (selectedVoiceId === previousWorkspaceVoiceId) {
                    setSelectedVoiceId('');
                }
                // Refresh voices list to remove the deleted voice from dropdowns
                try {
                    const voicesResponse = await api.getWorkspaceVoices(currentWorkspace._id);
                    setAvailableVoices(voicesResponse.data.voices || []);
                    // Update selectedVoiceId if it was cleared, use first available voice or empty
                    if (!selectedVoiceId || selectedVoiceId === previousWorkspaceVoiceId) {
                        setSelectedVoiceId(voicesResponse.data.voices?.[0]?.id || '');
                    }
                } catch (voiceError) {
                    console.error('Error refreshing voices after deletion:', voiceError);
                }
                setModalSuccessMessage('Recording and cloned voice deleted successfully.');
                setShowSuccessModal(true);
            }
        } catch (error) {
            console.error('Error deleting recording:', error);
            // Revert optimistic update
            setRecordings(previousRecordings);
            setRecordingLength(previousRecordingLength);
            setWorkspaceVoiceId(previousWorkspaceVoiceId);
            setSelectedVoiceId(previousSelectedVoiceId);
            setAvailableVoices(previousAvailableVoices);
            if (currentWorkspace) {
                currentWorkspace.voiceId = previousCurrentWorkspaceVoiceId;
            }
            setModalErrorMessage('Failed to delete recording. Please try again.');
            setShowErrorModal(true);
        } finally {
            setRecordingDeleteInFlight(false);
        }
    };

    // Handle audio/video import (click picker or drag-and-drop; supports multiple files)
    const isVoiceImportFile = (file) => {
        if (!file) return false;
        const type = String(file.type || '');
        if (type.startsWith('audio/') || type.startsWith('video/')) return true;
        const name = String(file.name || '').toLowerCase();
        return /\.(mp3|wav|m4a|aac|ogg|flac|webm|mp4|mov|m4v|avi|mkv|3gp)$/i.test(name);
    };

    const triggerRecordingGlow = (recordingIds) => {
        const ids = (Array.isArray(recordingIds) ? recordingIds : [recordingIds])
            .map((id) => String(id))
            .filter(Boolean);
        if (!ids.length) return;
        if (recordingGlowTimeoutRef.current) clearTimeout(recordingGlowTimeoutRef.current);
        try {
            document.body.style.setProperty('--wm-glow-1', '#667eea');
            document.body.style.setProperty('--wm-glow-2', '#764ba2');
        } catch (_) {}
        setGlowingRecordingIds(ids);
        recordingGlowTimeoutRef.current = setTimeout(() => {
            setGlowingRecordingIds([]);
            recordingGlowTimeoutRef.current = null;
        }, BATCH_GLOW_MS);
    };

    const importVoiceFiles = async (fileList) => {
        const allFiles = Array.from(fileList || []);
        if (!allFiles.length) return;

        const files = allFiles.filter(isVoiceImportFile);
        if (!files.length) {
            setModalErrorMessage('Please select an audio or video file');
            setShowErrorModal(true);
            return;
        }

        const slotsLeft = Math.max(0, 5 - recordings.length);
        if (slotsLeft <= 0) {
            setModalErrorMessage('Maximum number of recordings reached');
            setShowErrorModal(true);
            return;
        }

        const VOICE_IMPORT_MAX_BYTES = 10 * 1024 * 1024;
        const oversized = files.filter((f) => f.size > VOICE_IMPORT_MAX_BYTES);
        const validFiles = files.filter((f) => f.size <= VOICE_IMPORT_MAX_BYTES);
        if (!validFiles.length) {
            setModalErrorMessage('Each file must be 10MB or less (ElevenLabs limit)');
            setShowErrorModal(true);
            return;
        }

        const toImport = validFiles.slice(0, slotsLeft);
        const skippedForLimit = Math.max(0, validFiles.length - toImport.length);
        const addedIds = [];

        try {
            setIsImportingAudio(true);
            for (const file of toImport) {
                const formData = new FormData();
                formData.append('audio', file);
                const response = await api.uploadWorkspaceAudioForVoiceCloning(currentWorkspace._id, formData);
                if (response.data?.recording) {
                    const recording = response.data.recording;
                    setRecordings((prev) => [...prev, recording]);
                    if (recording._id) addedIds.push(recording._id);
                    trackEvent('audio_imported', {
                        fileType: file.type,
                        fileSize: file.size,
                        workspaceId: currentWorkspace._id
                    });
                }
            }

            if (addedIds.length) {
                triggerRecordingGlow(addedIds);
            }

            if (oversized.length || skippedForLimit) {
                const parts = [];
                if (addedIds.length) parts.push(`Imported ${addedIds.length} file${addedIds.length === 1 ? '' : 's'}.`);
                if (skippedForLimit) parts.push(`${skippedForLimit} skipped (max 5 samples).`);
                if (oversized.length) parts.push(`${oversized.length} skipped (over 10MB).`);
                setModalErrorMessage(parts.join(' '));
                setShowErrorModal(true);
            }
        } catch (error) {
            console.error('Error importing audio:', error);
            if (addedIds.length) triggerRecordingGlow(addedIds);
            setModalErrorMessage(addedIds.length
                ? `Imported ${addedIds.length}, then failed on another file. Please try again.`
                : 'Failed to import voice file. Please try again.');
            setShowErrorModal(true);
        } finally {
            setIsImportingAudio(false);
            if (audioImportRef.current) {
                audioImportRef.current.value = '';
            }
        }
    };

    const handleAudioImport = async (e) => {
        await importVoiceFiles(e.target.files);
    };

    const handleVoiceImportDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (recordings.length >= 5 || isImportingAudio) return;
        setIsVoiceImportDragging(true);
    };

    const handleVoiceImportDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (recordings.length >= 5 || isImportingAudio) return;
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
        setIsVoiceImportDragging(true);
    };

    const handleVoiceImportDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Ignore leave events that stay within the drop target
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setIsVoiceImportDragging(false);
    };

    const handleVoiceImportDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsVoiceImportDragging(false);
        if (recordings.length >= 5 || isImportingAudio) return;
        await importVoiceFiles(e.dataTransfer?.files);
    };

    const handleUpdateName = async () => {
        try {
            setMessage('');
            setErrorMessage('');
            setModalErrorMessage('');
            isUpdatingNameRef.current = true;
            await updateWorkspace(currentWorkspace._id, { name: newName });
            setMessage('Workspace name updated successfully!');
            setIsEditingName(false);
            
            // Clear the success message after 3 seconds
            setTimeout(() => {
                setMessage('');
                isUpdatingNameRef.current = false;
            }, 3000);
        } catch (err) {
            setModalErrorMessage('Failed to update workspace name.');
            console.error('Error updating workspace name:', err);
            isUpdatingNameRef.current = false;
        }
    };

    // Refresh studio voices
    const refreshVoices = async () => {
        if (!currentWorkspace) return;
        
        try {
            const voicesResponse = await api.getWorkspaceVoices(currentWorkspace._id);
            setAvailableVoices(voicesResponse.data.voices || []);
            setSelectedVoiceId(voicesResponse.data.workspaceVoiceId || voicesResponse.data.voices?.[0]?.id || '');
        } catch (error) {
            console.error('Error refreshing voices:', error);
        }
    };

    // Handle workspace deletion
    const handleDeleteWorkspace = async () => {
        if (!currentWorkspace) return;
        
        // Check for active subscription
        const hasActiveSubscription = currentWorkspace.subscriptionStatus === 'active' || 
                                     currentWorkspace.subscriptionPlan !== 'none';
        
        if (hasActiveSubscription) {
            setModalErrorMessage("Can't delete a workspace with an active subscription");
            setShowErrorModal(true);
            return;
        }
        
        // Show confirmation modal
        setShowDeleteWorkspaceModal(true);
    };

    const confirmDeleteWorkspace = async () => {
        setShowDeleteWorkspaceModal(false);
        try {
            await deleteWorkspace(currentWorkspace._id);
            setModalSuccessMessage('Workspace deleted successfully');
            setShowSuccessModal(true);
        } catch (error) {
            console.error('Error deleting workspace:', error);
            setModalErrorMessage(`Failed to delete workspace: ${error.response?.data?.error || error.message}`);
            setShowErrorModal(true);
        }
    };

    const handleGenerateInvite = async () => {
        // Track invite link generation
        trackEvent('invite_link_generated', {
            workspaceId: currentWorkspace?._id,
            workspaceName: currentWorkspace?.name
        });
        
        try {
            setMessage('');
            setModalErrorMessage('');
            const response = await generateInviteLink(currentWorkspace._id);
            setInviteLink(response.inviteUrl);
            setShowInviteModal(true);
        } catch (err) {
            setModalErrorMessage('Failed to generate invite link.');
            console.error('Error generating invite link:', err);
        }
    };

    const handleRoleChange = async (memberId, newRole) => {
        try {
            setMessage('');
            setModalErrorMessage('');
            await updateMemberRole(currentWorkspace._id, memberId, newRole);
            setMessage('Member role updated successfully!');
        } catch (err) {
            setModalErrorMessage('Failed to update member role.');
            console.error('Error updating member role:', err);
        }
    };

    const handleRemoveMember = (memberId) => {
        setMemberToRemove(memberId);
        setShowRemoveMemberModal(true);
    };

    const confirmRemoveMember = async () => {
        if (!memberToRemove) return;
        
        setShowRemoveMemberModal(false);
        try {
            setMessage('');
            setModalErrorMessage('');
            await removeMember(currentWorkspace._id, memberToRemove);
            setMessage('Member removed successfully!');
            setMemberToRemove(null);
        } catch (err) {
            setModalErrorMessage('Failed to remove member.');
            setShowErrorModal(true);
            console.error('Error removing member:', err);
        }
    };

    const handleLeaveWorkspace = () => {
        setShowLeaveWorkspaceModal(true);
    };

    const confirmLeaveWorkspace = async () => {
        setShowLeaveWorkspaceModal(false);
        try {
            setMessage('');
            setModalErrorMessage('');
            await leaveWorkspace(currentWorkspace._id);
            setMessage('Successfully left the workspace.');
        } catch (err) {
            setModalErrorMessage('Failed to leave workspace.');
            setShowErrorModal(true);
            console.error('Error leaving workspace:', err);
        }
    };

    const handleTabChange = async (tabName) => {
        setActiveTab(tabName);
        // Mark that user has visited the profile tab if they switch to it
        if (tabName === 'profile') {
            setHasVisitedProfile(true);
            persistSetupStep({ visitedProfileTab: true });
        }
        if (tabName === 'cuts') {
            setHasVisitedCutsTab(true);
            persistSetupStep({ visitedCutsTab: true });
        }
        if (tabName === 'voice') {
            setHasVisitedVoiceTab(true);
            persistSetupStep({ visitedVoiceTab: true });
        }
        
        // Reset scroll position when switching tabs
        const tabContent = document.querySelector('.workspace-management-content');
        if (tabContent) {
            tabContent.scrollTop = 0;
        }
        
        // Refresh data when switching to generated videos tab
        if (tabName === 'generated') {
            try {
                await fetchGeneratedVideosPage(1, false);
            } catch (error) {
                console.error('Error refreshing generated videos:', error);
            }
        }

        if (tabName === 'home') {
            try {
                if (!workspaceCuts.length) {
                    await fetchWorkspaceCuts(1, false);
                }
            } catch (error) {
                console.error('Error loading cuts for home:', error);
            }
        }
        
        // Track tab changes
        trackEvent('workspace_tab_changed', {
            from: activeTab,
            to: tabName,
            workspaceId: currentWorkspace?._id
        });
    };

    useEffect(() => {
        const el = tabNavScrollRef.current;
        if (!el) return;

        const update = () => {
            const scrollEl = tabNavScrollRef.current;
            if (!scrollEl) return;
            const canLeft = scrollEl.scrollLeft > 0;
            const canRight = scrollEl.scrollLeft < scrollEl.scrollWidth - scrollEl.clientWidth - 1;
            setTabCanScrollLeft(canLeft);
            setTabCanScrollRight(canRight);
        };

        update();

        const raf1 = requestAnimationFrame(() => {
            requestAnimationFrame(update);
        });
        const timeout = setTimeout(update, 200);

        const ro = new ResizeObserver(update);
        ro.observe(el);
        Array.from(el.children).forEach(child => {
            ro.observe(child);
        });

        const mutationObserver = new MutationObserver(() => {
            requestAnimationFrame(update);
        });
        mutationObserver.observe(el, {
            childList: true,
            subtree: true,
            characterData: true
        });

        el.addEventListener('scroll', update);

        return () => {
            cancelAnimationFrame(raf1);
            clearTimeout(timeout);
            ro.disconnect();
            mutationObserver.disconnect();
            el.removeEventListener('scroll', update);
        };
    }, [rawTotal, cutsTotal, generatedTotal]);

    // Mark that user has visited Cuts tab when activeTab is 'cuts' (from tab bar or sidebar)
    useEffect(() => {
        if (activeTab === 'cuts') {
            setHasVisitedCutsTab(true);
            persistSetupStep({ visitedCutsTab: true });
        }
    }, [activeTab, persistSetupStep]);

    // Mark that user has visited Profile tab when activeTab is 'profile' (from tab bar or sidebar)
    useEffect(() => {
        if (activeTab === 'profile') {
            setHasVisitedProfile(true);
            persistSetupStep({ visitedProfileTab: true });
        }
    }, [activeTab, persistSetupStep]);

    // Mark that user has visited Voice tab when activeTab is 'voice' (from tab bar or sidebar)
    useEffect(() => {
        if (activeTab === 'voice') {
            setHasVisitedVoiceTab(true);
            persistSetupStep({ visitedVoiceTab: true });
        }
    }, [activeTab, persistSetupStep]);

    // Report workspace setup progress (steps 1–4) to parent. Step 4 = user has clicked on Voice tab.
    useEffect(() => {
        if (typeof onSetupProgress !== 'function') return;
        onSetupProgress({
            step1: rawTotal >= 1,
            step2: hasVisitedCutsTab,
            step3: hasVisitedProfile,
            step4: hasVisitedVoiceTab
        });
    }, [rawTotal, hasVisitedCutsTab, hasVisitedProfile, hasVisitedVoiceTab, onSetupProgress]);

    // Keep sidebar counts in sync (rawTotal, cutsTotal, generatedTotal)
    useEffect(() => {
        if (typeof setWorkspaceCounts === 'function') {
            setWorkspaceCounts({ rawTotal, cutsTotal, generatedTotal });
        }
    }, [rawTotal, cutsTotal, generatedTotal, setWorkspaceCounts]);

    const copyInviteLink = async () => {
        try {
            await copyToClipboard(inviteLink);
            setLinkCopied(true);
            // Reset checkmark after 2 seconds
            setTimeout(() => {
                setLinkCopied(false);
            }, 2000);
        } catch (error) {
            console.error('Failed to copy invite link:', error);
            setMessage('Failed to copy invite link. Please try again.');
        }
    };

    // Safari-compatible clipboard function
    const copyToClipboard = async (text) => {
        try {
            // Try modern clipboard API first
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                return;
            }
        } catch (err) {
            console.log('Clipboard API failed, trying fallback:', err);
        }
        
        // Fallback for Safari and other browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
            const successful = document.execCommand('copy');
            if (!successful) {
                throw new Error('execCommand copy failed');
            }
        } catch (err) {
            throw new Error('Failed to copy to clipboard: ' + err.message);
        } finally {
            document.body.removeChild(textArea);
        }
    };

    // Video management functions
    const loadMore = async () => {
        if (isLoadingPage || !hasMore) return;
        try {
            setIsLoadingPage(true);
            const nextPage = page + 1;
            const response = await api.getWorkspaceVideos(currentWorkspace._id, { page: nextPage, limit: pageSize });
            const payload = response.data?.items ? response.data : { items: response.data, total: response.data?.length || 0, page: nextPage, pageSize };
            const mapped = (payload.items || []).map(video => ({
                _id: video._id,
                name: video.title,
                url: video.url,
                thumbnailUrl: video.thumbnailUrl || '',
                status: video.status,
                tags: Array.isArray(video.tags) ? video.tags : [],
                description: video.description,
                cuts: video.cuts,
                createdAt: video.createdAt
            }));
            // Update videos and calculate hasMore
            setVideos(prev => {
                const seen = new Set(prev.map((v) => String(v._id)));
                const deduped = mapped.filter((v) => {
                    const id = String(v._id);
                    if (seen.has(id)) return false;
                    seen.add(id);
                    return true;
                });
                const newVideos = [...prev, ...deduped];
                const newVideoCount = newVideos.length;
                
                // Prefer backend hasMore when present; otherwise infer from page fullness / total
                if (typeof payload.hasMore === 'boolean') {
                    setHasMore(payload.hasMore);
                } else if (payload.total) {
                    setHasMore(newVideoCount < payload.total);
                } else {
                    setHasMore(mapped.length === pageSize);
                }
                
                return newVideos;
            });
            setPage(nextPage);
            setRawTotal(prev => (payload.total ? payload.total : prev + mapped.length));
        } catch (error) {
            console.error('Error loading more videos:', error);
            setErrors(prev => [...prev, 'Failed to load more videos: ' + (error.response?.data?.error || error.message)]);
        } finally {
            setIsLoadingPage(false);
        }
    };

    // Mac Photos (and some other apps) drop videos with type image/jpeg or application/octet-stream; use filename extension to detect video
    const getEffectiveFileType = (file) => {
        const name = (file.name || '').toLowerCase();
        const videoExts = ['.mov', '.mp4', '.m4v', '.avi', '.webm', '.mkv', '.3gp'];
        const isVideoExt = videoExts.some(ext => name.endsWith(ext));
        const ambiguousType = !file.type || file.type.startsWith('image/') || file.type === 'application/octet-stream';
        if (isVideoExt && ambiguousType) {
            if (name.endsWith('.mov')) return 'video/quicktime';
            if (name.endsWith('.mp4') || name.endsWith('.m4v')) return 'video/mp4';
            if (name.endsWith('.webm')) return 'video/webm';
            if (name.endsWith('.avi')) return 'video/x-msvideo';
            if (name.endsWith('.mkv')) return 'video/x-matroska';
            if (name.endsWith('.3gp')) return 'video/3gpp';
            return 'video/mp4';
        }
        return file.type;
    };

    const isVideoFile = (file) => getEffectiveFileType(file).startsWith('video/');

    // Handle file drop
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        
        // Only videos: use getEffectiveFileType so Mac Photos (image/* or octet-stream) is accepted when extension is video
        const allFiles = Array.from(e.dataTransfer.files);
        const files = allFiles.filter(isVideoFile);

        if (files.length === 0 && allFiles.length > 0) {
            const hasImages = allFiles.some(f => (f.type || '').startsWith('image/'));
            if (hasImages) {
                window.alert(
                    "Looks like you dragged from macOS Photos. It provides a JPEG preview instead of the original video.\n\n" +
                    "Fix: export the video to Finder first (Photos > File > Export > Export Unmodified Original), " +
                    "then drag the .MOV/.MP4 here."
                );
            } else {
                setErrors(prev => [...prev, 'Only video files are accepted. Please drop or select video files (.mov, .mp4, etc.).']);
            }
            return;
        }

        trackEvent('files_dropped', {
            count: files.length,
            types: files.map(f => f.type),
            workspaceId: currentWorkspace?._id
        });

        if (files.length > 50) {
            trackEvent('files_drop_error', {
                error: 'Too many files',
                count: files.length,
                workspaceId: currentWorkspace?._id
            });
            setErrors(prev => [...prev, 'You can only upload up to 50 files at once']);
            return;
        }

        handleFiles(files);
    };

    // Handle file selection
    const handleFileSelect = (e) => {
        const allFiles = Array.from(e.target.files);
        const files = allFiles.filter(isVideoFile);
        if (files.length === 0 && allFiles.length > 0) {
            const hasImages = allFiles.some(f => (f.type || '').startsWith('image/'));
            if (hasImages) {
                window.alert(
                    "Looks like you dragged from macOS Photos. It provides a JPEG preview instead of the original video.\n\n" +
                    "Fix: export the video to Finder first (Photos > File > Export > Export Unmodified Original), " +
                    "then drag the .MOV/.MP4 here."
                );
            } else {
                setErrors(prev => [...prev, 'Only video files are accepted. Please drop or select video files (.mov, .mp4, etc.).']);
            }
            e.target.value = '';
            return;
        }
        trackEvent('files_selected', {
            count: files.length,
            types: files.map(f => f.type),
            workspaceId: currentWorkspace?._id
        });
        handleFiles(files);
        e.target.value = '';
    };

    // Cancel upload function
    const cancelUpload = async (fileName) => {
        console.log(`[CANCEL UPLOAD] Cancelling upload for: ${fileName}`);
        
        // Get the active upload
        const activeUploadsMap = activeUploads;
        const xhr = activeUploadsMap.get(fileName);
        
        if (xhr) {
            // Abort the XMLHttpRequest
            xhr.abort();
            console.log(`[CANCEL UPLOAD] Aborted XMLHttpRequest for: ${fileName}`);
        }
        
        // Remove from active uploads
        setActiveUploads(prev => {
            const newMap = new Map(prev);
            newMap.delete(fileName);
            return newMap;
        });
        
        // Remove from upload progress
        setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[fileName];
            return newProgress;
        });
        
        // Update upload stats
        setUploadStats(prev => {
            const newStats = {
                ...prev,
                failedFiles: prev.failedFiles + 1,
                currentFileIndex: Math.max(0, prev.currentFileIndex - 1)
            };
            
            // If all files are cancelled or completed, clear the upload stats
            const totalProcessed = newStats.completedFiles + newStats.failedFiles;
            if (totalProcessed >= prev.totalFiles) {
                console.log('[CANCEL UPLOAD] All files processed, clearing upload stats');
                // Clear stats after a short delay to show final state
                setTimeout(() => {
                    setUploadStats({
                        totalFiles: 0,
                        currentFileIndex: 0,
                        completedFiles: 0,
                        failedFiles: 0,
                        currentFileName: ''
                    });
                }, 2000);
            }
            
            return newStats;
        });
        
        // Call backend cancel API to clean up S3 and database
        if (currentWorkspace) {
            try {
                await api.cancelUpload(currentWorkspace._id, fileName);
                console.log(`[CANCEL UPLOAD] Backend cleanup completed for: ${fileName}`);
            } catch (error) {
                console.error(`[CANCEL UPLOAD] Backend cleanup failed for ${fileName}:`, error);
            }
        }
        
        // Track cancellation event
        trackEvent('video_upload_cancelled', {
            fileName: fileName,
            workspaceId: currentWorkspace?._id
        });
    };

    // Process files
    const handleFiles = async (files) => {
        const uploadErrors = [];
        const failedFiles = new Set();

        // Commit any in-progress batch tag (e.g. user dropped files without pressing Enter)
        const pendingBatchTag = batchUploadTagInput.trim().replace(/,$/, '');
        let effectiveBatchTags = batchUploadTags;
        if (pendingBatchTag && !batchUploadTags.includes(pendingBatchTag)) {
            effectiveBatchTags = [...batchUploadTags, pendingBatchTag];
            setBatchUploadTags(effectiveBatchTags);
            setBatchUploadTagInput('');
        } else if (pendingBatchTag) {
            setBatchUploadTagInput('');
        }

        // Calculate total size and estimate time
        const totalSize = files.reduce((sum, file) => sum + file.size, 0);
        const totalSizeMB = totalSize / (1024 * 1024); // 1024 * 1024 = 1MB
        const isLargeUpload = totalSizeMB > 1000 || files.length > 20; // 1GB or 20 files
        
        // Track video upload start
        trackEvent('video_upload_started', {
            fileCount: files.length,
            totalSizeMB: Math.round(totalSizeMB * 100) / 100,
            isLargeUpload,
            workspaceId: currentWorkspace?._id,
            batchTags: effectiveBatchTags.length > 0 ? effectiveBatchTags.join(',') : null
        });
        
        // Show helpful message for large uploads
        if (isLargeUpload) {
            const estimatedMinutes = Math.ceil(totalSizeMB / 50); // Rough estimate: 50MB per minute
            setMessage(`Uploading ${files.length} files (${totalSizeMB.toFixed(1)}MB) to storage. Please keep this tab open and don't refresh the page.`);  /*This may take ${estimatedMinutes}+ minutes.*/
        }

        // Initialize upload stats
        setUploadStats({
            totalFiles: files.length,
            currentFileIndex: 0,
            completedFiles: 0,
            failedFiles: 0,
            currentFileName: files[0]?.name || ''
        });

        // Upload files in parallel
        const uploadPromises = files.map(async (file, index) => {
            // Check file size
            if (file.size > 2000 * 1024 * 1024) { // 2000MB
                uploadErrors.push(`File ${file.name} is too large. Maximum size is 2GB`);
                setUploadStats(prev => ({
                    ...prev,
                    failedFiles: prev.failedFiles + 1
                }));
                return;
            }

            const durationSec = await readVideoFileDurationSec(file);
            if (isRawVideoTooLong(durationSec)) {
                uploadErrors.push(`${file.name} is longer than 5 minutes. ${RAW_VIDEO_TOO_LONG_MESSAGE}`);
                setUploadStats(prev => ({
                    ...prev,
                    failedFiles: prev.failedFiles + 1
                }));
                return;
            }

            try {
                const effectiveType = getEffectiveFileType(file);
                trackEvent('file_upload_started', {
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: effectiveType,
                    fileIndex: index + 1,
                    totalFiles: files.length,
                    workspaceId: currentWorkspace?._id
                });

                // Step 1: Get presigned URL for direct S3 upload (or multipart upload ID)
                const uploadUrlResponse = await api.getWorkspaceUploadUrl(
                    currentWorkspace._id,
                    file.name,
                    effectiveType,
                    file.size
                );

                const { uploadUrl, key, videoUrl: initialVideoUrl, multipart, uploadId } = uploadUrlResponse.data;
                let videoUrl = initialVideoUrl;

                // Step 2: Upload directly to S3 (multipart for large files, single-part for smaller)
                let uploadPromise;
                if (multipart && uploadId) {
                    // Multipart upload for files >500MB
                    uploadPromise = new Promise(async (resolve, reject) => {
                        try {
                            const PART_SIZE = 10 * 1024 * 1024; // 10MB per part
                            const totalParts = Math.ceil(file.size / PART_SIZE);
                            const parts = [];
                            let uploadedBytes = 0;

                            // Upload each part
                            for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
                                const start = (partNumber - 1) * PART_SIZE;
                                const end = Math.min(start + PART_SIZE, file.size);
                                const chunk = file.slice(start, end);

                                // Get presigned URL for this part
                                const partUrlResponse = await api.getWorkspaceMultipartUrl(
                                    currentWorkspace._id,
                                    key,
                                    uploadId,
                                    partNumber
                                );

                                // Upload the part
                                const partETag = await new Promise((partResolve, partReject) => {
                                    const xhr = new XMLHttpRequest();

                                    xhr.upload.addEventListener('progress', (event) => {
                                        if (event.lengthComputable) {
                                            uploadedBytes = start + event.loaded;
                                            const progress = Math.round((uploadedBytes * 100) / file.size);
                                            setUploadProgress(prev => ({
                                                ...prev,
                                                [file.name]: progress
                                            }));
                                        }
                                    });

                                    xhr.addEventListener('load', () => {
                                        if (xhr.status >= 200 && xhr.status < 300) {
                                            const etag = xhr.getResponseHeader('ETag')?.replace(/"/g, '');
                                            if (!etag) {
                                                partReject(new Error('No ETag in response'));
                                                return;
                                            }
                                            partResolve(etag);
                                        } else {
                                            partReject(new Error(`Part ${partNumber} upload failed: ${xhr.statusText}`));
                                        }
                                    });

                                    xhr.addEventListener('error', () => partReject(new Error(`Part ${partNumber} upload failed: Network error`)));
                                    
                                    const fileSizeMB = file.size / 1024 / 1024;
                                    const timeoutMinutes = Math.max(60, Math.ceil(fileSizeMB / 100) * 5 + 60);
                                    xhr.addEventListener('timeout', () => {
                                        partReject(new Error(`Part ${partNumber} upload timeout after ${timeoutMinutes} minutes`));
                                    });

                                    xhr.open('PUT', partUrlResponse.data.uploadUrl);
                                    xhr.timeout = timeoutMinutes * 60 * 1000;
                                    xhr.send(chunk);
                                });

                                parts.push({ ETag: partETag, PartNumber: partNumber });
                            }

                            // Complete multipart upload
                            const completeResponse = await api.completeWorkspaceMultipart(
                                currentWorkspace._id,
                                key,
                                uploadId,
                                parts
                            );

                            videoUrl = completeResponse.data.videoUrl || videoUrl;
                            
                            // Ensure progress shows 100% when complete
                            setUploadProgress(prev => ({
                                ...prev,
                                [file.name]: 100
                            }));
                            
                            resolve({ status: 200 }); // Resolve with success
                        } catch (error) {
                            // Abort multipart upload on error
                            try {
                                await api.abortWorkspaceMultipart(currentWorkspace._id, key, uploadId);
                            } catch (abortError) {
                                console.error('Failed to abort multipart upload:', abortError);
                            }
                            reject(error);
                        }
                    });
                } else {
                    // Single-part upload for smaller files
                    uploadPromise = new Promise((resolve, reject) => {
                        const xhr = new XMLHttpRequest();
                        
                        // Track this upload for cancellation
                        setActiveUploads(prev => {
                            const newMap = new Map(prev);
                            newMap.set(file.name, xhr);
                            return newMap;
                        });
                        
                        // Track upload progress
                        xhr.upload.addEventListener('progress', (event) => {
                            if (event.lengthComputable) {
                                const progress = Math.round((event.loaded * 100) / event.total);
                                setUploadProgress(prev => ({
                                    ...prev,
                                    [file.name]: progress
                                }));
                            }
                        });
                        
                        // Handle successful upload
                        xhr.addEventListener('load', () => {
                            // Clean up active upload tracking
                            setActiveUploads(prev => {
                                const newMap = new Map(prev);
                                newMap.delete(file.name);
                                return newMap;
                            });
                            
                            if (xhr.status >= 200 && xhr.status < 300) {
                                resolve(xhr);
                            } else {
                                reject(new Error(`S3 upload failed: ${xhr.statusText}`));
                            }
                        });
                        
                        // Handle upload errors
                        xhr.addEventListener('error', () => {
                            // Clean up active upload tracking
                            setActiveUploads(prev => {
                                const newMap = new Map(prev);
                                newMap.delete(file.name);
                                return newMap;
                            });
                            reject(new Error('S3 upload failed: Network error'));
                        });
                        
                        // Handle upload timeout
                        xhr.addEventListener('timeout', () => {
                            // Clean up active upload tracking
                            setActiveUploads(prev => {
                                const newMap = new Map(prev);
                                newMap.delete(file.name);
                                return newMap;
                            });
                            reject(new Error('S3 upload failed: Timeout'));
                        });
                        
                        // Configure and start upload (use effectiveType so Mac Photos videos saved as image/jpeg upload as video)
                        xhr.open('PUT', uploadUrl);
                        xhr.setRequestHeader('Content-Type', effectiveType);
                        xhr.timeout = 1800000; // 30 minutes timeout for very large files
                        xhr.send(file);
                    });
                }
                
                await uploadPromise;

                // Step 3: Confirm upload with our server
                const tags = effectiveBatchTags || [];

                const confirmResponse = await api.confirmWorkspaceUpload(
                    currentWorkspace._id,
                    key,
                    videoUrl,
                    file.name,
                    '',
                    JSON.stringify(tags),
                    { durationSec: durationSec ?? undefined }
                );

                trackEvent('file_upload_success', {
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: effectiveType,
                    fileIndex: index + 1,
                    totalFiles: files.length,
                    workspaceId: currentWorkspace?._id
                });

                // Update or insert the uploaded video so it appears immediately
                setVideos(prev => {
                    const uploaded = confirmResponse.data.video || {};
                    const updatedVideo = {
                        _id: uploaded._id,
                        name: uploaded.title,
                        url: uploaded.url,
                        thumbnailUrl: uploaded.thumbnailUrl || '',
                        status: uploaded.status,
                        tags: uploaded.tags || [],
                        description: uploaded.description || '',
                        cuts: uploaded.cuts || [],
                        createdAt: uploaded.createdAt
                    };
                    const exists = prev.some(v => v._id === uploaded._id);
                    if (exists) {
                        return prev.map(v => v._id === uploaded._id ? { ...v, ...updatedVideo } : v);
                    }
                    // New video added: keep sidebar raw count in sync
                    setRawTotal(prevTotal => prevTotal + 1);
                    // Prepend so newest shows first
                    return [updatedVideo, ...prev];
                });

                // Clear progress after successful upload
                setUploadProgress(prev => {
                    const newProgress = { ...prev };
                    delete newProgress[file.name];
                    return newProgress;
                });

                // Update completed files count
                setUploadStats(prev => {
                    const newStats = {
                        ...prev,
                        completedFiles: prev.completedFiles + 1
                    };
                    
                    // If all files are completed or failed, clear the upload stats
                    const totalProcessed = newStats.completedFiles + newStats.failedFiles;
                    if (totalProcessed >= prev.totalFiles) {
                        console.log('[UPLOAD COMPLETE] All files processed, clearing upload stats');
                        // Clear stats after a short delay to show final state
                        setTimeout(() => {
                            setUploadStats({
                                totalFiles: 0,
                                currentFileIndex: 0,
                                completedFiles: 0,
                                failedFiles: 0,
                                currentFileName: ''
                            });
                        }, 2000);
                    }
                    
                    return newStats;
                });
            } catch (error) {
                trackEvent('file_upload_error', {
                    fileName: file.name,
                    error: error.message,
                    fileIndex: index + 1,
                    totalFiles: files.length,
                    workspaceId: currentWorkspace?._id
                });
                
                // Handle upload errors
                const fileSizeMB = file.size / (1024 * 1024);
                uploadErrors.push(`Failed to upload ${file.name}: ${error.response?.data?.error || error.message}`);
                failedFiles.add(file.name);
                
                // Clear progress for failed upload
                setUploadProgress(prev => {
                    const newProgress = { ...prev };
                    delete newProgress[file.name];
                    return newProgress;
                });

                // Update failed files count
                setUploadStats(prev => {
                    const newStats = {
                        ...prev,
                        failedFiles: prev.failedFiles + 1
                    };
                    
                    // If all files are completed or failed, clear the upload stats
                    const totalProcessed = newStats.completedFiles + newStats.failedFiles;
                    if (totalProcessed >= prev.totalFiles) {
                        console.log('[UPLOAD FAILED] All files processed, clearing upload stats');
                        // Clear stats after a short delay to show final state
                        setTimeout(() => {
                            setUploadStats({
                                totalFiles: 0,
                                currentFileIndex: 0,
                                completedFiles: 0,
                                failedFiles: 0,
                                currentFileName: ''
                            });
                        }, 2000);
                    }
                    
                    return newStats;
                });
            }
        });

        // Wait for all uploads to complete
        await Promise.allSettled(uploadPromises);

        // Clear upload stats after all files are processed
        setTimeout(() => {
            setUploadStats({
                totalFiles: 0,
                currentFileIndex: 0,
                completedFiles: 0,
                failedFiles: 0,
                currentFileName: ''
            });
        }, 3000);

        // Clear the large upload message
        if (isLargeUpload) {
            setTimeout(() => {
                setMessage('');
            }, 5000);
        }

        // Start polling for cuts if any uploads were successful
        const successfulUploads = uploadStats.completedFiles;
        if (successfulUploads > 0) {
            console.log(`Starting cuts polling for ${successfulUploads} uploaded videos`);
            startCutsPolling();
        }

        if (uploadErrors.length > 0) {
            setErrors(prev => [...prev, ...uploadErrors]);
        }
    };

    // Clear errors
    const clearErrors = () => {
        setErrors([]);
    };

    // Clear individual error
    const clearError = (index) => {
        setErrors(prev => prev.filter((_, i) => i !== index));
    };

    // Auto-dismiss success toast
    useEffect(() => {
        if (!message) return;
        const t = setTimeout(() => setMessage(''), 6000);
        return () => clearTimeout(t);
    }, [message]);

    // Generic tags to exclude from suggestions
    const excludedTags = ['auto-generated', 'script-generated', 'voiceover'];
    
    // Get all existing tags - use backend tags if available, otherwise fallback to loaded items
    const getAllExistingTags = useMemo(() => {
        let tags = [];
        
        // If we have tags from the backend API, use those (they include ALL tags)
        if (allWorkspaceTags.length > 0) {
            tags = allWorkspaceTags;
        } else {
            // Fallback: collect tags from currently loaded items (for initial load or if API fails)
            const tagSet = new Set();
            
            // Collect tags from videos
            videos.forEach(video => {
                if (video.tags && Array.isArray(video.tags)) {
                    video.tags.forEach(tag => tagSet.add(tag.toLowerCase()));
                }
            });
            
            // Collect tags from cuts
            workspaceCuts.forEach(cut => {
                if (cut.tags && Array.isArray(cut.tags)) {
                    cut.tags.forEach(tag => tagSet.add(tag.toLowerCase()));
                }
            });
            
            // Collect tags from generated videos
            generatedVideos.forEach(video => {
                if (video.tags && Array.isArray(video.tags)) {
                    video.tags.forEach(tag => tagSet.add(tag.toLowerCase()));
                }
            });
            
            tags = Array.from(tagSet).sort();
        }
        
        // Filter out generic/excluded tags
        return tags.filter(tag => !excludedTags.includes(tag.toLowerCase()));
    }, [allWorkspaceTags, videos, workspaceCuts, generatedVideos]);

    // Get filtered tag suggestions based on input
    const getTagSuggestions = (inputValue, existingTags) => {
        if (!inputValue || inputValue.trim().length === 0) {
            return existingTags; // Show all tags when input is empty
        }
        const lowerInput = inputValue.toLowerCase().trim();
        return existingTags.filter(tag => 
            tag.toLowerCase().includes(lowerInput) && 
            tag.toLowerCase() !== lowerInput
        );
    };

    const commitBatchUploadTagInput = () => {
        setBatchUploadTagInput((input) => {
            const trimmed = input.trim().replace(/,$/, '');
            if (!trimmed) return input;
            setBatchUploadTags((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
            return '';
        });
        setShowBatchTagSuggestions(false);
    };

    // Render tag input with suggestions dropdown
    const renderTagInputWithSuggestions = (inputId, value, onChange, onKeyPress, onAdd, showSuggestions, setShowSuggestions, inputRef, placeholder = "Add tag...", inputStyle = {}, onSuggestionClick = null, dropdownStyle = {}, onInputBlur = null) => {
        const suggestions = getTagSuggestions(value, getAllExistingTags);
        const hasSuggestions = suggestions.length > 0;

        return (
            <div style={{ position: 'relative', width: '100%' }}>
                <input
                    ref={inputRef}
                    type="text"
                    value={value}
                    onChange={(e) => {
                        onChange(e);
                        setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={(e) => {
                        // Delay hiding to allow click on suggestion
                        setTimeout(() => {
                            setShowSuggestions(false);
                            if (onInputBlur) onInputBlur(e);
                        }, 200);
                    }}
                    placeholder={placeholder}
                    onKeyPress={onKeyPress}
                    style={{ width: '100%', ...inputStyle }}
                />
                {showSuggestions && hasSuggestions && (
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 10000,
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '4px',
                        marginTop: '2px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        ...dropdownStyle
                    }}>
                        {suggestions.map((tag, index) => (
                            <div
                                key={`${inputId}-suggestion-${index}`}
                                onMouseDown={(e) => {
                                    // Prevent input blur when clicking suggestion
                                    e.preventDefault();
                                }}
                                onClick={() => {
                                    if (onSuggestionClick) {
                                        // Custom handler for auto-adding tags
                                        onSuggestionClick(tag);
                                    } else {
                                        // Default behavior: just set the input value
                                        onChange({ target: { value: tag } });
                                        setShowSuggestions(false);
                                        if (inputRef?.current) {
                                            inputRef.current.focus();
                                        }
                                    }
                                }}
                                style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: index < suggestions.length - 1 ? '1px solid var(--input-border)' : 'none',
                                    color: 'var(--text-color)',
                                    fontSize: '12px'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--button-bg)';
                                    e.currentTarget.style.color = 'var(--button-text)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = 'var(--text-color)';
                                }}
                            >
                                {tag}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Handle tag addition
    const handleAddTag = async (videoId) => {
        const tagValue = tagInputs[videoId];
        if (!tagValue?.trim()) return;

        trackEvent('tag_add_attempt', {
            videoId,
            tag: tagValue.trim(),
            workspaceId: currentWorkspace?._id
        });

        const trimmedTag = tagValue.trim();

        // Optimistically update the UI
        setVideos(prev => prev.map(video => {
            if (video._id === videoId) {
                return {
                    ...video,
                    tags: [...(video.tags || []), trimmedTag]
                };
            }
            return video;
        }));

        // Clear the input immediately
        setTagInputs(prev => ({
            ...prev,
            [videoId]: ''
        }));

        try {
            await api.addWorkspaceVideoTag(currentWorkspace._id, videoId, trimmedTag);
            trackEvent('tag_add_success', {
                videoId,
                tag: trimmedTag,
                workspaceId: currentWorkspace?._id
            });
        } catch (error) {
            trackEvent('tag_add_error', {
                videoId,
                tag: trimmedTag,
                error: error.message,
                workspaceId: currentWorkspace?._id
            });
            console.error('Error adding tag:', error);
            // If there's an error, remove the tag from the UI
            setVideos(prev => prev.map(video => {
                if (video._id === videoId) {
                    return {
                        ...video,
                        tags: (video.tags || []).filter(tag => tag !== trimmedTag)
                    };
                }
                return video;
            }));
            setErrors(prev => [...prev, `Failed to add tag: ${error.response?.data?.error || error.message}`]);
        }
    };

    // Handle tag removal
    const handleRemoveTag = async (videoId, tagToRemove) => {
        trackEvent('tag_remove_attempt', {
            videoId,
            tag: tagToRemove,
            workspaceId: currentWorkspace?._id
        });

        try {
            await api.removeWorkspaceVideoTag(currentWorkspace._id, videoId, tagToRemove);
            trackEvent('tag_remove_success', {
                videoId,
                tag: tagToRemove,
                workspaceId: currentWorkspace?._id
            });
            
            // Update local state
            setVideos(prev => prev.map(video => {
                if (video._id === videoId) {
                    return {
                        ...video,
                        tags: (video.tags || []).filter(tag => tag !== tagToRemove)
                    };
                }
                return video;
            }));
        } catch (error) {
            trackEvent('tag_remove_error', {
                videoId,
                tag: tagToRemove,
                error: error.message,
                workspaceId: currentWorkspace?._id
            });
            console.error('Error removing tag:', error);
            setErrors(prev => [...prev, `Failed to remove tag: ${error.response?.data?.error || error.message}`]);
        }
    };

    // Handle adding include tags
    const handleAddIncludeTag = () => {
        const input = includeTagInput.trim();
        if (input) {
            // Handle comma-separated tags
            const tags = input.split(',').map(tag => tag.trim()).filter(tag => tag && !includeTags.includes(tag));
            if (tags.length > 0) {
                setIncludeTags(prev => [...prev, ...tags]);
                setIncludeTagInput('');
            }
        }
    };

    // Handle removing include tags
    const handleRemoveIncludeTag = (tagToRemove) => {
        setIncludeTags(prev => prev.filter(tag => tag !== tagToRemove));
    };

    // Handle adding exclude tags
    const handleAddExcludeTag = () => {
        const input = excludeTagInput.trim();
        if (input) {
            // Handle comma-separated tags
            const tags = input.split(',').map(tag => tag.trim()).filter(tag => tag && !excludeTags.includes(tag));
            if (tags.length > 0) {
                setExcludeTags(prev => [...prev, ...tags]);
                setExcludeTagInput('');
            }
        }
    };

    // Handle removing exclude tags
    const handleRemoveExcludeTag = (tagToRemove) => {
        setExcludeTags(prev => prev.filter(tag => tag !== tagToRemove));
    };

    // Handle description update
    const handleUpdateDescription = async (videoId) => {
        trackEvent('description_update_attempt', {
            videoId,
            descriptionLength: newDescription.length,
            workspaceId: currentWorkspace?._id
        });

        try {
            // Optimistically update the UI
            setVideos(prev => prev.map(video => {
                if (video._id === videoId) {
                    return {
                        ...video,
                        description: newDescription
                    };
                }
                return video;
            }));

            // Close the edit mode immediately
            setEditingVideo(null);
            setNewDescription('');

            // Make the API call in the background
            await api.updateWorkspaceVideoDescription(currentWorkspace._id, videoId, newDescription);
            trackEvent('description_update_success', {
                videoId,
                descriptionLength: newDescription.length,
                workspaceId: currentWorkspace?._id
            });
        } catch (error) {
            trackEvent('description_update_error', {
                videoId,
                error: error.message,
                workspaceId: currentWorkspace?._id
            });
            console.error('Error updating description:', error);
            // If there's an error, revert the description
            setVideos(prev => prev.map(video => {
                if (video._id === videoId) {
                    return {
                        ...video,
                        description: video.description // Revert to original description
                    };
                }
                return video;
            }));
            // Reopen edit mode with the original description
            setEditingVideo(videoId);
            setNewDescription(videos.find(v => v._id === videoId)?.description || '');
            setErrors(prev => [...prev, `Failed to update description: ${error.response?.data?.error || error.message}`]);
        }
    };

    // Handle video deletion
    const handleGenerateCuts = async (video) => {
        if (!currentWorkspace) return;
        
        // Add video to generating cuts set
        setGeneratingCuts(prev => new Set(prev).add(video._id));
        
        trackEvent('video_generate_cuts_attempt', {
            videoId: video._id,
            videoName: video.name || video.title,
            workspaceId: currentWorkspace._id
        });

        try {
            await api.generateVideoCuts(currentWorkspace._id, video._id);
            
            trackEvent('video_generate_cuts_success', {
                videoId: video._id,
                workspaceId: currentWorkspace._id
            });
            
            // Update the video's cuts generation status in the local state
            setVideos(prev => prev.map(v => 
                v._id === video._id 
                    ? { ...v, cutsGenerationStatus: 'generating' }
                    : v
            ));
            
            // Global polling will handle status checks (see useEffect above)
            
        } catch (error) {
            console.error('Error generating cuts:', error);
            trackEvent('video_generate_cuts_error', {
                videoId: video._id,
                workspaceId: currentWorkspace._id,
                error: error.message
            });
            
            setErrors(prev => [...prev, error.response?.data?.error || 'Failed to start cut generation. Please try again.']);
            
            // Remove video from generating cuts set on error
            setGeneratingCuts(prev => {
                const newSet = new Set(prev);
                newSet.delete(video._id);
                return newSet;
            });
        }
    };

    // Handle shorts generation using Vizard API
    const handleGenerateShorts = async (video) => {
        if (!currentWorkspace) return;
        
        // Add video to generating shorts set
        setGeneratingShorts(prev => new Set(prev).add(video._id));
        
        trackEvent('video_generate_shorts_attempt', {
            videoId: video._id,
            videoName: video.name || video.title,
            workspaceId: currentWorkspace._id
        });

        try {
            await api.generateVideoShorts(currentWorkspace._id, video._id);
            
            trackEvent('video_generate_shorts_success', {
                videoId: video._id,
                workspaceId: currentWorkspace._id
            });
            
            // Update the video's shorts generation status in the local state
            setVideos(prev => prev.map(v => 
                v._id === video._id 
                    ? { ...v, shortsGenerationStatus: 'generating' }
                    : v
            ));
            
        } catch (error) {
            console.error('Error generating shorts:', error);
            trackEvent('video_generate_shorts_error', {
                videoId: video._id,
                workspaceId: currentWorkspace._id,
                error: error.message
            });
            
            setErrors(prev => [...prev, error.response?.data?.error || 'Failed to start shorts generation. Please try again.']);
            
            // Remove video from generating shorts set on error
            setGeneratingShorts(prev => {
                const newSet = new Set(prev);
                newSet.delete(video._id);
                return newSet;
            });
        }
    };

    const handleDeleteVideo = (videoId) => {
        setVideoToDelete(videoId);
        setShowDeleteVideoModal(true);
    };

    const confirmDeleteVideo = async () => {
        if (!videoToDelete) return;
        
        setShowDeleteVideoModal(false);
        trackEvent('video_delete_attempt', { videoId: videoToDelete, workspaceId: currentWorkspace?._id });

        // Optimistically remove the video from the UI
        setVideos(prev => prev.filter(video => video._id !== videoToDelete));

        try {
            await api.deleteWorkspaceVideo(currentWorkspace._id, videoToDelete);
            trackEvent('video_delete_success', { videoId: videoToDelete, workspaceId: currentWorkspace?._id });
            setVideoToDelete(null);
        } catch (error) {
            trackEvent('video_delete_error', {
                videoId: videoToDelete,
                error: error.message,
                workspaceId: currentWorkspace?._id
            });
            console.error('Error deleting video:', error);
            // If there's an error, add the video back to the list
            const videoToRestore = videos.find(v => v._id === videoToDelete);
            if (videoToRestore) {
                setVideos(prev => [...prev, videoToRestore]);
            }
            setModalErrorMessage(`Failed to delete video: ${error.response?.data?.error || error.message}`);
            setShowErrorModal(true);
            setVideoToDelete(null);
        }
    };

    const parseBulkTagsInput = (text) =>
        String(text || '')
            .split(/[,\n]/)
            .map((t) => t.trim())
            .filter(Boolean);

    const toggleBulkVideoSelection = (videoId) => {
        setBulkSelectedVideoIds((prev) =>
            prev.includes(videoId) ? prev.filter((id) => id !== videoId) : [...prev, videoId]
        );
    };

    const clearBulkVideoSelection = () => {
        setBulkSelectedVideoIds([]);
        setBulkActionMenu(null);
        setBulkTagsInput('');
        setBulkRenameInput('');
    };

    const handleBulkAddTags = async () => {
        const tags = parseBulkTagsInput(bulkTagsInput);
        if (!tags.length || !bulkSelectedVideoIds.length || !currentWorkspace?._id) return;

        setBulkActionLoading(true);
        try {
            for (const videoId of bulkSelectedVideoIds) {
                const video = videos.find((v) => v._id === videoId);
                for (const tag of tags) {
                    if (!video?.tags?.includes(tag)) {
                        await api.addWorkspaceVideoTag(currentWorkspace._id, videoId, tag);
                    }
                }
            }
            setVideos((prev) =>
                prev.map((v) => {
                    if (!bulkSelectedVideoIds.includes(v._id)) return v;
                    const existing = v.tags || [];
                    const merged = [...existing];
                    tags.forEach((tag) => {
                        if (!merged.includes(tag)) merged.push(tag);
                    });
                    return { ...v, tags: merged };
                })
            );
            clearBulkVideoSelection();
        } catch (error) {
            console.error('Error adding bulk tags:', error);
            setModalErrorMessage(`Failed to add tags: ${error.response?.data?.error || error.message}`);
            setShowErrorModal(true);
        } finally {
            setBulkActionLoading(false);
        }
    };

    const handleBulkRename = async () => {
        const base = bulkRenameInput.trim();
        if (!base || !bulkSelectedVideoIds.length || !currentWorkspace?._id) return;

        setBulkActionLoading(true);
        try {
            const updates = [];
            for (let i = 0; i < bulkSelectedVideoIds.length; i++) {
                const videoId = bulkSelectedVideoIds[i];
                const title = `${base} ${i + 1}`;
                await api.updateWorkspaceVideoTitle(currentWorkspace._id, videoId, title);
                updates.push({ videoId, title });
            }
            setVideos((prev) =>
                prev.map((v) => {
                    const update = updates.find((u) => u.videoId === v._id);
                    return update ? { ...v, name: update.title } : v;
                })
            );
            clearBulkVideoSelection();
        } catch (error) {
            console.error('Error renaming videos:', error);
            setModalErrorMessage(`Failed to rename videos: ${error.response?.data?.error || error.message}`);
            setShowErrorModal(true);
        } finally {
            setBulkActionLoading(false);
        }
    };

    const handleBulkDelete = async () => {
        if (!bulkSelectedVideoIds.length || !currentWorkspace?._id) return;

        setBulkActionLoading(true);
        const idsToDelete = [...bulkSelectedVideoIds];
        const videosToRestore = videos.filter((v) => idsToDelete.includes(v._id));

        setVideos((prev) => prev.filter((v) => !idsToDelete.includes(v._id)));
        setRawTotal((prev) => Math.max(0, prev - idsToDelete.length));

        try {
            for (const videoId of idsToDelete) {
                await api.deleteWorkspaceVideo(currentWorkspace._id, videoId);
            }
            clearBulkVideoSelection();
        } catch (error) {
            console.error('Error deleting videos:', error);
            setVideos((prev) => {
                const merged = [...prev];
                videosToRestore.forEach((v) => {
                    if (!merged.some((x) => x._id === v._id)) merged.push(v);
                });
                return merged;
            });
            setRawTotal((prev) => prev + videosToRestore.length);
            setModalErrorMessage(`Failed to delete videos: ${error.response?.data?.error || error.message}`);
            setShowErrorModal(true);
        } finally {
            setBulkActionLoading(false);
            setBulkActionMenu(null);
        }
    };

    const handleDeleteGeneratedVideo = async (videoId) => {
        if (!window.confirm('Are you sure you want to delete this generated video? This action cannot be undone.')) {
            return;
        }

        trackEvent('generated_video_delete_attempt', { videoId, workspaceId: currentWorkspace?._id });

        // Optimistically remove the video from the UI
        const videoToRestore = generatedVideos.find(v => v._id === videoId);
        setGeneratedVideos(prev => prev.filter(video => video._id !== videoId));
        setGeneratedTotal(prev => Math.max(0, prev - 1));

        try {
            await api.deleteWorkspaceGeneratedVideo(currentWorkspace._id, videoId);
            trackEvent('generated_video_delete_success', { videoId, workspaceId: currentWorkspace?._id });
        } catch (error) {
            trackEvent('generated_video_delete_error', {
                videoId,
                error: error.message,
                workspaceId: currentWorkspace?._id
            });
            console.error('Error deleting generated video:', error);
            // If there's an error, add the video back to the list
            if (videoToRestore) {
                setGeneratedVideos(prev => [...prev, videoToRestore].sort((a, b) => 
                    new Date(b.createdAt) - new Date(a.createdAt)
                ));
                setGeneratedTotal(prev => prev + 1);
            }
            setModalErrorMessage(`Failed to delete generated video: ${error.response?.data?.error || error.message}`);
            setShowErrorModal(true);
        }
    };

    const saveGeneratedVideoTitle = async () => {
        if (!currentWorkspace?._id || !editingGeneratedVideoId) return;
        const title = (editingGeneratedVideoTitle || '').trim();
        if (!title) {
            setEditingGeneratedVideoId(null);
            return;
        }
        const video = generatedVideos.find(v => v._id === editingGeneratedVideoId);
        if (video && video.title === title) {
            setEditingGeneratedVideoId(null);
            return;
        }
        try {
            const updated = await api.updateWorkspaceGeneratedVideoTitle(currentWorkspace._id, editingGeneratedVideoId, title);
            setGeneratedVideos(prev => prev.map(v => v._id === editingGeneratedVideoId ? { ...v, title: updated.data?.title ?? title } : v));
        } catch (err) {
            console.error('Failed to update video title:', err);
            setErrors(prev => [...prev, 'Failed to update video title: ' + (err.response?.data?.error || err.message)]);
        }
        setEditingGeneratedVideoId(null);
    };

    const cancelEditGeneratedVideoTitle = () => {
        setEditingGeneratedVideoId(null);
        setEditingGeneratedVideoTitle('');
    };

    // Handle video download
    const handleDownloadVideo = async (video) => {
        setDownloadingVideos(prev => new Set(prev).add(video._id));

        trackEvent('video_download_attempt', {
            videoId: video._id,
            videoName: video.name || video.title,
            workspaceId: currentWorkspace?._id
        });

        try {
            const isGeneratedVideo = video.title && !video.name;
            const response = isGeneratedVideo
                ? await api.getWorkspaceGeneratedVideoDownloadUrl(currentWorkspace._id, video._id)
                : await api.getWorkspaceVideoDownloadUrl(currentWorkspace._id, video._id);
            const downloadUrl = response.data.downloadUrl;
            const filename = `${sanitizeVideoFilename(video.name || video.title || 'video')}-${Date.now()}.mp4`;

            triggerDirectVideoDownload(downloadUrl, filename);
            celebrateVideoDownload();

            trackEvent('video_download_success', {
                videoId: video._id,
                videoName: video.name || video.title,
                workspaceId: currentWorkspace?._id
            });
        } catch (error) {
            trackEvent('video_download_error', {
                videoId: video._id,
                error: error.message,
                workspaceId: currentWorkspace?._id
            });
            console.error('Error downloading video:', error);
            setErrors(prev => [...prev, `Failed to download video: ${error.message}`]);
        } finally {
            setDownloadingVideos(prev => {
                const newSet = new Set(prev);
                newSet.delete(video._id);
                return newSet;
            });
        }
    };

    // Handle Instagram video import (async with polling)
    const handleImportInstagramVideos = async () => {
        const username = formData.socialMedia.instagram.handle?.replace('@', '').trim();
        
        if (!username) {
            setErrors(prev => [...prev, 'Please enter an Instagram handle first']);
            return;
        }
        
        if (!currentWorkspace?._id) {
            setErrors(prev => [...prev, 'Workspace not found']);
            return;
        }
        
        // Prompt for number of videos to import
        const videoCountInput = prompt(`How many videos would you like to import from @${username}? (Maximum: 20)`);
        
        if (videoCountInput === null) {
            // User cancelled
            return;
        }
        
        const videoCount = parseInt(videoCountInput, 10);
        
        if (isNaN(videoCount) || videoCount < 1) {
            setErrors(prev => [...prev, 'Please enter a valid number of videos']);
            return;
        }
        
        if (videoCount > 20) {
            setErrors(prev => [...prev, 'Maximum 20 videos can be imported at once']);
            return;
        }
        
        setImportingInstagram(true);
        
        try {
            // Start the import (returns immediately)
            const response = await api.importInstagramVideos(currentWorkspace._id, username, videoCount);
            
            if (response.data.status !== 'started') {
                throw new Error('Failed to start Instagram import');
            }
            
            // Poll for status updates
            const pollInterval = setInterval(async () => {
                try {
                    const statusResponse = await api.getInstagramImportStatus(currentWorkspace._id);
                    const status = statusResponse.data;
                    
                    if (status.status === 'none') {
                        clearInterval(pollInterval);
                        setImportingInstagram(false);
                        setErrors(prev => [...prev, 'Import status not found']);
                        return;
                    }
                    
                    // Update progress (could show in UI if needed)
                    console.log(`[INSTAGRAM IMPORT] Progress: ${status.progress}% - ${status.imported}/${status.total} videos`);
                    
                    if (status.status === 'completed') {
                        clearInterval(pollInterval);
                        setImportingInstagram(false);
                        
                        const { imported, total, errors: importErrors } = status;
                        
                        if (imported > 0) {
                            // Show success modal instead of error message
                            setModalSuccessMessage(`Successfully imported ${imported} of ${total} videos from @${username}${importErrors && importErrors.length > 0 ? ` (${importErrors.length} failed)` : ''}`);
                            setShowSuccessModal(true);
                            // Refresh videos list
                            await reloadVideos();
                        } else {
                            setErrors(prev => [...prev, status.error || 'No videos were imported']);
                        }
                    } else if (status.status === 'error') {
                        clearInterval(pollInterval);
                        setImportingInstagram(false);
                        setErrors(prev => [...prev, `Instagram import failed: ${status.error || 'Unknown error'}`]);
                    }
                    // If status is 'scraping' or 'importing', continue polling
                } catch (pollError) {
                    console.error('Error polling Instagram import status:', pollError);
                    // Continue polling on error (might be temporary)
                }
            }, 2000); // Poll every 2 seconds
            
            // Timeout after 5 minutes
            setTimeout(() => {
                clearInterval(pollInterval);
                setImportingInstagram(prev => {
                    if (prev) {
                        setErrors(errors => [...errors, 'Instagram import is taking longer than expected. Please check back later.']);
                        return false;
                    }
                    return prev;
                });
            }, 5 * 60 * 1000);
            
        } catch (error) {
            console.error('Error starting Instagram import:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Failed to start Instagram import';
            setErrors(prev => [...prev, `Instagram import failed: ${errorMessage}`]);
            setImportingInstagram(false);
        }
    };

    // GetLate integration handlers (workspace-scoped: profile + account ids stored on workspace)
    const handleGetlateConnect = async (platform) => {
        if (!currentWorkspace?._id) return;
        try {
            setGetlateConnecting(prev => ({ ...prev, [platform]: true }));
            const response = platform === 'instagram'
                ? await api.connectInstagram(currentWorkspace._id)
                : await api.connectTikTok(currentWorkspace._id);
            if (response?.authUrl) {
                const width = 600, height = 700;
                const left = (window.screen.width - width) / 2, top = (window.screen.height - height) / 2;
                const authWindow = window.open(response.authUrl, 'GetLate Auth', `width=${width},height=${height},left=${left},top=${top}`);
                const checkInterval = setInterval(async () => {
                    if (authWindow.closed) {
                        clearInterval(checkInterval);
                        setGetlateConnecting(prev => ({ ...prev, [platform]: false }));
                        const data = await api.getConnectedAccounts(currentWorkspace._id);
                        const ids = data?.lateAccountIds || {};
                        setGetlateConnectedAccounts({
                            instagram: !!ids.instagram,
                            tiktok: !!ids.tiktok
                        });
                    }
                }, 1000);
            } else {
                setErrors(prev => [...prev, `Failed to initiate ${platform} connection`]);
            }
        } catch (error) {
            console.error(`Error connecting ${platform}:`, error);
            setErrors(prev => [...prev, `Failed to connect ${platform}: ${error.message}`]);
        } finally {
            setGetlateConnecting(prev => ({ ...prev, [platform]: false }));
        }
    };

    const clearPublishingForVideo = (videoId) => {
        setGetlatePublishing(prev => {
            const next = new Set(prev);
            next.delete(videoId);
            return next;
        });
    };

    // Returns { platform, success, error? }. Caller is responsible for modal, clearPublishing, refetch.
    const handleGetlatePublish = async (video, platform, publishNow = true, opts = {}) => {
        if (!currentWorkspace?._id) return { platform, success: false, error: 'No workspace' };
        const POLL_INTERVAL_MS = 2000;
        const POLL_TIMEOUT_MS = 120000; // 2 min
        const caption = opts.caption != null ? opts.caption : (publishCaption || '');
        let scheduleAt = opts.scheduleAt != null ? opts.scheduleAt : null;
        if (scheduleAt == null && !publishNow) {
            const dateTime = `${publishScheduleDate}T${publishScheduleTime}`;
            scheduleAt = new Date(dateTime).toISOString();
        }
        try {
            setGetlatePublishing(prev => new Set(prev).add(video._id));
            const result = platform === 'instagram'
                ? await api.publishToInstagram(currentWorkspace._id, video.url, caption, scheduleAt, publishNow)
                : await api.publishToTikTok(currentWorkspace._id, video.url, caption, scheduleAt, publishNow);

            if (result.jobId) {
                const start = Date.now();
                const jobId = result.jobId;
                return new Promise((resolve) => {
                    const poll = async () => {
                        try {
                            const job = await api.getPublishStatus(jobId);
                            if (job.status === 'done') {
                                resolve({ platform, success: true });
                                return;
                            }
                            if (job.status === 'failed') {
                                resolve({ platform, success: false, error: job.error || 'Unknown error' });
                                return;
                            }
                            if (Date.now() - start < POLL_TIMEOUT_MS) {
                                setTimeout(poll, POLL_INTERVAL_MS);
                            } else {
                                resolve({ platform, success: false, error: 'Timed out. Check your account shortly.' });
                            }
                        } catch (e) {
                            resolve({ platform, success: false, error: e.message });
                        }
                    };
                    setTimeout(poll, POLL_INTERVAL_MS);
                });
            }
            if (result.success) {
                return { platform, success: true };
            }
            return { platform, success: false, error: result.error || 'Unknown error' };
        } catch (error) {
            console.error(`Error publishing to ${platform}:`, error);
            return { platform, success: false, error: error.message };
        }
    };

    // Check GetLate connection status for current workspace (profile + account ids per workspace)
    useEffect(() => {
        if (!currentWorkspace?._id) return;
        const checkConnections = async () => {
            try {
                const data = await api.getConnectedAccounts(currentWorkspace._id);
                const ids = data?.lateAccountIds || {};
                setGetlateConnectedAccounts({
                    instagram: !!ids.instagram,
                    tiktok: !!ids.tiktok
                });
            } catch (error) {
                console.error('Error checking GetLate connections:', error);
            }
        };
        checkConnections();
    }, [currentWorkspace?._id]);

    // Handle video reaction (like/dislike)
    const handleVideoReaction = async (videoId, reaction) => {
        try {
            // Optimistically update the UI
            setGeneratedVideos(prev => prev.map(video => 
                video._id === videoId 
                    ? { ...video, userReaction: reaction } 
                    : video
            ));

            await api.updateWorkspaceVideoReaction(currentWorkspace._id, videoId, reaction);
            
            trackEvent('video_reaction', {
                videoId,
                reaction,
                action: reaction === null ? 'removed' : 'added',
                workspaceId: currentWorkspace?._id
            });
        } catch (error) {
            // Revert the optimistic update on error
            setGeneratedVideos(prev => prev.map(video => 
                video._id === videoId 
                    ? { ...video, userReaction: video.userReaction } 
                    : video
            ));
            
            console.error('Error updating video reaction:', error);
            setErrors(prev => [...prev, `Failed to update reaction: ${error.response?.data?.error || error.message}`]);
        }
    };

    // Handle cut hiding
    const handleHideCut = async (cutId) => {
        try {
            // Track this cut as hidden locally
            hiddenCutsRef.current.add(cutId);
            
            // Optimistically remove the cut from the UI
            setWorkspaceCuts(prev => prev.filter(cut => cut._id !== cutId));
            setCutsTotal(prev => Math.max(0, prev - 1));
            
            // Also remove from carousel cuts if present
            setCarouselCuts(prev => prev.filter(cut => cut._id !== cutId));

            await api.toggleWorkspaceCutVisibility(currentWorkspace._id, cutId);
            
            trackEvent('cut_hidden', {
                cutId,
                workspaceId: currentWorkspace?._id
            });
            
            // Update hasMore flag if we now have fewer cuts than pageSize on current page
            // This prevents trying to load more when we've removed cuts from the current page
            setWorkspaceCuts(prev => {
                if (prev.length < cutsPageSize && cutsPage > 0) {
                    // If current page has fewer cuts than pageSize, we might have reached the end
                    // But don't set hasMore to false if we're on page 1 and have some cuts
                    if (cutsPage === 1 && prev.length > 0) {
                        // Keep hasMore as is - backend will tell us if there are more
                    } else if (prev.length === 0) {
                        // No cuts left, definitely no more
                        setCutsHasMore(false);
                    }
                }
                return prev;
            });
        } catch (error) {
            // Revert the optimistic update on error
            hiddenCutsRef.current.delete(cutId);
            await fetchWorkspaceCuts(cutsPage, false);
            
            console.error('Error hiding cut:', error);
            setErrors(prev => [...prev, `Failed to hide cut: ${error.response?.data?.error || error.message}`]);
        }
    };

    // Studio functions
    const handleHookSelect = (hook) => {
        setStudioHook(hook);
    };

    const clearStudioHook = () => {
        setStudioHook('');
    };

    const getStudioHooksForGeneration = () => {
        const trimmed = studioHook?.trim();
        return trimmed ? [trimmed] : [''];
    };

    const selectRandomHook = () => {
        if (availableHooks.length > 0) {
            const randomIndex = Math.floor(Math.random() * availableHooks.length);
            setStudioHook(availableHooks[randomIndex]);
        }
    };

    const handleVoiceChange = async (voiceId) => {
        setSelectedVoiceId(voiceId);
    };

    const handleMusicChange = (musicId) => {
        setSelectedMusicId(musicId);
        setMusicSelectionChanged(true); // User explicitly selected music from dropdown
    };

    const handleFontChange = async (fontId) => {
        const normalizedFontId = fontId != null && fontId !== '' ? String(fontId) : '';
        setSelectedFontId(normalizedFontId);
        
        // Save font selection to workspace
        if (currentWorkspace) {
            try {
                await api.updateWorkspace(currentWorkspace._id, {
                    profile: {
                        customFontId: normalizedFontId || null
                    }
                });
                
                // Update local workspace object to reflect the change
                if (currentWorkspace.profile) {
                    currentWorkspace.profile.customFontId = normalizedFontId || null;
                } else {
                    currentWorkspace.profile = { customFontId: normalizedFontId || null };
                }
            } catch (error) {
                console.error('Error saving font selection to workspace:', error);
                // Revert local state on error - use current workspace's font ID
                const previousFontId = currentWorkspace?.profile?.customFontId || '';
                setSelectedFontId(previousFontId);
            }
        }
    };

    const handleThumbnailFontChange = async (fontId) => {
        const normalizedFontId = fontId != null && fontId !== '' ? String(fontId) : '';
        setThumbnailFontId(normalizedFontId);
        if (!currentWorkspace) return;
        try {
            await api.updateWorkspace(currentWorkspace._id, {
                profile: { thumbnailFontId: normalizedFontId || null }
            });
            if (currentWorkspace.profile) {
                currentWorkspace.profile.thumbnailFontId = normalizedFontId || null;
            } else {
                currentWorkspace.profile = { thumbnailFontId: normalizedFontId || null };
            }
        } catch (error) {
            console.error('Error saving thumbnail font to workspace:', error);
            setThumbnailFontId(currentWorkspace?.profile?.thumbnailFontId ? String(currentWorkspace.profile.thumbnailFontId) : '');
        }
    };

    const handleOpeningLineFontChange = (fontId) => {
        const normalizedFontId = fontId != null && fontId !== '' ? String(fontId) : '';
        setOpeningLineFontId(normalizedFontId);
        localStorage.setItem('workspace_opening_line_font_id', normalizedFontId);
    };

    const handleOpeningLineColorChange = (color) => {
        const normalizedColor = color && String(color).trim() ? String(color).trim() : '#ffffff';
        const withHash = normalizedColor.startsWith('#') ? normalizedColor : `#${normalizedColor}`;
        setOpeningLineFontColor(withHash);
        localStorage.setItem('workspace_opening_line_font_color', withHash);
    };

    const handleThumbnailColorChange = async (color) => {
        const normalizedColor = color && String(color).trim() ? String(color).trim() : '#ffffff';
        setThumbnailFontColor(normalizedColor);
        if (!currentWorkspace) return;
        try {
            await api.updateWorkspace(currentWorkspace._id, {
                profile: { thumbnailFontColor: normalizedColor }
            });
            if (currentWorkspace.profile) {
                currentWorkspace.profile.thumbnailFontColor = normalizedColor;
            } else {
                currentWorkspace.profile = { thumbnailFontColor: normalizedColor };
            }
        } catch (error) {
            console.error('Error saving thumbnail color to workspace:', error);
            setThumbnailFontColor(currentWorkspace.profile?.thumbnailFontColor || '#ffffff');
        }
    };

    const normalizeSubtitleHexColor = (color, fallback) => {
        const trimmed = color && String(color).trim() ? String(color).trim() : fallback;
        return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    };

    const saveSubtitleStyleField = async (fieldName, value, revertState) => {
        if (!currentWorkspace?._id) return false;
        try {
            await api.updateWorkspace(currentWorkspace._id, {
                profile: { [fieldName]: value }
            });
            if (currentWorkspace.profile) {
                currentWorkspace.profile[fieldName] = value;
            } else {
                currentWorkspace.profile = { [fieldName]: value };
            }
            return true;
        } catch (error) {
            console.error(`Error saving ${fieldName} to workspace:`, error);
            if (typeof revertState === 'function') revertState();
            return false;
        }
    };

    const handleSubtitleFontColorChange = async (color) => {
        const previous = subtitleFontColor;
        const normalized = normalizeSubtitleHexColor(color, '#ffffff');
        setSubtitleFontColor(normalized);
        await saveSubtitleStyleField('subtitleFontColor', normalized, () => setSubtitleFontColor(previous));
    };

    const handleSubtitleOutlineColorChange = async (color) => {
        const previous = subtitleOutlineColor;
        const normalized = normalizeSubtitleHexColor(color, '#000000');
        setSubtitleOutlineColor(normalized);
        await saveSubtitleStyleField('subtitleOutlineColor', normalized, () => setSubtitleOutlineColor(previous));
    };

    const handleSubtitleOutlineEnabledChange = async (enabled) => {
        const previous = subtitleOutlineEnabled;
        setSubtitleOutlineEnabled(enabled);
        await saveSubtitleStyleField('subtitleOutlineEnabled', enabled, () => setSubtitleOutlineEnabled(previous));
    };

             const buildThumbnailConfigPayload = (cut, title, bigText) => {
        if (!cut || !title?.trim()) return undefined;
        return {
            cutId: String(cut._id),
            title: normalizeThumbnailTitleText(title),
            bigText: bigText !== false,
            fontId: thumbnailFontId || undefined,
            fontColor: thumbnailFontColor || undefined,
        };
    };

    const buildOpeningLineFontPayload = () => {
        if (currentUserId !== BOUKAR_USER_ID) return {};
        if (!openingLineInCenter && !fontMixing && !centerStackOpening && !centerStackLineByLine) return {};
        const fontId = openingLineFontId || ((fontMixing || centerStackOpening || centerStackLineByLine) ? selectedFontId : '');
        if (!fontId) return {};
        const font = userFonts.find(f => String(f._id) === String(fontId));
        return {
            openingLineFontId: fontId,
            openingLineCustomFontUrl: font?.url || undefined,
        };
    };

    const hasConfiguredThumbnail = () => !!(thumbnailTitle.trim() && thumbnailCut);

    const handleVoicePreview = (voiceId, previewUrl) => {
        if (playingVoiceId === voiceId) {
            // Stop current preview
            if (currentAudio) {
                currentAudio.pause();
                currentAudio.currentTime = 0;
                // Properly dispose of audio object
                currentAudio.src = '';
                currentAudio.load();
                setCurrentAudio(null);
            }
            setPlayingVoiceId(null);
            return;
        }

        // Stop any currently playing preview and properly dispose
        if (currentAudio) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
            // Remove event listeners to prevent memory leaks
            currentAudio.onended = null;
            currentAudio.onerror = null;
            // Properly dispose of audio object
            currentAudio.src = '';
            currentAudio.load();
            setCurrentAudio(null);
        }
        setPlayingVoiceId(null);
        
        // Start new preview
        setTimeout(() => {
            setPlayingVoiceId(voiceId);
            const audio = new Audio(previewUrl);
            setCurrentAudio(audio);
            
            audio.play().catch(error => {
                console.error('Error playing voice preview:', error);
                setPlayingVoiceId(null);
                setCurrentAudio(null);
            });
            
            audio.onended = () => {
                setPlayingVoiceId(null);
                setCurrentAudio(null);
            };
            
            audio.onerror = () => {
                console.error('Error playing voice preview:', previewUrl);
                setPlayingVoiceId(null);
                setCurrentAudio(null);
            };
        }, 100);
    };

    const handleMusicPreview = (musicId, musicUrl) => {
        if (playingMusicId === musicId) {
            // Stop current preview
            if (currentMusicAudio) {
                currentMusicAudio.pause();
                currentMusicAudio.currentTime = 0;
                // Properly dispose of audio object
                currentMusicAudio.src = '';
                currentMusicAudio.load();
                setCurrentMusicAudio(null);
            }
            setPlayingMusicId(null);
            return;
        }

        // Stop any currently playing preview and properly dispose
        if (currentMusicAudio) {
            currentMusicAudio.pause();
            currentMusicAudio.currentTime = 0;
            // Remove event listeners to prevent memory leaks
            currentMusicAudio.onended = null;
            currentMusicAudio.onerror = null;
            // Properly dispose of audio object
            currentMusicAudio.src = '';
            currentMusicAudio.load();
            setCurrentMusicAudio(null);
        }
        setPlayingMusicId(null);
        
        // Start new preview
        setTimeout(() => {
            setPlayingMusicId(musicId);
            const audio = new Audio(musicUrl);
            setCurrentMusicAudio(audio);
            
            audio.play().catch(error => {
                console.error('Error playing music preview:', error);
                setPlayingMusicId(null);
                setCurrentMusicAudio(null);
            });
            
            audio.onended = () => {
                setPlayingMusicId(null);
                setCurrentMusicAudio(null);
            };
            
            audio.onerror = () => {
                console.error('Error playing music preview:', musicUrl);
                setPlayingMusicId(null);
                setCurrentMusicAudio(null);
            };
        }, 100);
    };

    const handleToggleMusicStar = async (musicId, e) => {
        if (e) {
            e.stopPropagation();
        }
        
        try {
            const response = await api.toggleMusicStar(currentWorkspace._id, musicId);
            const newIsStarred = response.data.isStarred;
            
            // Update the music item in the list
            setAvailableMusic(prev => prev.map(music => 
                music._id === musicId 
                    ? { ...music, isStarred: newIsStarred }
                    : music
            ));
        } catch (error) {
            console.error('Error toggling music star:', error);
            setErrors(prev => [...prev, 'Failed to update music star status']);
        }
    };

    const refreshAvailableHooks = async () => {
        try {
            const response = await api.getWorkspaceHooks(currentWorkspace._id);
            setAvailableHooks(response.data.hooks || []);
            setHookStats(response.data);
        } catch (error) {
            console.error('Error refreshing hooks:', error);
        }
    };

    const startSimulatedProgress = (currentStep, currentPercent) => {
        // Clear any existing simulated progress timers
        simulatedProgressTimers.forEach(timer => clearTimeout(timer));
        setSimulatedProgressTimers([]);
        
        // Set simulated progress as active (both state and ref)
        setSimulatedProgressActive(true);
        simulatedProgressActiveRef.current = true;
        
        // Lock progress updates for the duration of simulation
        const lockDuration = 6000; // 6 seconds to cover all simulated steps
        progressLockUntilRef.current = Date.now() + lockDuration;
        
        // Update latest backend progress ref
        latestBackendProgressRef.current = {
            percent: currentPercent,
            step: currentStep,
            timestamp: Date.now()
        };

        // Define simulated steps for full progress simulation (since backend only reports one step)
        const simulatedSteps = [
            { percent: 10, step: 'Coming up with a big idea...', delay: 1000 },
            { percent: 25, step: 'Generating a first version', delay: 3000 },
            { percent: 50, step: 'Refining the content...', delay: 5000 },
            { percent: 75, step: 'Generating an alternative version', delay: 8000 },
            { percent: 90, step: 'Finalizing details...', delay: 10000 },
        ];

        // Execute simulated steps
        const newTimers = simulatedSteps.map((step) => {
            return setTimeout(() => {
                setStudioProgress(prev => {
                    // Ensure monotonic progress - never go backwards
                    const shouldUpdate = step.percent >= prev.percent && 
                                       (stepOrder[step.step] || 0) >= (stepOrder[prev.currentStep] || 0);
                    
                    if (shouldUpdate) {
                        return {
                            ...prev,
                            percent: step.percent,
                            currentStep: step.step
                        };
                    }
                    return prev;
                });
            }, step.delay);
        });
        
        // Add a final timer to deactivate simulated progress
        const finalTimer = setTimeout(() => {
            setSimulatedProgressActive(false);
            simulatedProgressActiveRef.current = false;
            progressLockUntilRef.current = 0;
        }, Math.max(...simulatedSteps.map(s => s.delay)) + 1000);
        
        setSimulatedProgressTimers([...newTimers, finalTimer]);
    };

    const CUT_PICKER_PAGE_SIZE = 48;

    const parseScriptsListResponse = (data) => {
        if (Array.isArray(data)) return data;
        return data?.scripts || data?.items || [];
    };

    const scriptsFromGenerationStatus = (statusData, { type, max } = {}) => {
        let list = [];
        if (Array.isArray(statusData?.generatedScripts) && statusData.generatedScripts.length > 0) {
            list = statusData.generatedScripts;
        } else if (Array.isArray(statusData?.scripts) && statusData.scripts.length > 0) {
            list = statusData.scripts;
        }
        if (type) list = list.filter((s) => s.type === type);
        if (max != null && max > 0) return list.slice(0, max);
        return list;
    };

    const fetchWorkspaceScriptsLite = async (params = {}) => {
        if (!currentWorkspace?._id) return [];
        const resp = await api.getWorkspaceScripts(currentWorkspace._id, { lite: true, ...params });
        return parseScriptsListResponse(resp.data);
    };

    const loadWorkspaceCutsForPicker = async (maxPages = 3) => {
        if (!currentWorkspace?._id) return [];
        const pageSize = CUT_PICKER_PAGE_SIZE;
        let all = [];
        for (let page = 1; page <= maxPages; page++) {
            const response = await api.getWorkspaceCuts(currentWorkspace._id, { page, pageSize });
            const raw = response.data;
            const items = Array.isArray(raw) ? raw : (raw?.items || raw?.cuts || []);
            all = all.concat(items);
            const hasMore = raw?.hasMore === true || (items.length >= pageSize && raw?.total > page * pageSize);
            if (!hasMore || items.length === 0) break;
        }
        return all;
    };

    const mergeScriptIntoStudioScripts = (scriptId, script) => {
        if (!script) return;
        const id = String(scriptId);
        setStudioScripts((prev) => {
            const idx = prev.findIndex((p) => String(p._id) === id);
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = { ...next[idx], ...script };
                return next;
            }
            return [script, ...prev];
        });
    };

    const startStudioGenerate = async () => {
        // Manual script generation should not inherit a prior autopilot session.
        if (!studioAutoCreateModeRef.current) {
            exitStudioAutopilotMode();
        }
        
        const hookForGeneration = studioHook?.trim() || '';
        // Track script generation start
        trackEvent('script_generation_started', {
            hook: hookForGeneration || undefined,
            workspaceId: currentWorkspace?._id,
            hookLength: hookForGeneration.length,
            withoutHook: !hookForGeneration,
        });
        
        try {
            setStudioGenerating(true);
            setIsNoScriptMode(false);
            setStudioScripts([]);
            setStudioSelectedScriptId(null);
            setStudioVoiceoverUrl('');
            setStudioVoiceoverScriptContent(null);
            setScriptChangedSinceVoiceover(false);
            lastVoiceoverTimestampsRef.current = null;
            lastVoiceoverUrlForTimestampsRef.current = null;
            setStudioVideoUrl('');
            setEditingScriptContent({}); // Clear any manually entered script content
            setStudioProgress({ current: 0, total: 1, percent: 0, currentStep: 'Starting generation...' });
            
            // Start simulated progress immediately
            startSimulatedProgress('Starting generation...', 0);
            
            await api.generateWorkspaceScriptsAsync(currentWorkspace._id, {
                hooks: getStudioHooksForGeneration(),
                topic: studioTopic.trim() || undefined,
                includePublishedContext: false,
                creativityLevel: 3,
                language: generationLanguage
            });
            
            if (studioPollRef.current) clearInterval(studioPollRef.current);
            let polls = 0;
            studioPollRef.current = setInterval(async () => {
                try {
                    polls += 1;
                    const statusResp = await api.getWorkspaceScriptsStatus(currentWorkspace._id);
                    const status = statusResp.data;
                        
                        if (status.status === 'completed') {
                        clearInterval(studioPollRef.current);
                        studioPollRef.current = null;
                        // Clear simulated progress timers
                        simulatedProgressTimers.forEach(timer => clearTimeout(timer));
                        setSimulatedProgressTimers([]);
                        setSimulatedProgressActive(false);
                        simulatedProgressActiveRef.current = false;
                        progressLockUntilRef.current = 0;
                        // Reset simulated step tracking
                        setLastSimulatedStep('');
                        lastSimulatedStepRef.current = '';
                        let voiceoverOnly = scriptsFromGenerationStatus(status, { type: 'voiceover', max: 5 });
                        if (voiceoverOnly.length === 0) {
                            voiceoverOnly = await fetchWorkspaceScriptsLite({ type: 'voiceover', limit: 5 });
                        }
                        const top1 = voiceoverOnly.slice(0, 1);
                        setStudioScripts(top1);
                        setStudioSelectedScriptId(top1[0]?._id || null);
                            setStudioGenerating(false);
                        setStudioProgress({ current: 1, total: 1, percent: 100 });
                        // Play script completion sound
                        playCompletionSound('script_merra');
                        triggerCompletionGlow(); // keep default (green)
                        
                        // Auto-trigger voiceover if in auto-create mode
                        if (studioAutoCreateModeRef.current && top1[0]?._id) {
                            // Pass the script ID directly to avoid state timing issues
                            const scriptId = top1[0]._id;
                            // Ensure state is set (it should already be, but just in case)
                            if (!studioSelectedScriptId || studioSelectedScriptId !== scriptId) {
                                setStudioSelectedScriptId(scriptId);
                            }
                            // Small delay to ensure state is updated
                            setTimeout(() => {
                                studioGenerateVoiceover(scriptId);
                            }, 500);
                        }
                    } else if (status.status === 'generating') {
                        const total = 1;
                        const current = Math.min(1, status.currentScript || 0);
                        const backendPercent = Math.min(100, status.progress || Math.round((current / total) * 100));
                        const currentStep = status.currentStep || 'Generating...';
                        
                        // Check if this is a new progress update
                        const currentTime = Date.now();
                        const isNewUpdate = currentTime - lastProgressUpdate > 1000; // 1 second threshold
                        
                        // Update latest backend progress ref
                        latestBackendProgressRef.current = {
                            percent: backendPercent,
                            step: currentStep,
                            timestamp: currentTime
                        };
                        
                        if (isNewUpdate) {
                            setLastProgressUpdate(currentTime);
                            
                            // Note: Simulated progress is already running from the start
                            // We only check backend status for completion or errors
                        }
                        
                        // Check if we should accept backend updates
                        const isLocked = currentTime < progressLockUntilRef.current;
                        const isSimulatedActive = simulatedProgressActiveRef.current;
                        
                        // Always allow backend updates to complete, even if simulation is running
                        // This ensures we show completion immediately when the backend finishes
                        const shouldOverride = status.status === 'completed' || 
                                              backendPercent >= 95 ||  // Always show when backend is near completion
                                              !isLocked;              // Always accept backend updates after lock expires
                        
                        if (shouldOverride) {
                            setStudioProgress(prev => {
                                // Ensure monotonic progress - never go backwards
                                const shouldUpdate = backendPercent >= prev.percent && 
                                                   (stepOrder[currentStep] || 0) >= (stepOrder[prev.currentStep] || 0);
                                
                                if (shouldUpdate) {
                                    // If backend completed, clear simulation immediately
                                    if (status.status === 'completed') {
                                        simulatedProgressTimers.forEach(timer => clearTimeout(timer));
                                        setSimulatedProgressTimers([]);
                                        setSimulatedProgressActive(false);
                                        simulatedProgressActiveRef.current = false;
                                        progressLockUntilRef.current = 0;
                                    }
                                    
                                    return {
                                        current, 
                                        total, 
                                        percent: backendPercent,
                                        currentStep: currentStep
                                    };
                                }
                                return prev;
                            });
                        }
                    } else if (status.status === 'failed' || status.status === 'error') {
                        clearInterval(studioPollRef.current);
                        studioPollRef.current = null;
                        // Clear simulated progress timers
                        simulatedProgressTimers.forEach(timer => clearTimeout(timer));
                        setSimulatedProgressTimers([]);
                        setSimulatedProgressActive(false);
                        simulatedProgressActiveRef.current = false;
                        progressLockUntilRef.current = 0;
                        // Reset simulated step tracking
                        setLastSimulatedStep('');
                        lastSimulatedStepRef.current = '';
                        setStudioGenerating(false);
                        // Reset auto-create mode on error
                        exitStudioAutopilotMode();
                        // Show actual error message if available, otherwise generic message
                        const errorMessage = status.error || 'Failed to generate scripts.';
                        setErrors(prev => [...prev, errorMessage]);
                    } else if (status.status === 'none' && polls > 10) {
                        // If status is 'none' after several polls, it might mean the generation failed
                        // but the error status was cleaned up. Check if we were generating.
                        if (studioGenerating) {
                            clearInterval(studioPollRef.current);
                            studioPollRef.current = null;
                            // Clear simulated progress timers
                            simulatedProgressTimers.forEach(timer => clearTimeout(timer));
                            setSimulatedProgressTimers([]);
                            setSimulatedProgressActive(false);
                            simulatedProgressActiveRef.current = false;
                            progressLockUntilRef.current = 0;
                            // Reset simulated step tracking
                            setLastSimulatedStep('');
                            lastSimulatedStepRef.current = '';
                            setStudioGenerating(false);
                            // Reset auto-create mode
                            exitStudioAutopilotMode();
                            setErrors(prev => [...prev, 'Script generation failed. Please try again.']);
                        }
                    } else if (polls >= 120) { // ~4 minutes
                        clearInterval(studioPollRef.current);
                        studioPollRef.current = null;
                        // Clear simulated progress timers
                        simulatedProgressTimers.forEach(timer => clearTimeout(timer));
                        setSimulatedProgressTimers([]);
                        setSimulatedProgressActive(false);
                        simulatedProgressActiveRef.current = false;
                        progressLockUntilRef.current = 0;
                        // Reset simulated step tracking
                        setLastSimulatedStep('');
                        lastSimulatedStepRef.current = '';
                        setStudioGenerating(false);
                        // Reset auto-create mode on timeout
                        exitStudioAutopilotMode();
                        setErrors(prev => [...prev, 'Timed out while generating scripts.']);
                    }
                } catch (_) {}
                }, 2000);
        } catch (e) {
            setStudioGenerating(false);
            exitStudioAutopilotMode();
            setErrors(prev => [...prev, e.response?.data?.error || e.message]);
        }
    };

    const STUDIO_RAW_VIDEOS_PAGE_SIZE = 24;

    const openStudioRawVideosPicker = async () => {
        if (!currentWorkspace?._id) return;
        setShowStudioRawVideosPicker(true);
        setStudioRawPickerLoading(true);
        setStudioRawPickerLoadingMore(false);
        setStudioRawPickerPage(1);
        try {
            const res = await api.getWorkspaceVideos(currentWorkspace._id, { page: 1, pageSize: STUDIO_RAW_VIDEOS_PAGE_SIZE });
            const items = res.data?.items ?? [];
            setStudioRawPickerVideos(items);
            setStudioRawPickerHasMore(Boolean(res.data?.hasMore));
        } catch (_) {
            setStudioRawPickerVideos([]);
            setStudioRawPickerHasMore(false);
        } finally {
            setStudioRawPickerLoading(false);
        }
    };

    const closeStudioRawVideosPicker = () => {
        setShowStudioRawVideosPicker(false);
        if (studioSelectedRawVideoIds.length === 0) {
            setStudioVideoSourceMode('all');
        }
    };

    const loadMoreStudioRawVideos = async () => {
        if (!currentWorkspace?._id || studioRawPickerLoading || studioRawPickerLoadingMore || !studioRawPickerHasMore) return;
        const nextPage = studioRawPickerPage + 1;
        setStudioRawPickerLoadingMore(true);
        try {
            const res = await api.getWorkspaceVideos(currentWorkspace._id, { page: nextPage, pageSize: STUDIO_RAW_VIDEOS_PAGE_SIZE });
            const list = res.data?.items ?? [];
            setStudioRawPickerVideos((prev) => {
                const seen = new Set(prev.map((v) => String(v?._id)));
                const next = [...prev];
                for (const v of list) {
                    const id = String(v?._id || '');
                    if (!id || seen.has(id)) continue;
                    seen.add(id);
                    next.push(v);
                }
                return next;
            });
            setStudioRawPickerPage(nextPage);
            setStudioRawPickerHasMore(Boolean(res.data?.hasMore));
        } catch (_) {
            setStudioRawPickerHasMore(false);
        } finally {
            setStudioRawPickerLoadingMore(false);
        }
    };

    const handleStudioVideoSourceChange = (mode) => {
        setStudioVideoSourceMode(mode);
        if (mode === 'all') {
            setStudioSelectedRawVideoIds([]);
            setShowStudioRawVideosPicker(false);
            return;
        }
        if (mode === 'select') {
            openStudioRawVideosPicker();
            return;
        }
        if (mode === 'import') {
            setActiveTab('raw');
        }
    };

    const studioAutoCreate = async () => {
        // Track auto-create start
        const hookForGeneration = studioHook?.trim() || '';
        trackEvent('auto_create_started', {
            hook: hookForGeneration || undefined,
            workspaceId: currentWorkspace?._id,
            hookLength: hookForGeneration.length,
            withoutHook: !hookForGeneration,
        });
        
        // Enable auto-create mode (both state and ref)
        setStudioAutoCreateMode(true);
        studioAutoCreateModeRef.current = true;
        
        // Start script generation (which will auto-trigger voiceover, then video)
        await startStudioGenerate();
    };

    const studioRegenerateScript = async (scriptId) => {
        try {
            setScriptChangedSinceVoiceover(true); // New script will be generated, so it will differ from current voiceover
            setStudioRegeneratingScript(true);
            setStudioAnimationActive(false);
            setSimulatedProgressActive(false);
            simulatedProgressActiveRef.current = false;
            progressLockUntilRef.current = 0;
            setCurrentStudioExample('');
            setIsLastStudioExample(false);
            
            await api.regenerateWorkspaceScript(currentWorkspace._id, scriptId, {
                topic: studioTopic.trim() || undefined
            });

            pollWorkspaceScriptJob(
                scriptId,
                async (fromStatus) => {
                    try {
                        let updated = fromStatus;
                        if (!updated) {
                            const scriptResp = await api.getWorkspaceScript(currentWorkspace._id, scriptId);
                            updated = scriptResp.data?.script || scriptResp.data;
                        }
                        if (updated) mergeScriptIntoStudioScripts(scriptId, updated);
                    } catch (e) {
                        console.warn('Failed to refresh script after regenerate:', e);
                    }
                    setStudioRegeneratingScript(false);
                    setIsLastStudioExample(true);
                },
                (msg) => {
                    setStudioRegeneratingScript(false);
                    setErrors(prev => [...prev, `Script regeneration failed: ${msg}`]);
                }
            );
        } catch (error) {
            console.error('Error regenerating script:', error);
            setStudioRegeneratingScript(false);
            setErrors(prev => [...prev, 'Failed to regenerate script']);
        }
    };

    const translateScript = async (scriptId) => {
        if (!translationLanguage) {
            setErrors(prev => [...prev, 'Please select a translation language.']);
            return;
        }

        try {
            setIsTranslating(true);
            
            const script = studioScripts.find(s => s._id === scriptId);
            if (!script) {
                setErrors(prev => [...prev, 'Script not found.']);
                return;
            }

            // For custom scripts, get the current editing content
            const currentContent = getScriptContent(script, 'main');
            const currentContentAlt = getScriptContent(script, 'alt');
            const currentContentThird = getScriptContent(script, 'third');
            
            // Start async translation
            const response = await api.translateWorkspaceScript(currentWorkspace._id, scriptId, {
                language: translationLanguage,
                content: currentContent,
                contentAlt: currentContentAlt,
                contentThird: currentContentThird
            });

            if (response.data.success) {
                const translationId = response.data.translationId;
                
                // Poll for translation completion
                const pollInterval = setInterval(async () => {
                    try {
                        const statusResponse = await api.getWorkspaceTranslationStatus(currentWorkspace._id, translationId);
                        const status = statusResponse.data;
                        
                        if (status.status === 'completed') {
                            clearInterval(pollInterval);
                            setIsTranslating(false);
                            
                            // Update the script in local state
                            setStudioScripts(prev => prev.map(s => 
                                s._id === scriptId 
                                    ? { 
                                        ...s, 
                                        content: status.result.content,
                                        contentAlt: status.result.contentAlt,
                                        contentThird: status.result.contentThird
                                    }
                                    : s
                            ));
                            
                            // For custom scripts, also update the editing state so the UI reflects the translation
                            if (script.isCustom) {
                                setEditingScriptContent(prev => ({
                                    ...prev,
                                    [scriptId]: {
                                        ...prev[scriptId],
                                        main: status.result.content,
                                        alt: status.result.contentAlt,
                                        third: status.result.contentThird
                                    }
                                }));
                            }
                            
                            setModalSuccessMessage('Script translated successfully!');
                            setShowSuccessModal(true);
                        } else if (status.status === 'error') {
                            clearInterval(pollInterval);
                            setIsTranslating(false);
                            setModalErrorMessage(status.error || 'Translation failed.');
                            setShowErrorModal(true);
                        }
                    } catch (error) {
                        console.error('Error polling translation status:', error);
                        clearInterval(pollInterval);
                        setIsTranslating(false);
                        setModalErrorMessage('Failed to check translation status.');
                        setShowErrorModal(true);
                    }
                }, 1000); // Poll every second
                
            } else {
                setIsTranslating(false);
                setModalErrorMessage('Failed to start translation.');
                setShowErrorModal(true);
            }
        } catch (error) {
            console.error('Error translating script:', error);
            setIsTranslating(false);
            setModalErrorMessage(error.response?.data?.error || 'Failed to translate script.');
            setShowErrorModal(true);
        }
    };

    const studioGenerateVoiceover = async (scriptIdOverride = null, autoGenerateVideo = false) => {
        // If scriptIdOverride is an event object (from onClick), ignore it and use null
        let actualScriptIdOverride = scriptIdOverride;
        if (scriptIdOverride && typeof scriptIdOverride === 'object' && scriptIdOverride.nativeEvent) {
            // This is a React SyntheticEvent, not a script ID
            actualScriptIdOverride = null;
        }
        
        // Set the auto-generate video flag if requested
        if (autoGenerateVideo) {
            autoGenerateVideoAfterVoiceoverRef.current = true;
        } else if (!studioAutoCreateModeRef.current) {
            exitStudioAutopilotMode();
        }
        
        let scriptIdToUse = actualScriptIdOverride || studioSelectedScriptId;
        
        // Ensure scriptIdToUse is a string (extract _id if it's an object)
        if (scriptIdToUse) {
            if (typeof scriptIdToUse === 'object' && scriptIdToUse !== null && !scriptIdToUse.nativeEvent) {
                // If it's an object (but not an event), extract the _id property
                scriptIdToUse = scriptIdToUse._id || scriptIdToUse.id || null;
            }
            // Convert to string if it's not already (handles numbers, etc.)
            if (scriptIdToUse && typeof scriptIdToUse !== 'string') {
                scriptIdToUse = String(scriptIdToUse);
            }
        }
        
        // Final validation - must be a non-empty string
        if (!scriptIdToUse || scriptIdToUse === 'null' || scriptIdToUse === 'undefined' || (typeof scriptIdToUse === 'string' && scriptIdToUse.trim() === '')) {
            console.warn('studioGenerateVoiceover: No valid script ID provided', { 
                scriptIdOverride, 
                actualScriptIdOverride,
                studioSelectedScriptId, 
                scriptIdToUse,
                scriptIdType: typeof scriptIdToUse
            });
            setErrors(prev => [...prev, 'Please select a script to generate voiceover.']);
            return;
        }
        
        // Track voiceover generation start
        trackEvent('voiceover_generation_started', {
            scriptId: scriptIdToUse,
            voiceId: selectedVoiceId,
            workspaceId: currentWorkspace?._id,
            isCustomScript: studioScripts.find(s => s._id === scriptIdToUse)?.isCustom || false
        });
        
        const isVoiceoverRegeneration = !!studioVoiceoverUrl;
        try {
            setStudioGeneratingVoice(true);
            shouldAutoplayStudioVoiceoverRef.current = isVoiceoverRegeneration;
            studioVoiceoverCancelledRef.current = false; // Reset cancellation flag
            
            // Scroll to voiceover section when generation starts (only during auto-generation)
            if ((studioAutoCreateModeRef.current || autoGenerateVideoAfterVoiceoverRef.current) && voiceoverSectionRef.current) {
                setTimeout(() => {
                    voiceoverSectionRef.current?.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'start',
                        inline: 'nearest'
                    });
                }, 100);
            }
            
            // Find the selected script - use the current selected script ID, not the override
            // This ensures we get the script with the updated ID after auto-generation
            const actualSelectedScriptId = studioSelectedScriptId;
            const selectedScript = studioScripts.find(s => s._id === actualSelectedScriptId) || studioScripts.find(s => s._id === scriptIdToUse);
            
            const requestData = {
                voiceId: selectedVoiceId,
                voiceGenerationMode: voiceGenerationMode // Pass voice generation mode (stable/expressive)
            };
            
            // Always get the current content (including any edits) using getScriptContent
            // This ensures we use the latest edited content, not just what's in the database
            let scriptContentForVoiceover = null;
            if (selectedScript) {
                // Use getScriptContent which already handles checking multiple IDs
                const currentContent = getScriptContent(selectedScript, 'main');
                const currentContentAlt = getScriptContent(selectedScript, 'alt');
                const currentContentThird = getScriptContent(selectedScript, 'third');
                
                // Store the script content that will be used for voiceover generation
                scriptContentForVoiceover = {
                    main: currentContent,
                    alt: currentContentAlt,
                    third: currentContentThird,
                    scriptId: actualSelectedScriptId
                };
                
                // For custom scripts, always include the content so it can be saved to database
                // For non-custom scripts, include content if there are edits
                // ALWAYS send content to ensure backend uses the latest version
                requestData.content = currentContent;
                requestData.contentAlt = currentContentAlt;
                requestData.contentThird = currentContentThird;
                if (selectedScript.isCustom) {
                    requestData.title = selectedScript.title;
                }
            }
            
            // Use the actual selected script ID (which may have been updated after auto-generation)
            // instead of scriptIdToUse which might be stale
            const actualScriptIdForApi = selectedScript?._id || scriptIdToUse;
            const response = await api.generateWorkspaceVoiceover(currentWorkspace._id, actualScriptIdForApi, requestData);
            
            // If this was a custom script, update the local state with the new script ID
            if (selectedScript && selectedScript.isCustom && response.data.script) {
                const newScriptId = response.data.script._id;
                const oldScriptId = actualScriptIdForApi; // Use the ID we actually sent to the API
                
                // Migrate editingScriptContent from old ID to new ID
                // Use functional update to ensure we have the latest state
                setEditingScriptContent(prev => {
                    const newEditing = { ...prev };
                    if (prev[oldScriptId]) {
                        newEditing[newScriptId] = { ...prev[oldScriptId] };
                        delete newEditing[oldScriptId];
                    }
                    return newEditing;
                });
                
                // Migrate editDebounceRef from old ID to new ID
                Object.keys(editDebounceRef.current).forEach(key => {
                    const [scriptId, version] = key.split('-');
                    if (scriptId === oldScriptId) {
                        const newKey = `${newScriptId}-${version}`;
                        editDebounceRef.current[newKey] = editDebounceRef.current[key];
                        delete editDebounceRef.current[key];
                    }
                });
                
                // Update the script in local state with the real ID
                setStudioScripts(prev => prev.map(script => {
                    if (script._id === oldScriptId) {
                        const updatedScript = { ...script, _id: newScriptId, isCustom: false };
                        console.log('[VOICEOVER] Migrated script ID:', {
                            oldId: oldScriptId,
                            newId: newScriptId,
                            scriptContent: updatedScript.content?.substring(0, 50)
                        });
                        return updatedScript;
                    }
                    return script;
                }));
                setStudioSelectedScriptId(newScriptId);
            }
            
            if (response.data.voiceoverUrl) {
                // Check if cancelled before processing the response
                if (studioVoiceoverCancelledRef.current) {
                    // Don't set URL or play sounds if cancelled
                    return;
                }
                
                setStudioVoiceoverUrl(response.data.voiceoverUrl);
                setScriptChangedSinceVoiceover(false); // Voiceover just generated, script and voiceover are in sync
                // Store the script content that was used for this voiceover
                // Use the new script ID if the script was just saved, otherwise use the actual script ID
                const finalScriptId = response.data.script?._id || actualScriptIdForApi;
                if (scriptContentForVoiceover) {
                    setStudioVoiceoverScriptContent({
                        ...scriptContentForVoiceover,
                        scriptId: finalScriptId
                    });
                }
                // Update script in studioScripts and refs with new timestamps so Generate Video uses
                // timestamps that match the new voiceover (not stale ones)
                const newTimestamps = response.data.voiceoverTimestamps;
                const newVoiceoverUrl = response.data.voiceoverUrl;
                if (newTimestamps && typeof newTimestamps === 'object' && !Array.isArray(newTimestamps) && newVoiceoverUrl) {
                    lastVoiceoverTimestampsRef.current = newTimestamps;
                    lastVoiceoverUrlForTimestampsRef.current = newVoiceoverUrl;
                    setStudioScripts(prev => prev.map(s =>
                        s._id === finalScriptId ? { ...s, voiceoverUrl: newVoiceoverUrl, voiceoverTimestamps: newTimestamps } : s
                    ));
                }
                
                // Get voiceover duration for warning message
                const audio = new Audio(response.data.voiceoverUrl);
                audio.addEventListener('loadedmetadata', () => {
                    // Check again before setting duration (in case cancelled during async operation)
                    if (!studioVoiceoverCancelledRef.current) {
                        setVoiceoverDuration(audio.duration);
                    }
                });
                
                // Check one more time before playing completion effects
                if (!studioVoiceoverCancelledRef.current) {
                    // Play voiceover completion sound
                    playCompletionSound('voice_merra');
                    triggerCompletionGlow(['#667eea', '#764ba2']);
                    
                    // Auto-trigger video generation if in auto-create mode or auto-generate video mode
                    if (studioAutoCreateModeRef.current || autoGenerateVideoAfterVoiceoverRef.current) {
                        // Pass the voiceover URL and timestamps directly to avoid state timing issues
                        const voiceoverUrl = response.data.voiceoverUrl;
                        const voiceoverTimestamps = response.data.voiceoverTimestamps;
                        // Use the new script ID if the script was just saved, otherwise use the actual script ID
                        const currentScriptId = response.data.script?._id || actualScriptIdForApi;
                        // Ensure state is updated (it should already be, but just in case)
                        if (!studioVoiceoverUrl || studioVoiceoverUrl !== voiceoverUrl) {
                            setStudioVoiceoverUrl(voiceoverUrl);
                        }
                        // Small delay to ensure state is updated
                        setTimeout(() => {
                            studioGenerateVideo(currentScriptId, voiceoverUrl, voiceoverTimestamps);
                        }, 500);
                    }
                }
            }
            } catch (error) {
            console.error('Error generating voiceover:', error);
            
            exitStudioAutopilotMode();
            shouldAutoplayStudioVoiceoverRef.current = false;
            
            // Check if it's a voice expiration error (404 from ElevenLabs)
            if (error.response?.status === 500 && error.response?.data?.error?.includes('voice_not_found')) {
                setErrors(prev => [...prev, 'Your voice has expired and needs to be regenerated. Please go to the Profile tab to clone your voice again.']);
            } else {
                setErrors(prev => [...prev, 'Failed to generate voiceover']);
            }
        } finally {
            setStudioGeneratingVoice(false);
        }
    };

    const studioGenerateVideo = async (scriptIdOverride = null, voiceoverUrlOverride = null, voiceoverTimestampsOverride = null, orderedCutIdsOverride = null, forceFillWithTextScreens = false, forceProceedWithRepeatedCuts = false, exportExtras = null) => {
        // If scriptIdOverride is an event object (from onClick), ignore it and use null
        let actualScriptIdOverride = scriptIdOverride;
        if (scriptIdOverride && typeof scriptIdOverride === 'object' && scriptIdOverride.nativeEvent) {
            // This is a React SyntheticEvent, not a script ID
            actualScriptIdOverride = null;
        }
        
        // If voiceoverUrlOverride is an event object (from onClick), ignore it and use null
        let actualVoiceoverUrlOverride = voiceoverUrlOverride;
        if (voiceoverUrlOverride && typeof voiceoverUrlOverride === 'object' && voiceoverUrlOverride.nativeEvent) {
            // This is a React SyntheticEvent, not a voiceover URL
            actualVoiceoverUrlOverride = null;
        }
        
        const scriptIdToUse = actualScriptIdOverride || studioSelectedScriptId;
        const voiceoverUrlToUse = actualVoiceoverUrlOverride || studioVoiceoverUrl;
        // Use override if provided; otherwise script's stored timestamps; else last-used ref (so subtitles persist on regenerate)
        const scriptForTimestamps = studioScripts.find(s => s._id === scriptIdToUse);
        // Use ref only when URL matches (voiceover was just regenerated); else use script to avoid
        // wrong timestamps when switching scripts
        const refMatchesCurrentVoiceover = lastVoiceoverUrlForTimestampsRef.current && lastVoiceoverUrlForTimestampsRef.current === voiceoverUrlToUse;
        const voiceoverTimestampsToUse = voiceoverTimestampsOverride !== undefined && voiceoverTimestampsOverride !== null
            ? voiceoverTimestampsOverride
            : (refMatchesCurrentVoiceover ? lastVoiceoverTimestampsRef.current : null) ?? scriptForTimestamps?.voiceoverTimestamps ?? lastVoiceoverTimestampsRef.current ?? undefined;
        const orderedCutIdsToUse = Array.isArray(orderedCutIdsOverride) ? orderedCutIdsOverride : null;
        if (voiceoverTimestampsToUse && typeof voiceoverTimestampsToUse === 'object' && !Array.isArray(voiceoverTimestampsToUse)) {
            lastVoiceoverTimestampsRef.current = voiceoverTimestampsToUse;
            lastVoiceoverUrlForTimestampsRef.current = voiceoverUrlToUse; // Keep ref in sync
        }
        
        if (!scriptIdToUse || !voiceoverUrlToUse) return;

        const hasSelectedSourceVideos =
            (studioVideoSourceMode === 'select' || studioVideoSourceMode === 'import') &&
            Array.isArray(studioSelectedRawVideoIds) &&
            studioSelectedRawVideoIds.length > 0;
        if (studioVideoSourceMode === 'select' && !hasSelectedSourceVideos) {
            setErrors(prev => [...prev, 'Choose “Select” and pick at least one raw video, or switch to “All videos”.']);
            return;
        }
        if (rawTotal === 0 && !hasSelectedSourceVideos) {
            setErrors(prev => [...prev, 'No source videos yet. Upload at least one video in Raw Videos before generating.']);
            return;
        }

        const isAutopilotVideoRun =
            studioAutoCreateModeRef.current ||
            autoGenerateVideoAfterVoiceoverRef.current ||
            (voiceoverUrlOverride !== null && voiceoverUrlOverride !== undefined);
        if (!isAutopilotVideoRun) {
            exitStudioAutopilotMode();
        }

        // Upfront check: not enough cuts?
        // Skip when an explicit ordered timeline is sent (e.g. "Regenerate with this timeline"): that path already
        // validated totals in the modal; VO/slot cut-count math can disagree (raw vs effective seconds, fewer long clips).
        const hasExplicitOrderedTimeline = orderedCutIdsToUse && orderedCutIdsToUse.length > 0;
        if (!forceFillWithTextScreens && !forceProceedWithRepeatedCuts && !hasExplicitOrderedTimeline && voiceoverTimestampsToUse?.character_end_times_seconds?.length > 0) {
            const voiceoverDurationSec = Math.max(...voiceoverTimestampsToUse.character_end_times_seconds);
            // Regular pacing: 2s per cut; fast pacing: 1s per cut. Avoid triggering modal when user has enough 2s cuts.
            const effectiveSecondsPerCut = fastPacing ? 1 : 2;
            const effectiveDurationPerCut = slowMotion ? effectiveSecondsPerCut * (4 / 3) : effectiveSecondsPerCut;
            const requiredCuts = Math.ceil(voiceoverDurationSec / effectiveDurationPerCut);
            const availableCutsCount = (includeTags.length > 0 || excludeTags.length > 0) ? (matchingCutsCount ?? 0) : cutsTotal;
            if (availableCutsCount < requiredCuts) {
                // Always proceed with repeated cuts method.
                studioGenerateVideo(actualScriptIdOverride, actualVoiceoverUrlOverride, voiceoverTimestampsOverride, orderedCutIdsOverride, false, true);
                return;
            }
        }
        
        // Ensure cuts are fresh before generating video
        // await fetchWorkspaceCuts();
        
        // Track video generation start
        trackEvent('video_generation_started', {
            scriptId: scriptIdToUse,
            voiceId: selectedVoiceId,
            musicId: selectedMusicId,
            fontId: selectedFontId,
            workspaceId: currentWorkspace?._id,
            isCustomScript: studioScripts.find(s => s._id === scriptIdToUse)?.isCustom || false
        });
        
        let generatePayload = null;
        try {
            console.log(`[VIDEO] Starting video generation for script: ${scriptIdToUse}`);
            setStudioGeneratingVideo(true);
            setStudioVideoSaved(false);
            // Effective SFX mode: must match the payload below (Boukar-only; planning skipped when the export carries cues).
            const hasExportSfxCues = !!(exportExtras || newNleExportExtrasRef.current)?.sfxCues?.length;
            const planAiSfx = currentUserId === BOUKAR_USER_ID && aiSoundEffects === true && !hasExportSfxCues;
            setStudioSfxStepsActive(planAiSfx || (currentUserId === BOUKAR_USER_ID && hasExportSfxCues));
            // 2 = backend is planning SFX; 5 = Lambda start.
            setStudioVideoProgress(planAiSfx ? 2 : 5);
            setStudioVideoUrl(''); // Clear video URL immediately to prevent showing old video
            setStudioVideoUrlTimestamp(0); // Reset timestamp
            lastStudioVideoCompletionUrlRef.current = null;
            // Reset video played flag for new video
            studioVideoHasPlayedRef.current = false;
            
            // Scroll to video section when generation starts (only during auto-generation)
            // Check if voiceoverUrlOverride is provided (indicates auto-trigger) or if in auto-create mode
            const isAutoGenerated = studioAutoCreateModeRef.current || (voiceoverUrlOverride !== null && voiceoverUrlOverride !== undefined);
            if (isAutoGenerated && videoSectionRef.current) {
                setTimeout(() => {
                    videoSectionRef.current?.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'start',
                        inline: 'nearest'
                    });
                }, 100);
            }
            
            prepareCarouselCuts();
            
            // Check if this is a custom script that needs to be saved to database
            let actualScriptId = scriptIdToUse;
            const selectedScript = studioScripts.find(s => s._id === scriptIdToUse);
            if (selectedScript && selectedScript.isCustom) {
                console.log(`[VIDEO] Saving custom script to database before video generation`);
                
                // Get the current content (including any edits) using getScriptContent
                const currentContent = getScriptContent(selectedScript, 'main');
                const currentContentAlt = getScriptContent(selectedScript, 'alt');
                const currentContentThird = getScriptContent(selectedScript, 'third');
                
                const saveResponse = await api.saveCustomScript(currentWorkspace._id, {
                    content: currentContent,
                    contentAlt: currentContentAlt,
                    contentThird: currentContentThird,
                    title: selectedScript.title
                });
                actualScriptId = saveResponse.data.script._id;
                const oldScriptId = scriptIdToUse;
                
                // Migrate editingScriptContent from old ID to new ID
                if (editingScriptContent[oldScriptId]) {
                    setEditingScriptContent(prev => {
                        const newEditing = { ...prev };
                        newEditing[actualScriptId] = { ...newEditing[oldScriptId] };
                        delete newEditing[oldScriptId];
                        return newEditing;
                    });
                }
                
                // Migrate editDebounceRef from old ID to new ID
                Object.keys(editDebounceRef.current).forEach(key => {
                    const [scriptId, version] = key.split('-');
                    if (scriptId === oldScriptId) {
                        const newKey = `${actualScriptId}-${version}`;
                        editDebounceRef.current[newKey] = editDebounceRef.current[key];
                        delete editDebounceRef.current[key];
                    }
                });
                
                // Update the script in local state with the real ID
                setStudioScripts(prev => prev.map(script => 
                    script._id === oldScriptId 
                        ? { ...script, _id: actualScriptId, isCustom: false }
                        : script
                ));
                setStudioSelectedScriptId(actualScriptId);
            }
            
            // Determine music selection
            const extras = exportExtras || newNleExportExtrasRef.current || null;
            if (newNleExportExtrasRef.current) newNleExportExtrasRef.current = null;
            let musicIdToUse = null;
            let musicNameToUse = null;
            const useMusicForExport = extras && Object.prototype.hasOwnProperty.call(extras, 'selectedMusicId')
                ? !!extras.selectedMusicId
                : useMusic;
            if (useMusicForExport) {
                if (extras?.selectedMusicId) {
                    musicIdToUse = extras.selectedMusicId;
                    const selectedMusic = availableMusic.find(m => m._id === extras.selectedMusicId);
                    musicNameToUse = selectedMusic?.name || null;
                } else if (selectedMusicId) {
                    // Explicit dropdown selection takes priority
                    musicIdToUse = selectedMusicId;
                    const selectedMusic = availableMusic.find(m => m._id === selectedMusicId);
                    musicNameToUse = selectedMusic?.name || null;
                } else if (musicRegenerationMode === 'keep' && lastUsedMusicId) {
                    // Keep same song from previous generation
                    musicIdToUse = lastUsedMusicId;
                    musicNameToUse = lastUsedMusicName;
                } else if (availableMusic.length > 0) {
                    // Random music selection
                    const randomIndex = Math.floor(Math.random() * availableMusic.length);
                    musicIdToUse = availableMusic[randomIndex]._id;
                    musicNameToUse = availableMusic[randomIndex].name;
                }
            }
            
            // Store the music that will be used for next regeneration
            if (musicIdToUse) {
                setLastUsedMusicId(musicIdToUse);
                if (musicNameToUse) {
                    setLastUsedMusicName(musicNameToUse);
                }
            }
            
            // Send script content that matches the voiceover (including edits) to avoid voice/subtitle mismatch
            const scriptForContent = studioScripts.find(s => s._id === actualScriptId) || selectedScript;
            const contentVersion = scriptForContent?.selectedVersion || 'main';
            const scriptContentForVideo = scriptForContent ? getScriptContent(scriptForContent, contentVersion) : '';
            const generatePayload = {
                scriptContent: scriptContentForVideo || undefined,
                voiceoverUrl: voiceoverUrlToUse,
                voiceoverTimestamps: voiceoverTimestampsToUse,
                selectedMusicId: musicIdToUse,
                fontId: extras && Object.prototype.hasOwnProperty.call(extras, 'fontId')
                    ? (extras.fontId || undefined)
                    : selectedFontId,
                includeTags: includeTags.length > 0 ? includeTags.join(',') : undefined,
                excludeTags: excludeTags.length > 0 ? excludeTags.join(',') : undefined,
                slowMotion: slowMotion,
                cutSilenceMode: currentUserId === BOUKAR_USER_ID
                    ? (extras?.cutSilenceMode !== undefined ? !!extras.cutSilenceMode : cutSilenceMode)
                    : false,
                cutMoreSilenceMode: currentUserId === BOUKAR_USER_ID && cutMoreSilenceMode,
                aiSoundEffects: planAiSfx,
                speedUp: currentUserId === BOUKAR_USER_ID && speedUp,
                fastPacing: extras?.fastPacing !== undefined ? !!extras.fastPacing : fastPacing,
                bigText: extras?.bigText !== undefined ? !!extras.bigText : bigText,
                zoomWhoosh: zoomWhoosh,
                flipbookIntro: extras?.flipbookIntro !== undefined ? !!extras.flipbookIntro : flipbookIntro,
                openingLineInCenter: currentUserId === BOUKAR_USER_ID
                    ? (extras?.openingLineInCenter !== undefined ? !!extras.openingLineInCenter : openingLineInCenter)
                    : false,
                openingLineTextSize: extras?.openingLineTextSize || openingLineTextSize,
                ...(() => {
                    const isBoukar = currentUserId === BOUKAR_USER_ID;
                    const titleOn = isBoukar && (extras?.openingLineInCenter !== undefined
                        ? !!extras.openingLineInCenter
                        : openingLineInCenter);
                    const mix = isBoukar && (extras?.fontMixing !== undefined ? !!extras.fontMixing : fontMixing);
                    const stack = isBoukar && (extras?.centerStackOpening !== undefined ? !!extras.centerStackOpening : centerStackOpening);
                    const stackLine = isBoukar && (extras?.centerStackLineByLine !== undefined ? !!extras.centerStackLineByLine : centerStackLineByLine);
                    if (!titleOn && !mix && !stack && !stackLine) return {};
                    const fontId = (extras && Object.prototype.hasOwnProperty.call(extras, 'openingLineFontId')
                        ? extras.openingLineFontId
                        : openingLineFontId)
                        || ((mix || stack || stackLine)
                            ? (extras && Object.prototype.hasOwnProperty.call(extras, 'fontId') ? extras.fontId : selectedFontId)
                            : '');
                    if (!fontId) return {};
                    const font = userFonts.find((f) => String(f._id) === String(fontId));
                    return {
                        openingLineFontId: fontId,
                        openingLineCustomFontUrl: font?.url || undefined,
                    };
                })(),
                openingLineFontColor: (() => {
                    const isBoukar = currentUserId === BOUKAR_USER_ID;
                    const titleOn = isBoukar && (extras?.openingLineInCenter !== undefined ? !!extras.openingLineInCenter : openingLineInCenter);
                    const color = extras?.openingLineFontColor || openingLineFontColor;
                    return titleOn && color ? color : undefined;
                })(),
                glowText: extras?.glowText !== undefined ? !!extras.glowText : glowText,
                glowTextColor: glowTextColor,
                subtitlePosition: extras?.subtitlePosition || subtitlePosition,
                enableSubtitles: extras?.enableSubtitles !== undefined ? !!extras.enableSubtitles : enableSubtitles,
                oneWordPerSubtitle: extras?.oneWordPerSubtitle !== undefined ? !!extras.oneWordPerSubtitle : oneWordPerSubtitle,
                fontMixing: currentUserId === BOUKAR_USER_ID
                    ? (extras?.fontMixing !== undefined ? !!extras.fontMixing : fontMixing)
                    : false,
                centerStackOpening: currentUserId === BOUKAR_USER_ID
                    ? (extras?.centerStackOpening !== undefined ? !!extras.centerStackOpening : centerStackOpening)
                    : false,
                centerStackLineByLine: currentUserId === BOUKAR_USER_ID
                    ? (extras?.centerStackLineByLine !== undefined ? !!extras.centerStackLineByLine : centerStackLineByLine)
                    : false,
                orderedCutIds: orderedCutIdsToUse && orderedCutIdsToUse.length > 0 ? orderedCutIdsToUse : undefined,
                selectedVideoIds: studioVideoSourceMode === 'select' && studioSelectedRawVideoIds.length > 0 ? studioSelectedRawVideoIds : undefined,
                boukisIntro: boukisIntro || undefined,
                boukisIntroPitch: boukisIntro ? boukisIntroPitch : undefined,
                thumbnailConfig: (thumbnailTitle.trim() && thumbnailCut) ? buildThumbnailConfigPayload(thumbnailCut, thumbnailTitle, thumbnailBigText) : undefined,
                subtitleFontColor: extras?.subtitleFontColor || subtitleFontColor,
                subtitleOutlineColor: extras?.subtitleOutlineColor || subtitleOutlineColor,
                subtitleOutlineEnabled: extras?.subtitleOutlineEnabled !== undefined
                    ? !!extras.subtitleOutlineEnabled
                    : subtitleOutlineEnabled,
            };
            if (extras?.sfxCues?.length) generatePayload.sfxCues = extras.sfxCues;
            if (forceFillWithTextScreens) generatePayload.fillWithTextScreens = true;
            const response = await api.generateWorkspaceVideo(currentWorkspace._id, actualScriptId, generatePayload);
            console.log(`[VIDEO] Generation started successfully:`, response.data);
            
            // Capture actualScriptId for use in polling closure
            const scriptIdForPolling = actualScriptId;
            
            const start = Date.now();
            const TIMEOUT_MS = 10 * 60 * 1000;
            if (studioVideoPollRef.current) clearInterval(studioVideoPollRef.current);
            studioVideoPollRef.current = setInterval(async () => {
                try {
                    if (Date.now() - start > TIMEOUT_MS) {
                        clearInterval(studioVideoPollRef.current);
                        studioVideoPollRef.current = null;
                        setStudioVideoProgress(0);
                        setStudioGeneratingVideo(false);
                        exitStudioAutopilotMode();
                        setErrors(prev => [...prev, 'Video generation timed out.']);
                        return;
                    }
                    const statusResp = await api.getWorkspaceVideoStatus(currentWorkspace._id, scriptIdForPolling);
                    const { status, progress, videoUrl } = statusResp.data;
                    console.log(`[VIDEO] Status check: ${status}, progress: ${progress}%, videoUrl: ${videoUrl ? 'exists' : 'none'}`);
                    setStudioVideoProgress(progress || 0);
                    if ((status === 'draft' && videoUrl) || status === 'error') {
                        clearInterval(studioVideoPollRef.current);
                        studioVideoPollRef.current = null;
                        if (status === 'draft' && videoUrl) {
                            const wasAlreadyHandled = lastStudioVideoCompletionUrlRef.current === videoUrl;
                            console.log(`[VIDEO] Generation complete! Setting video URL: ${videoUrl} (dedup=${wasAlreadyHandled})`);
                            setStudioVideoUrl(videoUrl);
                            setStudioVideoProgress(0);
                            setStudioGeneratingVideo(false);
                            exitStudioAutopilotMode();
                            if (!wasAlreadyHandled) {
                                lastStudioVideoCompletionUrlRef.current = videoUrl;
                                setStudioVideoUrlTimestamp(Date.now()); // Force video reload only once
                                setMusicSelectionChanged(false);
                                if (lastUsedMusicId && !selectedMusicId) { /* keep dropdown as-is */ }
                                playCompletionSound('video_merra');
                                triggerCompletionGlow(['#f093fb', '#f5576c']);
                            }
                            // Refetch single script in background for usedCutIds (timeline) — avoid full workspace scripts payload
                            api.getWorkspaceScript(currentWorkspace._id, scriptIdForPolling).then((resp) => {
                                const updated = resp.data?.script || resp.data;
                                if (updated) mergeScriptIntoStudioScripts(scriptIdForPolling, updated);
                            }).catch(e => console.warn('Failed to refetch script after video generation:', e));
                        } else if (status === 'error') {
                            console.log(`[VIDEO] Generation failed with error status`);
                            setStudioVideoProgress(0);
                            exitStudioAutopilotMode();
                        }
                        setStudioGeneratingVideo(false);
                    }
                } catch (error) {
                    console.error('[VIDEO] Error checking video status:', error);
                }
            }, 2000);
        } catch (error) {
            setStudioVideoProgress(0);
            setStudioGeneratingVideo(false);
            exitStudioAutopilotMode();
            console.error('Error generating video:', error);
            setErrors(prev => [...prev, 'Failed to start video generation: ' + (error.response?.data?.error || error.message)]);
        }
    };

    const confirmInsufficientCutsOptimizeAndFill = () => {
        setShowInsufficientCutsModal(false);
        studioGenerateVideo(null, null, null, null, true, false);
    };
    const confirmInsufficientCutsRepeatedCuts = () => {
        setShowInsufficientCutsModal(false);
        studioGenerateVideo(null, null, null, null, false, true);
    };

    const TIMELINE_CUTS_PAGE_SIZE = 33;

    const NEW_NLE_CUTS_PAGE_SIZE = 33;

    useEffect(() => {
        if (useNewNle) preloadHlsEngine();
    }, [useNewNle]);

    // After VO (and when cut generation finishes), warm HLS/posters/VO so the
    // editor is not cold on first open.
    useEffect(() => {
        if (!useNewNle) return undefined;
        if (!currentWorkspace?._id || !studioVoiceoverUrl) return undefined;
        if (showNewNleEditor) return undefined;
        if (generatingCuts && generatingCuts.size > 0) return undefined;

        const workspaceId = currentWorkspace._id;
        const script = studioScripts.find((s) => String(s._id) === String(studioSelectedScriptId));
        const usedIds = (script?.usedCutIds || []).map(String).filter(Boolean);
        const voSec = voiceoverDuration
            || getVoiceoverSecondsFromTimestamps(script?.voiceoverTimestamps)
            || getVoiceoverSecondsFromTimestamps(lastVoiceoverTimestampsRef.current);
        const tagKey = timelinePickerTagsKey(includeTags, excludeTags);
        const warmKey = `${workspaceId}|${studioVoiceoverUrl}|${usedIds.join(',')}|${fastPacing ? 1 : 0}|${flipbookIntro ? 1 : 0}|${tagKey}|${voSec}`;
        if (nleWarmKeyRef.current === warmKey) return undefined;
        nleWarmKeyRef.current = warmKey;

        let cancelled = false;
        (async () => {
            try {
                preloadHlsEngine();
                prefetchVoiceover(studioVoiceoverUrl);

                let available = Array.isArray(newNleAvailableCuts) ? newNleAvailableCuts : [];
                if (!available.length) {
                    const pickResp = await api.getWorkspaceCuts(workspaceId, {
                        page: 1,
                        pageSize: NEW_NLE_CUTS_PAGE_SIZE,
                        ...timelinePickerCutsQuery(includeTags, excludeTags),
                    });
                    if (cancelled) return;
                    const pickRaw = pickResp.data;
                    const pickItems = Array.isArray(pickRaw) ? pickRaw : (pickRaw?.items || []);
                    available = dedupeCutsById(pickItems.filter((c) => !c.hidden));
                    setNewNleAvailableCuts(available);
                    setNewNleCutsPage(1);
                    setNewNleHasMoreCuts(pickRaw?.hasMore ?? (pickItems.length >= NEW_NLE_CUTS_PAGE_SIZE));
                    newNlePickerTagKeyRef.current = tagKey;
                }

                let ordered = [];
                if (usedIds.length) {
                    const response = await api.getWorkspaceCuts(workspaceId, { ids: usedIds.join(',') });
                    if (cancelled) return;
                    const raw = response.data;
                    const items = Array.isArray(raw) ? raw : (raw?.items || []);
                    const found = new Map(items.filter((c) => !c.hidden).map((c) => [String(c._id), c]));
                    ordered = usedIds.map((id) => found.get(id)).filter(Boolean);
                    setNewNleCuts(ordered);
                }
                // Do not seed from available pool here — that races with usedCutIds and
                // shows latest cuts instead of the Lambda timeline.

                prefetchCutPreviewAssets(ordered.length ? ordered : available.slice(0, 16));
                const ids = ordered.map((c) => String(c?._id || c?.id || '')).filter(Boolean);
                if (ids.length) {
                    await warmNleHlsPlaylist({
                        workspaceId,
                        orderedCutIds: ids,
                        fastPacing: !!fastPacing,
                        flipbookIntro: !!flipbookIntro,
                    });
                }
            } catch (err) {
                if (!cancelled) console.warn('[NLE warmup] failed:', err?.message || err);
            }
        })();

        return () => { cancelled = true; };
    }, [
        useNewNle,
        currentWorkspace?._id,
        studioVoiceoverUrl,
        studioSelectedScriptId,
        fastPacing,
        flipbookIntro,
        includeTags,
        excludeTags,
        voiceoverDuration,
        generatingCuts.size,
        showNewNleEditor,
    ]);

    const openNewNleEditor = async (options = {}) => {
        if (!currentWorkspace?._id) return;
        const batchScript = options?.batchScript || null;
        setNleEditorSource(batchScript
            ? { type: 'batch', scriptId: batchScript._id }
            : { type: 'studio' });
        // Always clear timeline cuts + show loading so NewNleEditor does not auto-seed
        // from the available pool before usedCutIds resolve.
        setNewNleCuts([]);
        setNewNleCutsLoading(true);
        setShowNewNleEditor(true);
        const hasCachedCuts = Array.isArray(newNleAvailableCuts) && newNleAvailableCuts.length > 0;
        if (!hasCachedCuts) {
            setNewNleAvailableCuts([]);
            setNewNleHasMoreCuts(false);
            setNewNleCutsPage(1);
        }
        try {
            let script = batchScript || null;
            if (!script) {
                const effectiveScriptId = isNoScriptMode && noScriptScriptId ? noScriptScriptId : studioSelectedScriptId;
                if (effectiveScriptId) {
                    script = studioScripts.find((s) => String(s._id) === String(effectiveScriptId));
                    if (!script) {
                        try {
                            const scriptResp = await api.getWorkspaceScript(currentWorkspace._id, effectiveScriptId);
                            script = scriptResp.data?.script || scriptResp.data;
                        } catch (_) {}
                    } else if (!script.usedCutIds?.length) {
                        // Lite list may omit usedCutIds — refresh full script
                        try {
                            const scriptResp = await api.getWorkspaceScript(currentWorkspace._id, effectiveScriptId);
                            script = scriptResp.data?.script || scriptResp.data || script;
                        } catch (_) {}
                    }
                }
            } else if (script._id && !String(script._id).startsWith('custom-') && !script.usedCutIds?.length) {
                try {
                    const scriptResp = await api.getWorkspaceScript(currentWorkspace._id, script._id);
                    script = scriptResp.data?.script || scriptResp.data || script;
                } catch (_) {}
            }
            const ids = (script && script.usedCutIds && script.usedCutIds.length) ? script.usedCutIds : [];
            const voiceoverSec = voiceoverDuration
                || getVoiceoverSecondsFromTimestamps(script?.voiceoverTimestamps)
                || (!batchScript ? getVoiceoverSecondsFromTimestamps(lastVoiceoverTimestampsRef.current) : 0);
            if (voiceoverSec > 0 && !(voiceoverDuration > 0)) setVoiceoverDuration(voiceoverSec);

            if (ids.length > 0) {
                const response = await api.getWorkspaceCuts(currentWorkspace._id, {
                    ids: ids.map((id) => String(id)).join(','),
                });
                const raw = response.data;
                const items = Array.isArray(raw) ? raw : (raw?.items || []);
                const found = new Map(items.filter((c) => !c.hidden).map((c) => [String(c._id), c]));
                setNewNleCuts(ids.map((id) => found.get(String(id))).filter(Boolean));
            }

            if (!hasCachedCuts) {
                const pickResp = await api.getWorkspaceCuts(currentWorkspace._id, {
                    page: 1,
                    pageSize: NEW_NLE_CUTS_PAGE_SIZE,
                    ...timelinePickerCutsQuery(includeTags, excludeTags),
                });
                const pickRaw = pickResp.data;
                const pickItems = Array.isArray(pickRaw) ? pickRaw : (pickRaw?.items || []);
                const pickVisible = pickItems.filter((c) => !c.hidden);
                setNewNleAvailableCuts(dedupeCutsById(pickVisible));
                setNewNleCutsPage(1);
                setNewNleHasMoreCuts(pickRaw?.hasMore ?? (pickItems.length >= NEW_NLE_CUTS_PAGE_SIZE));
                newNlePickerTagKeyRef.current = timelinePickerTagsKey(includeTags, excludeTags);
            }
        } catch (err) {
            console.error('Error opening new NLE:', err);
            setErrors((prev) => [...prev, 'Failed to open timeline editor']);
        } finally {
            setNewNleCutsLoading(false);
        }
    };

    const loadMoreNewNleCuts = async () => {
        if (!currentWorkspace?._id || newNleLoadingMore || !newNleHasMoreCuts) return;
        setNewNleLoadingMore(true);
        try {
            const nextPage = newNleCutsPage + 1;
            const response = await api.getWorkspaceCuts(currentWorkspace._id, {
                page: nextPage,
                pageSize: NEW_NLE_CUTS_PAGE_SIZE,
                ...timelinePickerCutsQuery(includeTags, excludeTags),
            });
            const raw = response.data;
            const items = Array.isArray(raw) ? raw : (raw?.items || []);
            const visibleCuts = items.filter((c) => !c.hidden);
            setNewNleAvailableCuts((prev) => dedupeCutsById([...prev, ...visibleCuts]));
            setNewNleCutsPage(nextPage);
            setNewNleHasMoreCuts(raw?.hasMore ?? (items.length >= NEW_NLE_CUTS_PAGE_SIZE));
        } catch (err) {
            console.error('Error loading more new NLE cuts:', err);
        } finally {
            setNewNleLoadingMore(false);
        }
    };

    useEffect(() => {
        if (!showNewNleEditor || !currentWorkspace?._id) return;
        const key = timelinePickerTagsKey(includeTags, excludeTags);
        if (newNlePickerTagKeyRef.current === key) return;
        let cancelled = false;
        (async () => {
            try {
                setNewNleLoadingMore(true);
                const response = await api.getWorkspaceCuts(currentWorkspace._id, {
                    page: 1,
                    pageSize: NEW_NLE_CUTS_PAGE_SIZE,
                    ...timelinePickerCutsQuery(includeTags, excludeTags),
                });
                if (cancelled) return;
                const raw = response.data;
                const items = Array.isArray(raw) ? raw : (raw?.items || []);
                const visibleCuts = items.filter((c) => !c.hidden);
                setNewNleAvailableCuts(dedupeCutsById(visibleCuts));
                setNewNleCutsPage(1);
                setNewNleHasMoreCuts(raw?.hasMore ?? (items.length >= NEW_NLE_CUTS_PAGE_SIZE));
                newNlePickerTagKeyRef.current = key;
            } catch (e) {
                console.error('New NLE picker tag refresh:', e);
            } finally {
                if (!cancelled) setNewNleLoadingMore(false);
            }
        })();
        return () => { cancelled = true; };
    }, [showNewNleEditor, currentWorkspace?._id, includeTags, excludeTags]);

    const handleNewNlePlanSfx = async () => {
        const isBatchNle = nleEditorSource?.type === 'batch';
        const scriptId = isBatchNle
            ? nleEditorSource.scriptId
            : studioSelectedScriptId;
        if (!currentWorkspace?._id || !scriptId) return [];
        setNewNlePlanningSfx(true);
        try {
            const script = isBatchNle
                ? batchScripts.find((s) => String(s._id) === String(scriptId))
                : studioScripts.find((s) => String(s._id) === String(scriptId));
            const contentVersion = script?.selectedVersion || 'main';
            const content = script
                ? (isBatchNle ? getBatchScriptContent(script, contentVersion) : getScriptContent(script, contentVersion))
                : '';
            const res = await api.planWorkspaceSfx(currentWorkspace._id, {
                scriptId,
                scriptContent: content,
                voiceoverTimestamps: script?.voiceoverTimestamps
                    || (!isBatchNle ? lastVoiceoverTimestampsRef.current : undefined),
            });
            return res.data?.cues || [];
        } catch (err) {
            console.error('Plan SFX failed:', err);
            setErrors((prev) => [...prev, err.response?.data?.error || 'Failed to plan sound effects']);
            return [];
        } finally {
            setNewNlePlanningSfx(false);
        }
    };

    const handleNewNleExport = (exportData) => {
        setShowNewNleEditor(false);
        newNleExportExtrasRef.current = {
            selectedMusicId: exportData.selectedMusicId || null,
            flipbookIntro: !!exportData.flipbookIntro,
            cutSilenceMode: !!exportData.cutSilenceMode,
            fastPacing: !!exportData.fastPacing,
            sfxCues: exportData.sfxCues || [],
            enableSubtitles: exportData.enableSubtitles,
            oneWordPerSubtitle: exportData.oneWordPerSubtitle,
            subtitlePosition: exportData.subtitlePosition,
            bigText: exportData.bigText,
            glowText: false,
            fontId: exportData.fontId || null,
            subtitleFontColor: exportData.subtitleFontColor,
            subtitleOutlineColor: exportData.subtitleOutlineColor,
            subtitleOutlineEnabled: exportData.subtitleOutlineEnabled,
            openingLineInCenter: exportData.openingLineInCenter,
            openingLineTextSize: exportData.openingLineTextSize,
            openingLineFontId: exportData.openingLineFontId || null,
            openingLineFontColor: exportData.openingLineFontColor,
            fontMixing: exportData.fontMixing,
            centerStackOpening: exportData.centerStackOpening,
            centerStackLineByLine: exportData.centerStackLineByLine,
            fontMixingSecondColor: exportData.fontMixingSecondColor,
        };
        const batchScriptIdForExport = nleEditorSource?.type === 'batch' ? nleEditorSource.scriptId : null;
        const batchOverrideActive = !!(batchScriptIdForExport && batchVideoSettingsOverrideRef.current[batchScriptIdForExport]);
        if (batchOverrideActive) {
            setBatchVideoSettingsOverrideByScriptId((prev) => {
                if (!prev[batchScriptIdForExport]) return prev;
                return {
                    ...prev,
                    [batchScriptIdForExport]: mergeBatchVideoSettingsWithExport(prev[batchScriptIdForExport], exportData),
                };
            });
        }
        if (!batchOverrideActive && exportData.flipbookIntro !== undefined) {
            setFlipbookIntro(!!exportData.flipbookIntro);
            try { localStorage.setItem('workspace_flipbook_intro', String(!!exportData.flipbookIntro)); } catch (_) {}
        }
        if (!batchOverrideActive) {
        if (exportData.cutSilenceMode !== undefined) {
            setCutSilenceMode(!!exportData.cutSilenceMode);
            try { localStorage.setItem('workspace_cut_silence_mode', String(!!exportData.cutSilenceMode)); } catch (_) {}
        }
        if (exportData.enableSubtitles !== undefined) {
            setEnableSubtitles(!!exportData.enableSubtitles);
            try { localStorage.setItem('workspace_enable_subtitles', String(!!exportData.enableSubtitles)); } catch (_) {}
        }
        if (exportData.oneWordPerSubtitle !== undefined) {
            setOneWordPerSubtitle(!!exportData.oneWordPerSubtitle);
            try { localStorage.setItem('workspace_one_word_per_subtitle', String(!!exportData.oneWordPerSubtitle)); } catch (_) {}
        }
        if (exportData.subtitlePosition === 'center' || exportData.subtitlePosition === 'bottom') {
            setSubtitlePosition(exportData.subtitlePosition);
            try { localStorage.setItem('workspace_subtitle_position', exportData.subtitlePosition); } catch (_) {}
        }
        if (exportData.bigText !== undefined) {
            setBigText(!!exportData.bigText);
            try { localStorage.setItem('workspace_big_text', String(!!exportData.bigText)); } catch (_) {}
        }
        if (exportData.glowText !== undefined) {
            setGlowText(!!exportData.glowText);
            try { localStorage.setItem('workspace_glow_text', String(!!exportData.glowText)); } catch (_) {}
        }
        if (exportData.fontId !== undefined) {
            setSelectedFontId(exportData.fontId || '');
        }
        if (exportData.subtitleFontColor) {
            setSubtitleFontColor(exportData.subtitleFontColor);
        }
        if (exportData.subtitleOutlineColor) {
            setSubtitleOutlineColor(exportData.subtitleOutlineColor);
        }
        if (exportData.subtitleOutlineEnabled !== undefined) {
            setSubtitleOutlineEnabled(!!exportData.subtitleOutlineEnabled);
        }
        if (exportData.openingLineInCenter !== undefined) {
            setOpeningLineInCenter(!!exportData.openingLineInCenter);
            try { localStorage.setItem('workspace_opening_line_in_center', String(!!exportData.openingLineInCenter)); } catch (_) {}
        }
        if (exportData.openingLineTextSize === 'big' || exportData.openingLineTextSize === 'small') {
            setOpeningLineTextSize(exportData.openingLineTextSize);
            try { localStorage.setItem('workspace_opening_line_text_size', exportData.openingLineTextSize); } catch (_) {}
        }
        if (exportData.openingLineFontId !== undefined) {
            setOpeningLineFontId(exportData.openingLineFontId || '');
            try { localStorage.setItem('workspace_opening_line_font_id', exportData.openingLineFontId || ''); } catch (_) {}
        }
        if (exportData.openingLineFontColor) {
            setOpeningLineFontColor(exportData.openingLineFontColor);
            try { localStorage.setItem('workspace_opening_line_font_color', exportData.openingLineFontColor); } catch (_) {}
        }
        if (exportData.fontMixing !== undefined || exportData.centerStackOpening !== undefined || exportData.centerStackLineByLine !== undefined) {
            const nextMix = !!exportData.fontMixing;
            const nextStack = !!exportData.centerStackOpening;
            const nextLineByLine = !!exportData.centerStackLineByLine;
            setFontMixing(nextMix);
            setCenterStackOpening(nextStack);
            setCenterStackLineByLine(nextLineByLine);
            try {
                localStorage.setItem('workspace_font_mixing', String(nextMix));
                localStorage.setItem('workspace_center_stack_opening', String(nextStack));
                localStorage.setItem('workspace_center_stack_line_by_line', String(nextLineByLine));
            } catch (_) {}
        }
        if (exportData.fontMixingSecondColor) {
            setFontMixingSecondColor(exportData.fontMixingSecondColor);
        }
        if (exportData.fastPacing !== undefined) {
            setFastPacing(!!exportData.fastPacing);
            try { localStorage.setItem('workspace_fast_pacing', String(!!exportData.fastPacing)); } catch (_) {}
        }
        if (exportData.selectedMusicId) {
            setSelectedMusicId(exportData.selectedMusicId);
            setUseMusic(true);
        } else if (exportData.selectedMusicId === null) {
            setUseMusic(false);
        }
        }
        const exportExtras = newNleExportExtrasRef.current;
        setNleEditorSource(null);
        if (batchScriptIdForExport) {
            if (exportData.selectedMusicId) {
                const music = availableMusic.find((m) => String(m._id) === String(exportData.selectedMusicId));
                setBatchScriptMusicSelections((prev) => ({
                    ...prev,
                    [batchScriptIdForExport]: {
                        musicId: exportData.selectedMusicId,
                        musicName: music?.name || 'Selected',
                        isRandom: false,
                    },
                }));
                if (!batchOverrideActive) setBatchUseMusic(true);
            } else if (exportData.selectedMusicId === null && !batchOverrideActive) {
                setBatchUseMusic(false);
            }
            handleBatchGenerateVideo(batchScriptIdForExport, null, null, exportData.orderedCutIds || [], exportExtras);
        } else {
            studioGenerateVideo(null, null, null, exportData.orderedCutIds || [], false, false, exportExtras);
        }
    };

    const openTimelineModal = async () => {
        // Visual only (no script) always uses the legacy editor
        if (useNewNle && !isNoScriptMode) {
            openNewNleEditor();
            return;
        }
        if (!currentWorkspace?._id) return;
        setShowTimelineModal(true);
        setShowTimelineAddCutPicker(false);
        setTimelineCuts([]);
        setTimelineAvailableCuts([]);
        setTimelineCutsPage(1);
        setTimelineHasMoreCuts(false);
        setTimelineCutsLoading(true);
        setTimelinePlayheadTime(0);
        timelineStripTimeRef.current = 0;
        setTimelineAudioPlaying(false);
        timelineAudioPlayingRef.current = false;
        setTimelineActiveCutIndex(null);
        try {
            let script = null;
            const effectiveScriptId = isNoScriptMode && noScriptScriptId ? noScriptScriptId : studioSelectedScriptId;
            if (effectiveScriptId) {
                if (isNoScriptMode && noScriptScriptId) {
                    try {
                        const scriptResp = await api.getWorkspaceScript(currentWorkspace._id, effectiveScriptId);
                        script = scriptResp.data?.script || scriptResp.data;
                    } catch (_) {
                        script = studioScripts.find((s) => String(s._id) === String(effectiveScriptId));
                    }
                } else {
                    script = studioScripts.find((s) => String(s._id) === String(effectiveScriptId));
                }
            }
            const ids = (script && script.usedCutIds && script.usedCutIds.length) ? script.usedCutIds : [];
            timelineUsedCutIdsRef.current = ids;
            const usedDurations = Array.isArray(script?.usedCutDurations) ? script.usedCutDurations : null;
            timelineUsedCutDurationsRef.current = usedDurations;

            const voiceoverSec = voiceoverDuration
                || getVoiceoverSecondsFromTimestamps(script?.voiceoverTimestamps)
                || getVoiceoverSecondsFromTimestamps(lastVoiceoverTimestampsRef.current);

            // Seed a stable VO duration so the audio lane / transport don't fall
            // back to "sum of cuts" (which grows as you add clips).
            if (voiceoverSec > 0 && !(voiceoverDuration > 0)) {
                setVoiceoverDuration(voiceoverSec);
            }

            if (ids.length > 0) {
                const response = await api.getWorkspaceCuts(currentWorkspace._id, {
                    ids: ids.map((id) => String(id)).join(','),
                });
                const raw = response.data;
                const items = Array.isArray(raw) ? raw : (raw?.items || []);
                const found = new Map(
                    items.filter((c) => !c.hidden).map((c) => [String(c._id), c])
                );
                const ordered = ids.map((id) => found.get(String(id))).filter(Boolean);
                setTimelineCuts(ordered);
                setTimelineRequiredCutCount(resolveTimelineRequiredSeconds({
                    voiceoverSeconds: voiceoverSec,
                    fastPacing,
                    orderedCuts: ordered,
                    usedDurations,
                    flipbookIntro,
                    isNoScriptMode,
                    noScriptVideoLength,
                }));
                const pickResp = await api.getWorkspaceCuts(currentWorkspace._id, {
                    page: 1,
                    pageSize: TIMELINE_CUTS_PAGE_SIZE,
                    ...timelinePickerCutsQuery(includeTags, excludeTags),
                });
                const pickRaw = pickResp.data;
                const pickItems = Array.isArray(pickRaw) ? pickRaw : (pickRaw?.items || []);
                const pickVisible = pickItems.filter((c) => !c.hidden);
                const pickHasMore = pickRaw?.hasMore ?? (pickItems.length >= TIMELINE_CUTS_PAGE_SIZE);
                setTimelineAvailableCuts(dedupeCutsById(pickVisible));
                setTimelineCutsPage(1);
                setTimelineHasMoreCuts(pickHasMore);
                timelinePickerTagKeyRef.current = timelinePickerTagsKey(includeTags, excludeTags);
            } else {
                const response = await api.getWorkspaceCuts(currentWorkspace._id, {
                    page: 1,
                    pageSize: TIMELINE_CUTS_PAGE_SIZE,
                    ...timelinePickerCutsQuery(includeTags, excludeTags),
                });
                const raw = response.data;
                const items = Array.isArray(raw) ? raw : (raw?.items || []);
                const visibleCuts = items.filter(c => !c.hidden);
                const hasMore = raw?.hasMore ?? (items.length >= TIMELINE_CUTS_PAGE_SIZE);
                setTimelineAvailableCuts(dedupeCutsById(visibleCuts));
                setTimelineCutsPage(1);
                setTimelineHasMoreCuts(hasMore);
                timelinePickerTagKeyRef.current = timelinePickerTagsKey(includeTags, excludeTags);
                setTimelineRequiredCutCount(resolveTimelineRequiredSeconds({
                    voiceoverSeconds: voiceoverSec,
                    fastPacing,
                    orderedCuts: [],
                    usedDurations,
                    flipbookIntro,
                    isNoScriptMode,
                    noScriptVideoLength,
                }));
            }
        } catch (err) {
            console.error('Error fetching cuts for timeline:', err);
            const fallback = workspaceCuts.filter(c => !c.hidden).length ? workspaceCuts.filter(c => !c.hidden) : carouselCuts;
            setTimelineAvailableCuts(dedupeCutsById(fallback));
            setTimelineHasMoreCuts(false);
            const effectiveScriptId = isNoScriptMode && noScriptScriptId ? noScriptScriptId : studioSelectedScriptId;
            const script = studioScripts.find(s => s._id === effectiveScriptId);
            const ids = (script && script.usedCutIds && script.usedCutIds.length) ? script.usedCutIds : [];
            timelineUsedCutIdsRef.current = ids;
            const voiceoverSecFallback = voiceoverDuration
                || getVoiceoverSecondsFromTimestamps(script?.voiceoverTimestamps)
                || getVoiceoverSecondsFromTimestamps(lastVoiceoverTimestampsRef.current);
            if (voiceoverSecFallback > 0 && !(voiceoverDuration > 0)) {
                setVoiceoverDuration(voiceoverSecFallback);
            }
            const ordered = ids.map(id => fallback.find(c => String(c._id) === String(id))).filter(Boolean);
            setTimelineCuts(ordered);
            setTimelineRequiredCutCount(resolveTimelineRequiredSeconds({
                voiceoverSeconds: voiceoverSecFallback,
                fastPacing,
                orderedCuts: ordered,
                usedDurations: Array.isArray(script?.usedCutDurations) ? script.usedCutDurations : null,
                flipbookIntro,
                isNoScriptMode,
                noScriptVideoLength,
            }));
        } finally {
            setTimelineCutsLoading(false);
        }
    };

    const loadMoreTimelineCuts = async () => {
        if (!currentWorkspace?._id || timelineCutsLoadingMore || !timelineHasMoreCuts) return;
        setTimelineCutsLoadingMore(true);
        try {
            const nextPage = timelineCutsPage + 1;
            const response = await api.getWorkspaceCuts(currentWorkspace._id, {
                page: nextPage,
                pageSize: TIMELINE_CUTS_PAGE_SIZE,
                ...timelinePickerCutsQuery(includeTags, excludeTags),
            });
            const raw = response.data;
            const items = Array.isArray(raw) ? raw : (raw?.items || []);
            const visibleCuts = items.filter(c => !c.hidden);
            const hasMore = raw?.hasMore ?? (items.length >= TIMELINE_CUTS_PAGE_SIZE);
            setTimelineAvailableCuts((prev) => dedupeCutsById([...prev, ...visibleCuts]));
            setTimelineCutsPage(nextPage);
            setTimelineHasMoreCuts(hasMore);
        } catch (err) {
            console.error('Error loading more timeline cuts:', err);
        } finally {
            setTimelineCutsLoadingMore(false);
        }
    };

    useEffect(() => {
        if (!showTimelineModal || !currentWorkspace?._id) return;
        if (timelineCutsLoading) return;
        const key = timelinePickerTagsKey(includeTags, excludeTags);
        if (timelinePickerTagKeyRef.current === key) return;
        let cancelled = false;
        (async () => {
            try {
                setTimelineCutsLoadingMore(true);
                const response = await api.getWorkspaceCuts(currentWorkspace._id, {
                    page: 1,
                    pageSize: TIMELINE_CUTS_PAGE_SIZE,
                    ...timelinePickerCutsQuery(includeTags, excludeTags),
                });
                if (cancelled) return;
                const raw = response.data;
                const items = Array.isArray(raw) ? raw : (raw?.items || []);
                const visibleCuts = items.filter((c) => !c.hidden);
                const hasMore = raw?.hasMore ?? (items.length >= TIMELINE_CUTS_PAGE_SIZE);
                setTimelineAvailableCuts(dedupeCutsById(visibleCuts));
                setTimelineCutsPage(1);
                setTimelineHasMoreCuts(hasMore);
                timelinePickerTagKeyRef.current = key;
            } catch (e) {
                console.error('Timeline picker tag refresh:', e);
            } finally {
                if (!cancelled) setTimelineCutsLoadingMore(false);
            }
        })();
        return () => { cancelled = true; };
    }, [showTimelineModal, includeTags, excludeTags, timelineCutsLoading, currentWorkspace?._id]);

    const closeTimelineModal = () => {
        setShowTimelineModal(false);
        setShowTimelineAddCutPicker(false);
        setTimelineActiveCutIndex(null);
        setTimelinePlayheadTime(0);
        timelineStripTimeRef.current = 0;
        setTimelineAudioPlaying(false);
        timelineAudioPlayingRef.current = false;
        setTimelineRequiredCutCount(null);
        timelinePickerTagKeyRef.current = null;
        const el = timelineAudioRef.current;
        if (el) {
            el.pause();
            try { el.currentTime = 0; } catch (_) {}
        }
    };

    const syncTimelinePlayhead = useCallback((stripTime) => {
        timelineStripTimeRef.current = stripTime;
        const duration = voiceoverDuration || timelineAudioRef.current?.duration || 0;
        const audioTime = duration > 0 ? Math.min(stripTime, duration) : stripTime;
        setTimelinePlayheadTime(audioTime);
        setTimelineActiveCutIndex(timelineActiveCutIndexFromTime(timelineCuts, stripTime, fastPacing));
    }, [timelineCuts, fastPacing, voiceoverDuration]);

    const handleTimelineAudioPlayPause = useCallback(() => {
        if (!studioVoiceoverUrl) return;
        const el = timelineAudioRef.current;
        if (timelineAudioPlayingRef.current) {
            timelineWasPlayingRef.current = false;
            if (el) {
                const pos = el.currentTime || 0;
                if (pos > 0) syncTimelinePlayhead(pos);
                el.pause();
            }
            timelineAudioPlayingRef.current = false;
            setTimelineAudioPlaying(false);
            return;
        }
        if (!el) return;
        let startPos = timelineStripTimeRef.current;
        const duration = voiceoverDuration || el.duration || 0;
        if (duration > 0) startPos = Math.min(startPos, duration);
        try {
            if (Math.abs(el.currentTime - startPos) > 0.25) el.currentTime = startPos;
            el.play().catch(() => {});
            timelineAudioPlayingRef.current = true;
            setTimelineAudioPlaying(true);
            if (startPos === 0) syncTimelinePlayhead(0);
        } catch (_) {
            timelineAudioPlayingRef.current = false;
            setTimelineAudioPlaying(false);
        }
    }, [studioVoiceoverUrl, voiceoverDuration, syncTimelinePlayhead]);

    const handleTimelineScrubStart = useCallback(() => {
        timelineWasPlayingRef.current = timelineAudioPlayingRef.current;
        const el = timelineAudioRef.current;
        if (timelineAudioPlayingRef.current && el) el.pause();
    }, []);

    const handleTimelineSeek = useCallback((t) => {
        syncTimelinePlayhead(t);
    }, [syncTimelinePlayhead]);

    const handleTimelineScrubEnd = useCallback((t) => {
        syncTimelinePlayhead(t);
        const duration = voiceoverDuration || timelineAudioRef.current?.duration || 0;
        const audioTime = duration > 0 ? Math.min(t, duration) : t;
        const el = timelineAudioRef.current;
        const atEnd = duration > 0 && audioTime >= duration - 0.05;
        if (el) {
            try { el.currentTime = audioTime; } catch (_) {}
            if (timelineWasPlayingRef.current && !atEnd) {
                el.play().catch(() => {});
                timelineAudioPlayingRef.current = true;
                setTimelineAudioPlaying(true);
            } else if (timelineWasPlayingRef.current && atEnd) {
                timelineAudioPlayingRef.current = false;
                setTimelineAudioPlaying(false);
            }
        }
        timelineWasPlayingRef.current = false;
    }, [syncTimelinePlayhead, voiceoverDuration]);

    const handleTimelineSkipToStart = useCallback(() => {
        handleTimelineSeek(0);
        const el = timelineAudioRef.current;
        if (el) {
            try { el.currentTime = 0; } catch (_) {}
        }
    }, [handleTimelineSeek]);
    const timelineMoveCut = (index, direction) => {
        const next = index + direction;
        if (next < 0 || next >= timelineCuts.length) return;
        setTimelineCuts(prev => {
            const arr = [...prev];
            [arr[index], arr[next]] = [arr[next], arr[index]];
            return arr;
        });
    };
    const timelineRemoveCut = (index) => {
        setTimelineCuts(prev => prev.filter((_, i) => i !== index));
    };
    const timelineReorderCut = (fromIndex, toIndex) => {
        if (fromIndex === toIndex) return;
        setTimelineCuts(prev => {
            const arr = [...prev];
            const [removed] = arr.splice(fromIndex, 1);
            arr.splice(toIndex, 0, removed);
            return arr;
        });
    };
    const timelineAddCut = useCallback((cut) => {
        // No-script has no voiceover playhead — always append.
        if (isNoScriptMode) {
            setTimelineCuts(prev => [...prev, cut]);
            return;
        }
        const stripTime = timelineStripTimeRef.current;
        setTimelineCuts(prevCuts => {
            const idx = timelineInsertIndexAtPlayhead(prevCuts, stripTime, fastPacing);
            const next = [...prevCuts];
            next.splice(idx, 0, cut);
            // Advance strip-time immediately so a rapid second add inserts after this cut,
            // even before the scroll/layout finishes.
            const playheadTime = timelinePlayheadJustPastMidCut(next, idx, fastPacing);
            timelineStripTimeRef.current = playheadTime;
            pendingTimelineScrollRef.current = { cuts: next, index: idx };
            return next;
        });
    }, [fastPacing, isNoScriptMode]);

    // After an add-cut, scroll the strip so the fixed playhead sits just past mid of the
    // new cut (layout must settle first so scrollWidth includes the new card).
    useEffect(() => {
        const pending = pendingTimelineScrollRef.current;
        if (!pending || isNoScriptMode) return;
        pendingTimelineScrollRef.current = null;
        const run = () => {
            const t = timelineCutStripRef.current?.scrollPlayheadPastCutMid?.(
                pending.cuts,
                pending.index,
                fastPacing
            );
            if (typeof t === 'number' && Number.isFinite(t)) {
                timelineStripTimeRef.current = t;
            }
        };
        requestAnimationFrame(() => requestAnimationFrame(run));
    }, [timelineCuts, fastPacing, isNoScriptMode]);
    const timelineCutUsageById = useMemo(() => timelineCutUsageCounts(timelineCuts), [timelineCuts]);
    const timelineRegenerateVideo = () => {
        if (timelineCuts.length === 0) return;
        const orderedIds = timelineCuts.map(c => c._id);
        closeTimelineModal();
        if (isNoScriptMode) {
            studioGenerateNoScriptVideo(orderedIds);
            return;
        }
        studioGenerateVideo(null, null, null, orderedIds);
    };

    const enterVisualOnlyMode = () => {
        trackEvent('no_script_mode_activated', { workspaceId: currentWorkspace?._id });
        setStudioScripts([]);
        setStudioSelectedScriptId(null);
        setNoScriptScriptId(null);
        setStudioVideoUrl('');
        setStudioVideoUrlTimestamp(0);
        setStudioVideoSaved(false);
        lastStudioVideoCompletionUrlRef.current = null;
        setIsNoScriptMode(true);
        setStudioSubTab('visual-only');
        try { localStorage.setItem('workspace_studio_sub_tab', 'visual-only'); } catch (_) {}
    };

    const exitVisualOnlyMode = (nextTab = 'vlog') => {
        setIsNoScriptMode(false);
        setNoScriptScriptId(null);
        setStudioScripts([]);
        setStudioSelectedScriptId(null);
        setStudioVideoUrl('');
        setStudioVideoUrlTimestamp(0);
        setStudioVideoSaved(false);
        lastStudioVideoCompletionUrlRef.current = null;
        setStudioSubTab(nextTab);
        try { localStorage.setItem('workspace_studio_sub_tab', nextTab); } catch (_) {}
    };

    const handleNoScriptModeClick = () => {
        if (studioScripts.length > 0) {
            setShowNoScriptConfirmModal(true);
        } else {
            enterVisualOnlyMode();
        }
    };
    const confirmNoScriptSwitch = () => {
        setShowNoScriptConfirmModal(false);
        enterVisualOnlyMode();
    };
    const studioGenerateNoScriptVideo = async (orderedCutIdsOverride = null) => {
        if (!isNoScriptMode) return;
        // Ignore React SyntheticEvent when wired directly to onClick
        if (orderedCutIdsOverride && typeof orderedCutIdsOverride === 'object' && orderedCutIdsOverride.nativeEvent) {
            orderedCutIdsOverride = null;
        }

        exitStudioAutopilotMode();

        const hasSelectedSourceVideos =
            (studioVideoSourceMode === 'select' || studioVideoSourceMode === 'import') &&
            Array.isArray(studioSelectedRawVideoIds) &&
            studioSelectedRawVideoIds.length > 0;
        if (
            (studioVideoSourceMode === 'select' || studioVideoSourceMode === 'import') &&
            !hasSelectedSourceVideos
        ) {
            setErrors(prev => [...prev, 'Choose “Select” and pick at least one raw video, import new footage, or switch to “All videos”.']);
            return;
        }
        if (rawTotal === 0 && !hasSelectedSourceVideos) {
            setErrors(prev => [...prev, 'No source videos yet. Upload at least one video in Raw Videos before generating.']);
            return;
        }

        const selectedVideoIdsPayload =
            (studioVideoSourceMode === 'select' || studioVideoSourceMode === 'import') &&
            studioSelectedRawVideoIds.length > 0
                ? studioSelectedRawVideoIds
                : undefined;

        const orderedCutIdsToUse = Array.isArray(orderedCutIdsOverride) && orderedCutIdsOverride.length > 0
            ? orderedCutIdsOverride.map((id) => String(id)).filter(Boolean)
            : undefined;
        
        // Track no-script video generation start
        trackEvent('no_script_video_generation_started', {
            videoLength: noScriptVideoLength,
            includeTags: includeTags.length > 0 ? includeTags.join(',') : null,
            excludeTags: excludeTags.length > 0 ? excludeTags.join(',') : null,
            sourceMode: studioVideoSourceMode,
            selectedVideoCount: selectedVideoIdsPayload?.length ?? 0,
            orderedCutCount: orderedCutIdsToUse?.length ?? 0,
            workspaceId: currentWorkspace?._id
        });
        
        try {
            console.log(`[NO-SCRIPT VIDEO] Starting no-script video generation for ${noScriptVideoLength}s`);
            setStudioGeneratingVideo(true);
            setStudioVideoSaved(false);
            setStudioVideoProgress(5);
            setStudioVideoUrl('');
            lastStudioVideoCompletionUrlRef.current = null;
            
            prepareCarouselCuts();
            
            const response = await api.generateNoScriptVideo(currentWorkspace._id, {
                videoLength: noScriptVideoLength,
                bigText: bigText,
                includeTags: includeTags.length > 0 ? includeTags.join(',') : undefined,
                excludeTags: excludeTags.length > 0 ? excludeTags.join(',') : undefined,
                slowMotion: slowMotion,
                fastPacing: fastPacing,
                selectedVideoIds: selectedVideoIdsPayload,
                orderedCutIds: orderedCutIdsToUse,
            });
            console.log(`[NO-SCRIPT VIDEO] Generation started successfully:`, response.data);
            
            // Store the script ID for later publishing / timeline editing
            const newScriptId = response.data.scriptId;
            setNoScriptScriptId(newScriptId);
            
            const start = Date.now();
            const TIMEOUT_MS = 10 * 60 * 1000;
            if (studioVideoPollRef.current) clearInterval(studioVideoPollRef.current);
            studioVideoPollRef.current = setInterval(async () => {
                try {
                    if (Date.now() - start > TIMEOUT_MS) {
                        clearInterval(studioVideoPollRef.current);
                        studioVideoPollRef.current = null;
                        setStudioVideoProgress(0);
                        setStudioGeneratingVideo(false);
                        exitStudioAutopilotMode();
                        setErrors(prev => [...prev, 'Video generation timed out.']);
                        return;
                    }
                    const statusResp = await api.getNoScriptVideoStatus(currentWorkspace._id);
                    const { status, progress, videoUrl, scriptId: statusScriptId } = statusResp.data;
                    console.log(`[NO-SCRIPT VIDEO] Status check: ${status}, progress: ${progress}%, videoUrl: ${videoUrl ? 'exists' : 'none'}`);
                    setStudioVideoProgress(progress || 0);
                    if (statusScriptId) setNoScriptScriptId(statusScriptId);
                    if ((status === 'draft' && videoUrl) || status === 'error') {
                        clearInterval(studioVideoPollRef.current);
                        studioVideoPollRef.current = null;
                        if (status === 'draft' && videoUrl) {
                            const wasAlreadyHandled = lastStudioVideoCompletionUrlRef.current === videoUrl;
                            console.log(`[NO-SCRIPT VIDEO] Generation complete! Setting video URL: ${videoUrl} (dedup=${wasAlreadyHandled})`);
                            setStudioVideoUrl(videoUrl);
                            setStudioVideoProgress(0);
                            exitStudioAutopilotMode();
                            if (!wasAlreadyHandled) {
                                lastStudioVideoCompletionUrlRef.current = videoUrl;
                                setStudioVideoUrlTimestamp(Date.now()); // Force video reload only once
                            }
                            const scriptIdForRefetch = statusScriptId || newScriptId;
                            if (scriptIdForRefetch) {
                                api.getWorkspaceScript(currentWorkspace._id, scriptIdForRefetch).then((resp) => {
                                    const updated = resp.data?.script || resp.data;
                                    if (updated) mergeScriptIntoStudioScripts(scriptIdForRefetch, updated);
                                }).catch(e => console.warn('Failed to refetch script after no-script video generation:', e));
                            }
                        } else if (status === 'error') {
                            console.log(`[NO-SCRIPT VIDEO] Generation failed with error status`);
                            setStudioVideoProgress(0);
                            exitStudioAutopilotMode();
                        }
                        setStudioGeneratingVideo(false);
                    }
                } catch (error) {
                    console.error('[NO-SCRIPT VIDEO] Error checking video status:', error);
                }
            }, 2000);
        } catch (error) {
            setStudioVideoProgress(0);
            setStudioGeneratingVideo(false);
            exitStudioAutopilotMode();
            console.error('Error generating no-script video:', error);
            setErrors(prev => [...prev, 'Failed to start video generation: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Cancel functions for generation processes
    const cancelScriptGeneration = () => {
        if (studioPollRef.current) {
            clearInterval(studioPollRef.current);
            studioPollRef.current = null;
        }
        // Clear simulated progress timers
        simulatedProgressTimers.forEach(timer => clearTimeout(timer));
        setSimulatedProgressTimers([]);
        setSimulatedProgressActive(false);
        simulatedProgressActiveRef.current = false;
        progressLockUntilRef.current = 0;
        setLastSimulatedStep('');
        lastSimulatedStepRef.current = '';
        setStudioGenerating(false);
        setStudioProgress({ current: 0, total: 1, percent: 0, currentStep: '' });
        exitStudioAutopilotMode();
    };

    const cancelVoiceoverGeneration = () => {
        // Voiceover generation is a direct API call, so we set a flag to prevent completion handlers
        studioVoiceoverCancelledRef.current = true;
        shouldAutoplayStudioVoiceoverRef.current = false;
        setStudioGeneratingVoice(false);
        // Don't clear the voiceover URL - keep the previous one if it exists
        exitStudioAutopilotMode();
    };

    const cancelVideoGeneration = () => {
        if (studioVideoPollRef.current) {
            clearInterval(studioVideoPollRef.current);
            studioVideoPollRef.current = null;
        }
        setStudioGeneratingVideo(false);
        setStudioVideoProgress(0);
        exitStudioAutopilotMode();
    };

    const cancelTextStudioOverlayGeneration = () => {
        textStudioOverlayCancelledRef.current = true;
        if (textStudioOverlayPollRef.current) {
            clearInterval(textStudioOverlayPollRef.current);
            textStudioOverlayPollRef.current = null;
        }
        setTextStudioGeneratingOverlay(false);
    };

    const cancelTextStudioVideoGeneration = () => {
        textStudioVideoCancelledRef.current = true;
        if (textStudioVideoPollRef.current) {
            clearInterval(textStudioVideoPollRef.current);
            textStudioVideoPollRef.current = null;
        }
        setTextStudioGeneratingVideo(false);
        setTextStudioVideoProgress(0);
    };

    const studioPublish = async () => {
        if (!studioVideoUrl) return;
        
        try {
            setStudioPublishingVideo(true);
            
            // Use the appropriate script ID based on mode
            const scriptId = isNoScriptMode ? noScriptScriptId : studioSelectedScriptId;
            if (!scriptId) {
                setErrors(prev => [...prev, 'No script ID available for publishing']);
                return;
            }
            
            const thumbTitle = hasConfiguredThumbnail() ? thumbnailTitle.trim() : '';
            const response = await api.publishWorkspaceVideo(currentWorkspace._id, scriptId, {
                videoUrl: studioVideoUrl,
                musicName: lastUsedMusicName || null,
                ...(thumbTitle ? { title: thumbTitle } : {}),
            });
            
            if (response.data.message === 'Video published successfully' || response.data.message === 'Video already published') {
                setStudioVideoSaved(true);
                onSetupStep3Complete?.();
                // Refresh generated videos list and count (first page; total from API)
                await fetchGeneratedVideosPage(1, false);
                
                // Also update the raw total count if needed
                const rawResponse = await api.getWorkspaceVideos(currentWorkspace._id, 1, 1);
                setRawTotal(rawResponse.data.total || 0);
            }
        } catch (error) {
            console.error('Error publishing video:', error);
            setErrors(prev => [...prev, 'Failed to publish video']);
        } finally {
            setStudioPublishingVideo(false);
        }
    };

    const handleStudioVideoDownload = async () => {
        if (!studioVideoUrl) return;

        try {
            const scriptId = isNoScriptMode ? noScriptScriptId : studioSelectedScriptId;
            const selectedScript = scriptId ? studioScripts.find(s => s._id === scriptId) : null;
            const contentVersion = selectedScript?.selectedVersion || 'main';
            const scriptContent = selectedScript ? getScriptContent(selectedScript, contentVersion) : '';
            const downloadName = buildMerraVideoDownloadFilename(scriptContent);
            let directUrl = studioVideoUrl;
            if (scriptId) {
                directUrl = await resolveStudioVideoDownloadUrl(
                    currentWorkspace._id,
                    scriptId,
                    downloadName,
                    studioVideoUrl
                );
            }
            triggerDirectVideoDownload(directUrl, downloadName);
            celebrateVideoDownload();
            onSetupStep3Complete?.();
        } catch (error) {
            console.error('Error downloading video:', error);
            setErrors(prev => [...prev, 'Failed to download video']);
        }
    };

    const handleStudioScriptVersionChange = async (scriptId, selectedVersion) => {
        // Optimistic update - update UI immediately
            setStudioScripts(prev => 
                prev.map(script => 
                    script._id === scriptId 
                        ? { ...script, selectedVersion }
                        : script
                )
            );
        
        try {
            await api.updateWorkspaceScriptVersion(currentWorkspace._id, scriptId, selectedVersion);
        } catch (error) {
            console.error('Error updating script version:', error);
            // Revert optimistic update on error
            setStudioScripts(prev => 
                prev.map(script => 
                    script._id === scriptId 
                        ? { ...script, selectedVersion: script.selectedVersion || 'main' }
                        : script
                )
            );
            setErrors(prev => [...prev, 'Failed to update script version']);
        }
    };

    const handleStudioScriptContentChange = (scriptId, version, newContent) => {
        setScriptChangedSinceVoiceover(true); // User edited script, so it may differ from current voiceover
        // Update editing state immediately for instant UI feedback
        console.log('[SCRIPT EDIT] Storing edit:', {
            scriptId,
            version,
            contentLength: newContent?.length,
            contentPreview: newContent?.substring(0, 50)
        });
        setEditingScriptContent(prev => ({
            ...prev,
            [scriptId]: {
                ...prev[scriptId],
                [version]: newContent
            }
        }));
        
        // Debounce the API call and state update
        const key = `${scriptId}-${version}`;
        if (editDebounceRef.current[key]) {
            clearTimeout(editDebounceRef.current[key]);
        }
        
        editDebounceRef.current[key] = setTimeout(async () => {
            try {
                await api.updateWorkspaceScriptContent(currentWorkspace._id, scriptId, version, newContent);
        
                // Update local state
        setStudioScripts(prev => 
            prev.map(script => 
                script._id === scriptId 
                            ? { ...script, [version === 'main' ? 'content' : (version === 'alt' ? 'contentAlt' : 'contentThird')]: newContent }
                    : script
            )
        );
                
                trackEvent('workspace_script_content_edited', {
                    scriptId,
                    version,
                    contentLength: newContent.length
                });
            } catch (error) {
                setErrors(prev => [...prev, 'Failed to save script changes: ' + (error.response?.data?.error || error.message)]);
            }
        }, 1000); // 1 second debounce for content editing
    };

    const getScriptContent = (script, version) => {
        const editingContent = editingScriptContent[script._id]?.[version];
        if (editingContent !== undefined) {
            return editingContent;
        }
        if (version === 'main') {
            return script.content;
        } else if (version === 'alt') {
            return script.contentAlt;
        } else if (version === 'third') {
            return script.contentThird;
        }
        return script.content;
    };

    /** Batch cards keep content on each script object; never read studio editingScriptContent. */
    const getBatchScriptContent = (script, version) => {
        if (version === 'main') return script.content ?? '';
        if (version === 'alt') return script.contentAlt ?? '';
        if (version === 'third') return script.contentThird ?? '';
        return script.content ?? '';
    };


    // Batch Creation Functions
    const startBatchGenerate = async () => {
        try {
            // Fetch available hooks
            const hooksResponse = await api.getWorkspaceHooks(currentWorkspace._id);
            const hooks = hooksResponse.data.hooks || [];
            
            if (hooks.length === 0) {
                setModalErrorMessage('No hooks available. Please add hooks in the Studio tab first.');
                setShowErrorModal(true);
                return;
            }
            
            const isBoukar = currentUserId === '673247877e4ac0a824cbac0d';
            const totalRequested = Math.max(1, Math.min(10, Number(batchTotalVideos) || 10));
            const textRequested = isBoukar && batchIncludeTextBased
                ? Math.max(0, Math.min(totalRequested, Number(batchTextVideos) || 0))
                : 0;
            const scriptRequested = Math.max(0, totalRequested - textRequested);

            const voiceoverCount = Math.min(scriptRequested, Math.max(0, hooks.length));
            const textCount = isBoukar && batchIncludeTextBased ? (totalRequested - voiceoverCount) : 0;
            const hooksToUse = hooks.slice(0, voiceoverCount);
            
            trackEvent('batch_script_generation_started', {
                hookCount: hooksToUse.length,
                textCount,
                workspaceId: currentWorkspace?._id
            });
            
            setBatchGenerating(true);
            setBatchScripts([]);
            batchGenerationStartTime.current = Date.now(); // Track when batch generation started
            setBatchProgress({ current: 0, total: voiceoverCount || 1, percent: 0, currentStep: voiceoverCount ? 'Starting generation...' : 'Preparing text-based videos...' });
            
            if (voiceoverCount === 0) {
                // All text-based: no API call, just create text placeholders
                const textPlaceholders = Array.from({ length: textCount }, (_, i) => ({
                    _id: `batch-text-${Date.now()}-${i}`,
                    content: '',
                    contentAlt: '',
                    contentThird: '',
                    title: `Text-based video ${i + 1}`,
                    type: 'text',
                    selectedVersion: 'main',
                    description: ''
                }));
                setBatchScripts(textPlaceholders);
                setBatchGenerating(false);
                setBatchProgress({ current: textCount, total: textCount, percent: 100, currentStep: 'Done' });
                return;
            }
            
            const batchTopicForApi = (batchTopic || '').trim() || undefined;
            await api.generateWorkspaceScriptsAsync(currentWorkspace._id, {
                hooks: hooksToUse,
                topic: batchTopicForApi,
                includePublishedContext: false,
                creativityLevel: 3,
                language: generationLanguage,
                duration: batchScriptDuration,
                mainContentOnly: true // one LLM call per script; alt/third mirror main (avoids 3× Gemini quota on batch)
            });
            
            if (batchPollRef.current) clearInterval(batchPollRef.current);
            let polls = 0;
            batchPollRef.current = setInterval(async () => {
                try {
                    polls += 1;
                    const statusResp = await api.getWorkspaceScriptsStatus(currentWorkspace._id);
                    const status = statusResp.data;
                    
                    if (status.status === 'completed') {
                        clearInterval(batchPollRef.current);
                        batchPollRef.current = null;
                        let voiceoverOnly = scriptsFromGenerationStatus(status, { type: 'voiceover' });
                        if (voiceoverOnly.length === 0) {
                            try {
                                const startTime = batchGenerationStartTime.current;
                                voiceoverOnly = await fetchWorkspaceScriptsLite({
                                    type: 'voiceover',
                                    since: startTime ? new Date(startTime - 60000).toISOString() : undefined,
                                    limit: Math.max(voiceoverCount, 10),
                                });
                                voiceoverOnly = voiceoverOnly
                                    .filter((s) => {
                                        if (!startTime) return true;
                                        const scriptTime = new Date(s.createdAt).getTime();
                                        return scriptTime >= startTime - 60000;
                                    })
                                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                            } catch (e) {
                                setBatchGenerating(false);
                                setErrors(prev => [...prev, 'Failed to load generated scripts.']);
                                return;
                            }
                        }
                        const scriptsFromApi = voiceoverOnly.slice(0, voiceoverCount);
                        const desiredTotal = (isBoukar && batchIncludeTextBased) ? totalRequested : scriptsFromApi.length;
                        const textCount = Math.max(0, desiredTotal - scriptsFromApi.length);
                        const ts = Date.now();
                        const textPlaceholders = Array.from({ length: textCount }, (_, i) => ({
                            _id: `batch-text-${ts}-${i}`,
                            content: '',
                            contentAlt: '',
                            contentThird: '',
                            title: `Text-based video ${i + 1}`,
                            type: 'text',
                            description: '',
                            selectedVersion: 'main',
                            isCustom: true
                        }));
                        setBatchScripts([...scriptsFromApi, ...(textCount > 0 ? textPlaceholders : [])]);
                        setBatchGenerating(false);
                        setBatchProgress({ current: desiredTotal, total: desiredTotal, percent: 100 });
                        scriptsFromApi.forEach((script, i) => {
                            setTimeout(() => {
                                triggerBatchScriptGlow(script._id, 'script');
                                playCompletionSound('script_merra');
                            }, i * 200);
                        });
                    } else if (status.status === 'generating') {
                        const total = status.totalScripts || 1;
                        const current = status.currentScript || 0;
                        const backendPercent = Math.min(100, status.progress || Math.round((current / total) * 100));
                        const currentStep = status.currentStep || 'Generating...';
                        setBatchProgress({ current, total, percent: backendPercent, currentStep });
                    } else if (status.status === 'failed' || status.status === 'error') {
                        clearInterval(batchPollRef.current);
                        batchPollRef.current = null;
                        setBatchGenerating(false);
                        const errorMessage = status.error || 'Failed to generate scripts.';
                        setErrors(prev => [...prev, errorMessage]);
                    } else if (polls >= 120) {
                        clearInterval(batchPollRef.current);
                        batchPollRef.current = null;
                        setBatchGenerating(false);
                        setErrors(prev => [...prev, 'Timed out while generating scripts.']);
                    }
                } catch (err) {
                    if (batchPollRef.current && polls >= 30) {
                        clearInterval(batchPollRef.current);
                        batchPollRef.current = null;
                        setBatchGenerating(false);
                        setErrors(prev => [...prev, 'Failed to check script generation status.']);
                    }
                }
            }, 2000);
        } catch (e) {
            setBatchGenerating(false);
            setErrors(prev => [...prev, e.response?.data?.error || e.message]);
        }
    };

    const startBatchAutopilot = async () => {
        try {
            const hooksResponse = await api.getWorkspaceHooks(currentWorkspace._id);
            const hooks = hooksResponse.data.hooks || [];
            if (hooks.length === 0) {
                setModalErrorMessage('No hooks available. Please add hooks in the Studio tab first.');
                setShowErrorModal(true);
                return;
            }
            const isBoukar = currentUserId === '673247877e4ac0a824cbac0d';
            const totalRequested = Math.max(1, Math.min(10, Number(batchTotalVideos) || 10));
            const textRequested = isBoukar && batchIncludeTextBased
                ? Math.max(0, Math.min(totalRequested, Number(batchTextVideos) || 0))
                : 0;
            const scriptRequested = Math.max(0, totalRequested - textRequested);

            const voiceoverCount = Math.min(scriptRequested, Math.max(0, hooks.length));
            const textCount = isBoukar && batchIncludeTextBased ? (totalRequested - voiceoverCount) : 0;
            const hooksToUse = hooks.slice(0, voiceoverCount);
            trackEvent('batch_autopilot_started', { hookCount: hooksToUse.length, textCount, workspaceId: currentWorkspace?._id });
            setBatchAutopilotSessionActive(true);
            setBatchAutopilotThoughtSeconds(null);
            setBatchAutopilotActive(true);
            setBatchScripts([]);
            const startTime = Date.now();
            const addedScriptIdsRef = { current: new Set() };
            const TARGET = voiceoverCount;
            const POLL_MS = 2000;
            const TIMEOUT_MS = 10 * 60 * 1000;

            if (voiceoverCount === 0) {
                batchAutopilotStartedAtRef.current = Date.now();
                // All text-based: add text placeholders and auto-start save + video for each
                const ts = Date.now();
                const textPlaceholders = Array.from({ length: textCount }, (_, i) => ({
                    _id: `batch-text-${ts}-${i}`,
                    content: '',
                    contentAlt: '',
                    contentThird: '',
                    title: `Text-based video ${i + 1}`,
                    type: 'text',
                    description: '',
                    selectedVersion: 'main',
                    isCustom: true
                }));
                setBatchScripts(textPlaceholders);
                (function runNext(idx) {
                    if (idx >= textPlaceholders.length) return;
                    const textScript = textPlaceholders[idx];
                    handleBatchGenerateTextOverlay(textScript._id, (actualScriptId) => {
                        queueMicrotask(() => handleBatchGenerateVideo(actualScriptId, () => {}));
                        setTimeout(() => runNext(idx + 1), 400);
                    });
                })(0);
                setBatchAutopilotActive(false);
                return;
            }

            setBatchAutopilotVoiceoverTarget(voiceoverCount);
            batchAutopilotStartedAtRef.current = Date.now();

            const batchTopicForApi = (batchTopic || '').trim() || undefined;

            // Fire voiceoverCount script generations at once (no await)
            hooksToUse.forEach((hook, i) => {
                api.generateWorkspaceScriptsAsync(currentWorkspace._id, {
                    hooks: [hook],
                    topic: batchTopicForApi,
                    includePublishedContext: false,
                    creativityLevel: 3,
                    language: generationLanguage,
                    duration: batchScriptDuration,
                    mainContentOnly: true // single-hook path would otherwise run main+alt+third (3× quota per script)
                }).catch(err => {
                    setErrors(prev => [...prev, `Script ${i + 1} failed to start: ${err.response?.data?.error || err.message}`]);
                });
            });

            // Single poll loop: discover new scripts as they complete, add and start voiceover → video for each
            let polls = 0;
            const pollInterval = setInterval(async () => {
                if (addedScriptIdsRef.current.size >= TARGET || Date.now() - startTime > TIMEOUT_MS) {
                    clearInterval(pollInterval);
                    setBatchAutopilotActive(false);
                    return;
                }
                try {
                    polls++;
                    const all = await fetchWorkspaceScriptsLite({
                        type: 'voiceover',
                        since: new Date(startTime - 60000).toISOString(),
                        limit: Math.max(TARGET + 5, 15),
                    });
                    const recent = all
                        .filter((s) => new Date(s.createdAt).getTime() >= startTime - 60000)
                        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                    const newScripts = recent.filter(s => !addedScriptIdsRef.current.has(s._id));
                    if (newScripts.length === 0) return;
                    newScripts.forEach(s => addedScriptIdsRef.current.add(s._id));
                    setBatchScripts(prev => [...prev, ...newScripts]);
                    newScripts.forEach((script, idx) => {
                        setTimeout(() => {
                            triggerBatchScriptGlow(script._id, 'script');
                            playCompletionSound('script_merra');
                            handleBatchGenerateVoiceover(script._id, script, (voMeta) => {
                                const targetId = voMeta?.scriptId ?? script._id;
                                queueMicrotask(() => handleBatchGenerateVideo(targetId, () => {}, voMeta));
                            });
                        }, idx * 150);
                    });
                    if (addedScriptIdsRef.current.size >= TARGET) {
                        clearInterval(pollInterval);
                        // Append text-based placeholders so we have 10 cards total
                        if (textCount > 0) {
                            const ts = Date.now();
                            const textPlaceholders = Array.from({ length: textCount }, (_, i) => ({
                                _id: `batch-text-${ts}-${i}`,
                                content: '',
                                contentAlt: '',
                                contentThird: '',
                                title: `Text-based video ${i + 1}`,
                                type: 'text',
                                description: '',
                                selectedVersion: 'main',
                                isCustom: true
                            }));
                            setBatchScripts(prev => [...prev, ...textPlaceholders]);
                            // Autopilot: run overlay then video for each text card, one after the other (status is workspace-global)
                            (function runNext(idx) {
                                if (idx >= textPlaceholders.length) return;
                                const textScript = textPlaceholders[idx];
                                handleBatchGenerateTextOverlay(textScript._id, (actualScriptId) => {
                                    queueMicrotask(() => handleBatchGenerateVideo(actualScriptId, () => {}));
                                    setTimeout(() => runNext(idx + 1), 400);
                                });
                            })(0);
                        }
                        setBatchAutopilotActive(false);
                    }
                } catch (err) {
                    if (polls >= 90) {
                        clearInterval(pollInterval);
                        setBatchAutopilotActive(false);
                        setErrors(prev => [...prev, 'Autopilot: failed to fetch scripts.']);
                    }
                }
            }, POLL_MS);
        } catch (e) {
            setErrors(prev => [...prev, e.response?.data?.error || e.message]);
            setBatchAutopilotActive(false);
            setBatchAutopilotSessionActive(false);
            batchAutopilotStartedAtRef.current = null;
        }
    };

    const handleBatchGenerateVoiceover = async (scriptId, scriptOverride, onVoiceoverComplete) => {
        const script = scriptOverride ?? batchScripts.find(s => s._id === scriptId);
        if (!script) return;
        
        try {
            setBatchGeneratingVoiceovers(prev => new Set(prev).add(scriptId));
            
            // Build request data — always send latest edited script text (same as single-script studio)
            const currentContent = getBatchScriptContent(script, 'main');
            const currentContentAlt = getBatchScriptContent(script, 'alt');
            const currentContentThird = getBatchScriptContent(script, 'third');
            const requestData = {
                voiceId: studioVoiceId,
                voiceGenerationMode: voiceGenerationMode,
                content: currentContent,
                contentAlt: currentContentAlt,
                contentThird: currentContentThird,
            };
            if (script.isCustom) {
                requestData.title = script.title || 'Custom Batch Script';
            }

            const response = await api.generateWorkspaceVoiceover(currentWorkspace._id, scriptId, requestData);
            
            // If this was a custom script, update with the new database ID
            if (script.isCustom && response.data.script) {
                const newScriptId = response.data.script._id;
                const oldScriptId = scriptId;
                
                // Update batch script with new ID and voiceover data
                setBatchScripts(prev => prev.map(s => 
                    s._id === oldScriptId 
                        ? { 
                            ...s, 
                            _id: newScriptId, 
                            isCustom: false,
                            voiceoverUrl: response.data.voiceoverUrl, 
                            voiceoverTimestamps: response.data.voiceoverTimestamps 
                        }
                        : s
                ));
                
                // Migrate music selections if any
                setBatchScriptMusicSelections(prev => {
                    if (prev[oldScriptId]) {
                        const newSelections = { ...prev };
                        newSelections[newScriptId] = prev[oldScriptId];
                        delete newSelections[oldScriptId];
                        return newSelections;
                    }
                    return prev;
                });
                
                // Migrate show dropdown state if any
                setBatchShowMusicDropdowns(prev => {
                    if (prev[oldScriptId]) {
                        const newDropdowns = { ...prev };
                        newDropdowns[newScriptId] = prev[oldScriptId];
                        delete newDropdowns[oldScriptId];
                        return newDropdowns;
                    }
                    return prev;
                });
                triggerBatchScriptGlow(newScriptId, 'voiceover');
                playCompletionSound('voice_merra');
                if (typeof onVoiceoverComplete === 'function') {
                    onVoiceoverComplete({
                        scriptId: newScriptId,
                        previousScriptId: oldScriptId,
                        voiceoverUrl: response.data.voiceoverUrl,
                        voiceoverTimestamps: response.data.voiceoverTimestamps
                    });
                }
            } else if (response.data.voiceoverUrl) {
                setBatchScripts(prev => prev.map(s => 
                    s._id === scriptId 
                        ? { ...s, voiceoverUrl: response.data.voiceoverUrl, voiceoverTimestamps: response.data.voiceoverTimestamps }
                        : s
                ));
                triggerBatchScriptGlow(scriptId, 'voiceover');
                playCompletionSound('voice_merra');
                if (typeof onVoiceoverComplete === 'function') {
                    onVoiceoverComplete({
                        scriptId,
                        voiceoverUrl: response.data.voiceoverUrl,
                        voiceoverTimestamps: response.data.voiceoverTimestamps
                    });
                }
            }
        } catch (error) {
            console.error('Error generating voiceover:', error);
            setErrors(prev => [...prev, 'Failed to generate voiceover: ' + (error.response?.data?.error || error.message)]);
        } finally {
            setBatchGeneratingVoiceovers(prev => {
                const next = new Set(prev);
                next.delete(scriptId);
                return next;
            });
        }
    };

    const handleBatchGenerateVideo = async (scriptId, onVideoComplete, voMeta = null, orderedCutIdsOverride = null, exportExtras = null) => {
        const resolvedId = voMeta?.scriptId ?? scriptId;
        let script = batchScriptsRef.current.find(s => s._id === resolvedId)
            || (voMeta?.previousScriptId && batchScriptsRef.current.find(s => s._id === voMeta.previousScriptId))
            || batchScriptsRef.current.find(s => s._id === scriptId);
        if (!script && voMeta?.voiceoverUrl) {
            script = {
                _id: resolvedId,
                type: 'voiceover',
                voiceoverUrl: voMeta.voiceoverUrl,
                voiceoverTimestamps: voMeta.voiceoverTimestamps
            };
        }
        if (!script) {
            console.warn('[batch] handleBatchGenerateVideo: script not found', { scriptId, voMeta });
            return;
        }

        let actualScriptId = resolvedId;
        let scriptToUse = voMeta?.voiceoverUrl
            ? { ...script, _id: resolvedId, voiceoverUrl: voMeta.voiceoverUrl, voiceoverTimestamps: voMeta.voiceoverTimestamps }
            : (resolvedId !== script._id ? { ...script, _id: resolvedId } : { ...script });

        // Text-based placeholder: save to DB first so backend has a real script
        if (script.type === 'text' && String(scriptId).startsWith('batch-text-')) {
            try {
                const saveRes = await api.saveCustomScript(currentWorkspace._id, {
                    content: getBatchScriptContent(script, 'main') || '',
                    description: script.description || '',
                    title: script.title || 'Text-based video',
                    type: 'text'
                });
                const saved = saveRes.data?.script;
                if (!saved || !saved._id) {
                    setErrors(prev => [...prev, 'Failed to save text script.']);
                    return;
                }
                actualScriptId = saved._id;
                scriptToUse = { ...script, _id: saved._id, content: saved.content, description: saved.description };
                setBatchScripts(prev => prev.map(s => s._id === scriptId ? scriptToUse : s));
                setBatchTextSelectedCutByScriptId(prev => {
                    const cut = prev[scriptId];
                    if (!cut) return prev;
                    const next = { ...prev }; next[actualScriptId] = cut; delete next[scriptId]; return next;
                });
                setBatchScriptMusicSelections(prev => prev[scriptId] != null ? { ...prev, [actualScriptId]: prev[scriptId], [scriptId]: undefined } : prev);
                setBatchThumbnailByScriptId(prev => prev[scriptId] != null ? { ...prev, [actualScriptId]: prev[scriptId], [scriptId]: undefined } : prev);
                setBatchVideoSettingsOverrideByScriptId(prev => rekeyByScriptId(prev, scriptId, actualScriptId));
            } catch (e) {
                setErrors(prev => [...prev, 'Failed to save text script: ' + (e.response?.data?.error || e.message)]);
                return;
            }
        }

        try {
            const extras = exportExtras || newNleExportExtrasRef.current || null;
            if (newNleExportExtrasRef.current) newNleExportExtrasRef.current = null;
            const orderedCutIdsToUse = Array.isArray(orderedCutIdsOverride) && orderedCutIdsOverride.length > 0
                ? orderedCutIdsOverride.map((id) => String(id)).filter(Boolean)
                : null;
            const hasExportSfxCues = !!extras?.sfxCues?.length;
            const perVideoSettings = batchVideoSettingsOverrideRef.current[actualScriptId]
                || batchVideoSettingsOverrideRef.current[scriptId]
                || null;
            const videoSettings = resolveBatchVideoSettings({
                perVideo: perVideoSettings,
                globals: batchVideoSettingsGlobalsRef.current,
                extras,
            });
            const isBoukarVideo = currentUserId === BOUKAR_USER_ID;
            const planAiSfx = isBoukarVideo && videoSettings.aiSoundEffects === true && !hasExportSfxCues && scriptToUse.type !== 'text';

            setBatchGeneratingVideos(prev => new Set(prev).add(actualScriptId));
            setBatchVideoProgressByScript(prev => ({ ...prev, [actualScriptId]: planAiSfx ? 2 : 0 }));
            
            // Determine music selection for this script
            let musicIdToUse = null;
            let musicNameToUse = null;
            const useMusicForExport = !!videoSettings.useMusic;
            if (useMusicForExport) {
                if (extras?.selectedMusicId) {
                    musicIdToUse = extras.selectedMusicId;
                    const selectedMusic = availableMusic.find(m => String(m._id) === String(extras.selectedMusicId));
                    musicNameToUse = selectedMusic?.name || null;
                } else {
                    const musicSelection = batchScriptMusicSelections[actualScriptId] || batchScriptMusicSelections[scriptId];
                    if (musicSelection && musicSelection.musicId) {
                        musicIdToUse = musicSelection.musicId;
                        musicNameToUse = musicSelection.musicName;
                    } else if (availableMusic.length > 0) {
                        const randomIndex = Math.floor(Math.random() * availableMusic.length);
                        musicIdToUse = availableMusic[randomIndex]._id;
                        musicNameToUse = availableMusic[randomIndex].name;
                        setBatchScriptMusicSelections(prev => ({
                            ...prev,
                            [actualScriptId]: { musicId: musicIdToUse, musicName: musicNameToUse, isRandom: true }
                        }));
                    }
                }
            }
            
            const selectedCutForText = scriptToUse.type === 'text' ? (batchTextSelectedCutByScriptId[actualScriptId] || batchTextSelectedCutByScriptId[scriptId] ? String((batchTextSelectedCutByScriptId[actualScriptId] || batchTextSelectedCutByScriptId[scriptId])._id || (batchTextSelectedCutByScriptId[actualScriptId] || batchTextSelectedCutByScriptId[scriptId]).id) : undefined) : undefined;
            const response = await api.generateWorkspaceVideo(currentWorkspace._id, actualScriptId, {
                voiceoverUrl: scriptToUse.voiceoverUrl,
                voiceoverTimestamps: scriptToUse.voiceoverTimestamps,
                selectedMusicId: musicIdToUse,
                selectedCutId: selectedCutForText,
                fontId: videoSettings.selectedFontId || undefined,
                includeTags: includeTags.length > 0 ? includeTags.join(',') : undefined,
                excludeTags: excludeTags.length > 0 ? excludeTags.join(',') : undefined,
                slowMotion: slowMotion,
                cutSilenceMode: isBoukarVideo ? !!videoSettings.cutSilenceMode : false,
                cutMoreSilenceMode: isBoukarVideo && !!videoSettings.cutMoreSilenceMode,
                aiSoundEffects: planAiSfx,
                speedUp: isBoukarVideo && !!videoSettings.speedUp,
                fastPacing: !!videoSettings.fastPacing,
                bigText: !!videoSettings.bigText,
                zoomWhoosh: scriptToUse.type === 'text' ? false : zoomWhoosh,
                flipbookIntro: scriptToUse.type === 'text' ? false : !!videoSettings.flipbookIntro,
                openingLineInCenter: isBoukarVideo ? !!videoSettings.openingLineInCenter : false,
                openingLineTextSize: videoSettings.openingLineTextSize || 'big',
                ...openingLineFontFields(videoSettings, userFonts, isBoukarVideo),
                openingLineFontColor: isBoukarVideo && videoSettings.openingLineInCenter && videoSettings.openingLineFontColor
                    ? videoSettings.openingLineFontColor
                    : undefined,
                glowText: !!videoSettings.glowText,
                glowTextColor: videoSettings.glowTextColor || 'white',
                subtitlePosition: videoSettings.subtitlePosition || 'bottom',
                enableSubtitles: scriptToUse.type === 'text' ? true : !!videoSettings.enableSubtitles,
                oneWordPerSubtitle: !!videoSettings.oneWordPerSubtitle,
                fontMixing: isBoukarVideo && scriptToUse.type !== 'text' ? !!videoSettings.fontMixing : false,
                centerStackOpening: isBoukarVideo ? !!videoSettings.centerStackOpening : false,
                centerStackLineByLine: isBoukarVideo ? !!videoSettings.centerStackLineByLine : false,
                boukisIntro: videoSettings.boukisIntro || undefined,
                boukisIntroPitch: videoSettings.boukisIntro ? videoSettings.boukisIntroPitch : undefined,
                orderedCutIds: orderedCutIdsToUse || undefined,
                ...(hasExportSfxCues ? { sfxCues: extras.sfxCues } : {}),
                subtitleFontColor: videoSettings.subtitleFontColor,
                subtitleOutlineColor: videoSettings.subtitleOutlineColor,
                subtitleOutlineEnabled: !!videoSettings.subtitleOutlineEnabled,
                ...((perVideoSettings || extras?.fontMixingSecondColor)
                    ? { fontMixingSecondColor: videoSettings.fontMixingSecondColor }
                    : {}),
                thumbnailConfig: scriptToUse.type === 'text' ? undefined : (() => {
                    const perScript = batchThumbnailByScriptId[actualScriptId] || batchThumbnailByScriptId[scriptId];
                    if (perScript?.cut && perScript?.title?.trim()) return buildThumbnailConfigPayload(perScript.cut, perScript.title, perScript.bigText !== false);
                    return undefined;
                })()
            });
            
            // Start polling for video status
            const start = Date.now();
            const TIMEOUT_MS = 10 * 60 * 1000;
            const pollInterval = setInterval(async () => {
                try {
                    if (Date.now() - start > TIMEOUT_MS) {
                        clearInterval(pollInterval);
                        if (typeof onVideoComplete === 'function') onVideoComplete();
                        setBatchVideoProgressByScript(prev => { const next = { ...prev }; delete next[actualScriptId]; return next; });
                        setBatchGeneratingVideos(prev => {
                            const next = new Set(prev);
                            next.delete(actualScriptId);
                            return next;
                        });
                        setErrors(prev => [...prev, 'Video generation timed out.']);
                        return;
                    }
                    const statusResp = await api.getWorkspaceVideoStatus(currentWorkspace._id, actualScriptId);
                    const { status, progress, videoUrl } = statusResp.data;
                    setBatchVideoProgressByScript(prev => ({ ...prev, [actualScriptId]: progress ?? prev[actualScriptId] ?? 0 }));
                    if ((status === 'draft' && videoUrl) || status === 'error') {
                        clearInterval(pollInterval);
                        if (typeof onVideoComplete === 'function') onVideoComplete();
                        setBatchVideoProgressByScript(prev => { const next = { ...prev }; delete next[actualScriptId]; return next; });
                        if (status === 'draft' && videoUrl) {
                            setBatchScripts(prev => prev.map(s => 
                                s._id === actualScriptId 
                                    ? { ...s, videoUrl, status: 'draft', progress: 100 }
                                    : s
                            ));
                            triggerBatchScriptGlow(actualScriptId, 'video');
                            playCompletionSound('video_merra');
                            // Refresh usedCutIds so Timeline/NLE reopens with the latest order
                            api.getWorkspaceScript(currentWorkspace._id, actualScriptId).then((resp) => {
                                const updated = resp.data?.script || resp.data;
                                if (!updated) return;
                                setBatchScripts((prev) => prev.map((s) =>
                                    String(s._id) === String(actualScriptId)
                                        ? { ...s, ...updated, videoUrl: updated.videoUrl || videoUrl }
                                        : s
                                ));
                            }).catch((e) => console.warn('Failed to refetch batch script after video generation:', e));
                            // If music was random, keep it for regeneration; if selected, keep the selection
                            const musicSelection = batchScriptMusicSelections[actualScriptId];
                            if (musicSelection && musicSelection.isRandom) {
                                // Keep the random selection for next regeneration
                                setBatchScriptMusicSelections(prev => ({
                                    ...prev,
                                    [actualScriptId]: { ...musicSelection, isRandom: true }
                                }));
                            }
                        }
                        setBatchGeneratingVideos(prev => {
                            const next = new Set(prev);
                            next.delete(actualScriptId);
                            return next;
                        });
                    }
                } catch (error) {
                    console.error('Error checking video status:', error);
                }
            }, 2000);
        } catch (error) {
            console.error('Error generating video:', error);
            if (typeof onVideoComplete === 'function') onVideoComplete();
            setBatchVideoProgressByScript(prev => { const next = { ...prev }; delete next[actualScriptId]; return next; });
            setErrors(prev => [...prev, 'Failed to generate video: ' + (error.response?.data?.error || error.message)]);
            setBatchGeneratingVideos(prev => {
                const next = new Set(prev);
                next.delete(actualScriptId);
                return next;
            });
        }
    };

    const handleBatchGenerateTextOverlay = async (scriptId, onComplete) => {
        const script = batchScripts.find(s => s._id === scriptId);
        if (!script || script.type !== 'text') return;
        let actualScriptId = scriptId;
        let scriptToUpdate = script;
        const isPlaceholder = String(scriptId).startsWith('batch-text-');
        if (isPlaceholder) {
            try {
                const saveRes = await api.saveCustomScript(currentWorkspace._id, {
                    content: getBatchScriptContent(script, 'main') || '',
                    description: script.description || '',
                    title: script.title || 'Text-based video',
                    type: 'text'
                });
                const saved = saveRes.data?.script;
                if (!saved?._id) {
                    setErrors(prev => [...prev, 'Failed to save text script.']);
                    return;
                }
                actualScriptId = saved._id;
                scriptToUpdate = { ...script, _id: saved._id, content: saved.content, description: saved.description };
                setBatchScripts(prev => prev.map(s => s._id === scriptId ? scriptToUpdate : s));
                setBatchTextSelectedCutByScriptId(prev => {
                    const cut = prev[scriptId];
                    if (!cut) return prev;
                    const next = { ...prev }; next[actualScriptId] = cut; delete next[scriptId]; return next;
                });
                setBatchScriptMusicSelections(prev => prev[scriptId] != null ? { ...prev, [actualScriptId]: prev[scriptId], [scriptId]: undefined } : prev);
                setBatchThumbnailByScriptId(prev => prev[scriptId] != null ? { ...prev, [actualScriptId]: prev[scriptId], [scriptId]: undefined } : prev);
                setBatchVideoSettingsOverrideByScriptId(prev => rekeyByScriptId(prev, scriptId, actualScriptId));
            } catch (e) {
                setErrors(prev => [...prev, 'Failed to save text script: ' + (e.response?.data?.error || e.message)]);
                return;
            }
        }
        setBatchGeneratingTextOverlay(prev => new Set(prev).add(actualScriptId));
        try {
            await api.regenerateWorkspaceScript(currentWorkspace._id, actualScriptId, {});
            const start = Date.now();
            const TIMEOUT_MS = 2 * 60 * 1000;
            const pollInterval = setInterval(async () => {
                try {
                    if (Date.now() - start > TIMEOUT_MS) {
                        clearInterval(pollInterval);
                        setBatchGeneratingTextOverlay(prev => { const n = new Set(prev); n.delete(actualScriptId); return n; });
                        setErrors(prev => [...prev, 'Generate text overlay timed out.']);
                        return;
                    }
                    const statusResp = await api.getWorkspaceScriptsStatus(currentWorkspace._id);
                    const status = statusResp.data?.status;
                    if (status === 'completed') {
                        clearInterval(pollInterval);
                        setBatchGeneratingTextOverlay(prev => { const n = new Set(prev); n.delete(actualScriptId); return n; });
                        const statusData = statusResp.data;
                        let updated = statusData?.scripts?.find((s) => String(s._id) === String(actualScriptId))
                            || statusData?.generatedScripts?.find((s) => String(s._id) === String(actualScriptId));
                        if (!updated) {
                            try {
                                const scriptResp = await api.getWorkspaceScript(currentWorkspace._id, actualScriptId);
                                updated = scriptResp.data?.script || scriptResp.data;
                            } catch (_) { /* ignore */ }
                        }
                        if (updated) {
                            setBatchScripts(prev => prev.map(s => s._id === actualScriptId ? { ...s, content: updated.content, description: updated.description } : s));
                        }
                        if (typeof onComplete === 'function') onComplete(actualScriptId);
                    } else if (status === 'failed') {
                        clearInterval(pollInterval);
                        setBatchGeneratingTextOverlay(prev => { const n = new Set(prev); n.delete(actualScriptId); return n; });
                        setErrors(prev => [...prev, 'Generate text overlay failed.']);
                    }
                } catch (err) {
                    clearInterval(pollInterval);
                    setBatchGeneratingTextOverlay(prev => { const n = new Set(prev); n.delete(actualScriptId); return n; });
                }
            }, 2000);
        } catch (e) {
            setBatchGeneratingTextOverlay(prev => { const n = new Set(prev); n.delete(actualScriptId); return n; });
            setErrors(prev => [...prev, 'Failed to start text overlay generation: ' + (e.response?.data?.error || e.message)]);
        }
    };

    const handleBatchGenerateDescription = async (scriptId) => {
        const script = batchScripts.find(s => s._id === scriptId);
        if (!script || script.type !== 'text') return;
        if (String(scriptId).startsWith('batch-text-')) {
            setErrors(prev => [...prev, 'Save the script first (e.g. by generating text overlay or generating video).']);
            return;
        }
        setBatchGeneratingDescription(prev => new Set(prev).add(scriptId));
        try {
            const res = await api.regenerateWorkspaceDescription(currentWorkspace._id, scriptId, {});
            const newDescription = res.data?.description;
            if (newDescription != null) {
                setBatchScripts(prev => prev.map(s => s._id === scriptId ? { ...s, description: newDescription } : s));
            }
        } catch (e) {
            setErrors(prev => [...prev, 'Failed to generate description: ' + (e.response?.data?.error || e.message)]);
        } finally {
            setBatchGeneratingDescription(prev => { const n = new Set(prev); n.delete(scriptId); return n; });
        }
    };

    const resetTextStudioState = useCallback(() => {
        textStudioOverlayCancelledRef.current = true;
        textStudioVideoCancelledRef.current = true;
        if (textStudioOverlayPollRef.current) {
            clearInterval(textStudioOverlayPollRef.current);
            textStudioOverlayPollRef.current = null;
        }
        if (textStudioVideoPollRef.current) {
            clearInterval(textStudioVideoPollRef.current);
            textStudioVideoPollRef.current = null;
        }
        setTextStudioScriptId(null);
        setTextStudioContent('');
        setTextStudioDescription('');
        setTextStudioSelectedCut(null);
        setTextStudioGeneratingOverlay(false);
        setTextStudioGeneratingDescription(false);
        setTextStudioGeneratingVideo(false);
        setTextStudioVideoProgress(0);
        setTextStudioVideoUrl('');
        setTextStudioShowCutModal(false);
        setTextStudioCuts([]);
        setTextStudioCutsLoading(false);
        setTextStudioHook('');
        setTextStudioTopic('');
        setTextStudioShowHookInput(false);
        setTextStudioShowTopicInput(true);
        setTextStudioContentUnlocked(false);
        setTextStudioPublishingVideo(false);
        setTextStudioVideoSaved(false);
    }, []);

    useEffect(() => {
        resetTextStudioState();
    }, [currentWorkspace?._id, resetTextStudioState]);

    useEffect(() => {
        if (studioSubTab !== 'text-on-screen' || textStudioHook || availableHooks.length === 0) return;
        const randomIndex = Math.floor(Math.random() * availableHooks.length);
        setTextStudioHook(availableHooks[randomIndex]);
    }, [studioSubTab, availableHooks, textStudioHook]);

    const selectRandomTextStudioHook = () => {
        if (availableHooks.length > 0) {
            const randomIndex = Math.floor(Math.random() * availableHooks.length);
            setTextStudioHook(availableHooks[randomIndex]);
        }
    };

    const handleTextStudioUseOwnText = () => {
        setTextStudioContentUnlocked(true);
    };

    const ensureTextStudioScriptId = async () => {
        if (!currentWorkspace?._id) return null;
        const trimmedContent = String(textStudioContent || '').trim();
        if (textStudioScriptId) {
            await api.updateWorkspaceScriptContent(
                currentWorkspace._id,
                textStudioScriptId,
                'main',
                trimmedContent || undefined,
                { description: textStudioDescription, hook: textStudioHook }
            );
            return textStudioScriptId;
        }
        const saveRes = await api.saveCustomScript(currentWorkspace._id, {
            content: trimmedContent || undefined,
            description: textStudioDescription || '',
            hook: textStudioHook || '',
            title: 'Text on screen video',
            type: 'text',
        });
        const saved = saveRes.data?.script;
        if (!saved?._id) throw new Error('Failed to save text script');
        setTextStudioScriptId(saved._id);
        if (saved.content) setTextStudioContent(saved.content);
        if (saved.description) setTextStudioDescription(saved.description);
        if (saved.hook) setTextStudioHook(saved.hook);
        return saved._id;
    };

    const applyTextStudioScriptPayload = (script) => {
        if (!script) return false;
        const id = script._id != null ? String(script._id) : null;
        if (id) setTextStudioScriptId(id);
        const content = script.content;
        if (content != null && String(content).trim() && content !== '.') {
            setTextStudioContent(content);
        }
        if (script.description != null) setTextStudioDescription(script.description);
        if (script.hook != null) setTextStudioHook(script.hook);
        if (script.videoUrl) setTextStudioVideoUrl(script.videoUrl);
        return Boolean(content != null && String(content).trim() && content !== '.');
    };

    /** Open an existing VideoScript (Home "project") in Studio for editing.
     *  Voiceover/video live on the VideoScript document (voiceoverUrl / videoUrl) and are restored into Studio state.
     *  @param {string} scriptId
     *  @param {object|null} snapshot optional lite project from the Home list (used as fallback while/if fetch is partial)
     */
    const openStudioProject = async (scriptId, snapshot = null) => {
        if (!currentWorkspace?._id || !scriptId) return;
        try {
            const resp = await api.getWorkspaceScript(currentWorkspace._id, scriptId);
            const fetched = resp.data?.script || resp.data || null;
            // Merge snapshot first so list fields (e.g. voiceoverUrl) are kept if fetch omits them
            const script = {
                ...(snapshot && typeof snapshot === 'object' ? snapshot : {}),
                ...(fetched && typeof fetched === 'object' ? fetched : {}),
            };
            if (!script?._id) {
                setErrors((prev) => [...prev, 'Failed to load project']);
                return;
            }

            // Normalize id to string for consistent comparisons in Studio UI
            script._id = String(script._id);
            const voiceoverUrl = String(script.voiceoverUrl || '').trim();
            const videoUrl = String(script.videoUrl || '').trim();

            const isNoScriptProject = script.hook === 'no-script-generation';
            const isTextProject = script.type === 'text';

            setEditingScriptContent({});
            setStudioGenerating(false);
            setStudioGeneratingVoice(false);
            setStudioGeneratingVideo(false);
            setStudioRegeneratingScript(false);
            setStudioManualVoiceoverRequested(false);
            setStudioVideoProgress(0);
            exitStudioAutopilotMode();

            if (isTextProject) {
                setIsNoScriptMode(false);
                setNoScriptScriptId(null);
                setStudioScripts([]);
                setStudioSelectedScriptId(null);
                setStudioVoiceoverUrl('');
                setStudioVoiceoverScriptContent(null);
                setScriptChangedSinceVoiceover(false);
                setStudioVideoUrl('');
                setStudioVideoUrlTimestamp(0);
                setStudioVideoSaved(false);
                setVoiceoverDuration(0);
                lastStudioVideoCompletionUrlRef.current = null;
                lastVoiceoverTimestampsRef.current = null;
                lastVoiceoverUrlForTimestampsRef.current = null;

                setStudioSubTab('text-on-screen');
                try { localStorage.setItem('workspace_studio_sub_tab', 'text-on-screen'); } catch (_) {}
                applyTextStudioScriptPayload({ ...script, videoUrl: videoUrl || script.videoUrl });
                setTextStudioContentUnlocked(true);
                setTextStudioVideoSaved(!!videoUrl);
                if (script.selectedCutId) {
                    const cutId = String(script.selectedCutId);
                    const fromList = (workspaceCuts || []).find((c) => String(c._id) === cutId);
                    if (fromList) {
                        setTextStudioSelectedCut(fromList);
                    } else {
                        try {
                            const cutsRes = await api.getWorkspaceCuts(currentWorkspace._id, { ids: cutId, pageSize: 1 });
                            const items = cutsRes.data?.items || (Array.isArray(cutsRes.data) ? cutsRes.data : []);
                            if (items[0]) setTextStudioSelectedCut(items[0]);
                        } catch (_) { /* ignore */ }
                    }
                } else {
                    setTextStudioSelectedCut(null);
                }
            } else if (isNoScriptProject) {
                resetTextStudioState();
                setStudioSubTab('visual-only');
                try { localStorage.setItem('workspace_studio_sub_tab', 'visual-only'); } catch (_) {}
                setStudioScripts([]);
                setStudioSelectedScriptId(null);
                setStudioVoiceoverUrl('');
                setStudioVoiceoverScriptContent(null);
                setScriptChangedSinceVoiceover(false);
                setIsNoScriptMode(true);
                setNoScriptScriptId(script._id);
                setStudioVideoUrl(videoUrl);
                setStudioVideoUrlTimestamp(videoUrl ? Date.now() : 0);
                setStudioVideoSaved(!!videoUrl);
                setVoiceoverDuration(0);
                lastStudioVideoCompletionUrlRef.current = videoUrl || null;
                lastVoiceoverTimestampsRef.current = null;
                lastVoiceoverUrlForTimestampsRef.current = null;
            } else {
                resetTextStudioState();
                setStudioSubTab('vlog');
                try { localStorage.setItem('workspace_studio_sub_tab', 'vlog'); } catch (_) {}
                setIsNoScriptMode(false);
                setNoScriptScriptId(null);
                setStudioScripts([{ ...script, voiceoverUrl: voiceoverUrl || null, videoUrl: videoUrl || null }]);
                setStudioSelectedScriptId(script._id);

                setStudioVoiceoverUrl(voiceoverUrl);
                if (voiceoverUrl) {
                    setStudioVoiceoverScriptContent({
                        main: script.content || '',
                        alt: script.contentAlt || '',
                        third: script.contentThird || '',
                        scriptId: script._id,
                    });
                } else {
                    setStudioVoiceoverScriptContent(null);
                }
                setScriptChangedSinceVoiceover(false);

                if (script.voiceoverTimestamps && typeof script.voiceoverTimestamps === 'object') {
                    lastVoiceoverTimestampsRef.current = script.voiceoverTimestamps;
                    lastVoiceoverUrlForTimestampsRef.current = voiceoverUrl || null;
                    const voSec = getVoiceoverSecondsFromTimestamps(script.voiceoverTimestamps);
                    if (voSec > 0) setVoiceoverDuration(voSec);
                } else {
                    lastVoiceoverTimestampsRef.current = null;
                    lastVoiceoverUrlForTimestampsRef.current = null;
                    setVoiceoverDuration(0);
                }

                setStudioVideoUrl(videoUrl);
                setStudioVideoUrlTimestamp(videoUrl ? Date.now() : 0);
                setStudioVideoSaved(!!videoUrl);
                lastStudioVideoCompletionUrlRef.current = videoUrl || null;
            }

            trackEvent('workspace_home_open_project', {
                workspaceId: currentWorkspace._id,
                scriptId: script._id,
                type: script.type,
                isNoScript: isNoScriptProject,
                hasVoiceover: !!voiceoverUrl,
                hasVideo: !!videoUrl,
            });

            setActiveTab('studio');
            const tabContent = document.querySelector('.workspace-management-content');
            if (tabContent) tabContent.scrollTop = 0;
        } catch (err) {
            console.error('Failed to open studio project:', err);
            setErrors((prev) => [...prev, 'Failed to open project: ' + (err.response?.data?.error || err.message)]);
        }
    };

    const resolveScriptFromStatusPayload = (statusData, scriptId) => {
        if (!statusData) return null;
        const targetId = String(scriptId);
        const fromList = Array.isArray(statusData.scripts)
            ? statusData.scripts.find((s) => String(s._id) === targetId)
            : null;
        if (fromList) return fromList;
        if (String(statusData.scriptId) === targetId && statusData.generatedScripts?.[0]) {
            return statusData.generatedScripts[0];
        }
        const fromGenerated = statusData.generatedScripts?.find((s) => String(s._id) === targetId);
        return fromGenerated || statusData.generatedScripts?.[0] || null;
    };

    const refreshTextStudioFromServer = async (scriptId) => {
        const scriptResp = await api.getWorkspaceScript(currentWorkspace._id, scriptId);
        const script = scriptResp.data?.script || scriptResp.data;
        applyTextStudioScriptPayload(script);
    };

    const pollWorkspaceScriptJob = (scriptId, onDone, onFail, { forTextStudio = false } = {}) => {
        const start = Date.now();
        const TIMEOUT_MS = 2 * 60 * 1000;
        const pollInterval = setInterval(async () => {
            try {
                if (Date.now() - start > TIMEOUT_MS) {
                    clearInterval(pollInterval);
                    onFail('Timed out');
                    return;
                }
                const statusResp = await api.getWorkspaceScriptsStatus(currentWorkspace._id);
                const statusData = statusResp.data;
                const status = statusData?.status;
                if (status === 'completed') {
                    clearInterval(pollInterval);
                    const fromStatus = resolveScriptFromStatusPayload(statusData, scriptId);
                    if (forTextStudio) {
                        if (fromStatus && applyTextStudioScriptPayload(fromStatus)) {
                            onDone(fromStatus);
                            return;
                        }
                        try {
                            await refreshTextStudioFromServer(scriptId);
                            onDone();
                        } catch (fetchErr) {
                            onFail(fetchErr.message || 'Failed to load generated script');
                        }
                        return;
                    }
                    onDone(fromStatus);
                } else if (status === 'failed' || status === 'error') {
                    clearInterval(pollInterval);
                    onFail('Generation failed');
                }
            } catch (err) {
                clearInterval(pollInterval);
                onFail(err.message || 'Generation failed');
            }
        }, 2000);
        return pollInterval;
    };

    const handleTextStudioGenerateOverlay = async () => {
        if (!currentWorkspace?._id) return;
        textStudioOverlayCancelledRef.current = false;
        if (textStudioOverlayPollRef.current) {
            clearInterval(textStudioOverlayPollRef.current);
            textStudioOverlayPollRef.current = null;
        }
        setTextStudioGeneratingOverlay(true);
        try {
            const scriptId = await ensureTextStudioScriptId();
            if (textStudioOverlayCancelledRef.current) return;
            await api.regenerateWorkspaceScript(currentWorkspace._id, scriptId, {
                topic: textStudioTopic.trim() || undefined,
                hook: textStudioHook || undefined,
                language: generationLanguage,
            });
            if (textStudioOverlayCancelledRef.current) return;
            textStudioOverlayPollRef.current = pollWorkspaceScriptJob(
                scriptId,
                () => {
                    textStudioOverlayPollRef.current = null;
                    if (textStudioOverlayCancelledRef.current) return;
                    setTextStudioContentUnlocked(true);
                    setTextStudioGeneratingOverlay(false);
                },
                (msg) => {
                    textStudioOverlayPollRef.current = null;
                    if (textStudioOverlayCancelledRef.current) return;
                    setTextStudioGeneratingOverlay(false);
                    setErrors(prev => [...prev, `Generate text overlay failed: ${msg}`]);
                },
                { forTextStudio: true }
            );
        } catch (e) {
            if (textStudioOverlayCancelledRef.current) return;
            setTextStudioGeneratingOverlay(false);
            setErrors(prev => [...prev, 'Failed to generate text overlay: ' + (e.response?.data?.error || e.message)]);
        }
    };

    const handleTextStudioGenerateDescription = async () => {
        if (!currentWorkspace?._id) return;
        setTextStudioGeneratingDescription(true);
        try {
            const scriptId = await ensureTextStudioScriptId();
            const res = await api.regenerateWorkspaceDescription(currentWorkspace._id, scriptId, {});
            if (res.data?.description != null) setTextStudioDescription(res.data.description);
        } catch (e) {
            setErrors(prev => [...prev, 'Failed to generate description: ' + (e.response?.data?.error || e.message)]);
        } finally {
            setTextStudioGeneratingDescription(false);
        }
    };

    const ensureTextStudioCutsLoaded = async () => {
        if (textStudioCuts.length > 0) return textStudioCuts;
        setTextStudioCutsLoading(true);
        try {
            const cuts = await loadWorkspaceCutsForPicker();
            setTextStudioCuts(cuts);
            return cuts;
        } catch (_) {
            setTextStudioCuts([]);
            return [];
        } finally {
            setTextStudioCutsLoading(false);
        }
    };

    const pickRandomTextStudioCut = useCallback((cutsOverride) => {
        const pool = Array.isArray(cutsOverride) && cutsOverride.length > 0
            ? cutsOverride
            : textStudioCuts;
        if (!pool.length) return;
        let candidates = pool;
        if (textStudioSelectedCut && pool.length > 1) {
            const currentId = String(textStudioSelectedCut._id || textStudioSelectedCut.id);
            const filtered = pool.filter((c) => String(c._id || c.id) !== currentId);
            if (filtered.length > 0) candidates = filtered;
        }
        setTextStudioSelectedCut(candidates[Math.floor(Math.random() * candidates.length)]);
    }, [textStudioCuts, textStudioSelectedCut]);

    useEffect(() => {
        if (!textStudioContentUnlocked || !currentWorkspace?._id || textStudioSelectedCut) return;
        let cancelled = false;
        (async () => {
            const cuts = await ensureTextStudioCutsLoaded();
            if (cancelled || !cuts.length) return;
            setTextStudioSelectedCut(cuts[Math.floor(Math.random() * cuts.length)]);
        })();
        return () => { cancelled = true; };
    }, [textStudioContentUnlocked, currentWorkspace?._id, textStudioSelectedCut]);

    const openTextStudioCutModal = async () => {
        setTextStudioShowCutModal(true);
        if (textStudioCuts.length > 0) return;
        await ensureTextStudioCutsLoaded();
    };

    const handleTextStudioDownloadAndSave = async () => {
        if (!textStudioVideoUrl || !currentWorkspace?._id) return;
        try {
            const scriptId = await ensureTextStudioScriptId();
            const baseName = String(textStudioDescription || textStudioContent || 'text-on-screen').trim().slice(0, 60);
            const filename = `${sanitizeVideoFilename(baseName || 'text-on-screen')}-${Date.now()}.mp4`;
            const directUrl = await resolveStudioVideoDownloadUrl(
                currentWorkspace._id,
                scriptId,
                filename,
                textStudioVideoUrl
            );
            triggerDirectVideoDownload(directUrl, filename);
            celebrateVideoDownload();
            setTextStudioPublishingVideo(true);
            const publishResponse = await api.publishWorkspaceVideo(currentWorkspace._id, scriptId, {
                videoUrl: textStudioVideoUrl,
                musicName: lastUsedMusicName || null,
                ...(String(textStudioDescription || '').trim() ? { title: String(textStudioDescription).trim().slice(0, 120) } : {}),
            });
            if (publishResponse.data?.message === 'Video published successfully' || publishResponse.data?.message === 'Video already published') {
                setTextStudioVideoSaved(true);
                onSetupStep3Complete?.();
                await fetchGeneratedVideosPage(1, false);
            }
        } catch (error) {
            console.error('Text studio download and save error:', error);
            setErrors(prev => [...prev, 'Failed to download and save video: ' + (error.response?.data?.error || error.message)]);
        } finally {
            setTextStudioPublishingVideo(false);
        }
    };

    const handleTextStudioGenerateVideo = async () => {
        if (!currentWorkspace?._id) return;
        if (!String(textStudioContent || '').trim()) {
            setErrors(prev => [...prev, 'Add on-screen text before generating a video.']);
            return;
        }
        textStudioVideoCancelledRef.current = false;
        if (textStudioVideoPollRef.current) {
            clearInterval(textStudioVideoPollRef.current);
            textStudioVideoPollRef.current = null;
        }
        setTextStudioGeneratingVideo(true);
        setTextStudioVideoProgress(0);
        setTextStudioVideoUrl('');
        try {
            const scriptId = await ensureTextStudioScriptId();
            if (textStudioVideoCancelledRef.current) return;
            let musicIdToUse = null;
            if (useMusic) {
                if (selectedMusicId) {
                    musicIdToUse = selectedMusicId;
                } else if (availableMusic.length > 0) {
                    const randomIndex = Math.floor(Math.random() * availableMusic.length);
                    musicIdToUse = availableMusic[randomIndex]._id;
                }
            }
            const selectedCutId = textStudioSelectedCut
                ? String(textStudioSelectedCut._id || textStudioSelectedCut.id)
                : undefined;
            await api.generateWorkspaceVideo(currentWorkspace._id, scriptId, {
                scriptContent: String(textStudioContent || '').trim(),
                selectedMusicId: musicIdToUse,
                selectedCutId,
                fontId: selectedFontId,
                includeTags: includeTags.length > 0 ? includeTags.join(',') : undefined,
                excludeTags: excludeTags.length > 0 ? excludeTags.join(',') : undefined,
                slowMotion: slowMotion,
                cutSilenceMode: false,
                cutMoreSilenceMode: false,
                fastPacing: false,
                bigText: bigText,
                zoomWhoosh: false,
                flipbookIntro: false,
                openingLineInCenter: currentUserId === BOUKAR_USER_ID && openingLineInCenter,
                openingLineTextSize: openingLineTextSize,
                ...buildOpeningLineFontPayload(),
                openingLineFontColor: currentUserId === BOUKAR_USER_ID && openingLineInCenter && openingLineFontColor ? openingLineFontColor : undefined,
                glowText: glowText,
                glowTextColor: glowTextColor,
                subtitlePosition: subtitlePosition,
                enableSubtitles: true,
                oneWordPerSubtitle: oneWordPerSubtitle,
                fontMixing: false,
                centerStackOpening: centerStackOpening,
                centerStackLineByLine: centerStackLineByLine,
                boukisIntro: boukisIntro || undefined,
                boukisIntroPitch: boukisIntro ? boukisIntroPitch : undefined,
                subtitleFontColor,
                subtitleOutlineColor,
                subtitleOutlineEnabled,
            });
            if (textStudioVideoCancelledRef.current) return;
            const start = Date.now();
            const TIMEOUT_MS = 10 * 60 * 1000;
            textStudioVideoPollRef.current = setInterval(async () => {
                try {
                    if (textStudioVideoCancelledRef.current) {
                        clearInterval(textStudioVideoPollRef.current);
                        textStudioVideoPollRef.current = null;
                        return;
                    }
                    if (Date.now() - start > TIMEOUT_MS) {
                        clearInterval(textStudioVideoPollRef.current);
                        textStudioVideoPollRef.current = null;
                        if (textStudioVideoCancelledRef.current) return;
                        setTextStudioGeneratingVideo(false);
                        setTextStudioVideoProgress(0);
                        setErrors(prev => [...prev, 'Video generation timed out.']);
                        return;
                    }
                    const statusResp = await api.getWorkspaceVideoStatus(currentWorkspace._id, scriptId);
                    if (textStudioVideoCancelledRef.current) return;
                    const { status, progress, videoUrl } = statusResp.data;
                    setTextStudioVideoProgress(progress ?? 0);
                    if ((status === 'draft' && videoUrl) || status === 'error') {
                        clearInterval(textStudioVideoPollRef.current);
                        textStudioVideoPollRef.current = null;
                        if (textStudioVideoCancelledRef.current) return;
                        setTextStudioGeneratingVideo(false);
                        if (status === 'draft' && videoUrl) {
                            setTextStudioVideoUrl(videoUrl);
                            setTextStudioVideoSaved(false);
                            playCompletionSound('video_merra');
                        } else {
                            setErrors(prev => [...prev, 'Video generation failed.']);
                        }
                    }
                } catch (error) {
                    console.error('Text studio video status error:', error);
                }
            }, 2000);
        } catch (e) {
            if (textStudioVideoCancelledRef.current) return;
            setTextStudioGeneratingVideo(false);
            setTextStudioVideoProgress(0);
            setErrors(prev => [...prev, 'Failed to generate video: ' + (e.response?.data?.error || e.message)]);
        }
    };

    const scriptHasNonEmptyMainBody = (script) =>
        String(getBatchScriptContent(script, 'main') ?? '').trim().length > 0;

    /** Text cards need content only; voiceover cards also need a voiceover audio URL before batch video. */
    const scriptEligibleForBatchGenerateAllVideo = (script) => {
        if (!scriptHasNonEmptyMainBody(script)) return false;
        if (script.videoUrl && script.status === 'draft' && script.progress === 100) return false;
        if (script.type === 'text') return true;
        return String(script.voiceoverUrl || '').trim().length > 0;
    };

    const openBatchConfirmModal = ({ title, message, confirmLabel, onConfirm }) => {
        batchConfirmActionRef.current = onConfirm;
        setBatchConfirmModal({ title, message, confirmLabel });
        setShowBatchConfirmModal(true);
    };

    const closeBatchConfirmModal = () => {
        setShowBatchConfirmModal(false);
        batchConfirmActionRef.current = null;
    };

    const confirmBatchModalAction = () => {
        const action = batchConfirmActionRef.current;
        closeBatchConfirmModal();
        if (action) action();
    };

    const runBatchGenerateAllVoiceovers = async (scriptsNeedingVoiceover) => {
        trackEvent('batch_voiceover_generation_started', {
            workspaceId: currentWorkspace?._id,
            scriptCount: scriptsNeedingVoiceover.length
        });

        await Promise.all(
            scriptsNeedingVoiceover.map(script =>
                handleBatchGenerateVoiceover(script._id, script)
            )
        );
    };

    const handleBatchGenerateAllVoiceovers = (options = {}) => {
        const skipConfirm = Boolean(options?.skipConfirm);
        const scriptsNeedingVoiceover = batchScripts.filter(script =>
            script.type === 'voiceover' &&
            !script.voiceoverUrl &&
            scriptHasNonEmptyMainBody(script)
        );

        if (scriptsNeedingVoiceover.length === 0) {
            setModalErrorMessage('No voiceover scripts with content found that need voiceover generation');
            setShowErrorModal(true);
            return;
        }

        if (skipConfirm) {
            runBatchGenerateAllVoiceovers(scriptsNeedingVoiceover);
            return;
        }

        openBatchConfirmModal({
            title: 'Generate All Voiceovers',
            message: `Generate voiceovers for ${scriptsNeedingVoiceover.length} script${scriptsNeedingVoiceover.length > 1 ? 's' : ''}?`,
            confirmLabel: 'Generate Voiceovers',
            onConfirm: () => runBatchGenerateAllVoiceovers(scriptsNeedingVoiceover),
        });
    };

    const runBatchGenerateAllVideos = async (scriptsNeedingVideo) => {
        setBatchVideoProgress({ current: 0, total: scriptsNeedingVideo.length, percent: 0 });

        trackEvent('batch_video_generation_started', {
            workspaceId: currentWorkspace?._id,
            scriptCount: scriptsNeedingVideo.length
        });

        const onVideoComplete = () => {
            setBatchVideoProgress(prev => {
                const nextCurrent = prev.current + 1;
                const percent = prev.total > 0 ? Math.round((nextCurrent / prev.total) * 100) : 0;
                return { ...prev, current: nextCurrent, percent };
            });
        };

        // Up to 10 concurrent video generations so batch creation can finish in parallel.
        const BATCH_VIDEO_CONCURRENCY = 10;
        let nextIndex = 0;
        const workers = Array.from(
            { length: Math.min(BATCH_VIDEO_CONCURRENCY, scriptsNeedingVideo.length) },
            async () => {
                while (nextIndex < scriptsNeedingVideo.length) {
                    const i = nextIndex++;
                    const script = scriptsNeedingVideo[i];
                    await handleBatchGenerateVideo(script._id, onVideoComplete);
                }
            }
        );
        await Promise.all(workers);
    };

    const handleBatchGenerateAllVideos = (options = {}) => {
        const skipConfirm = Boolean(options?.skipConfirm);
        const scriptsNeedingVideo = batchScripts.filter(scriptEligibleForBatchGenerateAllVideo);

        if (scriptsNeedingVideo.length === 0) {
            setModalErrorMessage('No scripts with content found that need video generation. Voiceover scripts must have a voiceover URL first.');
            setShowErrorModal(true);
            return;
        }

        if (skipConfirm) {
            runBatchGenerateAllVideos(scriptsNeedingVideo);
            return;
        }

        openBatchConfirmModal({
            title: 'Generate All Videos',
            message: `Generate videos for ${scriptsNeedingVideo.length} script${scriptsNeedingVideo.length > 1 ? 's' : ''}?`,
            confirmLabel: 'Generate Videos',
            onConfirm: () => runBatchGenerateAllVideos(scriptsNeedingVideo),
        });
    };

    const batchModEnterIsApple = useMemo(() => {
        if (typeof navigator === 'undefined') return false;
        const platform = navigator.platform || '';
        const ua = navigator.userAgent || '';
        return /Mac|iPhone|iPod|iPad/.test(platform) || /Mac OS X/.test(ua);
    }, []);
    const batchModEnterLabel = batchModEnterIsApple ? '⌘+Enter' : 'Ctrl+Enter';

    const VOICE_CLONE_MAX_TOTAL_SEC = 45 * 60;
    const totalVoiceSampleDurationSec = useMemo(() => {
        const parseStored = (raw) => {
            if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
            if (typeof raw === 'string' && /^\d+:\d{1,2}$/.test(raw)) {
                const [m, s] = raw.split(':').map(Number);
                if (Number.isFinite(m) && Number.isFinite(s)) return m * 60 + s;
            }
            return 0;
        };
        return (recordings || []).reduce((sum, rec) => {
            if (typeof rec?.duration === 'number' && Number.isFinite(rec.duration) && rec.duration > 0) {
                return sum + rec.duration;
            }
            return sum + parseStored(recordingLength[rec?._id]);
        }, 0);
    }, [recordings, recordingLength]);
    const voiceSamplesExceedCloneLimit = totalVoiceSampleDurationSec > VOICE_CLONE_MAX_TOTAL_SEC;
    const formatVoiceSampleDuration = (totalSec) => {
        const sec = Math.max(0, Math.floor(totalSec || 0));
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${m}:${String(s).padStart(2, '0')}`;
    };

    const batchKeycapStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '16px',
        height: '16px',
        padding: '0 3px',
        borderRadius: '4px',
        background: 'linear-gradient(180deg, #3a3a3a 0%, #2d2d2d 100%)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 1px 0 #1a1a1a, 0 1.5px 0 #151515',
        color: '#d4d4d8',
        fontSize: '9px',
        fontWeight: 600,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        lineHeight: 1,
        border: '1px solid #222',
    };

    const renderBatchModEnterHint = () => (
        <span
            aria-label={batchModEnterLabel}
            title={`${batchModEnterLabel} skips confirmation`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}
        >
            <kbd style={{ ...batchKeycapStyle, padding: '0 2px', ...(batchModEnterIsApple ? { minWidth: '16px', fontSize: '10px' } : { fontSize: '8px', letterSpacing: '-0.02em' }) }}>
                {batchModEnterIsApple ? '⌘' : 'Ctrl'}
            </kbd>
            <kbd style={{ ...batchKeycapStyle, minWidth: '18px', padding: '0' }} aria-hidden="true">
                <svg width="10" height="10" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M3.5 10.5H11.5V4.5"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M6 8L3.5 10.5L6 13"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </kbd>
        </span>
    );

    // Cmd/Ctrl+Enter on batch tab: generate all available voiceovers/videos, skip confirm modal
    useEffect(() => {
        if (activeTab !== 'batch') return;

        const onKeyDown = (e) => {
            if (!(e.metaKey || e.ctrlKey) || e.key !== 'Enter') return;
            if (showBatchConfirmModal) {
                e.preventDefault();
                confirmBatchModalAction();
                return;
            }

            const scripts = batchScriptsRef.current || [];
            const needsVoiceover = scripts.some(script =>
                script.type === 'voiceover' &&
                !script.voiceoverUrl &&
                scriptHasNonEmptyMainBody(script)
            );
            const needsVideo = scripts.some(scriptEligibleForBatchGenerateAllVideo);
            if (!needsVoiceover && !needsVideo) return;

            e.preventDefault();
            if (needsVoiceover) handleBatchGenerateAllVoiceovers({ skipConfirm: true });
            if (needsVideo) handleBatchGenerateAllVideos({ skipConfirm: true });
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [activeTab, showBatchConfirmModal, batchScripts]);

    // Hide video progress bar a few seconds after all videos complete
    useEffect(() => {
        if (batchVideoProgress.total > 0 && batchVideoProgress.percent >= 100) {
            const t = setTimeout(() => {
                setBatchVideoProgress({ current: 0, total: 0, percent: 0 });
            }, 4000);
            return () => clearTimeout(t);
        }
    }, [batchVideoProgress.total, batchVideoProgress.percent]);

    const handleBatchDownloadAndSave = async (scriptId) => {
        const script = batchScripts.find(s => s._id === scriptId);
        if (!script || !script.videoUrl) return;
        
        try {
            const batchThumb = batchThumbnailByScriptId[scriptId];
            const thumbName = (batchThumb?.title && String(batchThumb.title).trim()) ? String(batchThumb.title).trim() : '';
            const contentVersion = script.selectedVersion || 'main';
            const scriptContent = getBatchScriptContent(script, contentVersion);
            const filename = buildMerraVideoDownloadFilename(scriptContent);

            const directUrl = await resolveStudioVideoDownloadUrl(
                currentWorkspace._id,
                scriptId,
                filename,
                script.videoUrl
            );
            triggerDirectVideoDownload(directUrl, filename);
            celebrateVideoDownload();
            
            // Save/publish the video to workspace
            const publishResponse = await api.publishWorkspaceVideo(currentWorkspace._id, scriptId, {
                videoUrl: script.videoUrl,
                musicName: null,
                ...(thumbName ? { title: thumbName } : {}),
            });
            
            if (publishResponse.data.message === 'Video published successfully' || publishResponse.data.message === 'Video already published') {
                await fetchGeneratedVideosPage(1, false);
                
                // Update the script to mark it as saved
                setBatchScripts(prev => prev.map(s => 
                    s._id === scriptId 
                        ? { ...s, saved: true }
                        : s
                ));
            }
        } catch (error) {
            console.error('Error downloading and saving video:', error);
            setErrors(prev => [...prev, 'Failed to download and save video: ' + (error.response?.data?.error || error.message)]);
        }
    };

    if (!currentWorkspace) {
        return (
            <div className={`workspace-management ${darkMode ? 'dark-mode' : ''} ${completionGlowActive ? 'completion-glow' : ''}`}>
                {/* Header Skeleton */}
                <div className="workspace-header">
                    <div className="workspace-title-section">
                        <div className="shimmer-title shimmer" style={{ width: '200px', height: '28px', marginBottom: '8px' }}></div>
                        <div className="workspace-meta">
                            <div className="shimmer-text shimmer" style={{ width: '120px', height: '14px' }}></div>
                            <div className="shimmer-text shimmer" style={{ width: '80px', height: '14px' }}></div>
                        </div>
                    </div>
                    <div className="workspace-subscription-section">
                        <div className="shimmer-badge shimmer" style={{ width: '80px', height: '24px', borderRadius: '6px' }}></div>
                    </div>
                </div>

                {/* Tab Navigation Skeleton */}
                <div className="tab-navigation">
                    {[...Array(6)].map((_, index) => (
                        <div key={`tab-skeleton-${index}`} className="shimmer-tab shimmer" style={{ width: '120px', height: '40px', borderRadius: '6px' }}></div>
                    ))}
                </div>

                {/* Content Skeleton */}
                <div className="tab-content">
                    <div className="shimmer-grid">
                        {[...Array(6)].map((_, index) => (
                            <div key={`shimmer-${index}`} className="shimmer-card">
                                <div className="shimmer-preview"></div>
                                <div className="shimmer-content">
                                    <div className="shimmer-title shimmer"></div>
                                    <div className="shimmer-tags">
                                        <div className="shimmer-tag shimmer"></div>
                                        <div className="shimmer-tag shimmer"></div>
                                        <div className="shimmer-tag shimmer"></div>
                                    </div>
                                    <div className="shimmer-description shimmer"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    const BATCH_ALLOWLIST_IDS = ['673247877e4ac0a824cbac0d' /*Boukar*/, '68c2c068253b59e3eb46452c' /*Oleg*/, '68c2b8da253b59e3eb4644ff' /*Mauricio*/, '698da27e796493d5a4557a0e' /* Ismael */, '68e630224cd2834792553e52' /*Miguel*/, '68efcb7f0351dbb5c7280d59' /*Carol*/, '68efcb960351dbb5c7280d99' /*Messi*/, '69205f698ac2af5fdadd01d9' /*Amber*/, '68e420063a034e51476807cf' /*Coralie*/, '692edc9bc6f80cd708fe4ebc' /*Toni*/, '692f0a39e4c2a7e873d2707f' /*Marina*/, /*'6937f84fd1c654d8629ba3a6'*/ /*Donovan*/, '69fcd261ca736cd0a5b72e72' /*Valentina*/, '69ff1cc295660f26f7a896a6' /*Valentina*/];
    const TIMELINE_ALLOWLIST_IDS = ['673247877e4ac0a824cbac0d' /*Boukar*/, '68c2c068253b59e3eb46452c' /*Oleg*/, '68c2b8da253b59e3eb4644ff' /*Mauricio*/, '698da27e796493d5a4557a0e' /* Ismael */, '68e630224cd2834792553e52' /*Miguel*/, '68efcb7f0351dbb5c7280d59' /*Carol*/, '68efcb960351dbb5c7280d99' /*Messi*/, '69205f698ac2af5fdadd01d9' /*Amber*/, '68e420063a034e51476807cf' /*Coralie*/, '692edc9bc6f80cd708fe4ebc' /*Toni*/, '692f0a39e4c2a7e873d2707f' /*Marina*/, /*'6937f84fd1c654d8629ba3a6'*/ /*Donovan*/, '69fcd261ca736cd0a5b72e72' /*Valentina*/, '69ff1cc295660f26f7a896a6' /*Valentina*/];
    const hasProSubscription = currentWorkspace?.isSubscribed && currentWorkspace?.subscriptionPlan === 'pro';
    const STUDIO_PAYWALL_BYPASS_IDS = ['673247877e4ac0a824cbac0d' /*Boukar*/, '68c2c068253b59e3eb46452c' /*Oleg*/, '68c2b8da253b59e3eb4644ff' /*Mauricio*/, '698da27e796493d5a4557a0e' /* Ismael */, '68e630224cd2834792553e52' /*Miguel*/, '68efcb7f0351dbb5c7280d59' /*Carol*/, '68efcb960351dbb5c7280d99' /*Messi*/, '69205f698ac2af5fdadd01d9' /*Amber*/, '68e420063a034e51476807cf' /*Coralie*/, '692edc9bc6f80cd708fe4ebc' /*Toni*/, '692f0a39e4c2a7e873d2707f' /*Marina*/, /*'6937f84fd1c654d8629ba3a6'*/ /*Donovan*/, '698d4831796493d5a45569ed' /*Cassandre*/, '697b75e9e7f4c39a83be6ada' /*Lucie Beghin*/, '69fcd261ca736cd0a5b72e72' /*Valentina*/, '69ff1cc295660f26f7a896a6' /*Valentina*/];
    const organicStudioPaywall = Boolean(
        currentWorkspace &&
        !currentWorkspace.isSubscribed &&
        generatedTotal > 3 &&
        !STUDIO_PAYWALL_BYPASS_IDS.includes(currentUserId)
    );
    const showStudioPaywall = showUpgradePaywall || organicStudioPaywall;
    const canAccessBatch = BATCH_ALLOWLIST_IDS.includes(currentUserId) || hasProSubscription;
    const canAccessTimeline = TIMELINE_ALLOWLIST_IDS.includes(currentUserId) || hasProSubscription;

    const showStudioProcessOverlay =
        // Only show overlay during autopilot script/voiceover — not while encoding video.
        (studioAutoCreateMode || studioAutoCreateModeRef.current || autoGenerateVideoAfterVoiceoverRef.current) &&
        (studioGenerating || studioGeneratingVoice);

    const studioOverlayProgressPercent = studioGeneratingVoice
        ? studioVoiceoverOverlayProgress.percent
        : Math.min(100, Math.round(studioProgress.percent + 5));
    const studioOverlayStepTitle = studioGeneratingVoice
        ? studioVoiceoverOverlayProgress.step
        : (studioProgress.currentStep || 'Generating script...');
    const studioOverlayStepSubtitle = studioGeneratingVoice
        ? 'Creating your voiceover'
        : 'Writing your script';
    const studioOverlayShimmerDuration = Math.max(
        360,
        Math.round(1500 * (1 - Math.min(100, studioOverlayProgressPercent) / 100))
    );

    const batchNleOverrideSettings = nleEditorSource?.type === 'batch'
        ? (batchVideoSettingsOverrideByScriptId[nleEditorSource.scriptId] || null)
        : null;

    const toggleBatchVideoSettingsOverride = (scriptId) => {
        setBatchVideoSettingsOverrideByScriptId((prev) => {
            if (prev[scriptId]) {
                const next = { ...prev };
                delete next[scriptId];
                return next;
            }
            return {
                ...prev,
                [scriptId]: emptyBatchVideoSettingsFrom(batchVideoSettingsGlobalsRef.current),
            };
        });
    };

    const updateBatchVideoSetting = (scriptId, patch) => {
        setBatchVideoSettingsOverrideByScriptId((prev) => {
            if (!prev[scriptId]) return prev;
            return { ...prev, [scriptId]: { ...prev[scriptId], ...patch } };
        });
    };

    const resetBatchVideoSettingsOverride = (scriptId) => {
        setBatchVideoSettingsOverrideByScriptId((prev) => {
            if (!prev[scriptId]) return prev;
            return {
                ...prev,
                [scriptId]: emptyBatchVideoSettingsFrom(batchVideoSettingsGlobalsRef.current),
            };
        });
    };

    return (
        <div className={`workspace-management ${darkMode ? 'dark-mode' : ''} ${completionGlowActive ? 'completion-glow' : ''}`} style={{padding: screenWidth <= 600 ? '10px' : '',}}>
            {showStudioProcessOverlay && (
                <div className="studio-process-overlay" aria-hidden="true">
                    {studioOverlayCursors.map((cursor, index) => (
                        <div
                            key={index}
                            className="studio-process-cursor"
                            style={{
                                ['--studio-cursor-color']: BATCH_CURSOR_COLORS[index],
                                left: `${cursor.left}%`,
                                top: `${cursor.top}%`,
                                transform: `translate(-50%, -50%) rotate(${cursor.rotate}deg)`,
                            }}
                        >
                            <svg
                                className="studio-process-cursor-icon"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="var(--studio-cursor-color)"
                                stroke="var(--studio-cursor-color)"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
                            </svg>
                            <span className="studio-process-cursor-label">{BATCH_CURSOR_LABELS[index]}</span>
                        </div>
                    ))}

                    <div className="studio-process-panel">
                        <div className="studio-process-title">
                            {studioGeneratingVoice ? 'Generating voiceover' : 'Generating script'}
                        </div>
                        <div style={{
                            width: '100%',
                            height: '6px',
                            background: 'var(--input-border)',
                            borderRadius: '3px',
                            overflow: 'hidden',
                            marginTop: '14px',
                            position: 'relative',
                        }}>
                            <div
                                className="progress-bar-shimmer-script"
                                style={{
                                    width: `${studioOverlayProgressPercent}%`,
                                    height: '6px',
                                    transition: 'width 0.3s ease',
                                    position: 'relative',
                                    ['--shimmer-duration']: `${studioOverlayShimmerDuration}ms`,
                                }}
                            />
                        </div>
                        <div className="video-gen-steps">
                            <div className="video-gen-steps__title">{studioOverlayStepTitle}</div>
                            <div className="video-gen-steps__subtitle">
                                {studioOverlayStepSubtitle} · {studioOverlayProgressPercent}%
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Non-skippable minivlog onboarding overlay (replaces referral-only modal) */}
            {onboardingCompleted === false && (
                <OnboardingOverlay
                    open
                    workspaceId={currentWorkspace?._id}
                    workspace={currentWorkspace}
                    onReferralSourceSaved={onReferralSourceSaved}
                    onComplete={async () => {
                        onOnboardingComplete?.();
                        onSetupStep3Complete?.();
                        try {
                            setActiveTab('raw');
                            await Promise.all([
                                reloadVideos(),
                                fetchWorkspaceCuts(1, false),
                                fetchGeneratedVideosPage(1, false),
                            ]);
                            // Totals sync to sidebar via setWorkspaceCounts effect
                        } catch (err) {
                            console.warn('[WorkspaceManagement] refresh after onboarding failed', err);
                        }
                    }}
                />
            )}
            {/* LEGACY referral-only modal kept commented conceptually — gated off below */}
            {false && onboardingCompleted === false && !referralSource && null}
            {showMobilePickerNotice && (
                <div
                    role="dialog"
                    aria-modal="true"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 10000,
                        backgroundColor: 'rgba(0,0,0,0.55)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '16px'
                    }}
                    onClick={() => setShowMobilePickerNotice(false)}
                >
                    <div
                        style={{
                            width: 'min(520px, 100%)',
                            background: darkMode ? '#0b1220' : '#ffffff',
                            borderRadius: '14px',
                            padding: '16px',
                            border: darkMode ? '1px solid rgba(148, 163, 184, 0.22)' : '1px solid rgba(15, 23, 42, 0.12)',
                            boxShadow: darkMode
                                ? '0 18px 60px rgba(0,0,0,0.55)'
                                : '0 18px 60px rgba(15, 23, 42, 0.22)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ fontSize: '15px', fontWeight: 700, color: darkMode ? '#fff' : '#0f172a', marginBottom: '8px' }}>
                            Mobile upload note
                        </div>
                        <div style={{ fontSize: '13px', lineHeight: 1.5, color: darkMode ? '#cbd5e1' : '#334155' }}>
                            Mobile browsers can take time to load/export videos from your gallery before upload starts.
                            If it looks stuck, give it a moment — or select fewer videos at once.
                        </div>

                        <label style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '12px', fontSize: '13px', color: darkMode ? '#cbd5e1' : '#334155' }}>
                            <input
                                type="checkbox"
                                checked={mobilePickerNoticeDismissed}
                                onChange={(e) => {
                                    const next = !!e.target.checked;
                                    setMobilePickerNoticeDismissed(next);
                                    try {
                                        localStorage.setItem('workspace_mobile_picker_notice_dismissed', next ? '1' : '0');
                                    } catch {
                                        // ignore
                                    }
                                }}
                            />
                            Don’t show again
                        </label>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '14px' }}>
                            <button
                                type="button"
                                onClick={() => setShowMobilePickerNotice(false)}
                                style={{
                                    padding: '10px 12px',
                                    borderRadius: '10px',
                                    border: darkMode ? '1px solid rgba(148, 163, 184, 0.25)' : '1px solid rgba(15, 23, 42, 0.12)',
                                    background: 'transparent',
                                    color: darkMode ? '#e2e8f0' : '#0f172a',
                                    cursor: 'pointer',
                                    fontWeight: 600
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowMobilePickerNotice(false);
                                    fileInputRef.current?.click();
                                }}
                                style={{
                                    padding: '10px 12px',
                                    borderRadius: '10px',
                                    border: 'none',
                                    background: 'linear-gradient(90deg, #AB5898 0%, #5C4BD3 100%)',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontWeight: 700
                                }}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div className="workspace-header" style={{
                flexDirection: window.innerWidth <= 600 ? 'column' : 'row',
                alignItems: window.innerWidth <= 600 ? 'flex-start' : 'center',
                gap: window.innerWidth <= 600 ? '15px' : '20px'
            }}>
                <div className="workspace-title-section">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        {isEditingName ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                                <input
                                    type="text"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleUpdateName();
                                        if (e.key === 'Escape') {
                                            setNewName(currentWorkspace?.name || '');
                                            setIsEditingName(false);
                                        }
                                    }}
                                    placeholder="Workspace name"
                                    style={{
                                        fontSize: window.innerWidth <= 630 ? '20px' : '28px',
                                        fontWeight: 700,
                                        padding: '6px 10px',
                                        borderRadius: '10px',
                                        border: '1px solid var(--input-border)',
                                        background: 'var(--input-bg)',
                                        color: 'var(--input-text)',
                                        minWidth: window.innerWidth <= 630 ? '260px' : '320px',
                                        maxWidth: '100%',
                                        boxSizing: 'border-box'
                                    }}
                                    autoFocus
                                />
                                <button
                                    type="button"
                                    onClick={handleUpdateName}
                                    className="icon-button"
                                    aria-label="Save workspace name"
                                    title="Save"
                                >
                                    <Check size={18} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setNewName(currentWorkspace?.name || '');
                                        setIsEditingName(false);
                                    }}
                                    className="icon-button"
                                    aria-label="Cancel editing workspace name"
                                    title="Cancel"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        ) : (
                            <>
                                <h2 style={{ fontSize: window.innerWidth <= 630 ? '20px' : '28px', margin: 0 }}>
                                    {currentWorkspace.name}
                                </h2>
                                {hasPermission('admin') && (
                                    <button
                                        type="button"
                                        onClick={() => setIsEditingName(true)}
                                        className="icon-button"
                                        aria-label="Edit workspace name"
                                        title="Edit workspace name"
                                        style={{ display: 'flex', alignItems: 'center' }}
                                    >
                                        <Edit2 size={18} />
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                    {/* <div className="workspace-meta">
                        <span>Your role: {getUserRole()}</span>
                        <span>{currentWorkspace.members?.length || 0} member{(currentWorkspace.members?.length || 0) !== 1 ? 's' : ''}</span>
                    </div> */}
                    {['673247877e4ac0a824cbac0d' /*Boukar*/].includes(currentUserId) && (
                        <div
                            style={{
                                marginTop: '6px',
                                fontSize: '12px',
                                opacity: 0.75
                            }}
                        >
                            
                            Time keeps moving: {boukarTimeRoundedTo10s}
                            <style>{`
                                @keyframes boukarDotPulse {
                                    from { opacity: 1; }
                                    to { opacity: 0.5; }
                                }
                            `}</style>
                            <span
                                aria-hidden="true"
                                style={{
                                    display: 'inline-block',
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: '#68D6C4',
                                    marginLeft: '4px',
                                    animation: 'boukarDotPulse 0.16s ease-in-out infinite alternate'
                                }}
                            />
                        </div>
                    )}
                </div>
                <div className="workspace-subscription-section" style={{
                    alignSelf: window.innerWidth <= 630 ? 'flex-start' : 'center',
                    display: 'flex',
                    flexDirection: window.innerWidth <= 630 ? 'row' : 'column',
                    alignItems: window.innerWidth <= 630 ? 'center' : 'flex-end',
                    gap: window.innerWidth <= 630 ? '10px' : '5px',
                    flexWrap: 'nowrap'
                }}>
                    {/* Subscription Badge */}
                    {currentWorkspace.isSubscribed ? (
                        <span className={`subscription-badge subscribed${currentWorkspace.subscriptionPlan === 'pro' ? ' pro' : ''}`}>
                            {currentWorkspace.subscriptionPlan === 'creator' ? 'CREATOR PLAN' :
                             currentWorkspace.subscriptionPlan === 'pro' ? 'PRO PLAN' : 'SUBSCRIBED'}
                        </span>
                    ) : (
                        <span className="subscription-badge free">FREE PLAN</span>
                    )}
                    
                    {/* Manage Subscription Button (Admin only) */}
                    {hasPermission('admin') && (currentWorkspace.isSubscribed ||  ['673247877e4ac0a824cbac0d'].includes(currentUserId)) && (
                        <button
                            onClick={async () => {
                                try {
                                    const response = await api.createPortalSession(currentWorkspace._id);
                                    window.open(response.data.url, '_blank');
                                } catch (error) {
                                    console.error('Error creating portal session:', error);
                                    setErrors(prev => [...prev, 'Failed to open subscription management']);
                                }
                            }}
                            className="manage-subscription-btn"
                        >
                            <Settings size={16} />
                            Manage Subscription
                        </button>
                    )}
                </div>
            </div>

            {/* Toast notifications (success + errors) */}
            {(message || errorMessage || errors.length > 0) && (
                <div
                    style={{
                        position: 'fixed',
                        top: 16,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 9999,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 8,
                        maxWidth: 360,
                        width: 'calc(100% - 32px)',
                        pointerEvents: 'none'
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'auto', width: '100%' }}>
                        {message && (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    padding: '12px 14px',
                                    borderRadius: 8,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    backgroundColor: darkMode ? '#000000' : '#d4edda',
                                    border: `1px solid ${darkMode ? 'rgba(34, 197, 94, 0.4)' : '#c3e6cb'}`,
                                    color: darkMode ? '#86efac' : '#155724',
                                    fontSize: 14
                                }}
                            >
                                <CheckCircle size={18} style={{ flexShrink: 0 }} />
                                <span style={{ flex: 1 }}>{message}</span>
                                <button type="button" onClick={() => setMessage('')} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: 'inherit', opacity: 0.8 }} aria-label="Dismiss"><X size={16} /></button>
                            </div>
                        )}
                        {errorMessage && (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    padding: '12px 14px',
                                    borderRadius: 8,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    backgroundColor: darkMode ? '#000000' : '#f8d7da',
                                    border: `1px solid ${darkMode ? 'rgba(239, 68, 68, 0.4)' : '#f5c6cb'}`,
                                    color: darkMode ? '#fca5a5' : '#721c24',
                                    fontSize: 14
                                }}
                            >
                                <AlertCircle size={18} style={{ flexShrink: 0 }} />
                                <span style={{ flex: 1 }}>{errorMessage}</span>
                                <button type="button" onClick={() => setErrorMessage('')} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: 'inherit', opacity: 0.8 }} aria-label="Dismiss"><X size={16} /></button>
                            </div>
                        )}
                        {errors.map((error, index) => (
                            <div
                                key={`toast-error-${index}`}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    padding: '12px 14px',
                                    borderRadius: 8,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    backgroundColor: darkMode ? '#000000' : '#f8d7da',
                                    border: `1px solid ${darkMode ? 'rgba(239, 68, 68, 0.4)' : '#f5c6cb'}`,
                                    color: darkMode ? '#fca5a5' : '#721c24',
                                    fontSize: 14
                                }}
                            >
                                <AlertCircle size={18} style={{ flexShrink: 0 }} />
                                <span style={{ flex: 1 }}>{error}</span>
                                <button type="button" onClick={() => clearError(index)} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: 'inherit', opacity: 0.8 }} aria-label="Dismiss"><X size={16} /></button>
                            </div>
                        ))}
                        {errors.length > 1 && (
                            <button
                                type="button"
                                onClick={clearErrors}
                                style={{
                                    alignSelf: 'center',
                                    padding: '4px 10px',
                                    fontSize: 12,
                                    background: 'none',
                                    border: 'none',
                                    color: darkMode ? '#94a3b8' : '#64748b',
                                    cursor: 'pointer',
                                    textDecoration: 'underline'
                                }}
                            >
                                Clear all
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* <div className="tab-navigation-wrapper tab-navigation-mobile-only">
                <div className="tab-navigation-inner">
                    <div
                        ref={tabNavScrollRef}
                        className="tab-navigation"
                        style={{ marginLeft: '-10px', paddingLeft: '10px', paddingRight: '10px', marginRight: '-10px', paddingRight: '10px' }}
                    >
                    <button 
                        className={`tab-button ${activeTab === 'raw' ? 'active' : ''}`}
                        onClick={() => handleTabChange('raw')}
                    >
                        <Video size={18} />
                        Raw Videos {rawTotal > 0 ? `(${rawTotal})` : ''}
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'cuts' ? 'active' : ''}`}
                        onClick={() => handleTabChange('cuts')}
                        >
                        <Scissors size={18} />
                        Cuts {cutsTotal > 0 ? `(${cutsTotal})` : ''}
                        {isRefreshingCuts && <span style={{ marginLeft: '8px', fontSize: '12px' }}>⏳</span>}
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'studio' ? 'active' : ''}`}
                        onClick={() => handleTabChange('studio')}
                    >
                        <Film size={18} />
                        Studio
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'batch' ? 'active' : ''}`}
                        onClick={() => handleTabChange('batch')}
                        style={{ position: 'relative' }}
                    >
                        <Box size={18} />
                        Batch Creation
                        <span style={{
                            fontSize: '10px',
                            fontWeight: '600',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            backgroundColor: '#1976d2',
                            color: 'white'
                        }}>Pro</span>
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'generated' ? 'active' : ''}`}
                        onClick={() => handleTabChange('generated')}
                    >
                        <PlayCircle size={18} />
                        My Generated Videos {generatedTotal > 0 ? `(${generatedTotal})` : ''}
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'voice' ? 'active' : ''}`}
                        onClick={() => handleTabChange('voice')}
                    >
                        <Mic size={18} />
                        Voice
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
                        onClick={() => handleTabChange('profile')}
                    >
                        <User size={18} />
                        Profile
                        {hasUnsavedChanges && (
                            <span style={{
                                marginLeft: '8px',
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: '#ff6b6b',
                                display: 'inline-block'
                            }} title="Unsaved changes" />
                        )}
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'sharing' ? 'active' : ''}`}
                        onClick={() => handleTabChange('sharing')}
                        >
                        <Share2 size={18} />
                        Members
                    </button>
                    </div>
                    {tabCanScrollLeft && (
                        <button
                            type="button"
                            onClick={() => {
                                const el = tabNavScrollRef.current;
                                if (el) el.scrollBy({ left: -200, behavior: 'smooth' });
                            }}
                            className="tab-scroll-arrow tab-scroll-arrow-left"
                            aria-label="Scroll tabs left"
                        >
                            <ChevronLeft size={20} />
                        </button>
                    )}
                    {tabCanScrollRight && (
                        <button
                            type="button"
                            onClick={() => {
                                const el = tabNavScrollRef.current;
                                if (el) el.scrollBy({ left: 200, behavior: 'smooth' });
                            }}
                            className="tab-scroll-arrow tab-scroll-arrow-right"
                            aria-label="Scroll tabs right"
                        >
                            <ChevronRight size={20} />
                        </button>
                    )}
                </div>
            </div> */}

            <div className="tab-content">
                {activeTab === 'home' && ['673247877e4ac0a824cbac0d' /* Boukar */].includes(currentUserId) && (
                    <WorkspaceHomeTab
                        currentWorkspace={currentWorkspace}
                        updateWorkspace={updateWorkspace}
                        videos={videos}
                        workspaceCuts={workspaceCuts}
                        generatedVideos={generatedVideos}
                        onNavigateTab={handleTabChange}
                        onOpenProject={openStudioProject}
                        getlateConnectedAccounts={getlateConnectedAccounts}
                        handleGetlatePublish={handleGetlatePublish}
                        getlatePublishing={getlatePublishing}
                        clearPublishingForVideo={clearPublishingForVideo}
                        onScheduleSuccess={(succeeded) => {
                            if (!succeeded?.length) return;
                            const names = succeeded.map((r) => r.platform.charAt(0).toUpperCase() + r.platform.slice(1));
                            const message = names.length === 2
                                ? 'Video scheduled for Instagram and TikTok successfully!'
                                : `Video scheduled for ${names[0]} successfully!`;
                            setModalSuccessMessage(message);
                            setShowSuccessModal(true);
                            api.getScheduledPosts(currentWorkspace._id)
                                .then((data) => setWorkspaceScheduledPosts(Array.isArray(data) ? data : []))
                                .catch(() => {});
                        }}
                    />
                )}
                {activeTab === 'raw' && (
                    <div className={`raw-videos-tab${bulkSelectedVideoIds.length > 0 ? ' raw-videos-tab--bulk-active' : ''}`}>
                        <h3>Raw Videos</h3>
                    {/* Upload Area */}
                    <div 
                        className={`upload-area raw-upload-split ${isDragging ? 'dragging' : ''}`}
                        onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragging(true);
                        }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={handleDrop}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept="video/*"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                        <label 
                            className="upload-label raw-upload-split__drop"
                            onClick={openWorkspaceFilePicker}
                        >
                            <Plus size={24} strokeWidth={1.5} className="raw-upload-split__icon" />
                            <span className="raw-upload-split__title">Drag & drop your videos here</span>
                            <span className="raw-upload-split__browse">or click to browse</span>
                            <span className="upload-hint">Up to 50 files at once, maximum size of 2GB each</span>
                        </label>
                        <div className="raw-upload-split__divider" aria-hidden="true" style={{ margin: '8px' }} />
                        <div className="raw-upload-split__tags">
                            <label className="raw-upload-split__tags-label">Add tags to this batch (optional)</label>
                            <div className="raw-upload-split__tags-input-wrap">
                                {batchUploadTags.map((tag, index) => (
                                    <span
                                        key={index}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            padding: '2px 6px',
                                            backgroundColor: 'var(--button-bg)',
                                            color: 'var(--button-text)',
                                            borderRadius: '12px',
                                            fontSize: '12px',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {tag}
                                        <button
                                            onClick={() => {
                                                setBatchUploadTags(prev => prev.filter((_, i) => i !== index));
                                            }}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: 'var(--button-text)',
                                                cursor: 'pointer',
                                                padding: '0',
                                                display: 'flex',
                                                alignItems: 'center',
                                                fontSize: '14px',
                                                lineHeight: '1',
                                                opacity: 0.7
                                            }}
                                            onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                                            onMouseOut={(e) => e.currentTarget.style.opacity = '0.7'}
                                        >
                                            <X size={12} />
                                        </button>
                                    </span>
                                ))}
                                <div style={{ position: 'relative', flex: 1, minWidth: '100px' }}>
                                    {renderTagInputWithSuggestions(
                                        'batch-upload-tags',
                                        batchUploadTagInput,
                                        (e) => {
                                            const value = e.target.value;
                                            setBatchUploadTagInput(value);
                                            setShowBatchTagSuggestions(true);
                                        },
                                        (e) => {
                                            if (e.key === 'Enter' || e.key === ',') {
                                                e.preventDefault();
                                                const trimmed = batchUploadTagInput.trim();
                                                if (trimmed && !batchUploadTags.includes(trimmed)) {
                                                    setBatchUploadTags(prev => [...prev, trimmed]);
                                                    setBatchUploadTagInput('');
                                                    setShowBatchTagSuggestions(false);
                                                }
                                            }
                                        },
                                        () => {
                                            const trimmed = batchUploadTagInput.trim();
                                            if (trimmed && !batchUploadTags.includes(trimmed)) {
                                                setBatchUploadTags(prev => [...prev, trimmed]);
                                                setBatchUploadTagInput('');
                                                setShowBatchTagSuggestions(false);
                                            }
                                        },
                                        showBatchTagSuggestions,
                                        setShowBatchTagSuggestions,
                                        batchTagInputRef,
                                        "e.g., selfie, b-roll, BTS",
                                        {
                                            border: 'none',
                                            padding: '2px 4px',
                                            backgroundColor: 'transparent',
                                            color: 'var(--text-color)',
                                            fontSize: '13px',
                                            outline: 'none',
                                            width: '100%',
                                            minWidth: '100px'
                                        },
                                        (tag) => {
                                            if (!batchUploadTags.includes(tag)) {
                                                setBatchUploadTags(prev => [...prev, tag]);
                                                setBatchUploadTagInput('');
                                                setShowBatchTagSuggestions(false);
                                            }
                                        },
                                        {},
                                        commitBatchUploadTagInput
                                    )}
                                </div>
                            </div>
                            <p className="raw-upload-split__tags-hint">Helps Merra AI understand your clips better ✨</p>
                        </div>
                    </div>

                    {/* Upload Progress */}
                    {Object.keys(uploadProgress).length > 0 && (
                        <div className="upload-progress">
                            {/* Upload Summary */}
                            {uploadStats.totalFiles > 0 && (
                                <div className="upload-summary" style={{
                                    padding: '12px',
                                    marginBottom: '16px',
                                    backgroundColor: 'var(--input-bg)',
                                    borderRadius: '8px',
                                    border: '1px solid var(--input-border)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: '8px'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontWeight: 'bold', color: 'var(--text-color)' }}>
                                            Upload in progress
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '16px', fontSize: '14px' }}>
                                        <span style={{ color: '#4CAF50' }}>
                                            ✓ {uploadStats.completedFiles} completed
                                        </span>
                                        {uploadStats.failedFiles > 0 && (
                                            <span style={{ color: '#f44336' }}>
                                                ✗ {uploadStats.failedFiles} failed
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                            
                            {/* Individual File Progress */}
                            {Object.entries(uploadProgress).map(([fileName, progress]) => (
                                <div key={`progress-${fileName}`} className="progress-item">
                                    <span title={fileName}>{fileName.length > 10 ? `${fileName.substring(0, 10)}...` : fileName}</span>
                                    <div className="progress-bar">
                                        <div 
                                            className={`progress-fill ${progress < 100 ? 'progress-bar-shimmer-script' : ''}`}
                                            style={{ width: `${progress}%`, ...(progress < 100 ? { ['--shimmer-duration']: `${Math.max(360, Math.round(1500 * (1 - progress / 100)))}ms` } : {}) }}
                                        />
                                    </div>
                                    <span>{progress}%</span>
                                    <button
                                        onClick={() => cancelUpload(fileName)}
                                        style={{
                                            width: '24px',
                                            height: '24px',
                                            backgroundColor: '#dc3545',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '50%',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '12px',
                                            flexShrink: 0,
                                            marginLeft: '8px'
                                        }}
                                        title="Cancel upload"
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Search Bar */}
                    <div className="raw-videos-search">
                        <Search size={18} className="raw-videos-search__icon" />
                        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                            {renderTagInputWithSuggestions(
                                'video-search',
                                videoSearchQuery,
                                (e) => setVideoSearchQuery(e.target.value),
                                () => {},
                                () => {},
                                showSearchTagSuggestions,
                                setShowSearchTagSuggestions,
                                searchInputRef,
                                "Search videos by name or tags...",
                                {
                                    padding: '4px 0',
                                    borderRadius: 0,
                                    border: 'none',
                                    backgroundColor: 'transparent',
                                    color: 'var(--input-text)',
                                    fontSize: '14px',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    width: '100%'
                                }
                            )}
                        </div>
                        {videoSearchQuery && (
                            <button
                                onClick={() => setVideoSearchQuery('')}
                                style={{
                                    padding: '4px',
                                    borderRadius: '4px',
                                    border: 'none',
                                    backgroundColor: 'transparent',
                                    color: 'var(--text-secondary)',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxSizing: 'border-box',
                                    flexShrink: 0
                                }}
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>

                    {/* Video Grid */}
                    {isLoading ? (
                        <div className="shimmer-grid">
                            {[...Array(6)].map((_, index) => (
                                <div key={`shimmer-${index}`} className="shimmer-card">
                                    <div className="shimmer-preview"></div>
                                    <div className="shimmer-content">
                                        <div className="shimmer-title shimmer"></div>
                                        <div className="shimmer-tags">
                                            <div className="shimmer-tag shimmer"></div>
                                            <div className="shimmer-tag shimmer"></div>
                                            <div className="shimmer-tag shimmer"></div>
                                        </div>
                                        <div className="shimmer-description shimmer"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="video-grid">
                            {(() => {
                                const filteredVideos = videos.filter(video => {
                                    if (!videoSearchQuery.trim()) return true;
                                    const query = videoSearchQuery.toLowerCase();
                                    const nameMatch = video.name?.toLowerCase().includes(query);
                                    const tagMatch = video.tags?.some(tag => tag.toLowerCase().includes(query));
                                    return nameMatch || tagMatch;
                                });

                                if (filteredVideos.length === 0 && videoSearchQuery.trim()) {
                                    return (
                                        <div style={{
                                            gridColumn: '1 / -1',
                                            textAlign: 'center',
                                            padding: '40px 20px',
                                            color: 'var(--text-secondary)'
                                        }}>
                                            <Search size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                                            <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-color)' }}>No videos found</h3>
                                            <p style={{ margin: '0' }}>
                                                No videos match "{videoSearchQuery}". Try adjusting your search terms.
                                            </p>
                                        </div>
                                    );
                                }

                                return filteredVideos.map(video => (
                                <div key={video._id} className={`video-card${bulkSelectedVideoIds.includes(video._id) ? ' video-card--selected' : ''}`}>
                                    <div className="video-preview" style={{objectFit: 'contain'}}>
                                        <label
                                            className="video-bulk-select"
                                            onClick={(e) => e.stopPropagation()}
                                            title="Select video"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={bulkSelectedVideoIds.includes(video._id)}
                                                onChange={() => toggleBulkVideoSelection(video._id)}
                                            />
                                        </label>
                                        <video 
                                            src={video.url} 
                                            controls 
                                            preload={video.thumbnailUrl ? 'none' : 'metadata'}
                                            poster={video.thumbnailUrl || undefined}
                                            crossOrigin="anonymous"
                                            playsInline
                                            // muted
                                            style={{objectFit: 'contain'}}
                                            // onLoadedMetadata={(e) => {
                                            //     // Set preview to the middle of the video for better thumbnail
                                            //     if (e.target.duration > 0) {
                                            //         e.target.currentTime = Math.min(e.target.duration / 2, 1);
                                            //     }
                                            // }}
                                        >
                                            <source src={video.url} type="video/mp4" />
                                            Your browser does not support the video tag.
                                        </video>
                                    </div>
                                    <div className="video-info">
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '12px',
                                            alignContent: 'center',
                                            ...(window.innerWidth <= 600 ? {
                                                flexDirection: 'column',
                                                alignItems: 'flex-start',
                                                gap: '8px'
                                            } : {})
                                        }}>
                                            <div style={{ 
                                                flex: window.innerWidth <= 600 ? 'none' : 1, 
                                                minWidth: 0,
                                                width: window.innerWidth <= 600 ? '100%' : 'auto'
                                            }}>
                                                <h3 style={{
                                                    margin: '0 0 4px 0',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    maxWidth: '100%'
                                                }}>{video.name}</h3>
                                                {video.createdAt && (
                                                    <div style={{
                                                        fontSize: '12px',
                                                        color: 'var(--text-secondary)',
                                                        opacity: 0.7
                                                    }}>
                                                        {new Date(video.createdAt).toLocaleDateString()}
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{ 
                                                display: 'flex', 
                                                gap: '8px',
                                                width: window.innerWidth <= 600 ? '100%' : 'auto',
                                                justifyContent: window.innerWidth <= 600 ? 'flex-start' : 'flex-end'
                                            }}>
                                                <button 
                                                    className="download-video-btn"
                                                    onClick={() => handleDownloadVideo(video)}
                                                    disabled={downloadingVideos.has(video._id)}
                                                    style={{
                                                        backgroundColor: 'var(--button-bg)',
                                                        color: 'var(--button-text)',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        padding: '4px 8px',
                                                        cursor: downloadingVideos.has(video._id) ? 'not-allowed' : 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        opacity: downloadingVideos.has(video._id) ? '0.6' : '0.9',
                                                        transition: 'opacity 0.2s'
                                                    }}
                                                    onMouseOver={(e) => !downloadingVideos.has(video._id) && (e.currentTarget.style.opacity = '1')}
                                                    onMouseOut={(e) => !downloadingVideos.has(video._id) && (e.currentTarget.style.opacity = '0.9')}
                                                >
                                                    {downloadingVideos.has(video._id) ? (
                                                        <span className="spinner-border" style={{ width: 14, height: 14, borderWidth: '2px' }} />
                                                    ) : (
                                                    <Download size={14} />
                                                    )}
                                                </button>
                                                <button 
                                                    className="delete-video-btn"
                                                    onClick={() => handleDeleteVideo(video._id)}
                                                    style={{
                                                        backgroundColor: 'var(--input-bg)',
                                                        color: 'var(--input-text)',
                                                        border: '1px solid var(--input-border)',
                                                        borderRadius: '4px',
                                                        padding: '4px 8px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        opacity: '0.9',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onMouseOver={(e) => {
                                                        e.currentTarget.style.opacity = '1';
                                                        e.currentTarget.style.backgroundColor = '#ff4444';
                                                        e.currentTarget.style.color = 'white';
                                                        e.currentTarget.style.borderColor = '#ff4444';
                                                    }}
                                                    onMouseOut={(e) => {
                                                        e.currentTarget.style.opacity = '0.9';
                                                        e.currentTarget.style.backgroundColor = 'var(--input-bg)';
                                                        e.currentTarget.style.color = 'var(--text-color)';
                                                        e.currentTarget.style.borderColor = 'var(--input-border)';
                                                    }}
                                                >
                                                        <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {/* Tags */}
                                        <div className="video-tags">
                                            {(video.tags || []).map(tag => (
                                                <span key={`${video._id}-${tag}`} className="tag">
                                                    {tag}
                                                    <button 
                                                        className="remove-tag"
                                                        onClick={() => handleRemoveTag(video._id, tag)}
                                                    >
                                                        <X size={14} style={{paddingTop: '3px'}}/>
                                                    </button>
                                                </span>
                                            ))}
                                            <div className="add-tag" style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <div style={{ position: 'relative', flex: 1, minWidth: 0, maxWidth: '150px', paddingRight: '20px' }}>
                                                    {renderTagInputWithSuggestions(
                                                        `video-${video._id}`,
                                                        tagInputs[video._id] || '',
                                                        (e) => setTagInputs(prev => ({
                                                            ...prev,
                                                            [video._id]: e.target.value
                                                        })),
                                                        (e) => {
                                                            if (e.key === 'Enter') {
                                                                handleAddTag(video._id);
                                                            }
                                                        },
                                                        () => handleAddTag(video._id),
                                                        tagSuggestionDropdowns[`video-${video._id}`] || false,
                                                        (show) => setTagSuggestionDropdowns(prev => ({
                                                            ...prev,
                                                            [`video-${video._id}`]: show
                                                        })),
                                                        tagInputRefs.current[`video-${video._id}`],
                                                        "Add tag...",
                                                        {
                                                            width: '100%'
                                                        },
                                                        (tag) => {
                                                            // Auto-add tag when clicked from dropdown
                                                            setTagInputs(prev => ({
                                                                ...prev,
                                                                [video._id]: tag
                                                            }));
                                                            handleAddTag(video._id);
                                                        },
                                                        {
                                                            zIndex: 10001,
                                                            maxHeight: '75px',
                                                        }
                                                    )}
                                                </div>
                                                <button onClick={() => handleAddTag(video._id)} style={{borderRadius: '20%', position: 'relative'}}>
                                                    <Plus size={14} style={{paddingTop: '3px'}} />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Description */}
                                        <div className="video-description">
                                            {editingVideo === video._id ? (
                                                <div className="edit-description">
                                                    <textarea
                                                        value={newDescription}
                                                        onChange={(e) => setNewDescription(e.target.value)}
                                                        placeholder="Add a description..."
                                                        style={{ fontFamily: SYSTEM_UI_FONT, fontSize: '14px', boxSizing: 'border-box' }}
                                                        // autoFocus
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                                e.preventDefault();
                                                                handleUpdateDescription(video._id);
                                                            } else if (e.key === 'Escape') {
                                                                setEditingVideo(null);
                                                                setNewDescription('');
                                                            }
                                                        }}
                                                    />
                                                    <div className="description-actions">
                                                        <button onClick={() => handleUpdateDescription(video._id)}>
                                                            Save
                                                        </button>
                                                        <button onClick={() => {
                                                            setEditingVideo(null);
                                                            setNewDescription('');
                                                        }}>
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div 
                                                    className="description-display"
                                                    onClick={() => {
                                                        setEditingVideo(video._id);
                                                        setNewDescription(video.description || '');
                                                    }}
                                                    style={{ cursor: 'pointer', fontFamily: SYSTEM_UI_FONT, boxSizing: 'border-box' }}
                                                >
                                                    <p style={{ fontFamily: SYSTEM_UI_FONT, boxSizing: 'border-box' }}>{video.description || 'Click to add a description...'}</p>
                                                    <button 
                                                        className="edit-description-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setEditingVideo(video._id);
                                                            setNewDescription(video.description || '');
                                                        }}
                                                        style={{borderRadius: '20%'}}
                                                    >
                                                        <Edit2 size={14} style={{paddingTop: '3px'}} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        <button 
                                            className="generate-cuts-btn"
                                            onClick={() => handleGenerateCuts(video)}
                                            disabled={generatingCuts.has(video._id) || video.cutsGenerationStatus === 'generating'}
                                            title="Generate automatic cuts from this video"
                                            style={{
                                                backgroundColor: video.cutsGenerationStatus === 'generating' ? '#4CAF50' : 'var(--button-bg)',
                                                color: 'var(--button-text)',
                                                border: 'none',
                                                borderRadius: '4px',
                                                padding: '4px 8px',
                                                marginBottom: '10px',
                                                cursor: (generatingCuts.has(video._id) || video.cutsGenerationStatus === 'generating') ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                opacity: (generatingCuts.has(video._id) || video.cutsGenerationStatus === 'generating') ? '0.6' : '0.9',
                                                transition: 'opacity 0.2s',
                                            }}
                                            onMouseOver={(e) => !(generatingCuts.has(video._id) || video.cutsGenerationStatus === 'generating') && (e.currentTarget.style.opacity = '1')}
                                            onMouseOut={(e) => !(generatingCuts.has(video._id) || video.cutsGenerationStatus === 'generating') && (e.currentTarget.style.opacity = '0.9')}
                                        >
                                            {(generatingCuts.has(video._id) || video.cutsGenerationStatus === 'generating') ? (
                                                <>
                                                    <Loader size={14} className="spin" />
                                                    {window.innerWidth > 600 && <span style={{ fontSize: '12px' }}>Generating...</span>}
                                                </>
                                            ) : (
                                                <>
                                                    <Scissors size={14} />
                                                    {window.innerWidth > 600 && <span style={{ fontSize: '12px' }}>Generate Cuts</span>}
                                                </>
                                            )}
                                        </button>

                                        {/* Only show generate shorts button for Boukar, Oleg, Mauricio and Ismael */}
                                        {['673247877e4ac0a824cbac0d' /*Boukar*/, '68c2c068253b59e3eb46452c' /*Oleg*/, '68c2b8da253b59e3eb4644ff' /*Mauricio*/, '698da27e796493d5a4557a0e' /* Ismael */].includes(currentUserId) && false && (
                                            <button 
                                                className="generate-shorts-btn"
                                                onClick={() => handleGenerateShorts(video)}
                                                disabled={generatingShorts.has(video._id) || video.shortsGenerationStatus === 'generating'}
                                                title="Generate short clips using Vizard AI"
                                                style={{
                                                    backgroundColor: video.shortsGenerationStatus === 'generating' ? '#AB589850' : '#AB5898',
                                                    color: 'var(--button-text)',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    padding: '4px 8px',
                                                    cursor: (generatingShorts.has(video._id) || video.shortsGenerationStatus === 'generating') ? 'not-allowed' : 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    opacity: (generatingShorts.has(video._id) || video.shortsGenerationStatus === 'generating') ? '0.6' : '0.9',
                                                    transition: 'opacity 0.2s'
                                                }}
                                                onMouseOver={(e) => !(generatingShorts.has(video._id) || video.shortsGenerationStatus === 'generating') && (e.currentTarget.style.opacity = '1')}
                                                onMouseOut={(e) => !(generatingShorts.has(video._id) || video.shortsGenerationStatus === 'generating') && (e.currentTarget.style.opacity = '0.9')}
                                            >
                                                {(generatingShorts.has(video._id) || video.shortsGenerationStatus === 'generating') ? (
                                                    <>
                                                        <Loader size={14} className="spin" />
                                                        <span style={{ fontSize: '12px' }}>Generating...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Video size={14} />
                                                        <span style={{ fontSize: '12px' }}>Generate Shorts</span>
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>
                                ));
                            })()}
                        </div>
                    )}
                    {/* Infinite scroll sentinel */}
                    <div ref={loadMoreRef} style={{ height: '1px' }} />

                    {bulkSelectedVideoIds.length > 0 && (
                        <div className="raw-videos-bulk-bar" role="toolbar" aria-label="Bulk video actions">
                            <div className="raw-videos-bulk-bar__count">
                                {bulkSelectedVideoIds.length} selected
                            </div>

                            {bulkActionMenu === 'tags' ? (
                                <div className="raw-videos-bulk-bar__panel">
                                    <textarea
                                        className="raw-videos-bulk-bar__input"
                                        value={bulkTagsInput}
                                        onChange={(e) => setBulkTagsInput(e.target.value)}
                                        placeholder="Tags (comma or line separated)"
                                        rows={2}
                                        disabled={bulkActionLoading}
                                    />
                                    <div className="raw-videos-bulk-bar__panel-actions">
                                        <button
                                            type="button"
                                            className="main-button"
                                            onClick={handleBulkAddTags}
                                            disabled={bulkActionLoading || !bulkTagsInput.trim()}
                                        >
                                            {bulkActionLoading ? 'Adding…' : 'Apply tags'}
                                        </button>
                                        <button
                                            type="button"
                                            className="tertiary-button"
                                            onClick={() => { setBulkActionMenu(null); setBulkTagsInput(''); }}
                                            disabled={bulkActionLoading}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : bulkActionMenu === 'rename' ? (
                                <div className="raw-videos-bulk-bar__panel">
                                    <input
                                        type="text"
                                        className="raw-videos-bulk-bar__input raw-videos-bulk-bar__input--single"
                                        value={bulkRenameInput}
                                        onChange={(e) => setBulkRenameInput(e.target.value)}
                                        placeholder="Base name (videos become Name 1, Name 2…)"
                                        disabled={bulkActionLoading}
                                    />
                                    <div className="raw-videos-bulk-bar__panel-actions">
                                        <button
                                            type="button"
                                            className="main-button"
                                            onClick={handleBulkRename}
                                            disabled={bulkActionLoading || !bulkRenameInput.trim()}
                                        >
                                            {bulkActionLoading ? 'Renaming…' : 'Rename'}
                                        </button>
                                        <button
                                            type="button"
                                            className="tertiary-button"
                                            onClick={() => { setBulkActionMenu(null); setBulkRenameInput(''); }}
                                            disabled={bulkActionLoading}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : bulkActionMenu === 'delete' ? (
                                <div className="raw-videos-bulk-bar__panel">
                                    <p className="raw-videos-bulk-bar__confirm-text">
                                        Delete {bulkSelectedVideoIds.length} video{bulkSelectedVideoIds.length !== 1 ? 's' : ''}? This cannot be undone.
                                    </p>
                                    <div className="raw-videos-bulk-bar__panel-actions">
                                        <button
                                            type="button"
                                            className="main-button"
                                            style={{ backgroundColor: '#dc3545', color: '#fff' }}
                                            onClick={handleBulkDelete}
                                            disabled={bulkActionLoading}
                                        >
                                            {bulkActionLoading ? 'Deleting…' : 'Confirm delete'}
                                        </button>
                                        <button
                                            type="button"
                                            className="tertiary-button"
                                            onClick={() => setBulkActionMenu(null)}
                                            disabled={bulkActionLoading}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="raw-videos-bulk-bar__actions">
                                    <button type="button" className="tertiary-button" onClick={() => setBulkActionMenu('tags')}>
                                        Add tags
                                    </button>
                                    <button type="button" className="tertiary-button" onClick={() => setBulkActionMenu('rename')}>
                                        Rename
                                    </button>
                                    <button type="button" className="tertiary-button" onClick={() => setBulkActionMenu('delete')}>
                                        Delete
                                    </button>
                                </div>
                            )}

                            <button
                                type="button"
                                className="raw-videos-bulk-bar__close"
                                onClick={clearBulkVideoSelection}
                                aria-label="Clear selection"
                                disabled={bulkActionLoading}
                            >
                                <X size={18} />
                            </button>
                        </div>
                    )}
                    </div>
                )}

                {activeTab === 'batch' && !canAccessBatch && (
                    <div className="batch-creation-tab" style={{ paddingTop: '8px' }}>
                        <div style={{
                            marginTop: '16px',
                            padding: '12px',
                            backgroundColor: 'var(--input-bg)',
                            color: 'var(--text-color)',
                            border: '1px solid #FFD700',
                            borderRadius: '8px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignContent: 'center',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center'
                        }}>
                            {hasPermission('admin') ? (
                                <>
                                    <div style={{ marginBottom: '10px' }}>
                                        Batch Creation is a Pro feature. To use it, please upgrade your workspace plan.
                                    </div>
                                    {/* Feature Comparison */}
                                    <div style={{ marginBottom: '20px' }}>
                                        <h4 style={{ marginBottom: '15px', textAlign: 'center', color: 'var(--text-color)' }}>Choose Your Plan</h4>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                                            {/* Creator Plan */}
                                            <div style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--input-border)', borderRadius: '8px', padding: '15px', textAlign: 'center' }}>
                                                <h5 style={{ marginTop: 0, marginBottom: '10px', color: 'var(--text-color)' }}>Creator</h5>
                                                <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px', color: 'var(--text-color)' }}>{currency}25/month</div>
                                                <ul style={{ textAlign: 'left', fontSize: '12px', color: 'var(--text-secondary)', margin: 0, paddingLeft: '15px' }}>
                                                    <li><span style={{ textDecoration: 'line-through' }}>20 videos per month</span> Unlimited videos</li>
                                                    <li>Import raw content</li>
                                                    <li>Generate hooks, scripts, voiceovers</li>
                                                    <li>Generate videos</li>
                                                    <li>50GB storage</li>
                                                </ul>
                                            </div>
                                            {/* Pro Plan */}
                                            <div style={{ backgroundColor: 'var(--card-bg)', border: '2px solid #1976d2', borderRadius: '8px', padding: '15px', textAlign: 'center', position: 'relative' }}>
                                                <div style={{ position: 'absolute', top: '-8px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#1976d2', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>POPULAR</div>
                                                <h5 style={{ marginTop: '8px', marginBottom: '10px', color: 'var(--text-color)' }}>Pro</h5>
                                                <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px', color: 'var(--text-color)' }}>{currency}99/month</div>
                                                <ul style={{ textAlign: 'left', fontSize: '12px', color: 'var(--text-secondary)', margin: 0, paddingLeft: '15px' }}>
                                                    <li>150 videos per month</li>
                                                    <li>Import raw content</li>
                                                    <li>Generate hooks, scripts, voiceovers</li>
                                                    <li>Generate videos</li>
                                                    <li>200GB storage per profile</li>
                                                    <li>+ Invite team members</li>
                                                    <li>+ Batch Creation</li>
                                                    <li>+ Multiple brand profiles (+{currency}99 per profile)</li>
                                                </ul>
                                                <p>Become an infinite content machine for {currency}99/month</p>
                                            </div>
                                            {/* Custom Solution */}
                                            <div style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--input-border)', borderRadius: '8px', padding: '15px', textAlign: 'center' }}>
                                                <h5 style={{ marginTop: 0, marginBottom: '10px', color: 'var(--text-color)' }}>Custom</h5>
                                                <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px', color: 'var(--text-color)' }}>Contact Us</div>
                                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textAlign: 'center' }}>
                                                    <p style={{ margin: '0 0 8px 0' }}>Need more? Reach out for custom solution</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ marginBottom: '15px', fontSize: '14px', color: 'var(--text-color-secondary)' }}>
                                        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-color-secondary)', textDecoration: 'underline', marginRight: '15px' }}>Privacy Policy</a>
                                        <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-color-secondary)', textDecoration: 'underline' }}>Terms of Service</a>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                        <button className="main-button" onClick={async () => { try { const response = await api.createCheckoutSession('creator', currentWorkspace._id); window.location.href = response.data.url; } catch (e) { setErrors(prev => [...prev, 'Failed to start checkout for Creator plan']); } }}>
                                            Subscribe: Merra AI Creator — {currency}25/month
                                        </button>
                                        <button className="main-button" onClick={async () => { try { const response = await api.createCheckoutSession('pro', currentWorkspace._id); window.location.href = response.data.url; } catch (e) { setErrors(prev => [...prev, 'Failed to start checkout for Pro plan']); } }}>
                                            Subscribe: Merra AI Pro — {currency}99/month
                                        </button>
                                    </div>
                                    <div style={{ marginTop: '0px', textAlign: 'center' }}>
                                        <p style={{ marginBottom: '10px', color: 'var(--text-secondary)', fontSize: '14px' }}>or</p>
                                        <button className="main-button" onClick={() => { window.open('https://calendly.com/boukar-o-sall/30min', '_blank'); }} style={{ backgroundColor: 'transparent', border: darkMode ? '1px solid #e1f679' : '1px solid var(--text-secondary)', color: darkMode ? '#e1f679' : 'black', boxShadow: darkMode ? '0 4px 20px rgba(0,0,0,0.2)' : '0 4px 20px rgba(0,0,0,0.1)' }}>
                                            Book a feedback call with us and get a month for free
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div style={{ marginBottom: '10px' }}>
                                        Batch Creation is a Pro feature. To use it, ask your workspace administrator to upgrade the workspace plan.
                                    </div>
                                    <div style={{ marginBottom: '20px' }}>
                                        <h4 style={{ marginBottom: '15px', textAlign: 'center', color: 'var(--text-color)' }}>Available Plans</h4>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                                            <div style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--input-border)', borderRadius: '8px', padding: '15px', textAlign: 'center' }}>
                                                <h5 style={{ marginTop: 0, marginBottom: '10px', color: 'var(--text-color)' }}>Creator</h5>
                                                <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px', color: 'var(--text-color)' }}>{currency}25/month</div>
                                                <ul style={{ textAlign: 'left', fontSize: '12px', color: 'var(--text-secondary)', margin: 0, paddingLeft: '15px' }}>
                                                    <li><span style={{ textDecoration: 'line-through' }}>20 videos per month</span> Unlimited videos</li>
                                                    <li>Import raw content</li>
                                                    <li>Generate hooks, scripts, voiceovers</li>
                                                    <li>Generate videos</li>
                                                    <li>50GB storage</li>
                                                </ul>
                                            </div>
                                            <div style={{ backgroundColor: 'var(--card-bg)', border: '2px solid #1976d2', borderRadius: '8px', padding: '15px', textAlign: 'center', position: 'relative' }}>
                                                <div style={{ position: 'absolute', top: '-8px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#1976d2', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>POPULAR</div>
                                                <h5 style={{ marginTop: '8px', marginBottom: '10px', color: 'var(--text-color)' }}>Pro</h5>
                                                <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px', color: 'var(--text-color)' }}>{currency}99/month</div>
                                                <ul style={{ textAlign: 'left', fontSize: '12px', color: 'var(--text-secondary)', margin: 0, paddingLeft: '15px' }}>
                                                    <li>150 videos per month</li>
                                                    <li>Import raw content</li>
                                                    <li>Generate hooks, scripts, voiceovers</li>
                                                    <li>Generate videos</li>
                                                    <li>200GB storage per profile</li>
                                                    <li>+ Invite team members</li>
                                                    <li>+ Batch Creation</li>
                                                    <li>+ Multiple brand profiles (+{currency}99 per profile)</li>
                                                </ul>
                                                <p>Become an infinite content machine for {currency}99/month</p>
                                            </div>
                                            <div style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--input-border)', borderRadius: '8px', padding: '15px', textAlign: 'center' }}>
                                                <h5 style={{ marginTop: 0, marginBottom: '10px', color: 'var(--text-color)' }}>Custom</h5>
                                                <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px', color: 'var(--text-color)' }}>Contact Us</div>
                                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textAlign: 'center' }}>
                                                    <p style={{ margin: '0 0 8px 0' }}>Need more? Reach out for custom solution</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'batch' && canAccessBatch && (
                    <div className="batch-creation-tab" style={{ paddingTop: '8px' }}>
                        <div style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '12px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
                                <h3 style={{ marginTop: 0, marginBottom: 0 }}>Batch Video Generation</h3>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: 'var(--text-color)' }}>
                                    <input
                                        type="checkbox"
                                        checked={batchAutopilot}
                                        onChange={(e) => {
                                            const v = e.target.checked;
                                            setBatchAutopilot(v);
                                            try { localStorage.setItem('batch_autopilot', String(v)); } catch (_) {}
                                        }}
                                        style={{ accentColor: 'var(--button-bg)' }}
                                    />
                                    Autopilot
                                </label>
                            </div>
                            {/* Batch mix bar (text-based vs script-based) — commented out; replaced by count selectors below */}
                            {/* <div style={{ marginBottom: '16px' }}>
                              ...
                            </div> */}

                            {(() => {
                                const isBoukar = currentUserId === '673247877e4ac0a824cbac0d';
                                const total = Math.max(1, Math.min(10, Number(batchTotalVideos) || 10));
                                const text = (isBoukar && batchIncludeTextBased) ? Math.max(0, Math.min(total, Number(batchTextVideos) || 0)) : 0;
                                const scripts = Math.max(0, total - text);
                                const disabled = batchGenerating || batchAutopilotActive;

                                const clampTotal = (v) => Math.max(1, Math.min(10, v));
                                const clampText = (v) => Math.max(0, Math.min(total, v));
                                const stepBtn = (extra = {}) => ({
                                    width: 26,
                                    height: 26,
                                    padding: 0,
                                    borderRadius: '6px',
                                    border: '1px solid var(--input-border)',
                                    background: 'var(--input-bg)',
                                    color: 'var(--text-color)',
                                    fontSize: '16px',
                                    lineHeight: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    flexShrink: 0,
                                    ...extra,
                                });

                                return (
                                    <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: '13px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>How many videos?</span>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                <button
                                                    type="button"
                                                    disabled={disabled || total <= 1}
                                                    onClick={() => {
                                                        const v = clampTotal(total - 1);
                                                        setBatchTotalVideos(v);
                                                        try { localStorage.setItem('batch_total_videos', String(v)); } catch (_) {}
                                                        if (isBoukar && batchIncludeTextBased) {
                                                            const t = clampText(text);
                                                            if (t !== text) {
                                                                setBatchTextVideos(t);
                                                                try { localStorage.setItem('batch_text_videos', String(t)); } catch (_) {}
                                                            }
                                                        }
                                                    }}
                                                    style={stepBtn({ opacity: disabled || total <= 1 ? 0.45 : 1 })}
                                                    aria-label="Decrease video count"
                                                >
                                                    −
                                                </button>
                                                <span style={{ minWidth: 22, textAlign: 'center', fontSize: '13px', fontWeight: 600, color: 'var(--text-color)' }}>{total}</span>
                                                <button
                                                    type="button"
                                                    disabled={disabled || total >= 10}
                                                    onClick={() => {
                                                        const v = clampTotal(total + 1);
                                                        setBatchTotalVideos(v);
                                                        try { localStorage.setItem('batch_total_videos', String(v)); } catch (_) {}
                                                    }}
                                                    style={stepBtn({ opacity: disabled || total >= 10 ? 0.45 : 1 })}
                                                    aria-label="Increase video count"
                                                >
                                                    +
                                                </button>
                                            </span>
                                            <span style={{ width: 1, height: 22, background: 'var(--input-border)', flexShrink: 0, alignSelf: 'center', opacity: 0.85 }} aria-hidden />
                                            <span style={{ fontSize: '13px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Duration</span>
                                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }} role="group" aria-label="Script duration">
                                                {[15, 30, 60].map((sec) => (
                                                    <button
                                                        key={sec}
                                                        type="button"
                                                        disabled={disabled}
                                                        onClick={() => {
                                                            setBatchScriptDuration(sec);
                                                            try { localStorage.setItem('batch_script_duration_sec', String(sec)); } catch (_) {}
                                                        }}
                                                        style={{
                                                            padding: '6px 12px',
                                                            borderRadius: '8px',
                                                            border: `1px solid ${batchScriptDuration === sec ? 'var(--button-bg)' : 'var(--input-border)'}`,
                                                            background: batchScriptDuration === sec ? 'var(--button-bg)' : 'var(--input-bg)',
                                                            color: batchScriptDuration === sec ? 'var(--button-text)' : 'var(--text-color)',
                                                            fontSize: '13px',
                                                            fontWeight: batchScriptDuration === sec ? 600 : 500,
                                                            cursor: disabled ? 'not-allowed' : 'pointer',
                                                            opacity: disabled ? 0.6 : 1
                                                        }}
                                                    >
                                                        {sec}s
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                                            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Topic (optional)</span>
                                            <textarea
                                                value={batchTopic}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setBatchTopic(v);
                                                    try { localStorage.setItem('batch_topic', v); } catch (_) {}
                                                }}
                                                disabled={disabled}
                                                rows={2}
                                                placeholder="What should these scripts focus on?"
                                                style={{
                                                    width: '100%',
                                                    resize: 'vertical',
                                                    minHeight: 44,
                                                    padding: '8px 10px',
                                                    borderRadius: '8px',
                                                    border: '1px solid var(--input-border)',
                                                    background: 'var(--input-bg)',
                                                    color: 'var(--text-color)',
                                                    fontSize: '13px',
                                                    opacity: disabled ? 0.6 : 1,
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                        </label>

                                        {isBoukar && false && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '12px', color: 'var(--text-color)' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={batchIncludeTextBased}
                                                        onChange={(e) => {
                                                            const v = e.target.checked;
                                                            setBatchIncludeTextBased(v);
                                                            try { localStorage.setItem('batch_include_text_based', String(v)); } catch (_) {}
                                                            if (!v) {
                                                                setBatchTextVideos(0);
                                                                try { localStorage.setItem('batch_text_videos', '0'); } catch (_) {}
                                                            }
                                                        }}
                                                        disabled={disabled}
                                                        style={{ accentColor: 'var(--button-bg)' }}
                                                    />
                                                    Text-based
                                                </label>
                                                {batchIncludeTextBased ? (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                        <button
                                                            type="button"
                                                            disabled={disabled || text <= 0}
                                                            onClick={() => {
                                                                const v = clampText(text - 1);
                                                                setBatchTextVideos(v);
                                                                try { localStorage.setItem('batch_text_videos', String(v)); } catch (_) {}
                                                            }}
                                                            style={stepBtn({ opacity: disabled || text <= 0 ? 0.45 : 1 })}
                                                            aria-label="Decrease text-based count"
                                                        >
                                                            −
                                                        </button>
                                                        <span style={{ minWidth: 22, textAlign: 'center', fontSize: '13px', fontWeight: 600, color: 'var(--text-color)' }}>{text}</span>
                                                        <button
                                                            type="button"
                                                            disabled={disabled || text >= total}
                                                            onClick={() => {
                                                                const v = clampText(text + 1);
                                                                setBatchTextVideos(v);
                                                                try { localStorage.setItem('batch_text_videos', String(v)); } catch (_) {}
                                                            }}
                                                            style={stepBtn({ opacity: disabled || text >= total ? 0.45 : 1 })}
                                                            aria-label="Increase text-based count"
                                                        >
                                                            +
                                                        </button>
                                                    </span>
                                                ) : null}
                                            </div>
                                        )}

                                        {/* <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                                            Total: <span style={{ color: 'var(--text-color)', fontWeight: 600 }}>{total}</span> — Scripts: <span style={{ color: 'var(--text-color)', fontWeight: 600 }}>{scripts}</span>{isBoukar && batchIncludeTextBased ? <> — Text-based: <span style={{ color: 'var(--text-color)', fontWeight: 600 }}>{text}</span></> : null}
                                        </div> */}
                                    </div>
                                );
                            })()}
                            {/* <div style={{ 
                                fontSize: '12px', 
                                color: 'var(--text-secondary)', 
                                marginBottom: '16px',
                                padding: '8px',
                                backgroundColor: 'var(--card-bg)',
                                borderRadius: '4px'
                            }}>
                                💡 Each script will be generated with a different hook automatically. These settings will apply to all videos generated in batch mode. Configure voice, music, font, and generation options below.
                            </div> */}
                            {(() => {
                                const batchGenVideosCount = Math.max(1, Math.min(10, Number(batchTotalVideos) || 10));
                                const batchGenerateVideosLabel = `Generate ${batchGenVideosCount} video${batchGenVideosCount === 1 ? '' : 's'}`;
                                return (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        <button
                                            onClick={batchAutopilot ? startBatchAutopilot : startBatchGenerate}
                                            disabled={batchGenerating || batchAutopilotActive}
                                            className={batchAutopilot ? 'waitlist-button' : 'main-button'}
                                            aria-label={batchGenerateVideosLabel}
                                            onMouseEnter={(e) => {
                                                if (!(batchGenerating || batchAutopilotActive) && batchAutopilot) e.target.style.opacity = '0.9';
                                            }}
                                            onMouseLeave={(e) => {
                                                if (batchAutopilot) e.target.style.opacity = (batchGenerating || batchAutopilotActive) ? 0.6 : 1;
                                            }}
                                            style={{
                                                opacity: (batchGenerating || batchAutopilotActive) ? 0.6 : 1,
                                                cursor: (batchGenerating || batchAutopilotActive) ? 'not-allowed' : 'pointer',
                                                position: 'relative',
                                                overflow: 'hidden',
                                                flex: batchAutopilot ? 1 : undefined
                                            }}
                                        >
                                            {(batchGenerating || batchAutopilotActive) && (
                                                <span className="spinner-border" style={{ marginRight: '8px' }} />
                                            )}
                                            <span style={{ color: batchAutopilot ? 'black' : undefined, fontWeight: 'bold' }}>
                                                {(batchGenerating || batchAutopilotActive)
                                                    ? (batchAutopilotActive ? 'Auto-creating...' : 'Generating...')
                                                    : batchGenerateVideosLabel}
                                            </span>
                                            {batchAutopilot && <span className="waitlist-shimmer"></span>}
                                        </button>
                                        <div style={{ textAlign: 'center', fontSize: '14px', color: 'var(--text-secondary)' }}>
                                            OR
                                        </div>
                                        <button
                                            onClick={() => {
                                                // Create N empty custom script entries (N = batch total)
                                                // Use 'custom-' prefix so backend recognizes them as custom scripts
                                                const n = Math.max(1, Math.min(10, Number(batchTotalVideos) || 10));
                                                const customScripts = Array.from({ length: n }, (_, i) => ({
                                                    _id: `custom-batch-${Date.now()}-${i}`,
                                                    content: '',
                                                    contentAlt: '',
                                                    contentThird: '',
                                                    title: `Custom Script ${i + 1}`,
                                                    type: 'voiceover',
                                                    selectedVersion: 'main',
                                                    isCustom: true
                                                }));
                                                setBatchScripts(customScripts);
                                                // Clear any existing music selections
                                                setBatchScriptMusicSelections({});
                                                setBatchShowMusicDropdowns({});
                                            }}
                                            disabled={batchGenerating || batchAutopilotActive}
                                            className="tertiary-button"
                                            style={{
                                                opacity: (batchGenerating || batchAutopilotActive) ? 0.6 : 1,
                                                cursor: (batchGenerating || batchAutopilotActive) ? 'not-allowed' : 'pointer',
                                                fontWeight: 'bold',
                                                justifyContent: 'center',
                                                textAlign: 'center'
                                            }}
                                        >
                                            Use your own scripts
                                        </button>
                                    </div>
                                );
                            })()}
                            {(batchGenerating || batchAutopilotActive) && (
                                <div style={{ marginTop: '12px' }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        backgroundColor: 'var(--card-bg)',
                                        border: '1px solid var(--input-border)',
                                        borderRadius: '8px',
                                        padding: '12px'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span className="spinner-border" />
                                            <span style={{ fontSize: '14px', color: 'var(--text-color)' }}>
                                                {batchAutopilotActive
                                                    ? `Auto-creating... ${batchScripts.length}/10 scripts (voiceovers & videos in progress)`
                                                    : `${batchProgress.currentStep || 'Generating...'} ${batchProgress.current}/${batchProgress.total} scripts`}
                                            </span>
                                        </div>
                                        <div style={{ width: '160px' }}>
                                            <div style={{
                                                width: '100%', height: '4px', backgroundColor: 'var(--input-border)',
                                                borderRadius: '2px', overflow: 'hidden'
                                            }}>
                                                <div className="progress-bar-shimmer-script" style={{
                                                    width: `${batchAutopilotActive ? Math.round((batchScripts.length / 10) * 100) : batchProgress.percent}%`, height: '4px', backgroundColor: 'var(--button-bg)',
                                                    transition: 'width 0.3s ease-in-out',
                                                    ['--shimmer-duration']: `${Math.max(360, Math.round(1500 * (1 - Math.min(100, batchAutopilotActive ? Math.round((batchScripts.length / 10) * 100) : batchProgress.percent) / 100)))}ms`
                                                }} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Production Settings */}
                        <div style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '16px'
                        }}>
                            <div 
                                style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'space-between',
                                    cursor: 'pointer',
                                    marginBottom: batchProductionSettingsExpanded ? '16px' : '0'
                                }}
                                onClick={() => {
                                    const newValue = !batchProductionSettingsExpanded;
                                    setBatchProductionSettingsExpanded(newValue);
                                    localStorage.setItem('batch_production_settings_expanded', newValue.toString());
                                }}
                            >
                                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}> <Settings size={12} style={{marginRight: '4px'}} /> Production Settings</h4>
                                {batchProductionSettingsExpanded ? (
                                    <ChevronUp size={20} style={{ color: 'var(--text-secondary)' }} />
                                ) : (
                                    <ChevronDown size={20} style={{ color: 'var(--text-secondary)' }} />
                                )}
                            </div>
                            
                            {batchProductionSettingsExpanded && (
                                <>
                                    <p style={{ margin: '0 0 12px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                                        Applied to every video in this batch. Turn on custom video settings on a video to override them for that video only.
                                    </p>

                                    {/* Slow Motion Toggle */}
                                    {/* <div style={{ marginBottom: '12px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={slowMotion}
                                                onChange={(e) => setSlowMotion(e.target.checked)}
                                                style={{
                                                    cursor: 'pointer',
                                                    accentColor: 'var(--button-bg)'
                                                }}
                                            />
                                            <span>Slow motion (0.75x speed)</span>
                                        </label>
                                    </div> */}

                                    {/* Cut length */}
                                    <div style={{ marginBottom: '12px' }}>
                <span style={{ display: 'block', fontSize: '14px', color: 'var(--text-color)', marginBottom: '8px' }}>Cut length</span>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name="batch-cut-length"
                                                    checked={!!fastPacing}
                                                    onChange={() => { setFastPacing(true); localStorage.setItem('workspace_fast_pacing', 'true'); }}
                                                    style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                />
                                                <span>1 sec</span>
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name="batch-cut-length"
                                                    checked={!fastPacing}
                                                    onChange={() => { setFastPacing(false); localStorage.setItem('workspace_fast_pacing', 'false'); }}
                                                    style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                />
                                                <span>2 sec</span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* Voice Generation Mode */}
                                    <div style={{ marginBottom: '12px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '14px' }}>
                                            Voice Generation Mode
                                            <button
                                                type="button"
                                                onClick={() => setShowVoiceGenerationModeInfo(!showVoiceGenerationModeInfo)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: '2px',
                                                    borderRadius: '50%',
                                                    width: '18px',
                                                    height: '18px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '12px',
                                                    fontWeight: 'bold',
                                                    color: 'var(--text-secondary)',
                                                    backgroundColor: 'var(--background-tertiary)',
                                                    transition: 'all 0.2s ease',
                                                }}
                                                onMouseOver={(e) => {
                                                    e.target.style.backgroundColor = 'var(--button-bg)';
                                                    e.target.style.color = 'var(--button-text)';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.target.style.backgroundColor = 'var(--background-tertiary)';
                                                    e.target.style.color = 'var(--text-secondary)';
                                                }}
                                            >
                                                i
                                            </button>
                                        </label>
                                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '10px' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                                                <input
                                                    type="radio"
                                                    name="batchVoiceGenerationMode"
                                                    value="stable"
                                                    checked={voiceGenerationMode === 'stable'}
                                                    onChange={(e) => {
                                                        setVoiceGenerationMode(e.target.value);
                                                        localStorage.setItem('voiceover_voice_generation_mode', e.target.value);
                                                    }}
                                                    style={{
                                                        cursor: 'pointer',
                                                        accentColor: 'var(--button-bg)'
                                                    }}
                                                />
                                                <span>Stable</span>
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                                                <input
                                                    type="radio"
                                                    name="batchVoiceGenerationMode"
                                                    value="expressive"
                                                    checked={voiceGenerationMode === 'expressive'}
                                                    onChange={(e) => {
                                                        setVoiceGenerationMode(e.target.value);
                                                        localStorage.setItem('voiceover_voice_generation_mode', e.target.value);
                                                    }}
                                                    style={{
                                                        cursor: 'pointer',
                                                        accentColor: 'var(--button-bg)'
                                                    }}
                                                />
                                                <span>Expressive</span>
                                            </label>
                                        </div>
                                        {showVoiceGenerationModeInfo && (
                                            <div style={{
                                                backgroundColor: 'var(--background-tertiary)',
                                                border: '1px solid var(--border-primary)',
                                                borderRadius: '6px',
                                                padding: '12px 32px 12px 12px',
                                                marginBottom: '8px',
                                                fontSize: '13px',
                                                color: 'var(--text-primary)',
                                                lineHeight: '1.4',
                                                position: 'relative',
                                            }}>
                                                <button
                                                    onClick={() => setShowVoiceGenerationModeInfo(false)}
                                                    style={{
                                                        position: 'absolute',
                                                        top: '8px',
                                                        right: '8px',
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        fontSize: '16px',
                                                        color: 'var(--text-secondary)',
                                                        padding: '2px',
                                                        borderRadius: '50%',
                                                        width: '20px',
                                                        height: '20px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    ×
                                                </button>
                                                <strong>Stable:</strong> Stable tone across generations, but the voice might sound monotonous.<br/><br/>
                                                <strong>Expressive:</strong> More human-like, but with a more random tone across generations, more likely to sound weird, and may require more generations to get the right result.
                                            </div>
                                        )}
                                    </div>

                                    {currentUserId === BOUKAR_USER_ID && (
                                    <div style={{ margin: '8px 0 10px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
                                        Boukar only
                                    </div>
                                    )}

                                    {currentUserId === BOUKAR_USER_ID && (
                                    <div style={{ marginBottom: '12px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={cutSilenceMode}
                                                onChange={(e) => {
                                                    const newValue = e.target.checked;
                                                    setCutSilenceMode(newValue);
                                                    localStorage.setItem('workspace_cut_silence_mode', newValue.toString());
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    accentColor: 'var(--button-bg)'
                                                }}
                                            />
                                            <span>Cut silence in Voiceover</span>
                                        </label>
                                    </div>
                                    )}

                                    {currentUserId === BOUKAR_USER_ID && (
                                    <div style={{ marginBottom: '12px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={speedUp}
                                                onChange={(e) => {
                                                    const newValue = e.target.checked;
                                                    setSpeedUp(newValue);
                                                    localStorage.setItem('workspace_speed_up', newValue.toString());
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    accentColor: 'var(--button-bg)'
                                                }}
                                            />
                                            <span>Speed up (1.15x)</span>
                                        </label>
                                    </div>
                                    )}
                                    {currentUserId === BOUKAR_USER_ID && (
                                    <div style={{ marginBottom: '12px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={cutMoreSilenceMode}
                                                onChange={(e) => {
                                                    const newValue = e.target.checked;
                                                    setCutMoreSilenceMode(newValue);
                                                    localStorage.setItem('workspace_cut_more_silence_mode', newValue.toString());
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    accentColor: 'var(--button-bg)'
                                                }}
                                            />
                                            <span>Cut more silence</span>
                                        </label>
                                    </div>
                                    )}
                                    {currentUserId === BOUKAR_USER_ID && (
                                    <div style={{ marginBottom: '12px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={aiSoundEffects}
                                                onChange={(e) => {
                                                    const newValue = e.target.checked;
                                                    setAiSoundEffects(newValue);
                                                    localStorage.setItem('workspace_ai_sound_effects', newValue.toString());
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    accentColor: 'var(--button-bg)'
                                                }}
                                            />
                                            <span>AI sound effects</span>
                                        </label>
                                    </div>
                                    )}

                                    {/* Zoom + Whoosh Toggle */}
                                    {/* <div style={{ marginBottom: '12px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={zoomWhoosh}
                                                onChange={(e) => {
                                                    const newValue = e.target.checked;
                                                    setZoomWhoosh(newValue);
                                                    localStorage.setItem('workspace_zoom_whoosh', newValue.toString());
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    accentColor: 'var(--button-bg)'
                                                }}
                                            />
                                            <span>Zoom + Whoosh</span>
                                        </label>
                                    </div> */}

                                    {/* Flipbook Intro Toggle */}
                                    {renderFastCutIntroToggle()}

                                    {/* Use Music Toggle */}
                                    <div style={{ marginBottom: '12px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={batchUseMusic}
                                                onChange={(e) => {
                                                    const newValue = e.target.checked;
                                                    setBatchUseMusic(newValue);
                                                    localStorage.setItem('batch_use_music', newValue.toString());
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    accentColor: 'var(--button-bg)'
                                                }}
                                            />
                                            <span>Use background music</span>
                                        </label>
                                    </div>

                                    {/* Enable Subtitles Toggle */}
                                    <div style={{ marginBottom: '12px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={enableSubtitles}
                                                onChange={(e) => {
                                                    setEnableSubtitles(e.target.checked);
                                                    localStorage.setItem('workspace_enable_subtitles', e.target.checked.toString());
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    accentColor: 'var(--button-bg)'
                                                }}
                                            />
                                            <span>Enable subtitles</span>
                                        </label>
                                    </div>
                                    {/* Subtitle size */}
                                    {renderSubtitleSizeRadios('batch-subtitle-size')}

                                    {/* Subtitle Position */}
                                    {enableSubtitles && (
                                        <div style={{ marginBottom: '12px' }}>
                                            <label style={{ fontSize: '14px', marginBottom: '8px', display: 'block', color: 'var(--text-color)' }}>Subtitle position:</label>
                                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                    <input
                                                        type="radio"
                                                        name="batchSubtitlePosition"
                                                        value="center"
                                                        checked={subtitlePosition === 'center'}
                                                        onChange={(e) => {
                                                            setSubtitlePosition('center');
                                                            localStorage.setItem('workspace_subtitle_position', 'center');
                                                        }}
                                                        style={{
                                                            cursor: 'pointer',
                                                            accentColor: 'var(--button-bg)'
                                                        }}
                                                    />
                                                    <span>Center</span>
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                    <input
                                                        type="radio"
                                                        name="batchSubtitlePosition"
                                                        value="bottom"
                                                        checked={subtitlePosition === 'bottom'}
                                                        onChange={(e) => {
                                                            setSubtitlePosition('bottom');
                                                            localStorage.setItem('workspace_subtitle_position', 'bottom');
                                                        }}
                                                        style={{
                                                            cursor: 'pointer',
                                                            accentColor: 'var(--button-bg)'
                                                        }}
                                                    />
                                                    <span>Bottom</span>
                                                </label>
                                            </div>
                                        </div>
                                    )}

                                    {/* Opening Line in Center Toggle — Boukar only */}
                                    {currentUserId === BOUKAR_USER_ID && enableSubtitles && (
                                        <div style={{ marginBottom: '12px' }}>
                                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={openingLineInCenter}
                                                    onChange={(e) => {
                                                        const newValue = e.target.checked;
                                                        setOpeningLineInCenter(newValue);
                                                        localStorage.setItem('workspace_opening_line_in_center', newValue.toString());
                                                    }}
                                                    style={{
                                                        cursor: 'pointer',
                                                        accentColor: 'var(--button-bg)',
                                                        marginTop: '2px',
                                                    }}
                                                />
                                                <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                    <span>Title card</span>
                                                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 'normal' }}>
                                                        (First sentence becomes the title)
                                                    </span>
                                                </span>
                                            </label>
                                        </div>
                                    )}

                                    {/* One Word Per Subtitle Toggle */}
                                    {enableSubtitles && (
                                        <div style={{ marginBottom: '12px' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={oneWordPerSubtitle}
                                                    onChange={(e) => {
                                                        const newValue = e.target.checked;
                                                        setOneWordPerSubtitle(newValue);
                                                        localStorage.setItem('workspace_one_word_per_subtitle', newValue.toString());
                                                    }}
                                                    style={{
                                                        cursor: 'pointer',
                                                        accentColor: 'var(--button-bg)'
                                                    }}
                                                />
                                                <span>1 word per subtitle</span>
                                            </label>
                                        </div>
                                    )}

                                    {/* Opening Line Text Size (when opening line in center) */}
                                    {enableSubtitles && openingLineInCenter && (
                                        <div style={{ marginBottom: '12px', marginLeft: '24px' }}>
                                            <label style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', display: 'block' }}>Opening line text size:</label>
                                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                    <input
                                                        type="radio"
                                                        name="batchOpeningLineTextSize"
                                                        value="big"
                                                        checked={openingLineTextSize === 'big'}
                                                        onChange={(e) => {
                                                            setOpeningLineTextSize('big');
                                                            localStorage.setItem('workspace_opening_line_text_size', 'big');
                                                        }}
                                                        style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                    />
                                                    <span>Big</span>
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                    <input
                                                        type="radio"
                                                        name="batchOpeningLineTextSize"
                                                        value="small"
                                                        checked={openingLineTextSize === 'small'}
                                                        onChange={(e) => {
                                                            setOpeningLineTextSize('small');
                                                            localStorage.setItem('workspace_opening_line_text_size', 'small');
                                                        }}
                                                        style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                    />
                                                    <span>Small (top middle)</span>
                                                </label>
                                            </div>
                                            {renderTitleCardFontAndColor({ narrow: screenWidth <= 600 })}
                                        </div>
                                    )}

                                    {enableSubtitles && openingLineInCenter && (
                                        <div style={{ marginBottom: '12px', marginLeft: '24px' }}>
                                            {renderTitleCardStyleRadios('batch-title-card-style')}
                                        </div>
                                    )}


                                    {/* Bouki's intro (Boukar only) */}
                                    {currentUserId === BOUKAR_USER_ID && (
                                        <div>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={boukisIntro}
                                                    onChange={(e) => {
                                                        const newValue = e.target.checked;
                                                        setBoukisIntro(newValue);
                                                        localStorage.setItem('workspace_boukis_intro', newValue.toString());
                                                    }}
                                                    style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                />
                                                <span>Bouki's intro</span>
                                            </label>
                                            {boukisIntro && (
                                                <div style={{ marginTop: '8px', display: 'flex', gap: '12px', alignItems: 'center', marginLeft: '24px' }}>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                        <input
                                                            type="radio"
                                                            name="batchBoukisIntroPitch"
                                                            value="high"
                                                            checked={boukisIntroPitch === 'high'}
                                                            onChange={() => {
                                                                setBoukisIntroPitch('high');
                                                                localStorage.setItem('workspace_boukis_intro_pitch', 'high');
                                                            }}
                                                            style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                        />
                                                        <span>High pitch</span>
                                                    </label>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                        <input
                                                            type="radio"
                                                            name="batchBoukisIntroPitch"
                                                            value="low"
                                                            checked={boukisIntroPitch === 'low'}
                                                            onChange={() => {
                                                                setBoukisIntroPitch('low');
                                                                localStorage.setItem('workspace_boukis_intro_pitch', 'low');
                                                            }}
                                                            style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                        />
                                                        <span>Low pitch</span>
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                </>
                            )}
                        </div>

                        {batchAutopilot && (batchAutopilotSessionActive || batchAutopilotThoughtSeconds != null) && (() => {
                            const pipelineBusy = batchAutopilotSessionActive && (
                                batchAutopilotActive ||
                                batchGenerating ||
                                batchGeneratingVoiceovers.size > 0 ||
                                batchGeneratingVideos.size > 0 ||
                                batchGeneratingTextOverlay.size > 0
                            );
                            if (pipelineBusy) {
                                void batchAutopilotEtaTick;
                                const startAt = batchAutopilotStartedAtRef.current;
                                const targetScripts = batchAutopilotVoiceoverTarget;
                                const voiceoverReceived = batchScripts.filter((s) => s.type === 'voiceover').length;
                                const elapsedSec = startAt ? Math.floor((Date.now() - startAt) / 1000) : 0;
                                const scriptPhaseBusy = batchAutopilotActive || batchGenerating;
                                let etaLabel = '';
                                if (scriptPhaseBusy) {
                                    if (targetScripts > 0) {
                                        if (voiceoverReceived >= targetScripts) {
                                            etaLabel = 'All scripts received — finishing autopilot setup…';
                                        } else if (voiceoverReceived > 0) {
                                            const rate = elapsedSec / voiceoverReceived;
                                            const remaining = Math.max(0, Math.ceil(rate * (targetScripts - voiceoverReceived)));
                                            etaLabel = `About ${remaining}s until batch script generation finishes (${voiceoverReceived}/${targetScripts} scripts)`;
                                        } else {
                                            etaLabel = `Waiting for scripts… ${elapsedSec}s elapsed`;
                                        }
                                    } else {
                                        etaLabel = `Elapsed: ${elapsedSec}s`;
                                    }
                                } else {
                                    etaLabel = `Rendering voiceovers & videos… ${elapsedSec}s elapsed`;
                                }
                                return (
                                    <div
                                        style={{
                                            backgroundColor: 'var(--card-bg)',
                                            border: '1px solid var(--input-border)',
                                            borderRadius: '8px',
                                            padding: '10px 14px',
                                            marginBottom: '16px',
                                            fontSize: '13px',
                                            color: 'var(--text-color)'
                                        }}
                                    >
                                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Auto-create</div>
                                        <div style={{ fontWeight: 600 }}>{etaLabel}</div>
                                    </div>
                                );
                            }
                            if (batchAutopilotThoughtSeconds != null) {
                                return (
                                    <div
                                        style={{
                                            backgroundColor: 'var(--card-bg)',
                                            border: '1px solid var(--input-border)',
                                            borderRadius: '8px',
                                            padding: '10px 14px',
                                            marginBottom: '16px',
                                            fontSize: '13px',
                                            color: 'var(--text-color)'
                                        }}
                                    >
                                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Auto-create</div>
                                        <div style={{ fontWeight: 600 }}>Thought for {batchAutopilotThoughtSeconds}s</div>
                                    </div>
                                );
                            }
                            return null;
                        })()}

                        {batchScripts.length > 0 && (
                            <div style={{
                                backgroundColor: 'var(--input-bg)',
                                border: '1px solid var(--input-border)',
                                borderRadius: '8px',
                                padding: '16px',
                                marginBottom: '16px'
                            }}>
                                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                                    {batchScripts.some(script => script.type === 'voiceover' && !script.voiceoverUrl) && (
                                        <button
                                            onClick={(e) => handleBatchGenerateAllVoiceovers({ skipConfirm: e.metaKey || e.ctrlKey })}
                                            title={`Generate all voiceovers (${batchModEnterLabel} skips confirmation)`}
                                            style={{
                                                backgroundColor: 'var(--button-bg)',
                                                color: 'var(--button-text)',
                                                border: 'none',
                                                borderRadius: '8px',
                                                padding: '8px 16px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                opacity: '0.9',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                                            onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                                        >
                                            <Mic size={16} />
                                            Generate All Voiceovers
                                            {renderBatchModEnterHint()}
                                        </button>
                                    )}
                                    {batchScripts.some(scriptEligibleForBatchGenerateAllVideo) && (
                                        <button
                                            onClick={(e) => handleBatchGenerateAllVideos({ skipConfirm: e.metaKey || e.ctrlKey })}
                                            title={`Generate all videos (${batchModEnterLabel} skips confirmation)`}
                                            style={{
                                                backgroundColor: 'var(--button-bg)',
                                                color: 'var(--button-text)',
                                                border: 'none',
                                                borderRadius: '8px',
                                                padding: '8px 16px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                opacity: '0.9',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                                            onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                                        >
                                            <Video size={16} />
                                            Generate All Videos
                                            {renderBatchModEnterHint()}
                                        </button>
                                    )}
                                </div>
                                {batchVideoProgress.total > 0 && (
                                    <div style={{ marginBottom: '16px' }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            backgroundColor: 'var(--card-bg)',
                                            border: '1px solid var(--input-border)',
                                            borderRadius: '8px',
                                            padding: '12px'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                {batchVideoProgress.percent >= 100 ? (
                                                    <CheckCircle size={18} style={{ color: 'var(--success-color, #28a745)' }} />
                                                ) : (
                                                    <span className="spinner-border" style={{ width: '18px', height: '18px', borderWidth: '2px' }} />
                                                )}
                                                <span style={{ fontSize: '14px', color: 'var(--text-color)' }}>
                                                    {batchVideoProgress.percent >= 100
                                                        ? `Videos complete: ${batchVideoProgress.current}/${batchVideoProgress.total}`
                                                        : `Generating videos: ${batchVideoProgress.current}/${batchVideoProgress.total}`}
                                                </span>
                                            </div>
                                            <div style={{ width: '160px' }}>
                                                <div style={{
                                                    width: '100%', height: '6px', backgroundColor: 'var(--input-border)',
                                                    borderRadius: '3px', overflow: 'hidden'
                                                }}>
                                                    <div style={{
                                                        width: `${Math.min(100, batchVideoProgress.percent)}%`, height: '6px', backgroundColor: 'var(--primary-color, #8b5cf6)',
                                                        transition: 'width 0.3s ease-in-out'
                                                    }} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {batchGenerating && (
                                    <div
                                        className="batch-card-status-pill-wrap"
                                        style={{ marginBottom: '14px' }}
                                        role="status"
                                        aria-live="polite"
                                        aria-atomic="true"
                                    >
                                        <div className="batch-card-status-pill-outer">
                                            <div className="batch-card-status-pill-inner">
                                                <span className="batch-card-status-pill-shine" aria-hidden />
                                                <span className="batch-card-status-pill-text">Generating scripts…</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                                    gap: '16px'
                                }}>
                                    {batchScripts.map((script, index) => {
                                        const isGeneratingVoiceover = batchGeneratingVoiceovers.has(script._id);
                                        const isGeneratingVideo = batchGeneratingVideos.has(script._id);
                                        const isCardGlowing = batchScriptGlow?.scriptId === script._id;
                                        const showCursorsForCard = batchAutopilot && (
                                            batchAutopilotActive ||
                                            batchGenerating ||
                                            isGeneratingVoiceover ||
                                            isGeneratingVideo ||
                                            batchGeneratingTextOverlay.has(script._id) ||
                                            !script.voiceoverUrl
                                        ) && script._id;
                                        const cardMouse = showCursorsForCard ? batchCardMousePositions[script._id] : null;
                                        const batchCardVideoReady = Boolean(script.videoUrl && script.status === 'draft' && script.progress === 100);
                                        let batchCardBusyMessage = null;
                                        if (isGeneratingVideo) {
                                            const p = batchVideoProgressByScript[script._id];
                                            batchCardBusyMessage = p != null && p > 0
                                                ? `Generating video… ${Math.round(p)}%`
                                                : 'Generating video…';
                                        } else if (isGeneratingVoiceover) {
                                            batchCardBusyMessage = 'Generating voiceover…';
                                        } else if (batchGeneratingTextOverlay.has(script._id)) {
                                            batchCardBusyMessage = 'Generating on-screen text…';
                                        } else if (batchGeneratingDescription.has(script._id)) {
                                            batchCardBusyMessage = 'Generating description…';
                                        }
                                        return (
                                            <div
                                                key={script._id || index}
                                                className={isCardGlowing ? 'batch-card-glow' : ''}
                                                style={{
                                                    position: 'relative',
                                                    border: `2px solid ${script.type === 'text' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(139, 92, 246, 0.3)'}`,
                                                    borderRadius: '8px',
                                                    padding: '16px',
                                                    backgroundColor: 'var(--card-bg)',
                                                    boxShadow: `0 2px 8px ${script.type === 'text' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(139, 92, 246, 0.1)'}`
                                                }}
                                            >
                                                {batchCardBusyMessage && (
                                                    <div
                                                        className="batch-card-status-pill-wrap"
                                                        role="status"
                                                        aria-live="polite"
                                                        aria-atomic="true"
                                                    >
                                                        <div className="batch-card-status-pill-outer">
                                                            <div className="batch-card-status-pill-inner">
                                                                <span className="batch-card-status-pill-shine" aria-hidden />
                                                                <span className="batch-card-status-pill-text">{batchCardBusyMessage}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                {/* One moving cursor per card when autopilot is on (script/voiceover phase) */}
                                                {showCursorsForCard && cardMouse && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: batchCardMouseBounds.w,
                                                        height: batchCardMouseBounds.h,
                                                        overflow: 'visible',
                                                        pointerEvents: 'none',
                                                        zIndex: 5
                                                    }}>
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                left: `${cardMouse.x}px`,
                                                                top: `${cardMouse.y}px`,
                                                                transition: 'left 0.1s linear, top 0.1s linear',
                                                                zIndex: 10
                                                            }}
                                                        >
                                                            {/* Speech bubble with color name + @ */}
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: '20px',
                                                                left: '20px',
                                                                backgroundColor: cardMouse.color,
                                                                padding: '6px 10px',
                                                                borderRadius: '8px',
                                                                fontSize: '11px',
                                                                color: '#fff',
                                                                whiteSpace: 'nowrap',
                                                                boxShadow: `0 2px 8px ${cardMouse.color}80`,
                                                                border: `1px solid ${cardMouse.color}`,
                                                                fontWeight: '500'
                                                            }}>
                                                                {cardMouse.label || `@${cardMouse.color}`}
                                                            </div>
                                                            <svg
                                                                width="24"
                                                                height="24"
                                                                viewBox="0 0 24 24"
                                                                fill={cardMouse.color}
                                                                stroke={cardMouse.color}
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                style={{ filter: `drop-shadow(0 2px 4px ${cardMouse.color}80)` }}
                                                            >
                                                                <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                )}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        backgroundColor: script.type === 'text' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(139, 92, 246, 0.1)',
                                                        border: `1px solid ${script.type === 'text' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(139, 92, 246, 0.3)'}`
                                                    }}>
                                                        {script.type === 'text' ? (
                                                            <Type size={14} style={{ color: '#10b981' }} />
                                                        ) : (
                                                            <Mic size={14} style={{ color: '#8b5cf6' }} />
                                                        )}
                                                        <span style={{ fontSize: '12px', fontWeight: '500', color: script.type === 'text' ? '#10b981' : '#8b5cf6', textTransform: 'capitalize' }}>
                                                            {script.type}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: batchCardVideoReady ? 'row' : 'column',
                                                        alignItems: 'flex-start',
                                                        gap: batchCardVideoReady ? '14px' : 0,
                                                        width: '100%',
                                                    }}
                                                >
                                                <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
                                                
                                                <TextareaAutosize
                                                    value={getBatchScriptContent(script, script.selectedVersion || 'main')}
                                                    onChange={(e) => {
                                                        const version = script.selectedVersion || 'main';
                                                        const newScripts = [...batchScripts];
                                                        newScripts[index] = {
                                                            ...newScripts[index],
                                                            [version === 'main' ? 'content' : (version === 'alt' ? 'contentAlt' : 'contentThird')]: e.target.value
                                                        };
                                                        setBatchScripts(newScripts);
                                                    }}
                                                    placeholder={script.type === 'text' ? 'Text for your video (caption on screen)...' : undefined}
                                                    minRows={3}
                                                    maxRows={5}
                                                    style={{
                                                        width: '100%',
                                                        padding: '6px 8px',
                                                        borderRadius: '4px',
                                                        border: '1px solid var(--input-border)',
                                                        backgroundColor: 'var(--input-bg)',
                                                        color: 'var(--text-color)',
                                                        fontSize: '13px',
                                                        lineHeight: 1.4,
                                                        fontFamily: SYSTEM_UI_FONT,
                                                        marginBottom: '10px',
                                                        boxSizing: 'border-box'
                                                    }}
                                                />
                                                {/* Description (caption) for text-based cards */}
                                                {script.type === 'text' && (
                                                    <div style={{ marginBottom: '12px' }}>
                                                        <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Social media description (caption)</label>
                                                        <TextareaAutosize
                                                            value={script.description || ''}
                                                            onChange={(e) => {
                                                                const newScripts = [...batchScripts];
                                                                newScripts[index] = { ...newScripts[index], description: e.target.value };
                                                                setBatchScripts(newScripts);
                                                            }}
                                                            placeholder="Optional description with hashtags..."
                                                            minRows={2}
                                                            maxRows={4}
                                                            style={{
                                                                width: '100%',
                                                                padding: '8px',
                                                                borderRadius: '4px',
                                                                border: '1px solid var(--input-border)',
                                                                backgroundColor: 'var(--input-bg)',
                                                                color: 'var(--text-color)',
                                                                fontSize: '13px',
                                                                fontFamily: 'inherit',
                                                                boxSizing: 'border-box'
                                                            }}
                                                        />
                                                        <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleBatchGenerateTextOverlay(script._id)}
                                                                disabled={batchGeneratingTextOverlay.has(script._id)}
                                                                style={{
                                                                    padding: '4px 8px',
                                                                    borderRadius: '5px',
                                                                    border: '1px solid var(--input-border)',
                                                                    background: 'var(--input-bg)',
                                                                    color: 'var(--text-color)',
                                                                    fontSize: '11px',
                                                                    cursor: batchGeneratingTextOverlay.has(script._id) ? 'not-allowed' : 'pointer',
                                                                    opacity: batchGeneratingTextOverlay.has(script._id) ? 0.7 : 1
                                                                }}
                                                            >
                                                                {batchGeneratingTextOverlay.has(script._id) ? (
                                                                    <><span className="spinner-border" style={{ marginRight: '3px', width: '10px', height: '10px' }} /> Generating...</>
                                                                ) : (
                                                                    'Generate text (AI)'
                                                                )}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleBatchGenerateDescription(script._id)}
                                                                disabled={batchGeneratingDescription.has(script._id) || String(script._id).startsWith('batch-text-')}
                                                                style={{
                                                                    padding: '4px 8px',
                                                                    borderRadius: '5px',
                                                                    border: '1px solid var(--input-border)',
                                                                    background: 'var(--input-bg)',
                                                                    color: 'var(--text-color)',
                                                                    fontSize: '11px',
                                                                    cursor: (batchGeneratingDescription.has(script._id) || String(script._id).startsWith('batch-text-')) ? 'not-allowed' : 'pointer',
                                                                    opacity: (batchGeneratingDescription.has(script._id) || String(script._id).startsWith('batch-text-')) ? 0.7 : 1
                                                                }}
                                                            >
                                                                {batchGeneratingDescription.has(script._id) ? (
                                                                    <><span className="spinner-border" style={{ marginRight: '3px', width: '10px', height: '10px' }} /> Generating...</>
                                                                ) : (
                                                                    'Generate description (AI)'
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                                {/* Cut for text-based: show selected cut + Change button */}
                                                {script.type === 'text' && (
                                                    <div style={{ marginBottom: '12px' }}>
                                                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Cut for video</div>
                                                        {batchTextCutsLoading ? (
                                                            <div style={{ padding: '12px', textAlign: 'center', fontSize: '13px', color: 'var(--text-secondary)' }}>Loading cuts…</div>
                                                        ) : batchTextSelectedCutByScriptId[script._id] ? (
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                                                <div style={{ width: '80px', aspectRatio: '9/16', borderRadius: '6px', overflow: 'hidden', border: '1px solid var(--input-border)', flexShrink: 0 }}>
                                                                    <video src={batchTextSelectedCutByScriptId[script._id].url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted preload="metadata" />
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={async () => {
                                                                        setBatchTextCutModalScriptId(script._id);
                                                                        if (batchTextAvailableCuts.length === 0) {
                                                                            setBatchTextCutsLoading(true);
                                                                            try {
                                                                                setBatchTextAvailableCuts(await loadWorkspaceCutsForPicker());
                                                                            } catch (_) { setBatchTextAvailableCuts([]); }
                                                                            finally { setBatchTextCutsLoading(false); }
                                                                        }
                                                                    }}
                                                                    style={{
                                                                        padding: '4px 8px',
                                                                        borderRadius: '5px',
                                                                        border: '1px solid var(--input-border)',
                                                                        background: 'var(--input-bg)',
                                                                        color: 'var(--text-color)',
                                                                        fontSize: '11px',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >
                                                                    Pick cut
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={async () => {
                                                                        setBatchTextCutModalScriptId(script._id);
                                                                        if (batchTextAvailableCuts.length === 0) {
                                                                            setBatchTextCutsLoading(true);
                                                                            try {
                                                                                setBatchTextAvailableCuts(await loadWorkspaceCutsForPicker());
                                                                            } catch (_) { setBatchTextAvailableCuts([]); }
                                                                            finally { setBatchTextCutsLoading(false); }
                                                                        }
                                                                    }}
                                                                style={{
                                                                    padding: '5px 10px',
                                                                    borderRadius: '5px',
                                                                    border: '1px solid var(--input-border)',
                                                                    background: 'var(--input-bg)',
                                                                    color: 'var(--text-color)',
                                                                    fontSize: '11px',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px'
                                                                }}
                                                            >
                                                                <Video size={12} />
                                                                Select cut
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                                
                                                <BatchVideoSettingsOverride
                                                    scriptId={script._id}
                                                    scriptType={script.type}
                                                    enabled={!!batchVideoSettingsOverrideByScriptId[script._id]}
                                                    settings={batchVideoSettingsOverrideByScriptId[script._id] || null}
                                                    onToggle={() => toggleBatchVideoSettingsOverride(script._id)}
                                                    onChange={(patch) => updateBatchVideoSetting(script._id, patch)}
                                                    onReset={() => resetBatchVideoSettingsOverride(script._id)}
                                                    isBoukar={currentUserId === BOUKAR_USER_ID}
                                                    fonts={sortedUserFonts}
                                                    defaultFontFamilyCss={defaultFontFamilyCss}
                                                    fontFamilyCssForName={fontFamilyCssForName}
                                                />

                                                {script.type === 'voiceover' && !script.voiceoverUrl && (
                                                    <button
                                                        onClick={() => handleBatchGenerateVoiceover(script._id)}
                                                        disabled={isGeneratingVoiceover}
                                                        style={{
                                                            width: '100%',
                                                            padding: '5px 8px',
                                                            borderRadius: '8px',
                                                            backgroundColor: '#AB5898', //pink button
                                                            color: 'white',
                                                            fontWeight: 600,
                                                            fontSize: '12px',
                                                            border: 'none',
                                                            cursor: isGeneratingVoiceover ? 'not-allowed' : 'pointer',
                                                            opacity: isGeneratingVoiceover ? 0.6 : 1,
                                                            marginBottom: '6px'
                                                        }}
                                                    >
                                                        {isGeneratingVoiceover ? (
                                                            <>
                                                                <span className="spinner-border" style={{ marginRight: '4px', width: '11px', height: '11px' }} />
                                                                Generating...
                                                            </>
                                                        ) : (
                                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                                                <SparklesIcon size={14} />
                                                                Generate Voiceover
                                                            </span>
                                                        )}
                                                    </button>
                                                )}
                                                
                                                {script.voiceoverUrl && (
                                                    <>
                                                        <div
                                                            style={{
                                                                marginBottom: '6px',
                                                                maxWidth: '100%',
                                                                lineHeight: 0,
                                                            }}
                                                        >
                                                            <audio
                                                                src={script.voiceoverUrl}
                                                                controls
                                                                style={{
                                                                    width: '100%',
                                                                    maxWidth: '200px',
                                                                    height: '30px',
                                                                    transform: 'scale(0.9)',
                                                                    transformOrigin: 'left center',
                                                                }}
                                                            />
                                                        </div>
                                                        <button
                                                            onClick={() => handleBatchGenerateVoiceover(script._id)}
                                                            disabled={isGeneratingVoiceover}
                                                            style={{
                                                                width: '100%',
                                                                padding: '5px 8px',
                                                                borderRadius: '6px',
                                                                backgroundColor: '#AB5898',
                                                                color: 'white',
                                                                fontWeight: 600,
                                                                fontSize: '12px',
                                                                border: '1px solid var(--input-border)',
                                                                cursor: isGeneratingVoiceover ? 'not-allowed' : 'pointer',
                                                                opacity: isGeneratingVoiceover ? 0.6 : 1,
                                                                marginBottom: '6px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: '4px'
                                                            }}
                                                        >
                                                            {isGeneratingVoiceover ? (
                                                                <>
                                                                    <span className="spinner-border" style={{ marginRight: '2px', width: '11px', height: '11px' }} />
                                                                    Regenerating...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <RefreshCw size={12} />
                                                                    Try new voice tone
                                                                </>
                                                            )}
                                                        </button>
                                                    </>
                                                )}
                                                
                                                {/* Music Selection - Show when useMusic is enabled and before video generation */}
                                                {((batchVideoSettingsOverrideByScriptId[script._id]
                                                    ? !!batchVideoSettingsOverrideByScriptId[script._id].useMusic
                                                    : batchUseMusic) && (script.voiceoverUrl || script.type === 'text')) && (
                                                    <div style={{ marginBottom: '8px' }}>
                                                        {(() => {
                                                            const musicSelection = batchScriptMusicSelections[script._id];
                                                            const showDropdown = batchShowMusicDropdowns[script._id];
                                                            
                                                            if (musicSelection && musicSelection.musicName) {
                                                                // Show selected music with keep/random options
                                                                return (
                                                                    <div style={{
                                                                        padding: '6px',
                                                                        borderRadius: '4px',
                                                                        backgroundColor: 'var(--input-bg)',
                                                                        border: '1px solid var(--input-border)',
                                                                        marginBottom: '6px'
                                                                    }}>
                                                                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                                                                            Background Music:
                                                                        </div>
                                                                        <div style={{ fontSize: '12px', fontWeight: 500, marginBottom: '6px' }}>
                                                                            {musicSelection.musicName}
                                                                        </div>
                                                                        <div style={{ display: 'flex', gap: '6px' }}>
                                                                            <button
                                                                                onClick={() => {
                                                                                    // Keep the same music
                                                                                    setBatchScriptMusicSelections(prev => ({
                                                                                        ...prev,
                                                                                        [script._id]: { ...musicSelection, isRandom: false }
                                                                                    }));
                                                                                }}
                                                                                style={{
                                                                                    flex: 1,
                                                                                    padding: '4px 6px',
                                                                                    borderRadius: '4px',
                                                                                    backgroundColor: 'var(--button-bg)',
                                                                                    color: 'var(--button-text)',
                                                                                    border: 'none',
                                                                                    cursor: 'pointer',
                                                                                    fontSize: '11px'
                                                                                }}
                                                                            >
                                                                                Keep
                                                                            </button>
                                                                            <button
                                                                                onClick={() => {
                                                                                    // Get another random
                                                                                    if (availableMusic.length > 0) {
                                                                                        const randomIndex = Math.floor(Math.random() * availableMusic.length);
                                                                                        const randomMusic = availableMusic[randomIndex];
                                                                                        setBatchScriptMusicSelections(prev => ({
                                                                                            ...prev,
                                                                                            [script._id]: { musicId: randomMusic._id, musicName: randomMusic.name, isRandom: true }
                                                                                        }));
                                                                                    }
                                                                                }}
                                                                                style={{
                                                                                    flex: 1,
                                                                                    padding: '4px 6px',
                                                                                    borderRadius: '4px',
                                                                                    backgroundColor: 'var(--input-bg)',
                                                                                    color: 'var(--text-color)',
                                                                                    border: '1px solid var(--input-border)',
                                                                                    cursor: 'pointer',
                                                                                    fontSize: '11px'
                                                                                }}
                                                                            >
                                                                                Another Random
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            } else {
                                                                // Show music selection button
                                                                return (
                                                                    <div style={{ position: 'relative' }} data-music-dropdown>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setBatchShowMusicDropdowns(prev => ({
                                                                                    ...prev,
                                                                                    [script._id]: !showDropdown
                                                                                }));
                                                                            }}
                                                                            style={{
                                                                                width: '100%',
                                                                                padding: '5px 8px',
                                                                                borderRadius: '4px',
                                                                                border: '1px solid var(--input-border)',
                                                                                background: 'var(--input-bg)',
                                                                                color: 'var(--input-text)',
                                                                                fontSize: '12px',
                                                                                cursor: 'pointer',
                                                                                display: 'flex',
                                                                                justifyContent: 'space-between',
                                                                                alignItems: 'center',
                                                                                textAlign: 'left',
                                                                                marginBottom: '6px'
                                                                            }}
                                                                        >
                                                                            <span>Select background music (or random)</span>
                                                                            <span style={{ fontSize: '10px', opacity: 0.7 }}>
                                                                                {showDropdown ? '▲' : '▼'}
                                                                            </span>
                                                                        </button>
                                                                        
                                                                        {showDropdown && (
                                                                            <div style={{
                                                                                position: 'absolute',
                                                                                top: '100%',
                                                                                left: 0,
                                                                                right: 0,
                                                                                backgroundColor: 'var(--input-bg)',
                                                                                border: '1px solid var(--input-border)',
                                                                                borderRadius: '6px',
                                                                                marginTop: '2px',
                                                                                zIndex: 1000,
                                                                                maxHeight: '200px',
                                                                                overflowY: 'auto',
                                                                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.5)'
                                                                            }}>
                                                                                {/* Random Music Option */}
                                                                                <div
                                                                                    style={{
                                                                                        padding: '6px 10px',
                                                                                        cursor: 'pointer',
                                                                                        display: 'flex',
                                                                                        justifyContent: 'space-between',
                                                                                        alignItems: 'center',
                                                                                        borderBottom: '1px solid var(--input-border)',
                                                                                        backgroundColor: !musicSelection || !musicSelection.musicId ? 'var(--button-bg)' : 'transparent',
                                                                                        color: !musicSelection || !musicSelection.musicId ? 'var(--button-text)' : 'var(--text-color)'
                                                                                    }}
                                                                                    onClick={() => {
                                                                                        if (availableMusic.length > 0) {
                                                                                            const randomIndex = Math.floor(Math.random() * availableMusic.length);
                                                                                            const randomMusic = availableMusic[randomIndex];
                                                                                            setBatchScriptMusicSelections(prev => ({
                                                                                                ...prev,
                                                                                                [script._id]: { musicId: randomMusic._id, musicName: randomMusic.name, isRandom: true }
                                                                                            }));
                                                                                        }
                                                                                        setBatchShowMusicDropdowns(prev => ({
                                                                                            ...prev,
                                                                                            [script._id]: false
                                                                                        }));
                                                                                    }}
                                                                                >
                                                                                    <span style={{ fontSize: '12px' }}>Random music</span>
                                                                                </div>
                                                                                
                                                                                {/* Music Options */}
                                                                                {[...availableMusic].sort((a, b) => {
                                                                                    if (a.isStarred && !b.isStarred) return -1;
                                                                                    if (!a.isStarred && b.isStarred) return 1;
                                                                                    const nameA = (a.name || '').toLowerCase();
                                                                                    const nameB = (b.name || '').toLowerCase();
                                                                                    return nameA.localeCompare(nameB);
                                                                                }).map(music => (
                                                                                    <div
                                                                                        key={music._id}
                                                                                        style={{
                                                                                            padding: '6px 10px',
                                                                                            cursor: 'pointer',
                                                                                            display: 'flex',
                                                                                            justifyContent: 'space-between',
                                                                                            alignItems: 'center',
                                                                                            borderBottom: '1px solid var(--input-border)',
                                                                                            backgroundColor: musicSelection?.musicId === music._id ? 'var(--button-bg)' : 'transparent',
                                                                                            color: musicSelection?.musicId === music._id ? 'var(--button-text)' : 'var(--text-color)'
                                                                                        }}
                                                                                        onClick={() => {
                                                                                            setBatchScriptMusicSelections(prev => ({
                                                                                                ...prev,
                                                                                                [script._id]: { musicId: music._id, musicName: music.name, isRandom: false }
                                                                                            }));
                                                                                            setBatchShowMusicDropdowns(prev => ({
                                                                                                ...prev,
                                                                                                [script._id]: false
                                                                                            }));
                                                                                        }}
                                                                                    >
                                                                                        <span style={{ fontSize: '12px', flex: 1 }}>{music.name}</span>
                                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                                            {music.isStarred && (
                                                                                                <Star size={14} fill="#ffd700" color="#ffd700" />
                                                                                            )}
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    handleMusicPreview(music._id, music.url);
                                                                                                }}
                                                                                                style={{
                                                                                                    padding: '4px 6px',
                                                                                                    backgroundColor: playingMusicId === music._id ? '#ff4444' : 'var(--button-bg)',
                                                                                                    color: 'white',
                                                                                                    border: '1px solid var(--input-border)',
                                                                                                    borderRadius: '4px',
                                                                                                    cursor: 'pointer',
                                                                                                    fontSize: '12px',
                                                                                                    display: 'flex',
                                                                                                    alignItems: 'center',
                                                                                                    justifyContent: 'center',
                                                                                                    height: '22px',
                                                                                                    width: '22px'
                                                                                                }}
                                                                                            >
                                                                                                {playingMusicId === music._id ? '⏹' : '▶'}
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            }
                                                        })()}
                                                    </div>
                                                )}
                                                
                                                {/* Per-video thumbnail (batch only); not for text-based cards — temporarily disabled */}
                                                {/* {script.type === 'voiceover' && script.voiceoverUrl && (
                                                    <div style={{ marginBottom: '8px' }}>
                                                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Thumbnail</div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                            <span style={{ fontSize: '13px', color: 'var(--text-color)' }}>
                                                                {batchThumbnailByScriptId[script._id]?.title?.trim() && batchThumbnailByScriptId[script._id]?.cut
                                                                    ? `"${batchThumbnailByScriptId[script._id].title.trim()}"`
                                                                    : 'None'}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={async () => {
                                                                    setBatchThumbnailModalScriptId(script._id);
                                                                    setShowThumbnailModal(true);
                                                                    setThumbnailCutsLoading(true);
                                                                    try {
                                                                        setThumbnailAvailableCuts(await loadWorkspaceCutsForPicker());
                                                                    } catch (err) {
                                                                        console.error('Failed to load cuts for thumbnail:', err);
                                                                        setThumbnailAvailableCuts([]);
                                                                    } finally {
                                                                        setThumbnailCutsLoading(false);
                                                                    }
                                                                }}
                                                                style={{
                                                                    padding: '4px 8px',
                                                                    borderRadius: '5px',
                                                                    border: '1px solid var(--input-border)',
                                                                    background: 'var(--input-bg)',
                                                                    color: 'var(--text-color)',
                                                                    fontSize: '11px',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                {batchThumbnailByScriptId[script._id]?.cut ? 'Change thumbnail' : 'Configure thumbnail'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )} */}
                                                
                                                {(script.voiceoverUrl || script.type === 'text') && !(script.videoUrl && script.status === 'draft' && script.progress === 100) && (
                                                    <button
                                                        onClick={() => handleBatchGenerateVideo(script._id)}
                                                        disabled={isGeneratingVideo}
                                                        style={{
                                                            width: '100%',
                                                            padding: '5px 8px',
                                                            borderRadius: '6px',
                                                            backgroundColor: '#e1f679', // green button
                                                            color: 'black',
                                                            fontWeight: 600,
                                                            fontSize: '12px',
                                                            border: 'none',
                                                            cursor: isGeneratingVideo ? 'not-allowed' : 'pointer',
                                                            opacity: isGeneratingVideo ? 0.6 : 1
                                                        }}
                                                    >
                                                        {isGeneratingVideo ? (
                                                            <>
                                                                <span className="spinner-border" style={{ marginRight: '4px', width: '11px', height: '11px' }} />
                                                                Generating... {batchVideoProgressByScript[script._id] != null ? `${batchVideoProgressByScript[script._id]}%` : ''}
                                                            </>
                                                        ) : (
                                                            script.videoUrl ? 'Regenerate Video' : 'Generate Video'
                                                        )}
                                                    </button>
                                                )}
                                                {isGeneratingVideo && (
                                                    <VideoGenerationSteps
                                                        compact
                                                        progress={batchVideoProgressByScript[script._id] ?? 0}
                                                        perStepProgress
                                                        showProgressBar
                                                        shimmer
                                                        includeAiSoundEffects={currentUserId === BOUKAR_USER_ID && (batchVideoSettingsOverrideByScriptId[script._id]?.aiSoundEffects ?? aiSoundEffects) && script.type !== 'text'}
                                                    />
                                                )}
                                                </div>

                                                {batchCardVideoReady && (
                                                    <div
                                                        style={{
                                                            flexShrink: 0,
                                                            width: 'min(200px, 36%)',
                                                            maxWidth: '220px',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'stretch',
                                                            gap: '8px',
                                                            alignSelf: 'stretch',
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                maxHeight: '300px',
                                                                width: '100%',
                                                            }}
                                                        >
                                                            <video
                                                                src={script.videoUrl}
                                                                controls
                                                                style={{
                                                                    borderRadius: '4px',
                                                                    maxHeight: '300px',
                                                                    width: '100%',
                                                                    objectFit: 'contain',
                                                                }}
                                                            />
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
                                                            {useNewNle && script.type === 'voiceover' && script.voiceoverUrl && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => openNewNleEditor({ batchScript: script })}
                                                                    disabled={isGeneratingVideo}
                                                                    style={{
                                                                        width: '100%',
                                                                        padding: '5px 8px',
                                                                        borderRadius: '6px',
                                                                        backgroundColor: 'var(--input-bg)',
                                                                        color: 'var(--text-color)',
                                                                        fontWeight: 600,
                                                                        fontSize: '12px',
                                                                        border: '1px solid var(--input-border)',
                                                                        cursor: isGeneratingVideo ? 'not-allowed' : 'pointer',
                                                                        opacity: isGeneratingVideo ? 0.6 : 1,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        gap: '6px',
                                                                    }}
                                                                >
                                                                    <EditTimelineIcon size={14} />
                                                                    Timeline
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => handleBatchGenerateVideo(script._id)}
                                                                disabled={isGeneratingVideo}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '5px 8px',
                                                                    borderRadius: '6px',
                                                                    backgroundColor: '#e1f679',
                                                                    color: 'black',
                                                                    fontWeight: 600,
                                                                    fontSize: '12px',
                                                                    border: 'none',
                                                                    cursor: isGeneratingVideo ? 'not-allowed' : 'pointer',
                                                                    opacity: isGeneratingVideo ? 0.6 : 1,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    gap: '4px',
                                                                }}
                                                            >
                                                                {isGeneratingVideo ? (
                                                                    <>
                                                                        <span className="spinner-border" style={{ marginRight: '2px', width: '11px', height: '11px' }} />
                                                                        Regenerating...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <RefreshCw size={12} />
                                                                        Regenerate Video
                                                                    </>
                                                                )}
                                                            </button>
                                                            <button
                                                                onClick={() => handleBatchDownloadAndSave(script._id)}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '5px 8px',
                                                                    borderRadius: '4px',
                                                                    backgroundColor: script.saved ? '#28a745' : 'var(--button-bg)',
                                                                    color: 'var(--button-text)',
                                                                    border: 'none',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    gap: '4px',
                                                                    fontSize: '12px',
                                                                    fontWeight: 600,
                                                                    opacity: '0.9',
                                                                    transition: 'all 0.2s',
                                                                }}
                                                                onMouseOver={(e) => { e.currentTarget.style.opacity = '1'; }}
                                                                onMouseOut={(e) => { e.currentTarget.style.opacity = '0.9'; }}
                                                            >
                                                                <Download size={13} />
                                                                {script.saved ? 'Saved!' : 'Download & Save'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'generated' && (
                    <div className="generated-videos-tab">
                        <h3>My Generated Videos</h3>
                        {workspaceScheduledPosts.length > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                                <a
                                    href={`/calendar/${currentWorkspace?._id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '8px 14px',
                                        borderRadius: '6px',
                                        backgroundColor: '#E4405F',
                                        color: 'white',
                                        textDecoration: 'none',
                                        fontSize: '14px',
                                        fontWeight: 500
                                    }}
                                >
                                    <Calendar size={16} />
                                    View calendar
                                </a>
                            </div>
                        )}
                        {isLoading ? (
                            <div className="videos-grid">
                                <div className="shimmer-grid">
                                    {[...Array(6)].map((_, index) => (
                                        <div key={`shimmer-${index}`} className="shimmer-card">
                                            <div className="shimmer-preview"></div>
                                            <div className="shimmer-content">
                                                <div className="shimmer-title shimmer"></div>
                                                <div className="shimmer-tags">
                                                    <div className="shimmer-tag shimmer"></div>
                                                    <div className="shimmer-tag shimmer"></div>
                                                    <div className="shimmer-tag shimmer"></div>
                                                </div>
                                                <div className="shimmer-description shimmer"></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : generatedVideos.length > 0 ? (
                            <>
                            <div className="videos-grid">
                                {generatedVideos.map(video => (
                                    <div key={video._id} className="video-card">
                                        <div className="video-preview" style={{height: '250px'}}>
                                            <video 
                                                src={video.url} 
                                                controls 
                                                preload={video.thumbnailUrl ? 'none' : 'metadata'}
                                                poster={video.thumbnailUrl || undefined}
                                                crossOrigin="anonymous"
                                                playsInline
                                                // muted
                                                style={{objectFit: 'contain'}}
                                            >
                                                <source src={video.url} type="video/mp4" />
                                                Your browser does not support the video tag.
                                            </video>
                                        </div>
                                        <div className="video-info">
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '12px',
                                                alignContent: 'center',
                                                gap: '8px'
                                            }}>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    {editingGeneratedVideoId === video._id ? (
                                                        <input
                                                            type="text"
                                                            value={editingGeneratedVideoTitle}
                                                            onChange={(e) => setEditingGeneratedVideoTitle(e.target.value)}
                                                            onBlur={saveGeneratedVideoTitle}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') { e.preventDefault(); saveGeneratedVideoTitle(); }
                                                                if (e.key === 'Escape') cancelEditGeneratedVideoTitle();
                                                            }}
                                                            autoFocus
                                                            style={{
                                                                margin: '0 0 4px 0',
                                                                width: '100%',
                                                                padding: '4px 6px',
                                                                fontSize: '1rem',
                                                                fontWeight: 600,
                                                                border: '1px solid var(--input-border)',
                                                                borderRadius: 4,
                                                                background: 'var(--input-bg)',
                                                                color: 'var(--input-text)',
                                                                boxSizing: 'border-box'
                                                            }}
                                                        />
                                                    ) : (
                                                        <h3
                                                            onClick={() => {
                                                                setEditingGeneratedVideoId(video._id);
                                                                setEditingGeneratedVideoTitle(video.title || '');
                                                            }}
                                                            style={{
                                                                margin: '0 0 4px 0',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                cursor: 'pointer'
                                                            }}
                                                            title="Click to edit name"
                                                        >{video.title}</h3>
                                                    )}
                                                    {video.createdAt && (
                                                        <div style={{
                                                            fontSize: '12px',
                                                            color: 'var(--text-secondary)',
                                                            opacity: 0.7
                                                        }}>
                                                            {new Date(video.createdAt).toLocaleDateString()}
                                                        </div>
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                                                    {/* Like/Dislike buttons */}
                                                    <button 
                                                        className="reaction-btn"
                                                        onClick={() => handleVideoReaction(video._id, video.userReaction === 'like' ? null : 'like')}
                                                        style={{
                                                            backgroundColor: video.userReaction === 'like' ? '#10b981' : 'var(--button-bg)',
                                                            color: video.userReaction === 'like' ? 'white' : 'var(--button-text)',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            padding: '4px 8px',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            // marginLeft: '4px',
                                                            opacity: '0.9',
                                                            transition: 'all 0.2s',
                                                        }}
                                                        onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                                                        onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                                                        title={video.userReaction === 'like' ? 'Remove like' : 'Like video'}
                                                    >
                                                        <ThumbsUp size={14} />
                                                    </button>
                                                    {/* <button 
                                                        className="reaction-btn"
                                                        onClick={() => handleVideoReaction(video._id, video.userReaction === 'dislike' ? null : 'dislike')}
                                                        style={{
                                                            backgroundColor: video.userReaction === 'dislike' ? '#ef4444' : 'var(--button-bg)',
                                                            color: video.userReaction === 'dislike' ? 'white' : 'var(--button-text)',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            padding: '4px 8px',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            opacity: '0.9',
                                                            transition: 'all 0.2s',
                                                        }}
                                                        onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                                                        onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                                                        title={video.userReaction === 'dislike' ? 'Remove dislike' : 'Dislike video'}
                                                    >
                                                        <ThumbsDown size={14} />
                                                    </button> */}
                                                    <button 
                                                        className="download-video-btn"
                                                        onClick={() => handleDownloadVideo(video)}
                                                        disabled={downloadingVideos.has(video._id)}
                                                        style={{
                                                            backgroundColor: 'var(--button-bg)',
                                                            color: 'var(--button-text)',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            padding: '4px 8px',
                                                            cursor: downloadingVideos.has(video._id) ? 'not-allowed' : 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            opacity: downloadingVideos.has(video._id) ? '0.6' : '0.9',
                                                            transition: 'opacity 0.2s',
                                                            marginLeft: '10px'
                                                        }}
                                                        onMouseOver={(e) => !downloadingVideos.has(video._id) && (e.currentTarget.style.opacity = '1')}
                                                        onMouseOut={(e) => !downloadingVideos.has(video._id) && (e.currentTarget.style.opacity = '0.9')}
                                                    >
                                                        {downloadingVideos.has(video._id) ? (
                                                            <span className="spinner-border" style={{ width: 14, height: 14, borderWidth: '2px' }} />
                                                        ) : (
                                                        <Download size={14} />
                                                        )}
                                                    </button>
                                                    <button 
                                                        className="delete-video-btn"
                                                        onClick={() => handleDeleteGeneratedVideo(video._id)}
                                                        style={{
                                                            backgroundColor: 'var(--input-bg)',
                                                            color: 'var(--input-text)',
                                                            border: '1px solid var(--input-border)',
                                                            borderRadius: '4px',
                                                            padding: '4px 8px',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            opacity: '0.9',
                                                            transition: 'all 0.2s',
                                                            marginLeft: '8px'
                                                        }}
                                                        onMouseOver={(e) => {
                                                            e.currentTarget.style.opacity = '1';
                                                            e.currentTarget.style.backgroundColor = '#ff4444';
                                                            e.currentTarget.style.color = 'white';
                                                            e.currentTarget.style.borderColor = '#ff4444';
                                                        }}
                                                        onMouseOut={(e) => {
                                                            e.currentTarget.style.opacity = '0.9';
                                                            e.currentTarget.style.backgroundColor = 'var(--input-bg)';
                                                            e.currentTarget.style.color = 'var(--text-color)';
                                                            e.currentTarget.style.borderColor = 'var(--input-border)';
                                                        }}
                                                        title="Delete video"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px', fontSize: '14px', fontFamily: SYSTEM_UI_FONT }}>
                                                <div style={{ flex: 1, color: darkMode ? '#e0e0e0' : '#333', fontFamily: SYSTEM_UI_FONT }}>
                                                    {(() => {
                                                        const description = video.description || 'No description';
                                                        const isExpanded = expandedDescriptions.has(video._id);
                                                        const shouldTruncate = description.length > 70;
                                                        const displayText = shouldTruncate && !isExpanded 
                                                            ? description.substring(0, 70) + '...' 
                                                            : description;
                                                        
                                                        return (
                                                            <>
                                                                <span style={{marginRight: '4px'}}>{displayText}</span>
                                                                {shouldTruncate && (
                                                                    <button
                                                                        onClick={() => {
                                                                            const newExpanded = new Set(expandedDescriptions);
                                                                            if (isExpanded) {
                                                                                newExpanded.delete(video._id);
                                                                            } else {
                                                                                newExpanded.add(video._id);
                                                                            }
                                                                            setExpandedDescriptions(newExpanded);
                                                                        }}
                                                                        style={{
                                                                            background: 'none',
                                                                            border: 'none',
                                                                            color: 'var(--primary-color)',
                                                                            cursor: 'pointer',
                                                                            padding: '0',
                                                                            fontSize: '14px',
                                                                            fontWeight: '500',
                                                                            textDecoration: 'underline'
                                                                        }}
                                                                    >
                                                                        {isExpanded ? 'see less' : 'see more'}
                                                                    </button>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                                <button
                                                    onClick={async () => {
                                                        const description = video.description || 'No description';
                                                        try {
                                                            await navigator.clipboard.writeText(description);
                                                            const newCopied = new Set(copiedDescriptions);
                                                            newCopied.add(video._id);
                                                            setCopiedDescriptions(newCopied);
                                                            setTimeout(() => {
                                                                setCopiedDescriptions(prev => {
                                                                    const updated = new Set(prev);
                                                                    updated.delete(video._id);
                                                                    return updated;
                                                                });
                                                            }, 1000);
                                                        } catch (err) {
                                                            console.error('Failed to copy:', err);
                                                        }
                                                    }}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: darkMode ? '#e0e0e0' : '#666',
                                                        cursor: 'pointer',
                                                        padding: '4px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        opacity: 0.7,
                                                        transition: 'opacity 0.2s',
                                                        flexShrink: 0
                                                    }}
                                                    onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                                                    onMouseOut={(e) => e.currentTarget.style.opacity = '0.7'}
                                                    title="Copy description to clipboard"
                                                >
                                                    {copiedDescriptions.has(video._id) ? (
                                                        <Check size={16} style={{ color: '#4CAF50' }} />
                                                    ) : (
                                                        <Copy size={16} />
                                                    )}
                                                </button>
                                            </div>
                                            {video.musicName && (
                                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px', fontStyle: 'italic' }}>
                                                    Music: {video.musicName}
                                                </div>
                                            )}
                                            <div className="video-tags">
                                                {(video.tags || []).map(tag => (
                                                    <span key={tag} className="tag">{tag}</span>
                                                ))}
                                            </div>
                                            {['673247877e4ac0a824cbac0d' /*Boukar*/, '68c2c068253b59e3eb46452c' /*Oleg*/, '68c2b8da253b59e3eb4644ff' /*Mauricio*/, '698da27e796493d5a4557a0e' /* Ismael */, '69fcd261ca736cd0a5b72e72' /*Valentina*/, '69ff1cc295660f26f7a896a6' /*Valentina*/].includes(currentUserId) && (
                                                <div style={{ display: 'flex', gap: '8px', marginTop: '12px', paddingTop: '8px', borderTop: darkMode ? '1px solid #333' : '1px solid #eee' }}>
                                                    <button 
                                                        className="publish-btn"
                                                        onClick={() => setShowPublishModal({ videoId: video._id })}
                                                        disabled={(!getlateConnectedAccounts.instagram && !getlateConnectedAccounts.tiktok) || getlatePublishing.has(video._id)}
                                                        style={{
                                                            backgroundColor: '#E4405F',
                                                            color: 'white',
                                                            border: '1px solid var(--input-border)',
                                                            borderRadius: '4px',
                                                            padding: '4px 12px',
                                                            cursor: ((!getlateConnectedAccounts.instagram && !getlateConnectedAccounts.tiktok) || getlatePublishing.has(video._id)) ? 'not-allowed' : 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            opacity: ((!getlateConnectedAccounts.instagram && !getlateConnectedAccounts.tiktok) || getlatePublishing.has(video._id)) ? 0.6 : 1,
                                                            transition: 'all 0.2s',
                                                            fontSize: '12px'
                                                        }}
                                                        onMouseOver={(e) => {
                                                            if ((getlateConnectedAccounts.instagram || getlateConnectedAccounts.tiktok) && !getlatePublishing.has(video._id)) {
                                                                e.currentTarget.style.opacity = '1';
                                                            }
                                                        }}
                                                        onMouseOut={(e) => {
                                                            if ((getlateConnectedAccounts.instagram || getlateConnectedAccounts.tiktok) && !getlatePublishing.has(video._id)) {
                                                                e.currentTarget.style.opacity = '1';
                                                            }
                                                        }}
                                                        title={(!getlateConnectedAccounts.instagram && !getlateConnectedAccounts.tiktok) ? 'Connect Instagram or TikTok first' : 'Publish to social'}
                                                    >
                                                        {getlatePublishing.has(video._id) ? (
                                                            <div className="spinner-border" style={{
                                                                width: '12px',
                                                                height: '12px',
                                                                border: '2px solid currentColor',
                                                                borderTop: '2px solid transparent',
                                                                borderRadius: '50%',
                                                                animation: 'spin 1s linear infinite'
                                                            }} />
                                                        ) : (
                                                            <Share2 size={12} />
                                                        )}
                                                        Publish
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {generatedHasMore ? (
                                <div ref={generatedVideosLoadMoreRef} style={{ height: '1px', width: '100%' }} aria-hidden />
                            ) : null}
                            {isLoadingGeneratedPage ? (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '16px',
                                    fontSize: '13px',
                                    color: 'var(--text-secondary)',
                                }}>
                                    <span className="spinner-border" style={{
                                        marginRight: '8px',
                                        verticalAlign: 'middle',
                                        width: '14px',
                                        height: '14px',
                                        border: '2px solid var(--text-secondary)',
                                        borderTopColor: 'transparent',
                                        borderRadius: '50%',
                                        display: 'inline-block',
                                        animation: 'spin 1s linear infinite'
                                    }} />
                                    Loading more videos…
                                </div>
                            ) : null}
                            </>
                        ) : (
                            <div className="empty-state">
                                <div className="empty-state-icon">
                                    <PlayCircle size={48} />
                                </div>
                                <h3>No Generated Videos Yet</h3>
                                <p>Start creating amazing videos using the Studio tab. Generate scripts, add voiceovers, and create professional videos.</p>
                                <button 
                                    className="cta-button"
                                    onClick={() => setActiveTab('studio')}
                                    style={{
                                        backgroundColor: 'var(--primary-color)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '12px 24px',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        marginTop: '16px',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-color-hover)'}
                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-color)'}
                                >
                                    Go to Studio
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'studio' && (
                    /* START OF STUDIO TAB CONTENT */
                    <> 
                        <div className="studio-tab">
                            <div style={{ paddingTop: '8px' }}>
                                {/* Paywall for Studio when workspace has more than 3 generated video and is not subscribed */}
                                {showStudioPaywall ? (
                                    <div style={{
                                        marginTop: '16px',
                                        padding: '12px',
                                        backgroundColor: 'var(--input-bg)',
                                        color: 'var(--text-color)',
                                        border: '1px solid #FFD700',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignContent: 'center',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center'
                                    }}>
                                        {showUpgradePaywall && !organicStudioPaywall && (
                                            <button
                                                type="button"
                                                onClick={() => setShowUpgradePaywall(false)}
                                                style={{
                                                    alignSelf: 'flex-end',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: 'var(--text-secondary)',
                                                    cursor: 'pointer',
                                                    marginBottom: '8px',
                                                    fontSize: '13px'
                                                }}
                                            >
                                                Not now
                                            </button>
                                        )}
                                        {hasPermission('admin') ? (
                                            <>
                                        <div style={{ marginBottom: '10px' }}>
                                            You have reached the limit of the free plan. To access the studio, please subscribe to a paid plan.
                                        </div>
                                
                                        {/* Feature Comparison */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <h4 style={{ marginBottom: '15px', textAlign: 'center', color: 'var(--text-color)' }}>Choose Your Plan</h4>
                                            
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                                                {/* Creator Plan */}
                                                <div style={{ 
                                                    backgroundColor: 'var(--card-bg)', 
                                                    border: '1px solid var(--input-border)', 
                                                    borderRadius: '8px', 
                                                    padding: '15px',
                                                    textAlign: 'center'
                                                }}>
                                                    <h5 style={{ marginTop: 0, marginBottom: '10px', color: 'var(--text-color)' }}>Creator</h5>
                                                    <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px', color: 'var(--text-color)' }}>{currency}25/month</div>
                                                    <ul style={{ textAlign: 'left', fontSize: '12px', color: 'var(--text-secondary)', margin: 0, paddingLeft: '15px' }}>
                                                        <li><span style={{ textDecoration: 'line-through' }}>20 videos per month</span> Unlimited videos</li>
                                                        <li>Import raw content</li>
                                                        <li>Generate hooks, scripts, voiceovers</li>
                                                        <li>Generate videos</li>
                                                        <li>50GB storage</li>
                                                    </ul>
                                                </div>
                                                
                                                {/* Pro Plan */}
                                                <div style={{ 
                                                    backgroundColor: 'var(--card-bg)', 
                                                    border: '2px solid #1976d2', 
                                                    borderRadius: '8px', 
                                                    padding: '15px',
                                                    textAlign: 'center',
                                                    position: 'relative'
                                                }}>
                                                    <div style={{ 
                                                        position: 'absolute', 
                                                        top: '-8px', 
                                                        left: '50%', 
                                                        transform: 'translateX(-50%)', 
                                                        backgroundColor: '#1976d2', 
                                                        color: 'white', 
                                                        padding: '2px 8px', 
                                                        borderRadius: '4px', 
                                                        fontSize: '10px',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        POPULAR
                                                    </div>
                                                    <h5 style={{ marginTop: '8px', marginBottom: '10px', color: 'var(--text-color)' }}>Pro</h5>
                                                    <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px', color: 'var(--text-color)' }}>{currency}99/month</div>
                                                    <ul style={{ textAlign: 'left', fontSize: '12px', color: 'var(--text-secondary)', margin: 0, paddingLeft: '15px' }}>
                                                        <li>150 videos per month</li>
                                                        <li>Import raw content</li>
                                                        <li>Generate hooks, scripts, voiceovers</li>
                                                        <li>Generate videos</li>
                                                        <li>200GB storage per profile</li>
                                                        <li>+ Invite team members</li>
                                                        <li>+ Multiple brand profiles (+{currency}99 per profile)</li>
                                                    </ul>
                                                    <p> Become an infinite content machine for {currency}99/month</p>
                                                </div>
                                        
                                                {/* Custom Solution */}
                                                <div style={{ 
                                                    backgroundColor: 'var(--card-bg)', 
                                                    border: '1px solid var(--input-border)', 
                                                    borderRadius: '8px', 
                                                    padding: '15px',
                                                    textAlign: 'center'
                                                }}>
                                                    <h5 style={{ marginTop: 0, marginBottom: '10px', color: 'var(--text-color)' }}>Custom</h5>
                                                    <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px', color: 'var(--text-color)' }}>Contact Us</div>
                                                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textAlign: 'center' }}>
                                                        <p style={{ margin: '0 0 8px 0' }}>Need more? Reach out for custom solution</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                
                                        <div style={{ marginBottom: '15px', fontSize: '14px', color: 'var(--text-color-secondary)' }}>
                                            <a 
                                                href="/privacy-policy" 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                style={{ color: 'var(--text-color-secondary)', textDecoration: 'underline', marginRight: '15px' }}
                                            >
                                                Privacy Policy
                                            </a>
                                            <a 
                                                href="/terms-of-service" 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                style={{ color: 'var(--text-color-secondary)', textDecoration: 'underline' }}
                                            >
                                                Terms of Service
                                            </a>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                            <button
                                                className="main-button"
                                                onClick={async () => {
                                                    try {
                                                                const response = await api.createCheckoutSession('creator', currentWorkspace._id);
                                                        window.location.href = response.data.url;
                                                    } catch (e) {
                                                        setErrors(prev => [...prev, 'Failed to start checkout for Creator plan']);
                                                    }
                                                }}
                                            >
                                                Subscribe: Merra AI Creator — {currency}25/month
                                            </button>
                                            <button
                                                className="main-button"
                                                onClick={async () => {
                                                    try {
                                                                const response = await api.createCheckoutSession('pro', currentWorkspace._id);
                                                        window.location.href = response.data.url;
                                                    } catch (e) {
                                                        setErrors(prev => [...prev, 'Failed to start checkout for Pro plan']);
                                                    }
                                                }}
                                            >
                                                Subscribe: Merra AI Pro — {currency}99/month
                                            </button>
                                        </div>
                                        <div style={{ marginTop: '0px', textAlign: 'center' }}>
                                                <p style={{ marginBottom: '10px', color: 'var(--text-secondary)', fontSize: '14px' }}>or</p>
                                                <button
                                                    className="main-button"
                                                    onClick={() => {
                                                        window.open('https://calendly.com/boukar-o-sall/30min', '_blank');
                                                    }}
                                                    style={{
                                                        backgroundColor: 'transparent',
                                                        border: darkMode ? '1px solid #e1f679' : '1px solid var(--text-secondary)',
                                                        color: darkMode ? '#e1f679' : 'black',
                                                        boxShadow: darkMode ? '0 4px 20px rgba(0,0,0,0.2)' : '0 4px 20px rgba(0,0,0,0.1)',
                                                    }}
                                                >
                                                    Book a feedback call with us and get a month for free
                                                </button>
                                            </div>
                                            </>
                                        ) : (
                                            <>
                                                <div style={{ marginBottom: '10px' }}>
                                                    This workspace has reached its limit to create videos from the Studio. To use that feature, ask your workspace administrator to upgrade the workspace plan. Here are the options:
                                                </div>
                                                
                                                {/* Feature Comparison */}
                                                <div style={{ marginBottom: '20px' }}>
                                                    <h4 style={{ marginBottom: '15px', textAlign: 'center', color: 'var(--text-color)' }}>Available Plans</h4>
                                                    
                                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                                                        {/* Creator Plan */}
                                                        <div style={{ 
                                                            backgroundColor: 'var(--card-bg)', 
                                                            border: '1px solid var(--input-border)', 
                                                            borderRadius: '8px', 
                                                            padding: '15px',
                                                            textAlign: 'center'
                                                        }}>
                                                            <h5 style={{ marginTop: 0, marginBottom: '10px', color: 'var(--text-color)' }}>Creator</h5>
                                                            <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px', color: 'var(--text-color)' }}>{currency}25/month</div>
                                                            <ul style={{ textAlign: 'left', fontSize: '12px', color: 'var(--text-secondary)', margin: 0, paddingLeft: '15px' }}>
                                                                <li><span style={{ textDecoration: 'line-through' }}>20 videos per month</span> Unlimited videos</li>
                                                                <li>Import raw content</li>
                                                                <li>Generate hooks, scripts, voiceovers</li>
                                                                <li>Generate videos</li>
                                                                <li>50GB storage</li>
                                                            </ul>
                                                        </div>
                                                        
                                                        {/* Pro Plan */}
                                                        <div style={{ 
                                                            backgroundColor: 'var(--card-bg)', 
                                                            border: '2px solid #1976d2', 
                                                            borderRadius: '8px', 
                                                            padding: '15px',
                                                            textAlign: 'center',
                                                            position: 'relative'
                                                        }}>
                                                            <div style={{ 
                                                                position: 'absolute', 
                                                                top: '-8px', 
                                                                left: '50%', 
                                                                transform: 'translateX(-50%)', 
                                                                backgroundColor: '#1976d2', 
                                                                color: 'white', 
                                                                padding: '2px 8px', 
                                                                borderRadius: '4px', 
                                                                fontSize: '10px',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                POPULAR
                                                            </div>
                                                            <h5 style={{ marginTop: '8px', marginBottom: '10px', color: 'var(--text-color)' }}>Pro</h5>
                                                            <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px', color: 'var(--text-color)' }}>{currency}99/month</div>
                                                            <ul style={{ textAlign: 'left', fontSize: '12px', color: 'var(--text-secondary)', margin: 0, paddingLeft: '15px' }}>
                                                                <li>150 videos per month</li>
                                                                <li>Import raw content</li>
                                                                <li>Generate hooks, scripts, voiceovers</li>
                                                                <li>Generate videos</li>
                                                                <li>200GB storage per profile</li>
                                                                <li>+ Invite team members</li>
                                                                <li>+ Multiple brand profiles (+{currency}99 per profile)</li>
                                                            </ul>
                                                            <p> Become an infinite content machine for {currency}99/month</p>
                                                        </div>
                
                                                        {/* Custom Solution */}
                                                        <div style={{ 
                                                            backgroundColor: 'var(--card-bg)', 
                                                            border: '1px solid var(--input-border)', 
                                                            borderRadius: '8px', 
                                                            padding: '15px',
                                                            textAlign: 'center'
                                                        }}>
                                                            <h5 style={{ marginTop: 0, marginBottom: '10px', color: 'var(--text-color)' }}>Custom</h5>
                                                            <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px', color: 'var(--text-color)' }}>Contact Us</div>
                                                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textAlign: 'center' }}>
                                                                <p style={{ margin: '0 0 8px 0' }}>Need more? Reach out for custom solution</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ) :
                                (<div
                                    style={{
                                        backgroundColor: 'var(--input-bg)',
                                        border: '1px solid var(--input-border)',
                                        borderRadius: '8px', padding: '12px', marginBottom: '12px'
                                    }}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                alignItems: 'center',
                                                gap: '10px',
                                                marginTop: 0,
                                                marginBottom: '12px',
                                            }}
                                        >
                                            <span style={{
                                                fontSize: window.innerWidth <= 600 ? '16px' : '20px',
                                                fontWeight: 800,
                                                color: 'var(--text-color)',
                                                marginRight: '2px',
                                            }}>
                                                Studio
                                            </span>
                                            <div
                                                className="studio-subtab-picker"
                                                style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}
                                                role="tablist"
                                                aria-label="Studio mode"
                                            >
                                                {[
                                                    { id: 'vlog', label: 'Vlog' },
                                                    { id: 'visual-only', label: 'Visual only' },
                                                    { id: 'text-on-screen', label: 'Text on screen' },
                                                    { id: 'carousel', label: 'Carousel' },
                                                ].map(({ id, label }) => {
                                                    const selected = studioSubTab === id;
                                                    return (
                                                        <button
                                                            key={id}
                                                            type="button"
                                                            role="tab"
                                                            aria-selected={selected}
                                                            onClick={() => {
                                                                if (id === 'visual-only') {
                                                                    if (studioSubTab === 'visual-only') return;
                                                                    handleNoScriptModeClick();
                                                                    return;
                                                                }
                                                                if (studioSubTab === 'visual-only' || isNoScriptMode) {
                                                                    exitVisualOnlyMode(id);
                                                                    return;
                                                                }
                                                                setStudioSubTab(id);
                                                                try { localStorage.setItem('workspace_studio_sub_tab', id); } catch (_) {}
                                                            }}
                                                            style={{
                                                                padding: window.innerWidth <= 600 ? '9px 16px' : '10px 20px',
                                                                borderRadius: '16px',
                                                                border: selected
                                                                    ? '1px solid rgba(225, 246, 121, 0.65)'
                                                                    : (darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.14)'),
                                                                backgroundColor: selected
                                                                    ? (darkMode ? 'rgba(225, 246, 121, 0.14)' : 'rgba(200, 230, 80, 0.28)')
                                                                    : (darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'),
                                                                color: selected ? (darkMode ? '#e1f679' : '#3d4f00') : 'var(--text-secondary)',
                                                                fontSize: window.innerWidth <= 600 ? '13px' : '14.5px',
                                                                fontWeight: 800,
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            {label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <div
                                            role="separator"
                                            style={{
                                                borderTop: '1px solid var(--input-border)',
                                                marginBottom: '16px',
                                            }}
                                        />

                                        {studioSubTab === 'vlog' && (
                                        <>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                alignItems: 'center',
                                                gap: '8px',
                                                marginBottom: '12px',
                                            }}
                                        >
                                            <span style={{ fontSize: '13px', fontWeight: 800, color: 'var(--text-secondary)', marginRight: '2px' }}>
                                                Source videos:
                                            </span>
                                            {[
                                                { mode: 'all', label: 'All videos' },
                                                { mode: 'select', label: 'Select' },
                                                { mode: 'import', label: 'Import new' },
                                            ].map(({ mode, label }) => {
                                                const selected = studioVideoSourceMode === mode;
                                                return (
                                                    <button
                                                        key={mode}
                                                        type="button"
                                                        onClick={() => handleStudioVideoSourceChange(mode)}
                                                        style={{
                                                            padding: '8px 14px',
                                                            borderRadius: '14px',
                                                            border: selected
                                                                ? '1px solid rgba(225, 246, 121, 0.55)'
                                                                : (darkMode ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(0,0,0,0.12)'),
                                                            backgroundColor: selected
                                                                ? (darkMode ? 'rgba(225, 246, 121, 0.10)' : 'rgba(200, 230, 80, 0.22)')
                                                                : (darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'),
                                                            color: selected ? (darkMode ? '#e1f679' : '#3d4f00') : 'var(--text-secondary)',
                                                            fontSize: '12.5px',
                                                            fontWeight: 800,
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        {label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {studioSubTab === 'vlog' && studioVideoSourceMode === 'select' && studioSelectedRawVideoIds.length > 0 && (
                                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px' }}>
                                                Using {studioSelectedRawVideoIds.length} selected raw video{studioSelectedRawVideoIds.length !== 1 ? 's' : ''}.{' '}
                                                <button
                                                    type="button"
                                                    onClick={() => openStudioRawVideosPicker()}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: 'var(--button-bg, #1976d2)',
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline',
                                                        padding: 0,
                                                        fontSize: '12px',
                                                    }}
                                                >
                                                    Change selection
                                                </button>
                                            </div>
                                        )}

                                        {/* Profile Completeness Warning Banner */}
                                        {/* {!profileCompleteness.isComplete && (
                                            <div style={{
                                                backgroundColor: profileCompleteness.completenessPercentage >= 50 
                                                    ? (darkMode ? '#1a2332' : '#e7f3ff') 
                                                    : (darkMode ? '#2d2419' : '#fff3cd'),
                                                border: `1px solid ${profileCompleteness.completenessPercentage >= 50 
                                                    ? (darkMode ? '#2d4a6b' : '#b3d9ff') 
                                                    : (darkMode ? '#4a3a1a' : '#ffeaa7')}`,
                                                borderRadius: '8px',
                                                padding: '12px',
                                                marginBottom: '16px',
                                                color: profileCompleteness.completenessPercentage >= 50 
                                                    ? (darkMode ? '#7bb3f0' : '#0066cc') 
                                                    : (darkMode ? '#d4a853' : '#856404')
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                                    <span style={{ fontSize: '16px' }}>
                                                        {profileCompleteness.completenessPercentage >= 50 ? '💡' : '⚠️'}
                                                    </span>
                                                    <strong>
                                                        Profile {profileCompleteness.completenessPercentage >= 50 ? 'Could Be Enhanced' : 'info incomplete'} ({profileCompleteness.completenessPercentage}%)
                                                    </strong>
                                                </div>
                            
                                                <div style={{ 
                                                    width: '100%', 
                                                    backgroundColor: profileCompleteness.completenessPercentage >= 50 
                                                        ? (darkMode ? '#1e2a3a' : '#cce7ff') 
                                                        : (darkMode ? '#3d2f0f' : '#ffeaa7'), 
                                                    borderRadius: '4px', 
                                                    height: '8px', 
                                                    marginBottom: '8px',
                                                    overflow: 'hidden'
                                                }}>
                                                    <div style={{
                                                        width: `${profileCompleteness.completenessPercentage}%`,
                                                        backgroundColor: profileCompleteness.completenessPercentage >= 50 
                                                            ? (darkMode ? '#4a90e2' : '#0066cc') 
                                                            : (darkMode ? '#f39c12' : '#ffc107'),
                                                        height: '100%',
                                                        transition: 'width 0.3s ease'
                                                    }}></div>
                                                </div>
                                                
                                                {window.innerWidth > 600 && (
                                                    <>
                                                        <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                                                            {profileCompleteness.completenessPercentage >= 50 
                                                                ? 'Add more information to your profile for better results.'
                                                                : 'Your profile is not complete enough, so AI will have to guess, which can reduce script quality.'
                                                            }
                                                        </div>
                                                        <div style={{ 
                                                            fontSize: '12px', 
                                                            color: profileCompleteness.completenessPercentage >= 50 
                                                                ? (darkMode ? '#8bb3d9' : '#4d79a3') 
                                                                : (darkMode ? '#a0a0a0' : '#6c757d') 
                                                        }}>
                                                            <strong>Missing:</strong> {profileCompleteness.missingFields.join(', ')}
                                                        </div>
                                                        <div style={{ marginTop: '8px' }}>
                                                            <button 
                                                                onClick={() => setActiveTab('profile')}
                                                                style={{
                                                                    backgroundColor: profileCompleteness.completenessPercentage >= 50 
                                                                        ? (darkMode ? '#4a90e2' : '#0066cc') 
                                                                        : (darkMode ? '#f39c12' : '#ffc107'),
                                                                    color: profileCompleteness.completenessPercentage >= 50 ? '#fff' : (darkMode ? '#000' : '#000'),
                                                                    border: 'none',
                                                                    borderRadius: '4px',
                                                                    padding: '6px 12px',
                                                                    fontSize: '12px',
                                                                    cursor: 'pointer',
                                                                    fontWeight: 'bold',
                                                                }}
                                                            >
                                                                {profileCompleteness.completenessPercentage >= 50 ? 'Enhance Profile →' : 'Complete Profile →'}
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )} */}

                                        {/* Auto-Create Button with Settings */}
                                        <div style={{ marginBottom: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            <button
                                                onClick={() => {
                                                    trackEvent('auto_create_clicked', { page: 'WorkspaceManagement' });
                                                    studioAutoCreate();
                                                }}
                                                aria-label="Auto create video"
                                                className="waitlist-button"
                                                disabled={(studioGenerating || studioGeneratingVoice || studioGeneratingVideo) && studioAutoCreateMode || isNoScriptMode}
                                                onMouseEnter={(e) => {
                                                    if (!e.target.disabled) {
                                                        e.target.style.opacity = '0.9';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!e.target.disabled) {
                                                        e.target.style.opacity = '1';
                                                    }
                                                }}
                                                style={{
                                                    flex: 1, zIndex: 10003, marginLeft: '32px',
                                                    opacity: ((studioGenerating || studioGeneratingVoice || studioGeneratingVideo) && studioAutoCreateMode || isNoScriptMode) ? 0.5 : 1,
                                                    cursor: ((studioGenerating || studioGeneratingVoice || studioGeneratingVideo) && studioAutoCreateMode || isNoScriptMode) ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                <span style={{color: 'black'}}>
                                                    {(studioGenerating || studioGeneratingVoice || studioGeneratingVideo) && studioAutoCreateMode
                                                        ? 'Auto-creating video...' 
                                                        : 'One-click Auto-Create'}
                                                </span>
                                                <span className="waitlist-shimmer"></span>
                                            </button>
                                            <div style={{ position: 'relative', zIndex: 10001}} ref={studioSettingsDropdownRef}>
                                                <button
                                                    onClick={() => setShowStudioSettingsDropdown(!showStudioSettingsDropdown)}
                                                    aria-label="Generation settings"
                                                    style={{
                                                        backgroundColor: 'var(--input-bg)',
                                                        color: 'var(--input-text)',
                                                        border: '1px solid var(--input-border)',
                                                        borderRadius: '4px',
                                                        padding: '4px 8px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        opacity: '0.9',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onMouseOver={(e) => {
                                                        e.currentTarget.style.opacity = '1';
                                                        e.currentTarget.style.backgroundColor = 'var(--button-bg)';
                                                        e.currentTarget.style.color = 'white';
                                                        e.currentTarget.style.borderColor = 'var(--button-bg)';
                                                    }}
                                                    onMouseOut={(e) => {
                                                        e.currentTarget.style.opacity = '0.9';
                                                        e.currentTarget.style.backgroundColor = 'var(--input-bg)';
                                                        e.currentTarget.style.color = 'var(--text-color)';
                                                        e.currentTarget.style.borderColor = 'var(--input-border)';
                                                    }}
                                                >
                                                    <Settings size={14} />
                                                </button>
                                                {showStudioSettingsDropdown && renderGenerationSettingsDropdown()}
                                            </div>
                                        </div>
                                        
                                        <div style={{ textAlign: 'center', marginBottom: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                                            OR step by step
                                        </div>

                                        <div style={{backgroundColor: 'var(--input-bg)',
                                                border: '1px solid var(--input-border)',
                                                borderRadius: '8px', padding: '12px', marginBottom: '12px'
                                            }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                {currentUserId === '673247877e4ac0a824cbac0dx' && ( // remove x to display for boukar
                                                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                                        {hookStats.availableHooks} of {hookStats.totalHooks} hooks available
                                                    </span>
                                                )}
                                                {currentUserId === '673247877e4ac0a824cbac0dx' && ( // remove x to display for boukar
                                                    <button 
                                                        onClick={refreshAvailableHooks}
                                                        style={{
                                                            padding: '4px 8px',
                                                            fontSize: '12px',
                                                            backgroundColor: 'var(--button-bg)',
                                                            color: 'var(--button-text)',
                                                            border: '1px solid var(--input-border)',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        Refresh
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                
                                        <h4 style={{ whiteSpace: screenWidth <= 600 ? 'nowrap' : 'normal' }}> {/* remove x to display for boukar */}
                                            {currentUserId === '673247877e4ac0a824cbac0dx' ? '1 - Select Hook' : '1 - Hook'}
                                            <span style={{ 
                                                marginLeft: '8px', 
                                                fontSize: '12px', 
                                                fontWeight: 'normal',
                                                background: 'linear-gradient(45deg, #ff6b6b, #ff8e8e)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                                backgroundClip: 'text'
                                            }}>@HookDesigner</span>
                                        </h4>
                                
                                        {/* Hook */}
                                        <div style={{ marginBottom: '12px' }}>
                                            <div style={{
                                                display: 'flex',
                                                gap: '8px',
                                                alignItems: 'flex-start',
                                                flexDirection: screenWidth <= 600 ? 'column' : 'row',
                                            }}>
                                                {currentUserId === '673247877e4ac0a824cbac0dx' ? ( // remove x to display for boukar
                                                    <select
                                                        value={studioHook}
                                                        onChange={(e) => handleHookSelect(e.target.value)}
                                                        style={{
                                                            flex: 1,
                                                            padding: '8px',
                                                            borderRadius: '6px',
                                                            border: '1px solid var(--input-border)',
                                                            background: 'var(--input-bg)',
                                                            color: 'var(--button-text)',
                                                            fontSize: '14px',
                                                            boxSizing: 'border-box',
                                                            width: screenWidth <= 600 ? '100%' : 'auto',
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        <option value="">Select a hook...</option>
                                                        {availableHooks.map((hook, index) => (
                                                            <option key={index} value={hook}>
                                                                {hook.length > 80 ? `${hook.substring(0, 80)}...` : hook}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <TextareaAutosize
                                                        value={studioHook}
                                                        onChange={(e) => handleHookSelect(e.target.value)}
                                                        placeholder="No hook selected — scripts will use your profile and topic."
                                                        minRows={2}
                                                        maxRows={6}
                                                        style={{
                                                            flex: 1,
                                                            width: screenWidth <= 600 ? '100%' : 'auto',
                                                            minWidth: 0,
                                                            padding: '8px 10px',
                                                            borderRadius: '6px',
                                                            border: '1px solid var(--input-border)',
                                                            background: 'var(--input-bg)',
                                                            color: 'var(--text-color)',
                                                            fontSize: '13px',
                                                            lineHeight: 1.45,
                                                            resize: 'none',
                                                            boxSizing: 'border-box',
                                                            fontFamily: 'inherit',
                                                        }}
                                                    />
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={selectRandomHook}
                                                    disabled={availableHooks.length === 0}
                                                    style={{
                                                        padding: '8px 12px',
                                                        fontSize: '12px',
                                                        backgroundColor: 'var(--button-bg)',
                                                        color: 'var(--button-text)',
                                                        border: '1px solid var(--input-border)',
                                                        borderRadius: '6px',
                                                        cursor: availableHooks.length > 0 ? 'pointer' : 'not-allowed',
                                                        opacity: availableHooks.length > 0 ? 1 : 0.5,
                                                        fontWeight: 'bold',
                                                        flexShrink: 0,
                                                        width: screenWidth <= 600 ? '100%' : 'auto',
                                                    }}
                                                >
                                                    Refresh Hook
                                                </button>
                                            </div>
                                            {studioHook && (
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    marginTop: '8px',
                                                }}>
                                                    <button
                                                        type="button"
                                                        onClick={clearStudioHook}
                                                        aria-label="Remove hook"
                                                        title="Remove hook"
                                                        style={{
                                                            flexShrink: 0,
                                                            width: '32px',
                                                            height: '32px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            borderRadius: '8px',
                                                            border: '1px solid var(--input-border)',
                                                            background: 'var(--input-bg)',
                                                            color: 'var(--text-secondary)',
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                                                        Remove hook to generate scripts from your profile instead of a template.
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Topic Field */}
                                        <div style={{ marginBottom: '12px' }}>
                                            <label 
                                                onClick={() => setShowTopicInput(!showTopicInput)}
                                                style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    marginBottom: '6px', 
                                                    fontSize: '14px',
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    color: 'var(--text-primary)',
                                                    transition: 'opacity 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                                                onMouseLeave={(e) => e.target.style.opacity = '1'}
                                            >
                                                <span 
                                                    style={{
                                                        display: 'inline-block',
                                                        transform: showTopicInput ? 'rotate(90deg)' : 'rotate(0deg)',
                                                        transition: 'transform 0.2s ease',
                                                        fontSize: '10px',
                                                        color: 'var(--text-secondary)'
                                                    }}
                                                >
                                                    ▶
                                                </span>
                                                Video theme
                                            </label>
                                            {showTopicInput && (
                                                <TextareaAutosize
                                                    value={studioTopic}
                                                    onChange={(e) => setStudioTopic(e.target.value)}
                                                    placeholder="What is this video about? (optional)"
                                                    minRows={2}
                                                    maxRows={8}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px',
                                                        borderRadius: '6px',
                                                        border: '1px solid var(--input-border)',
                                                        background: 'var(--input-bg)',
                                                        color: 'var(--input-text)',
                                                        fontSize: '14px',
                                                        boxSizing: 'border-box',
                                                        resize: 'none',
                                                        fontFamily: 'inherit'
                                                    }}
                                                />
                                            )}
                                        </div>
                                
                                        {/* Topic Field */}
                                        {!(currentWorkspace?.profile?.currentSituation?.trim()) && (
                                            <div style={{
                                                backgroundColor: darkMode ? '#1a2332' : '#e7f3ff',
                                                border: `1px solid ${darkMode ? '#2d4a6b' : '#b3d9ff'}`,
                                                borderRadius: '8px',
                                                padding: '12px',
                                                marginBottom: '16px',
                                                color: darkMode ? '#7bb3f0' : '#0066cc'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                                    <span style={{ fontSize: '16px' }}>💡</span>
                                                    <strong>To make better scripts, add a bio to your profile</strong>
                                                </div>
                                                {/* <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                                                    A short "Current situation" in your workspace profile helps the AI tailor scripts to you.
                                                </div> */}
                                                <div style={{ marginTop: '8px' }}>
                                                    <button
                                                        onClick={() => setActiveTab('profile')}
                                                        style={{
                                                            backgroundColor: 'var(--button-bg)',
                                                            color: 'var(--button-text)',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            padding: '6px 12px',
                                                            fontSize: '13px',
                                                            cursor: 'pointer',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        Add profile bio →
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        <div style={{ 
                                            display: 'flex', 
                                            gap: '10px', 
                                            marginTop: '10px', 
                                            alignItems: 'center',
                                            flexDirection: screenWidth <= 450 ? 'column' : 'row'
                                        }}>
                                            {/* Language Picker for Generation */}
                                            <div style={{ position: 'relative', width: screenWidth <= 450 ? '100%' : 'auto' }} data-generation-language-dropdown>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowGenerationLanguageDropdown(!showGenerationLanguageDropdown)}
                                                    style={{
                                                        padding: '8px 12px',
                                                        borderRadius: '6px',
                                                        border: '1px solid var(--input-border)',
                                                        background: 'var(--input-bg)',
                                                        color: 'var(--input-text)',
                                                        fontSize: '12px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        minWidth: '120px',
                                                        width: screenWidth <= 450 ? '100%' : 'auto'
                                                    }}
                                                >
                                                    <span>🌐</span>
                                                    <span>{languageOptions.find(l => l.code === generationLanguage)?.name || 'English'}</span>
                                                    <span style={{ fontSize: '10px', opacity: 0.7 }}>
                                                        {showGenerationLanguageDropdown ? '▲' : '▼'}
                                                    </span>
                                                </button>
                                                
                                                {showGenerationLanguageDropdown && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '100%',
                                                        left: 0,
                                                        right: 0,
                                                        backgroundColor: 'var(--input-bg)',
                                                        border: '1px solid var(--input-border)',
                                                        borderRadius: '6px',
                                                        marginTop: '2px',
                                                        zIndex: 1000,
                                                        maxHeight: '200px',
                                                        overflowY: 'auto'
                                                    }}>
                                                        {languageOptions.map(lang => (
                                                            <div
                                                                key={lang.code}
                                                                style={{
                                                                    padding: '8px 12px',
                                                                    cursor: 'pointer',
                                                                    borderRadius: '6px',
                                                                    backgroundColor: generationLanguage === lang.code ? 'var(--button-bg)' : 'transparent',
                                                                    color: generationLanguage === lang.code ? 'var(--button-text)' : 'var(--text-color)',
                                                                    fontSize: '12px'
                                                                }}
                                                                onClick={() => {
                                                                    setGenerationLanguage(lang.code);
                                                                    setShowGenerationLanguageDropdown(false);
                                                                }}
                                                            >
                                                                {lang.name}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                                                <button className="main-button" onClick={startStudioGenerate} disabled={studioGenerating} style={{ flex: 1, width: screenWidth <= 450 ? '100%' : 'auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                    {studioGenerating ? (
                                                        <><span className="spinner-border" /> Generating script...</>
                                                    ) : (
                                                        <><SparklesIcon size={16} /> Generate Script</>
                                                    )}
                                                </button>
                                                {studioGenerating && (
                                                    <button 
                                                        className="tertiary-button" 
                                                        onClick={cancelScriptGeneration}
                                                        style={{ 
                                                            padding: '8px 12px',
                                                            minWidth: 'auto',
                                                            flexShrink: 0
                                                        }}
                                                        title="Cancel script generation"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Skip and use your own script button */}
                                        <div style={{ marginTop: '12px' }}>
                                            <div style={{ textAlign: 'center', marginBottom: '10px', fontSize: '12px', color: 'var(--text-secondary)', letterSpacing: '0.04em', fontWeight: 600 }}>
                                                OR
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: screenWidth <= 500 ? 'column' : 'row', gap: '10px'}}>
                                                <button 
                                                    type="button"
                                                    style={{
                                                        flex: 1,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        padding: '16px 14px',
                                                        borderRadius: '12px',
                                                        border: '1px solid var(--input-border)',
                                                        background: 'var(--input-bg)',
                                                        color: 'var(--text-color)',
                                                        cursor: 'pointer',
                                                        textAlign: 'center',
                                                    }}
                                                    onClick={() => {
                                                        setIsNoScriptMode(false);
                                                        setStudioVideoUrl('');
                                                        setStudioVideoSaved(false);
                                                        setNoScriptScriptId(null);
                                                        const customScript = {
                                                            _id: 'custom-' + Date.now(),
                                                            content: '',
                                                            contentAlt: '',
                                                            contentThird: '',
                                                            title: 'Custom Script',
                                                            type: 'voiceover',
                                                            selectedVersion: 'main',
                                                            isCustom: true
                                                        };
                                                        setStudioScripts([customScript]);
                                                        setStudioSelectedScriptId(customScript._id);
                                                    }}
                                                >
                                                    <Edit3 size={22} strokeWidth={1.5} aria-hidden="true" />
                                                    <span style={{ fontWeight: 700, fontSize: '14px' }}>Skip and use your own script</span>
                                                    <span style={{ fontSize: '12px', fontWeight: 400, color: 'var(--text-secondary)' }}>Write or paste your script manually</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                        </>
                                        )}

                                        {studioSubTab === 'text-on-screen' && (
                                            <>
                                                <div style={{
                                                    backgroundColor: 'var(--input-bg)',
                                                    border: '1px solid var(--input-border)',
                                                    borderRadius: '8px',
                                                    padding: '12px',
                                                    marginBottom: '12px',
                                                }}>
                                                    <h4 style={{ marginTop: 0, whiteSpace: screenWidth <= 600 ? 'nowrap' : 'normal' }}>
                                                        1 - Hook
                                                        <span style={{
                                                            marginLeft: '8px',
                                                            fontSize: '12px',
                                                            fontWeight: 'normal',
                                                            background: 'linear-gradient(45deg, #ff6b6b, #ff8e8e)',
                                                            WebkitBackgroundClip: 'text',
                                                            WebkitTextFillColor: 'transparent',
                                                            backgroundClip: 'text',
                                                        }}>@HookDesigner</span>
                                                    </h4>
                                                    <div style={{ marginBottom: '12px' }}>
                                                        <label
                                                            onClick={() => setTextStudioShowHookInput(!textStudioShowHookInput)}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '6px',
                                                                marginBottom: '6px',
                                                                fontSize: '14px',
                                                                cursor: 'pointer',
                                                                userSelect: 'none',
                                                                color: 'var(--text-primary)',
                                                            }}
                                                        >
                                                            <span style={{
                                                                display: 'inline-block',
                                                                transform: textStudioShowHookInput ? 'rotate(90deg)' : 'rotate(0deg)',
                                                                transition: 'transform 0.2s ease',
                                                                fontSize: '10px',
                                                                color: 'var(--text-secondary)',
                                                            }}>▶</span>
                                                            {textStudioShowHookInput ? 'Hide hook' : 'See hook'}
                                                        </label>
                                                        {textStudioShowHookInput && (
                                                            <div style={{
                                                                display: 'flex',
                                                                gap: '8px',
                                                                flexDirection: screenWidth <= 600 ? 'column' : 'row',
                                                                alignItems: screenWidth <= 600 ? 'stretch' : 'center',
                                                            }}>
                                                                <TextareaAutosize
                                                                    value={textStudioHook}
                                                                    onChange={(e) => setTextStudioHook(e.target.value)}
                                                                    placeholder="Hook template for on-screen text..."
                                                                    minRows={2}
                                                                    style={{
                                                                        flex: 1,
                                                                        width: '100%',
                                                                        padding: '8px',
                                                                        borderRadius: '6px',
                                                                        border: '1px solid var(--input-border)',
                                                                        background: 'var(--input-bg)',
                                                                        color: 'var(--input-text)',
                                                                        fontSize: '14px',
                                                                        resize: 'none',
                                                                        boxSizing: 'border-box',
                                                                        fontFamily: 'inherit',
                                                                    }}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={selectRandomTextStudioHook}
                                                                    disabled={availableHooks.length === 0}
                                                                    style={{
                                                                        padding: '8px 12px',
                                                                        fontSize: '12px',
                                                                        backgroundColor: 'var(--button-bg)',
                                                                        color: 'var(--button-text)',
                                                                        border: '1px solid var(--input-border)',
                                                                        borderRadius: '6px',
                                                                        cursor: availableHooks.length > 0 ? 'pointer' : 'not-allowed',
                                                                        opacity: availableHooks.length > 0 ? 1 : 0.5,
                                                                        fontWeight: 'bold',
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    Refresh Hook
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div style={{ marginBottom: '12px' }}>
                                                        <label
                                                            onClick={() => setTextStudioShowTopicInput(!textStudioShowTopicInput)}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '6px',
                                                                marginBottom: '6px',
                                                                fontSize: '14px',
                                                                cursor: 'pointer',
                                                                userSelect: 'none',
                                                                color: 'var(--text-primary)',
                                                            }}
                                                        >
                                                            <span style={{
                                                                display: 'inline-block',
                                                                transform: textStudioShowTopicInput ? 'rotate(90deg)' : 'rotate(0deg)',
                                                                transition: 'transform 0.2s ease',
                                                                fontSize: '10px',
                                                                color: 'var(--text-secondary)',
                                                            }}>▶</span>
                                                            Video theme
                                                        </label>
                                                        {textStudioShowTopicInput && (
                                                            <TextareaAutosize
                                                                value={textStudioTopic}
                                                                onChange={(e) => setTextStudioTopic(e.target.value)}
                                                                placeholder="What is this video about? (optional)"
                                                                minRows={2}
                                                                maxRows={8}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '8px',
                                                                    borderRadius: '6px',
                                                                    border: '1px solid var(--input-border)',
                                                                    background: 'var(--input-bg)',
                                                                    color: 'var(--input-text)',
                                                                    fontSize: '14px',
                                                                    boxSizing: 'border-box',
                                                                    resize: 'none',
                                                                    fontFamily: 'inherit',
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                    <div style={{
                                                        display: 'flex',
                                                        gap: '10px',
                                                        marginTop: '10px',
                                                        alignItems: 'center',
                                                        flexDirection: screenWidth <= 450 ? 'column' : 'row',
                                                    }}>
                                                        <div style={{ position: 'relative', width: screenWidth <= 450 ? '100%' : 'auto' }} data-generation-language-dropdown>
                                                            <button
                                                                type="button"
                                                                onClick={() => setShowGenerationLanguageDropdown(!showGenerationLanguageDropdown)}
                                                                style={{
                                                                    padding: '8px 12px',
                                                                    borderRadius: '6px',
                                                                    border: '1px solid var(--input-border)',
                                                                    background: 'var(--input-bg)',
                                                                    color: 'var(--input-text)',
                                                                    fontSize: '12px',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    minWidth: '120px',
                                                                    width: screenWidth <= 450 ? '100%' : 'auto',
                                                                }}
                                                            >
                                                                <span>🌐</span>
                                                                <span>{languageOptions.find(l => l.code === generationLanguage)?.name || 'English'}</span>
                                                                <span style={{ fontSize: '10px', opacity: 0.7 }}>
                                                                    {showGenerationLanguageDropdown ? '▲' : '▼'}
                                                                </span>
                                                            </button>
                                                            {showGenerationLanguageDropdown && (
                                                                <div style={{
                                                                    position: 'absolute',
                                                                    top: '100%',
                                                                    left: 0,
                                                                    right: 0,
                                                                    backgroundColor: 'var(--input-bg)',
                                                                    border: '1px solid var(--input-border)',
                                                                    borderRadius: '6px',
                                                                    marginTop: '2px',
                                                                    zIndex: 1000,
                                                                    maxHeight: '200px',
                                                                    overflowY: 'auto',
                                                                }}>
                                                                    {languageOptions.map(lang => (
                                                                        <div
                                                                            key={lang.code}
                                                                            style={{
                                                                                padding: '8px 12px',
                                                                                cursor: 'pointer',
                                                                                backgroundColor: generationLanguage === lang.code ? 'var(--button-bg)' : 'transparent',
                                                                                color: generationLanguage === lang.code ? 'var(--button-text)' : 'var(--text-color)',
                                                                                fontSize: '12px',
                                                                            }}
                                                                            onClick={() => {
                                                                                setGenerationLanguage(lang.code);
                                                                                setShowGenerationLanguageDropdown(false);
                                                                            }}
                                                                        >
                                                                            {lang.name}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="main-button"
                                                            onClick={handleTextStudioGenerateOverlay}
                                                            disabled={textStudioGeneratingOverlay || !String(textStudioHook || '').trim()}
                                                            style={{
                                                                flex: 1,
                                                                width: screenWidth <= 450 ? '100%' : 'auto',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: '4px',
                                                                opacity: (textStudioGeneratingOverlay || !String(textStudioHook || '').trim()) ? 0.7 : 1,
                                                            }}
                                                        >
                                                            {textStudioGeneratingOverlay ? (
                                                                <><span className="spinner-border" /> Generating text…</>
                                                            ) : (
                                                                <><SparklesIcon size={16} /> Generate on-screen text</>
                                                            )}
                                                        </button>
                                                        {textStudioGeneratingOverlay && (
                                                            <button
                                                                type="button"
                                                                className="tertiary-button"
                                                                onClick={cancelTextStudioOverlayGeneration}
                                                                style={{
                                                                    padding: '8px 12px',
                                                                    minWidth: 'auto',
                                                                    flexShrink: 0,
                                                                    width: screenWidth <= 450 ? '100%' : 'auto',
                                                                }}
                                                                title="Cancel text generation"
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div style={{ marginTop: '12px' }}>
                                                        <div style={{ textAlign: 'center', marginBottom: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                                                            OR
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                                                            <button
                                                                type="button"
                                                                style={{ flex: 1, justifyContent: 'center', textAlign: 'center', fontWeight: 'bold' }}
                                                                className="tertiary-button"
                                                                onClick={handleTextStudioUseOwnText}
                                                            >
                                                                Use your own text
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                {textStudioContentUnlocked && (
                                                    <>
                                                        <div style={{
                                                            backgroundColor: 'var(--input-bg)',
                                                            border: '1px solid var(--input-border)',
                                                            borderRadius: '8px',
                                                            padding: '12px',
                                                            marginBottom: '12px',
                                                        }}>
                                                            <h4 style={{ marginTop: 0 }}>
                                                                2 - On-screen text
                                                                <span style={{
                                                                    marginLeft: '8px',
                                                                    fontSize: '12px',
                                                                    fontWeight: 'normal',
                                                                    background: 'linear-gradient(45deg, #4ecdc4, #44a08d)',
                                                                    WebkitBackgroundClip: 'text',
                                                                    WebkitTextFillColor: 'transparent',
                                                                    backgroundClip: 'text',
                                                                }}>@ContentWriter</span>
                                                            </h4>
                                                            <TextareaAutosize
                                                                value={textStudioContent}
                                                                onChange={(e) => setTextStudioContent(e.target.value)}
                                                                placeholder="Text shown on your video..."
                                                                minRows={3}
                                                                maxRows={6}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '8px',
                                                                    borderRadius: '6px',
                                                                    border: '1px solid var(--input-border)',
                                                                    background: 'var(--input-bg)',
                                                                    color: 'var(--input-text)',
                                                                    fontSize: '14px',
                                                                    lineHeight: 1.4,
                                                                    fontFamily: SYSTEM_UI_FONT,
                                                                    boxSizing: 'border-box',
                                                                    resize: 'none',
                                                                }}
                                                            />
                                                            {String(textStudioContent || '').trim() && (
                                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '10px' }}>
                                                                    <button
                                                                        type="button"
                                                                        onClick={handleTextStudioGenerateOverlay}
                                                                        disabled={textStudioGeneratingOverlay || !String(textStudioHook || '').trim()}
                                                                        className="main-button"
                                                                        style={{
                                                                            padding: '8px 16px',
                                                                            borderRadius: '10px',
                                                                            fontSize: '14px',
                                                                            fontWeight: 600,
                                                                            opacity: (textStudioGeneratingOverlay || !String(textStudioHook || '').trim()) ? 0.7 : 1,
                                                                            cursor: (textStudioGeneratingOverlay || !String(textStudioHook || '').trim()) ? 'not-allowed' : 'pointer',
                                                                        }}
                                                                    >
                                                                        {textStudioGeneratingOverlay ? (
                                                                            <><span className="spinner-border" style={{ marginRight: '6px', width: '12px', height: '12px' }} />Regenerating…</>
                                                                        ) : 'Re-generate'}
                                                                    </button>
                                                                    {textStudioGeneratingOverlay && (
                                                                        <button
                                                                            type="button"
                                                                            className="tertiary-button"
                                                                            onClick={cancelTextStudioOverlayGeneration}
                                                                            style={{
                                                                                padding: '8px 12px',
                                                                                minWidth: 'auto',
                                                                                flexShrink: 0,
                                                                            }}
                                                                            title="Cancel text generation"
                                                                        >
                                                                            <X size={16} />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div style={{
                                                            backgroundColor: 'var(--input-bg)',
                                                            border: '1px solid var(--input-border)',
                                                            borderRadius: '8px',
                                                            padding: '12px',
                                                            marginBottom: '12px',
                                                        }}>
                                                            <h4 style={{ marginTop: 0 }}>3 - Description</h4>
                                                            <TextareaAutosize
                                                                value={textStudioDescription}
                                                                onChange={(e) => setTextStudioDescription(e.target.value)}
                                                                placeholder="Social caption with hashtags..."
                                                                minRows={2}
                                                                maxRows={5}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '8px',
                                                                    borderRadius: '6px',
                                                                    border: '1px solid var(--input-border)',
                                                                    background: 'var(--input-bg)',
                                                                    color: 'var(--input-text)',
                                                                    fontSize: '13px',
                                                                    fontFamily: 'inherit',
                                                                    boxSizing: 'border-box',
                                                                    resize: 'none',
                                                                    marginBottom: '10px',
                                                                }}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={handleTextStudioGenerateDescription}
                                                                disabled={textStudioGeneratingDescription || !String(textStudioContent || '').trim()}
                                                                style={{
                                                                    padding: '8px 16px',
                                                                    borderRadius: '10px',
                                                                    border: 'none',
                                                                    background: '#D7F06D',
                                                                    color: '#0B0E16',
                                                                    fontSize: '14px',
                                                                    fontWeight: 600,
                                                                    cursor: (textStudioGeneratingDescription || !String(textStudioContent || '').trim()) ? 'not-allowed' : 'pointer',
                                                                    opacity: (textStudioGeneratingDescription || !String(textStudioContent || '').trim()) ? 0.7 : 1,
                                                                }}
                                                            >
                                                                {textStudioGeneratingDescription ? (
                                                                    <><span className="spinner-border" style={{ marginRight: '6px', width: '12px', height: '12px' }} />Generating…</>
                                                                ) : (String(textStudioDescription || '').trim() ? 'Regenerate description' : 'Generate description')}
                                                            </button>
                                                        </div>

                                                        <div style={{
                                                            backgroundColor: 'var(--input-bg)',
                                                            border: '1px solid var(--input-border)',
                                                            borderRadius: '8px',
                                                            padding: '12px',
                                                            marginBottom: '12px',
                                                        }}>
                                                            <h4 style={{ marginTop: 0 }}>4 - Cut</h4>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                                                {textStudioCutsLoading && !textStudioSelectedCut ? (
                                                                    <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Loading cut…</span>
                                                                ) : textStudioSelectedCut ? (
                                                                    <>
                                                                        <div style={{ width: '72px', aspectRatio: '9/16', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--input-border)', flexShrink: 0 }}>
                                                                            <video src={textStudioSelectedCut.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted preload="metadata" />
                                                                        </div>
                                                                        {(() => {
                                                                            const cutList = textStudioCuts.length > 0 ? textStudioCuts : [textStudioSelectedCut];
                                                                            const cutIdx = Math.max(0, cutList.findIndex(c => String(c._id || c.id) === String(textStudioSelectedCut._id || textStudioSelectedCut.id)));
                                                                            const dur = typeof textStudioSelectedCut.duration === 'number' ? textStudioSelectedCut.duration : null;
                                                                            return (
                                                                                <div style={{ flex: 1, minWidth: 120 }}>
                                                                                    <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-color)', marginBottom: 2 }}>
                                                                                        {textStudioSelectedCut.name || 'Cut'}
                                                                                    </div>
                                                                                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: 2 }}>
                                                                                        {textStudioSelectedCut.description || (textStudioSelectedCut.autoGenerated ? 'Auto-generated cut' : 'Selected cut')}
                                                                                    </div>
                                                                                    <div style={{ fontSize: '12px', color: '#958DF1' }}>
                                                                                        {cutIdx + 1}/{cutList.length}
                                                                                        {dur != null ? ` • ${dur.toFixed(2)}s` : ''}
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })()}
                                                                    </>
                                                                ) : (
                                                                    <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>No cut available</span>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={async () => {
                                                                        const cuts = textStudioCuts.length > 0 ? textStudioCuts : await ensureTextStudioCutsLoaded();
                                                                        if (cuts.length > 0) pickRandomTextStudioCut(cuts);
                                                                    }}
                                                                    disabled={textStudioCutsLoading}
                                                                    style={{
                                                                        padding: '8px 14px',
                                                                        borderRadius: '8px',
                                                                        border: '1px solid var(--input-border)',
                                                                        background: 'var(--input-bg)',
                                                                        color: 'var(--text-color)',
                                                                        fontSize: '14px',
                                                                        cursor: textStudioCutsLoading ? 'not-allowed' : 'pointer',
                                                                        opacity: textStudioCutsLoading ? 0.7 : 1,
                                                                    }}
                                                                >
                                                                    Random
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={openTextStudioCutModal}
                                                                    style={{
                                                                        padding: '8px 14px',
                                                                        borderRadius: '8px',
                                                                        border: '1px solid var(--input-border)',
                                                                        background: 'var(--input-bg)',
                                                                        color: 'var(--text-color)',
                                                                        fontSize: '14px',
                                                                        cursor: 'pointer',
                                                                    }}
                                                                >
                                                                    Change cut
                                                                </button>
                                                            </div>
                                                        </div>

                                                        <div style={{
                                                            backgroundColor: 'var(--input-bg)',
                                                            border: '1px solid var(--input-border)',
                                                            borderRadius: '8px',
                                                            padding: '12px',
                                                            marginBottom: '12px',
                                                        }}>
                                                            <h4 style={{ marginTop: 0, marginBottom: '12px' }}>5 - Text style & music</h4>
                                                            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px' }}>On-screen font</label>
                                                            <div style={{ position: 'relative', marginBottom: '16px' }} data-font-dropdown>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setShowFontDropdown(!showFontDropdown)}
                                                                    style={{
                                                                        width: '100%',
                                                                        padding: '8px 12px',
                                                                        borderRadius: '6px',
                                                                        border: '1px solid var(--input-border)',
                                                                        background: 'var(--input-bg)',
                                                                        color: 'var(--input-text)',
                                                                        fontSize: '14px',
                                                                        cursor: 'pointer',
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center',
                                                                        textAlign: 'left',
                                                                    }}
                                                                >
                                                                    <span style={{
                                                                        fontFamily: selectedFontId ?
                                                                            fontFamilyCssForName(userFonts.find(f => f._id === selectedFontId)?.name)
                                                                            : defaultFontFamilyCss,
                                                                    }}>
                                                                        {selectedFontId ?
                                                                            userFonts.find(f => f._id === selectedFontId)?.name || 'Select font...'
                                                                            : 'Default (TikTok medium)'}
                                                                    </span>
                                                                    <span style={{ fontSize: '12px', opacity: 0.7 }}>
                                                                        {showFontDropdown ? '▲' : '▼'}
                                                                    </span>
                                                                </button>
                                                                {showFontDropdown && (
                                                                    <div style={{
                                                                        position: 'absolute',
                                                                        top: '100%',
                                                                        left: 10,
                                                                        right: 10,
                                                                        backgroundColor: 'var(--input-bg)',
                                                                        border: '1px solid var(--input-border)',
                                                                        borderRadius: '6px',
                                                                        marginTop: '2px',
                                                                        zIndex: 1000,
                                                                        maxHeight: '200px',
                                                                        overflowY: 'auto',
                                                                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.5)',
                                                                    }}>
                                                                        <div
                                                                            style={{
                                                                                padding: '8px 12px',
                                                                                cursor: 'pointer',
                                                                                borderRadius: '6px',
                                                                                backgroundColor: selectedFontId === '' ? 'var(--button-bg)' : 'transparent',
                                                                                color: selectedFontId === '' ? 'var(--button-text)' : 'var(--text-color)',
                                                                            }}
                                                                            onClick={() => {
                                                                                handleFontChange('');
                                                                                setShowFontDropdown(false);
                                                                            }}
                                                                        >
                                                                            <span style={{ fontSize: '14px', fontFamily: defaultFontFamilyCss }}>Default (TikTok medium)</span>
                                                                        </div>
                                                                        {sortedUserFonts.map(font => (
                                                                            <div
                                                                                key={font._id}
                                                                                style={{
                                                                                    padding: '8px 12px',
                                                                                    cursor: 'pointer',
                                                                                    borderRadius: '6px',
                                                                                    backgroundColor: selectedFontId === font._id ? 'var(--button-bg)' : 'transparent',
                                                                                    color: selectedFontId === font._id ? 'var(--button-text)' : 'var(--text-color)',
                                                                                }}
                                                                                onClick={() => {
                                                                                    handleFontChange(font._id);
                                                                                    setShowFontDropdown(false);
                                                                                }}
                                                                            >
                                                                                <div style={{
                                                                                    fontSize: '14px',
                                                                                    fontFamily: fontFamilyCssForName(font.name),
                                                                                    fontWeight: '500',
                                                                                }}>
                                                                                    {font.name}
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {renderStudioSubtitleColorPickers('text')}
                                                            <div style={{ marginBottom: '12px' }}>
                                                                <label style={{ fontSize: '14px', marginBottom: '8px', display: 'block', color: 'var(--text-color)' }}>
                                                                    Text size:
                                                                </label>
                                                                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                                        <input
                                                                            type="radio"
                                                                            name="text-studio-text-size"
                                                                            value="small"
                                                                            checked={!bigText}
                                                                            onChange={() => {
                                                                                setBigText(false);
                                                                                localStorage.setItem('workspace_big_text', 'false');
                                                                            }}
                                                                            style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                                        />
                                                                        <span>Small</span>
                                                                    </label>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                                        <input
                                                                            type="radio"
                                                                            name="text-studio-text-size"
                                                                            value="large"
                                                                            checked={bigText}
                                                                            onChange={() => {
                                                                                setBigText(true);
                                                                                localStorage.setItem('workspace_big_text', 'true');
                                                                            }}
                                                                            style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                                        />
                                                                        <span>Large</span>
                                                                    </label>
                                                                </div>
                                                            </div>
                                                            <div style={{ marginBottom: '12px' }}>
                                                                <label style={{ fontSize: '14px', marginBottom: '8px', display: 'block', color: 'var(--text-color)' }}>
                                                                    Text position:
                                                                </label>
                                                                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                                        <input
                                                                            type="radio"
                                                                            name="text-studio-text-position"
                                                                            value="center"
                                                                            checked={subtitlePosition === 'center'}
                                                                            onChange={() => {
                                                                                setSubtitlePosition('center');
                                                                                localStorage.setItem('workspace_subtitle_position', 'center');
                                                                            }}
                                                                            style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                                        />
                                                                        <span>Center</span>
                                                                    </label>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                                        <input
                                                                            type="radio"
                                                                            name="text-studio-text-position"
                                                                            value="bottom"
                                                                            checked={subtitlePosition === 'bottom'}
                                                                            onChange={() => {
                                                                                setSubtitlePosition('bottom');
                                                                                localStorage.setItem('workspace_subtitle_position', 'bottom');
                                                                            }}
                                                                            style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                                        />
                                                                        <span>Bottom</span>
                                                                    </label>
                                                                </div>
                                                            </div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    id="textStudioUseMusic"
                                                                    checked={useMusic}
                                                                    onChange={(e) => {
                                                                        const newValue = e.target.checked;
                                                                        setUseMusic(newValue);
                                                                        localStorage.setItem('workspace_use_music', newValue.toString());
                                                                    }}
                                                                    style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                                />
                                                                <label htmlFor="textStudioUseMusic" style={{ fontSize: '14px', cursor: 'pointer' }}>
                                                                    Use background music
                                                                </label>
                                                            </div>
                                                            {useMusic && (
                                                                <div style={{ position: 'relative' }} data-music-dropdown>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setShowMusicDropdown(!showMusicDropdown)}
                                                                        style={{
                                                                            width: '100%',
                                                                            padding: '8px 12px',
                                                                            borderRadius: '6px',
                                                                            border: '1px solid var(--input-border)',
                                                                            background: 'var(--input-bg)',
                                                                            color: 'var(--input-text)',
                                                                            fontSize: '14px',
                                                                            cursor: 'pointer',
                                                                            display: 'flex',
                                                                            justifyContent: 'space-between',
                                                                            alignItems: 'center',
                                                                            textAlign: 'left',
                                                                        }}
                                                                    >
                                                                        <span>
                                                                            {selectedMusicId ?
                                                                                availableMusic.find(m => m._id === selectedMusicId)?.name || 'Select music...'
                                                                                : lastUsedMusicName ?
                                                                                    `${lastUsedMusicName}`
                                                                                    : 'Random music'}
                                                                        </span>
                                                                        <span style={{ fontSize: '12px', opacity: 0.7 }}>
                                                                            {showMusicDropdown ? '▲' : '▼'}
                                                                        </span>
                                                                    </button>
                                                                    {showMusicDropdown && (
                                                                        <div style={{
                                                                            position: 'absolute',
                                                                            top: '100%',
                                                                            left: 10,
                                                                            right: 10,
                                                                            backgroundColor: 'var(--input-bg)',
                                                                            border: '1px solid var(--input-border)',
                                                                            borderRadius: '6px',
                                                                            marginTop: '2px',
                                                                            zIndex: 1000,
                                                                            maxHeight: '200px',
                                                                            overflowY: 'auto',
                                                                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.5)',
                                                                        }}>
                                                                            <div
                                                                                style={{
                                                                                    padding: '8px 12px',
                                                                                    cursor: 'pointer',
                                                                                    display: 'flex',
                                                                                    justifyContent: 'space-between',
                                                                                    alignItems: 'center',
                                                                                    borderBottom: '1px solid var(--input-border)',
                                                                                    backgroundColor: selectedMusicId === '' ? 'var(--button-bg)' : 'transparent',
                                                                                    color: selectedMusicId === '' ? 'var(--button-text)' : 'var(--text-color)',
                                                                                }}
                                                                                onClick={() => {
                                                                                    handleMusicChange('');
                                                                                    setShowMusicDropdown(false);
                                                                                }}
                                                                            >
                                                                                <span style={{ fontSize: '14px' }}>Random music</span>
                                                                            </div>
                                                                            {[...availableMusic].sort((a, b) => {
                                                                                if (a.isStarred && !b.isStarred) return -1;
                                                                                if (!a.isStarred && b.isStarred) return 1;
                                                                                return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
                                                                            }).map(music => (
                                                                                <div
                                                                                    key={music._id}
                                                                                    style={{
                                                                                        padding: '8px 12px',
                                                                                        cursor: 'pointer',
                                                                                        display: 'flex',
                                                                                        justifyContent: 'space-between',
                                                                                        alignItems: 'center',
                                                                                        borderBottom: '1px solid var(--input-border)',
                                                                                        backgroundColor: selectedMusicId === music._id ? 'var(--button-bg)' : 'transparent',
                                                                                        color: selectedMusicId === music._id ? 'var(--button-text)' : 'var(--text-color)',
                                                                                    }}
                                                                                    onClick={() => {
                                                                                        handleMusicChange(music._id);
                                                                                        setShowMusicDropdown(false);
                                                                                    }}
                                                                                >
                                                                                    <span style={{ fontSize: '14px', flex: 1 }}>{music.name}</span>
                                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={(e) => handleToggleMusicStar(music._id, e)}
                                                                                            style={{
                                                                                                background: 'none',
                                                                                                border: 'none',
                                                                                                padding: '2px',
                                                                                                cursor: 'pointer',
                                                                                                display: 'flex',
                                                                                                alignItems: 'center',
                                                                                                justifyContent: 'center',
                                                                                                color: music.isStarred ? '#ffd700' : 'var(--text-secondary)',
                                                                                                opacity: music.isStarred ? 1 : 0.5,
                                                                                                transition: 'all 0.2s',
                                                                                            }}
                                                                                            onMouseOver={(e) => {
                                                                                                if (!music.isStarred) {
                                                                                                    e.currentTarget.style.opacity = '1';
                                                                                                    e.currentTarget.style.color = '#ffd700';
                                                                                                }
                                                                                            }}
                                                                                            onMouseOut={(e) => {
                                                                                                if (!music.isStarred) {
                                                                                                    e.currentTarget.style.opacity = '0.5';
                                                                                                    e.currentTarget.style.color = 'var(--text-secondary)';
                                                                                                }
                                                                                            }}
                                                                                        >
                                                                                            <Star size={18} fill={music.isStarred ? '#ffd700' : 'none'} />
                                                                                        </button>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                handleMusicPreview(music._id, music.url);
                                                                                            }}
                                                                                            style={{
                                                                                                padding: '4px 6px',
                                                                                                backgroundColor: playingMusicId === music._id ? '#ff4444' : 'var(--button-bg)',
                                                                                                color: 'white',
                                                                                                border: '1px solid var(--input-border)',
                                                                                                borderRadius: '4px',
                                                                                                cursor: 'pointer',
                                                                                                fontSize: '12px',
                                                                                                display: 'flex',
                                                                                                alignItems: 'center',
                                                                                                gap: '4px',
                                                                                                height: '22px',
                                                                                                width: '22px',
                                                                                            }}
                                                                                        >
                                                                                            {playingMusicId === music._id ? '⏹' : '▶'}
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div style={{
                                                            backgroundColor: 'var(--input-bg)',
                                                            border: '1px solid var(--input-border)',
                                                            borderRadius: '8px',
                                                            padding: '12px',
                                                        }}>
                                                            <h4 style={{ marginTop: 0 }}>6 - Generate video</h4>
                                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%', marginBottom: textStudioGeneratingVideo || textStudioVideoUrl ? '12px' : 0 }}>
                                                                <button type="button" className="main-button" onClick={handleTextStudioGenerateVideo} disabled={textStudioGeneratingVideo || !String(textStudioContent || '').trim()} style={{ flex: 1, padding: '12px', borderRadius: '10px', fontWeight: 700, opacity: (textStudioGeneratingVideo || !String(textStudioContent || '').trim()) ? 0.7 : 1 }}>
                                                                    {textStudioGeneratingVideo ? <>Generating video… {textStudioVideoProgress > 0 ? `${textStudioVideoProgress}%` : ''}</> : (textStudioVideoUrl ? 'Regenerate video' : 'Generate video')}
                                                                </button>
                                                                {textStudioGeneratingVideo && (
                                                                    <button
                                                                        type="button"
                                                                        className="tertiary-button"
                                                                        onClick={cancelTextStudioVideoGeneration}
                                                                        style={{ padding: '8px 12px', minWidth: 'auto', flexShrink: 0 }}
                                                                        title="Cancel video generation"
                                                                    >
                                                                        <X size={16} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {textStudioGeneratingVideo && (
                                                                <div style={{ marginBottom: '12px' }}>
                                                                    <VideoGenerationSteps
                                                                        compact
                                                                        progress={textStudioVideoProgress}
                                                                        perStepProgress
                                                                        showProgressBar
                                                                        shimmer
                                                                        textOnScreen
                                                                    />
                                                                </div>
                                                            )}
                                                            {textStudioVideoUrl && !textStudioGeneratingVideo && (
                                                                <>
                                                                    <div style={{ marginTop: '12px', marginBottom: '12px' }}>
                                                                        <div style={{ maxHeight: '500px', display: 'flex', justifyContent: 'center' }}>
                                                                            <video
                                                                                src={textStudioVideoUrl}
                                                                                controls
                                                                                preload="metadata"
                                                                                playsInline
                                                                                onContextMenu={(e) => e.preventDefault()}
                                                                                style={{ maxHeight: '500px', alignSelf: 'center', borderRadius: '6px' }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        className="main-button"
                                                                        onClick={handleTextStudioDownloadAndSave}
                                                                        disabled={textStudioPublishingVideo}
                                                                        style={{
                                                                            width: '100%',
                                                                            padding: '8px 12px',
                                                                            borderRadius: '10px',
                                                                            border: '1px solid var(--input-border)',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            cursor: textStudioPublishingVideo ? 'not-allowed' : 'pointer',
                                                                            opacity: textStudioPublishingVideo ? 0.7 : 1,
                                                                        }}
                                                                    >
                                                                        {textStudioPublishingVideo ? (
                                                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                                                                <span className="spinner-border" />
                                                                                Saving…
                                                                            </span>
                                                                        ) : textStudioVideoSaved ? (
                                                                            'Saved!'
                                                                        ) : (
                                                                            'Download & Save'
                                                                        )}
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        )}

                                        {studioSubTab === 'carousel' && (
                                            <StudioCarousel
                                                workspaceId={currentWorkspace?._id}
                                                darkMode={darkMode}
                                                screenWidth={screenWidth}
                                                availableHooks={availableHooks}
                                                userFonts={userFonts}
                                                defaultFontFamilyCss={defaultFontFamilyCss}
                                                fontFamilyCssForName={fontFamilyCssForName}
                                                generationLanguage={generationLanguage}
                                                setGenerationLanguage={setGenerationLanguage}
                                                languageOptions={languageOptions}
                                                showGenerationLanguageDropdown={showGenerationLanguageDropdown}
                                                setShowGenerationLanguageDropdown={setShowGenerationLanguageDropdown}
                                                loadCuts={loadWorkspaceCutsForPicker}
                                                onError={(msg) => setErrors((prev) => [...prev, msg])}
                                            />
                                        )}
                                </div>
                                )}

                                {(studioSubTab === 'vlog' || studioSubTab === 'visual-only') && (
                                    <>
                                        {(<div>
                                            <div>
                                                {studioGenerating && (
                                                <div style={{ margin: '20px' }}>
                                                    <div style={{ 
                                                        width: '100%', 
                                                        height: '6px', 
                                                        background: 'var(--input-border)', 
                                                        borderRadius: '3px', 
                                                        overflow: 'hidden',
                                                        position: 'relative'
                                                    }}>
                                                        <div 
                                                            className="progress-bar-shimmer-script"
                                                            style={{ 
                                                                width: `${studioProgress.percent+5}%`, 
                                                                height: '6px', 
                                                                transition: 'width 0.3s ease',
                                                                position: 'relative',
                                                                ['--shimmer-duration']: `${Math.max(360, Math.round(1500 * (1 - Math.min(100, studioProgress.percent + 5) / 100)))}ms`
                                                            }} 
                                                        />
                                                    </div>
                                                    <div style={{ 
                                                        fontSize: '12px', 
                                                        opacity: 0.8, 
                                                        marginTop: '6px', 
                                                        textAlign: 'center'
                                                    }}>
                                                        {studioProgress.currentStep || 'Generating script...'} {Math.round(studioProgress.percent+5)}%
                                                    </div>
                                                </div>
                                            )}
                                            </div>

                                            {studioScripts.length > 0 && (
                                                <div style={{
                                                    backgroundColor: 'var(--input-bg)',
                                                    border: '1px solid var(--input-border)',
                                                    borderRadius: '8px', padding: '12px',
                                                    marginBottom: '12px',
                                                    marginTop: '12px'
                                                }}>
                                                    <h4 style={{ marginTop: 0 }}>
                                                        2 - Your Generated Script
                                                        <span style={{ 
                                                            marginLeft: '8px', 
                                                            fontSize: '12px', 
                                                            fontWeight: 'normal',
                                                            background: 'linear-gradient(45deg, #4ecdc4, #44a08d)',
                                                            WebkitBackgroundClip: 'text',
                                                            WebkitTextFillColor: 'transparent',
                                                            backgroundClip: 'text'
                                                        }}>@ContentWriter</span>
                                                    </h4>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                        {studioScripts.slice(0, 1).map(scr => (
                                                            <div key={scr._id}
                                                                style={{
                                                                    // border: '1px solid var(--input-border)',
                                                                    // borderRadius: '8px',
                                                                    // padding: '8px'
                                                                }}
                                                            >
                                                                <div style={{ 
                                                                    display: 'flex', 
                                                                    justifyContent: 'flex-end', 
                                                                    marginBottom: '6px', 
                                                                    gap: '8px',
                                                                    flexDirection: window.innerWidth <= 600 ? 'column' : 'row',
                                                                    alignItems: window.innerWidth <= 600 ? 'center' : 'center'
                                                                }}>
                                                                    {/* Regenerate Button */}
                                                                    <button 
                                                                        className="main-button" 
                                                                        onClick={() => studioRegenerateScript(scr._id)} 
                                                                        disabled={studioRegeneratingScript}
                                                                        style={{ 
                                                                            padding: '4px 8px', 
                                                                            borderRadius: '4px',
                                                                            opacity: studioRegeneratingScript ? 0.6 : 1,
                                                                            cursor: studioRegeneratingScript ? 'not-allowed' : 'pointer',
                                                                            width: window.innerWidth <= 600 ? '100%' : 'auto'
                                                                        }}
                                                                    > 
                                                                        {studioRegeneratingScript ? <><span className="spinner-border" style={{marginRight:'5px'}}/> Regenerating...</> : 'Regenerate'}
                                                                    </button>

                                                                    <div style={{ textAlign: 'center', fontSize: '14px', color: 'var(--text-secondary)' }}>
                                                                        OR
                                                                    </div>
                                                                    
                                                                    {/* Translate Script Button - Shows/hides translation controls */}
                                                                    {!showTranslateControls ? (
                                                                        <button 
                                                                            onClick={() => setShowTranslateControls(true)}
                                                                            style={{ 
                                                                                padding: '4px 8px', 
                                                                                borderRadius: '4px',
                                                                                backgroundColor: 'var(--button-bg)',
                                                                                color: 'var(--button-text)',
                                                                                border: '1px solid var(--input-border)',
                                                                                cursor: 'pointer',
                                                                                fontWeight: 'bold',
                                                                                width: window.innerWidth <= 600 ? '100%' : 'auto'
                                                                            }}
                                                                        > 
                                                                            Translate script
                                                                        </button>
                                                                    ) : (
                                                                        /* Translate Button and Language Picker Container */
                                                                        <div style={{ 
                                                                            display: 'flex', 
                                                                            gap: '8px', 
                                                                            alignItems: 'center',
                                                                            flexDirection: window.innerWidth <= 600 ? 'row' : 'row',
                                                                            width: window.innerWidth <= 600 ? '100%' : 'auto'
                                                                        }}>
                                                                            {/* Translate Button */}
                                                                            <button 
                                                                                onClick={() => translateScript(scr._id)} 
                                                                                disabled={isTranslating}
                                                                                style={{ 
                                                                                    padding: '4px 8px', 
                                                                                    borderRadius: '4px',
                                                                                    backgroundColor: 'var(--button-bg)',
                                                                                    color: 'var(--button-text)',
                                                                                    border: '1px solid var(--input-border)',
                                                                                    opacity: isTranslating ? 0.6 : 1,
                                                                                    cursor: isTranslating ? 'not-allowed' : 'pointer',
                                                                                    fontWeight: 'bold',
                                                                                }}
                                                                            > 
                                                                                {isTranslating ? <><span className="spinner-border" style={{marginRight:'5px'}}/> Translating...</> : 'Translate'}
                                                                            </button>

                                                                            {/* Translation Language Picker */}
                                                                            <div style={{ position: 'relative' }} data-translation-language-dropdown>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => setShowTranslationLanguageDropdown(!showTranslationLanguageDropdown)}
                                                                                    style={{
                                                                                        padding: '4px 8px',
                                                                                        borderRadius: '4px',
                                                                                        border: '1px solid var(--input-border)',
                                                                                        background: 'var(--input-bg)',
                                                                                        color: 'var(--input-text)',
                                                                                        cursor: 'pointer',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        gap: '2px',
                                                                                        minWidth: '80px'
                                                                                    }}
                                                                                >
                                                                                    <span>🌐</span>
                                                                                    <span>{languageOptions.find(l => l.code === translationLanguage)?.name || 'Spanish'}</span>
                                                                                    <span style={{ fontSize: '8px', opacity: 0.7 }}>
                                                                                        {showTranslationLanguageDropdown ? '▲' : '▼'}
                                                                                    </span>
                                                                                </button>
                                                                                
                                                                                {showTranslationLanguageDropdown && (
                                                                                    <div style={{
                                                                                        position: 'absolute',
                                                                                        top: '100%',
                                                                                        right: 0,
                                                                                        backgroundColor: 'var(--input-bg)',
                                                                                        border: '1px solid var(--input-border)',
                                                                                        borderRadius: '6px',
                                                                                        marginTop: '2px',
                                                                                        zIndex: 1000,
                                                                                        maxHeight: '200px',
                                                                                        overflowY: 'auto',
                                                                                        minWidth: '120px'
                                                                                    }}>
                                                                                        {languageOptions.map(lang => (
                                                                                            <div
                                                                                                key={lang.code}
                                                                                                style={{
                                                                                                    padding: '6px 8px',
                                                                                                    cursor: 'pointer',
                                                                                                    borderRadius: '4px',
                                                                                                    backgroundColor: translationLanguage === lang.code ? 'var(--button-bg)' : 'transparent',
                                                                                                    color: translationLanguage === lang.code ? 'var(--button-text)' : 'var(--text-color)',
                                                                                                    fontSize: '11px'
                                                                                                }}
                                                                                                onClick={() => {
                                                                                                    setTranslationLanguage(lang.code);
                                                                                                    setShowTranslationLanguageDropdown(false);
                                                                                                }}
                                                                                            >
                                                                                                {lang.name}
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                
                                                                {/* Script Content - Show both versions if contentAlt exists */}
                                                                {studioRegeneratingScript ? (
                                                                    <div style={{ 
                                                                        display: 'flex', 
                                                                        alignItems: 'center', 
                                                                        justifyContent: 'center', 
                                                                        padding: '20px',
                                                                        background: 'var(--input-bg)',
                                                                        borderRadius: '8px',
                                                                        border: '1px solid var(--input-border)',
                                                                        flexDirection: 'column',
                                                                        gap: '8px'
                                                                    }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                            <span className="spinner-border" style={{marginRight:'10px'}}/>
                                                                            <span style={{ color: 'var(--text-color)', fontSize: '14px' }}>Regenerating script...</span>
                                                                        </div>
                                                                        <div style={{ 
                                                                            fontSize: '12px', 
                                                                            opacity: 0.8, 
                                                                            textAlign: 'center',
                                                                            position: 'relative',
                                                                            height: '20px',
                                                                            width: '100%'
                                                                        }}>
                                                                            <span
                                                                                key={currentStudioExample || 'placeholder'}
                                                                                style={{
                                                                                    fontStyle: 'italic',
                                                                                    position: 'absolute',
                                                                                    left: '0',
                                                                                    right: '0'
                                                                                }}
                                                                                className={isLastStudioExample ? "studio-example-text-final" : "studio-example-text"}
                                                                            >
                                                                                {currentStudioExample}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                ) : scr.type === 'voiceover' && !scr.isCustom && (scr.contentAlt || scr.contentThird) ? ( 
                                                                    <div style={{ position: 'relative' }}>
                                                                        {/* Translation Overlay */}
                                                                        {isTranslating && (
                                                                            <div style={{
                                                                                position: 'absolute',
                                                                                top: 0,
                                                                                left: 0,
                                                                                right: 0,
                                                                                bottom: 0,
                                                                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                zIndex: 1000,
                                                                                borderRadius: '8px'
                                                                            }}>
                                                                                <div style={{
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: '10px',
                                                                                    color: 'white',
                                                                                    fontSize: '14px'
                                                                                }}>
                                                                                    <span className="spinner-border" />
                                                                                    Translating...
                                                                                </div>
                                                                            </div>
                                                                        )}

                                                                        {(() => {
                                                                            const versions = [
                                                                                { key: 'main', label: 'A' },
                                                                                ...(scr.contentAlt ? [{ key: 'alt', label: 'B' }] : []),
                                                                                ...(scr.contentThird ? [{ key: 'third', label: 'C' }] : []),
                                                                            ];
                                                                            const selected = scr.selectedVersion || 'main';
                                                                            const selectedIdx = Math.max(0, versions.findIndex(v => v.key === selected));
                                                                            const canGoPrev = selectedIdx > 0;
                                                                            const canGoNext = selectedIdx < versions.length - 1;

                                                                            const go = (delta) => {
                                                                                const next = versions[Math.max(0, Math.min(versions.length - 1, selectedIdx + delta))]?.key;
                                                                                if (!next || next === selected) return;
                                                                                handleStudioScriptVersionChange(scr._id, next);
                                                                            };

                                                                            return (
                                                                                <>
                                                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '10px' }}>
                                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => go(-1)}
                                                                                                disabled={!canGoPrev}
                                                                                                style={{
                                                                                                    width: '34px',
                                                                                                    height: '30px',
                                                                                                    borderRadius: '8px',
                                                                                                    border: '1px solid var(--input-border)',
                                                                                                    background: 'var(--input-bg)',
                                                                                                    color: 'var(--text-color)',
                                                                                                    cursor: canGoPrev ? 'pointer' : 'not-allowed',
                                                                                                    opacity: canGoPrev ? 1 : 0.45,
                                                                                                }}
                                                                                                title="Previous version"
                                                                                            >
                                                                                                ←
                                                                                            </button>
                                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '3px', borderRadius: '10px', border: '1px solid var(--input-border)', background: 'var(--input-bg)' }}>
                                                                                                {versions.map((v) => {
                                                                                                    const isSelected = (scr.selectedVersion || 'main') === v.key;
                                                                                                    return (
                                                                                                        <button
                                                                                                            key={v.key}
                                                                                                            type="button"
                                                                                                            onClick={() => handleStudioScriptVersionChange(scr._id, v.key)}
                                                                                                            style={{
                                                                                                                padding: '6px 10px',
                                                                                                                borderRadius: '8px',
                                                                                                                border: isSelected ? '1px solid #8b5cf6' : '1px solid transparent',
                                                                                                                background: isSelected ? 'rgba(139,92,246,0.18)' : 'transparent',
                                                                                                                color: 'var(--text-color)',
                                                                                                                fontSize: '12px',
                                                                                                                fontWeight: 600,
                                                                                                                cursor: 'pointer',
                                                                                                            }}
                                                                                                            title={`Version ${v.label}`}
                                                                                                        >
                                                                                                            Version {v.label}
                                                                                                        </button>
                                                                                                    );
                                                                                                })}
                                                                                            </div>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => go(1)}
                                                                                                disabled={!canGoNext}
                                                                                                style={{
                                                                                                    width: '34px',
                                                                                                    height: '30px',
                                                                                                    borderRadius: '8px',
                                                                                                    border: '1px solid var(--input-border)',
                                                                                                    background: 'var(--input-bg)',
                                                                                                    color: 'var(--text-color)',
                                                                                                    cursor: canGoNext ? 'pointer' : 'not-allowed',
                                                                                                    opacity: canGoNext ? 1 : 0.45,
                                                                                                }}
                                                                                                title="Next version"
                                                                                            >
                                                                                                →
                                                                                            </button>
                                                                                        </div>
                                                                                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                                                                                            {selectedIdx + 1}/{versions.length}
                                                                                        </div>
                                                                                    </div>

                                                                                    <TextareaAutosize
                                                                                        value={getScriptContent(scr, scr.selectedVersion || 'main')}
                                                                                        onChange={(e) => handleStudioScriptContentChange(scr._id, scr.selectedVersion || 'main', e.target.value)}
                                                                                        minRows={4}
                                                                                        maxRows={12}
                                                                                        style={{
                                                                                            width: '100%',
                                                                                            padding: '8px',
                                                                                            borderRadius: '8px',
                                                                                            border: '2px solid #8b5cf6',
                                                                                            background: 'transparent',
                                                                                            color: 'var(--input-text)',
                                                                                            fontSize: '12px',
                                                                                            lineHeight: 1.4,
                                                                                            boxSizing: 'border-box',
                                                                                            fontFamily: SYSTEM_UI_FONT,
                                                                                        }}
                                                                                        placeholder="Edit script content..."
                                                                                    />
                                                                                </>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                ) : (
                                                                    <TextareaAutosize
                                                                        ref={scr.isCustom ? customScriptTextareaRef : null}
                                                                        value={getScriptContent(scr, 'main')}
                                                                        onChange={(e) => handleStudioScriptContentChange(scr._id, 'main', e.target.value)}
                                                                        minRows={3}
                                                                        maxRows={14}
                                                                        style={{
                                                                            width: '100%',
                                                                            padding: '6px 8px',
                                                                            borderRadius: '6px',
                                                                            border: '1px solid var(--input-border)',
                                                                            background: 'transparent',
                                                                            color: 'var(--text-color)',
                                                                            fontSize: '12px',
                                                                            lineHeight: 1.4,
                                                                            boxSizing: 'border-box',
                                                                            fontFamily: SYSTEM_UI_FONT,
                                                                        }}
                                                                        placeholder="Write your script..."
                                                                    />
                                                                )}
                                                                {getScriptContent(scr, 'main').length > 1500 && (
                                                                    <div style={{
                                                                        marginTop: '8px',
                                                                        padding: '8px',
                                                                        backgroundColor: '#fff3cd',
                                                                        border: '1px solid #ffeaa7',
                                                                        borderRadius: '4px',
                                                                        fontSize: '12px',
                                                                        color: '#856404'
                                                                    }}>
                                                                        ℹ️ Long scripts will make it harder to get a good voiceover
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>)}

                                {/* No-Script Mode UI */}
                                {isNoScriptMode && (
                                    <div style={{
                                        backgroundColor: 'var(--input-bg)',
                                        border: '1px solid var(--input-border)',
                                        borderRadius: '8px', 
                                        padding: '12px', 
                                        marginBottom: '12px'
                                    }}>
                                        <div style={{ marginBottom: '12px' }}>
                                            {screenWidth <= 600 && (
                                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            // Track back to script mode
                                                            trackEvent('back_to_script_mode', {
                                                                workspaceId: currentWorkspace?._id
                                                            });
                                                            
                                                            exitVisualOnlyMode('vlog');
                                                        }}
                                                        style={{
                                                            padding: '4px 8px',
                                                            borderRadius: '4px',
                                                            border: '1px solid var(--input-border)',
                                                            background: 'var(--button-bg)',
                                                            color: 'var(--button-text)',
                                                            fontSize: '12px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        Back to Script Mode
                                                    </button>
                                                </div>
                                            )}
                                            <div style={{ 
                                                display: 'flex', 
                                                justifyContent: screenWidth <= 600 ? 'flex-start' : 'space-between', 
                                                alignItems: 'center' 
                                            }}>
                                                <h4 style={{ marginTop: 0, marginBottom: 0 }}>
                                                    2 - Video Settings
                                                    <span style={{ 
                                                        marginLeft: '8px', 
                                                        fontSize: '12px', 
                                                        fontWeight: 'normal',
                                                        background: 'linear-gradient(45deg, #4ecdc4, #44a08d)',
                                                        WebkitBackgroundClip: 'text',
                                                        WebkitTextFillColor: 'transparent',
                                                        backgroundClip: 'text'
                                                    }}>@ProductionTeam</span>
                                                </h4>
                                                {screenWidth > 600 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            exitVisualOnlyMode('vlog');
                                                        }}
                                                        style={{
                                                            padding: '4px 8px',
                                                            borderRadius: '4px',
                                                            border: '1px solid var(--input-border)',
                                                            background: 'var(--button-bg)',
                                                            color: 'var(--button-text)',
                                                            fontSize: '12px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        Back to Script Mode
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Source videos */}
                                        <div style={{ marginBottom: '12px' }}>
                                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>Source videos</label>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                                                {[
                                                    { mode: 'all', label: 'All videos' },
                                                    { mode: 'select', label: 'Select' },
                                                    { mode: 'import', label: 'Import new' },
                                                ].map(({ mode, label }) => {
                                                    const selected = studioVideoSourceMode === mode;
                                                    return (
                                                        <button
                                                            key={mode}
                                                            type="button"
                                                            onClick={() => handleStudioVideoSourceChange(mode)}
                                                            style={{
                                                                padding: '8px 14px',
                                                                borderRadius: '14px',
                                                                border: selected
                                                                    ? '1px solid rgba(225, 246, 121, 0.55)'
                                                                    : (darkMode ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(0,0,0,0.12)'),
                                                                backgroundColor: selected
                                                                    ? (darkMode ? 'rgba(225, 246, 121, 0.10)' : 'rgba(200, 230, 80, 0.22)')
                                                                    : (darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'),
                                                                color: selected ? (darkMode ? '#e1f679' : '#3d4f00') : 'var(--text-secondary)',
                                                                fontSize: '12.5px',
                                                                fontWeight: 800,
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            {label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            {studioVideoSourceMode === 'select' && studioSelectedRawVideoIds.length > 0 && (
                                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                                    Using {studioSelectedRawVideoIds.length} selected raw video{studioSelectedRawVideoIds.length !== 1 ? 's' : ''}.{' '}
                                                    <button
                                                        type="button"
                                                        onClick={() => openStudioRawVideosPicker()}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            color: 'var(--button-bg, #1976d2)',
                                                            cursor: 'pointer',
                                                            textDecoration: 'underline',
                                                            padding: 0,
                                                            fontSize: '12px',
                                                        }}
                                                    >
                                                        Change selection
                                                    </button>
                                                </div>
                                            )}
                                            {studioVideoSourceMode === 'import' && studioSelectedRawVideoIds.length > 0 && (
                                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                                    Using {studioSelectedRawVideoIds.length} imported video{studioSelectedRawVideoIds.length !== 1 ? 's' : ''}.{' '}
                                                    <button
                                                        type="button"
                                                        onClick={() => setActiveTab('raw')}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            color: 'var(--button-bg, #1976d2)',
                                                            cursor: 'pointer',
                                                            textDecoration: 'underline',
                                                            padding: 0,
                                                            fontSize: '12px',
                                                        }}
                                                    >
                                                        View raw videos
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Video Length Selection */}
                                        <div style={{ marginBottom: '12px' }}>
                                            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px' }}>Video Length</label>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setNoScriptVideoLength(15)}
                                                    style={{
                                                        padding: '8px 16px',
                                                        borderRadius: '6px',
                                                        border: noScriptVideoLength === 15 ? '2px solid #2d5a9e' : '1px solid var(--input-border)',
                                                        background: noScriptVideoLength === 15 ? '#2d5a9e' : 'var(--input-bg)',
                                                        color: noScriptVideoLength === 15 ? 'white' : 'var(--input-text)',
                                                        fontSize: '14px',
                                                        cursor: 'pointer',
                                                        fontWeight: noScriptVideoLength === 15 ? '500' : 'normal'
                                                    }}
                                                >
                                                    15 seconds
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setNoScriptVideoLength(30)}
                                                    style={{
                                                        padding: '8px 16px',
                                                        borderRadius: '6px',
                                                        border: noScriptVideoLength === 30 ? '2px solid #2d5a9e' : '1px solid var(--input-border)',
                                                        background: noScriptVideoLength === 30 ? '#2d5a9e' : 'var(--input-bg)',
                                                        color: noScriptVideoLength === 30 ? 'white' : 'var(--input-text)',
                                                        fontSize: '14px',
                                                        cursor: 'pointer',
                                                        fontWeight: noScriptVideoLength === 30 ? '500' : 'normal'
                                                    }}
                                                >
                                                    30 seconds
                                                </button>
                                            </div>
                                        </div>

                                        {/* Tag Filtering */}
                                        <div style={{ marginBottom: '12px' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '14px' }}>
                                                Tags to Include <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>(optional)</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowIncludeTagsInfo(!showIncludeTagsInfo)}
                                                    style={{
                                                        width: '20px',
                                                        height: '20px',
                                                        borderRadius: '50%',
                                                        border: '1px solid var(--input-border)',
                                                        background: 'var(--input-bg)',
                                                        color: 'var(--text-secondary)',
                                                        fontSize: '12px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    i
                                                </button>
                                            </label>
                                            {showIncludeTagsInfo && (
                                                <div style={{
                                                    backgroundColor: 'var(--background-tertiary)',
                                                    border: '1px solid var(--border-primary)',
                                                    borderRadius: '6px',
                                                    padding: '12px 32px 12px 12px',
                                                    marginBottom: '8px',
                                                    fontSize: '12px',
                                                    color: 'var(--text-secondary)',
                                                    position: 'relative'
                                                }}>
                                                    <button
                                                        onClick={() => setShowIncludeTagsInfo(false)}
                                                        style={{
                                                            position: 'absolute',
                                                            top: '8px',
                                                            right: '8px',
                                                            background: 'none',
                                                            border: 'none',
                                                            fontSize: '16px',
                                                            cursor: 'pointer',
                                                            color: 'var(--text-secondary)',
                                                            padding: '2px',
                                                            borderRadius: '50%',
                                                            width: '20px',
                                                            height: '20px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        ×
                                                    </button>
                                                    Your uploaded videos have tags. Writing tags here will prioritize cuts from videos that contain these tags, ensuring your generated video uses the most relevant content.
                                                </div>
                                            )}
                                            <div className="video-tags">
                                                {includeTags.map(tag => (
                                                    <span key={`include-no-script-${tag}`} className="tag">
                                                        {tag}
                                                        <button 
                                                            className="remove-tag"
                                                            onClick={() => handleRemoveIncludeTag(tag)}
                                                        >
                                                            <X size={14} style={{paddingTop: '3px'}}/>
                                                        </button>
                                                    </span>
                                                ))}
                                                <div className="add-tag" style={{ position: 'relative' }}>
                                                    {renderTagInputWithSuggestions(
                                                        'include-tag-studio',
                                                        includeTagInput,
                                                        (e) => setIncludeTagInput(e.target.value),
                                                        (e) => {
                                                            if (e.key === 'Enter') {
                                                                handleAddIncludeTag();
                                                            }
                                                        },
                                                        handleAddIncludeTag,
                                                        showIncludeTagSuggestions,
                                                        setShowIncludeTagSuggestions,
                                                        includeTagInputRef,
                                                        "Add tag...",
                                                        {},
                                                        (tag) => {
                                                            // Auto-add tag when clicked from dropdown
                                                            if (!includeTags.includes(tag)) {
                                                                setIncludeTags(prev => [...prev, tag]);
                                                                setIncludeTagInput('');
                                                                setShowIncludeTagSuggestions(false);
                                                            }
                                                        }
                                                    )}
                                                    <button onClick={handleAddIncludeTag} style={{borderRadius: '20%', position: 'relative', marginLeft: '20px'}}>
                                                        <Plus size={14} style={{paddingTop: '3px'}} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: '12px' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '14px' }}>
                                                Tags to Exclude <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>(optional)</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowExcludeTagsInfo(!showExcludeTagsInfo)}
                                                    style={{
                                                        width: '20px',
                                                        height: '20px',
                                                        borderRadius: '50%',
                                                        border: '1px solid var(--input-border)',
                                                        background: 'var(--input-bg)',
                                                        color: 'var(--text-secondary)',
                                                        fontSize: '12px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    i
                                                </button>
                                            </label>
                                            {showExcludeTagsInfo && (
                                                <div style={{
                                                    backgroundColor: 'var(--background-tertiary)',
                                                    border: '1px solid var(--border-primary)',
                                                    borderRadius: '6px',
                                                    padding: '12px 32px 12px 12px',
                                                    marginBottom: '8px',
                                                    fontSize: '12px',
                                                    color: 'var(--text-secondary)',
                                                    position: 'relative'
                                                }}>
                                                    <button
                                                        onClick={() => setShowExcludeTagsInfo(false)}
                                                        style={{
                                                            position: 'absolute',
                                                            top: '8px',
                                                            right: '8px',
                                                            background: 'none',
                                                            border: 'none',
                                                            fontSize: '16px',
                                                            cursor: 'pointer',
                                                            color: 'var(--text-secondary)',
                                                            padding: '2px',
                                                            borderRadius: '50%',
                                                            width: '20px',
                                                            height: '20px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        ×
                                                    </button>
                                                    Your uploaded videos have tags. Writing tags here will avoid cuts from videos that contain these tags, ensuring your generated video doesn't include unwanted content.
                                                </div>
                                            )}
                                            <div className="video-tags">
                                                {excludeTags.map(tag => (
                                                    <span key={`exclude-no-script-${tag}`} className="tag">
                                                        {tag}
                                                        <button 
                                                            className="remove-tag"
                                                            onClick={() => handleRemoveExcludeTag(tag)}
                                                        >
                                                            <X size={14} style={{paddingTop: '3px'}}/>
                                                        </button>
                                                    </span>
                                                ))}
                                                <div className="add-tag" style={{ position: 'relative' }}>
                                                    {renderTagInputWithSuggestions(
                                                        'exclude-tag-studio',
                                                        excludeTagInput,
                                                        (e) => setExcludeTagInput(e.target.value),
                                                        (e) => {
                                                            if (e.key === 'Enter') {
                                                                handleAddExcludeTag();
                                                            }
                                                        },
                                                        handleAddExcludeTag,
                                                        showExcludeTagSuggestions,
                                                        setShowExcludeTagSuggestions,
                                                        excludeTagInputRef,
                                                        "Add tag...",
                                                        {},
                                                        (tag) => {
                                                            // Auto-add tag when clicked from dropdown
                                                            if (!excludeTags.includes(tag)) {
                                                                setExcludeTags(prev => [...prev, tag]);
                                                                setExcludeTagInput('');
                                                                setShowExcludeTagSuggestions(false);
                                                            }
                                                        }
                                                    )}
                                                    <button onClick={handleAddExcludeTag} style={{borderRadius: '20%', position: 'relative', marginLeft: '20px'}}>
                                                        <Plus size={14} style={{paddingTop: '3px'}} />
                                                    </button>
                                                </div>
                                            </div>
                                            {matchingCutsCount !== null && (
                                                <div style={{ marginTop: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                                                    {matchingCutsCount === 0 ? (
                                                        <span style={{ color: '#dc3545' }}>No cuts match your criteria.</span>
                                                    ) : (
                                                        <span style={{border: '1px solid var(--input-border)', borderRadius: '6px', padding: '4px 8px'}}>Cuts matching your tagging criteria: <strong>{matchingCutsCount}</strong> cut{matchingCutsCount !== 1 ? 's' : ''}</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {/* Slow motion toggle */}
                                        {/* <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <label style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                                fontSize: '14px',
                                                color: 'var(--text-color)'
                                            }}>
                                                <input
                                                    type="checkbox"
                                                    checked={slowMotion}
                                                    onChange={(e) => setSlowMotion(e.target.checked)}
                                                    style={{
                                                        cursor: 'pointer',
                                                        accentColor: 'var(--button-bg)'
                                                    }}
                                                />
                                                <span style={{marginLeft:'8px'}}>Slow motion (0.75x speed)</span>
                                            </label>
                                        </div> */}
                                    </div>
                                )}

                                {studioSelectedScriptId && (
                                    <div 
                                        ref={voiceoverSectionRef}
                                        style={{
                                            backgroundColor: 'var(--input-bg)',
                                            border: '1px solid var(--input-border)',
                                            borderRadius: '8px', padding: '12px', marginBottom: '12px'
                                        }}>
                                        <h4 style={{ marginTop: 0 }}>
                                            3 - Generate voiceover
                                            <span style={{ 
                                                marginLeft: '8px', 
                                                fontSize: '12px', 
                                                fontWeight: 'normal',
                                                background: 'linear-gradient(45deg, #667eea, #764ba2)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                                backgroundClip: 'text'
                                            }}>@VoiceActor</span>
                                        </h4>

                                        {/* Audio Section */}
                                        <div style={{ 
                                            marginTop: '8px',
                                            marginBottom: '12px',
                                        }}>
                                            <h5 style={{ 
                                                margin: '0 0 12px 0',
                                                fontSize: '15px',
                                                fontWeight: '600',
                                                color: 'var(--text-color)'
                                            }}>Voice</h5>
                                        </div>
                                        
                                        {/* Voice Selection */}
                                        <div style={{ marginBottom: '12px' }}>
                                            <div style={{ position: 'relative' }} data-voice-dropdown>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowVoiceDropdown(!showVoiceDropdown)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px 12px',
                                                        borderRadius: '6px',
                                                        border: '1px solid var(--input-border)',
                                                        background: 'var(--input-bg)',
                                                        color: 'var(--input-text)',
                                                        fontSize: '14px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        textAlign: 'left'
                                                    }}
                                                >
                                                    <span>
                                                        {selectedVoiceId ? 
                                                            availableVoices.find(v => v.id === selectedVoiceId)?.name || 'Select a voice...' 
                                                            : 'Select a voice...'
                                                        }
                                                    </span>
                                                    <span style={{ fontSize: '12px', opacity: 0.7 }}>
                                                        {showVoiceDropdown ? '▲' : '▼'}
                                                    </span>
                                                </button>
                                                
                                                {showVoiceDropdown && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '100%',
                                                        left: 10,
                                                        right: 10,
                                                        backgroundColor: 'var(--input-bg)',
                                                        border: '1px solid var(--input-border)',
                                                        borderRadius: '6px',
                                                        marginTop: '2px',
                                                        zIndex: 10015,
                                                        maxHeight: '200px',
                                                        overflowY: 'auto',
                                                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.5)'
                                                    }}>
                                                        {availableVoices.map(voice => (
                                                            <div
                                                                key={voice.id}
                                                                style={{
                                                                    padding: '8px 12px',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                    borderBottom: '1px solid var(--input-border)',
                                                                    backgroundColor: selectedVoiceId === voice.id ? 'var(--button-bg)' : 'transparent',
                                                                    color: selectedVoiceId === voice.id ? 'var(--button-text)' : 'var(--text-color)'
                                                                }}
                                                                onClick={() => {
                                                                    handleVoiceChange(voice.id);
                                                                    setShowVoiceDropdown(false);
                                                                }}
                                                            >
                                                                <span style={{ fontSize: '14px' }}>{voice.name}</span>
                                                                {voice.previewUrl && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleVoicePreview(voice.id, voice.previewUrl);
                                                                        }}
                                                                        style={{
                                                                            padding: '4px 6px',
                                                                            backgroundColor: playingVoiceId === voice.id ? '#ff4444' : 'var(--button-bg)',
                                                                            color: 'white',
                                                                            border: '1px solid var(--input-border)',
                                                                            borderRadius: '4px',
                                                                            cursor: 'pointer',
                                                                            fontSize: '12px',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            gap: '4px',
                                                                            height: '22px',
                                                                            width: '22px'
                                                                        }}
                                                                    >
                                                                        {playingVoiceId === voice.id ? '⏹' : '▶'}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {availableVoices.length === 0 && (
                                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                                                    Loading voices...
                                                </div>
                                            )}
                                        </div>

                                        {/* No Cloned Voice Message */}
                                        {!currentWorkspace?.voiceId && (
                                            <div style={{ 
                                                marginBottom: '25px',
                                                marginTop: '-5px',
                                                marginLeft: '12px',
                                                marginRight: '12px',
                                                padding: '12px',
                                                backgroundColor: 'var(--input-bg)',
                                                border: '1px solid var(--button-bg)',
                                                borderRadius: '6px',
                                                fontSize: '13px',
                                                color: 'var(--text-secondary)'
                                            }}>
                                                <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                                                <AlertCircle size={14} style={{marginRight: '5px'}} /> This workspace doesn't have a cloned voice. You can use default voices or create your voice in the voice tab.
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setActiveTab('voice')}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: 'var(--button-bg)',
                                                        textDecoration: 'underline',
                                                        cursor: 'pointer',
                                                        fontSize: '13px',
                                                        padding: 0
                                                    }}
                                                >
                                                    Click here to open the voice tab
                                                </button>
                                            </div>
                                        )}
                                        
                                        {/* Voice Generation Mode Toggle */}
                                        <div style={{ marginBottom: '12px', marginTop: '20px', margin:1 }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '14px' }}>
                                                Voice Generation Mode
                                                <button
                                                    type="button"
                                                    onClick={() => setShowVoiceGenerationModeInfo(!showVoiceGenerationModeInfo)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        padding: '2px',
                                                        borderRadius: '50%',
                                                        width: '18px',
                                                        height: '18px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '12px',
                                                        fontWeight: 'bold',
                                                        color: 'var(--text-secondary)',
                                                        backgroundColor: 'var(--background-tertiary)',
                                                        transition: 'all 0.2s ease',
                                                    }}
                                                    onMouseOver={(e) => {
                                                        e.target.style.backgroundColor = 'var(--button-bg)';
                                                        e.target.style.color = 'var(--button-text)';
                                                    }}
                                                    onMouseOut={(e) => {
                                                        e.target.style.backgroundColor = 'var(--background-tertiary)';
                                                        e.target.style.color = 'var(--text-secondary)';
                                                    }}
                                                >
                                                    i
                                                </button>
                                            </label>
                                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '10px' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                                                    <input
                                                        type="radio"
                                                        name="voiceGenerationMode"
                                                        value="stable"
                                                        checked={voiceGenerationMode === 'stable'}
                                                        onChange={(e) => {
                                                            setVoiceGenerationMode(e.target.value);
                                                            localStorage.setItem('voiceover_voice_generation_mode', e.target.value);
                                                            // Auto-regenerate if voiceover already exists
                                                            if (studioVoiceoverUrl) {
                                                                studioGenerateVoiceover();
                                                            }
                                                        }}
                                                        style={{
                                                            cursor: 'pointer',
                                                            accentColor: 'var(--button-bg)'
                                                        }}
                                                    />
                                                    <span>Stable</span>
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                                                    <input
                                                        type="radio"
                                                        name="voiceGenerationMode"
                                                        value="expressive"
                                                        checked={voiceGenerationMode === 'expressive'}
                                                        onChange={(e) => {
                                                            setVoiceGenerationMode(e.target.value);
                                                            localStorage.setItem('voiceover_voice_generation_mode', e.target.value);
                                                            // Auto-regenerate if voiceover already exists
                                                            if (studioVoiceoverUrl) {
                                                                studioGenerateVoiceover();
                                                            }
                                                        }}
                                                        style={{
                                                            cursor: 'pointer',
                                                            accentColor: 'var(--button-bg)'
                                                        }}
                                                    />
                                                    <span>Expressive</span>
                                                </label>
                                            </div>
                                            {showVoiceGenerationModeInfo && (
                                                <div style={{
                                                    backgroundColor: 'var(--background-tertiary)',
                                                    border: '1px solid var(--border-primary)',
                                                    borderRadius: '6px',
                                                    padding: '12px 32px 12px 12px',
                                                    marginBottom: '8px',
                                                    fontSize: '13px',
                                                    color: 'var(--text-primary)',
                                                    lineHeight: '1.4',
                                                    position: 'relative',
                                                    marginTop: '12px'
                                                }}>
                                                    <button
                                                        onClick={() => setShowVoiceGenerationModeInfo(false)}
                                                        style={{
                                                            position: 'absolute',
                                                            top: '8px',
                                                            right: '8px',
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            fontSize: '16px',
                                                            color: 'var(--text-secondary)',
                                                            padding: '2px',
                                                            borderRadius: '50%',
                                                            width: '20px',
                                                            height: '20px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        onMouseOver={(e) => {
                                                            e.target.style.backgroundColor = 'var(--border-primary)';
                                                            e.target.style.color = 'var(--text-primary)';
                                                        }}
                                                        onMouseOut={(e) => {
                                                            e.target.style.backgroundColor = 'transparent';
                                                            e.target.style.color = 'var(--text-secondary)';
                                                        }}
                                                    >
                                                        ×
                                                    </button>
                                                    <strong>Stable:</strong> Stable tone accross generations, but the voice might sound monotonous.<br/><br/>
                                                    <strong>Expressive:</strong> More human-like, but with a more random tone across generations, more likely to sound weird, and may require more generations to get the right result.
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom:'12px' }}>
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flex: 1 }}>
                                                <button
                                                    className="main-button"
                                                    onClick={() => {
                                                        setShowSkipSettingsDropdown(false);
                                                        setStudioManualVoiceoverRequested(true);
                                                        studioGenerateVoiceover();
                                                    }}
                                                    disabled={studioGeneratingVoice}
                                                    style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                                >
                                                    {studioGeneratingVoice ? (
                                                        <><span className="spinner-border" /> Generating voiceover...</>
                                                    ) : (
                                                        <>
                                                            {studioVoiceoverUrl ? <RefreshCw size={16} /> : <SparklesIcon size={16} />}
                                                            {studioVoiceoverUrl ? (scriptChangedSinceVoiceover ? 'Regenerate' : 'Try new voice tone') : 'Generate Voiceover For This Script'}
                                                        </>
                                                    )}
                                                </button>
                                                {studioGeneratingVoice && (
                                                    <button 
                                                        className="tertiary-button" 
                                                        onClick={cancelVoiceoverGeneration}
                                                        style={{ 
                                                            padding: '8px 12px',
                                                            minWidth: 'auto',
                                                            flexShrink: 0
                                                        }}
                                                        title="Cancel voiceover generation"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                )}
                                            </div>
                                            {studioVoiceoverUrl && (
                                                <audio
                                                    ref={studioVoiceoverAudioRef}
                                                    src={studioVoiceoverUrl}
                                                    controls
                                                    style={{ height: '28px' }}
                                                />
                                            )}
                                        </div>
                                        
                                        {/* Warning message if script changed after voiceover generation */}
                                        {studioVoiceoverUrl && scriptChangedSinceVoiceover && !studioGeneratingVoice && (
                                            <div style={{
                                                marginTop: '8px',
                                                padding: '10px 12px',
                                                backgroundColor: 'var(--input-bg)',
                                                border: '1px solid var(--input-border)',
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                color: '#ff6b6b',
                                                gap: '8px'
                                            }}>
                                                <span>Script changed. Regenerate voiceover to match?</span>
                                            </div>
                                        )}
                                        
                                        {/* Skip and auto-generate video option */}
                                        {!studioVoiceoverUrl && (
                                            <div style={{ marginTop: '12px' }}>
                                                <div style={{ textAlign: 'center', marginBottom: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                                                    OR
                                                </div>
                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                                                    <button
                                                        onClick={() => {
                                                            trackEvent('skip_and_auto_generate_video_clicked', {
                                                                workspaceId: currentWorkspace?._id
                                                            });
                                                            studioGenerateVoiceover(null, true);
                                                        }}
                                                        aria-label="Auto generate voiceover and video"
                                                        className="waitlist-button"
                                                        disabled={studioGenerating || studioGeneratingVoice || studioGeneratingVideo || isNoScriptMode}
                                                        onMouseEnter={(e) => {
                                                            if (!e.target.disabled) {
                                                                e.target.style.opacity = '0.9';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (!e.target.disabled) {
                                                                e.target.style.opacity = '1';
                                                            }
                                                        }}
                                                        style={{
                                                            flex: 1,
                                                            justifyContent: 'center',
                                                            textAlign: 'center',
                                                            fontWeight: 'bold',
                                                            opacity: (studioGenerating || studioGeneratingVoice || studioGeneratingVideo || isNoScriptMode) ? 0.5 : 1,
                                                            cursor: (studioGenerating || studioGeneratingVoice || studioGeneratingVideo || isNoScriptMode) ? 'not-allowed' : 'pointer'
                                                        }}
                                                    >
                                                        <span style={{ color: 'black' }}>
                                                            {(studioGenerating || studioGeneratingVideo || (studioGeneratingVoice && !studioManualVoiceoverRequested))
                                                                ? 'Auto-generating…'
                                                                : 'Auto-Generate Voiceover + Video'}
                                                        </span>
                                                        <span className="waitlist-shimmer"></span>
                                                    </button>
                                                    <div style={{ position: 'relative', zIndex: 10001, alignItems: 'center', display: 'flex' }} ref={skipSettingsDropdownRef}>
                                                        <button
                                                            type="button"
                                                            disabled={studioGenerating || studioGeneratingVoice || studioGeneratingVideo || isNoScriptMode}
                                                            onClick={() => setShowSkipSettingsDropdown(!showSkipSettingsDropdown)}
                                                            aria-label="Generation settings"
                                                            style={{
                                                                backgroundColor: 'var(--input-bg)',
                                                                color: 'var(--input-text)',
                                                                border: '1px solid var(--input-border)',
                                                                borderRadius: '4px',
                                                                padding: '4px 8px',
                                                                cursor: (studioGenerating || studioGeneratingVoice || studioGeneratingVideo || isNoScriptMode) ? 'not-allowed' : 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                                opacity: (studioGenerating || studioGeneratingVoice || studioGeneratingVideo || isNoScriptMode) ? 0.45 : 0.9,
                                                                transition: 'all 0.2s'
                                                            }}
                                                            onMouseOver={(e) => {
                                                                if (e.currentTarget.disabled) return;
                                                                e.currentTarget.style.opacity = '1';
                                                                e.currentTarget.style.backgroundColor = 'var(--button-bg)';
                                                                e.currentTarget.style.color = 'white';
                                                                e.currentTarget.style.borderColor = 'var(--button-bg)';
                                                            }}
                                                            onMouseOut={(e) => {
                                                                if (e.currentTarget.disabled) return;
                                                                e.currentTarget.style.opacity = '0.9';
                                                                e.currentTarget.style.backgroundColor = 'var(--input-bg)';
                                                                e.currentTarget.style.color = 'var(--text-color)';
                                                                e.currentTarget.style.borderColor = 'var(--input-border)';
                                                            }}
                                                        >
                                                            <Settings size={14} />
                                                        </button>
                                                        {showSkipSettingsDropdown && renderGenerationSettingsDropdown('above')}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Voice, Music, and Font Selection — settings panel + video preview */}
                                {studioSelectedScriptId && (studioVoiceoverUrl || studioVideoUrl) && (
                                    <>
                                        <div
                                            ref={studioGenerationSettingsRef}
                                            style={{
                                            backgroundColor: 'var(--input-bg)',
                                            border: '1px solid var(--input-border)',
                                            borderRadius: '8px', padding: '12px', marginBottom: '12px'
                                        }}>
                                            <h4 style={{ marginTop: 0, marginBottom: '12px' }}>
                                                4 - Video Settings
                                                <span style={{ 
                                                    marginLeft: '8px', 
                                                    fontSize: '12px', 
                                                    fontWeight: 'normal',
                                                    background: 'linear-gradient(45deg, #f093fb, #f5576c)',
                                                    WebkitBackgroundClip: 'text',
                                                    WebkitTextFillColor: 'transparent',
                                                    backgroundClip: 'text'
                                                }}>@VideoEditor</span>
                                            </h4>

                                            <div style={{
                                                display: 'flex',
                                                flexDirection: studioSettingsNarrow ? 'column' : 'row',
                                                gap: '16px',
                                                alignItems: 'stretch',
                                            }}>
                                                {/* Left: section jump nav + scrollable settings list */}
                                                <div style={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    display: 'flex',
                                                    flexDirection: studioSettingsNarrow ? 'column' : 'row',
                                                    gap: '12px',
                                                }}>
                                                    <div
                                                        role="navigation"
                                                        aria-label="Jump to settings section"
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection: studioSettingsNarrow ? 'row' : 'column',
                                                            gap: '4px',
                                                            flexShrink: 0,
                                                            overflowX: studioSettingsNarrow ? 'auto' : 'visible',
                                                            position: studioSettingsNarrow ? 'static' : 'sticky',
                                                            top: 0,
                                                            alignSelf: studioSettingsNarrow ? 'stretch' : 'flex-start',
                                                        }}
                                                    >
                                                        {[
                                                            { id: 'subtitle', label: 'Subtitle', Icon: Type },
                                                            { id: 'video', label: 'Video', Icon: Video },
                                                            { id: 'audio', label: 'Audio', Icon: Mic },
                                                            { id: 'tags', label: 'Tags', Icon: Search },
                                                        ].map(({ id, label, Icon }) => {
                                                            const selected = studioSettingsTab === id;
                                                            const hovered = studioSettingsNavHoverId === id;
                                                            const idleBg = 'transparent';
                                                            const hoverBg = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
                                                            const selectedBg = darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)';
                                                            const showHoverChrome = hovered || selected;
                                                            return (
                                                                <button
                                                                    key={id}
                                                                    type="button"
                                                                    aria-current={selected ? 'true' : undefined}
                                                                    onClick={() => scrollToStudioSettingsSection(id)}
                                                                    onMouseEnter={() => setStudioSettingsNavHoverId(id)}
                                                                    onMouseLeave={() => setStudioSettingsNavHoverId((prev) => (prev === id ? null : prev))}
                                                                    style={{
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        gap: '4px',
                                                                        padding: studioSettingsNarrow ? '8px 10px' : '10px 8px',
                                                                        borderRadius: '8px',
                                                                        border: selected
                                                                            ? '1px solid var(--button-bg)'
                                                                            : hovered
                                                                                ? `1px solid ${darkMode ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.12)'}`
                                                                                : '1px solid transparent',
                                                                        background: selected ? selectedBg : (hovered ? hoverBg : idleBg),
                                                                        color: showHoverChrome ? 'var(--text-color)' : 'var(--text-secondary)',
                                                                        fontSize: '10px',
                                                                        fontWeight: selected ? 600 : 500,
                                                                        cursor: 'pointer',
                                                                        whiteSpace: 'nowrap',
                                                                        textAlign: 'center',
                                                                        minWidth: studioSettingsNarrow ? '56px' : '64px',
                                                                        lineHeight: 1.15,
                                                                        transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease',
                                                                    }}
                                                                >
                                                                    <Icon size={16} style={{ flexShrink: 0, opacity: selected || hovered ? 1 : 0.7 }} />
                                                                    <span>{label}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>

                                                    <div
                                                        style={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                            position: 'relative',
                                                            maxHeight: studioSettingsNarrow ? 'none' : '560px',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                        }}
                                                    >
                                                    <div
                                                        ref={(node) => {
                                                            studioSettingsListRef.current = node;
                                                            setStudioSettingsListEl((prev) => (prev === node ? prev : node));
                                                        }}
                                                        style={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                            overflowY: 'auto',
                                                            maxHeight: studioSettingsNarrow ? 'none' : '560px',
                                                            paddingRight: '4px',
                                                            // Extra space (wide layout only) so Audio can sit at the active marker before Tags takes over
                                                            paddingBottom: studioSettingsNarrow ? 0 : '280px',
                                                            boxSizing: 'border-box',
                                                        }}
                                                    >

                                                        <div
                                                            data-studio-section="subtitle"
                                                            style={{ scrollMarginTop: '8px' }}
                                                        >
                                                            <h5 style={{
                                                                margin: '0 0 14px',
                                                                fontSize: '12px',
                                                                fontWeight: 700,
                                                                letterSpacing: '0.04em',
                                                                textTransform: 'uppercase',
                                                                color: 'var(--text-secondary)',
                                                            }}>
                                                                Subtitle
                                                            </h5>

                                                            {/* Enable Subtitles Toggle */}
                                                            <div style={{ marginBottom: '12px' }}>
                                                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={enableSubtitles}
                                                                        onChange={(e) => {
                                                                            setEnableSubtitles(e.target.checked);
                                                                            localStorage.setItem('workspace_enable_subtitles', e.target.checked.toString());
                                                                        }}
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            accentColor: 'var(--button-bg)'
                                                                        }}
                                                                    />
                                                                    <span>Enable subtitles</span>
                                                                </label>
                                                            </div>

                                                            {/* Font Selection */}
                                                            {enableSubtitles && (<div style={{ marginBottom: '12px' }}>
                                                                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px' }}>Subtitle Font</label>
                                                                <div style={{ position: 'relative' }} data-font-dropdown>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setShowFontDropdown(!showFontDropdown)}
                                                                    style={{
                                                                        width: '100%',
                                                                            padding: '8px 12px',
                                                                        borderRadius: '6px',
                                                                        border: '1px solid var(--input-border)',
                                                                        background: 'var(--input-bg)',
                                                                        color: 'var(--input-text)',
                                                                            fontSize: '14px',
                                                                            cursor: 'pointer',
                                                                            display: 'flex',
                                                                            justifyContent: 'space-between',
                                                                            alignItems: 'center',
                                                                            textAlign: 'left'
                                                                        }}
                                                                    >
                                                                        <span style={{
                                                                            fontFamily: selectedFontId ? 
                                                                                fontFamilyCssForName(userFonts.find(f => f._id === selectedFontId)?.name)
                                                                                : defaultFontFamilyCss
                                                                        }}>
                                                                            {selectedFontId ? 
                                                                                userFonts.find(f => f._id === selectedFontId)?.name || 'Select font...' 
                                                                                : 'Default (TikTok medium)'
                                                                            }
                                                                        </span>
                                                                        <span style={{ fontSize: '12px', opacity: 0.7 }}>
                                                                            {showFontDropdown ? '▲' : '▼'}
                                                                        </span>
                                                                    </button>
                                                                    
                                                                    {showFontDropdown && (
                                                                        <div style={{
                                                                            position: 'absolute',
                                                                            top: '100%',
                                                                            left: 10,
                                                                            right: 10,
                                                                            backgroundColor: 'var(--input-bg)',
                                                                            border: '1px solid var(--input-border)',
                                                                            borderRadius: '6px',
                                                                            marginTop: '2px',
                                                                            zIndex: 1000,
                                                                            maxHeight: '200px',
                                                                            overflowY: 'auto',
                                                                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.5)'
                                                                        }}>
                                                                            {/* Default Font Option */}
                                                                            <div
                                                                                style={{
                                                                                    padding: '8px 12px',
                                                                                    cursor: 'pointer',
                                                                                    borderRadius: '6px',
                                                                                    backgroundColor: selectedFontId === '' ? 'var(--button-bg)' : 'transparent',
                                                                                    color: selectedFontId === '' ? 'var(--button-text)' : 'var(--text-color)'
                                                                                }}
                                                                                onClick={() => {
                                                                                    handleFontChange('');
                                                                                    setShowFontDropdown(false);
                                                                                }}
                                                                            >
                                                                                <span style={{ fontSize: '14px', fontFamily: defaultFontFamilyCss }}>Default (TikTok medium)</span>
                                                                            </div>
                                                                            
                                                                            {/* Custom Font Options */}
                                                                            {sortedUserFonts.map(font => (
                                                                                <div
                                                                                    key={font._id}
                                                                                    style={{
                                                                                        padding: '8px 12px',
                                                                                        cursor: 'pointer',
                                                                                        borderRadius: '6px',
                                                                                        backgroundColor: selectedFontId === font._id ? 'var(--button-bg)' : 'transparent',
                                                                                        color: selectedFontId === font._id ? 'var(--button-text)' : 'var(--text-color)',
                                                                                        display: 'flex',
                                                                                        flexDirection: 'column',
                                                                                        gap: '2px'
                                                                                    }}
                                                                                    onClick={() => {
                                                                                        handleFontChange(font._id);
                                                                                        setShowFontDropdown(false);
                                                                                    }}
                                                                                >
                                                                                    <div style={{ 
                                                                                        fontSize: '14px', 
                                                                                        fontFamily: fontFamilyCssForName(font.name),
                                                                                        fontWeight: '500'
                                                                                    }}>
                                                                                        {font.name}
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>)}

                                                            {enableSubtitles && renderStudioSubtitleColorPickers('vlog')}

                                                            {/* Subtitle size */}
                                                            {renderSubtitleSizeRadios('studio-subtitle-size')}

                                                            {/* Glow Text Toggle */}
                                                            {['673247877e4ac0a824cbac0dx' /*Boukar*/,].includes(currentUserId) &&  <div style={{ marginBottom: '12px' }}>
                                                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={glowText}
                                                                        onChange={(e) => {
                                                                            const newValue = e.target.checked;
                                                                            setGlowText(newValue);
                                                                            localStorage.setItem('workspace_glow_text', newValue.toString());
                                                                        }}
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            accentColor: 'var(--button-bg)'
                                                                        }}
                                                                    />
                                                                    <span>Glow Text</span>
                                                                </label>
                                                                {glowText && (
                                                                    <div style={{ marginTop: '8px', marginLeft: '24px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                                                                        <label style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Glow color:</label>
                                                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                                            <input
                                                                                type="radio"
                                                                                name="glowTextColor"
                                                                                value="white"
                                                                                checked={glowTextColor === 'white'}
                                                                                onChange={(e) => {
                                                                                    setGlowTextColor('white');
                                                                                    localStorage.setItem('workspace_glow_text_color', 'white');
                                                                                }}
                                                                                style={{
                                                                                    cursor: 'pointer',
                                                                                    accentColor: 'var(--button-bg)'
                                                                                }}
                                                                            />
                                                                            <span>White</span>
                                                                        </label>
                                                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                                            <input
                                                                                type="radio"
                                                                                name="glowTextColor"
                                                                                value="black"
                                                                                checked={glowTextColor === 'black'}
                                                                                onChange={(e) => {
                                                                                    setGlowTextColor('black');
                                                                                    localStorage.setItem('workspace_glow_text_color', 'black');
                                                                                }}
                                                                                style={{
                                                                                    cursor: 'pointer',
                                                                                    accentColor: 'var(--button-bg)'
                                                                                }}
                                                                            />
                                                                            <span>Black</span>
                                                                        </label>
                                                                    </div>
                                                                )}
                                                            </div>}

                                                            {/* Subtitle Position */}
                                                            {enableSubtitles && (
                                                            <div style={{ marginBottom: '12px' }}>
                                                                <label style={{ fontSize: '14px', marginBottom: '8px', display: 'block', color: 'var(--text-color)' }}>Subtitle position:</label>
                                                                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                                        <input
                                                                            type="radio"
                                                                            name="subtitlePosition"
                                                                            value="center"
                                                                            checked={subtitlePosition === 'center'}
                                                                            onChange={(e) => {
                                                                                setSubtitlePosition('center');
                                                                                localStorage.setItem('workspace_subtitle_position', 'center');
                                                                            }}
                                                                            style={{
                                                                                cursor: 'pointer',
                                                                                accentColor: 'var(--button-bg)'
                                                                            }}
                                                                        />
                                                                        <span>Center</span>
                                                                    </label>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                                        <input
                                                                            type="radio"
                                                                            name="subtitlePosition"
                                                                            value="bottom"
                                                                            checked={subtitlePosition === 'bottom'}
                                                                            onChange={(e) => {
                                                                                setSubtitlePosition('bottom');
                                                                                localStorage.setItem('workspace_subtitle_position', 'bottom');
                                                                            }}
                                                                            style={{
                                                                                cursor: 'pointer',
                                                                                accentColor: 'var(--button-bg)'
                                                                            }}
                                                                        />
                                                                        <span>Bottom</span>
                                                                    </label>
                                                                </div>
                                                            </div>
                                                            )}

                                                            {/* One Word Per Subtitle Toggle */}
                                                            {enableSubtitles && (
                                                                <div style={{ marginBottom: '12px' }}>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={oneWordPerSubtitle}
                                                                            onChange={(e) => {
                                                                                const newValue = e.target.checked;
                                                                                setOneWordPerSubtitle(newValue);
                                                                                localStorage.setItem('workspace_one_word_per_subtitle', newValue.toString());
                                                                            }}
                                                                            style={{
                                                                                cursor: 'pointer',
                                                                                accentColor: 'var(--button-bg)'
                                                                            }}
                                                                        />
                                                                        <span>1 word per subtitle</span>
                                                                    </label>
                                                                </div>
                                                            )}


                                                        </div>

                                                        <div
                                                            aria-hidden="true"
                                                            style={{
                                                                height: '1px',
                                                                background: 'var(--border-primary)',
                                                                margin: '20px 0 16px',
                                                                opacity: 0.85,
                                                            }}
                                                        />
                                                        <div
                                                            data-studio-section="video"
                                                            style={{ scrollMarginTop: '8px' }}
                                                        >
                                                            <h5 style={{
                                                                margin: '0 0 14px',
                                                                fontSize: '12px',
                                                                fontWeight: 700,
                                                                letterSpacing: '0.04em',
                                                                textTransform: 'uppercase',
                                                                color: 'var(--text-secondary)',
                                                            }}>
                                                                Video
                                                            </h5>

                                                            {/* Cut length */}
                                                            <div style={{ marginBottom: '12px' }}>
                                                                <span style={{ display: 'block', fontSize: '14px', color: 'var(--text-color)', marginBottom: '8px' }}>Cut length</span>
                                                                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                                        <input
                                                                            type="radio"
                                                                            name="studio-cut-length"
                                                                            checked={!!fastPacing}
                                                                            onChange={() => { setFastPacing(true); localStorage.setItem('workspace_fast_pacing', 'true'); }}
                                                                            style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                                        />
                                                                        <span>1 sec</span>
                                                                    </label>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                                        <input
                                                                            type="radio"
                                                                            name="studio-cut-length"
                                                                            checked={!fastPacing}
                                                                            onChange={() => { setFastPacing(false); localStorage.setItem('workspace_fast_pacing', 'false'); }}
                                                                            style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                                        />
                                                                        <span>2 sec</span>
                                                                    </label>
                                                                </div>
                                                            </div>

                                                            {/* Slow motion toggle */}
                                                            {/* <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <label style={{ 
                                                                    display: 'flex', 
                                                                    alignItems: 'center', 
                                                                    cursor: 'pointer',
                                                                    userSelect: 'none',
                                                                    fontSize: '14px',
                                                                    color: 'var(--text-color)'
                                                                }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={slowMotion}
                                                                        onChange={(e) => setSlowMotion(e.target.checked)}
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            accentColor: 'var(--button-bg)'
                                                                        }}
                                                                    />
                                                                    <span style={{marginLeft:'8px'}}>Slow motion clips</span>
                                                                </label>
                                                            </div> */}

                                                            {currentUserId === BOUKAR_USER_ID && (
                                                            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <label style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    cursor: 'pointer',
                                                                    userSelect: 'none',
                                                                    fontSize: '14px',
                                                                    color: 'var(--text-color)'
                                                                }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={speedUp}
                                                                        onChange={(e) => {
                                                                            const newValue = e.target.checked;
                                                                            setSpeedUp(newValue);
                                                                            localStorage.setItem('workspace_speed_up', newValue.toString());
                                                                        }}
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            accentColor: 'var(--button-bg)'
                                                                        }}
                                                                    />
                                                                    <span style={{marginLeft:'8px'}}>Speed up (1.15x)</span>
                                                                </label>
                                                            </div>
                                                            )}

                                                            {/* Flipbook Intro Toggle */}
                                                            {renderFastCutIntroToggle()}

                                                            {/* Title card — Boukar only */}
                                                            {currentUserId === BOUKAR_USER_ID && enableSubtitles && (
                                                            <div style={{ marginBottom: '12px' }}>
                                                                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={openingLineInCenter}
                                                                        onChange={(e) => {
                                                                            const newValue = e.target.checked;
                                                                            setOpeningLineInCenter(newValue);
                                                                            localStorage.setItem('workspace_opening_line_in_center', newValue.toString());
                                                                        }}
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            accentColor: 'var(--button-bg)',
                                                                            marginTop: '2px',
                                                                        }}
                                                                    />
                                                                    <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                        <span>Title card</span>
                                                                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 'normal' }}>
                                                                            (First sentence becomes the title)
                                                                        </span>
                                                                    </span>
                                                                </label>
                                                                {openingLineInCenter && (
                                                                    <div style={{ marginTop: '8px', marginBottom: '24px', marginLeft: '12px', paddingLeft: '12px', display: 'flex', flexDirection: 'column', gap: '12px', borderLeft: '2px solid var(--border-primary)' }}>
                                                                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                                                            <label style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Text size:</label>
                                                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                                                <input
                                                                                    type="radio"
                                                                                    name="openingLineTextSize"
                                                                                    value="big"
                                                                                    checked={openingLineTextSize === 'big'}
                                                                                    onChange={(e) => {
                                                                                        setOpeningLineTextSize('big');
                                                                                        localStorage.setItem('workspace_opening_line_text_size', 'big');
                                                                                    }}
                                                                                    style={{
                                                                                        cursor: 'pointer',
                                                                                        accentColor: 'var(--button-bg)'
                                                                                    }}
                                                                                />
                                                                                <span>Big</span>
                                                                            </label>
                                                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                                                <input
                                                                                    type="radio"
                                                                                    name="openingLineTextSize"
                                                                                    value="small"
                                                                                    checked={openingLineTextSize === 'small'}
                                                                                    onChange={(e) => {
                                                                                        setOpeningLineTextSize('small');
                                                                                        localStorage.setItem('workspace_opening_line_text_size', 'small');
                                                                                    }}
                                                                                    style={{
                                                                                        cursor: 'pointer',
                                                                                        accentColor: 'var(--button-bg)'
                                                                                    }}
                                                                                />
                                                                                <span>Small (top middle)</span>
                                                                            </label>
                                                                        </div>
                                                                        {renderTitleCardFontAndColor({ narrow: studioSettingsNarrow })}
                                                                        {renderTitleCardStyleRadios('studio-title-card-style')}

                                                                    </div>
                                                                )}
                                                            </div>
                                                            )}

                                                            {/* Zoom + Whoosh Toggle */}
                                                            {/* <div style={{ marginBottom: '12px' }}>
                                                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={zoomWhoosh}
                                                                        onChange={(e) => {
                                                                            const newValue = e.target.checked;
                                                                            setZoomWhoosh(newValue);
                                                                            localStorage.setItem('workspace_zoom_whoosh', newValue.toString());
                                                                        }}
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            accentColor: 'var(--button-bg)'
                                                                        }}
                                                                    />
                                                                    <span>Zoom + Whoosh</span>
                                                                </label>
                                                            </div> */}

                                                            {/* Create thumbnail */}
                                                            {/* <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <label style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    cursor: 'pointer',
                                                                    userSelect: 'none',
                                                                    fontSize: '14px',
                                                                    color: 'var(--text-color)'
                                                                }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={createThumbnail}
                                                                        onChange={(e) => {
                                                                            const checked = e.target.checked;
                                                                            setCreateThumbnail(checked);
                                                                            if (!checked) {
                                                                                setThumbnailTitle('');
                                                                                setThumbnailCut(null);
                                                                            }
                                                                        }}
                                                                        style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                                    />
                                                                    <span style={{ marginLeft: '8px' }}>Create thumbnail</span>
                                                                </label>
                                                            </div>
                                                            {createThumbnail && (
                                                                <div style={{ marginBottom: '12px' }}>
                                                                    <button
                                                                        type="button"
                                                                        onClick={async () => {
                                                                            setShowThumbnailModal(true);
                                                                            setThumbnailCutsLoading(true);
                                                                            try {
                                                                                setThumbnailAvailableCuts(await loadWorkspaceCutsForPicker());
                                                                            } catch (err) {
                                                                                console.error('Failed to load cuts for thumbnail:', err);
                                                                                setThumbnailAvailableCuts([]);
                                                                            } finally {
                                                                                setThumbnailCutsLoading(false);
                                                                            }
                                                                        }}
                                                                        style={{
                                                                            padding: '8px 14px',
                                                                            borderRadius: '8px',
                                                                            border: '1px solid var(--input-border)',
                                                                            background: 'var(--input-bg)',
                                                                            color: 'var(--text-color)',
                                                                            fontSize: '14px',
                                                                            cursor: 'pointer'
                                                                        }}
                                                                    >
                                                                        Configure thumbnail
                                                                    </button>
                                                                </div>
                                                            )} */}

                                                            {/* Bouki's intro (Boukar only) */}
                                                            {currentUserId === BOUKAR_USER_ID && (
                                                                <div>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={boukisIntro}
                                                                            onChange={(e) => {
                                                                                const newValue = e.target.checked;
                                                                                setBoukisIntro(newValue);
                                                                                localStorage.setItem('workspace_boukis_intro', newValue.toString());
                                                                            }}
                                                                            style={{
                                                                                cursor: 'pointer',
                                                                                accentColor: 'var(--button-bg)'
                                                                            }}
                                                                        />
                                                                        <span>Bouki's intro</span>
                                                                    </label>
                                                                    {boukisIntro && (
                                                                        <div style={{ marginTop: '8px', display: 'flex', gap: '12px', alignItems: 'center', marginLeft: '24px', }}>
                                                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                                                <input
                                                                                    type="radio"
                                                                                    name="boukisIntroPitch"
                                                                                    value="high"
                                                                                    checked={boukisIntroPitch === 'high'}
                                                                                    onChange={() => {
                                                                                        setBoukisIntroPitch('high');
                                                                                        localStorage.setItem('workspace_boukis_intro_pitch', 'high');
                                                                                    }}
                                                                                    style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                                                />
                                                                                <span>High pitch</span>
                                                                            </label>
                                                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                                                <input
                                                                                    type="radio"
                                                                                    name="boukisIntroPitch"
                                                                                    value="low"
                                                                                    checked={boukisIntroPitch === 'low'}
                                                                                    onChange={() => {
                                                                                        setBoukisIntroPitch('low');
                                                                                        localStorage.setItem('workspace_boukis_intro_pitch', 'low');
                                                                                    }}
                                                                                    style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                                                />
                                                                                <span>Low pitch</span>
                                                                            </label>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}

                                                        </div>

                                                        <div
                                                            aria-hidden="true"
                                                            style={{
                                                                height: '1px',
                                                                background: 'var(--border-primary)',
                                                                margin: '20px 0 16px',
                                                                opacity: 0.85,
                                                            }}
                                                        />
                                                        <div
                                                            data-studio-section="audio"
                                                            style={{ scrollMarginTop: '8px' }}
                                                        >
                                                            <h5 style={{
                                                                margin: '0 0 14px',
                                                                fontSize: '12px',
                                                                fontWeight: 700,
                                                                letterSpacing: '0.04em',
                                                                textTransform: 'uppercase',
                                                                color: 'var(--text-secondary)',
                                                            }}>
                                                                Audio
                                                            </h5>

                                                            {/* Cut silence mode toggle — Boukar only */}
                                                            {currentUserId === BOUKAR_USER_ID && (
                                                            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <label style={{ 
                                                                    display: 'flex', 
                                                                    alignItems: 'center', 
                                                                    cursor: 'pointer',
                                                                    userSelect: 'none',
                                                                    fontSize: '14px',
                                                                    color: 'var(--text-color)'
                                                                }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={cutSilenceMode}
                                                                        onChange={(e) => {
                                                                            const newValue = e.target.checked;
                                                                            setCutSilenceMode(newValue);
                                                                            localStorage.setItem('workspace_cut_silence_mode', newValue.toString());
                                                                        }}
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            accentColor: 'var(--button-bg)'
                                                                        }}
                                                                    />
                                                                    <span style={{marginLeft:'8px'}}>Cut silence in Voiceover</span>
                                                                </label>
                                                            </div>
                                                            )}

                                                            {currentUserId === BOUKAR_USER_ID && (
                                                            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <label style={{ 
                                                                    display: 'flex', 
                                                                    alignItems: 'center', 
                                                                    cursor: 'pointer',
                                                                    userSelect: 'none',
                                                                    fontSize: '14px',
                                                                    color: 'var(--text-color)'
                                                                }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={cutMoreSilenceMode}
                                                                        onChange={(e) => {
                                                                            const newValue = e.target.checked;
                                                                            setCutMoreSilenceMode(newValue);
                                                                            localStorage.setItem('workspace_cut_more_silence_mode', newValue.toString());
                                                                        }}
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            accentColor: 'var(--button-bg)'
                                                                        }}
                                                                    />
                                                                    <span style={{marginLeft:'8px'}}>Cut more silence</span>
                                                                </label>
                                                            </div>
                                                            )}

                                                            {currentUserId === BOUKAR_USER_ID && (
                                                            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <label style={{ 
                                                                    display: 'flex', 
                                                                    alignItems: 'center', 
                                                                    cursor: 'pointer',
                                                                    userSelect: 'none',
                                                                    fontSize: '14px',
                                                                    color: 'var(--text-color)'
                                                                }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={aiSoundEffects}
                                                                        onChange={(e) => {
                                                                            const newValue = e.target.checked;
                                                                            setAiSoundEffects(newValue);
                                                                            localStorage.setItem('workspace_ai_sound_effects', newValue.toString());
                                                                        }}
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            accentColor: 'var(--button-bg)'
                                                                        }}
                                                                    />
                                                                    <span style={{marginLeft:'8px'}}>AI sound effects</span>
                                                                </label>
                                                            </div>
                                                            )}

                                                            {/* Music Selection */}
                                                            <div style={{ marginBottom: '12px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        id="useMusic"
                                                                        checked={useMusic}
                                                                        onChange={(e) => {
                                                                            const newValue = e.target.checked;
                                                                            setUseMusic(newValue);
                                                                            localStorage.setItem('workspace_use_music', newValue.toString());
                                                                        }}
                                                                        style={{ cursor: 'pointer',
                                                                            accentColor: 'var(--button-bg)' }}
                                                                    />
                                                                    <label htmlFor="useMusic" style={{ fontSize: '14px', cursor: 'pointer' }}>
                                                                        Use background music
                                                                    </label>
                                                                </div>
                                                                {useMusic && (
                                                                    <div style={{ position: 'relative' }} data-music-dropdown>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setShowMusicDropdown(!showMusicDropdown)}
                                                                            style={{
                                                                                width: '100%',
                                                                                padding: '8px 12px',
                                                                                borderRadius: '6px',
                                                                                border: '1px solid var(--input-border)',
                                                                                background: 'var(--input-bg)',
                                                                                color: 'var(--input-text)',
                                                                                fontSize: '14px',
                                                                                cursor: 'pointer',
                                                                                display: 'flex',
                                                                                justifyContent: 'space-between',
                                                                                alignItems: 'center',
                                                                                textAlign: 'left'
                                                                            }}
                                                                        >
                                                                            <span>
                                                                                {selectedMusicId ? 
                                                                                    availableMusic.find(m => m._id === selectedMusicId)?.name || 'Select music...' 
                                                                                    : lastUsedMusicName ? 
                                                                                        `${lastUsedMusicName}` 
                                                                                        : 'Random music'
                                                                                }
                                                                            </span>
                                                                            <span style={{ fontSize: '12px', opacity: 0.7 }}>
                                                                                {showMusicDropdown ? '▲' : '▼'}
                                                                            </span>
                                                                        </button>
                                                                        
                                                                        {showMusicDropdown && (
                                                                            <div style={{
                                                                                position: 'absolute',
                                                                                top: '100%',
                                                                                left: 10,
                                                                                right: 10,
                                                                                backgroundColor: 'var(--input-bg)',
                                                                                border: '1px solid var(--input-border)',
                                                                                borderRadius: '6px',
                                                                                marginTop: '2px',
                                                                                zIndex: 1000,
                                                                                maxHeight: '200px',
                                                                                overflowY: 'auto',
                                                                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.5)'
                                                                            }}>
                                                                                {/* Random Music Option */}
                                                                                <div
                                                                                    style={{
                                                                                        padding: '8px 12px',
                                                                                        cursor: 'pointer',
                                                                                        display: 'flex',
                                                                                        justifyContent: 'space-between',
                                                                                        alignItems: 'center',
                                                                                        borderBottom: '1px solid var(--input-border)',
                                                                                        backgroundColor: selectedMusicId === '' ? 'var(--button-bg)' : 'transparent',
                                                                                        color: selectedMusicId === '' ? 'var(--button-text)' : 'var(--text-color)'
                                                                                    }}
                                                                                    onClick={() => {
                                                                                        handleMusicChange('');
                                                                                        setShowMusicDropdown(false);
                                                                                    }}
                                                                                >
                                                                                    <span style={{ fontSize: '14px' }}>Random music</span>
                                                                                </div>
                                                                                
                                                                                {/* Music Options */}
                                                                                {[...availableMusic].sort((a, b) => {
                                                                                    // Sort starred songs first
                                                                                    if (a.isStarred && !b.isStarred) return -1;
                                                                                    if (!a.isStarred && b.isStarred) return 1;
                                                                                    // Then sort alphabetically
                                                                                    const nameA = (a.name || '').toLowerCase();
                                                                                    const nameB = (b.name || '').toLowerCase();
                                                                                    return nameA.localeCompare(nameB);
                                                                                }).map(music => (
                                                                                    <div
                                                                                        key={music._id}
                                                                                        style={{
                                                                                            padding: '8px 12px',
                                                                                            cursor: 'pointer',
                                                                                            display: 'flex',
                                                                                            justifyContent: 'space-between',
                                                                                            alignItems: 'center',
                                                                                            borderBottom: '1px solid var(--input-border)',
                                                                                            backgroundColor: selectedMusicId === music._id ? 'var(--button-bg)' : 'transparent',
                                                                                            color: selectedMusicId === music._id ? 'var(--button-text)' : 'var(--text-color)'
                                                                                        }}
                                                                                        onClick={() => {
                                                                                            handleMusicChange(music._id);
                                                                                            setShowMusicDropdown(false);
                                                                                        }}
                                                                                    >
                                                                                        <span style={{ fontSize: '14px', flex: 1 }}>{music.name}</span>
                                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={(e) => handleToggleMusicStar(music._id, e)}
                                                                                                style={{
                                                                                                    background: 'none',
                                                                                                    border: 'none',
                                                                                                    padding: '2px',
                                                                                                    cursor: 'pointer',
                                                                                                    display: 'flex',
                                                                                                    alignItems: 'center',
                                                                                                    justifyContent: 'center',
                                                                                                    color: music.isStarred ? '#ffd700' : 'var(--text-secondary)',
                                                                                                    opacity: music.isStarred ? 1 : 0.5,
                                                                                                    transition: 'all 0.2s'
                                                                                                }}
                                                                                                onMouseOver={(e) => {
                                                                                                    if (!music.isStarred) {
                                                                                                        e.currentTarget.style.opacity = '1';
                                                                                                        e.currentTarget.style.color = '#ffd700';
                                                                                                    }
                                                                                                }}
                                                                                                onMouseOut={(e) => {
                                                                                                    if (!music.isStarred) {
                                                                                                        e.currentTarget.style.opacity = '0.5';
                                                                                                        e.currentTarget.style.color = 'var(--text-secondary)';
                                                                                                    }
                                                                                                }}
                                                                                            >
                                                                                                <Star size={18} fill={music.isStarred ? '#ffd700' : 'none'} />
                                                                                            </button>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    handleMusicPreview(music._id, music.url);
                                                                                                }}
                                                                                                style={{
                                                                                                    padding: '4px 6px',
                                                                                                    backgroundColor: playingMusicId === music._id ? '#ff4444' : 'var(--button-bg)',
                                                                                                    color: 'white',
                                                                                                    border: '1px solid var(--input-border)',
                                                                                                    borderRadius: '4px',
                                                                                                    cursor: 'pointer',
                                                                                                    fontSize: '12px',
                                                                                                    display: 'flex',
                                                                                                    alignItems: 'center',
                                                                                                    gap: '4px',
                                                                                                    height: '22px',
                                                                                                    width: '22px'
                                                                                                }}
                                                                                            >
                                                                                                {playingMusicId === music._id ? '⏹' : '▶'}
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>

                                                        </div>

                                                        <div
                                                            aria-hidden="true"
                                                            style={{
                                                                height: '1px',
                                                                background: 'var(--border-primary)',
                                                                margin: '20px 0 16px',
                                                                opacity: 0.85,
                                                            }}
                                                        />
                                                        <div
                                                            data-studio-section="tags"
                                                            style={{ scrollMarginTop: '8px' }}
                                                        >
                                                            <h5 style={{
                                                                margin: '0 0 14px',
                                                                fontSize: '12px',
                                                                fontWeight: 700,
                                                                letterSpacing: '0.04em',
                                                                textTransform: 'uppercase',
                                                                color: 'var(--text-secondary)',
                                                            }}>
                                                                Tags
                                                            </h5>

                                                            {/* Tag Settings */}
                                                            <div style={{ marginBottom: '12px' }}>
                                                                <div style={{display: 'flex'}}>
                                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '14px', marginRight:'6px' }}>
                                                                        Include raw videos with these tags <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>(optional)</span>
                                                                    </label>
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            setShowIncludeTagsInfo(!showIncludeTagsInfo);
                                                                        }}
                                                                        style={{
                                                                            background: 'none',
                                                                            border: 'none',
                                                                            cursor: 'pointer',
                                                                            padding: '2px',
                                                                            borderRadius: '50%',
                                                                            width: '18px',
                                                                            height: '18px',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            fontSize: '12px',
                                                                            fontWeight: 'bold',
                                                                            color: 'var(--text-secondary)',
                                                                            backgroundColor: 'var(--background-tertiary)',
                                                                            transition: 'all 0.2s ease'
                                                                        }}
                                                                        onMouseOver={(e) => {
                                                                            e.target.style.backgroundColor = 'var(--button-bg)';
                                                                            e.target.style.color = 'var(--button-text)';
                                                                        }}
                                                                        onMouseOut={(e) => {
                                                                            e.target.style.backgroundColor = 'var(--background-tertiary)';
                                                                            e.target.style.color = 'var(--text-secondary)';
                                                                        }}
                                                                    >
                                                                        i
                                                                    </button>
                                                                </div>
                                                                {showIncludeTagsInfo && (
                                                                    <div style={{
                                                                        backgroundColor: 'var(--background-tertiary)',
                                                                        border: '1px solid var(--border-primary)',
                                                                        borderRadius: '6px',
                                                                        padding: '12px 32px 12px 12px',
                                                                        marginBottom: '8px',
                                                                        fontSize: '13px',
                                                                        color: 'var(--text-primary)',
                                                                        lineHeight: '1.4',
                                                                        position: 'relative'
                                                                    }}>
                                                                        <button
                                                                            onClick={() => setShowIncludeTagsInfo(false)}
                                                                            style={{
                                                                                position: 'absolute',
                                                                                top: '8px',
                                                                                right: '8px',
                                                                                background: 'none',
                                                                                border: 'none',
                                                                                cursor: 'pointer',
                                                                                fontSize: '16px',
                                                                                color: 'var(--text-secondary)',
                                                                                padding: '2px',
                                                                                borderRadius: '50%',
                                                                                width: '20px',
                                                                                height: '20px',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                transition: 'all 0.2s ease'
                                                                            }}
                                                                            onMouseOver={(e) => {
                                                                                e.target.style.backgroundColor = 'var(--border-primary)';
                                                                                e.target.style.color = 'var(--text-primary)';
                                                                            }}
                                                                            onMouseOut={(e) => {
                                                                                e.target.style.backgroundColor = 'transparent';
                                                                                e.target.style.color = 'var(--text-secondary)';
                                                                            }}
                                                                        >
                                                                            ×
                                                                        </button>
                                                                        Your uploaded videos have tags. Writing tags here will prioritize cuts from videos that contain these tags, ensuring your generated video uses the most relevant content.
                                                                    </div>
                                                                )}
                                                                <div className="video-tags">
                                                                    {includeTags.map(tag => (
                                                                        <span key={`include-${tag}`} className="tag">
                                                                            {tag}
                                                                            <button 
                                                                                className="remove-tag"
                                                                                onClick={() => handleRemoveIncludeTag(tag)}
                                                                            >
                                                                                <X size={14} style={{paddingTop: '3px'}}/>
                                                                            </button>
                                                                        </span>
                                                                    ))}
                                                                    <div className="add-tag" style={{ position: 'relative' }}>
                                                                        {renderTagInputWithSuggestions(
                                                                            'include-tag-skip',
                                                                            includeTagInput,
                                                                            (e) => setIncludeTagInput(e.target.value),
                                                                            (e) => {
                                                                                if (e.key === 'Enter') {
                                                                                    handleAddIncludeTag();
                                                                                }
                                                                            },
                                                                            handleAddIncludeTag,
                                                                            showIncludeTagSuggestions,
                                                                            setShowIncludeTagSuggestions,
                                                                            includeTagInputRef,
                                                                            "Add tag...",
                                                                            {},
                                                                            (tag) => {
                                                                                // Auto-add tag when clicked from dropdown
                                                                                if (!includeTags.includes(tag)) {
                                                                                    setIncludeTags(prev => [...prev, tag]);
                                                                                    setIncludeTagInput('');
                                                                                    setShowIncludeTagSuggestions(false);
                                                                                }
                                                                            }
                                                                        )}
                                                                        <button onClick={handleAddIncludeTag} style={{borderRadius: '20%', position: 'relative', marginLeft: '20px'}}>
                                                                            <Plus size={14} style={{paddingTop: '3px'}} />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div style={{ marginBottom: '12px' }}>
                                                                <div style={{display: 'flex'}}>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '14px', marginRight:'6px' }}>
                                                                        Exclude raw videos with these tags <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>(optional)</span>
                                                                    </label>
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            setShowExcludeTagsInfo(!showExcludeTagsInfo);
                                                                        }}
                                                                        style={{
                                                                            background: 'none',
                                                                            border: 'none',
                                                                            cursor: 'pointer',
                                                                            padding: '2px',
                                                                            borderRadius: '50%',
                                                                            width: '18px',
                                                                            height: '18px',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            fontSize: '12px',
                                                                            fontWeight: 'bold',
                                                                            color: 'var(--text-secondary)',
                                                                            backgroundColor: 'var(--background-tertiary)',
                                                                            transition: 'all 0.2s ease'
                                                                        }}
                                                                        onMouseOver={(e) => {
                                                                            e.target.style.backgroundColor = 'var(--button-bg)';
                                                                            e.target.style.color = 'var(--button-text)';
                                                                        }}
                                                                        onMouseOut={(e) => {
                                                                            e.target.style.backgroundColor = 'var(--background-tertiary)';
                                                                            e.target.style.color = 'var(--text-secondary)';
                                                                        }}
                                                                    >
                                                                        i
                                                                    </button>
                                                                </div>
                                                                {showExcludeTagsInfo && (
                                                                    <div style={{
                                                                        backgroundColor: 'var(--background-tertiary)',
                                                                        border: '1px solid var(--border-primary)',
                                                                        borderRadius: '6px',
                                                                        padding: '12px 32px 12px 12px',
                                                                        marginBottom: '8px',
                                                                        fontSize: '13px',
                                                                        color: 'var(--text-primary)',
                                                                        lineHeight: '1.4',
                                                                        position: 'relative'
                                                                    }}>
                                                                        <button
                                                                            onClick={() => setShowExcludeTagsInfo(false)}
                                                                            style={{
                                                                                position: 'absolute',
                                                                                top: '8px',
                                                                                right: '8px',
                                                                                background: 'none',
                                                                                border: 'none',
                                                                                cursor: 'pointer',
                                                                                fontSize: '16px',
                                                                                color: 'var(--text-secondary)',
                                                                                padding: '2px',
                                                                                borderRadius: '50%',
                                                                                width: '20px',
                                                                                height: '20px',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                transition: 'all 0.2s ease',
                                                                            }}
                                                                            onMouseOver={(e) => {
                                                                                e.target.style.backgroundColor = 'var(--border-primary)';
                                                                                e.target.style.color = 'var(--text-primary)';
                                                                            }}
                                                                            onMouseOut={(e) => {
                                                                                e.target.style.backgroundColor = 'transparent';
                                                                                e.target.style.color = 'var(--text-secondary)';
                                                                            }}
                                                                        >
                                                                            ×
                                                                        </button>
                                                                        Your uploaded videos have tags. Writing tags here will avoid cuts from videos that contain these tags, ensuring your generated video excludes unwanted content.
                                                                    </div>
                                                                )}
                                                                <div className="video-tags">
                                                                    {excludeTags.map(tag => (
                                                                        <span key={`exclude-${tag}`} className="tag">
                                                                            {tag}
                                                                            <button 
                                                                                className="remove-tag"
                                                                                onClick={() => handleRemoveExcludeTag(tag)}
                                                                            >
                                                                                <X size={14} style={{paddingTop: '3px'}}/>
                                                                            </button>
                                                                        </span>
                                                                    ))}
                                                                    <div className="add-tag" style={{ position: 'relative' }}>
                                                                        {renderTagInputWithSuggestions(
                                                                            'exclude-tag-skip',
                                                                            excludeTagInput,
                                                                            (e) => setExcludeTagInput(e.target.value),
                                                                            (e) => {
                                                                                if (e.key === 'Enter') {
                                                                                    handleAddExcludeTag();
                                                                                }
                                                                            },
                                                                            handleAddExcludeTag,
                                                                            showExcludeTagSuggestions,
                                                                            setShowExcludeTagSuggestions,
                                                                            excludeTagInputRef,
                                                                            "Add tag...",
                                                                            {},
                                                                            (tag) => {
                                                                                // Auto-add tag when clicked from dropdown
                                                                                if (!excludeTags.includes(tag)) {
                                                                                    setExcludeTags(prev => [...prev, tag]);
                                                                                    setExcludeTagInput('');
                                                                                    setShowExcludeTagSuggestions(false);
                                                                                }
                                                                            }
                                                                        )}
                                                                        <button onClick={handleAddExcludeTag} style={{borderRadius: '20%', position: 'relative', marginLeft: '20px'}}>
                                                                            <Plus size={14} style={{paddingTop: '3px'}} />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                {matchingCutsCount !== null && (
                                                                    <div style={{ marginTop: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                                                                        {matchingCutsCount === 0 ? (
                                                                            <span style={{ color: '#dc3545' }}>No cuts match your criteria.</span>
                                                                        ) : (
                                                                            <span style={{border: '1px solid var(--input-border)', borderRadius: '6px', padding: '4px 8px'}}>Cuts matching your tagging criteria: <strong>{matchingCutsCount}</strong> cut{matchingCutsCount !== 1 ? 's' : ''}</span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>

                                                        </div>

                                                    {!studioSettingsNarrow && (
                                                        <div
                                                            aria-hidden="true"
                                                            style={{
                                                                pointerEvents: 'none',
                                                                position: 'absolute',
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                height: 56,
                                                                background: darkMode
                                                                    ? 'linear-gradient(to bottom, rgba(22, 27, 39, 0), var(--input-bg))'
                                                                    : 'linear-gradient(to bottom, rgba(255, 255, 255, 0), var(--input-bg))',
                                                                zIndex: 2,
                                                            }}
                                                        />
                                                    )}
                                                    </div>
                                            
                                                    </div>
                                                </div>

                                                {/* Right: video preview */}
                                                <div
                                                    ref={videoSectionRef}
                                                    style={{
                                                        flex: studioSettingsNarrow ? '1 1 auto' : '0 0 260px',
                                                        width: studioSettingsNarrow ? '100%' : '260px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        gap: '12px',
                                                    }}
                                                >
                                                    {rawTotal === 0 && (
                                                        <div style={{
                                                            backgroundColor: darkMode ? '#3d3511' : '#fff3cd',
                                                            border: darkMode ? '1px solid #5c5016' : '1px solid #ffeaa7',
                                                            borderRadius: '6px',
                                                            padding: '10px',
                                                            fontSize: '12px',
                                                            color: darkMode ? '#e6d98a' : '#856404',
                                                            width: '100%',
                                                        }}>
                                                            <strong>No source videos yet.</strong>{' '}
                                                            <button
                                                                type="button"
                                                                onClick={() => handleTabChange('raw')}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    color: 'inherit',
                                                                    textDecoration: 'underline',
                                                                    cursor: 'pointer',
                                                                    padding: 0,
                                                                    fontSize: '12px',
                                                                    fontWeight: 'bold',
                                                                }}
                                                            >
                                                                Go to Raw Videos →
                                                            </button>
                                                        </div>
                                                    )}
                                                    {rawTotal > 0 && cutsTotal === 0 && (
                                                        <div style={{
                                                            backgroundColor: darkMode ? '#3d3511' : '#fff3cd',
                                                            border: darkMode ? '1px solid #5c5016' : '1px solid #ffeaa7',
                                                            borderRadius: '6px',
                                                            padding: '10px',
                                                            fontSize: '12px',
                                                            color: darkMode ? '#e6d98a' : '#856404',
                                                            width: '100%',
                                                        }}>
                                                            <strong>No cuts yet.</strong>{' '}
                                                            <button
                                                                type="button"
                                                                onClick={() => handleTabChange('cuts')}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    color: 'inherit',
                                                                    textDecoration: 'underline',
                                                                    cursor: 'pointer',
                                                                    padding: 0,
                                                                    fontSize: '12px',
                                                                    fontWeight: 'bold',
                                                                }}
                                                            >
                                                                Go to Cuts →
                                                            </button>
                                                        </div>
                                                    )}
                                                    {voiceoverDuration > 0 && cutsTotal > 0 && (() => {
                                                        const reqSec = voiceoverDuration;
                                                        const effSecPerCut = fastPacing ? 1 : 2;
                                                        const effDurPerCut = slowMotion ? effSecPerCut * (4 / 3) : effSecPerCut;
                                                        return Math.ceil(reqSec / effDurPerCut) > cutsTotal;
                                                    })() && (
                                                        <div style={{
                                                            backgroundColor: darkMode ? '#3d3511' : '#fff3cd',
                                                            border: darkMode ? '1px solid #5c5016' : '1px solid #ffeaa7',
                                                            borderRadius: '6px',
                                                            padding: '10px',
                                                            fontSize: '12px',
                                                            color: darkMode ? '#e6d98a' : '#856404',
                                                            width: '100%',
                                                        }}>
                                                            <strong>Insufficient cuts:</strong> video will reuse cuts to fill the duration.
                                                        </div>
                                                    )}

                                                    <div
                                                        style={{
                                                            width: '100%',
                                                            maxWidth: '260px',
                                                            aspectRatio: '9 / 16',
                                                            maxHeight: '460px',
                                                            borderRadius: '16px',
                                                            overflow: (studioGeneratingVideo || (studioVideoProgress > 0 && studioVideoProgress < 100))
                                                                && carouselCuts.length > 0
                                                                && studioSettingsNarrow
                                                                ? 'visible'
                                                                : 'hidden',
                                                            backgroundColor: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                                                            border: '1px solid var(--input-border)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            position: 'relative',
                                                        }}
                                                    >
                                                        {studioVideoUrl ? (
                                                            <video
                                                                key={`${studioVideoUrl}-${studioVideoUrlTimestamp}`}
                                                                src={`${studioVideoUrl}${studioVideoUrl.includes('?') ? '&' : '?'}t=${studioVideoUrlTimestamp}`}
                                                                controls
                                                                preload="metadata"
                                                                crossOrigin="anonymous"
                                                                playsInline
                                                                onContextMenu={(e) => e.preventDefault()}
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover',
                                                                    borderRadius: '16px',
                                                                }}
                                                                onLoadedMetadata={(e) => {
                                                                    if (e.target.duration > 0) {
                                                                        e.target.currentTime = Math.min(e.target.duration / 2, 1);
                                                                    }
                                                                }}
                                                                onPlay={(e) => {
                                                                    if (!studioVideoHasPlayedRef.current && e.target.currentTime > 0.1) {
                                                                        e.target.currentTime = 0;
                                                                    }
                                                                    studioVideoHasPlayedRef.current = true;
                                                                }}
                                                            >
                                                                <source src={`${studioVideoUrl}${studioVideoUrl.includes('?') ? '&' : '?'}t=${studioVideoUrlTimestamp}`} type="video/mp4" />
                                                            </video>
                                                        ) : (
                                                            <div style={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                color: 'var(--text-secondary)',
                                                                padding: '16px',
                                                                textAlign: 'center',
                                                            }}>
                                                                {!((studioGeneratingVideo || (studioVideoProgress > 0 && studioVideoProgress < 100))
                                                                    && carouselCuts.length > 0) && (
                                                                    <Film size={28} style={{ opacity: 0.45 }} />
                                                                )}
                                                                <span style={{ fontSize: '12px', lineHeight: 1.4 }}>
                                                                    {(studioGeneratingVideo || (studioVideoProgress > 0 && studioVideoProgress < 100))
                                                                        ? ''
                                                                        : 'Video preview'}
                                                                </span>
                                                            </div>
                                                        )}

                                                        {/* Carousel overlaid in front of the preview while generating */}
                                                        {(studioGeneratingVideo || (studioVideoProgress > 0 && studioVideoProgress < 100))
                                                            && carouselCuts.length > 0 && (
                                                            <div
                                                                style={{
                                                                    position: 'absolute',
                                                                    zIndex: 2,
                                                                    ...( !studioSettingsNarrow
                                                                        ? {
                                                                            inset: 0,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            background: darkMode
                                                                                ? 'rgba(0,0,0,0.45)'
                                                                                : 'rgba(255,255,255,0.35)',
                                                                            borderRadius: '16px',
                                                                            padding: '4px',
                                                                        }
                                                                        : {
                                                                            left: '50%',
                                                                            top: '50%',
                                                                            transform: 'translate(-50%, -50%)',
                                                                            width: 'min(100vw - 24px, 800px)',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                        }),
                                                                }}
                                                            >
                                                                <SmoothScaleCarousel
                                                                    items={carouselCuts}
                                                                    compact={!studioSettingsNarrow}
                                                                />
                                                            </div>
                                                        )}
                                                        {(studioGeneratingVideo || (studioVideoProgress > 0 && studioVideoProgress < 100))
                                                            && carouselCuts.length > 0
                                                            && currentUserId === BOUKAR_USER_ID && (
                                                            <CarouselHerokuLogsOverlay />
                                                        )}
                                                    </div>

                                                    {(studioGeneratingVideo || (studioVideoProgress > 0 && studioVideoProgress < 100)) && (
                                                        <div style={{ width: '100%' }}>
                                                            <VideoGenerationSteps
                                                                progress={studioVideoProgress}
                                                                perStepProgress
                                                                showProgressBar
                                                                shimmer
                                                                includeAiSoundEffects={studioSfxStepsActive && !isNoScriptMode}
                                                                noScript={isNoScriptMode}
                                                            />
                                                        </div>
                                                    )}

                                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                                                        <button
                                                            className="main-button"
                                                            onClick={() => {
                                                                studioGenerateVideo();
                                                            }}
                                                            disabled={studioGeneratingVideo}
                                                            style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                                        >
                                                            {studioGeneratingVideo
                                                                ? <><span className="spinner-border" /> Generating…</>
                                                                : (studioVideoUrl ? 'Regenerate' : 'Generate Video')}
                                                        </button>
                                                        {studioGeneratingVideo && (
                                                            <button
                                                                className="tertiary-button"
                                                                onClick={cancelVideoGeneration}
                                                                style={{ padding: '8px 12px', minWidth: 'auto', flexShrink: 0 }}
                                                                title="Cancel video generation"
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        )}
                                                    </div>

                                                    {(studioVideoUrl || (useNewNle && studioVoiceoverUrl)) && (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                                                            {studioVideoUrl && (
                                                            <button
                                                                type="button"
                                                                onClick={openTimelineModal}
                                                                style={{
                                                                    padding: '8px 14px',
                                                                    fontSize: '13px',
                                                                    backgroundColor: 'var(--input-bg)',
                                                                    border: '1px solid var(--input-border)',
                                                                    borderRadius: '6px',
                                                                    color: 'var(--text-color)',
                                                                    cursor: 'pointer',
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    gap: '6px',
                                                                    width: '100%',
                                                                }}
                                                            >
                                                                <EditTimelineIcon size={16} />
                                                                Timeline
                                                            </button>
                                                            )}
                                                            {studioVideoUrl && (
                                                            <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                                                <button
                                                                    className="main-button"
                                                                    onClick={studioPublish}
                                                                    disabled={studioPublishingVideo || studioVideoSaved}
                                                                    style={{
                                                                        flex: 1,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        gap: '6px',
                                                                        opacity: studioPublishingVideo ? 0.7 : 1,
                                                                        cursor: (studioPublishingVideo || studioVideoSaved) ? 'not-allowed' : 'pointer',
                                                                        backgroundColor: studioVideoSaved ? '#28a745' : 'var(--button-bg)',
                                                                        color: studioVideoSaved ? 'white' : 'var(--button-text, #fff)',
                                                                        fontSize: '13px',
                                                                        padding: '8px 10px',
                                                                    }}
                                                                >
                                                                    {studioPublishingVideo && <span className="spinner-border" style={{ marginRight: '4px' }} />}
                                                                    {studioPublishingVideo ? 'Saving...' : studioVideoSaved ? 'Saved' : 'Save'}
                                                                    {studioVideoSaved && <Check size={14} />}
                                                                </button>
                                                                <button
                                                                    className="main-button"
                                                                    onClick={async () => {
                                                                        await handleStudioVideoDownload();
                                                                        await studioPublish();
                                                                    }}
                                                                    disabled={studioPublishingVideo}
                                                                    style={{
                                                                        flex: 1,
                                                                        fontSize: '13px',
                                                                        padding: '8px 10px',
                                                                        opacity: studioPublishingVideo ? 0.7 : 1,
                                                                        cursor: studioPublishingVideo ? 'not-allowed' : 'pointer',
                                                                    }}
                                                                >
                                                                    {studioPublishingVideo ? 'Saving…' : 'Download'}
                                                                </button>
                                                            </div>
                                                            )}
                                                            {lastUsedMusicName && (
                                                                <div style={{
                                                                    padding: '8px 10px',
                                                                    backgroundColor: 'var(--input-bg)',
                                                                    borderRadius: '6px',
                                                                    border: '1px solid var(--input-border)',
                                                                    fontSize: '12px',
                                                                    color: 'var(--text-secondary)',
                                                                }}>
                                                                    Music: <span style={{ color: 'var(--text-color)', fontWeight: 500 }}>{lastUsedMusicName}</span>
                                                                    {!musicSelectionChanged && (
                                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
                                                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                                                                <input
                                                                                    type="radio"
                                                                                    name="musicRegenerationPanel"
                                                                                    value="keep"
                                                                                    checked={musicRegenerationMode === 'keep'}
                                                                                    onChange={() => setMusicRegenerationMode('keep')}
                                                                                    style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                                                />
                                                                                <span>Keep same song</span>
                                                                            </label>
                                                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                                                                <input
                                                                                    type="radio"
                                                                                    name="musicRegenerationPanel"
                                                                                    value="random"
                                                                                    checked={musicRegenerationMode === 'random'}
                                                                                    onChange={() => {
                                                                                        setMusicRegenerationMode('random');
                                                                                        setSelectedMusicId('');
                                                                                        setMusicSelectionChanged(false);
                                                                                    }}
                                                                                    style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                                                />
                                                                                <span>Get another random</span>
                                                                            </label>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {isNoScriptMode && (
                                    <div 
                                        ref={videoSectionRef}
                                        style={{
                                            backgroundColor: 'var(--input-bg)',
                                            border: '1px solid var(--input-border)',
                                            borderRadius: '8px', padding: '12px', marginBottom: '12px'
                                        }}>
                                        <h4 style={{ marginTop: 0 }}>
                                            3 - Generate visual only (no script) video
                                            <span style={{ 
                                                marginLeft: '8px', 
                                                fontSize: '12px', 
                                                fontWeight: 'normal',
                                                color: darkMode ? '#fcb69f' : '#D2691E'
                                            }}>@VideoEditor</span>
                                        </h4>
                                        
                                        {/* Warning when there are no source videos */}
                                        {rawTotal === 0 && (
                                            <div style={{
                                                backgroundColor: darkMode ? '#3d3511' : '#fff3cd',
                                                border: darkMode ? '1px solid #5c5016' : '1px solid #ffeaa7',
                                                borderRadius: '6px',
                                                padding: '10px',
                                                marginBottom: '12px',
                                                fontSize: '13px',
                                                color: darkMode ? '#e6d98a' : '#856404',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '8px'
                                            }}>
                                                <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                                                <div style={{ flex: 1 }}>
                                                    <strong>No source videos yet.</strong> Upload at least one video in Raw Videos before generating.
                                                    <div style={{ marginTop: '8px' }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleTabChange('raw')}
                                                            style={{
                                                                backgroundColor: darkMode ? '#5c5016' : '#ffc107',
                                                                color: darkMode ? '#e6d98a' : '#856404',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                padding: '6px 12px',
                                                                fontSize: '12px',
                                                                cursor: 'pointer',
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            Go to Raw Videos →
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {/* Warning when there are videos but no cuts yet */}
                                        {rawTotal > 0 && cutsTotal === 0 && (
                                            <div style={{
                                                backgroundColor: darkMode ? '#3d3511' : '#fff3cd',
                                                border: darkMode ? '1px solid #5c5016' : '1px solid #ffeaa7',
                                                borderRadius: '6px',
                                                padding: '10px',
                                                marginBottom: '12px',
                                                fontSize: '13px',
                                                color: darkMode ? '#e6d98a' : '#856404',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '8px'
                                            }}>
                                                <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                                                <div style={{ flex: 1 }}>
                                                    <strong>No cuts yet.</strong> Merra AI's studio edits your raw content, cuts them, and assembles them into a new video. You need at least one cut to generate a video. Wait for cuts to finish processing, or generate cuts from your raw videos.
                                                    <div style={{ marginTop: '8px' }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleTabChange('cuts')}
                                                            style={{
                                                                backgroundColor: darkMode ? '#5c5016' : '#ffc107',
                                                                color: darkMode ? '#e6d98a' : '#856404',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                padding: '6px 12px',
                                                                fontSize: '12px',
                                                                cursor: 'pointer',
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            Go to Cuts →
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            <button className="main-button" onClick={studioGenerateNoScriptVideo} disabled={studioGeneratingVideo} style={{ flex: 1 }}>
                                                {studioGeneratingVideo ? <><span className="spinner-border" style={{marginRight:'5px'}}/> Generating…</> : ( studioVideoUrl ? 'Regenerate Video':'Generate Video')}
                                            </button>
                                            {studioGeneratingVideo && (
                                                <button 
                                                    className="tertiary-button" 
                                                    onClick={cancelVideoGeneration}
                                                    style={{ 
                                                        padding: '8px 12px',
                                                        minWidth: 'auto',
                                                        flexShrink: 0
                                                    }}
                                                    title="Cancel video generation"
                                                >
                                                    <X size={16} />
                                                </button>
                                            )}
                                        </div>
                                        {(studioGeneratingVideo || (studioVideoProgress > 0 && studioVideoProgress < 100)) && (
                                            <VideoGenerationSteps
                                                progress={studioVideoProgress}
                                                perStepProgress
                                                showProgressBar
                                                shimmer
                                                includeAiSoundEffects={studioSfxStepsActive && !isNoScriptMode}
                                                noScript={isNoScriptMode}
                                            />
                                        )}
                                        {(studioGeneratingVideo || (studioVideoProgress > 0 && studioVideoProgress < 100)) && carouselCuts.length > 0 && (
                                            <div>
                                                <SmoothScaleCarousel items={carouselCuts} />
                                            </div>
                                        )}
                                        {studioVideoUrl && (
                                            <div style={{ marginTop: '12px' }}>
                                                <div style={{ maxHeight: '500px', display: 'flex', justifyContent: 'center' }}>
                                                    <video 
                                                        key={`${studioVideoUrl}-${studioVideoUrlTimestamp}`} // Force reload when URL or timestamp changes
                                                        src={`${studioVideoUrl}${studioVideoUrl.includes('?') ? '&' : '?'}t=${studioVideoUrlTimestamp}`} // Cache-busting parameter
                                                        controls 
                                                        preload="metadata"
                                                        crossOrigin="anonymous"
                                                        playsInline
                                                        // muted
                                                        onContextMenu={(e) => e.preventDefault()}
                                                        style={{ maxHeight: '500px', alignSelf: 'center', borderRadius: '6px' }}
                                                        onLoadedMetadata={(e) => {
                                                            // Set preview to the middle of the video for better thumbnail
                                                            // This only affects the initial preview frame, not playback
                                                            if (e.target.duration > 0) {
                                                                e.target.currentTime = Math.min(e.target.duration / 2, 1);
                                                            }
                                                        }}
                                                        onPlay={(e) => {
                                                            // On first play, ensure video starts from beginning (if it was at preview position)
                                                            if (!studioVideoHasPlayedRef.current && e.target.currentTime > 0.1) {
                                                                e.target.currentTime = 0;
                                                            }
                                                            studioVideoHasPlayedRef.current = true;
                                                        }}
                                                    >
                                                        <source src={`${studioVideoUrl}${studioVideoUrl.includes('?') ? '&' : '?'}t=${studioVideoUrlTimestamp}`} type="video/mp4" />
                                                        Your browser does not support the video tag.
                                                    </video>
                                                </div>
                                                {//canAccessTimeline && (
                                                    <div style={{ marginTop: '8px' }}>
                                                        <button
                                                            type="button"
                                                            onClick={openTimelineModal}
                                                            style={{
                                                                padding: '8px 14px',
                                                                fontSize: '13px',
                                                                backgroundColor: 'var(--input-bg)',
                                                                border: '1px solid var(--input-border)',
                                                                borderRadius: '6px',
                                                                color: 'var(--text-color)',
                                                                cursor: 'pointer',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '6px'
                                                            }}
                                                        >
                                                            <EditTimelineIcon size={16} />
                                                            Open editor
                                                        </button>
                                                    </div>
                                                //)
                                                }
                                                {/* Music info and regeneration options */}
                                                {lastUsedMusicName && (
                                                    <div style={{ marginTop: '12px', padding: '8px 12px', backgroundColor: 'var(--input-bg)', borderRadius: '6px', border: '1px solid var(--input-border)' }}>
                                                        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                                                            Music: <span style={{ color: 'var(--text-color)', fontWeight: '500' }}>{lastUsedMusicName}</span>
                                                        </div>
                                                        {/* Radio buttons for music regeneration - only show if no explicit dropdown selection */}
                                                        {!musicSelectionChanged && (
                                                            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                                    <input
                                                                        type="radio"
                                                                        name="musicRegeneration"
                                                                        value="keep"
                                                                        checked={musicRegenerationMode === 'keep'}
                                                                        onChange={(e) => setMusicRegenerationMode('keep')}
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            accentColor: 'var(--button-bg)'
                                                                        }}
                                                                    />
                                                                    <span>Keep same song</span>
                                                                </label>
                                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                                    <input
                                                                        type="radio"
                                                                        name="musicRegeneration"
                                                                        value="random"
                                                                        checked={musicRegenerationMode === 'random'}
                                                                        onChange={(e) => {
                                                                            setMusicRegenerationMode('random');
                                                                            // Clear dropdown selection so random music is actually used
                                                                            setSelectedMusicId('');
                                                                            // Reset flag so radio buttons stay visible
                                                                            setMusicSelectionChanged(false);
                                                                        }}
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            accentColor: 'var(--button-bg)'
                                                                        }}
                                                                    />
                                                                    <span>Get another random</span>
                                                                </label>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {isNoScriptMode && studioVideoUrl && (
                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                        <button 
                                            className="main-button" 
                                            onClick={studioPublish}
                                            disabled={studioPublishingVideo || studioVideoSaved}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                opacity: (studioPublishingVideo) ? 0.7 : 1,
                                                cursor: (studioPublishingVideo || studioVideoSaved) ? 'not-allowed' : 'pointer',
                                                backgroundColor: studioVideoSaved ? '#28a745' : 'var(--button-bg)',
                                                color: studioVideoSaved ? 'white' : 'var(--button-text, #fff)'
                                            }}
                                        >
                                            {studioPublishingVideo && (
                                                <span className="spinner-border" style={{marginRight:'5px'}} />
                                            )}
                                            {studioPublishingVideo ? 'Saving...' : 
                                            studioVideoSaved ? 'Saved ' : 
                                            'Save Video'}
                                            {studioVideoSaved && (
                                                <span style={{marginLeft:'5px'}}><Check size={16} /></span>
                                            )}
                                        </button>
                                        <button 
                                            className="main-button" 
                                            onClick={async () => {
                                                // First download the video
                                                await handleStudioVideoDownload();
                                                // Then publish it to generated videos
                                                await studioPublish();
                                            }}
                                            disabled={studioPublishingVideo}
                                            style={{ 
                                                textDecoration: 'none', 
                                                padding: '8px 12px', 
                                                borderRadius: '10px', 
                                                border: '1px solid var(--input-border)', 
                                                color: 'var(--text-color)',
                                                backgroundColor: 'var(--button-bg)',
                                                cursor: studioPublishingVideo ? 'not-allowed' : 'pointer',
                                                opacity: studioPublishingVideo ? 0.7 : 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            {studioPublishingVideo ? (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                                    <span className="spinner-border" />
                                                    Saving…
                                                </span>
                                            ) : (
                                                'Download & Save'
                                            )}
                                        </button>
                                    </div>
                                )}
                                </>
                                )}
                            </div>
                            {/* Error Messages */}
                            {errors.length > 0 && (
                                <div className="error-container">
                                    {errors.map((error, index) => (
                                        <div key={`error-${index}`} className="error-banner" style={{display: 'flex', alignItems: 'center', marginBottom: '10px'}}>
                                            <AlertCircle size={20} style={{marginRight: '5px'}} />
                                            {error}
                                            <button 
                                                className="error-close"
                                                onClick={() => clearError(index)}
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ))}
                                    <button 
                                        className="clear-errors"
                                        onClick={clearErrors}
                                    >
                                        Clear All Errors
                                    </button>
                                </div>
                            )}
                        </div>
                    </> /* END OF STUDIO TAB CONTENT */
                    
                )}
                        
                {activeTab === 'cuts' && (
                    <div className="cuts-tab">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3>Workspace Cuts</h3>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <button
                                    onClick={async () => {
                                        if (!currentWorkspace?._id || cutsLoading) return;
                                        setCutsLoading(true);
                                        try {
                                            hiddenCutsRef.current.clear();
                                            await fetchWorkspaceCuts(1, false);
                                        } catch (error) {
                                            console.error('Error refreshing cuts:', error);
                                            setErrors(prev => [...prev, 'Failed to refresh cuts: ' + (error.response?.data?.error || error.message)]);
                                        } finally {
                                            setCutsLoading(false);
                                        }
                                    }}
                                    disabled={cutsLoading}
                                    style={{
                                        padding: '6px 12px',
                                        fontSize: '12px',
                                        backgroundColor: 'var(--button-bg)',
                                        color: 'var(--button-text)',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: cutsLoading ? 'not-allowed' : 'pointer',
                                        opacity: cutsLoading ? 0.6 : 1
                                    }}
                                >
                                    {cutsLoading ? 'Loading...' : 'Refresh'}
                                </button>
                            {cutsLoading && <span className="spinner-border" />}
                            </div>
                        </div>
                        
                        {/* Tag Filter */}
                        <div style={{ marginBottom: '16px', position: 'relative', width: '100%', maxWidth: '400px' }}>
                            {(() => {
                                const filterInputId = 'cuts-tag-filter';
                                return renderTagInputWithSuggestions(
                                    filterInputId,
                                    cutsTagFilter,
                                    (e) => setCutsTagFilter(e.target.value),
                                    () => {},
                                    () => {},
                                    tagSuggestionDropdowns[filterInputId] || false,
                                    (show) => setTagSuggestionDropdowns(prev => ({
                                        ...prev,
                                        [filterInputId]: show
                                    })),
                                    tagInputRefs.current[filterInputId],
                                    "Filter cuts by tag...",
                                    {
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        border: '1px solid var(--input-border)',
                                        background: 'var(--input-bg)',
                                        color: 'var(--input-text)',
                                        fontSize: '14px',
                                        boxSizing: 'border-box'
                                    }
                                );
                            })()}
                        </div>
                        
                        {/* Show message when cuts are being generated */}
                        {isRefreshingCuts && (
                            <div style={{
                                backgroundColor: 'var(--background-tertiary)',
                                border: '1px solid var(--border-primary)',
                                borderRadius: '6px',
                                padding: '12px',
                                marginBottom: '16px',
                                fontSize: '14px',
                                color: 'var(--text-primary)',
                                textAlign: 'center'
                            }}>
                                🔄 Generating cuts from uploaded videos... This may take a few minutes.
                            </div>
                        )}
                        {cutsLoading && cutsPage === 1 && workspaceCuts.length === 0 ? (
                            <div className="cuts-grid" style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', 
                                gap: '12px' 
                            }}>
                                {[...Array(8)].map((_, index) => (
                                    <div 
                                        key={`cut-shimmer-${index}`} 
                                        style={{
                                            border: '1px solid var(--input-border)',
                                            backgroundColor: 'var(--input-bg)',
                                            borderRadius: '8px',
                                            padding: '12px'
                                        }}
                                    >
                                        {/* Video preview skeleton */}
                                        <div 
                                            className="shimmer" 
                                            style={{ 
                                                width: '100%', 
                                                aspectRatio: '16/9',
                                                borderRadius: '4px', 
                                                marginBottom: '8px',
                                                backgroundColor: 'var(--input-border)'
                                            }}
                                        />
                                        {/* Name and button area skeleton */}
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center',
                                            marginBottom: '4px'
                                        }}>
                                            <div 
                                                className="shimmer" 
                                                style={{
                                                    height: '14px',
                                                    width: '60%',
                                                    borderRadius: '4px',
                                                    backgroundColor: 'var(--input-border)'
                                                }}
                                            />
                                            <div 
                                                className="shimmer" 
                                                style={{
                                                    height: '24px',
                                                    width: '50px',
                                                    borderRadius: '4px',
                                                    backgroundColor: 'var(--input-border)'
                                                }}
                                            />
                                        </div>
                                        {/* Optional description skeleton */}
                                        {index % 3 === 0 && (
                                            <div 
                                                className="shimmer" 
                                                style={{
                                                    height: '12px',
                                                    width: '80%',
                                                    borderRadius: '4px',
                                                    marginTop: '4px',
                                                    backgroundColor: 'var(--input-border)'
                                                }}
                                            />
                                        )}
                                        {/* Optional tags skeleton */}
                                        {index % 2 === 0 && (
                                            <div style={{ 
                                                display: 'flex', 
                                                flexWrap: 'wrap',
                                                gap: '4px',
                                                marginTop: '8px'
                                            }}>
                                                {[...Array(2)].map((_, tagIndex) => (
                                                    <div 
                                                        key={tagIndex}
                                                        className="shimmer" 
                                                        style={{
                                                            height: '18px',
                                                            width: '50px',
                                                            borderRadius: '3px',
                                                            backgroundColor: 'var(--input-border)'
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : workspaceCuts.length === 0 && !cutsLoading ? (
                            <div style={{ 
                                border: '1px dashed var(--input-border)',
                                backgroundColor: 'var(--input-bg)',
                                borderRadius: '8px',
                                padding: '24px',
                                textAlign: 'center',
                                color: 'var(--text-color)',
                                opacity: 0.8
                            }}>
                                No visible cuts in this workspace.
                            </div>
                        ) : (
                            <div className="cuts-grid" style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', 
                                gap: '12px' 
                            }}>
                                {(() => {
                                    const filteredCuts = workspaceCuts.filter(cut => {
                                        if (!cutsTagFilter.trim()) return true;
                                        const filterLower = cutsTagFilter.toLowerCase().trim();
                                        return cut.tags && cut.tags.some(tag =>
                                            tag.toLowerCase().includes(filterLower)
                                        );
                                    });
                                    return filteredCuts.map((cut, cutIndex) => (
                                    <div 
                                        key={cut._id} 
                                        style={{
                                            border: '1px solid var(--input-border)',
                                            backgroundColor: 'var(--input-bg)',
                                            borderRadius: '8px',
                                            padding: '12px'
                                        }}
                                        onMouseEnter={(e) => {
                                            const video = e.currentTarget.querySelector('video');
                                            if (video) {
                                                video.currentTime = 0;
                                                video.play().catch(err => {
                                                    console.log('Video autoplay prevented:', err);
                                                });
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            const video = e.currentTarget.querySelector('video');
                                            if (video) {
                                                video.pause();
                                                video.currentTime = 0;
                                            }
                                        }}
                                    >
                                        <video 
                                            src={cut.url} 
                                            controls 
                                            preload="metadata"
                                            muted
                                            loop
                                            style={{ 
                                                width: '100%', 
                                                borderRadius: '4px', 
                                                marginBottom: '8px' 
                                            }} 
                                        />
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'flex-start',
                                            gap: '8px'
                                        }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                fontSize: '13px',
                                                fontWeight: 700,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                color: 'var(--text-color)'
                                            }}>
                                                {cut.name}
                                            </div>
                                            <div style={{
                                                fontSize: '12px',
                                                color: 'var(--text-secondary)',
                                                marginTop: '2px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {cut.description || (cut.autoGenerated ? 'Auto-generated cut' : 'Cut')}
                                            </div>
                                            <div style={{
                                                fontSize: '11px',
                                                color: '#958DF1',
                                                marginTop: '2px'
                                            }}>
                                                {cutIndex + 1}/{filteredCuts.length}
                                                {typeof cut.duration === 'number' ? ` • ${Number(cut.duration).toFixed(2)}s` : ''}
                                            </div>
                                        </div>
                                            <button 
                                                onClick={() => handleHideCut(cut._id)}
                                                style={{ 
                                                    backgroundColor: '#dc3545',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    padding: '4px 8px',
                                                    fontSize: '12px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    flexShrink: 0
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#c82333';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#dc3545';
                                                }}
                                                title="Hide cut"
                                            >
                                                Hide
                                            </button>
                                        </div>
                                        {cut.tags && cut.tags.length > 0 && (
                                            <div style={{ 
                                                display: 'flex', 
                                                flexWrap: 'wrap',
                                                gap: '4px',
                                                marginTop: '8px'
                                            }}>
                                                {cut.tags.slice(0, 3).map(tag => (
                                                    <span key={tag} style={{
                                                        backgroundColor: darkMode ? 'rgba(74, 144, 226, 0.25)' : 'rgba(74, 144, 226, 0.15)',
                                                        color: darkMode ? '#e8f0fe' : '#1a56a0',
                                                        fontSize: '10px',
                                                        padding: '2px 6px',
                                                        borderRadius: '3px',
                                                        border: darkMode ? '1px solid rgba(74, 144, 226, 0.35)' : '1px solid rgba(74, 144, 226, 0.25)',
                                                    }}>
                                                        {tag}
                                                    </span>
                                                ))}
                                                {cut.tags.length > 3 && (
                                                    <span style={{
                                                        fontSize: '10px',
                                                        color: 'var(--text-secondary)'
                                                    }}>
                                                        +{cut.tags.length - 3} more
                                                    </span>
                                                )}
                                                </div>
                                        )}
                                    </div>
                                    ));
                                })()}
                            </div>
                        )}
                        {/* Infinite scroll trigger */}
                        {cutsHasMore && <div ref={cutsLoadMoreRef} style={{ height: '1px', marginTop: '20px' }} />}
                        {cutsLoading && workspaceCuts.length > 0 && (
                            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>
                                <span className="spinner-border" style={{ marginRight: '8px' }} />
                                Loading more cuts...
                            </div>
                        )}
                        {!cutsHasMore && workspaceCuts.length > 0 && !cutsLoading && (
                            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)', fontSize: '14px' }}>
                                All cuts loaded
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'profile' && (
                    <div>
                        <h3>Workspace Profile</h3>

                        <div className="profile-tab" style={{backgroundColor: 'var(--input-bg)', padding: '20px', borderRadius: '8px', border: '1px solid var(--input-border)'}}>

                            <div className="profile-header-row">
                                {(['673247877e4ac0a824cbac0d'].includes(currentUserId) || currentUserId === BOUKAR_USER_ID) && (
                                    <div className="profile-header-row__actions">
                                        {['673247877e4ac0a824cbac0d'].includes(currentUserId) && (
                                            <button
                                                type="button"
                                                onClick={handleResetOnboarding}
                                                className="profile-header-row__action-btn"
                                                style={{ borderColor: darkMode ? '#444' : undefined }}
                                            >
                                                <RotateCcw size={15} />
                                                Reset onboarding
                                            </button>
                                        )}
                                        {currentUserId === BOUKAR_USER_ID && (
                                            <button
                                                type="button"
                                                onClick={() => navigate('/workspace/covers')}
                                                className="profile-header-row__action-btn"
                                                style={{ borderColor: darkMode ? '#444' : undefined }}
                                            >
                                                <ImageIcon size={15} />
                                                Go to covers
                                            </button>
                                        )}
                                    </div>
                                )}

                                {['673247877e4ac0a824cbac0d'].includes(currentUserId) && (
                                    <div className="profile-header-row__avatar">
                                        <input
                                            type="file"
                                            ref={workspaceProfilePictureInputRef}
                                            accept="image/jpeg,image/png,image/gif,image/webp"
                                            style={{ display: 'none' }}
                                            onChange={async (e) => {
                                                const file = e.target.files?.[0];
                                                if (!file || !currentWorkspace?._id) return;
                                                setWorkspaceProfilePictureError('');
                                                setWorkspaceProfilePictureUploading(true);
                                                try {
                                                    const formData = new FormData();
                                                    formData.append('picture', file);
                                                    const res = await api.uploadWorkspaceProfilePicture(currentWorkspace._id, formData);
                                                    if (res.data?.profilePicture) {
                                                        await updateWorkspace(currentWorkspace._id, { profilePicture: res.data.profilePicture });
                                                    }
                                                } catch (err) {
                                                    setWorkspaceProfilePictureError(err.response?.data?.error || 'Failed to upload picture');
                                                } finally {
                                                    setWorkspaceProfilePictureUploading(false);
                                                    e.target.value = '';
                                                }
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => !workspaceProfilePictureUploading && workspaceProfilePictureInputRef.current?.click()}
                                            disabled={workspaceProfilePictureUploading}
                                            style={{
                                                position: 'relative',
                                                padding: 0,
                                                border: 'none',
                                                background: 'none',
                                                cursor: workspaceProfilePictureUploading ? 'not-allowed' : 'pointer',
                                                borderRadius: '50%',
                                                outline: 'none',
                                                boxShadow: darkMode ? '0 0 0 0px #444' : '0 0 0 0px #e0e0e0',
                                                transition: 'box-shadow 0.2s ease, transform 0.2s ease'
                                            }}
                                            onMouseOver={(e) => {
                                                if (workspaceProfilePictureUploading) return;
                                                e.currentTarget.style.boxShadow = darkMode ? '0 0 0 3px #4A9EFF' : '0 0 0 3px #1976d2';
                                                e.currentTarget.style.transform = 'scale(1.02)';
                                            }}
                                            onMouseOut={(e) => {
                                                e.currentTarget.style.boxShadow = darkMode ? '0 0 0 0px #444' : '0 0 0 0px #e0e0e0';
                                                e.currentTarget.style.transform = 'scale(1)';
                                            }}
                                        >
                                            <div style={{
                                                width: '80px',
                                                height: '80px',
                                                borderRadius: '50%',
                                                overflow: 'hidden',
                                                ...(getOrbGradientForString(currentWorkspace?.name) || { background: darkMode ? '#333' : '#e8e8e8' }),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                {currentWorkspace?.profilePicture ? (
                                                    <img
                                                        src={currentWorkspace.profilePicture}
                                                        alt="Workspace"
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <Users size={32} strokeWidth={1.5} color="rgba(255,255,255,0.85)" />
                                                )}
                                            </div>
                                            {workspaceProfilePictureUploading && (
                                                <div style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    borderRadius: '50%',
                                                    background: 'rgba(0,0,0,0.5)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <Loader size={24} color="#fff" style={{ animation: 'spin 0.8s linear infinite' }} />
                                                </div>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => !workspaceProfilePictureUploading && workspaceProfilePictureInputRef.current?.click()}
                                            disabled={workspaceProfilePictureUploading}
                                            style={{
                                                marginTop: '12px',
                                                padding: '6px 12px',
                                                border: 'none',
                                                background: 'transparent',
                                                color: darkMode ? '#4A9EFF' : '#1976d2',
                                                fontSize: '13px',
                                                fontWeight: 500,
                                                cursor: workspaceProfilePictureUploading ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                opacity: workspaceProfilePictureUploading ? 0.6 : 1,
                                                transition: 'opacity 0.2s'
                                            }}
                                        >
                                            <Camera size={14} strokeWidth={2} />
                                            {workspaceProfilePictureUploading ? 'Uploading…' : 'Change workspace photo'}
                                        </button>
                                        {workspaceProfilePictureError && (
                                            <p style={{ marginTop: '8px', color: '#e57373', fontSize: '12px', textAlign: 'center' }}>{workspaceProfilePictureError}</p>
                                        )}
                                    </div>
                                )}

                                <div className="profile-header-row__divider" aria-hidden="true" />

                                <div className="profile-header-row__name">
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 600, color: 'var(--text-color)' }}>
                                        Workspace Name
                                    </label>
                                    {isEditingName ? (
                                        <div className="edit-input-group" style={{ boxSizing: 'border-box' }}>
                                            <input
                                                type="text"
                                                value={newName}
                                                onChange={(e) => setNewName(e.target.value)}
                                                placeholder="Workspace name"
                                                style={{ boxSizing: 'border-box', fontSize: '14px', fontFamily: SYSTEM_UI_FONT }}
                                            />
                                            <button onClick={handleUpdateName} className="icon-button">
                                                <Check size={18} />
                                            </button>
                                            <button onClick={() => setIsEditingName(false)} className="icon-button">
                                                <X size={18} />
                                            </button>
                                        </div>
                                    ) : (
                                        <div
                                            className="display-group"
                                            style={{
                                                boxSizing: 'border-box',
                                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                                borderRadius: '8px',
                                                padding: '6px 10px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                background: darkMode ? '#2A2B2F' : '#fff',
                                                fontSize: '14px',
                                                fontFamily: SYSTEM_UI_FONT,
                                                minHeight: 0,
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            <span style={{ fontSize: '14px' }}>{currentWorkspace.name}</span>
                                            {hasPermission('admin') && (
                                                <button onClick={() => { setNewName(currentWorkspace.name || ''); setIsEditingName(true); }} className="icon-button" aria-label="Edit workspace name">
                                                    <Edit2 size={14} />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {currentUserId === BOUKAR_USER_ID &&
                                /^boukar'?s spaceship$/i.test(String(currentWorkspace?.name || '').trim()) && (
                                <div
                                    className="form-group"
                                    style={{
                                        marginTop: '20px',
                                        marginBottom: '20px',
                                        padding: '14px 16px',
                                        borderRadius: '8px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        background: darkMode ? '#1f2024' : '#fafafa',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                                        <div>
                                            <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-color)', marginBottom: '4px' }}>
                                                ElevenLabs model
                                                <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: 500, opacity: 0.65 }}>
                                                    Boukar only
                                                </span>
                                            </div>
                                            <div style={{ fontSize: '12px', color: darkMode ? '#94a3b8' : '#64748b', lineHeight: 1.4 }}>
                                                Applies to voiceovers on web and mobile for this workspace.
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: fieldLoadingStates.elevenLabsModel ? 'wait' : 'pointer', fontSize: '14px' }}>
                                                <input
                                                    type="radio"
                                                    name="elevenLabsModel"
                                                    value="current"
                                                    disabled={!!fieldLoadingStates.elevenLabsModel}
                                                    checked={(currentWorkspace?.elevenLabsModel || 'current') === 'current'}
                                                    onChange={async () => {
                                                        if ((currentWorkspace?.elevenLabsModel || 'current') === 'current') return;
                                                        try {
                                                            setFieldLoading('elevenLabsModel', true);
                                                            await updateWorkspace(currentWorkspace._id, { elevenLabsModel: 'current' }, { skipLoading: true });
                                                            setFieldSaved('elevenLabsModel', true);
                                                        } catch (err) {
                                                            console.error('Failed to set ElevenLabs model:', err);
                                                            setFieldSaved('elevenLabsModel', false);
                                                        } finally {
                                                            setFieldLoading('elevenLabsModel', false);
                                                        }
                                                    }}
                                                    style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                />
                                                <span>Current (Flash v2.5)</span>
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: fieldLoadingStates.elevenLabsModel ? 'wait' : 'pointer', fontSize: '14px' }}>
                                                <input
                                                    type="radio"
                                                    name="elevenLabsModel"
                                                    value="v4"
                                                    disabled={!!fieldLoadingStates.elevenLabsModel}
                                                    checked={currentWorkspace?.elevenLabsModel === 'v4'}
                                                    onChange={async () => {
                                                        if (currentWorkspace?.elevenLabsModel === 'v4') return;
                                                        try {
                                                            setFieldLoading('elevenLabsModel', true);
                                                            await updateWorkspace(currentWorkspace._id, { elevenLabsModel: 'v4' }, { skipLoading: true });
                                                            setFieldSaved('elevenLabsModel', true);
                                                        } catch (err) {
                                                            console.error('Failed to set ElevenLabs model:', err);
                                                            setFieldSaved('elevenLabsModel', false);
                                                        } finally {
                                                            setFieldLoading('elevenLabsModel', false);
                                                        }
                                                    }}
                                                    style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                />
                                                <span>V4</span>
                                            </label>
                                        </div>
                                    </div>
                                    {fieldLoadingStates.elevenLabsModel && (
                                        <div style={{ marginTop: '8px', fontSize: '12px', color: darkMode ? '#94a3b8' : '#64748b' }}>
                                            Saving…
                                        </div>
                                    )}
                                    {fieldSaveStates.elevenLabsModel === 'saved' && !fieldLoadingStates.elevenLabsModel && (
                                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#28a745' }}>
                                            Saved — mobile will use this on the next voiceover.
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* NLE mode picker removed — Timeline always opens the new editor */}

                            {/* First-time visit indicator */}
                            {/* {!hasVisitedProfile && (
                            <div style={{ 
                                    backgroundColor: darkMode ? '#1a2332' : '#e3f2fd',
                                    border: `1px solid ${darkMode ? '#2d4a6b' : '#2196f3'}`,
                                    borderRadius: '8px',
                                    padding: '16px',
                                    marginBottom: '20px',
                                    color: darkMode ? '#7bb3f0' : '#1976d2'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                        <User size={20} />
                                        <strong>Welcome to your workspace!</strong>
                                    </div>
                                    <p style={{ margin: 0, fontSize: '14px' }}>
                                        Complete your workspace profile to get the most out of your video generation experience. 
                                        This information helps us create better, more personalized content for you.
                                    </p>
                                </div>
                            )} */}

                            <div className="form-group">
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    Bio
                                    {fieldLoadingStates.currentSituation && (
                                        <Loader size={16} className="spinner-border" />
                                    )}
                                    {fieldSaveStates.currentSituation === 'saved' && (
                                        <Check size={16} style={{ color: '#28a745' }} />
                                    )}
                                </label>
                                <TextareaAutosize
                                    value={formData.currentSituation}
                                    onChange={(e) => {
                                        setFormData(prev => ({
                                            ...prev,
                                            currentSituation: e.target.value
                                        }));
                                        // Auto-save after 2 seconds of inactivity
                                        clearTimeout(window.currentSituationTimeout);
                                        window.currentSituationTimeout = setTimeout(async () => {
                                            try {
                                                await saveField('currentSituation', {
                                                    profile: {
                                                        currentSituation: e.target.value
                                                    }
                                                });
                                            } catch (error) {
                                                console.error('Error auto-saving currentSituation:', error);
                                            }
                                        }, 2000);
                                    }}
                                    placeholder="Name (optional), experience, skills, background, etc..."
                                    minRows={3}
                                    maxRows={8}
                                    style={{
                                        width: '100%', 
                                        padding: '10px',
                                        borderRadius: '5px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        fontFamily: SYSTEM_UI_FONT,
                                        boxSizing: 'border-box',
                                        resize: 'none',
                                        backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                        color: darkMode ? '#fff' : '#333'
                                    }}
                                />
                                <p style={{ marginTop: '8px', marginBottom: 0, fontSize: '13px', color: darkMode ? '#94a3b8' : '#64748b', lineHeight: 1.4 }}>
                                    Ideas to include: your purpose (or company's purpose), target audience, their main challenges and how you solve them, ideal tone, current content strategy, content ideas, competitors and main differentiation.
                                </p>
                            </div>

                            <div className="form-group">
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    Website URL
                                    {fieldLoadingStates.websiteUrl && (
                                        <Loader size={16} className="spinner-border" />
                                    )}
                                    {fieldSaveStates.websiteUrl === 'saved' && (
                                        <Check size={16} style={{ color: '#28a745' }} />
                                    )}
                                </label>
                                <input
                                    type="url"
                                    value={formData.websiteUrl}
                                    onChange={(e) => {
                                        setFormData(prev => ({
                                            ...prev,
                                            websiteUrl: e.target.value
                                        }));
                                        // Auto-save after 2 seconds of inactivity
                                        clearTimeout(window.websiteUrlTimeout);
                                        window.websiteUrlTimeout = setTimeout(async () => {
                                            try {
                                                await saveField('websiteUrl', {
                                                    profile: {
                                                        websiteUrl: e.target.value
                                                    }
                                                });
                                            } catch (error) {
                                                console.error('Error auto-saving websiteUrl:', error);
                                            }
                                        }, 2000);
                                    }}
                                    placeholder="https://your-website.com"
                                    style={{ 
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        fontFamily: SYSTEM_UI_FONT,
                                        boxSizing: 'border-box',
                                        backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                        color: darkMode ? '#fff' : '#333'
                                        }} 
                                />
                            </div>

                            {/* <h4 style={{ 
                                marginTop: '30px', 
                                marginBottom: '20px', 
                                color: darkMode ? '#fff' : '#333',
                                borderBottom: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                paddingBottom: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                Professional Profile
                                {fieldLoadingStates.prospectingProfile && (
                                    <Loader size={16} className="spinner-border" />
                                )}
                                {fieldSaveStates.prospectingProfile === 'saved' && (
                                    <Check size={16} style={{ color: '#28a745' }} />
                                )}
                            </h4> */}

                                {(formData.prospectingProfile.companyPurpose ?? '').toString().trim() !== '' && (
                                <div className="form-group">
                                    <label>Company Purpose</label>
                                    <TextareaAutosize
                                        value={formData.prospectingProfile.companyPurpose}
                                        onChange={(e) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                prospectingProfile: {
                                                    ...prev.prospectingProfile,
                                                    companyPurpose: e.target.value
                                                }
                                            }));
                                            // Auto-save after 2 seconds of inactivity
                                            clearTimeout(window.prospectingProfileTimeout);
                                            window.prospectingProfileTimeout = setTimeout(async () => {
                                                try {
                                                    await saveField('prospectingProfile', {
                                                        prospectingProfile: {
                                                            ...formData.prospectingProfile,
                                                            companyPurpose: e.target.value
                                                        }
                                                    });
                                                } catch (error) {
                                                    console.error('Error auto-saving prospectingProfile:', error);
                                                }
                                            }, 2000);
                                        }}
                                        placeholder="Your company's purpose..."
                                        minRows={3}
                                        maxRows={8}
                                        style={{ 
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '5px',
                                            border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                            fontFamily: SYSTEM_UI_FONT,
                                            boxSizing: 'border-box',
                                            resize: 'none',
                                            backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                            color: darkMode ? '#fff' : '#333'
                                        }}
                                    />
                                </div>
                                )}

                                {(formData.prospectingProfile.targetAudience ?? '').toString().trim() !== '' && (
                                <div className="form-group">
                                    <label>Target Audience</label>
                                    <TextareaAutosize 
                                        value={formData.prospectingProfile.targetAudience}
                                        onChange={(e) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                prospectingProfile: {
                                                    ...prev.prospectingProfile,
                                                    targetAudience: e.target.value
                                                }
                                            }));
                                            // Auto-save after 2 seconds of inactivity
                                            clearTimeout(window.prospectingProfileTimeout);
                                            window.prospectingProfileTimeout = setTimeout(async () => {
                                                try {
                                                    await saveField('prospectingProfile', {
                                                        prospectingProfile: {
                                                            ...formData.prospectingProfile,
                                                            targetAudience: e.target.value
                                                        }
                                                    });
                                                } catch (error) {
                                                    console.error('Error auto-saving prospectingProfile:', error);
                                                }
                                            }, 2000);
                                        }}
                                        placeholder="Your target audience..."
                                        minRows={3}
                                        maxRows={8}
                                        style={{ 
                                            width: '100%', 
                                            padding: '10px',
                                            borderRadius: '5px',
                                            border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                            fontFamily: SYSTEM_UI_FONT,
                                            boxSizing: 'border-box',
                                            resize: 'none',
                                            backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                            color: darkMode ? '#fff' : '#333'
                                        }}
                                    />
                                </div>
                                )}

                                {(formData.prospectingProfile.howToBePerceived ?? '').toString().trim() !== '' && (
                                <div className="form-group">
                                    <label>How do you want to be perceived?</label>
                                    <TextareaAutosize
                                        value={formData.prospectingProfile.howToBePerceived}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            prospectingProfile: {
                                                ...prev.prospectingProfile,
                                                howToBePerceived: e.target.value
                                            }
                                        }))}
                                        placeholder="How do you want your audience to perceive you and your brand..."
                                        minRows={3}
                                        maxRows={8}
                                                            style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '5px',
                                            border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                            fontFamily: SYSTEM_UI_FONT,
                                            boxSizing: 'border-box',
                                            resize: 'none',
                                            backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                            color: darkMode ? '#fff' : '#333'
                                        }}
                                    />
                                </div>
                                )}

                                {(formData.prospectingProfile.currentMarketingStrategy ?? '').toString().trim() !== '' && (
                                <div className="form-group">
                                    <label>Current Marketing Strategy</label>
                                    <TextareaAutosize
                                        value={formData.prospectingProfile.currentMarketingStrategy}
                                        onChange={(e) => setFormData(prev => ({
                                                                ...prev,
                                            prospectingProfile: {
                                                ...prev.prospectingProfile,
                                                currentMarketingStrategy: e.target.value
                                            }
                                        }))}
                                        placeholder="Describe your current marketing approach and strategies..."
                                        minRows={3}
                                        maxRows={8}
                                        style={{ 
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '5px',
                                            border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                            fontFamily: SYSTEM_UI_FONT,
                                            boxSizing: 'border-box',
                                            resize: 'none',
                                            backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                            color: darkMode ? '#fff' : '#333'
                                        }}
                                    />
                                </div>
                                )}

                                {(formData.prospectingProfile.contentIdeas ?? '').toString().trim() !== '' && (
                                <div className="form-group">
                                    <label>Content Ideas</label>
                                                            <TextareaAutosize 
                                        value={formData.prospectingProfile.contentIdeas}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            prospectingProfile: {
                                                ...prev.prospectingProfile,
                                                contentIdeas: e.target.value
                                            }
                                        }))}
                                        placeholder="What types of content do you want to create? Any specific ideas or themes..."
                                        minRows={3}
                                        maxRows={8}
                                        style={{ 
                                            width: '100%', 
                                            padding: '10px',
                                            borderRadius: '5px',
                                            border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                            fontFamily: SYSTEM_UI_FONT,
                                            boxSizing: 'border-box',
                                            resize: 'none',
                                            backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                            color: darkMode ? '#fff' : '#333'
                                        }}
                                        />
                                    </div>
                                )}

                                {(formData.prospectingProfile.inspiringBrands ?? '').toString().trim() !== '' && (
                                <div className="form-group">
                                    <label>Brands / Accounts that inspire you</label>
                                    <TextareaAutosize
                                        value={formData.prospectingProfile.inspiringBrands}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            prospectingProfile: {
                                                ...prev.prospectingProfile,
                                                inspiringBrands: e.target.value
                                            }
                                        }))}
                                        placeholder="List brands, influencers, or accounts that inspire your content and business approach..."
                                        minRows={3}
                                        maxRows={8}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '5px',
                                            border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                            fontFamily: SYSTEM_UI_FONT,
                                            boxSizing: 'border-box',
                                            resize: 'none',
                                            backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                            color: darkMode ? '#fff' : '#333'
                                        }}
                                    />
                                </div>
                                )}

                                {(formData.prospectingProfile.competitors ?? '').toString().trim() !== '' && (
                                <div className="form-group">
                                    <label>Competitors and main differentiation</label>
                                                    <TextareaAutosize 
                                        value={formData.prospectingProfile.competitors}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            prospectingProfile: {
                                                ...prev.prospectingProfile,
                                                competitors: e.target.value
                                            }
                                        }))}
                                        placeholder="Who are your main competitors and what makes you different from them..."
                                        minRows={3}
                                        maxRows={8}
                                        style={{ 
                                            width: '100%', 
                                            padding: '10px',
                                            borderRadius: '5px',
                                            border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                            fontFamily: SYSTEM_UI_FONT,
                                            boxSizing: 'border-box',
                                            resize: 'none',
                                            backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                            color: darkMode ? '#fff' : '#333'
                                        }}
                                    />
                                </div>
                                )}
                                
                                {(formData.prospectingProfile.targetChallenge ?? '').toString().trim() !== '' && (
                                <div className="form-group">
                                    <label>Your target's main challenges and how you solve them</label>
                                    <TextareaAutosize
                                        value={formData.prospectingProfile.targetChallenge}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            prospectingProfile: {
                                                ...prev.prospectingProfile,
                                                targetChallenge: e.target.value
                                            }
                                        }))}
                                        placeholder="What are the main challenges your target audience faces and how do you help solve them..."
                                        minRows={3}
                                        maxRows={8}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '5px',
                                            border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                            fontFamily: SYSTEM_UI_FONT,
                                            boxSizing: 'border-box',
                                            resize: 'none',
                                            backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                            color: darkMode ? '#fff' : '#333'
                                        }}
                                    />
                                    </div>
                                )}
                            </div>

                            <h4 style={{ 
                                marginTop: '30px', 
                                marginBottom: '10px', 
                                color: darkMode ? '#fff' : '#333',
                                }}>
                                Social Media
                            </h4>

                            <div className="profile-tab" style={{backgroundColor: 'var(--input-bg)', padding: '20px', paddingTop: '0px', borderRadius: '8px', border: '1px solid var(--input-border)'}}>

                            {/* Main Platform Selection - hidden */}
                            {false && (
                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ 
                                        display: 'block', 
                                        marginBottom: '10px', 
                                        fontSize: '16px', 
                                        fontWeight: 'bold',
                                        color: darkMode ? '#fff' : '#333'
                                    }}>Main Social Media Platform</label>
                                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <div onClick={() => setFormData(prev => ({ ...prev, socialMedia: { ...prev.socialMedia, mainPlatform: 'instagram' } }))} style={{ width: '12px', height: '12px', borderRadius: '50%', border: `2px solid ${darkMode ? '#6366f1' : '#1976d2'}`, backgroundColor: darkMode ? '#2A2B2F' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s ease' }}>
                                                {formData.socialMedia.mainPlatform === 'instagram' && <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: darkMode ? '#6366f1' : '#1976d2' }} />}
                                            </div>
                                            <span style={{ fontSize: '14px', color: darkMode ? '#fff' : '#333' }}>Instagram</span>
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <div onClick={() => setFormData(prev => ({ ...prev, socialMedia: { ...prev.socialMedia, mainPlatform: 'tiktok' } }))} style={{ width: '12px', height: '12px', borderRadius: '50%', border: `2px solid ${darkMode ? '#6366f1' : '#1976d2'}`, backgroundColor: darkMode ? '#2A2B2F' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s ease' }}>
                                                {formData.socialMedia.mainPlatform === 'tiktok' && <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: darkMode ? '#6366f1' : '#1976d2' }} />}
                                            </div>
                                            <span style={{ fontSize: '14px', color: darkMode ? '#fff' : '#333' }}>TikTok</span>
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <div onClick={() => setFormData(prev => ({ ...prev, socialMedia: { ...prev.socialMedia, mainPlatform: 'youtube' } }))} style={{ width: '12px', height: '12px', borderRadius: '50%', border: `2px solid ${darkMode ? '#6366f1' : '#1976d2'}`, backgroundColor: darkMode ? '#2A2B2F' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s ease' }}>
                                                {formData.socialMedia.mainPlatform === 'youtube' && <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: darkMode ? '#6366f1' : '#1976d2' }} />}
                                            </div>
                                            <span style={{ fontSize: '14px', color: darkMode ? '#fff' : '#333' }}>YouTube</span>
                                        </label>
                                    </div>
                                </div>
                            )}

                            {/* Social: Instagram, TikTok, YouTube - row on desktop, stack on mobile */}
                            <div style={{ 
                                display: 'flex', 
                                flexDirection: screenWidth <= 630 ? 'column' : 'row', 
                                gap: '20px', 
                                marginBottom: '20px',
                                alignItems: 'stretch'
                            }}>
                                <div style={{ flex: screenWidth <= 630 ? 'none' : '1 1 0', minWidth: 0 }}>
                                    <h5 style={{ marginBottom: '15px', color: darkMode ? '#fff' : '#333' }}>Instagram</h5>
                                    <div style={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: '1fr', 
                                        gap: '15px', 
                                        marginBottom: '0' 
                                    }}>
                                        <div>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                                                Handle
                                                <button
                                                    onClick={handleImportInstagramVideos}
                                                    disabled={importingInstagram || !formData.socialMedia.instagram.handle?.trim()}
                                                    style={{
                                                        padding: '6px 12px',
                                                        borderRadius: '5px',
                                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                                        backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                                        color: darkMode ? '#fff' : '#333',
                                                        cursor: importingInstagram || !formData.socialMedia.instagram.handle?.trim() ? 'not-allowed' : 'pointer',
                                                        fontSize: '12px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        opacity: importingInstagram || !formData.socialMedia.instagram.handle?.trim() ? 0.6 : 1,
                                                        transition: 'opacity 0.2s'
                                                    }}
                                                    title="Import last 20 videos from this Instagram profile"
                                                >
                                                    {importingInstagram ? (
                                                        <>
                                                            <Loader size={14} className="spinner-border" />
                                                            Importing...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Download size={14} />
                                                            Import Videos
                                                        </>
                                                    )}
                                                </button>
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="@username"
                                                value={formData.socialMedia.instagram.handle}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    socialMedia: {
                                                        ...prev.socialMedia,
                                                        instagram: {
                                                            ...prev.socialMedia.instagram,
                                                            handle: e.target.value
                                                        }
                                                    }
                                                }))}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px',
                                                    borderRadius: '5px',
                                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                                    color: darkMode ? '#fff' : '#333',
                                                    fontFamily: SYSTEM_UI_FONT,
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                            {['673247877e4ac0a824cbac0d' /*Boukar*/, '68c2c068253b59e3eb46452c' /*Oleg*/, '68c2b8da253b59e3eb4644ff' /*Mauricio*/, '698da27e796493d5a4557a0e' /* Ismael */, '69fcd261ca736cd0a5b72e72' /*Valentina*/, '69ff1cc295660f26f7a896a6' /*Valentina*/].includes(currentUserId) && (
                                                <button
                                                    onClick={() => handleGetlateConnect('instagram')}
                                                    disabled={getlateConnecting.instagram}
                                                    style={{
                                                        marginTop: '8px',
                                                        width: '100%',
                                                        padding: '8px 12px',
                                                        borderRadius: '5px',
                                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                                        backgroundColor: getlateConnectedAccounts.instagram 
                                                            ? (darkMode ? '#2d5016' : '#d4edda')
                                                            : (darkMode ? '#2A2B2F' : '#fff'),
                                                        color: getlateConnectedAccounts.instagram 
                                                            ? (darkMode ? '#90ee90' : '#155724')
                                                            : (darkMode ? '#fff' : '#333'),
                                                        cursor: getlateConnecting.instagram ? 'not-allowed' : 'pointer',
                                                        fontSize: '12px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '6px',
                                                        opacity: getlateConnecting.instagram ? 0.6 : 1,
                                                        transition: 'opacity 0.2s'
                                                    }}
                                                >
                                                    {getlateConnecting.instagram ? (
                                                        <>
                                                            <Loader size={14} className="spinner-border" />
                                                            Connecting...
                                                        </>
                                                    ) : getlateConnectedAccounts.instagram ? (
                                                        <>
                                                            <CheckCircle size={14} />
                                                            Connected to Instagram
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Share2 size={14} />
                                                            Connect to Instagram
                                                        </>
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                        {false && (
                                        <>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '5px' }}>Current Followers</label>
                                            <input
                                                value={formData.socialMedia.instagram.currentFollowers}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    socialMedia: {
                                                        ...prev.socialMedia,
                                                        instagram: {
                                                            ...prev.socialMedia.instagram,
                                                            currentFollowers: parseInt(e.target.value) || 0
                                                        }
                                                    }
                                                }))}
                                                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: darkMode ? '1px solid #444' : '1px solid #dfe1e5', backgroundColor: darkMode ? '#2A2B2F' : 'white', color: darkMode ? '#fff' : '#333', fontFamily: SYSTEM_UI_FONT, boxSizing: 'border-box' }}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '5px' }}>Target Followers</label>
                                            <input
                                                value={formData.socialMedia.instagram.targetFollowers}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    socialMedia: {
                                                        ...prev.socialMedia,
                                                        instagram: {
                                                            ...prev.socialMedia.instagram,
                                                            targetFollowers: parseInt(e.target.value) || 0
                                                        }
                                                    }
                                                }))}
                                                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: darkMode ? '1px solid #444' : '1px solid #dfe1e5', backgroundColor: darkMode ? '#2A2B2F' : 'white', color: darkMode ? '#fff' : '#333', fontFamily: SYSTEM_UI_FONT, boxSizing: 'border-box' }}
                                            />
                                        </div>
                                        </>
                                        )}
                                        </div>
                                    </div>

                                    {/* TikTok */}
                                    <div style={{ flex: screenWidth <= 630 ? 'none' : '1 1 0', minWidth: 0 }}>
                                        <h5 style={{ marginBottom: '15px', color: darkMode ? '#fff' : '#333' }}>TikTok</h5>
                                        <div style={{ 
                                            display: 'grid', 
                                            gridTemplateColumns: '1fr', 
                                            gap: '15px', 
                                            marginBottom: '0' 
                                        }}>
                                            <div>
                                                <label style={{ display: 'block', marginBottom: '5px', paddingTop:'4px', paddingBottom:'4px' }}>Handle</label>
                                                <input
                                                    type="text"
                                                    placeholder="@username"
                                                    value={formData.socialMedia.tiktok.handle}
                                                    onChange={(e) => setFormData(prev => ({
                                                        ...prev,
                                                        socialMedia: {
                                                            ...prev.socialMedia,
                                                            tiktok: {
                                                                ...prev.socialMedia.tiktok,
                                                                handle: e.target.value
                                                            }
                                                        }
                                                    }))}
                                                    style={{
                                                        width: '100%',
                                                        padding: '10px',
                                                        borderRadius: '5px',
                                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                                        backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                                        color: darkMode ? '#fff' : '#333',
                                                        fontFamily: SYSTEM_UI_FONT,
                                                        boxSizing: 'border-box'
                                                    }}
                                                />
                                                {['673247877e4ac0a824cbac0d' /*Boukar*/, '68c2c068253b59e3eb46452c' /*Oleg*/, '68c2b8da253b59e3eb4644ff' /*Mauricio*/, '698da27e796493d5a4557a0e' /* Ismael */, '69fcd261ca736cd0a5b72e72' /*Valentina*/, '69ff1cc295660f26f7a896a6' /*Valentina*/].includes(currentUserId) && (
                                                    <button
                                                        onClick={() => handleGetlateConnect('tiktok')}
                                                        disabled={getlateConnecting.tiktok}
                                                        style={{
                                                            marginTop: '8px',
                                                            width: '100%',
                                                            padding: '8px 12px',
                                                            borderRadius: '5px',
                                                            border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                                            backgroundColor: getlateConnectedAccounts.tiktok 
                                                                ? (darkMode ? '#2d5016' : '#d4edda')
                                                                : (darkMode ? '#2A2B2F' : '#fff'),
                                                            color: getlateConnectedAccounts.tiktok 
                                                                ? (darkMode ? '#90ee90' : '#155724')
                                                                : (darkMode ? '#fff' : '#333'),
                                                            cursor: getlateConnecting.tiktok ? 'not-allowed' : 'pointer',
                                                            fontSize: '12px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '6px',
                                                            opacity: getlateConnecting.tiktok ? 0.6 : 1,
                                                            transition: 'opacity 0.2s'
                                                        }}
                                                    >
                                                        {getlateConnecting.tiktok ? (
                                                            <>
                                                                <Loader size={14} className="spinner-border" />
                                                                Connecting...
                                                            </>
                                                        ) : getlateConnectedAccounts.tiktok ? (
                                                            <>
                                                                <CheckCircle size={14} />
                                                                Connected to TikTok
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Share2 size={14} />
                                                                Connect to TikTok
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                            {false && (
                                            <>
                                            <div>
                                                <label style={{ display: 'block', marginBottom: '5px' }}>Current Followers</label>
                                                <input
                                                    value={formData.socialMedia.tiktok.currentFollowers}
                                                    onChange={(e) => setFormData(prev => ({
                                                        ...prev,
                                                        socialMedia: {
                                                            ...prev.socialMedia,
                                                            tiktok: {
                                                                ...prev.socialMedia.tiktok,
                                                                currentFollowers: parseInt(e.target.value) || 0
                                                            }
                                                        }
                                                    }))}
                                                    style={{ width: '100%', padding: '10px', borderRadius: '5px', border: darkMode ? '1px solid #444' : '1px solid #dfe1e5', backgroundColor: darkMode ? '#2A2B2F' : 'white', color: darkMode ? '#fff' : '#333', fontFamily: SYSTEM_UI_FONT, boxSizing: 'border-box' }}
                                                />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', marginBottom: '5px' }}>Target Followers</label>
                                                <input
                                                    value={formData.socialMedia.tiktok.targetFollowers}
                                                    onChange={(e) => setFormData(prev => ({
                                                        ...prev,
                                                        socialMedia: {
                                                            ...prev.socialMedia,
                                                            tiktok: {
                                                                ...prev.socialMedia.tiktok,
                                                                targetFollowers: parseInt(e.target.value) || 0
                                                            }
                                                        }
                                                    }))}
                                                    style={{ width: '100%', padding: '10px', borderRadius: '5px', border: darkMode ? '1px solid #444' : '1px solid #dfe1e5', backgroundColor: darkMode ? '#2A2B2F' : 'white', color: darkMode ? '#fff' : '#333', fontFamily: SYSTEM_UI_FONT, boxSizing: 'border-box' }}
                                                />
                                            </div>
                                            </>
                                            )}
                                        </div>
                                    </div>

                                    {/* YouTube */}
                                    <div style={{ flex: screenWidth <= 630 ? 'none' : '1 1 0', minWidth: 0 }}>
                                    <h5 style={{ marginBottom: '15px', color: darkMode ? '#fff' : '#333' }}>YouTube</h5>
                                    <div style={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: '1fr', 
                                        gap: '15px', 
                                        marginBottom: '0' 
                                    }}>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '5px', paddingTop:'4px', paddingBottom:'4px' }}>Handle</label>
                                            <input
                                                type="text"
                                                placeholder="@username"
                                                value={formData.socialMedia.youtube.handle}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    socialMedia: {
                                                        ...prev.socialMedia,
                                                        youtube: {
                                                            ...prev.socialMedia.youtube,
                                                            handle: e.target.value
                                                        }
                                                    }
                                                }))}
                                                style={{ 
                                                    width: '100%', 
                                                    padding: '10px',
                                                    borderRadius: '5px',
                                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                                    color: darkMode ? '#fff' : '#333',
                                                    fontFamily: SYSTEM_UI_FONT,
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                                </div>
                                        {false && (
                                        <>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '5px' }}>Current Followers</label>
                                            <input
                                                value={formData.socialMedia.youtube.currentFollowers}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    socialMedia: {
                                                        ...prev.socialMedia,
                                                        youtube: {
                                                            ...prev.socialMedia.youtube,
                                                            currentFollowers: parseInt(e.target.value) || 0
                                                        }
                                                    }
                                                }))}
                                                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: darkMode ? '1px solid #444' : '1px solid #dfe1e5', backgroundColor: darkMode ? '#2A2B2F' : 'white', color: darkMode ? '#fff' : '#333', fontFamily: SYSTEM_UI_FONT, boxSizing: 'border-box' }}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '5px' }}>Target Followers</label>
                                            <input
                                                value={formData.socialMedia.youtube.targetFollowers}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    socialMedia: {
                                                        ...prev.socialMedia,
                                                        youtube: {
                                                            ...prev.socialMedia.youtube,
                                                            targetFollowers: parseInt(e.target.value) || 0
                                                        }
                                                    }
                                                }))}
                                                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: darkMode ? '1px solid #444' : '1px solid #dfe1e5', backgroundColor: darkMode ? '#2A2B2F' : 'white', color: darkMode ? '#fff' : '#333', fontFamily: SYSTEM_UI_FONT, boxSizing: 'border-box' }}
                                            />
                                        </div>
                                        </>
                                        )}
                                        </div>
                                    </div>
                                </div>

                                {/* Video Production Goals - hidden */}
                                {false && (
                                    <>
                                        <h5 style={{ marginBottom: '15px', color: darkMode ? '#fff' : '#333' }}>Video Production Goals</h5>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '30px' }}>
                                            <div>
                                                <label style={{ display: 'block', marginBottom: '5px' }}>Current Videos Per Month</label>
                                                <input
                                                    value={formData.videoProduction.currentVideosPerMonth}
                                                    onChange={(e) => setFormData(prev => ({
                                                        ...prev,
                                                        videoProduction: {
                                                            ...prev.videoProduction,
                                                            currentVideosPerMonth: parseInt(e.target.value) || 0
                                                        }
                                                    }))}
                                                    style={{ width: '100%', padding: '10px', borderRadius: '5px', border: darkMode ? '1px solid #444' : '1px solid #dfe1e5', backgroundColor: darkMode ? '#2A2B2F' : 'white', color: darkMode ? '#fff' : '#333', fontFamily: SYSTEM_UI_FONT, boxSizing: 'border-box' }}
                                                />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', marginBottom: '5px' }}>Target Videos Per Month</label>
                                                <input
                                                    value={formData.videoProduction.targetVideosPerMonth}
                                                    onChange={(e) => setFormData(prev => ({
                                                        ...prev,
                                                        videoProduction: {
                                                            ...prev.videoProduction,
                                                            targetVideosPerMonth: parseInt(e.target.value) || 0
                                                        }
                                                    }))}
                                                    style={{ width: '100%', padding: '10px', borderRadius: '5px', border: darkMode ? '1px solid #444' : '1px solid #dfe1e5', backgroundColor: darkMode ? '#2A2B2F' : 'white', color: darkMode ? '#fff' : '#333', fontFamily: SYSTEM_UI_FONT, boxSizing: 'border-box' }}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            {false && (
                            <>
                            <h4 style={{ 
                                    marginTop: '30px', 
                                    marginBottom: '10px', 
                                    color: darkMode ? '#fff' : '#333',
                            }}>
                                Subtitle Style
                            </h4>

                            <div className="profile-tab" style={{backgroundColor: 'var(--input-bg)', padding: '20px', paddingTop: '10px', borderRadius: '8px', border: '1px solid var(--input-border)'}}>

                                
                            
                            <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: screenWidth <= 630 ? '1fr' : '1fr 1fr', 
                                gap: '20px', 
                                marginBottom: '10px' 
                            }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '5px' }}>Font Color</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }} data-font-color-picker>
                                        <input
                                            type="text"
                                            value={subtitleFontColor}
                                            onChange={(e) => setSubtitleFontColor(e.target.value)}
                                            onBlur={(e) => handleSubtitleFontColorChange(e.target.value)}
                                            placeholder="#ffffff"
                                            style={{ 
                                                width: '70px', 
                                                padding: '10px', 
                                                borderRadius: '5px', 
                                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5', 
                                                backgroundColor: darkMode ? '#2A2B2F' : '#fff', 
                                                color: darkMode ? '#fff' : '#333' 
                                            }}
                                        />
                                        <input
                                            type="color"
                                            value={subtitleFontColor}
                                            onChange={(e) => handleSubtitleFontColorChange(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                height: '40px',
                                                border: 'none',
                                                borderRadius: '3px',
                                                cursor: 'pointer'
                                            }}
                                            // autoFocus
                                        />
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                type="color"
                                                value={subtitleFontColor}
                                                onChange={(e) => handleSubtitleFontColorChange(e.target.value)}
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '40px',
                                                    height: '40px',
                                                    border: 'none',
                                                    borderRadius: '3px',
                                                            cursor: 'pointer',
                                                    opacity: 0,
                                                    zIndex: 1
                                                }}
                                                ref={(input) => {
                                                    if (input) {
                                                        input.click = () => {
                                                            input.click();
                                                        };
                                                    }
                                                }}
                                            />
                                            <img
                                                src={'/color-picker.png'}
                                                alt="Color Picker"
                                                            style={{
                                                    width: '30px', 
                                                    height: '30px', 
                                                                cursor: 'pointer',
                                                    position: 'relative',
                                                    zIndex: 0
                                                            }}
                                                            onClick={() => {
                                                    const colorInput = document.querySelector('input[type="color"][value="' + subtitleFontColor + '"]');
                                                    if (colorInput) {
                                                        colorInput.click();
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label
                                        htmlFor="profile-subtitle-outline-enabled"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            marginBottom: '5px',
                                            cursor: 'pointer',
                                            color: darkMode ? '#fff' : '#333',
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            id="profile-subtitle-outline-enabled"
                                            checked={subtitleOutlineEnabled}
                                            onChange={(e) => handleSubtitleOutlineEnabledChange(e.target.checked)}
                                            style={{
                                                width: '16px',
                                                height: '16px',
                                                cursor: 'pointer',
                                            }}
                                        />
                                        <span>Outline color</span>
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative', opacity: subtitleOutlineEnabled ? 1 : 0.55 }} data-outline-color-picker>
                                        <input
                                            type="text"
                                            value={subtitleOutlineColor}
                                            onChange={(e) => setSubtitleOutlineColor(e.target.value)}
                                            onBlur={(e) => handleSubtitleOutlineColorChange(e.target.value)}
                                            placeholder="#000000"
                                            disabled={!subtitleOutlineEnabled}
                                            style={{ 
                                                width: '70px', 
                                                padding: '10px', 
                                                borderRadius: '5px', 
                                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5', 
                                                backgroundColor: darkMode ? '#2A2B2F' : '#fff', 
                                                color: darkMode ? '#fff' : '#333' 
                                            }}
                                        />
                                        <input
                                            type="color"
                                            value={subtitleOutlineColor}
                                            onChange={(e) => handleSubtitleOutlineColorChange(e.target.value)}
                                            disabled={!subtitleOutlineEnabled}
                                            style={{
                                                width: '100%',
                                                height: '40px',
                                                border: 'none',
                                                borderRadius: '3px',
                                                cursor: subtitleOutlineEnabled ? 'pointer' : 'not-allowed'
                                            }}
                                            // autoFocus
                                        />
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                type="color"
                                                value={subtitleOutlineColor}
                                                onChange={(e) => handleSubtitleOutlineColorChange(e.target.value)}
                                                disabled={!subtitleOutlineEnabled}
                                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '40px',
                                                    height: '40px',
                                                                    border: 'none',
                                                    borderRadius: '3px',
                                                                    cursor: subtitleOutlineEnabled ? 'pointer' : 'not-allowed',
                                                    opacity: 0,
                                                    zIndex: 1
                                                }}
                                                ref={(input) => {
                                                    if (input) {
                                                        input.click = () => {
                                                            input.click();
                                                        };
                                                    }
                                                }}
                                            />
                                            <img
                                                src={'/color-picker.png'}
                                                alt="Color Picker"
                                                style={{
                                                    width: '30px', 
                                                    height: '30px', 
                                                    cursor: subtitleOutlineEnabled ? 'pointer' : 'not-allowed',
                                                    position: 'relative',
                                                    zIndex: 0,
                                                    opacity: subtitleOutlineEnabled ? 1 : 0.7,
                                                }}
                                                onClick={() => {
                                                    if (!subtitleOutlineEnabled) return;
                                                    const colorInput = document.querySelector('input[type="color"][value="' + subtitleOutlineColor + '"]');
                                                    if (colorInput) {
                                                        colorInput.click();
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px' }}>Preview</label>
                                <div style={{
                                    width: '100%',
                                    height: '200px',
                                    borderRadius: '8px',
                                    background: '#000',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative'
                                }}>
                                <img src={'https://cdn.fstoppers.com/styles/full/s3/media/2019/12/04/nando-jpeg-quality-050.jpg'} alt="Subtitle Preview" style={{ width: '100%', maxWidth: '600px', height: '100%', objectFit: 'cover', opacity: 0.5, borderRadius: '8px' }} />
                                <div style={{
                                    position: 'absolute',
                                    bottom: '30px',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    padding: subtitleBackgroundColor && subtitleBackgroundColor.toLowerCase() !== 'transparent' ? '6px 10px' : '0px',
                                    backgroundColor: subtitleBackgroundColor && subtitleBackgroundColor.toLowerCase() !== 'transparent' ? `${subtitleBackgroundColor}99` : 'transparent',
                                    color: subtitleFontColor,
                                    fontWeight: 700,
                                    fontSize: '20px',
                                    textShadow: subtitleOutlineEnabled ? `-1px -1px 0 ${subtitleOutlineColor}, 1px -1px 0 ${subtitleOutlineColor}, -1px 1px 0 ${subtitleOutlineColor}, 1px 1px 0 ${subtitleOutlineColor}` : 'none',
                                    borderRadius: '4px'
                                }}>Subtitle preview text</div>
                                </div>
                            </div>

                            
                        </div>
                            </>
                            )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px' }}>
                                <button 
                                    onClick={saveWorkspaceProfile}
                                    className="main-button" 
                                    disabled={isSaving || isAutoSaving}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        minWidth: '120px',
                                        gap: '8px',
                                        backgroundColor: saveStatus === 'saved' ? '#4CAF50' : saveStatus === 'error' ? '#f44336' : undefined,
                                        color: saveStatus === 'saved' || saveStatus === 'error' ? 'white' : undefined,
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    {isSaving ? (
                                        <>
                                        <span className="spinner-border" style={{marginRight:'5px'}} />
                                            Saving...
                                        </>
                                    ) : isAutoSaving ? (
                                        <>
                                            <span className="spinner-border" style={{marginRight:'5px'}} />
                                            Auto-saving...
                                        </>
                                    ) : saveStatus === 'saved' ? (
                                        <>
                                            <Check size={16} />
                                            Saved
                                        </>
                                    ) : saveStatus === 'error' ? (
                                        <>
                                            <AlertCircle size={16} />
                                            Error
                                        </>
                                    ) : hasUnsavedChanges ? (
                                        <>
                                            <Edit2 size={16} />
                                            Save Changes
                                        </>
                                    ) : (
                                        'Save Profile'
                                    )}
                                </button>
                            </div>
                    </div>
                )}

            {activeTab === 'voice' && (
                <>
                    <h3 style={{ marginTop: 0, marginBottom: '12px', color: 'var(--text-primary)' }}>Voice Cloning</h3>
                    <div className="voice-tab" style={{ backgroundColor: 'var(--input-bg)', padding: '20px', borderRadius: '8px', border: '1px solid var(--input-border)' }}>
                    <p style={{ color: darkMode ? '#aaa' : '#666', marginBottom: '12px', fontSize: '14px', lineHeight: 1.45, marginTop: '0px' }}>
                        Add <strong>1–5</strong> samples (~10–30s each) to clone your voice for this workspace.
                    </p>

                    {/* Inspiration / recording tips — hidden (keep hooks: showExamples, displayedExamples still in state)
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '10px' }}>
                        <p style={{ fontSize: '14px', marginBottom: '10px', marginTop: 0, color: darkMode ? '#aaa' : '#666' }}>
                            Record at least 1 sample to clone your voice. For best results, record 3-5 samples, each 10-30 seconds long.
                        </p>
                        <button
                            onClick={() => setShowExamples(!showExamples)}
                            style={{
                                padding: '2px 12px',
                                backgroundColor: 'transparent',
                                color: '#1a73e8',
                                border: '1px solid #1a73e8',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            {showExamples ? 'Hide inspiration' : 'Click for inspiration'}
                        </button>
                    </div>
                    {showExamples && (
                        <div style={{ padding: '12px', backgroundColor: darkMode ? '#2A2B2F' : '#f8f9fa', borderRadius: '4px', marginBottom: '16px', border: darkMode ? '1px solid #444' : '1px solid #dfe1e5', position: 'relative', overflow: 'hidden', height: '60px' }}>
                            {displayedExamples.map((example, index) => (
                                <div key={index} style={{ position: 'absolute', top: '50%', left: '12px', right: '12px', transform: `translateY(-50%) translateY(${(index - currentExample) * 60}px)`, opacity: index === currentExample ? 1 : 0, transition: 'all 0.5s ease-in-out', textAlign: 'center', color: darkMode ? '#aaa' : '#666' }}>"{example}"</div>
                            ))}
                        </div>
                    )}
                    {!showExamples && <p style={{ fontSize: '14px', marginTop: 0, marginBottom: '30px', color: darkMode ? '#aaa' : '#666' }}>Not sure what to say? Click for inspiration.</p>}
                    <div style={{ marginBottom: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.9em', color: darkMode ? '#aaa' : '#666' }}>
                            <span>Recording Progress</span>
                            <span>{recordings.length} recording{recordings.length !== 1 ? 's' : ''} {recordings.length >= 1 ? '(Ready to clone)' : '(Need at least 1)'}</span>
                        </div>
                        <div style={{ width: '100%', height: '8px', backgroundColor: darkMode ? '#444' : '#f0f0f0', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${Math.min((recordings.length / 5) * 100, 100)}%`, height: '100%', background: 'linear-gradient(to right, #AB5898 50%, #5C4BD3 80%)', transition: 'width 0.3s ease', borderRadius: '4px' }} />
                        </div>
                        {recordings.length < 1 && <p style={{ fontSize: '0.8em', color: darkMode ? '#aaa' : '#666', marginTop: '8px' }}>At least 1 recording needed to clone your voice</p>}
                        {recordings.length >= 1 && recordings.length < 5 && <p style={{ fontSize: '0.8em', color: darkMode ? '#aaa' : '#666', marginTop: '8px' }}>{5 - recordings.length} more {5 - recordings.length === 1 ? 'recording' : 'recordings'} for best results</p>}
                    </div>
                    */}

                    <p style={{
                        margin: '0 0 16px',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: recordings.length >= 5 ? (darkMode ? '#e8b86d' : '#b45309') : (darkMode ? '#bbb' : '#555'),
                    }}>
                        {recordings.length >= 5
                            ? 'Maximum samples (5 of 5). Delete one to add another.'
                            : `${recordings.length}/5 samples`}
                    </p>

                    {recordings.length > 0 && (
                    <div
                        style={{
                            marginBottom: '20px',
                            width: '100%',
                            boxSizing: 'border-box',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            border: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'var(--input-border)'}`,
                            backgroundColor: darkMode ? 'rgba(255,255,255,0.03)' : '#ffffff',
                        }}
                    >
                        {recordings.map((recording, index) => {
                            const handleAudioLoaded = (e) => {
                                if (isFinite(e.target.duration) && e.target.duration > 0) {
                                    const totalSeconds = Math.floor(e.target.duration);
                                    setRecordingLength(prev => ({
                                        ...prev,
                                        [recording._id]: totalSeconds
                                    }));
                                }
                            };
                            const isRecordingGlowing = glowingRecordingIds.some((id) => String(id) === String(recording._id));
                            return (
                            <div
                                key={recording._id || index}
                                className={isRecordingGlowing ? 'voice-recording-glow' : ''}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    flexWrap: window.innerWidth < 630 ? 'wrap' : 'nowrap',
                                    padding: '10px 10px',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    borderBottom: index === recordings.length - 1 ? 'none' : `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'var(--input-border)'}`,
                                    borderRadius: isRecordingGlowing ? '8px' : undefined,
                                }}
                            >
                                <div
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: window.innerWidth < 630 ? 34 : 78,
                                        padding: window.innerWidth < 630 ? '6px 8px' : '6px 10px',
                                        borderRadius: '999px',
                                        backgroundColor: darkMode ? 'rgba(225, 246, 121, 0.10)' : 'rgba(225, 246, 121, 0.25)',
                                        color: darkMode ? '#e1f679' : '#0f172a',
                                        fontSize: '12px',
                                        fontWeight: 700,
                                        flexShrink: 0,
                                        border: `1px solid ${darkMode ? 'rgba(225, 246, 121, 0.18)' : 'rgba(15, 23, 42, 0.10)'}`,
                                    }}
                                    title={`Sample ${index + 1}`}
                                >
                                    {window.innerWidth < 630 ? `${index + 1}` : `Sample ${index + 1}`}
                                    {/* ({recordingLength[recording._id] ? formatTime(recordingLength[recording._id]) : (recording.duration ? formatTime(recording.duration) : '')}) */}
                                </div>

                                <audio
                                    controls
                                    style={{
                                        flex: 1,
                                        minWidth: 120,
                                        width: window.innerWidth < 630 ? '100%' : 'auto',
                                        height: window.innerWidth < 630 ? '22px' : '28px',
                                    }}
                                    controlsList="nodownload"
                                    onError={(e) => console.error('Audio playback error:', e.target.error)}
                                    onLoadedMetadata={handleAudioLoaded}
                                >
                                    <source src={recording.url} type={recording.url.includes('.mp3') ? 'audio/mpeg' : recording.url.includes('.wav') ? 'audio/wav' : recording.url.includes('.m4a') ? 'audio/mp4' : recording.url.includes('.ogg') ? 'audio/ogg' : 'audio/webm'} />
                                </audio>

                                <button
                                    type="button"
                                    onClick={() => handleDeleteRecording(recording._id)}
                                    disabled={recordingDeleteInFlight}
                                    aria-label="Delete sample"
                                    title={recordingDeleteInFlight ? 'Please wait…' : 'Delete'}
                                    style={{
                                        width: window.innerWidth < 630 ? 34 : 38,
                                        height: window.innerWidth < 630 ? 34 : 38,
                                        borderRadius: '10px',
                                        border: '1px solid #ef4444',
                                        backgroundColor: 'transparent',
                                        color: '#ef4444',
                                        cursor: recordingDeleteInFlight ? 'not-allowed' : 'pointer',
                                        display: 'grid',
                                        placeItems: 'center',
                                        opacity: recordingDeleteInFlight ? 0.55 : 0.95,
                                        flexShrink: 0,
                                    }}
                                    onMouseEnter={(e) => {
                                        if (recordingDeleteInFlight) return;
                                        e.currentTarget.style.opacity = '1';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (recordingDeleteInFlight) return;
                                        e.currentTarget.style.opacity = '0.95';
                                    }}
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                            );
                        })}
                    </div>
                    )}

                    {voiceSamplesExceedCloneLimit && (
                        <div
                            style={{
                                marginBottom: '16px',
                                padding: '12px 14px',
                                borderRadius: '8px',
                                border: darkMode ? '1px solid rgba(245, 158, 11, 0.45)' : '1px solid rgba(180, 83, 9, 0.35)',
                                backgroundColor: darkMode ? 'rgba(245, 158, 11, 0.12)' : 'rgba(251, 191, 36, 0.18)',
                                color: darkMode ? '#fbbf24' : '#92400e',
                                fontSize: '13px',
                                lineHeight: 1.45,
                            }}
                        >
                            Total sample length is <strong>{formatVoiceSampleDuration(totalVoiceSampleDurationSec)}</strong> (limit 45:00).
                            ElevenLabs won’t clone when samples exceed 45 minutes — delete or replace some files to continue.
                        </div>
                    )}

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: window.innerWidth >= 820 ? 'row' : 'column',
                            gap: '10px',
                            marginBottom: '20px',
                            alignItems: 'stretch'
                        }}
                    >
                        <button
                            onClick={isRecording ? stopRecording : startRecording}
                            className={`main-button ${isRecording ? 'recording' : ''}`}
                            style={{ flex: 1, width: '100%', padding: '12px 16px', backgroundColor: (recordings.length >= 5 ? '#d9df70' : (isRecording ? (isSavingRecording ? '#ccc' : '#dc3545') : '#e1f679')), cursor: recordings.length >= 5 ? 'not-allowed' : 'pointer', gap: '4px' }}
                            disabled={recordings.length >= 5 && !isRecording}
                        >
                            {isRecording ? (
                                <>
                                    {isSavingRecording ? <Loader size={16} /> : <Square size={16} />}
                                    {isSavingRecording ? 'Saving...' : <>Stop ({formatTime(recordingDuration)})</>}
                                </>
                            ) : (
                                <>
                                    {recordings.length >= 5 ? <Check size={16} /> : <Mic size={16} />}
                                    {recordings.length >= 5 ? 'Maximum recordings (5)' : 'Record sample'}
                                </>
                            )}
                        </button>
                        <input
                            ref={audioImportRef}
                            type="file"
                            accept="audio/*,video/*"
                            multiple
                            onChange={handleAudioImport}
                            style={{ display: 'none' }}
                        />
                        <div
                            onDragEnter={handleVoiceImportDragEnter}
                            onDragOver={handleVoiceImportDragOver}
                            onDragLeave={handleVoiceImportDragLeave}
                            onDrop={handleVoiceImportDrop}
                            style={{
                                flex: window.innerWidth >= 820 ? '0 0 240px' : undefined,
                                width: '100%',
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => {
                                    if (recordings.length >= 5 || isImportingAudio) return;
                                    audioImportRef.current?.click();
                                }}
                                className="tertiary-button"
                                style={{
                                    width: '100%',
                                    justifyContent: 'center',
                                    padding: '10px 14px',
                                    gap: '4px',
                                    opacity: (recordings.length >= 5 || isImportingAudio) ? 0.65 : 1,
                                    cursor: (recordings.length >= 5 || isImportingAudio) ? 'not-allowed' : 'pointer',
                                    ...(isVoiceImportDragging && recordings.length < 5 && !isImportingAudio ? {
                                        boxShadow: '0 0 0 2px color-mix(in srgb, var(--button-bg, #667eea) 70%, transparent), 0 0 18px color-mix(in srgb, var(--button-bg, #667eea) 35%, transparent)',
                                    } : {}),
                                }}
                                disabled={recordings.length >= 5 || isImportingAudio}
                            >
                                {isImportingAudio ? (
                                    <><RefreshCw className="spin" size={16} />Importing…</>
                                ) : (
                                    <><Upload size={16} />Import voice file (audio or video)</>
                                )}
                            </button>
                        </div>
                    </div>
                    {recordings.length >= 1 && (
                        <button
                            onClick={handleVoiceCloning}
                            className={`main-button${recloneButtonShake && workspaceVoiceId && workspaceVoiceId.trim() !== '' ? ' reclone-button-shake' : ''}`}
                            disabled={isGeneratingVoice || recordings.length < 1 || voiceSamplesExceedCloneLimit}
                            title={voiceSamplesExceedCloneLimit ? 'Total sample length must be 45 minutes or less' : undefined}
                            style={{ width: '100%', marginBottom: '16px', padding: '14px 16px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                        >
                            {isGeneratingVoice ? (
                                <><RefreshCw className="spin" size={16} />Creating clone…</>
                            ) : (
                                <>
                                    {workspaceVoiceId && workspaceVoiceId.trim() !== ''
                                        ? <RefreshCw size={16} />
                                        : <SparklesIcon size={16} />}
                                    {workspaceVoiceId && workspaceVoiceId.trim() !== ''
                                        ? 'Re-clone with all recordings'
                                        : 'Clone my voice'}
                                </>
                            )}
                        </button>
                    )}
                </div>

                {workspaceVoiceId && workspaceVoiceId.trim() !== '' && (
                    <div
                        style={{
                            marginTop: '12px',
                            backgroundColor: 'var(--input-bg)',
                            padding: '16px',
                            borderRadius: '8px',
                            border: '1px solid var(--input-border)',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '12px' }}>
                            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: 'var(--text-color)' }}>Try the clone</h4>
                            {testAudioUrl ? (
                                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block', flexShrink: 0 }} aria-hidden="true" />
                                    Test ready
                                </span>
                            ) : (
                                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Generate a test voiceover</span>
                            )}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'stretch' }}>
                            <button onClick={testClonedVoice} className="main-button" disabled={isTestingVoice || !workspaceVoiceId || workspaceVoiceId.trim() === ''} style={{ flex: '1 1 180px', padding: '11px 14px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                {isTestingVoice ? (
                                    <><RefreshCw className="spin" size={16} />Exporting…</>
                                ) : (
                                    <><Mic size={16} />{testAudioUrl ? 'Regenerate test' : 'Generate test'}</>
                                )}
                            </button>
                            {testAudioUrl && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (audioRef.current) {
                                            if (isPlayingVoice) audioRef.current.pause();
                                            else audioRef.current.play();
                                            setIsPlayingVoice(!isPlayingVoice);
                                        }
                                    }}
                                    className="tertiary-button"
                                    style={{
                                        flex: '1 1 180px',
                                        padding: '11px 14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '4px',
                                    }}
                                >
                                    {isPlayingVoice ? <Pause size={16} /> : <Play size={16} />}
                                    Listen to your test voice
                                </button>
                            )}
                        </div>
                        <audio ref={audioRef} src={testAudioUrl} onEnded={() => setIsPlayingVoice(false)} style={{ display: 'none' }} />
                    </div>
                )}
                </>
            )}

            {activeTab === 'sharing' && (
                <div className="sharing-tab">
                    <h3 style={{ marginTop: 0, marginBottom: '12px' }}>Workspace Members</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: 980 }}>
                    
                    {/* Debug: Show current workspace data */}
                    {hasPermission('admin') && (
                        <div
                            className="invite-section"
                            style={{
                                backgroundColor: 'var(--input-bg)',
                                border: '1px solid var(--input-border)',
                                borderRadius: '10px',
                                padding: '14px',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                                <div style={{ minWidth: 240 }}>
                                    <h4 style={{ margin: 0 }}>Invite members</h4>
                                    <p style={{ margin: '6px 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                                        Generate a link to invite others to this workspace.
                                    </p>
                                </div>
                                <button onClick={handleGenerateInvite} className="main-button" style={{ whiteSpace: 'nowrap', gap: '4px' }}>
                                <Share2 size={18} />
                                Generate Invite Link
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="members-section">
                        <div
                            style={{
                                backgroundColor: 'var(--input-bg)',
                                border: '1px solid var(--input-border)',
                                borderRadius: '10px',
                                padding: '14px',
                            }}
                        >
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                            <h4 style={{ margin: 0 }}>Members</h4>
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                {(currentWorkspace.members?.length || 0)} total
                            </div>
                        </div>
                        <div className="members-list">
                            {currentWorkspace.members?.map((member) => (
                                <div key={member.userId._id} className="member-item" style={{
                                    flexDirection: screenWidth <= 640 ? 'column' : 'row',
                                    alignItems: screenWidth <= 640 ? 'stretch' : 'center',
                                    backgroundColor: 'var(--background-primary)',
                                    border: '1px solid var(--input-border)',
                                    borderRadius: '10px',
                                    padding: '12px',
                                    gap: '10px',
                                    marginBottom: '10px'
                                }}>
                                    <div className="member-info" style={{
                                        flexDirection: screenWidth <= 500 ? 'row' : 'row',
                                        alignItems: 'center',
                                        marginBottom: screenWidth <= 640 ? '0' : '0',
                                        minWidth: 0,
                                        flex: 1
                                    }}>
                                        <div
                                            className="member-avatar"
                                            style={{
                                                ...(getOrbGradientForString(
                                                    member?.userId?.name ||
                                                    member?.userId?.email ||
                                                    member?.userId?._id
                                                ) || {})
                                            }}
                                        >
                                            {member.userId.name?.charAt(0)?.toUpperCase()}
                                        </div>
                                        <div className="member-details">
                                            <div className="member-name">{member.userId.name}</div>
                                            <div className="member-email">{member.userId.email}</div>
                                        </div>
                                    </div>

                                    <div className="member-actions" style={{
                                        flexDirection: screenWidth <= 640 ? 'row' : 'row',
                                        alignItems: 'center',
                                        justifyContent: screenWidth <= 640 ? 'space-between' : 'flex-end',
                                        gap: '10px',
                                        flexWrap: 'wrap'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            justifyContent: 'flex-start'
                                        }}>
                                            <span className={`role-badge role-${member.role}`}>
                                                {member.role}
                                            </span>
                                                
                                            {member.userId._id === currentUserId && (
                                                <span className="current-user" style={{ fontSize: '12px', opacity: 0.85 }}>You</span>
                                            )}
                                        </div>
                                            
                                        {hasPermission('admin') && member.userId._id !== currentUserId && (
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                justifyContent: 'flex-start'
                                            }}>
                                                <select
                                                    value={member.role}
                                                    onChange={(e) => handleRoleChange(member.userId._id, e.target.value)}
                                                    className="role-select"
                                                    style={{
                                                        minWidth: 140
                                                    }}
                                                >
                                                    <option value="viewer">Viewer</option>
                                                    <option value="editor">Editor</option>
                                                    <option value="admin">Admin</option>
                                                </select>
                                                <button 
                                                    onClick={() => handleRemoveMember(member.userId._id)}
                                                    className="remove-button"
                                                    title="Remove member"
                                                    aria-label="Remove member"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                ))}
                            </div>
                        </div>
                        </div>

                        {!hasPermission('admin') && (
                            <div
                                className="leave-section"
                                style={{
                                    backgroundColor: darkMode ? 'rgba(239, 68, 68, 0.10)' : 'rgba(239, 68, 68, 0.08)',
                                    border: `1px solid ${darkMode ? 'rgba(239, 68, 68, 0.28)' : 'rgba(239, 68, 68, 0.20)'}`,
                                    borderRadius: '10px',
                                    padding: '14px'
                                }}
                            >
                                <h4 style={{ margin: 0 }}>Leave workspace</h4>
                                <p style={{ margin: '6px 0 12px', fontSize: '13px', color: 'var(--text-secondary)' }}>You can leave this workspace if you no longer need access.</p>
                                <button onClick={handleLeaveWorkspace} className="danger-button">Leave Workspace</button>
                            </div>
                        )}

                        {hasPermission('admin') && (
                            <div
                                className="leave-section"
                                style={{
                                    backgroundColor: darkMode ? 'rgba(239, 68, 68, 0.10)' : 'rgba(239, 68, 68, 0.08)',
                                    border: `1px solid ${darkMode ? 'rgba(239, 68, 68, 0.28)' : 'rgba(239, 68, 68, 0.20)'}`,
                                    borderRadius: '10px',
                                    padding: '14px'
                                }}
                            >
                                <h4 style={{ margin: 0 }}>Delete workspace</h4>
                                <p style={{ margin: '6px 0 12px', fontSize: '13px', color: 'var(--text-secondary)' }}>Permanently delete this workspace and all its content. This action cannot be undone.</p>
                                <button onClick={handleDeleteWorkspace} className="danger-button">Delete Workspace</button>
                            </div>
                        )}
                    </div>
                    </div>
                )}
            </div>

            {/* Studio: pick raw videos for cut pool (matches mobile Studio source chips) */}
            {showStudioRawVideosPicker && (
                <div className="modal-overlay" onClick={closeStudioRawVideosPicker} style={{ zIndex: 10004 }}>
                    <div
                        className="modal-content"
                        style={{
                            maxWidth: '520px',
                            width: '92%',
                            maxHeight: '85vh',
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '20px',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h3 style={{ margin: 0, color: 'var(--text-color)', fontSize: '18px', fontWeight: 700 }}>Pick raw videos</h3>
                            <button
                                type="button"
                                aria-label="Close"
                                onClick={closeStudioRawVideosPicker}
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                    color: 'var(--text-color)',
                                    fontSize: '26px',
                                    lineHeight: 1,
                                    cursor: 'pointer',
                                    padding: '4px 8px',
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 14px 0', lineHeight: 1.45 }}>
                            Only cuts from these videos will be used for the next studio generation.
                        </p>
                        <div style={{ flex: 1, overflowY: 'auto', minHeight: '160px' }}>
                            {studioRawPickerLoading ? (
                                <div style={{ textAlign: 'center', padding: '28px', color: 'var(--text-secondary)' }}>
                                    <span className="spinner-border" style={{ marginRight: '8px' }} />
                                    Loading videos…
                                </div>
                            ) : studioRawPickerVideos.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)', fontSize: '14px' }}>
                                    No raw videos in this workspace yet. Use &quot;Import new&quot; to add footage.
                                </div>
                            ) : (
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                                        gap: '10px',
                                    }}
                                >
                                    {studioRawPickerVideos.map((v) => {
                                        const id = String(v?._id || '');
                                        if (!id) return null;
                                        const selectedIndex = studioSelectedRawVideoIds.indexOf(id);
                                        const selected = selectedIndex >= 0;
                                        return (
                                            <button
                                                key={id}
                                                type="button"
                                                onClick={() => {
                                                    setStudioSelectedRawVideoIds((prev) =>
                                                        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                                                    );
                                                }}
                                                style={{
                                                    position: 'relative',
                                                    padding: 0,
                                                    border: selected ? '2px solid #e1f679' : '1px solid var(--input-border)',
                                                    borderRadius: '10px',
                                                    overflow: 'hidden',
                                                    aspectRatio: '9 / 16',
                                                    cursor: 'pointer',
                                                    backgroundColor: 'var(--input-bg)',
                                                }}
                                            >
                                                {v?.url ? (
                                                    v?.thumbnailUrl ? (
                                                        <img
                                                            src={v.thumbnailUrl}
                                                            alt=""
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                        />
                                                    ) : (
                                                        <video
                                                            src={v.url}
                                                            muted
                                                            playsInline
                                                            preload="metadata"
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                        />
                                                    )
                                                ) : (
                                                    <div style={{ width: '100%', height: '100%', minHeight: '80px', background: 'var(--card-bg)' }} />
                                                )}
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        top: '6px',
                                                        right: '6px',
                                                        minWidth: '22px',
                                                        height: '22px',
                                                        padding: selected ? '0 6px' : 0,
                                                        borderRadius: selected ? '11px' : '50%',
                                                        border: '2px solid #fff',
                                                        backgroundColor: selected ? 'rgba(225,246,121,0.95)' : 'rgba(0,0,0,0.35)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
                                                        boxSizing: 'border-box',
                                                    }}
                                                >
                                                    {selected ? (
                                                        <span style={{ color: '#1a1a1a', fontSize: '11px', fontWeight: 900, lineHeight: 1 }}>
                                                            {selectedIndex + 1}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                            {studioRawPickerHasMore && !studioRawPickerLoading && studioRawPickerVideos.length > 0 && (
                                <div style={{ textAlign: 'center', padding: '14px 0 4px' }}>
                                    <button
                                        type="button"
                                        className="main-button"
                                        onClick={() => loadMoreStudioRawVideos()}
                                        disabled={studioRawPickerLoadingMore}
                                        style={{ padding: '8px 16px', fontSize: '13px' }}
                                    >
                                        {studioRawPickerLoadingMore ? 'Loading…' : 'Load more'}
                                    </button>
                                </div>
                            )}
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                gap: '10px',
                                justifyContent: 'flex-end',
                                marginTop: '16px',
                                paddingTop: '14px',
                                borderTop: darkMode ? '1px solid #333' : '1px solid #e0e0e0',
                                flexWrap: 'wrap',
                            }}
                        >
                            <button
                                type="button"
                                onClick={closeStudioRawVideosPicker}
                                style={{
                                    padding: '10px 16px',
                                    borderRadius: '6px',
                                    border: darkMode ? '1px solid #555' : '1px solid #ccc',
                                    background: 'var(--input-bg)',
                                    color: 'var(--text-color)',
                                    cursor: 'pointer',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="main-button"
                                onClick={() => {
                                    if (studioSelectedRawVideoIds.length === 0) return;
                                    setShowStudioRawVideosPicker(false);
                                }}
                                disabled={studioSelectedRawVideoIds.length === 0}
                                style={{ padding: '10px 16px', opacity: studioSelectedRawVideoIds.length === 0 ? 0.5 : 1 }}
                            >
                                Use selected ({studioSelectedRawVideoIds.length})
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Invite Modal */}
            {showInviteModal && (
                <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
                    <div className="modal-content" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ 
                            margin: '0 0 20px 0', 
                            color: darkMode ? '#fff' : '#333',
                            fontSize: '20px',
                            fontWeight: '600'
                        }}>
                            Invite Members
                        </h3>
                        <p style={{ 
                            margin: '0 0 20px 0', 
                            color: darkMode ? '#ccc' : '#666',
                            fontSize: '14px',
                            lineHeight: '1.5'
                        }}>
                            Share this link with others to invite them to your workspace:
                        </p>
                        <div style={{
                            display: 'flex',
                            gap: '10px',
                            marginBottom: '20px',
                            alignItems: 'center'
                        }}>
                            <input
                                type="text"
                                value={inviteLink}
                                readOnly
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    borderRadius: '6px',
                                    border: darkMode ? '1px solid #444' : '1px solid #ddd',
                                    backgroundColor: darkMode ? '#2A2B2F' : '#f8f9fa',
                                    color: darkMode ? '#fff' : '#333',
                                    fontSize: '14px',
                                    fontFamily: 'monospace',
                                    wordBreak: 'break-all'
                                }}
                            />
                            <button
                                onClick={copyInviteLink}
                                style={{
                                    backgroundColor: linkCopied ? '#28a745' : '#17a2b8',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '12px 16px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    transition: 'all 0.2s ease',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {linkCopied ? <Check size={16} /> : <Users size={16} />}
                                {linkCopied ? 'Copied!' : 'Copy Link'}
                            </button>
                        </div>
                        <div style={{
                            padding: '12px',
                            backgroundColor: darkMode ? '#1a1a1a' : '#f8f9fa',
                            borderRadius: '6px',
                            border: darkMode ? '1px solid #333' : '1px solid #e9ecef',
                            marginBottom: '20px'
                        }}>
                            <p style={{ 
                                margin: '0', 
                                fontSize: '12px', 
                                color: darkMode ? '#999' : '#666',
                                lineHeight: '1.4'
                            }}>
                                💡 <strong>Tip:</strong> If copying doesn't work, you can manually select and copy the link above.
                            </p>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowInviteModal(false)}
                                style={{
                                    backgroundColor: darkMode ? '#444' : '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '10px 20px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* No-script mode confirmation modal */}
            {showNoScriptConfirmModal && (
                <div className="modal" onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowNoScriptConfirmModal(false);
                    }
                }}>
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <h3 style={{ marginBottom: '16px', color: darkMode ? '#fff' : '#333' }}>
                            Switch to visual only (no script)
                        </h3>
                        <p style={{ 
                            marginBottom: '24px', 
                            color: '#666', 
                            fontSize: '16px',
                            lineHeight: '1.5'
                        }}>
                            This will delete your script.
                        </p>
                        <div className="modal-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button 
                                onClick={() => setShowNoScriptConfirmModal(false)}
                                style={{
                                    backgroundColor: 'var(--input-bg)',
                                    color: 'var(--text-color)',
                                    border: '1px solid var(--input-border)',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Keep script
                            </button>
                            <button 
                                onClick={confirmNoScriptSwitch}
                                style={{
                                    backgroundColor: 'var(--button-bg)',
                                    color: 'var(--button-text)',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Continue with visual only (no script)
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showBatchConfirmModal && (
                <div className="modal" onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        closeBatchConfirmModal();
                    }
                }}>
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <h3 style={{ marginBottom: '16px', color: darkMode ? '#fff' : '#333' }}>
                            {batchConfirmModal.title}
                        </h3>
                        <p style={{
                            marginBottom: '24px',
                            color: '#666',
                            fontSize: '16px',
                            lineHeight: '1.5'
                        }}>
                            {batchConfirmModal.message}
                        </p>
                        <div className="modal-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                                onClick={closeBatchConfirmModal}
                                style={{
                                    backgroundColor: 'var(--input-bg)',
                                    color: 'var(--text-color)',
                                    border: '1px solid var(--input-border)',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmBatchModalAction}
                                style={{
                                    backgroundColor: 'var(--button-bg)',
                                    color: 'var(--button-text)',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                {batchConfirmModal.confirmLabel}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Modal */}
            {showErrorModal && (
                <div className="modal" onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowErrorModal(false);
                    }
                }}>
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center' }}>
                        {modalErrorMessage !== VOICE_CLONE_LIMIT_MESSAGE && (
                        <div style={{ 
                            fontSize: '48px', 
                            marginBottom: '16px',
                            color: '#dc3545'
                        }}>
                            <AlertCircle size={48} />
                        </div>
                        )}
                        {modalErrorMessage !== VOICE_CLONE_LIMIT_MESSAGE && (
                            <h3 style={{ marginBottom: '16px', color: darkMode ? '#fff' : '#333' }}>
                                Error
                            </h3>
                        )}
                        <p style={{ 
                            marginBottom: '24px', 
                            color: '#666', 
                            fontSize: '16px',
                            lineHeight: '1.5'
                        }}>
                            {modalErrorMessage}
                        </p>
                        <div className="modal-actions" style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            {modalErrorMessage === VOICE_CLONE_LIMIT_MESSAGE && (
                                <button
                                    onClick={() => {
                                        setShowErrorModal(false);
                                        setShowUpgradePaywall(true);
                                        setActiveTab('studio');
                                    }}
                                    style={{
                                        backgroundColor: '#e1f679',
                                        color: '#111',
                                        border: 'none',
                                        padding: '10px 20px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '600'
                                    }}
                                >
                                    Upgrade
                                </button>
                            )}
                            <button 
                                onClick={() => setShowErrorModal(false)}
                                style={{
                                    backgroundColor: modalErrorMessage === VOICE_CLONE_LIMIT_MESSAGE ? 'transparent' : '#dc3545',
                                    color: modalErrorMessage === VOICE_CLONE_LIMIT_MESSAGE ? (darkMode ? '#ccc' : '#666') : 'white',
                                    border: modalErrorMessage === VOICE_CLONE_LIMIT_MESSAGE ? `1px solid ${darkMode ? '#444' : '#ddd'}` : 'none',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                {modalErrorMessage === VOICE_CLONE_LIMIT_MESSAGE ? 'Not now' : 'OK'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            {/* New multi-track NLE */}
            <NewNleEditor
                open={showNewNleEditor}
                onClose={() => {
                    setShowNewNleEditor(false);
                    setNleEditorSource(null);
                }}
                isBoukar={currentUserId === BOUKAR_USER_ID}
                voiceoverUrl={(() => {
                    if (nleEditorSource?.type === 'batch') {
                        return batchScripts.find((s) => String(s._id) === String(nleEditorSource.scriptId))?.voiceoverUrl || '';
                    }
                    return studioVoiceoverUrl;
                })()}
                voiceoverTimestamps={(() => {
                    if (nleEditorSource?.type === 'batch') {
                        return batchScripts.find((s) => String(s._id) === String(nleEditorSource.scriptId))?.voiceoverTimestamps;
                    }
                    return (studioScripts.find((s) => String(s._id) === String(studioSelectedScriptId))?.voiceoverTimestamps)
                        || lastVoiceoverTimestampsRef.current;
                })()}
                scriptContent={(() => {
                    if (nleEditorSource?.type === 'batch') {
                        const script = batchScripts.find((s) => String(s._id) === String(nleEditorSource.scriptId));
                        if (!script) return '';
                        return getBatchScriptContent(script, script.selectedVersion || 'main');
                    }
                    const script = studioScripts.find((s) => String(s._id) === String(studioSelectedScriptId));
                    if (!script) return '';
                    return getScriptContent(script, script.selectedVersion || 'main');
                })()}
                initialCuts={newNleCuts}
                availableCuts={newNleAvailableCuts}
                availableMusic={availableMusic}
                selectedMusicId={(() => {
                    if (nleEditorSource?.type === 'batch') {
                        const batchMusic = batchScriptMusicSelections[nleEditorSource.scriptId];
                        if (batchMusic?.musicId) return batchMusic.musicId;
                    }
                    return selectedMusicId || lastUsedMusicId || '';
                })()}
                useMusic={nleEditorSource?.type === 'batch'
                    ? (batchNleOverrideSettings ? !!batchNleOverrideSettings.useMusic : batchUseMusic)
                    : useMusic}
                flipbookIntro={batchNleOverrideSettings ? !!batchNleOverrideSettings.flipbookIntro : flipbookIntro}
                cutSilenceMode={batchNleOverrideSettings ? !!batchNleOverrideSettings.cutSilenceMode : cutSilenceMode}
                fastPacing={batchNleOverrideSettings ? !!batchNleOverrideSettings.fastPacing : fastPacing}
                enableSubtitles={batchNleOverrideSettings ? !!batchNleOverrideSettings.enableSubtitles : enableSubtitles}
                oneWordPerSubtitle={batchNleOverrideSettings ? !!batchNleOverrideSettings.oneWordPerSubtitle : oneWordPerSubtitle}
                subtitlePosition={batchNleOverrideSettings ? batchNleOverrideSettings.subtitlePosition : subtitlePosition}
                bigText={batchNleOverrideSettings ? !!batchNleOverrideSettings.bigText : bigText}
                glowText={batchNleOverrideSettings ? !!batchNleOverrideSettings.glowText : glowText}
                availableFonts={userFonts}
                selectedFontId={batchNleOverrideSettings ? (batchNleOverrideSettings.selectedFontId || '') : selectedFontId}
                subtitleFontColor={batchNleOverrideSettings ? batchNleOverrideSettings.subtitleFontColor : subtitleFontColor}
                subtitleOutlineColor={batchNleOverrideSettings ? batchNleOverrideSettings.subtitleOutlineColor : subtitleOutlineColor}
                subtitleOutlineEnabled={batchNleOverrideSettings ? !!batchNleOverrideSettings.subtitleOutlineEnabled : subtitleOutlineEnabled}
                openingLineInCenter={batchNleOverrideSettings ? !!batchNleOverrideSettings.openingLineInCenter : openingLineInCenter}
                openingLineTextSize={batchNleOverrideSettings ? batchNleOverrideSettings.openingLineTextSize : openingLineTextSize}
                openingLineFontId={batchNleOverrideSettings ? (batchNleOverrideSettings.openingLineFontId || '') : openingLineFontId}
                openingLineFontColor={batchNleOverrideSettings ? batchNleOverrideSettings.openingLineFontColor : openingLineFontColor}
                fontMixing={batchNleOverrideSettings ? !!batchNleOverrideSettings.fontMixing : fontMixing}
                centerStackOpening={batchNleOverrideSettings ? !!batchNleOverrideSettings.centerStackOpening : centerStackOpening}
                centerStackLineByLine={batchNleOverrideSettings ? !!batchNleOverrideSettings.centerStackLineByLine : centerStackLineByLine}
                fontMixingSecondColor={batchNleOverrideSettings ? batchNleOverrideSettings.fontMixingSecondColor : fontMixingSecondColor}
                includeTags={includeTags}
                excludeTags={excludeTags}
                availableTags={getAllExistingTags}
                onTagsChange={(inc, exc) => {
                    setIncludeTags(inc);
                    setExcludeTags(exc);
                }}
                loading={newNleCutsLoading}
                planningSfx={newNlePlanningSfx}
                exporting={
                    nleEditorSource?.type === 'batch'
                        ? batchGeneratingVideos.has(nleEditorSource.scriptId)
                        : studioGeneratingVideo
                }
                onRequestAutoSfx={handleNewNlePlanSfx}
                onLoadMoreCuts={loadMoreNewNleCuts}
                hasMoreCuts={newNleHasMoreCuts}
                loadingMoreCuts={newNleLoadingMore}
                onExport={handleNewNleExport}
                workspaceId={currentWorkspace?._id}
                darkMode={darkMode}
            />

            {/* Timeline editing modal (legacy) */}
            {showTimelineModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000
                }} onClick={closeTimelineModal}>
                    <div style={{
                        backgroundColor: 'var(--background-primary)',
                        borderRadius: '12px',
                        padding: '24px',
                        maxWidth: '700px',
                        width: '95%',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        border: '1px solid var(--input-border)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
                    }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text-color)' }}>Timeline Editor</h3>
                            <button type="button" onClick={closeTimelineModal} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text-color)' }}><X size={20} /></button>
                        </div>
                        {/* Hidden voiceover audio (transport lives in timeline strip) */}
                        {studioVoiceoverUrl && (
                            <audio
                                ref={timelineAudioRef}
                                src={studioVoiceoverUrl}
                                preload="metadata"
                                style={{ display: 'none' }}
                                onLoadedMetadata={() => {
                                    const el = timelineAudioRef.current;
                                    const dur = el?.duration;
                                    if (Number.isFinite(dur) && dur > 0) {
                                        setVoiceoverDuration((prev) => (prev > 0 ? prev : dur));
                                    }
                                }}
                                onTimeUpdate={() => {
                                    const el = timelineAudioRef.current;
                                    if (!el || timelineCuts.length === 0) return;
                                    const duration = el.duration;
                                    if (!Number.isFinite(duration) || duration <= 0) return;
                                    syncTimelinePlayhead(el.currentTime);
                                }}
                                onPlay={() => {
                                    timelineAudioPlayingRef.current = true;
                                    setTimelineAudioPlaying(true);
                                }}
                                onPause={() => {
                                    timelineAudioPlayingRef.current = false;
                                    setTimelineAudioPlaying(false);
                                }}
                                onEnded={() => {
                                    setTimelineActiveCutIndex(null);
                                    const totalSec = timelineCuts.reduce(
                                        (acc, c) => acc + timelineCutEffectiveSeconds(c, fastPacing),
                                        0
                                    );
                                    setTimelinePlayheadTime(totalSec);
                                    timelineAudioPlayingRef.current = false;
                                    setTimelineAudioPlaying(false);
                                }}
                            />
                        )}
                        {/* Timeline cuts + required count */}
                        <div style={{ marginBottom: '16px' }}>
                            {(() => {
                                const requiredCutCount = timelineRequiredCutCount;
                                const getEffectiveSec = (c) => {
                                    const d = (typeof c.duration === 'number' && c.duration >= 2) ? 2 : 1;
                                    return fastPacing && d === 2 ? 1 : d;
                                };
                                const timelineTotalSeconds = timelineCuts.reduce((acc, c) => acc + getEffectiveSec(c), 0);
                                const countValid = requiredCutCount > 0 && timelineTotalSeconds === requiredCutCount;
                                return (
                                    <>
                                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
                                            <span>Long press to drag & reorder</span>
                                            {requiredCutCount > 0 && (
                                                <span style={{ color: countValid ? 'var(--text-secondary)' : '#dc3545', fontWeight: 500, flex: '1 1 100%', minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                    {timelineTotalSeconds} / {Math.round(requiredCutCount)} sec required
                                                    {!countValid && timelineCuts.length > 0 && (
                                                        <span style={{ marginLeft: '4px', fontSize: '11px' }}>
                                                            {timelineTotalSeconds < requiredCutCount ? '— add more' : '— remove some'}
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ marginBottom: '8px' }}>
                <span style={{ display: 'block', fontSize: '13px', color: 'var(--text-color)', marginBottom: '8px' }}>Cut length</span>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                    <input
                                                        type="radio"
                                                        name="timeline-cut-length"
                                                        checked={!!fastPacing}
                                                        onChange={() => { setFastPacing(true); localStorage.setItem('workspace_fast_pacing', 'true'); }}
                                                        style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                    />
                                                    <span>1 sec</span>
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                    <input
                                                        type="radio"
                                                        name="timeline-cut-length"
                                                        checked={!fastPacing}
                                                        onChange={() => { setFastPacing(false); localStorage.setItem('workspace_fast_pacing', 'false'); }}
                                                        style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                                    />
                                                    <span>2 sec</span>
                                                </label>
                                            </div>
                                        </div>
                                    </>
                                );
                            })()}
                            {timelineCutsLoading ? (
                                <div style={{ padding: '20px', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '10px', width: '100%' }}>
                                    <FastSpinner size="small" color="var(--button-bg, #5c4bd3)" />
                                    <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Loading cuts…</span>
                                </div>
                            ) : timelineCuts.length === 0 ? (
                                <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '14px', width: '100%', backgroundColor: 'var(--input-bg)', borderRadius: '8px', border: '1px solid var(--input-border)' }}>No cuts in timeline. Add cuts below.</div>
                            ) : (
                                <TimelineCutStrip
                                    ref={timelineCutStripRef}
                                    cuts={timelineCuts}
                                    onCutsReorder={setTimelineCuts}
                                    fastPacing={fastPacing}
                                    playheadTime={timelinePlayheadTime}
                                    showPlayhead={!!studioVoiceoverUrl}
                                    isPlaying={timelineAudioPlaying}
                                    hasAudio={!!studioVoiceoverUrl}
                                    audioDuration={voiceoverDuration || timelineAudioRef.current?.duration || 0}
                                    onPlayPause={handleTimelineAudioPlayPause}
                                    onSkipToStart={handleTimelineSkipToStart}
                                    onScrubStart={handleTimelineScrubStart}
                                    onSeek={handleTimelineSeek}
                                    onScrubEnd={handleTimelineScrubEnd}
                                    onRemoveCut={timelineRemoveCut}
                                    renderThumb={(cut, { width, height }) => (
                                        <div
                                            style={{ width, height }}
                                            onMouseEnter={handleCutThumbMouseEnter}
                                            onMouseLeave={handleCutThumbMouseLeave}
                                        >
                                            <video
                                                src={cut.url}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
                                                muted
                                                playsInline
                                                preload="metadata"
                                            />
                                        </div>
                                    )}
                                />
                            )}
                        </div>
                        {/* Add cut */}
                        <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <button type="button" onClick={() => setShowTimelineAddCutPicker(prev => !prev)} style={{ padding: '8px 12px', fontSize: '13px', backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', color: 'var(--text-color)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                <Plus size={14} /> Add cut
                            </button>
                            {showTimelineAddCutPicker && (
                                <div
                                    style={{ marginTop: '8px', padding: '12px', backgroundColor: 'var(--input-bg)', borderRadius: '8px', border: '1px solid var(--input-border)', maxHeight: '240px', overflowY: 'auto', width: '100%', boxSizing: 'border-box' }}
                                    onScroll={(e) => {
                                        if (!timelineHasMoreCuts || timelineCutsLoadingMore) return;
                                        const el = e.currentTarget;
                                        const distanceFromBottom = (el.scrollHeight - el.scrollTop - el.clientHeight);
                                        if (distanceFromBottom < 200) loadMoreTimelineCuts();
                                    }}
                                >
                                    {timelineCutsLoading ? (
                                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '16px' }}>
                                            <FastSpinner size="small" color="var(--button-bg, #5c4bd3)" />
                                            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Loading…</span>
                                        </div>
                                    ) : timelineAvailableCuts.length === 0 ? (
                                        <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>No cuts to add.{includeTags.length > 0 || excludeTags.length > 0 ? ' Try adjusting tags.' : ''}</div>
                                    ) : (
                                        <>
                                            {/* Tags to include / exclude (same as studio; used when regenerating) */}
                                            <div style={{
                                                marginBottom: '16px',
                                                display: 'flex',
                                                flexDirection: screenWidth <= 600 ? 'column' : 'row',
                                                flexWrap: screenWidth <= 600 ? 'nowrap' : 'wrap',
                                                gap: screenWidth <= 600 ? '12px' : '16px',
                                                alignItems: screenWidth <= 600 ? 'stretch' : 'flex-start',
                                            }}>
                                                <div style={{ flex: screenWidth <= 600 ? 'none' : '1 1 260px', minWidth: 0 }}>
                                                    {includeTags.length > 0 && (
                                                        <div className="video-tags" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                                            {includeTags.map((tag, tagIndex) => (
                                                                <span key={`timeline-include-${tagIndex}-${tag}`} className="tag">
                                                                    {tag}
                                                                    <button type="button" className="remove-tag" onClick={() => handleRemoveIncludeTag(tag)}><X size={14} style={{ paddingTop: '3px' }} /></button>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <div style={{
                                                        display: 'flex',
                                                        flexDirection: screenWidth <= 600 ? 'column' : 'row',
                                                        alignItems: screenWidth <= 600 ? 'stretch' : 'center',
                                                        gap: screenWidth <= 600 ? '6px' : '8px',
                                                    }}>
                                                        <label style={{
                                                            display: 'block',
                                                            marginBottom: screenWidth <= 600 ? '0' : 0,
                                                            flexShrink: 0,
                                                            fontSize: '13px',
                                                            color: 'var(--text-secondary)',
                                                            whiteSpace: screenWidth <= 600 ? 'normal' : 'nowrap',
                                                        }}>Tags to include</label>
                                                        <div className="add-tag" style={{ position: 'relative', flex: screenWidth <= 600 ? undefined : 1, minWidth: 0 }}>
                                                            {renderTagInputWithSuggestions(
                                                                'include-tag-timeline',
                                                                includeTagInput,
                                                                (e) => setIncludeTagInput(e.target.value),
                                                                (e) => { if (e.key === 'Enter') handleAddIncludeTag(); },
                                                                handleAddIncludeTag,
                                                                showIncludeTagSuggestions,
                                                                setShowIncludeTagSuggestions,
                                                                includeTagInputRef,
                                                                'Add tag…',
                                                                {},
                                                                (tag) => {
                                                                    if (!includeTags.includes(tag)) {
                                                                        setIncludeTags(prev => [...prev, tag]);
                                                                        setIncludeTagInput('');
                                                                        setShowIncludeTagSuggestions(false);
                                                                    }
                                                                }
                                                            )}
                                                            <button type="button" onClick={handleAddIncludeTag} style={{ borderRadius: '20%', position: 'relative', marginLeft: '20px' }}><Plus size={14} style={{ paddingTop: '3px' }} /></button>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div style={{ flex: screenWidth <= 600 ? 'none' : '1 1 260px', minWidth: 0 }}>
                                                    {excludeTags.length > 0 && (
                                                        <div className="video-tags" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                                            {excludeTags.map((tag, tagIndex) => (
                                                                <span key={`timeline-exclude-${tagIndex}-${tag}`} className="tag">
                                                                    {tag}
                                                                    <button type="button" className="remove-tag" onClick={() => handleRemoveExcludeTag(tag)}><X size={14} style={{ paddingTop: '3px' }} /></button>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <div style={{
                                                        display: 'flex',
                                                        flexDirection: screenWidth <= 600 ? 'column' : 'row',
                                                        alignItems: screenWidth <= 600 ? 'stretch' : 'center',
                                                        gap: screenWidth <= 600 ? '6px' : '8px',
                                                    }}>
                                                        <label style={{
                                                            display: 'block',
                                                            marginBottom: screenWidth <= 600 ? '0' : 0,
                                                            flexShrink: 0,
                                                            fontSize: '13px',
                                                            color: 'var(--text-secondary)',
                                                            whiteSpace: screenWidth <= 600 ? 'normal' : 'nowrap',
                                                        }}>Tags to exclude</label>
                                                        <div className="add-tag" style={{ position: 'relative', flex: screenWidth <= 600 ? undefined : 1, minWidth: 0 }}>
                                                            {renderTagInputWithSuggestions(
                                                                'exclude-tag-timeline',
                                                                excludeTagInput,
                                                                (e) => setExcludeTagInput(e.target.value),
                                                                (e) => { if (e.key === 'Enter') handleAddExcludeTag(); },
                                                                handleAddExcludeTag,
                                                                showExcludeTagSuggestions,
                                                                setShowExcludeTagSuggestions,
                                                                excludeTagInputRef,
                                                                'Add tag…',
                                                                {},
                                                                (tag) => {
                                                                    if (!excludeTags.includes(tag)) {
                                                                        setExcludeTags(prev => [...prev, tag]);
                                                                        setExcludeTagInput('');
                                                                        setShowExcludeTagSuggestions(false);
                                                                    }
                                                                }
                                                            )}
                                                            <button type="button" onClick={handleAddExcludeTag} style={{ borderRadius: '20%', position: 'relative', marginLeft: '20px' }}><Plus size={14} style={{ paddingTop: '3px' }} /></button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(54px, 1fr))', gap: '8px' }}>
                                                {timelineAvailableCuts.map((cut) => (
                                                    <TimelineAddCutThumb
                                                        key={String(cut._id || cut.id)}
                                                        cut={cut}
                                                        fastPacing={fastPacing}
                                                        usageCount={timelineCutUsageById.get(String(cut._id || cut.id)) || 0}
                                                        onAdd={timelineAddCut}
                                                    />
                                                ))}
                                            </div>
                                            {timelineHasMoreCuts && (
                                                <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                                                    {timelineCutsLoadingMore && <FastSpinner size="small" color="var(--button-bg, #5c4bd3)" />}
                                                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                                        {timelineCutsLoadingMore ? 'Loading…' : 'Scroll to load more'}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            {(() => {
                                const canRegenerate = timelineCuts.length > 0;
                                return (
                                    <>
                                        <button type="button" onClick={closeTimelineModal} style={{ padding: '8px 16px', fontSize: '14px', backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', color: 'var(--text-color)', cursor: 'pointer' }}>Cancel</button>
                                        <button type="button" onClick={timelineRegenerateVideo} disabled={!canRegenerate} style={{ padding: '8px 16px', fontSize: '14px', backgroundColor: canRegenerate ? 'var(--button-bg)' : '#666', color: 'var(--button-text)', border: 'none', borderRadius: '6px', cursor: canRegenerate ? 'pointer' : 'not-allowed' }}>Regenerate video with this timeline</button>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* Text-on-screen studio: select cut modal */}
            {textStudioShowCutModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10000,
                    }}
                    onClick={() => setTextStudioShowCutModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: 'var(--background-primary)',
                            borderRadius: '12px',
                            padding: '24px',
                            maxWidth: '520px',
                            width: '95%',
                            maxHeight: '85vh',
                            overflow: 'auto',
                            border: '1px solid var(--input-border)',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text-color)' }}>Select cut for text video</h3>
                            <button type="button" onClick={() => setTextStudioShowCutModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text-color)' }}><X size={20} /></button>
                        </div>
                        {textStudioCutsLoading ? (
                            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading cuts…</div>
                        ) : !Array.isArray(textStudioCuts) || textStudioCuts.length === 0 ? (
                            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>No cuts in workspace. Add raw videos and generate cuts in the Vlog studio first.</div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))', gap: '10px', maxHeight: '320px', overflowY: 'auto' }}>
                                {textStudioCuts.map((cut) => {
                                    const isSelected = textStudioSelectedCut?._id === cut._id || textStudioSelectedCut?.id === cut.id;
                                    return (
                                        <button
                                            key={cut._id || cut.id}
                                            type="button"
                                            onClick={() => {
                                                setTextStudioSelectedCut(cut);
                                                setTextStudioShowCutModal(false);
                                            }}
                                            style={{
                                                padding: 0,
                                                border: isSelected ? '2px solid #10b981' : '2px solid var(--input-border)',
                                                borderRadius: '8px',
                                                overflow: 'hidden',
                                                cursor: 'pointer',
                                                background: 'none',
                                                transition: 'border-color 0.15s',
                                            }}
                                        >
                                            <video src={cut.url} style={{ width: '100%', aspectRatio: '9/16', objectFit: 'cover', display: 'block', pointerEvents: 'none' }} muted preload="metadata" />
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Batch text-based: select cut for video modal */}
            {batchTextCutModalScriptId && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10000,
                    }}
                    onClick={() => setBatchTextCutModalScriptId(null)}
                >
                    <div
                        style={{
                            backgroundColor: 'var(--background-primary)',
                            borderRadius: '12px',
                            padding: '24px',
                            maxWidth: '520px',
                            width: '95%',
                            maxHeight: '85vh',
                            overflow: 'auto',
                            border: '1px solid var(--input-border)',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text-color)' }}>Select cut for text-based video</h3>
                            <button type="button" onClick={() => setBatchTextCutModalScriptId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text-color)' }}><X size={20} /></button>
                        </div>
                        {batchTextCutsLoading ? (
                            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading cuts…</div>
                        ) : !Array.isArray(batchTextAvailableCuts) || batchTextAvailableCuts.length === 0 ? (
                            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>No cuts in workspace. Upload cuts in Studio first.</div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))', gap: '10px', maxHeight: '320px', overflowY: 'auto' }}>
                                {batchTextAvailableCuts.map((cut) => {
                                    const isSelected = batchTextSelectedCutByScriptId[batchTextCutModalScriptId]?._id === cut._id || batchTextSelectedCutByScriptId[batchTextCutModalScriptId]?.id === cut.id;
                                    return (
                                        <button
                                            key={cut._id || cut.id}
                                            type="button"
                                            onClick={() => {
                                                setBatchTextSelectedCutByScriptId((prev) => ({ ...prev, [batchTextCutModalScriptId]: cut }));
                                                setBatchTextCutModalScriptId(null);
                                            }}
                                            style={{
                                                padding: 0,
                                                border: isSelected ? '2px solid #10b981' : '2px solid var(--input-border)',
                                                borderRadius: '8px',
                                                overflow: 'hidden',
                                                cursor: 'pointer',
                                                background: 'none',
                                                transition: 'border-color 0.15s',
                                            }}
                                        >
                                            <video src={cut.url} style={{ width: '100%', aspectRatio: '9/16', objectFit: 'cover', display: 'block', pointerEvents: 'none' }} muted preload="metadata" />
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Thumbnail configuration modal */}
            {showThumbnailModal && (() => {
                const isBatchThumbnail = !!batchThumbnailModalScriptId;
                const batchConfig = isBatchThumbnail ? (batchThumbnailByScriptId[batchThumbnailModalScriptId] || {}) : null;
                const modalTitle = isBatchThumbnail ? (batchConfig.title ?? '') : thumbnailTitle;
                const modalCut = isBatchThumbnail ? (batchConfig.cut ?? null) : thumbnailCut;
                const modalBigText = isBatchThumbnail ? (batchConfig.bigText !== false) : thumbnailBigText;
                const modalFontId = thumbnailFontId;
                const modalFontColor = thumbnailFontColor;
                const modalFontName = modalFontId
                    ? (userFonts.find(f => String(f._id) === String(modalFontId))?.name || 'Select font...')
                    : 'Default (TikTok medium)';
                const closeModal = () => {
                    setShowThumbnailModal(false);
                    setBatchThumbnailModalScriptId(null);
                };
                const confirmModal = () => {
                    if (!modalTitle.trim() || !modalCut) return;
                    if (!isBatchThumbnail) setCreateThumbnail(true);
                    closeModal();
                };
                const setModalTitle = (v) => {
                    if (isBatchThumbnail) setBatchThumbnailByScriptId(prev => ({ ...prev, [batchThumbnailModalScriptId]: { ...batchConfig, title: v } }));
                    else setThumbnailTitle(v);
                };
                const setModalCut = (cut) => {
                    if (isBatchThumbnail) setBatchThumbnailByScriptId(prev => ({ ...prev, [batchThumbnailModalScriptId]: { ...batchConfig, cut } }));
                    else setThumbnailCut(cut);
                };
                const setModalBigText = (v) => {
                    if (isBatchThumbnail) setBatchThumbnailByScriptId(prev => ({ ...prev, [batchThumbnailModalScriptId]: { ...batchConfig, bigText: v } }));
                    else setThumbnailBigText(v);
                };
                return (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10002
                }} onClick={closeModal}>
                    <div style={{
                        backgroundColor: 'var(--background-primary)',
                        borderRadius: '12px',
                        padding: '24px',
                        maxWidth: '560px',
                        width: '95%',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        border: '1px solid var(--input-border)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
                    }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text-color)' }}>Configure thumbnail{isBatchThumbnail ? ' (this video)' : ''}</h3>
                            <button type="button" onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text-color)' }}><X size={20} /></button>
                        </div>
                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>Pick a cut and add a title. The first frame of the cut with this title will be shown at the start of the video (0.1s).</p>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Title (shown on thumbnail)</label>
                            <textarea
                                value={modalTitle}
                                onChange={(e) => setModalTitle(e.target.value)}
                                placeholder="Enter title…"
                                rows={3}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    border: '1px solid var(--input-border)',
                                    background: 'var(--input-bg)',
                                    color: 'var(--text-color)',
                                    fontSize: '14px',
                                    resize: 'vertical',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px', color: 'var(--text-color)' }}>
                                <input
                                    type="radio"
                                    name="thumbnailTextSize"
                                    checked={modalBigText}
                                    onChange={() => setModalBigText(true)}
                                    style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                />
                                <span style={{ marginLeft: '6px' }}>Big text</span>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px', color: 'var(--text-color)' }}>
                                <input
                                    type="radio"
                                    name="thumbnailTextSize"
                                    checked={!modalBigText}
                                    onChange={() => setModalBigText(false)}
                                    style={{ cursor: 'pointer', accentColor: 'var(--button-bg)' }}
                                />
                                <span style={{ marginLeft: '6px' }}>Small text</span>
                            </label>
                        </div>
                        <div style={{ marginBottom: '16px', display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', alignItems: 'end' }}>
                            <div style={{ position: 'relative' }} data-thumbnail-font-dropdown>
                                <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Font</label>
                                <button
                                    type="button"
                                    onClick={() => setShowThumbnailFontDropdown(!showThumbnailFontDropdown)}
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        borderRadius: '8px',
                                        border: '1px solid var(--input-border)',
                                        background: 'var(--input-bg)',
                                        color: 'var(--text-color)',
                                        fontSize: '14px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        textAlign: 'left',
                                    }}
                                >
                                    <span style={{
                                        fontFamily: modalFontId
                                            ? fontFamilyCssForName(userFonts.find(f => String(f._id) === String(modalFontId))?.name)
                                            : defaultFontFamilyCss,
                                    }}>
                                        {modalFontName}
                                    </span>
                                    <span style={{ fontSize: '12px', opacity: 0.7 }}>{showThumbnailFontDropdown ? '▲' : '▼'}</span>
                                </button>
                                {showThumbnailFontDropdown && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        backgroundColor: 'var(--input-bg)',
                                        border: '1px solid var(--input-border)',
                                        borderRadius: '8px',
                                        marginTop: '4px',
                                        zIndex: 1000,
                                        maxHeight: '180px',
                                        overflowY: 'auto',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
                                    }}>
                                        <div
                                            style={{
                                                padding: '8px 12px',
                                                cursor: 'pointer',
                                                backgroundColor: !modalFontId ? 'var(--button-bg)' : 'transparent',
                                                color: !modalFontId ? 'var(--button-text)' : 'var(--text-color)',
                                                fontFamily: defaultFontFamilyCss,
                                            }}
                                            onClick={() => { handleThumbnailFontChange(''); setShowThumbnailFontDropdown(false); }}
                                        >
                                            Default (TikTok medium)
                                        </div>
                                        {sortedUserFonts.map(font => (
                                            <div
                                                key={font._id}
                                                style={{
                                                    padding: '8px 12px',
                                                    cursor: 'pointer',
                                                    backgroundColor: String(modalFontId) === String(font._id) ? 'var(--button-bg)' : 'transparent',
                                                    color: String(modalFontId) === String(font._id) ? 'var(--button-text)' : 'var(--text-color)',
                                                    fontFamily: fontFamilyCssForName(font.name),
                                                }}
                                                onClick={() => { handleThumbnailFontChange(font._id); setShowThumbnailFontDropdown(false); }}
                                            >
                                                {font.name}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Text color</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }} data-thumbnail-color-picker>
                                    <input
                                        type="text"
                                        value={modalFontColor}
                                        onChange={(e) => handleThumbnailColorChange(e.target.value)}
                                        placeholder="#ffffff"
                                        style={{
                                            width: '70px',
                                            fontSize: '12px',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            border: darkMode ? '1px solid var(--input-border)' : '1px solid var(--input-border)',
                                            backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                            color: darkMode ? '#fff' : '#333'
                                        }}
                                    />
                                    <input
                                        type="color"
                                        value={modalFontColor}
                                        onChange={(e) => handleThumbnailColorChange(e.target.value)}
                                        style={{
                                            width: '30px',
                                            height: '30px',
                                            border: 'none',
                                            borderRadius: '3px',
                                            cursor: 'pointer'
                                        }}
                                    />
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="color"
                                            ref={thumbnailFontColorInputRef}
                                            value={modalFontColor}
                                            onChange={(e) => handleThumbnailColorChange(e.target.value)}
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '30px',
                                                height: '30px',
                                                border: 'none',
                                                borderRadius: '3px',
                                                cursor: 'pointer',
                                                opacity: 0,
                                                zIndex: 1
                                            }}
                                        />
                                        <img
                                            src="/color-picker.png"
                                            alt="Color Picker"
                                            style={{
                                                width: '25px',
                                                height: '25px',
                                                cursor: 'pointer',
                                                position: 'relative',
                                                zIndex: 0,
                                                top: 2
                                            }}
                                            onClick={() => {
                                                if (thumbnailFontColorInputRef.current) {
                                                    thumbnailFontColorInputRef.current.click();
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '8px' }}>
                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Cut for thumbnail</div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!Array.isArray(thumbnailAvailableCuts) || thumbnailAvailableCuts.length === 0) return;
                                        let candidates = thumbnailAvailableCuts;
                                        if (modalCut?._id && thumbnailAvailableCuts.length > 1) {
                                            const filtered = thumbnailAvailableCuts.filter(c => c?._id !== modalCut._id);
                                            if (filtered.length > 0) candidates = filtered;
                                        }
                                        const chosen = candidates[Math.floor(Math.random() * candidates.length)];
                                        if (!chosen) return;
                                        setModalCut(chosen);
                                        // Reset preview to first frame after selecting a new cut
                                        setTimeout(() => {
                                            const el = thumbnailPreviewVideoRef.current;
                                            if (el) el.currentTime = 0;
                                        }, 80);
                                    }}
                                    disabled={thumbnailCutsLoading || !Array.isArray(thumbnailAvailableCuts) || thumbnailAvailableCuts.length === 0}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '6px 10px',
                                        fontSize: '12px',
                                        borderRadius: '8px',
                                        border: '1px solid var(--input-border)',
                                        background: 'var(--input-bg)',
                                        color: 'var(--text-color)',
                                        cursor: (thumbnailCutsLoading || !Array.isArray(thumbnailAvailableCuts) || thumbnailAvailableCuts.length === 0) ? 'not-allowed' : 'pointer',
                                        opacity: (thumbnailCutsLoading || !Array.isArray(thumbnailAvailableCuts) || thumbnailAvailableCuts.length === 0) ? 0.55 : 1,
                                    }}
                                    title="Pick a random cut"
                                >
                                    <RefreshCw size={14} />
                                    Random
                                </button>
                            </div>
                            {thumbnailCutsLoading ? (
                                <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '14px' }}>Loading cuts…</div>
                            ) : !Array.isArray(thumbnailAvailableCuts) || thumbnailAvailableCuts.length === 0 ? (
                                <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '14px' }}>No cuts in workspace.</div>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                                    {thumbnailAvailableCuts.map(cut => (
                                        <button
                                            key={cut._id}
                                            type="button"
                                            onClick={() => setModalCut(cut)}
                                            style={{
                                                padding: 0,
                                                border: modalCut?._id === cut._id ? '2px solid var(--button-bg)' : '2px solid var(--input-border)',
                                                borderRadius: '8px',
                                                overflow: 'hidden',
                                                cursor: 'pointer',
                                                background: 'none',
                                                transition: 'border-color 0.15s'
                                            }}
                                            onMouseEnter={handleCutThumbMouseEnter}
                                            onMouseLeave={handleCutThumbMouseLeave}
                                        >
                                            <video src={cut.url} style={{ width: '100%', aspectRatio: '9/16', objectFit: 'cover', display: 'block', pointerEvents: 'none', filter: THUMBNAIL_COLOR_GRADE_CSS_FILTER }} muted playsInline preload="metadata" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        {modalCut && (
                            <div style={{ marginBottom: '16px' }}>
                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Preview (first frame)</div>
                                <div style={{ position: 'relative', width: '100%', maxWidth: '280px', margin: '0 auto', aspectRatio: '9/16', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#000', border: '1px solid var(--input-border)' }}>
                                    <video
                                        ref={thumbnailPreviewVideoRef}
                                        src={modalCut.url}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: THUMBNAIL_COLOR_GRADE_CSS_FILTER }}
                                        muted
                                        preload="metadata"
                                        onLoadedMetadata={(e) => { e.target.currentTime = 0; }}
                                        onSeeked={(e) => { e.target.currentTime = 0; }}
                                    />
                                    {modalTitle.trim() && (
                                        <div style={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '12px',
                                            textAlign: 'center',
                                            pointerEvents: 'none'
                                        }}>
                                            <span style={{
                                                color: modalFontColor || '#ffffff',
                                                fontSize: modalBigText ? '56px' : '36px',
                                                fontWeight: 'bold',
                                                fontFamily: modalFontId
                                                    ? fontFamilyCssForName(userFonts.find(f => String(f._id) === String(modalFontId))?.name)
                                                    : defaultFontFamilyCss,
                                                lineHeight: 1.3,
                                                whiteSpace: 'pre-line',
                                                textShadow: (subtitleOutlineColor || '#000000') ? `0 0 4px ${subtitleOutlineColor}, 0 0 6px ${subtitleOutlineColor}, 0 1px 2px ${subtitleOutlineColor}, 1px 1px 0 ${subtitleOutlineColor}, -1px -1px 0 ${subtitleOutlineColor}, 1px -1px 0 ${subtitleOutlineColor}, -1px 1px 0 ${subtitleOutlineColor}` : 'none',
                                                maxWidth: '100%'
                                            }}>
                                                {formatThumbnailTitleForRender(modalTitle, modalBigText)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button type="button" onClick={closeModal} style={{ padding: '8px 16px', fontSize: '14px', backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', color: 'var(--text-color)', cursor: 'pointer' }}>Cancel</button>
                            <button
                                type="button"
                                onClick={confirmModal}
                                disabled={!modalTitle.trim() || !modalCut}
                                style={{
                                    padding: '8px 16px',
                                    fontSize: '14px',
                                    backgroundColor: 'var(--button-bg)',
                                    color: 'var(--button-text)',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: (!modalTitle.trim() || !modalCut) ? 'not-allowed' : 'pointer',
                                    opacity: (!modalTitle.trim() || !modalCut) ? 0.55 : 1,
                                }}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
                );
            })()}

            {/* Publish Modal */}
            {showPublishModal.videoId && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000
                }}>
                    <div style={{
                        backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                        borderRadius: '8px',
                        padding: '24px',
                        maxWidth: '500px',
                        width: '90%',
                        maxHeight: '90vh',
                        overflow: 'auto'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                            <h3 style={{ margin: 0 }}>Publish</h3>
                            {currentWorkspace?._id && (
                                <a
                                    href={`/calendar/${currentWorkspace._id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        border: '1px solid var(--input-border)',
                                        backgroundColor: 'var(--input-bg)',
                                        color: 'var(--text-color)',
                                        textDecoration: 'none',
                                        fontSize: '13px'
                                    }}
                                >
                                    <Calendar size={14} />
                                    View calendar
                                </a>
                            )}
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>Caption</label>
                            <TextareaAutosize
                                value={publishCaption}
                                onChange={(e) => setPublishCaption(e.target.value)}
                                placeholder="Enter caption for your post..."
                                minRows={3}
                                maxRows={6}
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#1a1a1a' : '#fff',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: SYSTEM_UI_FONT,
                                    fontSize: '14px',
                                    resize: 'vertical'
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>Platforms</label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '14px', cursor: getlateConnectedAccounts.instagram ? 'pointer' : 'default', opacity: getlateConnectedAccounts.instagram ? 1 : 0.6 }}>
                                <input
                                    type="checkbox"
                                    checked={publishPlatformInstagram}
                                    onChange={(e) => setPublishPlatformInstagram(e.target.checked)}
                                    disabled={!getlateConnectedAccounts.instagram}
                                    style={{ cursor: getlateConnectedAccounts.instagram ? 'pointer' : 'not-allowed' }}
                                />
                                <span>Instagram</span>
                                {!getlateConnectedAccounts.instagram && <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>(connect first)</span>}
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '14px', cursor: getlateConnectedAccounts.tiktok ? 'pointer' : 'default', opacity: getlateConnectedAccounts.tiktok ? 1 : 0.6 }}>
                                <input
                                    type="checkbox"
                                    checked={publishPlatformTiktok}
                                    onChange={(e) => setPublishPlatformTiktok(e.target.checked)}
                                    disabled={!getlateConnectedAccounts.tiktok}
                                    style={{ cursor: getlateConnectedAccounts.tiktok ? 'pointer' : 'not-allowed' }}
                                />
                                <span>TikTok</span>
                                {!getlateConnectedAccounts.tiktok && <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>(connect first)</span>}
                            </label>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name="publishMode"
                                    checked={publishMode === 'now'}
                                    onChange={() => setPublishMode('now')}
                                    style={{ cursor: 'pointer' }}
                                />
                                <span>Publish Now</span>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name="publishMode"
                                    checked={publishMode === 'schedule'}
                                    onChange={() => setPublishMode('schedule')}
                                    style={{ cursor: 'pointer' }}
                                />
                                <span>Schedule</span>
                            </label>
                        </div>
                        {publishMode === 'schedule' && (
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', fontSize: '14px', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={publishScheduleAfterLast}
                                    onChange={(e) => setPublishScheduleAfterLast(e.target.checked)}
                                    style={{ cursor: 'pointer' }}
                                />
                                <span>Autoschedule after last scheduled post</span>
                            </label>
                            {publishScheduleAfterLast ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                                    <input
                                        type="number"
                                        min={1}
                                        value={publishAfterLastValue}
                                        onChange={(e) => setPublishAfterLastValue(Math.max(1, parseInt(e.target.value, 10) || 1))}
                                        style={{
                                            width: '64px',
                                            padding: '6px 8px',
                                            borderRadius: '4px',
                                            border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                            backgroundColor: darkMode ? '#1a1a1a' : '#fff',
                                            color: darkMode ? '#fff' : '#333',
                                            fontSize: '14px'
                                        }}
                                    />
                                    <select
                                        value={publishAfterLastUnit}
                                        onChange={(e) => setPublishAfterLastUnit(e.target.value)}
                                        style={{
                                            padding: '6px 8px',
                                            borderRadius: '4px',
                                            border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                            backgroundColor: darkMode ? '#1a1a1a' : '#fff',
                                            color: darkMode ? '#fff' : '#333',
                                            fontSize: '14px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <option value="hours">hours</option>
                                        <option value="days">days</option>
                                    </select>
                                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>after last post</span>
                                </div>
                            ) : (
                                <>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>Schedule Date</label>
                            <input
                                type="date"
                                value={publishScheduleDate}
                                onChange={(e) => setPublishScheduleDate(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#1a1a1a' : '#fff',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    fontSize: '14px',
                                    marginBottom: '8px'
                                }}
                            />
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>Schedule Time</label>
                            <input
                                type="time"
                                value={publishScheduleTime}
                                onChange={(e) => setPublishScheduleTime(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#1a1a1a' : '#fff',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    fontSize: '14px'
                                }}
                            />
                            </>
                            )}
                        </div>
                        )}
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => {
                                    setShowPublishModal({ videoId: null });
                                    setPublishCaption('');
                                    setPublishScheduleDate('');
                                    setPublishScheduleTime('');
                                    setPublishMode('now');
                                    setPublishScheduleAfterLast(false);
                                }}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#1a1a1a' : '#f8f9fa',
                                    color: darkMode ? '#fff' : '#333',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={async () => {
                                    const video = generatedVideos.find(v => v._id === showPublishModal.videoId);
                                    if (!video) return;
                                    const publishNow = publishMode === 'now';
                                    let opts = publishNow
                                        ? { caption: publishCaption || '', scheduleAt: null }
                                        : {};
                                    if (!publishNow) {
                                        if (publishScheduleAfterLast && workspaceScheduledPosts.length > 0) {
                                            const sorted = [...workspaceScheduledPosts].sort((a, b) => new Date(b.scheduledAt) - new Date(a.scheduledAt));
                                            const lastAt = new Date(sorted[0].scheduledAt).getTime();
                                            const offsetMs = publishAfterLastValue * (publishAfterLastUnit === 'hours' ? 3600000 : 86400000);
                                            const now = Date.now();
                                            const base = lastAt > now ? lastAt : now;
                                            opts = { caption: publishCaption || '', scheduleAt: new Date(base + offsetMs).toISOString() };
                                        } else {
                                            const dateTime = `${publishScheduleDate}T${publishScheduleTime}`;
                                            opts = { caption: publishCaption || '', scheduleAt: new Date(dateTime).toISOString() };
                                        }
                                    }
                                    const platforms = [];
                                    if (publishPlatformInstagram && getlateConnectedAccounts.instagram) platforms.push('instagram');
                                    if (publishPlatformTiktok && getlateConnectedAccounts.tiktok) platforms.push('tiktok');
                                    if (platforms.length === 0) return;
                                    if (publishNow) {
                                        setShowPublishModal({ videoId: null });
                                        setPublishCaption('');
                                        setPublishScheduleDate('');
                                        setPublishScheduleTime('');
                                        setPublishMode('now');
                                        setPublishScheduleAfterLast(false);
                                    }
                                    const results = await Promise.all(platforms.map(platform => handleGetlatePublish(video, platform, publishNow, opts)));
                                    clearPublishingForVideo(video._id);
                                    const succeeded = results.filter(r => r && r.success);
                                    const failed = results.filter(r => r && !r.success);
                                    failed.forEach(r => setErrors(prev => [...prev, `Failed to publish to ${r.platform}: ${r.error || 'Unknown error'}`]));
                                    const verb = publishNow ? 'published to' : 'scheduled for';
                                    if (succeeded.length > 0) {
                                        const names = succeeded.map(r => r.platform.charAt(0).toUpperCase() + r.platform.slice(1));
                                        const message = names.length === 2
                                            ? `Video ${verb} Instagram and TikTok successfully!`
                                            : `Video ${verb} ${names[0]} successfully!`;
                                        if (failed.length > 0) {
                                            setModalSuccessMessage(message + ' ' + failed.map(r => `${r.platform} failed: ${r.error}`).join('. '));
                                        } else {
                                            setModalSuccessMessage(message);
                                        }
                                        setShowSuccessModal(true);
                                        setPublishCaption('');
                                        setPublishScheduleDate('');
                                        setPublishScheduleTime('');
                                        setPublishMode('now');
                                        setPublishScheduleAfterLast(false);
                                        setShowPublishModal({ videoId: null });
                                        if (currentWorkspace?._id) {
                                            api.getScheduledPosts(currentWorkspace._id)
                                                .then((data) => setWorkspaceScheduledPosts(Array.isArray(data) ? data : []))
                                                .catch(() => {});
                                        }
                                    }
                                }}
                                disabled={getlatePublishing.has(showPublishModal.videoId) || (publishMode === 'schedule' && publishScheduleAfterLast && workspaceScheduledPosts.length === 0) || (publishMode === 'schedule' && !publishScheduleAfterLast && (!publishScheduleDate || !publishScheduleTime)) || !((publishPlatformInstagram && getlateConnectedAccounts.instagram) || (publishPlatformTiktok && getlateConnectedAccounts.tiktok))}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: 'none',
                                    backgroundColor: '#E4405F',
                                    color: 'white',
                                    cursor: (getlatePublishing.has(showPublishModal.videoId) || (publishMode === 'schedule' && publishScheduleAfterLast && workspaceScheduledPosts.length === 0) || (publishMode === 'schedule' && !publishScheduleAfterLast && (!publishScheduleDate || !publishScheduleTime)) || !((publishPlatformInstagram && getlateConnectedAccounts.instagram) || (publishPlatformTiktok && getlateConnectedAccounts.tiktok))) ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    opacity: (getlatePublishing.has(showPublishModal.videoId) || (publishMode === 'schedule' && publishScheduleAfterLast && workspaceScheduledPosts.length === 0) || (publishMode === 'schedule' && !publishScheduleAfterLast && (!publishScheduleDate || !publishScheduleTime)) || !((publishPlatformInstagram && getlateConnectedAccounts.instagram) || (publishPlatformTiktok && getlateConnectedAccounts.tiktok))) ? 0.6 : 1
                                }}
                            >
                                {getlatePublishing.has(showPublishModal.videoId) 
                                    ? 'Publishing...' 
                                    : publishMode === 'now' 
                                        ? 'Publish Now' 
                                        : 'Schedule'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/*
              Insufficient cuts modal disabled.
              We now always proceed with the repeated-cuts method when cuts are insufficient.
            */}

            {showSuccessModal && (
                <div className="modal" onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowSuccessModal(false);
                    }
                }}>
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <div style={{ 
                            fontSize: '48px', 
                            marginBottom: '16px',
                            color: '#28a745'
                        }}>
                            <CheckCircle size={48} />
                        </div>
                        <h3 style={{ marginBottom: '16px', color: darkMode ? '#fff' : '#333' }}>
                            Success!
                        </h3>
                        <p style={{ 
                            marginBottom: '24px', 
                            color: '#666', 
                            fontSize: '16px',
                            lineHeight: '1.5'
                        }}>
                            {modalSuccessMessage}
                        </p>
                        <div className="modal-actions">
                            <button 
                                onClick={() => setShowSuccessModal(false)}
                                style={{
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Workspace Confirmation Modal */}
            {showDeleteWorkspaceModal && (
                <div className="modal" onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowDeleteWorkspaceModal(false);
                    }
                }}>
                    <div className="modal-content" style={{ maxWidth: '500px', textAlign: 'center' }}>
                        <div style={{ 
                            fontSize: '48px', 
                            marginBottom: '16px',
                            color: '#dc3545'
                        }}>
                            <AlertCircle size={48} />
                        </div>
                        <h3 style={{ marginBottom: '16px', color: darkMode ? '#fff' : '#333' }}>
                            Delete Workspace
                        </h3>
                        <p style={{ 
                            marginBottom: '24px', 
                            color: '#666', 
                            fontSize: '16px',
                            lineHeight: '1.5',
                            textAlign: 'left'
                        }}>
                            Are you sure you want to delete "{currentWorkspace?.name}"? This will permanently delete:
                            <br /><br />
                            • All workspace data and settings<br />
                            • All videos and their cuts<br />
                            • All generated videos<br />
                            • All member access<br />
                            • All voice cloning data<br /><br />
                            <strong>This action cannot be undone.</strong>
                        </p>
                        <div className="modal-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <button 
                                onClick={() => setShowDeleteWorkspaceModal(false)}
                                style={{
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={confirmDeleteWorkspace}
                                style={{
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Delete Workspace
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove Member Confirmation Modal */}
            {showRemoveMemberModal && (
                <div className="modal" onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowRemoveMemberModal(false);
                    }
                }}>
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <div style={{ 
                            fontSize: '48px', 
                            marginBottom: '16px',
                            color: '#dc3545'
                        }}>
                            <AlertCircle size={48} />
                        </div>
                        <h3 style={{ marginBottom: '16px', color: darkMode ? '#fff' : '#333' }}>
                            Remove Member
                        </h3>
                        <p style={{ 
                            marginBottom: '24px', 
                            color: '#666', 
                            fontSize: '16px',
                            lineHeight: '1.5'
                        }}>
                            Are you sure you want to remove this member from the workspace?
                        </p>
                        <div className="modal-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <button 
                                onClick={() => setShowRemoveMemberModal(false)}
                                style={{
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={confirmRemoveMember}
                                style={{
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Remove Member
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Leave Workspace Confirmation Modal */}
            {showLeaveWorkspaceModal && (
                <div className="modal" onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowLeaveWorkspaceModal(false);
                    }
                }}>
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <div style={{ 
                            fontSize: '48px', 
                            marginBottom: '16px',
                            color: '#dc3545'
                        }}>
                            <AlertCircle size={48} />
                        </div>
                        <h3 style={{ marginBottom: '16px', color: darkMode ? '#fff' : '#333' }}>
                            Leave Workspace
                        </h3>
                        <p style={{ 
                            marginBottom: '24px', 
                            color: '#666', 
                            fontSize: '16px',
                            lineHeight: '1.5'
                        }}>
                            Are you sure you want to leave this workspace? You will lose access to all workspace content.
                        </p>
                        <div className="modal-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <button 
                                onClick={() => setShowLeaveWorkspaceModal(false)}
                                style={{
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={confirmLeaveWorkspace}
                                style={{
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Leave Workspace
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Recording Confirmation Modal */}
            {showDeleteRecordingModal && (
                <div className="modal" onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowDeleteRecordingModal(false);
                        setRecordingToDelete(null);
                    }
                }}>
                    <div className="modal-content" style={{ maxWidth: '500px', textAlign: 'center' }}>
                        <div style={{ 
                            fontSize: '48px', 
                            marginBottom: '16px',
                            color: '#dc3545'
                        }}>
                            <AlertCircle size={48} />
                        </div>
                        <h3 style={{ marginBottom: '16px', color: darkMode ? '#fff' : '#333' }}>
                            Delete Last Recording
                        </h3>
                        <p style={{ 
                            marginBottom: '24px', 
                            color: '#666', 
                            fontSize: '16px',
                            lineHeight: '1.5',
                            textAlign: 'left'
                        }}>
                            This is the last recording. Deleting it will also delete your cloned voice because there won't be any recordings left to re-clone it.
                            <br /><br />
                            <strong>This means:</strong>
                            <br />
                            • Your cloned voice will be permanently deleted<br />
                            • You'll need to record new samples to create a new cloned voice<br />
                            • Any future voiceover generation will use the default voice<br /><br />
                            <strong>This action cannot be undone.</strong>
                        </p>
                        <div className="modal-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <button 
                                onClick={() => {
                                    setShowDeleteRecordingModal(false);
                                    setRecordingToDelete(null);
                                }}
                                style={{
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={() => confirmDeleteRecording(recordingToDelete)}
                                disabled={recordingDeleteInFlight}
                                style={{
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    cursor: recordingDeleteInFlight ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    opacity: recordingDeleteInFlight ? 0.7 : 1
                                }}
                            >
                                {recordingDeleteInFlight ? 'Deleting…' : 'Delete Recording & Voice'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Video Confirmation Modal */}
            {showDeleteVideoModal && (
                <div className="modal" onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowDeleteVideoModal(false);
                    }
                }}>
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <div style={{ 
                            fontSize: '48px', 
                            marginBottom: '16px',
                            color: '#dc3545'
                        }}>
                            <AlertCircle size={48} />
                        </div>
                        <h3 style={{ marginBottom: '16px', color: darkMode ? '#fff' : '#333' }}>
                            Delete Video
                        </h3>
                        <p style={{ 
                            marginBottom: '24px', 
                            color: '#666', 
                            fontSize: '16px',
                            lineHeight: '1.5'
                        }}>
                            Are you sure you want to delete this video? This action cannot be undone.
                        </p>
                        <div className="modal-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <button 
                                onClick={() => setShowDeleteVideoModal(false)}
                                style={{
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={confirmDeleteVideo}
                                style={{
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Delete Video
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WorkspaceManagement; 
